// ===== 01-ios-frame.jsx =====
// iOS.jsx — Simplified iOS 26 (Liquid Glass) device frame
// Based on the iOS 26 UI Kit + Figma status bar spec. No assets, no deps.
// Exports: IOSDevice, IOSStatusBar, IOSNavBar, IOSGlassPill, IOSList, IOSListRow, IOSKeyboard

// ─────────────────────────────────────────────────────────────
// Status bar
// ─────────────────────────────────────────────────────────────
function IOSStatusBar({ dark = false }) {
  const c = dark ? '#fff' : '#000';
  const [liveTime, setLiveTime] = React.useState(() => {
    const d = new Date();
    return d.getHours().toString().padStart(2,'0') + ':' + d.getMinutes().toString().padStart(2,'0');
  });
  React.useEffect(() => {
    const id = setInterval(() => {
      const d = new Date();
      setLiveTime(d.getHours().toString().padStart(2,'0') + ':' + d.getMinutes().toString().padStart(2,'0'));
    }, 30000);
    return () => clearInterval(id);
  }, []);
  return (
    <div style={{
      display: 'flex', gap: 154, alignItems: 'center', justifyContent: 'center',
      padding: '21px 24px 19px', boxSizing: 'border-box',
      position: 'relative', zIndex: 20, width: '100%',
    }}>
      <div style={{ flex: 1, height: 22, display: 'flex', alignItems: 'center', justifyContent: 'center', paddingTop: 1.5 }}>
        <span style={{
          fontFamily: '-apple-system, "SF Pro", system-ui', fontWeight: 590,
          fontSize: 17, lineHeight: '22px', color: c,
        }}>{liveTime}</span>
      </div>
      <div style={{ flex: 1, height: 22, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7, paddingTop: 1, paddingRight: 1 }}>
        <svg width="19" height="12" viewBox="0 0 19 12">
          <rect x="0" y="7.5" width="3.2" height="4.5" rx="0.7" fill={c}/>
          <rect x="4.8" y="5" width="3.2" height="7" rx="0.7" fill={c}/>
          <rect x="9.6" y="2.5" width="3.2" height="9.5" rx="0.7" fill={c}/>
          <rect x="14.4" y="0" width="3.2" height="12" rx="0.7" fill={c}/>
        </svg>
        <svg width="17" height="12" viewBox="0 0 17 12">
          <path d="M8.5 3.2C10.8 3.2 12.9 4.1 14.4 5.6L15.5 4.5C13.7 2.7 11.2 1.5 8.5 1.5C5.8 1.5 3.3 2.7 1.5 4.5L2.6 5.6C4.1 4.1 6.2 3.2 8.5 3.2Z" fill={c}/>
          <path d="M8.5 6.8C9.9 6.8 11.1 7.3 12 8.2L13.1 7.1C11.8 5.9 10.2 5.1 8.5 5.1C6.8 5.1 5.2 5.9 3.9 7.1L5 8.2C5.9 7.3 7.1 6.8 8.5 6.8Z" fill={c}/>
          <circle cx="8.5" cy="10.5" r="1.5" fill={c}/>
        </svg>
        <svg width="27" height="13" viewBox="0 0 27 13">
          <rect x="0.5" y="0.5" width="23" height="12" rx="3.5" stroke={c} strokeOpacity="0.35" fill="none"/>
          <rect x="2" y="2" width="20" height="9" rx="2" fill={c}/>
          <path d="M25 4.5V8.5C25.8 8.2 26.5 7.2 26.5 6.5C26.5 5.8 25.8 4.8 25 4.5Z" fill={c} fillOpacity="0.4"/>
        </svg>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Liquid glass pill — blur + tint + shine
// ─────────────────────────────────────────────────────────────
function IOSGlassPill({ children, dark = false, style = {} }) {
  return (
    <div style={{
      height: 44, minWidth: 44, borderRadius: 9999,
      position: 'relative', overflow: 'hidden',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      boxShadow: dark
        ? '0 2px 6px rgba(0,0,0,0.35), 0 6px 16px rgba(0,0,0,0.2)'
        : '0 1px 3px rgba(0,0,0,0.07), 0 3px 10px rgba(0,0,0,0.06)',
      ...style,
    }}>
      {/* blur + tint */}
      <div style={{
        position: 'absolute', inset: 0, borderRadius: 9999,
        backdropFilter: 'blur(12px) saturate(180%)',
        WebkitBackdropFilter: 'blur(12px) saturate(180%)',
        background: dark ? 'rgba(120,120,128,0.28)' : 'rgba(255,255,255,0.5)',
      }} />
      {/* shine */}
      <div style={{
        position: 'absolute', inset: 0, borderRadius: 9999,
        boxShadow: dark
          ? 'inset 1.5px 1.5px 1px rgba(255,255,255,0.15), inset -1px -1px 1px rgba(255,255,255,0.08)'
          : 'inset 1.5px 1.5px 1px rgba(255,255,255,0.7), inset -1px -1px 1px rgba(255,255,255,0.4)',
        border: dark ? '0.5px solid rgba(255,255,255,0.15)' : '0.5px solid rgba(0,0,0,0.06)',
      }} />
      <div style={{ position: 'relative', zIndex: 1, display: 'flex', alignItems: 'center', padding: '0 4px' }}>
        {children}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Navigation bar — glass pills + large title
// ─────────────────────────────────────────────────────────────
function IOSNavBar({ title = 'Title', dark = false, trailingIcon = true }) {
  const muted = dark ? 'rgba(255,255,255,0.6)' : '#404040';
  const text = dark ? '#fff' : '#000';
  const pillIcon = (content) => (
    <IOSGlassPill dark={dark}>
      <div style={{ width: 36, height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {content}
      </div>
    </IOSGlassPill>
  );
  return (
    <div style={{
      display: 'flex', flexDirection: 'column', gap: 10,
      paddingTop: 62, paddingBottom: 10, position: 'relative', zIndex: 5,
    }}>
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '0 16px',
      }}>
        {/* back chevron */}
        {pillIcon(
          <svg width="12" height="20" viewBox="0 0 12 20" fill="none" style={{ marginLeft: -1 }}>
            <path d="M10 2L2 10l8 8" stroke={muted} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        )}
        {/* trailing ellipsis */}
        {trailingIcon && pillIcon(
          <svg width="22" height="6" viewBox="0 0 22 6">
            <circle cx="3" cy="3" r="2.5" fill={muted}/>
            <circle cx="11" cy="3" r="2.5" fill={muted}/>
            <circle cx="19" cy="3" r="2.5" fill={muted}/>
          </svg>
        )}
      </div>
      {/* large title */}
      <div style={{
        padding: '0 16px',
        fontFamily: '-apple-system, system-ui',
        fontSize: 34, fontWeight: 700, lineHeight: '41px',
        color: text, letterSpacing: 0.4,
      }}>{title}</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Grouped list (inset card, r:26) + row (52px)
// ─────────────────────────────────────────────────────────────
function IOSListRow({ title, detail, icon, chevron = true, isLast = false, dark = false }) {
  const text = dark ? '#fff' : '#000';
  const sec = dark ? 'rgba(235,235,245,0.6)' : 'rgba(60,60,67,0.6)';
  const ter = dark ? 'rgba(235,235,245,0.3)' : 'rgba(60,60,67,0.3)';
  const sep = dark ? 'rgba(84,84,88,0.65)' : 'rgba(60,60,67,0.12)';
  return (
    <div style={{
      display: 'flex', alignItems: 'center', minHeight: 52,
      padding: '0 16px', position: 'relative',
      fontFamily: '-apple-system, system-ui', fontSize: 17,
      letterSpacing: -0.43,
    }}>
      {icon && (
        <div style={{
          width: 30, height: 30, borderRadius: 7, background: icon,
          marginRight: 12, flexShrink: 0,
        }} />
      )}
      <div style={{ flex: 1, color: text }}>{title}</div>
      {detail && <span style={{ color: sec, marginRight: 6 }}>{detail}</span>}
      {chevron && (
        <svg width="8" height="14" viewBox="0 0 8 14" style={{ flexShrink: 0 }}>
          <path d="M1 1l6 6-6 6" stroke={ter} strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      )}
      {!isLast && (
        <div style={{
          position: 'absolute', bottom: 0, right: 0,
          left: icon ? 58 : 16, height: 0.5, background: sep,
        }} />
      )}
    </div>
  );
}

function IOSList({ header, children, dark = false }) {
  const hc = dark ? 'rgba(235,235,245,0.6)' : 'rgba(60,60,67,0.6)';
  const bg = dark ? '#1C1C1E' : '#fff';
  return (
    <div>
      {header && (
        <div style={{
          fontFamily: '-apple-system, system-ui', fontSize: 13,
          color: hc, textTransform: 'uppercase',
          padding: '8px 36px 6px', letterSpacing: -0.08,
        }}>{header}</div>
      )}
      <div style={{
        background: bg, borderRadius: 26,
        margin: '0 16px', overflow: 'hidden',
      }}>{children}</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Device frame
// ─────────────────────────────────────────────────────────────
function IOSDevice({
  children, width = 402, height = 874, dark = false,
  title, keyboard = false,
}) {
  // Inside the real native app (SkaleDesk-Native's WebView), the device
  // already draws its own status bar, dynamic island, and home indicator —
  // this preview frame's simulated chrome is only for showing the design in
  // a plain desktop browser. Rendering it inside the real app doubles up on
  // top of the OS's real chrome. The native wrapper sets
  // window.isNativeApp = true before this page loads.
  const isNative = typeof window !== 'undefined' && window.isNativeApp === true;

  return (
    <div style={{
      width: isNative ? '100%' : width,
      height: isNative ? '100%' : height,
      borderRadius: isNative ? 0 : 48,
      overflow: 'hidden',
      position: 'relative', background: dark ? '#000' : '#F2F2F7',
      boxShadow: isNative ? 'none' : '0 40px 80px rgba(0,0,0,0.18), 0 0 0 1px rgba(0,0,0,0.12)',
      fontFamily: '-apple-system, system-ui, sans-serif',
      WebkitFontSmoothing: 'antialiased',
    }}>
      {!isNative && (
        <>
          {/* dynamic island */}
          <div style={{
            position: 'absolute', top: 11, left: '50%', transform: 'translateX(-50%)',
            width: 126, height: 37, borderRadius: 24, background: '#000', zIndex: 50,
          }} />
          {/* status bar (absolute) */}
          <div style={{ position: 'absolute', top: 0, left: 0, right: 0, zIndex: 10 }}>
            <IOSStatusBar dark={dark} />
          </div>
        </>
      )}
      {/* nav + content */}
      <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        {title !== undefined && <IOSNavBar title={title} dark={dark} />}
        <div style={{ flex: 1, overflow: 'auto' }}>{children}</div>
        {keyboard && <IOSKeyboard dark={dark} />}
      </div>
      {!isNative && (
        /* home indicator — always on top */
        <div style={{
          position: 'absolute', bottom: 0, left: 0, right: 0, zIndex: 60,
          height: 34, display: 'flex', justifyContent: 'center', alignItems: 'flex-end',
          paddingBottom: 8, pointerEvents: 'none',
        }}>
          <div style={{
            width: 139, height: 5, borderRadius: 100,
            background: dark ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.25)',
          }} />
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Keyboard — iOS 26 liquid glass
// ─────────────────────────────────────────────────────────────
function IOSKeyboard({ dark = false }) {
  const glyph = dark ? 'rgba(255,255,255,0.7)' : '#595959';
  const sugg = dark ? 'rgba(255,255,255,0.6)' : '#333';
  const keyBg = dark ? 'rgba(255,255,255,0.22)' : 'rgba(255,255,255,0.85)';

  // special-key icons
  const icons = {
    shift: <svg width="19" height="17" viewBox="0 0 19 17"><path d="M9.5 1L1 9.5h4.5V16h8V9.5H18L9.5 1z" fill={glyph}/></svg>,
    del: <svg width="23" height="17" viewBox="0 0 23 17"><path d="M7 1h13a2 2 0 012 2v11a2 2 0 01-2 2H7l-6-7.5L7 1z" fill="none" stroke={glyph} strokeWidth="1.6" strokeLinejoin="round"/><path d="M10 5l7 7M17 5l-7 7" stroke={glyph} strokeWidth="1.6" strokeLinecap="round"/></svg>,
    ret: <svg width="20" height="14" viewBox="0 0 20 14"><path d="M18 1v6H4m0 0l4-4M4 7l4 4" fill="none" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>,
  };

  const key = (content, { w, flex, ret, fs = 25, k } = {}) => (
    <div key={k} style={{
      height: 42, borderRadius: 8.5,
      flex: flex ? 1 : undefined, width: w, minWidth: 0,
      background: ret ? '#08f' : keyBg,
      boxShadow: '0 1px 0 rgba(0,0,0,0.075)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontFamily: '-apple-system, "SF Compact", system-ui',
      fontSize: fs, fontWeight: 458, color: ret ? '#fff' : glyph,
    }}>{content}</div>
  );

  const row = (keys, pad = 0) => (
    <div style={{ display: 'flex', gap: 6.5, justifyContent: 'center', padding: `0 ${pad}px` }}>
      {keys.map(l => key(l, { flex: true, k: l }))}
    </div>
  );

  return (
    <div style={{
      position: 'relative', zIndex: 15, borderRadius: 27, overflow: 'hidden',
      padding: '11px 0 2px',
      display: 'flex', flexDirection: 'column', alignItems: 'center',
      boxShadow: dark
        ? '0 -2px 20px rgba(0,0,0,0.09)'
        : '0 -1px 6px rgba(0,0,0,0.018), 0 -3px 20px rgba(0,0,0,0.012)',
    }}>
      {/* liquid glass bg — same recipe as nav pills */}
      <div style={{
        position: 'absolute', inset: 0, borderRadius: 27,
        backdropFilter: 'blur(12px) saturate(180%)',
        WebkitBackdropFilter: 'blur(12px) saturate(180%)',
        background: dark ? 'rgba(120,120,128,0.14)' : 'rgba(255,255,255,0.25)',
      }} />
      <div style={{
        position: 'absolute', inset: 0, borderRadius: 27,
        boxShadow: dark
          ? 'inset 1.5px 1.5px 1px rgba(255,255,255,0.15)'
          : 'inset 1.5px 1.5px 1px rgba(255,255,255,0.7), inset -1px -1px 1px rgba(255,255,255,0.4)',
        border: dark ? '0.5px solid rgba(255,255,255,0.15)' : '0.5px solid rgba(0,0,0,0.06)',
        pointerEvents: 'none',
      }} />

      {/* autocorrect bar */}
      <div style={{
        display: 'flex', gap: 20, alignItems: 'center',
        padding: '8px 22px 13px', width: '100%', boxSizing: 'border-box',
        position: 'relative',
      }}>
        {['"The"', 'the', 'to'].map((w, i) => (
          <React.Fragment key={i}>
            {i > 0 && <div style={{ width: 1, height: 25, background: '#ccc', opacity: 0.3 }} />}
            <div style={{
              flex: 1, textAlign: 'center',
              fontFamily: '-apple-system, system-ui', fontSize: 17,
              color: sugg, letterSpacing: -0.43, lineHeight: '22px',
            }}>{w}</div>
          </React.Fragment>
        ))}
      </div>

      {/* key layout */}
      <div style={{
        display: 'flex', flexDirection: 'column', gap: 13,
        padding: '0 6.5px', width: '100%', boxSizing: 'border-box',
        position: 'relative',
      }}>
        {row(['q','w','e','r','t','y','u','i','o','p'])}
        {row(['a','s','d','f','g','h','j','k','l'], 20)}
        <div style={{ display: 'flex', gap: 14.25, alignItems: 'center' }}>
          {key(icons.shift, { w: 45, k: 'shift' })}
          <div style={{ display: 'flex', gap: 6.5, flex: 1 }}>
            {['z','x','c','v','b','n','m'].map(l => key(l, { flex: true, k: l }))}
          </div>
          {key(icons.del, { w: 45, k: 'del' })}
        </div>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          {key('ABC', { w: 92.25, fs: 18, k: 'abc' })}
          {key('', { flex: true, k: 'space' })}
          {key(icons.ret, { w: 92.25, ret: true, k: 'ret' })}
        </div>
      </div>

      {/* bottom spacer (emoji+mic area, icons omitted) */}
      <div style={{ height: 56, width: '100%', position: 'relative' }} />
    </div>
  );
}

Object.assign(window, {
  IOSDevice, IOSStatusBar, IOSNavBar, IOSGlassPill, IOSList, IOSListRow, IOSKeyboard,
});



// ===== 02-android-frame.jsx =====
// Android.jsx — Simplified Android (Material 3) device frame
// Status bar + top app bar + content + gesture nav + keyboard.
// Based on Figma M3 spec. No dependencies, no image assets.

const MD_C = {
  surface: '#f4fbf8',
  surfaceVariant: '#dae5e1',
  inverseOnSurface: '#ecf2ef',
  secondaryContainer: '#cde8e1',
  primaryFixedDim: '#83d5c6',
  onSurface: '#171d1b',
  onSurfaceVar: '#49454f',
  onPrimaryContainer: '#00201c',
  primary: '#006a60',
  frameBorder: 'rgba(116,119,117,0.5)',
};

// ─────────────────────────────────────────────────────────────
// Status bar (time left, wifi/cell/battery right)
// ─────────────────────────────────────────────────────────────
function AndroidStatusBar({ dark = false }) {
  const c = dark ? '#fff' : MD_C.onSurface;
  const [liveTime, setLiveTime] = React.useState(() => {
    const d = new Date();
    return d.getHours().toString().padStart(2,'0') + ':' + d.getMinutes().toString().padStart(2,'0');
  });
  React.useEffect(() => {
    const id = setInterval(() => {
      const d = new Date();
      setLiveTime(d.getHours().toString().padStart(2,'0') + ':' + d.getMinutes().toString().padStart(2,'0'));
    }, 30000);
    return () => clearInterval(id);
  }, []);
  return (
    <div style={{
      height: 40, display: 'flex', alignItems: 'center',
      justifyContent: 'space-between', padding: '0 16px',
      position: 'relative',
      fontFamily: 'Roboto, system-ui, sans-serif',
    }}>
      {/* time left */}
      <div style={{ width: 128, display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ fontSize: 14, fontWeight: 400, letterSpacing: 0.25, lineHeight: '20px', color: c }}>{liveTime}</span>
      </div>
      {/* camera punch-hole (center) */}
      <div style={{
        position: 'absolute', left: '50%', top: 8, transform: 'translateX(-50%)',
        width: 24, height: 24, borderRadius: 100, background: '#2e2e2e',
      }} />
      {/* status icons right */}
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <div style={{ display: 'flex', paddingRight: 2 }}>
          <svg width="16" height="16" viewBox="0 0 16 16" style={{ marginRight: -2 }}>
            <path d="M8 13.3L.67 5.97a10.37 10.37 0 0114.66 0L8 13.3z" fill={c}/>
          </svg>
          <svg width="16" height="16" viewBox="0 0 16 16" style={{ marginRight: -2 }}>
            <path d="M14.67 14.67V1.33L1.33 14.67h13.34z" fill={c}/>
          </svg>
        </div>
        <svg width="16" height="16" viewBox="0 0 16 16">
          <rect x="3.75" y="2" width="8.5" height="13" rx="1.5" fill={c}/>
          <rect x="5.5" y="0.9" width="5" height="2" rx="0.5" fill={c}/>
        </svg>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Top app bar (Material 3 small/medium)
// ─────────────────────────────────────────────────────────────
function AndroidAppBar({ title = 'Title', large = false }) {
  const iconDot = (
    <div style={{
      width: 48, height: 48, display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <div style={{ width: 22, height: 22, borderRadius: '50%', background: MD_C.onSurfaceVar, opacity: 0.3 }} />
    </div>
  );
  return (
    <div style={{ background: MD_C.surface, padding: '4px 4px 0' }}>
      <div style={{ height: 56, display: 'flex', alignItems: 'center', gap: 4 }}>
        {iconDot}
        {!large && (
          <span style={{
            flex: 1, fontSize: 22, fontWeight: 400, color: MD_C.onSurface,
            fontFamily: 'Roboto, system-ui, sans-serif',
          }}>{title}</span>
        )}
        {large && <div style={{ flex: 1 }} />}
        {iconDot}
      </div>
      {large && (
        <div style={{
          padding: '16px 16px 20px',
          fontSize: 28, fontWeight: 400, color: MD_C.onSurface,
          fontFamily: 'Roboto, system-ui, sans-serif',
        }}>{title}</div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// List item (Material 3)
// ─────────────────────────────────────────────────────────────
function AndroidListItem({ headline, supporting, leading }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 16,
      padding: '12px 16px', minHeight: 56, boxSizing: 'border-box',
      fontFamily: 'Roboto, system-ui, sans-serif',
    }}>
      {leading && (
        <div style={{
          width: 40, height: 40, borderRadius: '50%',
          background: MD_C.primary, color: '#fff',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 18, fontWeight: 500, flexShrink: 0,
        }}>{leading}</div>
      )}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 16, color: MD_C.onSurface, lineHeight: '24px' }}>{headline}</div>
        {supporting && (
          <div style={{ fontSize: 14, color: MD_C.onSurfaceVar, lineHeight: '20px' }}>{supporting}</div>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Gesture nav bar (pill)
// ─────────────────────────────────────────────────────────────
function AndroidNavBar({ dark = false }) {
  return (
    <div style={{
      height: 24, display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <div style={{
        width: 108, height: 4, borderRadius: 2,
        background: dark ? '#fff' : MD_C.onSurface, opacity: 0.4,
      }} />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Device frame — wraps everything
// ─────────────────────────────────────────────────────────────
function AndroidDevice({
  children, width = 412, height = 892, dark = false,
  title, large = false, keyboard = false,
}) {
  return (
    <div style={{
      width, height, borderRadius: 18, overflow: 'hidden',
      background: dark ? '#1d1b20' : MD_C.surface,
      border: `8px solid ${MD_C.frameBorder}`,
      boxShadow: '0 30px 80px rgba(0,0,0,0.25)',
      display: 'flex', flexDirection: 'column', boxSizing: 'border-box',
    }}>
      <AndroidStatusBar dark={dark} />
      {title !== undefined && <AndroidAppBar title={title} large={large} />}
      <div style={{ flex: 1, overflow: 'auto' }}>
        {children}
      </div>
      {keyboard && <AndroidKeyboard />}
      <AndroidNavBar dark={dark} />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Keyboard — Gboard (Material 3)
// ─────────────────────────────────────────────────────────────
function AndroidKeyboard() {
  let _k = 0;
  const key = (l, { flex = 1, bg = MD_C.surface, r = 6, minW, fs = 21 } = {}) => (
    <div key={_k++} style={{
      height: 46, borderRadius: r, flex, minWidth: minW,
      background: bg, display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontFamily: 'Roboto, system-ui', fontSize: fs,
      color: MD_C.onPrimaryContainer,
    }}>{l}</div>
  );
  const row = (keys, style = {}) => (
    <div style={{ display: 'flex', gap: 6, justifyContent: 'center', ...style }}>
      {keys.map(l => key(l))}
    </div>
  );
  return (
    <div style={{
      background: MD_C.inverseOnSurface, padding: '0 8px 8px',
      display: 'flex', flexDirection: 'column', gap: 4,
    }}>
      {/* navbar spacer (icons omitted) */}
      <div style={{ height: 44 }} />
      {/* key rows */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {row(['q','w','e','r','t','y','u','i','o','p'])}
        {row(['a','s','d','f','g','h','j','k','l'], { padding: '0 20px' })}
        <div style={{ display: 'flex', gap: 6 }}>
          {key('', { bg: MD_C.surfaceVariant })}
          <div style={{ display: 'flex', gap: 6, flex: 7, minWidth: 274 }}>
            {['z','x','c','v','b','n','m'].map(l => key(l))}
          </div>
          {key('', { bg: MD_C.surfaceVariant })}
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          {key('?123', { bg: MD_C.secondaryContainer, r: 100, minW: 58, fs: 14 })}
          {key(',', { bg: MD_C.surfaceVariant })}
          {key('', { flex: 3, minW: 154 })}
          {key('.', { bg: MD_C.surfaceVariant })}
          {key('', { bg: MD_C.primaryFixedDim, r: 100, minW: 58 })}
        </div>
      </div>
    </div>
  );
}

Object.assign(window, {
  AndroidDevice, AndroidStatusBar, AndroidAppBar, AndroidListItem, AndroidNavBar, AndroidKeyboard,
});



// ===== 03-tweaks-panel.jsx =====
// tweaks-panel.jsx
// Reusable Tweaks shell + form-control helpers.
//
// Owns the host protocol (listens for __activate_edit_mode / __deactivate_edit_mode,
// posts __edit_mode_available / __edit_mode_set_keys / __edit_mode_dismissed) so
// individual prototypes don't re-roll it. Ships a consistent set of controls so you
// don't hand-draw <input type="range">, segmented radios, steppers, etc.
//
// Usage (in an HTML file that loads React + Babel):
//
//   const TWEAK_DEFAULTS = /*EDITMODE-BEGIN*/{
//     "primaryColor": "#D97757",
//     "fontSize": 16,
//     "density": "regular",
//     "dark": false
//   }/*EDITMODE-END*/;
//
//   function App() {
//     const [t, setTweak] = useTweaks(TWEAK_DEFAULTS);
//     return (
//       <div style={{ fontSize: t.fontSize, color: t.primaryColor }}>
//         Hello
//         <TweaksPanel>
//           <TweakSection label="Typography" />
//           <TweakSlider label="Font size" value={t.fontSize} min={10} max={32} unit="px"
//                        onChange={(v) => setTweak('fontSize', v)} />
//           <TweakRadio  label="Density" value={t.density}
//                        options={['compact', 'regular', 'comfy']}
//                        onChange={(v) => setTweak('density', v)} />
//           <TweakSection label="Theme" />
//           <TweakColor  label="Primary" value={t.primaryColor}
//                        onChange={(v) => setTweak('primaryColor', v)} />
//           <TweakToggle label="Dark mode" value={t.dark}
//                        onChange={(v) => setTweak('dark', v)} />
//         </TweaksPanel>
//       </div>
//     );
//   }
//
// ─────────────────────────────────────────────────────────────────────────────

const __TWEAKS_STYLE = `
  .twk-panel{position:fixed;right:16px;bottom:16px;z-index:2147483646;width:280px;
    max-height:calc(100vh - 32px);display:flex;flex-direction:column;
    background:rgba(250,249,247,.78);color:#29261b;
    -webkit-backdrop-filter:blur(24px) saturate(160%);backdrop-filter:blur(24px) saturate(160%);
    border:.5px solid rgba(255,255,255,.6);border-radius:14px;
    box-shadow:0 1px 0 rgba(255,255,255,.5) inset,0 12px 40px rgba(0,0,0,.18);
    font:11.5px/1.4 ui-sans-serif,system-ui,-apple-system,sans-serif;overflow:hidden}
  .twk-hd{display:flex;align-items:center;justify-content:space-between;
    padding:10px 8px 10px 14px;cursor:move;user-select:none}
  .twk-hd b{font-size:12px;font-weight:600;letter-spacing:.01em}
  .twk-x{appearance:none;border:0;background:transparent;color:rgba(41,38,27,.55);
    width:22px;height:22px;border-radius:6px;cursor:default;font-size:13px;line-height:1}
  .twk-x:hover{background:rgba(0,0,0,.06);color:#29261b}
  .twk-body{padding:2px 14px 14px;display:flex;flex-direction:column;gap:10px;
    overflow-y:auto;overflow-x:hidden;min-height:0;
    scrollbar-width:thin;scrollbar-color:rgba(0,0,0,.15) transparent}
  .twk-body::-webkit-scrollbar{width:8px}
  .twk-body::-webkit-scrollbar-track{background:transparent;margin:2px}
  .twk-body::-webkit-scrollbar-thumb{background:rgba(0,0,0,.15);border-radius:4px;
    border:2px solid transparent;background-clip:content-box}
  .twk-body::-webkit-scrollbar-thumb:hover{background:rgba(0,0,0,.25);
    border:2px solid transparent;background-clip:content-box}
  .twk-row{display:flex;flex-direction:column;gap:5px}
  .twk-row-h{flex-direction:row;align-items:center;justify-content:space-between;gap:10px}
  .twk-lbl{display:flex;justify-content:space-between;align-items:baseline;
    color:rgba(41,38,27,.72)}
  .twk-lbl>span:first-child{font-weight:500}
  .twk-val{color:rgba(41,38,27,.5);font-variant-numeric:tabular-nums}

  .twk-sect{font-size:10px;font-weight:600;letter-spacing:.06em;text-transform:uppercase;
    color:rgba(41,38,27,.45);padding:10px 0 0}
  .twk-sect:first-child{padding-top:0}

  .twk-field{appearance:none;width:100%;height:26px;padding:0 8px;
    border:.5px solid rgba(0,0,0,.1);border-radius:7px;
    background:rgba(255,255,255,.6);color:inherit;font:inherit;outline:none}
  .twk-field:focus{border-color:rgba(0,0,0,.25);background:rgba(255,255,255,.85)}
  select.twk-field{padding-right:22px;
    background-image:url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6'><path fill='rgba(0,0,0,.5)' d='M0 0h10L5 6z'/></svg>");
    background-repeat:no-repeat;background-position:right 8px center}

  .twk-slider{appearance:none;-webkit-appearance:none;width:100%;height:4px;margin:6px 0;
    border-radius:999px;background:rgba(0,0,0,.12);outline:none}
  .twk-slider::-webkit-slider-thumb{-webkit-appearance:none;appearance:none;
    width:14px;height:14px;border-radius:50%;background:#fff;
    border:.5px solid rgba(0,0,0,.12);box-shadow:0 1px 3px rgba(0,0,0,.2);cursor:default}
  .twk-slider::-moz-range-thumb{width:14px;height:14px;border-radius:50%;
    background:#fff;border:.5px solid rgba(0,0,0,.12);box-shadow:0 1px 3px rgba(0,0,0,.2);cursor:default}

  .twk-seg{position:relative;display:flex;padding:2px;border-radius:8px;
    background:rgba(0,0,0,.06);user-select:none}
  .twk-seg-thumb{position:absolute;top:2px;bottom:2px;border-radius:6px;
    background:rgba(255,255,255,.9);box-shadow:0 1px 2px rgba(0,0,0,.12);
    transition:left .15s cubic-bezier(.3,.7,.4,1),width .15s}
  .twk-seg.dragging .twk-seg-thumb{transition:none}
  .twk-seg button{appearance:none;position:relative;z-index:1;flex:1;border:0;
    background:transparent;color:inherit;font:inherit;font-weight:500;height:22px;
    border-radius:6px;cursor:default;padding:0}

  .twk-toggle{position:relative;width:32px;height:18px;border:0;border-radius:999px;
    background:rgba(0,0,0,.15);transition:background .15s;cursor:default;padding:0}
  .twk-toggle[data-on="1"]{background:#34c759}
  .twk-toggle i{position:absolute;top:2px;left:2px;width:14px;height:14px;border-radius:50%;
    background:#fff;box-shadow:0 1px 2px rgba(0,0,0,.25);transition:transform .15s}
  .twk-toggle[data-on="1"] i{transform:translateX(14px)}

  .twk-num{display:flex;align-items:center;height:26px;padding:0 0 0 8px;
    border:.5px solid rgba(0,0,0,.1);border-radius:7px;background:rgba(255,255,255,.6)}
  .twk-num-lbl{font-weight:500;color:rgba(41,38,27,.6);cursor:ew-resize;
    user-select:none;padding-right:8px}
  .twk-num input{flex:1;min-width:0;height:100%;border:0;background:transparent;
    font:inherit;font-variant-numeric:tabular-nums;text-align:right;padding:0 8px 0 0;
    outline:none;color:inherit;-moz-appearance:textfield}
  .twk-num input::-webkit-inner-spin-button,.twk-num input::-webkit-outer-spin-button{
    -webkit-appearance:none;margin:0}
  .twk-num-unit{padding-right:8px;color:rgba(41,38,27,.45)}

  .twk-btn{appearance:none;height:26px;padding:0 12px;border:0;border-radius:7px;
    background:rgba(0,0,0,.78);color:#fff;font:inherit;font-weight:500;cursor:default}
  .twk-btn:hover{background:rgba(0,0,0,.88)}
  .twk-btn.secondary{background:rgba(0,0,0,.06);color:inherit}
  .twk-btn.secondary:hover{background:rgba(0,0,0,.1)}

  .twk-swatch{appearance:none;-webkit-appearance:none;width:56px;height:22px;
    border:.5px solid rgba(0,0,0,.1);border-radius:6px;padding:0;cursor:default;
    background:transparent;flex-shrink:0}
  .twk-swatch::-webkit-color-swatch-wrapper{padding:0}
  .twk-swatch::-webkit-color-swatch{border:0;border-radius:5.5px}
  .twk-swatch::-moz-color-swatch{border:0;border-radius:5.5px}
`;

// ── useTweaks ───────────────────────────────────────────────────────────────
// Single source of truth for tweak values. setTweak persists via the host
// (__edit_mode_set_keys → host rewrites the EDITMODE block on disk).
function useTweaks(defaults) {
  const [values, setValues] = React.useState(defaults);
  const setTweak = React.useCallback((key, val) => {
    setValues((prev) => ({ ...prev, [key]: val }));
    window.parent.postMessage({ type: '__edit_mode_set_keys', edits: { [key]: val } }, '*');
  }, []);
  return [values, setTweak];
}

// ── TweaksPanel ─────────────────────────────────────────────────────────────
// Floating shell. Registers the protocol listener BEFORE announcing
// availability — if the announce ran first, the host's activate could land
// before our handler exists and the toolbar toggle would silently no-op.
// The close button posts __edit_mode_dismissed so the host's toolbar toggle
// flips off in lockstep; the host echoes __deactivate_edit_mode back which
// is what actually hides the panel.
function TweaksPanel({ title = 'Tweaks', children }) {
  const [open, setOpen] = React.useState(false);
  const dragRef = React.useRef(null);
  const offsetRef = React.useRef({ x: 16, y: 16 });
  const PAD = 16;

  const clampToViewport = React.useCallback(() => {
    const panel = dragRef.current;
    if (!panel) return;
    const w = panel.offsetWidth, h = panel.offsetHeight;
    const maxRight = Math.max(PAD, window.innerWidth - w - PAD);
    const maxBottom = Math.max(PAD, window.innerHeight - h - PAD);
    offsetRef.current = {
      x: Math.min(maxRight, Math.max(PAD, offsetRef.current.x)),
      y: Math.min(maxBottom, Math.max(PAD, offsetRef.current.y)),
    };
    panel.style.right = offsetRef.current.x + 'px';
    panel.style.bottom = offsetRef.current.y + 'px';
  }, []);

  React.useEffect(() => {
    if (!open) return;
    clampToViewport();
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', clampToViewport);
      return () => window.removeEventListener('resize', clampToViewport);
    }
    const ro = new ResizeObserver(clampToViewport);
    ro.observe(document.documentElement);
    return () => ro.disconnect();
  }, [open, clampToViewport]);

  React.useEffect(() => {
    const onMsg = (e) => {
      const t = e?.data?.type;
      if (t === '__activate_edit_mode') setOpen(true);
      else if (t === '__deactivate_edit_mode') setOpen(false);
    };
    window.addEventListener('message', onMsg);
    window.parent.postMessage({ type: '__edit_mode_available' }, '*');
    return () => window.removeEventListener('message', onMsg);
  }, []);

  const dismiss = () => {
    setOpen(false);
    window.parent.postMessage({ type: '__edit_mode_dismissed' }, '*');
  };

  const onDragStart = (e) => {
    const panel = dragRef.current;
    if (!panel) return;
    const r = panel.getBoundingClientRect();
    const sx = e.clientX, sy = e.clientY;
    const startRight = window.innerWidth - r.right;
    const startBottom = window.innerHeight - r.bottom;
    const move = (ev) => {
      offsetRef.current = {
        x: startRight - (ev.clientX - sx),
        y: startBottom - (ev.clientY - sy),
      };
      clampToViewport();
    };
    const up = () => {
      window.removeEventListener('mousemove', move);
      window.removeEventListener('mouseup', up);
    };
    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup', up);
  };

  if (!open) return null;
  return (
    <>
      <style>{__TWEAKS_STYLE}</style>
      <div ref={dragRef} className="twk-panel"
           style={{ right: offsetRef.current.x, bottom: offsetRef.current.y }}>
        <div className="twk-hd" onMouseDown={onDragStart}>
          <b>{title}</b>
          <button className="twk-x" aria-label="Close tweaks"
                  onMouseDown={(e) => e.stopPropagation()}
                  onClick={dismiss}>✕</button>
        </div>
        <div className="twk-body">{children}</div>
      </div>
    </>
  );
}

// ── Layout helpers ──────────────────────────────────────────────────────────

function TweakSection({ label, children }) {
  return (
    <>
      <div className="twk-sect">{label}</div>
      {children}
    </>
  );
}

function TweakRow({ label, value, children, inline = false }) {
  return (
    <div className={inline ? 'twk-row twk-row-h' : 'twk-row'}>
      <div className="twk-lbl">
        <span>{label}</span>
        {value != null && <span className="twk-val">{value}</span>}
      </div>
      {children}
    </div>
  );
}

// ── Controls ────────────────────────────────────────────────────────────────

function TweakSlider({ label, value, min = 0, max = 100, step = 1, unit = '', onChange }) {
  return (
    <TweakRow label={label} value={`${value}${unit}`}>
      <input type="range" className="twk-slider" min={min} max={max} step={step}
             value={value} onChange={(e) => onChange(Number(e.target.value))} />
    </TweakRow>
  );
}

function TweakToggle({ label, value, onChange }) {
  return (
    <div className="twk-row twk-row-h">
      <div className="twk-lbl"><span>{label}</span></div>
      <button type="button" className="twk-toggle" data-on={value ? '1' : '0'}
              role="switch" aria-checked={!!value}
              onClick={() => onChange(!value)}><i /></button>
    </div>
  );
}

function TweakRadio({ label, value, options, onChange }) {
  const trackRef = React.useRef(null);
  const [dragging, setDragging] = React.useState(false);
  const opts = options.map((o) => (typeof o === 'object' ? o : { value: o, label: o }));
  const idx = Math.max(0, opts.findIndex((o) => o.value === value));
  const n = opts.length;

  // The active value is read by pointer-move handlers attached for the lifetime
  // of a drag — ref it so a stale closure doesn't fire onChange for every move.
  const valueRef = React.useRef(value);
  valueRef.current = value;

  const segAt = (clientX) => {
    const r = trackRef.current.getBoundingClientRect();
    const inner = r.width - 4;
    const i = Math.floor(((clientX - r.left - 2) / inner) * n);
    return opts[Math.max(0, Math.min(n - 1, i))].value;
  };

  const onPointerDown = (e) => {
    setDragging(true);
    const v0 = segAt(e.clientX);
    if (v0 !== valueRef.current) onChange(v0);
    const move = (ev) => {
      if (!trackRef.current) return;
      const v = segAt(ev.clientX);
      if (v !== valueRef.current) onChange(v);
    };
    const up = () => {
      setDragging(false);
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  };

  return (
    <TweakRow label={label}>
      <div ref={trackRef} role="radiogroup" onPointerDown={onPointerDown}
           className={dragging ? 'twk-seg dragging' : 'twk-seg'}>
        <div className="twk-seg-thumb"
             style={{ left: `calc(2px + ${idx} * (100% - 4px) / ${n})`,
                      width: `calc((100% - 4px) / ${n})` }} />
        {opts.map((o) => (
          <button key={o.value} type="button" role="radio" aria-checked={o.value === value}>
            {o.label}
          </button>
        ))}
      </div>
    </TweakRow>
  );
}

function TweakSelect({ label, value, options, onChange }) {
  return (
    <TweakRow label={label}>
      <select className="twk-field" value={value} onChange={(e) => onChange(e.target.value)}>
        {options.map((o) => {
          const v = typeof o === 'object' ? o.value : o;
          const l = typeof o === 'object' ? o.label : o;
          return <option key={v} value={v}>{l}</option>;
        })}
      </select>
    </TweakRow>
  );
}

function TweakText({ label, value, placeholder, onChange }) {
  return (
    <TweakRow label={label}>
      <input className="twk-field" type="text" value={value} placeholder={placeholder}
             onChange={(e) => onChange(e.target.value)} />
    </TweakRow>
  );
}

function TweakNumber({ label, value, min, max, step = 1, unit = '', onChange }) {
  const clamp = (n) => {
    if (min != null && n < min) return min;
    if (max != null && n > max) return max;
    return n;
  };
  const startRef = React.useRef({ x: 0, val: 0 });
  const onScrubStart = (e) => {
    e.preventDefault();
    startRef.current = { x: e.clientX, val: value };
    const decimals = (String(step).split('.')[1] || '').length;
    const move = (ev) => {
      const dx = ev.clientX - startRef.current.x;
      const raw = startRef.current.val + dx * step;
      const snapped = Math.round(raw / step) * step;
      onChange(clamp(Number(snapped.toFixed(decimals))));
    };
    const up = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  };
  return (
    <div className="twk-num">
      <span className="twk-num-lbl" onPointerDown={onScrubStart}>{label}</span>
      <input type="number" value={value} min={min} max={max} step={step}
             onChange={(e) => onChange(clamp(Number(e.target.value)))} />
      {unit && <span className="twk-num-unit">{unit}</span>}
    </div>
  );
}

function TweakColor({ label, value, onChange }) {
  return (
    <div className="twk-row twk-row-h">
      <div className="twk-lbl"><span>{label}</span></div>
      <input type="color" className="twk-swatch" value={value}
             onChange={(e) => onChange(e.target.value)} />
    </div>
  );
}

function TweakButton({ label, onClick, secondary = false }) {
  return (
    <button type="button" className={secondary ? 'twk-btn secondary' : 'twk-btn'}
            onClick={onClick}>{label}</button>
  );
}

Object.assign(window, {
  useTweaks, TweaksPanel, TweakSection, TweakRow,
  TweakSlider, TweakToggle, TweakRadio, TweakSelect,
  TweakText, TweakNumber, TweakColor, TweakButton,
});



// ===== 04-pf-shared.jsx =====
/* global React */
// Line-icon set — consistent 1.5 stroke, 20px
const PFIcon = ({ name, size = 20, color = 'currentColor', strokeWidth = 1.7 }) => {
  const paths = {
    home: <><path d="M3 11.5L12 4l9 7.5" /><path d="M5 10v10h14V10" /></>,
    leads: <><circle cx="9" cy="8" r="3.2" /><path d="M3.5 19c.7-3.2 3-5 5.5-5s4.8 1.8 5.5 5" /><path d="M16 11.5c1.8 0 3.2-1.3 3.2-3s-1.4-3-3.2-3" /><path d="M18 19c-.3-2-1.2-3.3-2.4-4" /></>,
    phone: <><path d="M5 4h3l2 5-2 1.5c1 2.2 2.8 4 5 5L14.5 14l5 2v3c0 1-.7 1.7-1.7 1.7C11 20.6 4.5 14 4.4 6.7 4.4 5.7 5 5 5 4z" /></>,
    chat: <><path d="M4 5h16v11H9l-5 4z" /></>,
    tasks: <><rect x="4" y="4" width="16" height="16" rx="3" /><path d="M8.5 12.2l2.3 2.3 4.7-4.7" /></>,
    grid: <><rect x="4" y="4" width="7" height="7" rx="1.5" /><rect x="13" y="4" width="7" height="7" rx="1.5" /><rect x="4" y="13" width="7" height="7" rx="1.5" /><rect x="13" y="13" width="7" height="7" rx="1.5" /></>,
    book: <><path d="M5 4h10a3 3 0 0 1 3 3v13H7a2 2 0 0 1-2-2V4z" /><path d="M5 17h13" /></>,
    search: <><circle cx="11" cy="11" r="6" /><path d="M20 20l-4.5-4.5" /></>,
    bell: <><path d="M6 16V11a6 6 0 0 1 12 0v5l2 2H4z" /><path d="M10 20a2 2 0 0 0 4 0" /></>,
    chevR: <path d="M9 6l6 6-6 6" />,
    chevL: <path d="M15 6l-9 6 9 6" />,
    chevD: <path d="M6 9l6 6 6-6" />,
    close: <path d="M6 6l12 12M18 6l-12 12" />,
    plus: <path d="M12 5v14M5 12h14" />,
    check: <path d="M5 12.5l4.5 4.5 10-10" />,
    mic: <><rect x="9" y="3" width="6" height="12" rx="3" /><path d="M5 11a7 7 0 0 0 14 0" /><path d="M12 18v3" /></>,
    camera: <><path d="M4 7h4l2-2h4l2 2h4v12H4z" /><circle cx="12" cy="13" r="3.5" /></>,
    doc: <><path d="M7 3h7l4 4v14H7z" /><path d="M14 3v4h4" /><path d="M10 13h6M10 17h6" /></>,
    upload: <><path d="M12 16V5" /><path d="M7 10l5-5 5 5" /><path d="M5 20h14" /></>,
    rupee: <><path d="M8 6h9" /><path d="M8 10h9" /><path d="M8 6c3 0 5 1.5 5 4s-2 4-5 4h-1l6 6" /></>,
    pin: <><path d="M12 22s6-6.2 6-11a6 6 0 1 0-12 0c0 4.8 6 11 6 11z" /><circle cx="12" cy="11" r="2.2" /></>,
    play: <path d="M8 5v14l12-7z" />,
    pause: <><rect x="7" y="5" width="3.5" height="14" rx="0.8" /><rect x="13.5" y="5" width="3.5" height="14" rx="0.8" /></>,
    record: <circle cx="12" cy="12" r="5" />,
    clock: <><circle cx="12" cy="12" r="8" /><path d="M12 8v4.5l3 2" /></>,
    calendar: <><rect x="4" y="5" width="16" height="15" rx="2" /><path d="M4 10h16" /><path d="M9 3v4M15 3v4" /></>,
    history: <><circle cx="12" cy="12" r="8" /><path d="M12 7v5l3.5 2" /><path d="M4 6l1.5 2.5" /></>,
    filter: <><path d="M4 5h16" /><path d="M7 12h10" /><path d="M10 19h4" /></>,
    more: <><circle cx="6" cy="12" r="1.2" /><circle cx="12" cy="12" r="1.2" /><circle cx="18" cy="12" r="1.2" /></>,
    send: <path d="M4 12l16-7-5 16-4-7z" />,
    star: <path d="M12 4l2.5 5.2 5.5.8-4 4 1 5.6-5-2.8-5 2.8 1-5.6-4-4 5.5-.8z" />,
    mail: <><rect x="3" y="5" width="18" height="14" rx="2" /><path d="M3 6l9 7 9-7" /></>,
    whatsapp: <><path d="M5 19l1.2-3.6a7 7 0 1 1 3.4 3.2z" /><path d="M9 10c.5 2 2 3.5 4 4l1.2-1 2 1.2c0 1-.8 1.8-2 1.8-3 0-6-3-6-6 0-1.2.8-2 1.8-2l1.2 2z" fill="currentColor" stroke="none" /></>,
    training: <><path d="M3 8l9-4 9 4-9 4z" /><path d="M7 10v4c0 1.5 2.2 2.5 5 2.5s5-1 5-2.5v-4" /><path d="M21 8v5" /></>,
    video: <><rect x="3" y="6" width="13" height="12" rx="2" /><path d="M16 10l5-3v10l-5-3z" /></>,
    note: <><path d="M5 4h10l4 4v12H5z" /><path d="M8 10h8M8 14h6" /></>,
    share: <><circle cx="6" cy="12" r="2.2" /><circle cx="17" cy="6" r="2.2" /><circle cx="17" cy="18" r="2.2" /><path d="M8 11l7-4M8 13l7 4" /></>,
    sparkle: <><path d="M12 3l1.6 4.4L18 9l-4.4 1.6L12 15l-1.6-4.4L6 9l4.4-1.6z" /><path d="M19 15l.7 1.8L21.5 17.5l-1.8.7L19 20l-.7-1.8L16.5 17.5l1.8-.7z" /></>,
    flame: <path d="M12 3c1 4 5 5 5 10a5 5 0 1 1-10 0c0-2 1-3 2-4 0 2 1 3 2 3 0-3-1-5 1-9z" />,
    lock:    <><rect x="5" y="11" width="14" height="9" rx="2" /><path d="M8 11V8a4 4 0 0 1 8 0v3" /></>,
    gallery: <><rect x="3" y="6" width="18" height="14" rx="2" /><circle cx="8.5" cy="11" r="1.5" /><path d="M3 17l4.5-4.5 3.5 3.5 3-3L21 17" /></>,
    pdf:     <><path d="M7 3h7l4 4v14H7z" /><path d="M14 3v4h4" /><path d="M9.5 12h1c.8 0 1.5.7 1.5 1.5S11.3 15 10.5 15H9.5z" /><path d="M13 12h1.5a1.5 1.5 0 0 1 0 3H13z" /><path d="M16.5 12v3" /></>,
    eye:     <><path d="M2 12s3-6 10-6 10 6 10 6-3 6-10 6-10-6-10-6z" /><circle cx="12" cy="12" r="2.5" /></>,
    download:<><path d="M12 15V4" /><path d="M7 11l5 5 5-5" /><path d="M4 19h16" /></>,
  };
  const p = paths[name];
  if (!p) return null;
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
      stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round"
      style={{ flex: '0 0 auto' }}>
      {p}
    </svg>
  );
};

// ─── Logo mark ───
const PFLogo = ({ size = 'md' }) => (
  <div className="pf-logo" style={{ padding: '2px 0' }}>
    <img
      src="./skaledesk-logo.png?v=2"
      alt="SkaleDesk"
      style={{
        height: size === 'sm' ? 22 : 28,
        width: 'auto',
        objectFit: 'contain',
        display: 'block',
      }}
    />
  </div>
);

// ─── Bottom tabbar ───
const PFTabBar = ({ active, onChange }) => {
  const tabs = [
    { id: 'home',    icon: 'home',     label: 'Today'   },
    { id: 'leads',   icon: 'leads',    label: 'Leads'   },
    { id: 'calls',   icon: 'phone',    label: 'Calls'   },
    { id: 'assets',  icon: 'gallery',  label: 'Assets'  },
    { id: 'chat',    icon: 'chat',     label: 'Chat' },
  ];
  return (
    <div className="pf-tabbar">
      {tabs.map(t => (
        <button key={t.id} className="pf-tab" data-active={active === t.id} onClick={() => onChange(t.id)}>
          <PFIcon name={t.icon} size={26} strokeWidth={active === t.id ? 2 : 1.6} />
          <div>{t.label}</div>
        </button>
      ))}
    </div>
  );
};

// ─── Screen header ───
const PFHeader = ({ title, subtitle, left, right, eyebrow, padX }) => (
  <div className="pf-h-bar" style={padX != null ? { paddingLeft: padX, paddingRight: padX } : undefined}>
    <div style={{ flex: 1, minWidth: 0 }}>
      {eyebrow && <div className="pf-eyebrow" style={{ marginBottom: 2 }}>{eyebrow}</div>}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        {left}
        <div style={{ minWidth: 0 }}>
          <div className="pf-h-title" style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{title}</div>
          {subtitle && <div className="pf-h-sub">{subtitle}</div>}
        </div>
      </div>
    </div>
    {right}
  </div>
);

const PFBackBtn = ({ onClick }) => (
  <button onClick={onClick} style={{
    width: 36, height: 36, border: '1px solid var(--pf-line)', background: 'var(--pf-surface)',
    borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', padding: 0, color: 'var(--pf-ink)'
  }}>
    <PFIcon name="chevL" size={18} />
  </button>
);

// ADDITION 4: Haversine distance formula — returns kilometres
const haversineKm = (lat1, lng1, lat2, lng2) => {
  const R = 6371;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLng = (lng2 - lng1) * Math.PI / 180;
  const a = Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

/**
 * Makes a screen show real backend data instead of the hardcoded demo seed.
 *
 * PF_DATA ships with demo records (Rohan Mehta and friends) and is only
 * replaced once SRAPI.sync() resolves. PFScreenHome self-healed by kicking
 * off a sync on mount and re-rendering on 'sr:sync', but the Leads list did
 * neither — it read PF_DATA.leads straight through. So any entry point that
 * landed directly on Leads without Home mounting first rendered 8 fabricated
 * leads, and never corrected itself when the real (possibly empty) data
 * arrived, because nothing was listening. Observed on device: Home said
 * "0 active leads" while Leads said "8 of 8" in the same session.
 *
 * Deep links made this much easier to hit — they now land straight on
 * /leads and /lead/{id} instead of collapsing to Home, which had been
 * masking it.
 */
const usePFLiveData = () => {
  const [, bump] = React.useReducer(x => x + 1, 0);
  React.useEffect(() => {
    const onSync = () => bump();
    document.addEventListener('sr:sync', onSync);
    document.addEventListener('sr:task-updated', onSync);
    return () => {
      document.removeEventListener('sr:sync', onSync);
      document.removeEventListener('sr:task-updated', onSync);
    };
  }, []);
  // Don't depend on winning a race against a sync fired elsewhere: if the
  // real data isn't here yet, ask for it. PF_DATA.kpi is only ever set by a
  // successful sync, never by the seed, so it's a reliable "is this real?".
  React.useEffect(() => {
    if (!PF_DATA.kpi && window.SRAPI && window.SRAPI._token && typeof window.SRAPI.sync === 'function') {
      window.SRAPI.sync().catch(() => {});
    }
  }, []);
  return !!(PF_DATA.kpi && PF_DATA.kpi.total_leads !== undefined);
};

/* ── Audio capture settings (call recording, voice notes) ──────────────────
 * Nothing constrained these before: getUserMedia({audio:true}) and
 * new MediaRecorder(stream, {mimeType}) with no bitrate. The UA then picked
 * its own — typically ~128 kbps stereo Opus, about 1 MB per minute, roughly
 * 58 MB for an hour-long call. That is 4-6x more than speech needs, it is
 * uploaded over mobile data, it is billed to the workspace by the GB
 * (storage_price_inr_per_gb), and because it is a browser default it varies
 * by OS version — so the same workspace gets different sizes and an
 * unpredictable bill for identical calls.
 *
 * 32 kbps mono Opus is comfortably intelligible for speech and well above
 * what Whisper needs to transcribe accurately (~0.24 MB/min, ~14 MB/hour).
 * channelCount uses `ideal` rather than an exact value so a device that
 * cannot do mono degrades instead of throwing OverconstrainedError.
 */
const SR_AUDIO_CONSTRAINTS = {
  channelCount: { ideal: 1 },
  echoCancellation: true,
  noiseSuppression: true,
  autoGainControl: true,
};
const SR_AUDIO_BITS_PER_SECOND = 32000;

Object.assign(window, { PFIcon, PFLogo, PFTabBar, PFHeader, PFBackBtn, haversineKm, usePFLiveData,
  SR_AUDIO_CONSTRAINTS, SR_AUDIO_BITS_PER_SECOND });



// ===== 05-pf-data.jsx =====
/* global React */
// Seed data for the prototype

const PF_DATA = {
  user: {
    name: 'Vinay Joshi',
    role: 'Sales Head',
    group: 'Bhawani Group',
    initials: 'VJ',
  },
  today: {
    date: new Date().toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' }),
    calls: { done: 7, target: 18 },
    visits: { done: 2, scheduled: 4 },
    pipeline: '₹4.8 Cr',
    hotLeads: 3,
  },
  tasks: [
    { id: 't1', title: 'Call back Rohan Mehta — re: floor plan questions', due: '10:30 AM', client: 'Rohan Mehta', kind: 'call', done: false },
    { id: 't2', title: 'Upload cheque copy — Neha Kulkarni booking', due: '11:00 AM', client: 'Neha Kulkarni', kind: 'doc', done: false },
    { id: 't3', title: 'Site visit: Aarav Singh @ Whispering Oaks, T-2', due: '1:30 PM', client: 'Aarav Singh', kind: 'visit', done: false },
    { id: 't4', title: 'Send brochure — Meera Iyer (WhatsApp)', due: '3:00 PM', client: 'Meera Iyer', kind: 'doc', done: true },
    { id: 't5', title: 'Profile update: Kunal Shah financial info', due: '4:30 PM', client: 'Kunal Shah', kind: 'form', done: false },
    { id: 't6', title: 'Negotiation ping to manager — Patel family', due: '5:00 PM', client: 'Patel Family', kind: 'chat', done: false },
  ],
  leads: [
    { id: 'l1', name: 'Rohan Mehta', initials: 'RM', phone: '+91 98200 12345', stage: 'Qualified', temp: 'hot', project: 'Whispering Oaks · 3BHK', budget: '₹1.8 – 2.1 Cr', lastTouch: '2h ago · Call', source: 'Facebook Ad', project_coords: { lat: 19.1136, lng: 72.8697, fence_radius_m: 500, address: 'Survey No. 42, Baner Road, Pune 411045' } },
    { id: 'l2', name: 'Neha Kulkarni', initials: 'NK', phone: '+91 99300 44556', stage: 'Booked', temp: 'won', project: 'Meadowview · 2BHK', budget: '₹1.2 Cr', lastTouch: 'Today · WhatsApp', source: '99acres' },
    { id: 'l3', name: 'Aarav Singh', initials: 'AS', phone: '+91 90045 11223', stage: 'Site Visit', temp: 'warm', project: 'Whispering Oaks · 4BHK', budget: '₹3.0 – 3.5 Cr', lastTouch: 'Yesterday', source: 'Referral', project_coords: { lat: 19.1136, lng: 72.8697, fence_radius_m: 500, address: 'Survey No. 42, Baner Road, Pune 411045' } },
    { id: 'l4', name: 'Meera Iyer', initials: 'MI', phone: '+91 87665 99887', stage: 'New Lead', temp: 'warm', project: 'Rivercrest · 2BHK', budget: '₹90 L – 1.1 Cr', lastTouch: '3d ago · Missed call', source: 'Walk-in' },
    { id: 'l5', name: 'Kunal Shah', initials: 'KS', phone: '+91 98200 77889', stage: 'Negotiation', temp: 'hot', project: 'Whispering Oaks · 3BHK', budget: '₹1.9 – 2.2 Cr', lastTouch: '4h ago · Call', source: 'Referral' },
    { id: 'l6', name: 'Patel Family', initials: 'PF', phone: '+91 99200 33344', stage: 'Negotiation', temp: 'hot', project: 'Meadowview · 3BHK', budget: '₹2.1 Cr', lastTouch: 'Yesterday', source: 'Walk-in' },
    { id: 'l7', name: 'Rhea Kapoor', initials: 'RK', phone: '+91 90087 22113', stage: 'New Lead', temp: 'cold', project: 'Rivercrest · 1BHK', budget: '₹60 – 75 L', lastTouch: '5d ago', source: 'MagicBricks' },
    { id: 'l8', name: 'Sameer Joshi', initials: 'SJ', phone: '+91 98900 65432', stage: 'Qualified', temp: 'warm', project: 'Meadowview · 2BHK', budget: '₹1.1 – 1.3 Cr', lastTouch: '1d ago', source: 'Facebook Ad' },
  ],
  timeline: [
    { id: 'e1', when: '10:12 AM', type: 'call', title: 'Call · 6m 42s', text: 'Discussed east-facing units, parking allotment. Wants floor plan by EOD.' },
    { id: 'e2', when: 'Yesterday', type: 'visit', title: 'Site visit · Whispering Oaks', text: 'Walked T-2, 14th floor. Liked the view; asked about amenities.' },
    { id: 'e3', when: 'Yesterday', type: 'doc', title: 'Brochure sent via WhatsApp', text: 'Whispering Oaks 3BHK master plan + pricing sheet.' },
    { id: 'e4', when: '22 Apr', type: 'note', title: 'Profile updated', text: 'Added PAN, employer (Infosys), family size: 4.' },
    { id: 'e5', when: '20 Apr', type: 'call', title: 'Call · 3m 10s', text: 'Introductory call. Qualified budget ₹1.8–2.1 Cr, ready in 60d.' },
  ],
  scripts: [
    {
      id: 's1',
      title: 'Cold intro — inbound lead',
      hinglish: 'Namaste {leadName} ji, main {agentName} bol raha hoon Bhawani Group se. Aapne kal hamari Whispering Oaks ki enquiry ki thi — is conversation ke liye 2 minute hai aapke paas?',
      english: 'Hello {leadName}, this is {agentName} from Bhawani Group. You enquired about Whispering Oaks yesterday — do you have 2 minutes for this conversation?',
    },
    {
      id: 's2',
      title: 'Objection: "Price is too high"',
      hinglish: 'Main samajh sakta hoon. Is area mein similar 3BHK ka rate ₹12,500/sqft chal raha hai — hamari pricing usse 8% kam hai, plus 2 saal ka maintenance free. Kya main aapko comparison sheet bhej doon?',
      english: 'I understand completely. Similar 3BHKs in this area are at ₹12,500/sqft — our pricing is 8% below that, plus 2 years of free maintenance. May I send you a comparison sheet?',
    },
    {
      id: 's3',
      title: 'Site visit scheduling',
      hinglish: 'Weekend mein Saturday ya Sunday — aap ke liye kab convenient hai? Hum cab arrange kar denge, aur sample flat mein chai ke saath 30 min ki walkthrough hogi.',
      english: 'Weekends — Saturday or Sunday, which works better? We\'ll arrange a cab, and you\'ll get a 30-min walkthrough of the sample flat over chai.',
    },
    {
      id: 's4',
      title: 'Close — push for booking',
      hinglish: 'Is unit pe abhi sirf 2 flats bache hain iss floor pe — aur next month pricing revise ho rahi hai. Agar aap aaj token book kar dete hain toh main current rate lock kar sakta hoon.',
      english: 'There are only 2 flats left on this floor, and pricing is revised next month. If you book a token today, I can lock in the current rate for you.',
    },
  ],
  training: [
    { id: 'tr1', title: 'Handling objections — Advanced', kind: 'video', duration: '18 min', progress: 0.65, tag: 'Core' },
    { id: 'tr2', title: 'Whispering Oaks — master plan walkthrough', kind: 'video', duration: '12 min', progress: 0.0, tag: 'Project' },
    { id: 'tr3', title: 'RERA compliance basics', kind: 'doc', duration: '8 min read', progress: 1.0, tag: 'Compliance' },
    { id: 'tr4', title: 'Closing techniques for HNI buyers', kind: 'video', duration: '24 min', progress: 0.3, tag: 'Core' },
    { id: 'tr5', title: 'Home loan — partner banks cheat sheet', kind: 'doc', duration: '5 min read', progress: 0.0, tag: 'Reference' },
  ],
  teleTargets: {
    day: { svBooked: 2, svTarget: 5, calls: 7, callTarget: 18, talkTime: '42m', conn: 71, qualified: 4 },
    week: { svBooked: 11, svTarget: 22, calls: 64, callTarget: 90, talkTime: '5h 12m', conn: 68, qualified: 18 },
    streak: { current: 6, best: 11, unit: 'days hitting SV target', fireDays: [true, true, true, true, true, true, false] },
    leaderboard: [
      { rank: 1, name: 'Priya Rao', sv: 4, you: false },
      { rank: 2, name: 'You', sv: 2, you: true },
      { rank: 3, name: 'Arjun Mehra', sv: 2, you: false },
      { rank: 4, name: 'Kavya Nair', sv: 1, you: false },
    ],
  },
  templates: [
    { id: 'tm1', kind: 'brochure', title: 'Whispering Oaks · 3BHK brochure', sub: 'PDF · 4.2 MB · Master plan + pricing', icon: 'doc' },
    { id: 'tm2', kind: 'brochure', title: 'Meadowview · 2BHK brochure', sub: 'PDF · 3.1 MB · Floor plans', icon: 'doc' },
    { id: 'tm3', kind: 'brochure', title: 'Rivercrest · All configs', sub: 'PDF · 5.8 MB · Full project deck', icon: 'doc' },
    { id: 'tm4', kind: 'msg', title: 'Site visit confirmation', sub: 'Namaste {name}, aap ka site visit {date} ko confirm hua hai…', icon: 'chat' },
    { id: 'tm5', kind: 'msg', title: 'Post-call follow up', sub: 'Hi {name}, thank you for the call. Sharing brochure and pricing…', icon: 'chat' },
    { id: 'tm6', kind: 'msg', title: 'Price justification', sub: 'Comparing {project} with nearby launches — our rate is 8% below…', icon: 'chat' },
    { id: 'tm7', kind: 'msg', title: 'Loan assistance offer', sub: 'We have tie-ups with HDFC, SBI, ICICI — pre-approval in 48 hrs…', icon: 'chat' },
    { id: 'tm8', kind: 'msg', title: 'Token booking reminder', sub: 'Unit {unit} is on hold till {time}. Shall I lock it in for you?', icon: 'chat' },
  ],
  callHistory: [
    { id: 'c1', who: 'Rohan Mehta', when: 'Today · 10:12 AM', duration: '6m 42s', direction: 'out', disposition: 'Interested', recorded: true },
    { id: 'c2', who: 'Kunal Shah', when: 'Today · 09:30 AM', duration: '11m 05s', direction: 'out', disposition: 'Negotiating', recorded: true },
    { id: 'c3', who: 'Meera Iyer', when: 'Today · 08:45 AM', duration: '0m 22s', direction: 'out', disposition: 'No Answer', recorded: false },
    { id: 'c4', who: 'Aarav Singh', when: 'Yesterday · 6:18 PM', duration: '4m 12s', direction: 'in', disposition: 'Site Visit Booked', recorded: true },
    { id: 'c5', who: 'Sameer Joshi', when: 'Yesterday · 3:40 PM', duration: '2m 58s', direction: 'out', disposition: 'Callback later', recorded: true },
  ],
};

// Keys are the literal stage strings the backend stores in leads.stage
// (PIPELINE_STAGES in backend/src/server.js + the full realty pipeline in
// frontend/.../leads/page.tsx's STAGES) — not a shorthand. This map used to
// cover only 5 of the 12 real stages and keyed the site-visit one as bare
// 'Site Visit' (which the backend never actually stores), so a lead in any
// other real stage — including the two the backend DOES use, 'Site Visit
// Scheduled' and 'Site Visit Done' — fell through to the unstyled neutral
// chip. 'Site Visit' is kept too, mapped the same as 'Site Visit Scheduled',
// for any lead already written with the old wrong value before this fix.
const PF_STAGES = {
  'New Lead': { color: 'blue', label: 'New' },
  'Contacted': { color: 'blue', label: 'Contacted' },
  'Qualified': { color: 'green', label: 'Qualified' },
  'Site Visit Scheduled': { color: 'amber', label: 'Site Visit' },
  'Site Visit': { color: 'amber', label: 'Site Visit' },
  'Site Visit Done': { color: 'amber', label: 'Visit Done' },
  'Negotiation': { color: 'amber', label: 'Negotiation' },
  'Booked': { color: 'green', label: 'Booked' },
  'Loan Process': { color: 'amber', label: 'Loan Process' },
  'Agreement': { color: 'green', label: 'Agreement' },
  'Registration': { color: 'green', label: 'Registration' },
  'Possession / Handover': { color: 'green', label: 'Possession' },
  'Lost': { color: 'red', label: 'Lost' },
};

const PF_TEMP = {
  hot: { label: '🔥 Hot', klass: 'pf-chip-red' },
  warm: { label: 'Warm', klass: 'pf-chip-amber' },
  cold: { label: 'Cold', klass: 'pf-chip-blue' },
  won: { label: 'Won', klass: 'pf-chip' },
};

Object.assign(window, { PF_DATA, PF_STAGES, PF_TEMP });



// ===== 06-pf-screens-1.jsx =====
/* global React, PFIcon, PFHeader, PFLogo, PF_DATA, PF_STAGES, PF_TEMP */

// ─────────────────────────────────────────────────────────────
// HOME / TODAY
// ─────────────────────────────────────────────────────────────
// ── Notification panel ───────────────────────────────────────────────────────
const NOTIF_SEED = [
  { id:'n1', icon:'🔥', title:'Rohan Mehta — follow-up overdue',   sub:'Scheduled for 10:30 AM · not done yet',   time:'2m ago',  unread:true  },
  { id:'n2', icon:'📍', title:'Site visit confirmed',               sub:'Aarav Singh @ Whispering Oaks · 1:30 PM', time:'18m ago', unread:true  },
  { id:'n3', icon:'💬', title:'Manager replied',                    sub:'Ankit: "Go to 5% + 2 parking if he books today"', time:'1h ago', unread:true },
  { id:'n4', icon:'🏆', title:'Streak: 6 days hitting SV target',   sub:'Keep it up — best is 11 days',            time:'3h ago',  unread:false },
  { id:'n5', icon:'📞', title:'Missed call — Meera Iyer',           sub:'+91 87665 99887 · called at 8:45 AM',     time:'4h ago',  unread:false },
  { id:'n6', icon:'✅', title:'Neha Kulkarni booking confirmed',     sub:'Meadowview 2BHK · ₹1.2 Cr token received', time:'Yesterday', unread:false },
];

// Real notification records (migration 151) have a `type`, not an icon —
// map to the same emoji style NOTIF_SEED used for visual continuity.
const NOTIF_TYPE_ICON = {
  followup_overdue: '🔥', site_visit_booked: '📍', manager_reply: '💬',
  chat_message: '💬', missed_call: '📞', booking_confirmed: '✅',
  streak_milestone: '🏆',
};
const timeAgo = (iso) => {
  const diffMs = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return days === 1 ? 'Yesterday' : `${days}d ago`;
};

const NotifSheet = ({ onClose }) => {
  const hasToken = !!(window.SRAPI && window.SRAPI._token);
  const [notifs, setNotifs] = React.useState(hasToken ? [] : NOTIF_SEED);
  const [loading, setLoading] = React.useState(hasToken);

  React.useEffect(() => {
    if (!hasToken) return;
    window.SRAPI._get('/api/notifications?limit=30')
      .then(res => {
        setNotifs((res.notifications || []).map(n => ({
          id: n.id, icon: NOTIF_TYPE_ICON[n.type] || '🔔',
          title: n.title, sub: n.body || '', time: timeAgo(n.createdAt), unread: !n.read,
        })));
      })
      .catch(() => { setNotifs([]); })
      .finally(() => setLoading(false));
  }, [hasToken]);

  const unreadCount = notifs.filter(n => n.unread).length;
  const markAll = () => {
    setNotifs(ns => ns.map(n => ({ ...n, unread: false })));
    if (hasToken) window.SRAPI._patch('/api/notifications/read-all').catch(() => {});
  };
  const markOne = (id) => {
    setNotifs(ns => ns.map(x => x.id === id ? { ...x, unread: false } : x));
    if (hasToken) window.SRAPI._patch('/api/notifications/' + id + '/read').catch(() => {});
  };
  return (
    <div style={{ position:'fixed', inset:0, zIndex:200, display:'flex', flexDirection:'column', justifyContent:'flex-end' }}>
      <div style={{ position:'absolute', inset:0, background:'rgba(0,0,0,0.45)' }} onClick={onClose} />
      <div style={{ position:'relative', background:'var(--pf-surface)', borderRadius:'20px 20px 0 0', maxHeight:'78vh', display:'flex', flexDirection:'column' }}>
        {/* Handle */}
        <div style={{ width:36, height:4, background:'var(--pf-line)', borderRadius:2, margin:'14px auto 0' }} />
        {/* Header */}
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'12px 16px 8px' }}>
          <div style={{ display:'flex', alignItems:'center', gap:8 }}>
            <span style={{ fontWeight:700, fontSize:16, color:'var(--pf-ink)' }}>Notifications</span>
            {unreadCount > 0 && (
              <span style={{ background:'#ef4444', color:'#fff', fontSize:10, fontWeight:800, padding:'2px 7px', borderRadius:99 }}>{unreadCount}</span>
            )}
          </div>
          {unreadCount > 0 && (
            <button onClick={markAll} style={{ background:'none', border:'none', color:'var(--pf-green)', fontSize:12.5, fontWeight:700, cursor:'pointer' }}>Mark all read</button>
          )}
        </div>
        {/* List */}
        <div style={{ overflowY:'auto', padding:'4px 0 28px' }}>
          {loading && (
            <div style={{ padding:'30px 16px', textAlign:'center', color:'var(--pf-ink-3)', fontSize:13 }}>Loading…</div>
          )}
          {!loading && notifs.length === 0 && (
            <div style={{ padding:'30px 16px', textAlign:'center', color:'var(--pf-ink-3)', fontSize:13 }}>No notifications yet</div>
          )}
          {!loading && notifs.map(n => (
            <div key={n.id} onClick={() => markOne(n.id)}
              style={{ display:'flex', gap:12, padding:'11px 16px', background: n.unread ? 'var(--pf-green-tint)' : 'transparent', cursor:'pointer', borderBottom:'1px solid var(--pf-line)' }}>
              <div style={{ fontSize:22, flexShrink:0, width:36, height:36, borderRadius:10, background:'var(--pf-bg)', display:'flex', alignItems:'center', justifyContent:'center' }}>{n.icon}</div>
              <div style={{ flex:1, minWidth:0 }}>
                <div style={{ fontSize:13.5, fontWeight: n.unread ? 700 : 500, color:'var(--pf-ink)', lineHeight:1.3 }}>{n.title}</div>
                <div style={{ fontSize:11.5, color:'var(--pf-ink-3)', marginTop:2, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{n.sub}</div>
                <div style={{ fontSize:10.5, color:'var(--pf-ink-4)', marginTop:3 }}>{n.time}</div>
              </div>
              {n.unread && <div style={{ width:8, height:8, borderRadius:'50%', background:'var(--pf-green)', flexShrink:0, marginTop:5 }} />}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const INDUSTRY_OPTIONS = [
  { id: 'realty',        label: 'SkaleRealty',      icon: '🏠', role: 'Sales Head' },
  { id: 'skalecap',     label: 'SkaleCap ILIS',    icon: '🧠', role: 'L&D Manager' },
  { id: 'healthcare',   label: 'SkaleHealth',       icon: '🏥', role: 'Manager' },
  { id: 'dermatology',  label: 'SkaleDerma',        icon: '💊', role: 'Manager' },
  { id: 'manufacturing',label: 'SkaleMFG',          icon: '🏭', role: 'Manager' },
];

const ProfileSheet = ({ user, onClose, go }) => {
  const [editing, setEditing] = React.useState(false);
  const [form, setForm]       = React.useState({ name: user.name || '', phone: '', email: '' });
  const [saving, setSaving]   = React.useState(false);
  const [saveErr, setSaveErr] = React.useState('');

  async function saveProfile() {
    setSaving(true); setSaveErr('');
    try {
      if (window.SRAPI && window.SRAPI._token) {
        await window.SRAPI._patch('/api/auth/me', {
          name:  form.name.trim(),
          phone: form.phone.trim() || undefined,
          email: form.email.trim() || undefined,
        });
      }
      // Update local DEFAULTS so header reflects new name immediately
      if (form.name.trim()) {
        window.__SR_CONFIG = Object.assign(window.__SR_CONFIG || {}, { displayName: form.name.trim() });
        document.dispatchEvent(new CustomEvent('sr:profile-updated', { detail: { name: form.name.trim() } }));
      }
      setEditing(false);
    } catch (e) {
      setSaveErr(e.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  const sheetStyle = {
    position: 'fixed', inset: 0, zIndex: 300, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end',
  };
  const overlayStyle = {
    position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.45)',
  };
  const bodyStyle = {
    position: 'relative', background: 'var(--pf-surface)', borderRadius: '20px 20px 0 0',
    padding: '20px 16px 32px', display: 'flex', flexDirection: 'column', gap: 0,
  };
  const inputStyle = {
    width: '100%', padding: '9px 12px', borderRadius: 10, border: '1.5px solid var(--pf-line)',
    background: 'var(--pf-bg)', color: 'var(--pf-ink)', fontSize: 14, fontFamily: 'inherit',
    marginBottom: 8, outline: 'none',
  };

  return (
    <div style={sheetStyle}>
      <div style={overlayStyle} onClick={onClose} />
      <div style={bodyStyle}>
        <div style={{ width: 36, height: 4, background: 'var(--pf-line)', borderRadius: 2, margin: '0 auto 18px' }} />

        {/* User card */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16, padding: '0 4px' }}>
          <div className="pf-avatar" style={{ width: 48, height: 48, fontSize: 16, flexShrink: 0 }}>{user.initials}</div>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 700, fontSize: 15, color: 'var(--pf-ink)' }}>{user.name}</div>
            <div style={{ fontSize: 12.5, color: 'var(--pf-ink-2)', marginTop: 2 }}>{user.role} · {user.group}</div>
          </div>
          <button
            onClick={() => setEditing(e => !e)}
            style={{ background: 'var(--pf-bg)', border: '1px solid var(--pf-line)', borderRadius: 8, padding: '5px 10px', fontSize: 12, cursor: 'pointer', color: 'var(--pf-ink-2)', fontFamily: 'inherit' }}
          >{editing ? 'Cancel' : 'Edit'}</button>
        </div>

        {/* Inline edit form */}
        {editing && (
          <div style={{ marginBottom: 12 }}>
            <input
              style={inputStyle}
              placeholder="Display name"
              value={form.name}
              onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
            />
            <input
              style={inputStyle}
              placeholder="Phone (optional)"
              value={form.phone}
              onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
              inputMode="tel"
            />
            <input
              style={inputStyle}
              placeholder="Email (optional)"
              value={form.email}
              onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
              inputMode="email"
            />
            {saveErr && <div style={{ color: '#dc2626', fontSize: 12, marginBottom: 6 }}>{saveErr}</div>}
            <button
              onClick={saveProfile}
              disabled={saving}
              style={{
                width: '100%', padding: '10px', borderRadius: 10, border: 0,
                background: 'var(--pf-green)', color: '#fff', fontWeight: 600, fontSize: 14,
                cursor: saving ? 'default' : 'pointer', fontFamily: 'inherit', opacity: saving ? 0.7 : 1,
              }}
            >{saving ? 'Saving…' : 'Save profile'}</button>
          </div>
        )}

        {/* Industry switcher — web/browser only. Native fixes one industry
            per login (skaledesk-native/App.tsx → session.industry) and each
            RootTabs tab is a separately-loaded WebView for a FIXED path;
            native never listens for this sr:switch-industry event, so
            firing it here only swaps content inside the one WebView tab
            that's currently focused, orphaning it from the native tab bar
            (still showing the original industry) and every other tab. */}
        {!IS_NATIVE_APP && (
          <>
            <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8, color: 'var(--pf-ink-3)', marginBottom: 8, paddingLeft: 4 }}>Switch Industry</div>
            {INDUSTRY_OPTIONS.map(opt => (
              <button key={opt.id}
                onClick={() => {
                  document.dispatchEvent(new CustomEvent('sr:switch-industry', { detail: { industry: opt.id, role: opt.role } }));
                  onClose();
                }}
                style={{
                  display: 'flex', alignItems: 'center', gap: 12, width: '100%', background: 'transparent',
                  border: 'none', borderRadius: 12, padding: '11px 12px', cursor: 'pointer', textAlign: 'left',
                  marginBottom: 2,
                }}
              >
                <span style={{ fontSize: 22, width: 32, textAlign: 'center' }}>{opt.icon}</span>
                <span style={{ fontSize: 14, fontWeight: 500, color: 'var(--pf-ink)' }}>{opt.label}</span>
              </button>
            ))}
            <div style={{ height: 1, background: 'var(--pf-line)', margin: '12px 0' }} />
          </>
        )}
        {/* Voice credits — admins/owners only (the server enforces the real
            boundary; this just hides the entry from people who can't use it). */}
        {go && canManageCredits() && (
          <button
            onClick={() => { onClose(); go('credits'); }}
            style={{
              display: 'flex', alignItems: 'center', gap: 12, width: '100%', background: 'transparent',
              border: 'none', borderRadius: 12, padding: '11px 12px', cursor: 'pointer', textAlign: 'left', color: 'var(--pf-ink)',
            }}
          >
            <PFIcon name="rupee" size={20} />
            <span style={{ fontSize: 14, fontWeight: 500 }}>Voice credits</span>
          </button>
        )}
        {/* Logout */}
        <button
          onClick={() => { document.dispatchEvent(new CustomEvent('sr:logout')); onClose(); }}
          style={{
            display: 'flex', alignItems: 'center', gap: 12, width: '100%', background: 'transparent',
            border: 'none', borderRadius: 12, padding: '11px 12px', cursor: 'pointer', textAlign: 'left', color: '#dc2626',
          }}
        >
          <PFIcon name="logout" size={20} />
          <span style={{ fontSize: 14, fontWeight: 500 }}>Sign out</span>
        </button>
      </div>
    </div>
  );
};

const PFScreenHome = ({ go, user }) => {
  const [profileOpen, setProfileOpen] = React.useState(false);
  const [bellOpen,    setBellOpen]    = React.useState(false);
  // Merge live TASK_STORE overlay so done/snoozed/reassign reflect here too.
  // Also re-render on sr:sync — SRAPI.sync() (called right after login)
  // replaces PF_DATA.leads/tasks/kpi with real data, but nothing was ever
  // listening for that here, so this screen kept showing whatever the
  // seed data looked like at first mount regardless of what arrived after.
  const [taskTick, setTaskTick] = React.useState(0);
  React.useEffect(() => {
    const onUpdate = () => setTaskTick(t => t + 1);
    document.addEventListener('sr:task-updated', onUpdate);
    document.addEventListener('sr:sync', onUpdate);
    return () => {
      document.removeEventListener('sr:task-updated', onUpdate);
      document.removeEventListener('sr:sync', onUpdate);
    };
  }, []);
  // Self-heal instead of only passively listening for sr:sync: that event
  // only fires if this screen's listener above happened to already be
  // attached by the time SRAPI.sync() (called once, right after login)
  // resolved. If Home mounts even slightly after that resolves — a very
  // plausible race, not a rare one — the event is gone forever and this
  // screen is stuck rendering the hardcoded demo seed for the rest of the
  // session, no matter what real data exists. Kick off a sync directly on
  // mount whenever PF_DATA.kpi isn't already populated, so Home never
  // depends on winning a timing race against another part of the app.
  React.useEffect(() => {
    if (!PF_DATA.kpi && window.SRAPI && window.SRAPI._token && typeof window.SRAPI.sync === 'function') {
      window.SRAPI.sync().catch(() => {});
    }
  }, []);
  const liveTasks = PF_DATA.tasks.map(t => Object.assign({}, t, (window.TASK_STORE || {})[t.id] || {}));

  // PF_DATA.kpi is only ever set by a successful SRAPI.sync() — never
  // present in the hardcoded seed — so its presence is a reliable signal
  // that PF_DATA.leads now holds real backend leads (numeric budget, `hot`
  // boolean) rather than the demo shape (string budget range, `temp`
  // string). The hero stats below were always the fixed seed literals in
  // PF_DATA.today regardless of this; now computed from real data once
  // it's actually there.
  const rawLeads = PF_DATA.leads;
  const hasLiveData = !!(PF_DATA.kpi && PF_DATA.kpi.total_leads !== undefined);

  const adaptLead = (l) => {
    if (l.temp !== undefined) return l; // already demo-shaped, pass through
    const initials = (l.name || '').split(' ').filter(Boolean).slice(0, 2).map(w => w[0]).join('').toUpperCase() || '?';
    const budgetNum = Number(l.budget) || 0;
    const budgetStr = budgetNum ? `₹${(budgetNum / 10000000).toFixed(2).replace(/\.?0+$/, '')} Cr` : '—';
    const lastTouch = l.lastCall
      ? new Date(l.lastCall).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })
      : (l.nextFollowup ? 'Follow-up due' : '—');
    return Object.assign({}, l, { initials, temp: l.hot ? 'hot' : 'warm', budget: budgetStr, lastTouch });
  };
  const liveLeads = rawLeads.map(adaptLead);

  const activeLeads = hasLiveData ? rawLeads.filter(l => !['Lost', 'Booked'].includes(l.stage)) : null;
  const pipelineDisplay = hasLiveData
    ? `₹${(activeLeads.reduce((sum, l) => sum + (Number(l.budget) || 0), 0) / 10000000).toFixed(2).replace(/\.?0+$/, '')} Cr`
    : PF_DATA.today.pipeline;
  const activeLeadsCount = hasLiveData ? activeLeads.length : rawLeads.length;
  const hotLeadsCount = hasLiveData ? rawLeads.filter(l => l.hot).length : PF_DATA.today.hotLeads;
  // "Today" counts are real once synced (see mobile.js /sync calls_today/
  // visits_today); no real per-agent daily quota system exists to compute
  // an honest target denominator, so live mode shows the count alone
  // rather than inventing one the way the seed's "/18" and "/4" did.
  const callsDisplay  = hasLiveData ? String(PF_DATA.kpi.calls_today  ?? 0) : `${PF_DATA.today.calls.done}/${PF_DATA.today.calls.target}`;
  const visitsDisplay = hasLiveData ? String(PF_DATA.kpi.visits_today ?? 0) : `${PF_DATA.today.visits.done}/${PF_DATA.today.visits.scheduled}`;

  const d = Object.assign({}, PF_DATA, { tasks: liveTasks, leads: liveLeads });
  const _hr = new Date().getHours();
  const _greeting = _hr < 12 ? 'GOOD MORNING' : _hr < 17 ? 'GOOD AFTERNOON' : 'GOOD EVENING';
  return (
    <div className="pf-screen pf-anim-in">
      <PFHeader
        padX={30}
        eyebrow={`${_greeting} · ${d.today.date.toUpperCase()}`}
        title={user.name}
        subtitle={`${user.role} · ${user.group}`}
        right={
          <div style={{ display: 'flex', gap: 8 }}>
            <button style={iconBtn} onClick={() => setBellOpen(true)}><PFIcon name="bell" size={18} /></button>
            <button
              onClick={() => {
                // iOS's native wrapper has its own richer "More" screen
                // (profile + support + legal + sign-out) reachable via a
                // bridge message instead of this narrower web-only
                // profile-edit sheet — see OPEN_MORE in SyncBridge.ts.
                // Everywhere else (Android, plain browser) keeps this.
                if (IS_NATIVE_IOS && window.ReactNativeWebView) {
                  window.ReactNativeWebView.postMessage(JSON.stringify({ type: 'OPEN_MORE' }));
                } else {
                  setProfileOpen(true);
                }
              }}
              style={{ ...iconBtn, padding: 0, overflow: 'hidden', borderRadius: '50%' }}
            >
              <div className="pf-avatar" style={{ width: 36, height: 36, fontSize: 13 }}>{user.initials}</div>
            </button>
          </div>
        }
      />
      {profileOpen && <ProfileSheet user={user} go={go} onClose={() => setProfileOpen(false)} />}
      {bellOpen && <NotifSheet onClose={() => setBellOpen(false)} />}
      <div className="pf-scroll" style={{ padding: '0 16px 20px' }}>
        <PFCreditsBanner go={go} />
        {/* Hero KPI */}
        <div style={{
          background: 'linear-gradient(180deg, #0f5132 0%, #0b3a24 100%)',
          color: '#fff', borderRadius: 20, padding: 18, marginBottom: 14,
          position: 'relative', overflow: 'hidden',
        }}>
          <div style={{
            position: 'absolute', right: -40, top: -40, width: 160, height: 160, borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(255,255,255,0.18), transparent 70%)',
          }} />
          <div style={{ fontSize: 10.5, letterSpacing: '0.14em', opacity: 0.7, fontWeight: 600 }}>TODAY'S PIPELINE</div>
          <div style={{ fontFamily: 'var(--pf-serif)', fontSize: 34, fontWeight: 600, marginTop: 4, letterSpacing: '-0.01em' }}>
            {pipelineDisplay}
          </div>
          <div style={{ fontSize: 12.5, opacity: 0.8, marginTop: 2 }}>across {activeLeadsCount} active leads</div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, marginTop: 16 }}>
            <HeroStat label="Calls" val={callsDisplay} />
            <HeroStat label="Visits" val={visitsDisplay} />
            <HeroStat label="Hot leads" val={hotLeadsCount} />
          </div>
        </div>

        {/* Quick actions */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 6, marginBottom: 16 }}>
          <QuickAction icon="phone" label="Call" onClick={() => go('calls')} />
          <QuickAction icon="pin" label="Site visit" onClick={() => { const id = (PF_DATA.leads[0] || {}).id; if (id) go('siteVisit', id); else go('leads'); }} />
          <QuickAction icon="doc" label="Profile" onClick={() => { const id = (PF_DATA.leads[0] || {}).id; if (id) go('profiling', id); else go('leads'); }} />
          <QuickAction icon="camera" label="Evidence" onClick={() => { const id = (PF_DATA.leads[0] || {}).id; if (id) go('evidence', id); else go('leads'); }} />
          <QuickAction icon="check" label="Overview" onClick={() => go('overview')} />
        </div>

        {/* Today's tasks */}
        <SectionTitle
          title="Today's tasks"
          right={<button onClick={() => go('tasks')} style={linkBtn}>View all ({d.tasks.filter(t => !t.done).length})</button>}
        />
        <div>
          {d.tasks.length === 0 ? (
            <div style={{ padding: '20px 4px', textAlign: 'center', color: 'var(--pf-ink-4)', fontSize: 12.5 }}>No tasks for today.</div>
          ) : (
            // Open tasks first (stable order preserved within each group) —
            // this preview used to show whatever the first 3 items in raw
            // order happened to be, so a completed task could sit above an
            // untouched one with no way to tell at a glance what still
            // needs doing.
            [...d.tasks].sort((a, b) => (a.done ? 1 : 0) - (b.done ? 1 : 0)).slice(0, 3).map(t => <TaskMini key={t.id} task={t} go={go} />)
          )}
        </div>

        {/* Hot leads */}
        <SectionTitle
          title="Follow up today"
          right={<button onClick={() => go('leads')} style={linkBtn}>All leads</button>}
          style={{ marginTop: 18 }}
        />
        <div className="pf-card" style={{ padding: 0, overflow: 'hidden' }}>
          {d.leads.filter(l => l.temp === 'hot').length === 0 ? (
            <div style={{ padding: 20, textAlign: 'center', color: 'var(--pf-ink-4)', fontSize: 12.5 }}>No hot leads need follow-up right now.</div>
          ) : (
            d.leads.filter(l => l.temp === 'hot').map(l => (
              <LeadRow key={l.id} lead={l} onClick={() => go('lead', l.id)} />
            ))
          )}
        </div>

        {/* Learning nudge — no real training-progress backend exists, so
            this stays a demo-mode-only prototype preview rather than
            showing a fabricated "65% complete" to a real logged-in user. */}
        {!hasLiveData && (
        <div style={{ marginTop: 18 }}>
          <div className="pf-card-tinted" onClick={() => go('learn')} style={{ cursor: 'pointer' }}>
            <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
              <div style={{
                width: 42, height: 42, borderRadius: 12, background: 'var(--pf-green)', color: '#fff',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <PFIcon name="sparkle" size={22} color="#fff" />
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 11, letterSpacing: '0.1em', color: 'var(--pf-green)', fontWeight: 600 }}>PICK UP WHERE YOU LEFT OFF</div>
                <div style={{ fontFamily: 'var(--pf-serif)', fontSize: 16, fontWeight: 600, marginTop: 2 }}>Handling objections — Advanced</div>
                <div style={{ fontSize: 11.5, color: 'var(--pf-ink-3)', marginTop: 2 }}>6 min left · 65% complete</div>
              </div>
              <PFIcon name="chevR" size={18} color="var(--pf-green)" />
            </div>
          </div>
        </div>
        )}

        <div style={{ height: 20 }} />
      </div>
    </div>
  );
};

const HeroStat = ({ label, val }) => (
  <div>
    <div style={{ fontSize: 10.5, letterSpacing: '0.1em', opacity: 0.7, fontWeight: 600 }}>{label.toUpperCase()}</div>
    <div style={{ fontFamily: 'var(--pf-serif)', fontSize: 22, fontWeight: 600, marginTop: 2 }}>{val}</div>
  </div>
);

const QuickAction = ({ icon, label, onClick }) => (
  <button onClick={onClick} style={{
    background: 'var(--pf-surface)', border: '1px solid var(--pf-line)',
    borderRadius: 12, padding: '10px 4px', display: 'flex', flexDirection: 'column',
    alignItems: 'center', gap: 5, cursor: 'pointer', color: 'var(--pf-ink)', fontFamily: 'inherit', width: '100%',
  }}>
    <div style={{
      width: 32, height: 32, borderRadius: 10, background: 'var(--pf-green-tint)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--pf-green)',
    }}>
      <PFIcon name={icon} size={18} color="var(--pf-green)" />
    </div>
    <div style={{ fontSize: 11, fontWeight: 500 }}>{label}</div>
  </button>
);

const SectionTitle = ({ title, right, style }) => (
  <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 8, ...style }}>
    <div style={{ fontFamily: 'var(--pf-serif)', fontSize: 17, fontWeight: 600, letterSpacing: '-0.005em' }}>{title}</div>
    {right}
  </div>
);

const iconBtn = {
  width: 36, height: 36, borderRadius: '50%',
  background: 'var(--pf-surface)', border: '1px solid var(--pf-line)',
  display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
  color: 'var(--pf-ink-2)', padding: 0,
};

const linkBtn = {
  background: 'transparent', border: 0, fontFamily: 'inherit',
  fontSize: 12.5, color: 'var(--pf-green)', fontWeight: 600, cursor: 'pointer', padding: 0,
};

const TaskMini = ({ task, go }) => (
  <div className="pf-task" data-done={task.done} onClick={() => go('taskDetail', task.id)}>
    <div className="pf-check" onClick={e => {
      e.stopPropagation();
      const next = !task.done;
      if (typeof window.patchTask === 'function') {
        window.patchTask(task.id, { done: next, completedAt: next ? new Date().toISOString() : null });
      }
    }}>
      {task.done && <PFIcon name="check" size={14} color="#fff" strokeWidth={2.5} />}
    </div>
    <div style={{ flex: 1, minWidth: 0 }}>
      <div className="pf-task-title">{task.title}</div>
      <div className="pf-task-meta">
        <PFIcon name="clock" size={11} /> &nbsp;{task.due} · {task.client}
      </div>
    </div>
  </div>
);

const LeadRow = ({ lead, onClick }) => {
  const stage = PF_STAGES[lead.stage] || { color: 'neutral', label: lead.stage };
  const stageCls = stage.color === 'green' ? 'pf-chip' :
                   stage.color === 'amber' ? 'pf-chip pf-chip-amber' :
                   stage.color === 'blue' ? 'pf-chip pf-chip-blue' :
                   stage.color === 'red' ? 'pf-chip pf-chip-red' : 'pf-chip pf-chip-neutral';
  return (
    <div className="pf-row" onClick={onClick}>
      <div className="pf-avatar">{lead.initials}</div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <div style={{ fontWeight: 600, fontSize: 14.5 }}>{lead.name}</div>
          {(lead.temp === 'hot' || lead.hot === true) && <span style={{ fontSize: 11 }}>🔥</span>}
        </div>
        <div style={{ fontSize: 12, color: 'var(--pf-ink-3)', marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {lead.project} · {lead.budget}
        </div>
        <div style={{ marginTop: 6, display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
          <span className={stageCls} style={{ padding: '3px 8px', fontSize: 10.5 }}>{stage.label}</span>
          <span style={{ fontSize: 11, color: 'var(--pf-ink-4)' }}>{lead.lastTouch}</span>
        </div>
      </div>
      <PFIcon name="chevR" size={16} color="var(--pf-ink-4)" />
    </div>
  );
};

Object.assign(window, { PFScreenHome, SectionTitle, LeadRow, TaskMini, iconBtn, linkBtn });



// ===== 07-pf-screens-2.jsx =====
/* global React, PFIcon, PFHeader, PFBackBtn, PF_DATA, PF_STAGES, LeadRow, SectionTitle, iconBtn, linkBtn, usePFLiveData */

// ─────────────────────────────────────────────────────────────
// LEADS LIST
// ─────────────────────────────────────────────────────────────
const LEAD_SORTS = [
  { id: 'default', label: 'Default' },
  { id: 'name', label: 'Name (A–Z)' },
  { id: 'hot', label: 'Hottest first' },
  { id: 'stage', label: 'Stage' },
];

const PFScreenLeads = ({ go }) => {
  // Pull real leads rather than rendering PF_DATA's demo seed — see
  // usePFLiveData in 04-pf-shared.jsx for why this screen needs it.
  usePFLiveData();
  const [filter, setFilter] = React.useState('All');
  const [q, setQ] = React.useState('');
  const [sortOpen, setSortOpen] = React.useState(false);
  const [sortBy, setSortBy] = React.useState('default');
  const filters = ['All', 'Hot 🔥', 'New', 'Qualified', 'Site Visit', 'Negotiation', 'Booked'];
  // Live-synced leads carry `hot` (boolean) + `score`, not the demo seed's
  // `temp` string — normalize both shapes here so "Hot 🔥" and sort-by-hot
  // work whether PF_DATA.leads is still the seed or has been replaced by
  // a real SRAPI.sync().
  const isHot = (l) => l.temp === 'hot' || l.hot === true;

  const filtered = PF_DATA.leads.filter(l => {
    if (filter === 'Hot 🔥' && !isHot(l)) return false;
    if (filter === 'New' && l.stage !== 'New Lead') return false;
    if (['Qualified', 'Site Visit', 'Negotiation', 'Booked'].includes(filter) && l.stage !== filter) return false;
    if (q && !(l.name.toLowerCase().includes(q.toLowerCase()) || l.project.toLowerCase().includes(q.toLowerCase()))) return false;
    return true;
  });
  const sorted = [...filtered].sort((a, b) => {
    if (sortBy === 'name') return a.name.localeCompare(b.name);
    if (sortBy === 'hot') return (isHot(b) ? 1 : 0) - (isHot(a) ? 1 : 0);
    if (sortBy === 'stage') return (a.stage || '').localeCompare(b.stage || '');
    return 0;
  });

  return (
    <div className="pf-screen pf-anim-in">
      <PFHeader
        title="Leads"
        subtitle={`${filtered.length} of ${PF_DATA.leads.length} · ${new Date().toLocaleDateString('en-IN',{day:'numeric',month:'short'})}`}
        right={
          <div style={{ display: 'flex', gap: 8 }}>
            {canRecoverLeads() && (
              <button style={iconBtn} onClick={() => go('deletedLeads')} aria-label="Deleted leads">
                <PFIcon name="history" size={18} />
              </button>
            )}
            <button style={{ ...iconBtn, position: 'relative' }} onClick={() => setSortOpen(true)} aria-label="Sort leads">
              <PFIcon name="filter" size={18} />
              {sortBy !== 'default' && (
                <span style={{ position: 'absolute', top: 6, right: 6, width: 7, height: 7, borderRadius: '50%', background: 'var(--pf-green)', border: '1.5px solid var(--pf-surface)' }} />
              )}
            </button>
          </div>
        }
      />
      <div style={{ padding: '4px 16px 10px', background: 'var(--pf-bg)' }}>
        <div style={{ position: 'relative' }}>
          <input
            value={q} onChange={e => setQ(e.target.value)}
            placeholder="Search leads, projects, phone…"
            className="pf-input"
            style={{ paddingLeft: 36, fontSize: 13.5, background: 'var(--pf-surface)' }}
          />
          <div style={{ position: 'absolute', left: 12, top: 13 }}>
            <PFIcon name="search" size={16} color="var(--pf-ink-4)" />
          </div>
        </div>
        <div style={{ display: 'flex', gap: 6, marginTop: 10, overflowX: 'auto', paddingBottom: 4 }}>
          {filters.map(f => (
            <button key={f} onClick={() => setFilter(f)} style={{
              padding: '6px 12px', borderRadius: 999,
              background: filter === f ? 'var(--pf-green)' : 'var(--pf-surface)',
              color: filter === f ? '#fff' : 'var(--pf-ink-2)',
              border: `1px solid ${filter === f ? 'var(--pf-green)' : 'var(--pf-line)'}`,
              fontSize: 12, fontWeight: 500, whiteSpace: 'nowrap', cursor: 'pointer', fontFamily: 'inherit',
            }}>{f}</button>
          ))}
        </div>
      </div>
      <div className="pf-scroll" style={{ padding: '4px 16px 20px' }}>
        <div className="pf-card" style={{ padding: 0, overflow: 'hidden' }}>
          {sorted.map(l => <LeadRow key={l.id} lead={l} onClick={() => go('lead', l.id)} />)}
          {sorted.length === 0 && (
            <div style={{ padding: 40, textAlign: 'center', color: 'var(--pf-ink-4)', fontSize: 13 }}>No leads match.</div>
          )}
        </div>
      </div>
      <button className="pf-fab" onClick={() => go('addLead')} aria-label="New lead">
        <PFIcon name="plus" size={22} color="#fff" strokeWidth={2} />
      </button>
      {sortOpen && (
        <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'flex-end', zIndex: 30 }}
          onClick={() => setSortOpen(false)}>
          <div style={{ background: '#fffdf5', color: 'var(--pf-ink)', width: '100%', borderRadius: '20px 20px 0 0', padding: 16 }}
            onClick={e => e.stopPropagation()}>
            <div style={{ width: 36, height: 4, background: 'var(--pf-line)', borderRadius: 2, margin: '0 auto 10px' }} />
            <div style={{ fontFamily: 'var(--pf-serif)', fontSize: 18, fontWeight: 600, marginBottom: 12 }}>Sort by</div>
            {LEAD_SORTS.map(s => (
              <div key={s.id} onClick={() => { setSortBy(s.id); setSortOpen(false); }} style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 4px', cursor: 'pointer',
                borderTop: s.id === 'default' ? 'none' : '1px solid var(--pf-line-2)',
              }}>
                <span style={{ fontSize: 14, fontWeight: sortBy === s.id ? 700 : 500 }}>{s.label}</span>
                {sortBy === s.id && <PFIcon name="check" size={16} color="var(--pf-green)" />}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// LEAD DETAIL
// ─────────────────────────────────────────────────────────────
const PFScreenLead = ({ go, leadId }) => {
  usePFLiveData();
  // Only fall back to the first lead when no id was asked for. With
  // `|| PF_DATA.leads[0]` unconditional, a deep link to an unknown or
  // not-yet-synced id silently rendered a *different* customer's record —
  // name, phone, budget, profiling — as though it were the requested one,
  // and the "Lead not found" branch below could never be reached. Now that
  // skaledesk://lead/{id} actually resolves, that was readily reachable.
  const lead = leadId
    ? PF_DATA.leads.find(l => l.id === leadId)
    : PF_DATA.leads[0];
  const [tab, setTab] = React.useState('timeline');
  const [menuOpen, setMenuOpen] = React.useState(false);
  const [confirmDelete, setConfirmDelete] = React.useState(false);
  const [deleting, setDeleting] = React.useState(false);
  const [checkinOpen, setCheckinOpen] = React.useState(false);

  const handleDelete = async () => {
    setDeleting(true);
    try {
      if (window.SRAPI && typeof window.SRAPI._delete === 'function') {
        await window.SRAPI._delete('/api/leads/' + lead.id);
      }
      if (window.SRAPI && window.SRAPI.sync) await window.SRAPI.sync();
      go('leads');
    } catch (e) {
      window.SR_TOAST ? window.SR_TOAST.error((e && e.message) || 'Could not delete lead') : alert('Could not delete lead: ' + ((e && e.message) || 'please try again'));
      setDeleting(false);
      setConfirmDelete(false);
    }
  };

  if (!lead) {
    return (
      <div className="pf-screen pf-anim-in">
        <PFHeader left={<PFBackBtn onClick={() => go('leads')} />} title="Lead not found" />
        <div className="pf-scroll" style={{ padding: 24, textAlign: 'center', color: 'var(--pf-ink-3)', fontSize: 13 }}>
          This lead couldn't be loaded. It may still be syncing — try going back and reopening it.
        </div>
      </div>
    );
  }
  const stage = PF_STAGES[lead.stage] || { color: 'neutral', label: lead.stage };
  const stageCls = stage.color === 'green' ? 'pf-chip' :
                   stage.color === 'amber' ? 'pf-chip pf-chip-amber' :
                   stage.color === 'blue' ? 'pf-chip pf-chip-blue' :
                   stage.color === 'red' ? 'pf-chip pf-chip-red' : 'pf-chip pf-chip-neutral';

  return (
    <div className="pf-screen pf-anim-in">
      <PFHeader
        left={<PFBackBtn onClick={() => go('leads')} />}
        title={lead.name}
        subtitle={lead.phone}
        right={
          <div style={{ position: 'relative' }}>
            <button style={iconBtn} onClick={() => setMenuOpen(o => !o)}><PFIcon name="more" size={18} /></button>
            {menuOpen && (
              <>
                <div style={{ position: 'fixed', inset: 0, zIndex: 40 }} onClick={() => setMenuOpen(false)} />
                <div style={{
                  position: 'absolute', right: 0, top: 42, zIndex: 41, background: '#fff',
                  border: '1px solid var(--pf-line)', borderRadius: 12, boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                  minWidth: 160, overflow: 'hidden',
                }}>
                  <button onClick={() => { setMenuOpen(false); setCheckinOpen(true); }} style={{
                    width: '100%', textAlign: 'left', padding: '12px 16px', border: 0, background: 'transparent',
                    color: 'var(--pf-ink)', fontSize: 13.5, fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit',
                    display: 'flex', alignItems: 'center', gap: 8, borderBottom: '1px solid var(--pf-line-2)',
                  }}>
                    <PFIcon name="whatsapp" size={14} color="var(--pf-green)" /> Send WhatsApp check-in
                  </button>
                  <button onClick={() => { setMenuOpen(false); setConfirmDelete(true); }} style={{
                    width: '100%', textAlign: 'left', padding: '12px 16px', border: 0, background: 'transparent',
                    color: 'var(--pf-red)', fontSize: 13.5, fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit',
                    display: 'flex', alignItems: 'center', gap: 8,
                  }}>
                    <PFIcon name="close" size={14} color="var(--pf-red)" /> Delete lead
                  </button>
                </div>
              </>
            )}
          </div>
        }
      />
      {checkinOpen && <WhatsAppCheckinSheet lead={lead} onClose={() => setCheckinOpen(false)} />}
      {confirmDelete && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 100, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
          <div style={{ background: '#fff', borderRadius: 16, padding: 20, maxWidth: 320, width: '100%' }}>
            <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 8 }}>Delete this lead?</div>
            <div style={{ fontSize: 13, color: 'var(--pf-ink-2)', marginBottom: 18, lineHeight: 1.5 }}>
              <strong>{lead.name}</strong> and all of its history, notes, and evidence will be permanently deleted. This can't be undone.
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="pf-btn pf-btn-ghost pf-btn-block" disabled={deleting} onClick={() => setConfirmDelete(false)}>Cancel</button>
              <button className="pf-btn pf-btn-block" disabled={deleting} onClick={handleDelete}
                style={{ background: 'var(--pf-red)' }}>
                {deleting ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
      <div className="pf-scroll" style={{ padding: '0 16px 24px' }}>
        {/* Summary card */}
        <div className="pf-card" style={{ marginBottom: 12 }}>
          <div style={{ display: 'flex', gap: 12 }}>
            <div className="pf-avatar" style={{ width: 52, height: 52, fontSize: 17 }}>{lead.initials}</div>
            <div style={{ flex: 1 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                <div style={{ fontFamily: 'var(--pf-serif)', fontSize: 20, fontWeight: 600 }}>{lead.name}</div>
                {(lead.temp === 'hot' || lead.hot === true) && <span>🔥</span>}
              </div>
              <div style={{ marginTop: 4, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                <span className={stageCls} style={{ fontSize: 10.5 }}>{stage.label}</span>
                <span className="pf-chip pf-chip-neutral" style={{ fontSize: 10.5 }}>{lead.source}</span>
              </div>
            </div>
          </div>
          <div style={{
            marginTop: 12, paddingTop: 12, borderTop: '1px solid var(--pf-line-2)',
            display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10,
          }}>
            <KV label="Project" val={lead.project} />
            <KV label="Budget" val={lead.budget} />
            <KV label="Last touch" val={lead.lastTouch} />
            <KV label="Source" val={lead.source} />
          </div>
        </div>

        {/* Action row */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 6, marginBottom: 14 }}>
          <ActionChip icon="phone" label="Call" green onClick={() => go('callActive', lead.id)} />
          <ActionChip icon="pin" label="Visit" onClick={() => go('siteVisit', lead.id)} />
          <ActionChip icon="doc" label="Profile" onClick={() => go('profiling', lead.id)} />
          <ActionChip icon="camera" label="Evidence" onClick={() => go('evidence', lead.id)} />
          <ActionChip icon="chat" label="Ping" onClick={() => go('chat', lead.id)} />
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', gap: 2, borderBottom: '1px solid var(--pf-line)', marginBottom: 14, overflowX: 'auto' }}>
          {[
            ['timeline', 'History'],
            ['profile', 'Profile'],
            ['requires', 'Needs'],
            ['tasks', 'Tasks'],
            ['notes', 'Notes'],
          ].map(([id, label]) => (
            <button key={id} onClick={() => setTab(id)} style={{
              flexShrink: 0, padding: '10px 10px', background: 'transparent', border: 0, fontFamily: 'inherit',
              color: tab === id ? 'var(--pf-green)' : 'var(--pf-ink-3)',
              fontWeight: tab === id ? 600 : 500, fontSize: 12.5,
              borderBottom: `2px solid ${tab === id ? 'var(--pf-green)' : 'transparent'}`,
              marginBottom: -1, cursor: 'pointer', whiteSpace: 'nowrap',
            }}>{label}</button>
          ))}
        </div>

        {tab === 'timeline' && <TimelineView leadId={lead.id} />}
        {tab === 'profile' && <ProfilePreview go={go} leadId={lead.id} />}
        {tab === 'requires' && <LeadRequirements leadId={lead.id} lead={lead} />}
        {tab === 'tasks' && <LeadTasks lead={lead} go={go} />}
        {tab === 'notes' && <LeadNotes leadId={lead.id} />}
      </div>
    </div>
  );
};

const KV = ({ label, val }) => (
  <div>
    <div style={{ fontSize: 10.5, letterSpacing: '0.1em', color: 'var(--pf-ink-3)', fontWeight: 600 }}>{label.toUpperCase()}</div>
    <div style={{ fontSize: 13, fontWeight: 500, marginTop: 2 }}>{val}</div>
  </div>
);

const ActionChip = ({ icon, label, green, onClick }) => (
  <button onClick={onClick} style={{
    background: green ? 'var(--pf-green)' : 'var(--pf-surface)',
    color: green ? '#fff' : 'var(--pf-ink)',
    border: green ? 0 : '1px solid var(--pf-line)',
    borderRadius: 14, padding: '12px 4px',
    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4,
    cursor: 'pointer', fontFamily: 'inherit',
  }}>
    <PFIcon name={icon} size={18} color={green ? '#fff' : 'var(--pf-green)'} />
    <span style={{ fontSize: 10.5, fontWeight: 500 }}>{label}</span>
  </button>
);

const TimelineView = ({ leadId }) => {
  const [shareLog, setShareLog] = React.useState([]);
  const [activityLog, setActivityLog] = React.useState([]);
  const [waLog, setWaLog] = React.useState([]);
  React.useEffect(() => {
    if (window.SRAPI && leadId) {
      window.SRAPI.getShareHistory(leadId).then(rows => setShareLog(rows || [])).catch(() => {});
      if (typeof window.SRAPI.getLeadActivity === 'function') {
        window.SRAPI.getLeadActivity(leadId).then(rows => setActivityLog(rows || [])).catch(() => {});
      }
      if (typeof window.SRAPI.getLeadWhatsApp === 'function') {
        window.SRAPI.getLeadWhatsApp(leadId).then(rows => setWaLog(rows || [])).catch(() => {});
      }
    }
  }, [leadId]);

  const shareEvents = shareLog.map(s => ({
    id: 'sl_' + s.id,
    ts: s.created_at,
    when: s.created_at ? new Date(s.created_at).toLocaleString('en-IN', { day:'numeric', month:'short', hour:'2-digit', minute:'2-digit' }) : '',
    type: 'doc',
    title: `📤 ${s.asset_name} shared via ${s.channel}`,
    text: s.message_preview ? s.message_preview.slice(0, 80) + (s.message_preview.length > 80 ? '…' : '') : `Sent by ${s.shared_by_name || 'agent'}`,
    isShare: true,
  }));
  const ACTION_LABELS = {
    created: '➕ Lead created', updated: '✏️ Lead updated', stage_change: '🔀 Stage changed',
    deleted: '🗑️ Lead deleted', restored: '♻️ Lead restored', note: '📝 Note added',
    evidence_uploaded: '📷 Evidence uploaded', call_logged: '📞 Call logged', visit_logged: '📍 Site visit logged',
  };
  const activityEvents = activityLog.map((a, i) => ({
    id: 'al_' + i,
    ts: a.created_at,
    when: a.created_at ? new Date(a.created_at).toLocaleString('en-IN', { day:'numeric', month:'short', hour:'2-digit', minute:'2-digit' }) : '',
    title: ACTION_LABELS[a.action] || a.action,
    text: `${a.detail || ''}${a.user_name ? ` — ${a.user_name}` : ''}`,
  }));
  // "WhatsApp — Agent" not "WhatsApp sent" — this only means the agent
  // tapped the deep-link. There is no delivery/read/reply visibility
  // without a real WhatsApp Business API integration.
  const waEvents = waLog.map((w, i) => ({
    id: 'wa_' + i,
    ts: w.created_at,
    when: w.created_at ? new Date(w.created_at).toLocaleString('en-IN', { day:'numeric', month:'short', hour:'2-digit', minute:'2-digit' }) : '',
    title: '💬 WhatsApp — Agent',
    text: `${w.message ? w.message.slice(0, 80) + (w.message.length > 80 ? '…' : '') : (w.kind || '')}${w.agent_name ? ` — ${w.agent_name}` : ''}`,
  }));
  // Previously this fell back to PF_DATA.timeline — a hardcoded fake call/
  // visit history shown identically for every lead regardless of whether
  // any of it actually happened. Now real events only, newest first.
  const combined = [...shareEvents, ...activityEvents, ...waEvents]
    .sort((a, b) => new Date(b.ts || 0) - new Date(a.ts || 0))
    .slice(0, 20);

  if (combined.length === 0) {
    return <div style={{ fontSize: 12.5, color: 'var(--pf-ink-3)', padding: '8px 2px' }}>No activity yet.</div>;
  }

  return (
    <div>
      {shareLog.length > 0 && (
        <div style={{ padding:'8px 12px', borderRadius:10, background:'#f0fdf4', border:'1px solid #bbf7d0', marginBottom:14, fontSize:12.5, color:'#15803d', fontWeight:600 }}>
          📊 {shareLog.length} asset{shareLog.length > 1 ? 's' : ''} shared · last {shareLog[0] ? new Date(shareLog[0].created_at).toLocaleDateString('en-IN',{day:'numeric',month:'short'}) : ''}
        </div>
      )}
      <div style={{ position: 'relative', paddingLeft: 22 }}>
        <div style={{ position: 'absolute', left: 9, top: 6, bottom: 6, width: 1, background: 'var(--pf-line)' }} />
        {combined.map((e, i) => (
          <div key={e.id} style={{ marginBottom: 14, position: 'relative' }}>
            <div style={{
              position: 'absolute', left: -18, top: 4, width: 10, height: 10, borderRadius: '50%',
              background: i === 0 ? 'var(--pf-green)' : e.isShare ? '#dcfce7' : '#fff',
              border: `2px solid ${i === 0 ? 'var(--pf-green)' : e.isShare ? 'var(--pf-green)' : 'var(--pf-line)'}`,
            }} />
            <div style={{ fontSize: 11, color: 'var(--pf-ink-3)', fontWeight: 500 }}>{e.when}</div>
            <div style={{ fontSize: 13.5, fontWeight: 600, marginTop: 2 }}>{e.title}</div>
            <div style={{ fontSize: 12.5, color: 'var(--pf-ink-2)', marginTop: 3, lineHeight: 1.45 }}>{e.text}</div>
          </div>
        ))}
      </div>
    </div>
  );
};

const ProfilePreview = ({ go, leadId }) => {
  // Read the RAW stored profile only — not loadProfile()'s merge with
  // DEFAULT_PROFILE (which fills in plausible-looking placeholder values
  // like "4 members" / "Mixed · 70% loan" / "30–60 days" for the profiling
  // FORM's pre-fill convenience). Showing those merged defaults here made
  // every never-profiled lead look like it already had real answers.
  const getRaw = (id) => (window.PROFILE_STORE && window.PROFILE_STORE[id]) || {};
  const [profile, setProfile] = React.useState(() => getRaw(leadId));
  React.useEffect(() => {
    const onUpdate = (e) => { if (e.detail && e.detail.leadId === leadId) setProfile(getRaw(leadId)); };
    document.addEventListener('sr:profile-updated', onUpdate);
    return () => document.removeEventListener('sr:profile-updated', onUpdate);
  }, [leadId]);

  // Completion = % of meaningful fields filled.
  const tracked = ['name','mobile','email','pan','maritalStatus','familySize','coApplicant','employer','designation','annualIncome','fundingType','purchaseIntent','timeline','concerns'];
  const filled = tracked.filter(k => {
    const v = profile[k];
    return v !== undefined && v !== null && String(v).trim() !== '';
  }).length;
  const pct = Math.round((filled / tracked.length) * 100);
  const loan = Number(profile.loanPct) || 0;
  const empty = '—';
  const fmt = (v) => (v === undefined || v === null || String(v).trim() === '') ? empty : v;
  const fundingDisplay = profile.fundingType
    ? `${profile.fundingType}${profile.loanPct != null ? ` · ${loan}% loan / ${100-loan}% own` : ''}`
    : empty;
  const incomeDisplay = profile.annualIncome
    ? `${profile.currency || '₹'} ${profile.annualIncome}`
    : empty;

  return (
    <div>
      <div className="pf-card" style={{ marginBottom: 10 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
          <div style={{ fontWeight: 600, fontSize: 13.5 }}>Customer profiling</div>
          <span className={pct >= 100 ? 'pf-chip' : 'pf-chip pf-chip-amber'} style={{ fontSize: 10.5 }}>{pct}% complete</span>
        </div>
        <div style={{ height: 6, background: 'var(--pf-line-2)', borderRadius: 3, overflow: 'hidden' }}>
          <div style={{ width: `${pct}%`, height: '100%', background: 'var(--pf-green)', transition: 'width .3s' }} />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 14 }}>
          <KV label="PAN" val={fmt(profile.pan)} />
          <KV label="Employer" val={fmt(profile.employer)} />
          <KV label="Designation" val={fmt(profile.designation)} />
          <KV label="Income" val={incomeDisplay} />
          <KV label="Family size" val={fmt(profile.familySize)} />
          <KV label="Funding" val={fundingDisplay} />
          <KV label="Co-applicant" val={fmt(profile.coApplicant)} />
          <KV label="Timeline" val={fmt(profile.timeline)} />
        </div>
        {profile.concerns && (
          <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid var(--pf-line-2)' }}>
            <div style={{ fontSize: 10.5, letterSpacing: '0.1em', color: 'var(--pf-ink-3)', fontWeight: 600 }}>TOP CONCERNS</div>
            <div style={{ fontSize: 12.5, color: 'var(--pf-ink-2)', marginTop: 4, lineHeight: 1.45 }}>{profile.concerns}</div>
          </div>
        )}
      </div>
      <button className="pf-btn pf-btn-soft pf-btn-block" onClick={() => go('profiling', leadId)}>
        <PFIcon name="doc" size={16} color="var(--pf-green)" />
        {pct >= 100 ? 'Edit profile' : 'Continue profiling form'}
      </button>
    </div>
  );
};

// ── Lead Requirements Capture ─────────────────────────────────────────────────
const CONFIGS  = ['1BHK','2BHK','3BHK','4BHK','Villa','Plot','Commercial'];
const TIMELINES = ['Immediate','Within 3 months','3–6 months','6–12 months','Just exploring'];
const PURPOSES  = ['Self use','Investment','Both'];
const FLOORS    = ['Low (1–5)','Mid (6–15)','High (16+)','Any'];
const FACINGS   = ['North','East','South','West','Any'];

const LeadRequirements = ({ leadId, lead }) => {
  const [data, setData]       = React.useState({});
  const [saving, setSaving]   = React.useState(false);
  const [saved, setSaved]     = React.useState(false);
  const [bundleOpen, setBundleOpen] = React.useState(false);
  const [suggestion, setSug]  = React.useState(null);

  React.useEffect(() => {
    // Load from local cache first
    const local = window.REQUIREMENTS_STORE && window.REQUIREMENTS_STORE[leadId];
    if (local) setData(local);
    // Then fetch from backend
    if (window.SRAPI) {
      window.SRAPI.getRequirements(leadId).then(r => { if (r && Object.keys(r).length > 0) setData(r); }).catch(() => {});
      window.SRAPI.getNextSuggested(leadId).then(r => setSug(r.suggestion)).catch(() => {});
    }
  }, [leadId]);

  const set = (k, v) => setData(d => ({ ...d, [k]: v }));
  const toggleArr = (k, v) => {
    const cur = Array.isArray(data[k]) ? data[k] : [];
    setData(d => ({ ...d, [k]: cur.includes(v) ? cur.filter(x => x !== v) : [...cur, v] }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (window.SRAPI) await window.SRAPI.saveRequirements(leadId, data);
      else {
        if (!window.REQUIREMENTS_STORE) window.REQUIREMENTS_STORE = {};
        window.REQUIREMENTS_STORE[leadId] = data;
      }
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } finally { setSaving(false); }
  };

  const chip = (val, selected, onClick) => (
    <button key={val} onClick={onClick} style={{
      padding:'5px 12px', borderRadius:20, fontSize:12, fontWeight:600, cursor:'pointer', flexShrink:0,
      background: selected ? 'var(--pf-green)' : '#f3f4f6',
      color: selected ? '#fff' : '#374151',
      border: `1.5px solid ${selected ? 'var(--pf-green)' : '#e5e7eb'}`,
    }}>{val}</button>
  );

  return (
    <div>
      {bundleOpen && <FieldBundleModal leadId={leadId} lead={lead} onClose={() => setBundleOpen(false)} />}

      {suggestion && (
        <div style={{ padding:'10px 12px', borderRadius:10, background:'#fffbeb', border:'1px solid #fde68a', marginBottom:14, fontSize:13, color:'#92400e', display:'flex', gap:8 }}>
          <span>💡</span>
          <div><strong>Send next:</strong> {suggestion.reason}
            <span style={{ fontSize:11, color:'var(--pf-green)', fontWeight:700, marginLeft:6 }}>· {suggestion.urgency} priority</span>
          </div>
        </div>
      )}

      {/* Field bundle CTA */}
      <button onClick={() => setBundleOpen(true)} style={{
        width:'100%', padding:'12px 14px', borderRadius:12, marginBottom:14,
        background:'linear-gradient(135deg,#0C1A2E,#1B3D61)',
        border:'none', color:'#fff', cursor:'pointer',
        display:'flex', alignItems:'center', justifyContent:'center', gap:10,
        fontFamily:'var(--pf-sans)', fontWeight:700, fontSize:14,
      }}>
        <span style={{ fontSize:18 }}>📲</span> Send Field Bundle (location + brochure + number)
      </button>

      {/* Requirements form */}
      <div className="pf-card" style={{ marginBottom:12 }}>
        <div style={{ fontWeight:700, fontSize:14, marginBottom:4 }}>Configuration</div>
        <div style={{ display:'flex', flexWrap:'wrap', gap:6 }}>
          {CONFIGS.map(v => chip(v, data.configuration === v, () => set('configuration', data.configuration === v ? '' : v)))}
        </div>
      </div>

      <div className="pf-card" style={{ marginBottom:12 }}>
        <div style={{ fontWeight:700, fontSize:14, marginBottom:4 }}>Budget range</div>
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
          {[['budget_min','Min (₹)'],['budget_max','Max (₹)']].map(([k,lbl]) => (
            <div key={k}>
              <div style={{ fontSize:11, color:'var(--pf-ink-3)', marginBottom:4 }}>{lbl}</div>
              <input value={data[k] || ''} onChange={e => set(k, e.target.value)}
                placeholder="e.g. 80,00,000"
                style={{ width:'100%', boxSizing:'border-box', padding:'9px 10px', border:'1.5px solid #e5e7eb', borderRadius:8, fontSize:13, outline:'none', fontFamily:'var(--pf-sans)' }} />
            </div>
          ))}
        </div>
      </div>

      <div className="pf-card" style={{ marginBottom:12 }}>
        <div style={{ fontWeight:700, fontSize:14, marginBottom:4 }}>Move-in timeline</div>
        <div style={{ display:'flex', flexWrap:'wrap', gap:6 }}>
          {TIMELINES.map(v => chip(v, data.move_in_timeline === v, () => set('move_in_timeline', data.move_in_timeline === v ? '' : v)))}
        </div>
      </div>

      <div className="pf-card" style={{ marginBottom:12 }}>
        <div style={{ display:'flex', gap:12 }}>
          <div style={{ flex:1 }}>
            <div style={{ fontWeight:700, fontSize:13, marginBottom:6 }}>Purpose</div>
            <div style={{ display:'flex', flexDirection:'column', gap:5 }}>
              {PURPOSES.map(v => chip(v, data.purpose === v, () => set('purpose', data.purpose === v ? '' : v)))}
            </div>
          </div>
          <div style={{ flex:1 }}>
            <div style={{ fontWeight:700, fontSize:13, marginBottom:6 }}>Preferred floor</div>
            <div style={{ display:'flex', flexDirection:'column', gap:5 }}>
              {FLOORS.map(v => chip(v, data.preferred_floors === v, () => set('preferred_floors', data.preferred_floors === v ? '' : v)))}
            </div>
          </div>
        </div>
      </div>

      <div className="pf-card" style={{ marginBottom:12 }}>
        <div style={{ fontWeight:700, fontSize:14, marginBottom:4 }}>Facing preference</div>
        <div style={{ display:'flex', flexWrap:'wrap', gap:6 }}>
          {FACINGS.map(v => chip(v, data.preferred_facing === v, () => set('preferred_facing', data.preferred_facing === v ? '' : v)))}
        </div>
      </div>

      <div className="pf-card" style={{ marginBottom:12 }}>
        <div style={{ fontWeight:700, fontSize:13, marginBottom:6 }}>Must-haves</div>
        <div style={{ display:'flex', flexWrap:'wrap', gap:6, marginBottom:10 }}>
          {['Parking','Garden view','Corner unit','East facing','Vastu compliant','Swimming pool','Gym'].map(v => {
            const sel = Array.isArray(data.must_haves) && data.must_haves.includes(v);
            return chip(v, sel, () => toggleArr('must_haves', v));
          })}
        </div>
        <div style={{ fontWeight:700, fontSize:13, marginBottom:6 }}>Deal-breakers</div>
        <div style={{ display:'flex', flexWrap:'wrap', gap:6 }}>
          {['Top floor','4th floor','Road facing','No lift','Ground floor'].map(v => {
            const sel = Array.isArray(data.deal_breakers) && data.deal_breakers.includes(v);
            return chip(v, sel, () => toggleArr('deal_breakers', v));
          })}
        </div>
      </div>

      <div className="pf-card" style={{ marginBottom:12 }}>
        <div style={{ fontWeight:700, fontSize:13, marginBottom:6 }}>Concerns / notes</div>
        <textarea value={data.concerns || ''} onChange={e => set('concerns', e.target.value)}
          placeholder="Client's concerns, family situation, urgency notes…" rows={3}
          style={{ width:'100%', boxSizing:'border-box', padding:'10px 12px', border:'1.5px solid #e5e7eb', borderRadius:10, fontSize:13, resize:'none', outline:'none', fontFamily:'var(--pf-sans)' }} />
      </div>

      <div style={{ display:'flex', gap:10, marginBottom:12 }}>
        <div style={{ flex:1 }}>
          <div style={{ fontSize:11.5, color:'var(--pf-ink-3)', marginBottom:4 }}>Nearest school / landmark</div>
          <input value={data.school_distance || ''} onChange={e => set('school_distance', e.target.value)}
            placeholder="DPS 3.2km, Metro 500m…"
            style={{ width:'100%', boxSizing:'border-box', padding:'9px 10px', border:'1.5px solid #e5e7eb', borderRadius:8, fontSize:13, outline:'none', fontFamily:'var(--pf-sans)' }} />
        </div>
        <div style={{ flex:1 }}>
          <div style={{ fontSize:11.5, color:'var(--pf-ink-3)', marginBottom:4 }}>Workplace</div>
          <input value={data.workplace || ''} onChange={e => set('workplace', e.target.value)}
            placeholder="Nariman Point, WFH…"
            style={{ width:'100%', boxSizing:'border-box', padding:'9px 10px', border:'1.5px solid #e5e7eb', borderRadius:8, fontSize:13, outline:'none', fontFamily:'var(--pf-sans)' }} />
        </div>
      </div>

      <button onClick={handleSave} disabled={saving} style={{
        width:'100%', padding:'13px', borderRadius:12, border:'none',
        background: saved ? '#15803d' : saving ? '#9aa29d' : 'var(--pf-green)',
        color:'#fff', fontWeight:700, fontSize:14, cursor: saving ? 'wait' : 'pointer',
        display:'flex', alignItems:'center', justifyContent:'center', gap:8,
      }}>
        {saved ? '✓ Requirements saved' : saving ? 'Saving…' : '💾 Save requirements'}
      </button>
    </div>
  );
};

// ── Field Bundle Modal ────────────────────────────────────────────────────────
const FieldBundleModal = ({ leadId, lead, onClose }) => {
  const [loading, setLoading] = React.useState(false);
  const [result, setResult]   = React.useState(null);
  const [copied, setCopied]   = React.useState(false);
  const [locNote, setLocNote] = React.useState('');

  const generate = async () => {
    setLoading(true);
    try {
      let res;
      if (window.SRAPI) {
        res = await window.SRAPI.sendFieldBundle(leadId, [], locNote, false);
      } else {
        // Offline fallback — compose locally
        const agentName  = 'Your Sales Executive';
        const project    = lead?.project || 'SkaleRealty Project';
        const mapsLink   = `https://maps.google.com/?q=${encodeURIComponent(project + ' ' + locNote)}`;
        const msg = `Hi ${lead?.name || ''} 👋\n\nI'm ${agentName}, your dedicated sales executive for *${project}*.\n\n📍 *Project Location:* ${mapsLink}\n\nI'm available to answer your questions and arrange a site visit at your convenience. Looking forward to speaking with you! 🏡`;
        res = { message: msg, waUrl: `https://wa.me/?text=${encodeURIComponent(msg)}` };
      }
      setResult(res);
    } finally { setLoading(false); }
  };

  React.useEffect(() => { generate(); }, []);

  const openWA = () => { if (result?.waUrl) window.open(result.waUrl, '_blank'); };
  const copyMsg = () => {
    try { navigator.clipboard.writeText(result?.message || ''); } catch(e) {}
    setCopied(true); setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,.55)', zIndex:300, display:'flex', alignItems:'flex-end' }}>
      <div style={{ background:'#fff', width:'100%', borderRadius:'18px 18px 0 0', padding:'20px 18px 36px', maxHeight:'85vh', overflowY:'auto' }}>
        <div style={{ width:40, height:4, background:'#e5e7eb', borderRadius:2, margin:'0 auto 14px' }} />
        <div style={{ fontWeight:800, fontSize:16, color:'#111827', marginBottom:4 }}>📲 Field Bundle</div>
        <div style={{ fontSize:12.5, color:'#6b7280', marginBottom:16 }}>One message with your number, location pin, and asset links</div>

        <div style={{ marginBottom:12 }}>
          <div style={{ fontSize:12, fontWeight:600, color:'#374151', marginBottom:6 }}>Location hint (optional)</div>
          <input value={locNote} onChange={e => { setLocNote(e.target.value); setResult(null); }}
            placeholder="e.g. near Bandra station, Andheri West"
            onBlur={generate}
            style={{ width:'100%', boxSizing:'border-box', padding:'10px 12px', border:'1.5px solid #e5e7eb', borderRadius:10, fontSize:13, outline:'none', fontFamily:'var(--pf-sans)' }} />
        </div>

        {loading ? (
          <div style={{ padding:'24px', textAlign:'center', color:'#9ca3af', fontSize:13 }}>Composing message…</div>
        ) : result ? (
          <>
            <div style={{ padding:'14px', background:'#f9fafb', borderRadius:12, border:'1px solid #e5e7eb', fontSize:13, color:'#111827', lineHeight:1.6, whiteSpace:'pre-wrap', marginBottom:14, maxHeight:200, overflowY:'auto' }}>
              {result.message}
            </div>
            <div style={{ display:'flex', gap:8, marginBottom:8 }}>
              <button onClick={openWA} style={{
                flex:2, padding:'13px', borderRadius:12, border:'none',
                background:'#15803d', color:'#fff', fontWeight:700, fontSize:14, cursor:'pointer',
                display:'flex', alignItems:'center', justifyContent:'center', gap:8,
              }}>💬 Open in WhatsApp</button>
              <button onClick={copyMsg} style={{
                flex:1, padding:'13px', borderRadius:12, border:'1px solid #e5e7eb',
                background: copied ? '#f0fdf4' : '#fff', color: copied ? '#15803d' : '#374151',
                fontWeight:700, fontSize:13, cursor:'pointer',
              }}>{copied ? '✓ Copied' : '🔗 Copy'}</button>
            </div>
          </>
        ) : null}
        <button onClick={onClose} style={{ width:'100%', padding:'12px', borderRadius:12, background:'#f3f4f6', border:'none', color:'#374151', fontWeight:700, fontSize:14, cursor:'pointer' }}>Close</button>
      </div>
    </div>
  );
};

const LeadTasks = ({ lead, go }) => {
  // Was PF_DATA.tasks.slice(0, 3) with no lead filter at all — just the
  // first 3 items of the agent's ENTIRE task list, regardless of which
  // lead they actually belonged to. Real synced tasks carry leadId; the
  // demo seed has no leadId field at all, only a client name string, so
  // match on whichever the task actually has.
  const tasks = PF_DATA.tasks.filter(t =>
    t.leadId ? t.leadId === lead.id : t.client === lead.name
  );
  return (
    <div>
      {tasks.length === 0 ? (
        <div style={{ padding: '20px 4px', textAlign: 'center', color: 'var(--pf-ink-4)', fontSize: 12.5 }}>No tasks linked to this lead.</div>
      ) : (
        tasks.map(t => (
          <div key={t.id} className="pf-task" data-done={t.done} onClick={() => go && go('taskDetail', t.id)} style={{ cursor: 'pointer' }}>
            <div className="pf-check">{t.done && <PFIcon name="check" size={14} color="#fff" strokeWidth={2.5} />}</div>
            <div style={{ flex: 1 }}>
              <div className="pf-task-title">{t.title}</div>
              <div className="pf-task-meta">{t.due}</div>
            </div>
          </div>
        ))
      )}
    </div>
  );
};

const LeadNotes = ({ leadId }) => {
  const [notes, setNotes]     = React.useState(null); // null = loading
  const [adding, setAdding]   = React.useState(false);
  const [draft, setDraft]     = React.useState('');
  const [saving, setSaving]   = React.useState(false);

  const load = React.useCallback(() => {
    if (window.SRAPI && typeof window.SRAPI.getLeadNotes === 'function') {
      window.SRAPI.getLeadNotes(leadId).then(setNotes).catch(() => setNotes([]));
    } else {
      setNotes([]);
    }
  }, [leadId]);

  React.useEffect(() => { load(); }, [load]);

  const handleAdd = async () => {
    if (!draft.trim()) return;
    setSaving(true);
    try {
      if (window.SRAPI && typeof window.SRAPI.addLeadNote === 'function') {
        await window.SRAPI.addLeadNote(leadId, draft.trim());
      }
      setDraft('');
      setAdding(false);
      load();
    } catch (e) {
      alert('Could not save note: ' + (e && e.message ? e.message : 'please try again.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="pf-card" style={{ background: '#fffdf5', borderColor: '#f1e9c9' }}>
      {notes === null ? (
        <div style={{ fontSize: 12.5, color: 'var(--pf-ink-3)' }}>Loading notes…</div>
      ) : notes.length === 0 && !adding ? (
        <div style={{ fontSize: 12.5, color: 'var(--pf-ink-3)' }}>No notes yet.</div>
      ) : (
        <div style={{ fontSize: 12.5, lineHeight: 1.55, color: 'var(--pf-ink-2)' }}>
          {notes.map((n, i) => (
            <div key={n.id || i} style={{ marginBottom: i < notes.length - 1 ? 10 : 0 }}>
              <strong>{n.author_name || 'You'} · {new Date(n.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}</strong><br />
              {n.body}
            </div>
          ))}
        </div>
      )}
      {adding ? (
        <div style={{ marginTop: 10 }}>
          <textarea className="pf-textarea" value={draft} onChange={e => setDraft(e.target.value)}
            placeholder="Type a note…" style={{ minHeight: 64 }} autoFocus />
          <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
            <button className="pf-btn pf-btn-sm" disabled={saving || !draft.trim()} onClick={handleAdd}>
              {saving ? 'Saving…' : 'Save note'}
            </button>
            <button className="pf-btn pf-btn-ghost pf-btn-sm" onClick={() => { setAdding(false); setDraft(''); }}>Cancel</button>
          </div>
        </div>
      ) : (
        <button className="pf-btn pf-btn-light pf-btn-sm" style={{ marginTop: 10 }} onClick={() => setAdding(true)}>
          <PFIcon name="plus" size={12} /> Add note
        </button>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// ADD LEAD — the "+" FAB on the Leads screen previously navigated
// to Chat with no way to actually create a lead.
// ─────────────────────────────────────────────────────────────
const genLeadId = () => 'L' + Date.now();

const PFScreenAddLead = ({ go, user }) => {
  const [name, setName] = React.useState('');
  const [phone, setPhone] = React.useState('');
  const [email, setEmail] = React.useState('');
  const [project, setProject] = React.useState('');
  const [budget, setBudget] = React.useState('');
  const [leadId, setLeadId] = React.useState(genLeadId); // auto-suggested, editable
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState('');
  const [dupe, setDupe] = React.useState(null); // { id, name, phone, email, stage } when a duplicate is found

  const handleSave = async (allowDuplicate) => {
    if (!name.trim()) { setError('Name is required'); return; }
    if (!leadId.trim()) { setError('Lead ID is required'); return; }
    setSaving(true);
    setError('');
    try {
      if (!window.SRAPI || typeof window.SRAPI._post !== 'function') {
        throw new Error('Not connected — cannot create a lead right now.');
      }
      // `user` here is the app's hardcoded prototype identity (a fixed demo
      // name), not necessarily the real logged-in account — GET
      // /api/mobile/sync only returns leads owned by the real user's actual
      // name/email, so using the wrong one here made the new lead invisible
      // to its own creator (and crashed the next screen, since it couldn't
      // find a lead that sync excluded). Use the real authenticated user.
      const realUser = window.SRAPI.getUser && window.SRAPI.getUser();
      const ownerName = (realUser && (realUser.name || realUser.email)) || (user && user.name) || undefined;
      const result = await window.SRAPI._post('/api/leads', {
        id: leadId.trim(),
        name: name.trim(),
        phone: phone.trim() || undefined,
        email: email.trim() || undefined,
        project_interest: project.trim() || undefined,
        budget: budget ? parseFloat(budget) : undefined,
        owner: ownerName,
        allowDuplicate: !!allowDuplicate,
      });
      if (!result || !result.id) {
        throw new Error('Lead was not created — please try again.');
      }
      if (window.SRAPI.sync) await window.SRAPI.sync();
      go('lead', result.id);
    } catch (e) {
      if (e && e.status === 409 && e.body && e.body.code === 'DUPLICATE_ID') {
        setLeadId(genLeadId());
        setError('That Lead ID is already in use — generated a new one, please save again.');
        setSaving(false);
        return;
      }
      if (e && e.status === 409 && e.body && e.body.existing) {
        setDupe(e.body.existing);
        setSaving(false);
        return;
      }
      setError((e && e.message) || 'Could not save — please try again.');
      setSaving(false);
    }
  };

  return (
    <div className="pf-screen pf-anim-in">
      <PFHeader left={<PFBackBtn onClick={() => go('leads')} />} title="New lead" />
      <div className="pf-scroll" style={{ padding: '0 16px 24px' }}>
        <div className="pf-field">
          <div className="pf-label">Name *</div>
          <input className="pf-input" value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Rohan Mehta" autoFocus />
        </div>
        <div className="pf-field">
          <div className="pf-label">Lead ID</div>
          <input className="pf-input" value={leadId} onChange={e => setLeadId(e.target.value)} style={{ fontFamily: 'var(--pf-mono)', fontSize: 13 }} />
          <div style={{ fontSize: 11, color: 'var(--pf-ink-3)', marginTop: 4 }}>Auto-generated — edit if you want a specific ID.</div>
        </div>
        <div className="pf-field">
          <div className="pf-label">Phone</div>
          <input className="pf-input" value={phone} onChange={e => setPhone(e.target.value)} placeholder="+91 98200 12345" inputMode="tel" />
        </div>
        <div className="pf-field">
          <div className="pf-label">Email</div>
          <input className="pf-input" value={email} onChange={e => setEmail(e.target.value)} placeholder="name@mail.com" inputMode="email" />
        </div>
        <div className="pf-field">
          <div className="pf-label">Project interest</div>
          <input className="pf-input" value={project} onChange={e => setProject(e.target.value)} placeholder="e.g. Whispering Oaks · 3BHK" />
        </div>
        <div className="pf-field">
          <div className="pf-label">Budget (₹)</div>
          <input className="pf-input" value={budget} onChange={e => setBudget(e.target.value.replace(/[^0-9]/g, ''))} placeholder="e.g. 19500000" inputMode="numeric" />
        </div>
        {error && <div style={{ color: 'var(--pf-red)', fontSize: 12.5, marginBottom: 10 }}>{error}</div>}
        {dupe && (
          <div style={{ background: 'var(--pf-amber-tint)', border: '1px solid #ebd9bf', borderRadius: 12, padding: 14, marginBottom: 12 }}>
            <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--pf-amber)', marginBottom: 4 }}>Possible duplicate</div>
            <div style={{ fontSize: 12.5, color: 'var(--pf-ink-2)', marginBottom: 10 }}>
              <strong>{dupe.name}</strong> ({dupe.phone || dupe.email}) already exists — stage: {dupe.stage || 'New'}.
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="pf-btn pf-btn-sm" onClick={() => go('lead', dupe.id)}>View existing lead</button>
              <button className="pf-btn pf-btn-ghost pf-btn-sm" disabled={saving} onClick={() => handleSave(true)}>
                {saving ? 'Saving…' : 'Create anyway'}
              </button>
            </div>
          </div>
        )}
        <button className="pf-btn pf-btn-block" disabled={saving} onClick={() => handleSave(false)}>
          {saving ? 'Saving…' : 'Save lead'}
        </button>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// DELETED LEADS — recovery screen for managers/admins. Server-enforced
// (GET /api/leads/deleted/list and POST /:id/restore both 403 for anyone
// below manager) — the role check here is just to hide the entry point
// for people who couldn't use it anyway, not the actual security boundary.
// ─────────────────────────────────────────────────────────────
const canRecoverLeads = () => {
  const role = (window.SRAPI && window.SRAPI.role) || (typeof localStorage !== 'undefined' && localStorage.getItem('sr_role')) || '';
  return ['manager', 'admin', 'business_owner', 'superadmin'].includes(String(role).toLowerCase().replace(/\s+/g, '_'));
};

const PFScreenDeletedLeads = ({ go }) => {
  const [rows, setRows] = React.useState(null); // null = loading
  const [restoring, setRestoring] = React.useState(null); // id currently restoring
  const [confirmId, setConfirmId] = React.useState(null);
  const [toast, setToast] = React.useState(null);
  const showToast = (m) => { setToast(m); setTimeout(() => setToast(null), 2000); };

  const load = React.useCallback(() => {
    if (window.SRAPI && typeof window.SRAPI.getDeletedLeads === 'function') {
      window.SRAPI.getDeletedLeads().then(setRows).catch(() => setRows([]));
    } else {
      setRows([]);
    }
  }, []);
  React.useEffect(() => { load(); }, [load]);

  const doRestore = async (id) => {
    setRestoring(id);
    setConfirmId(null);
    try {
      await window.SRAPI.restoreLead(id);
      if (window.SRAPI.sync) await window.SRAPI.sync();
      showToast('✓ Lead restored');
      load();
    } catch (e) {
      showToast((e && e.message) || 'Could not restore lead');
    } finally {
      setRestoring(null);
    }
  };

  return (
    <div className="pf-screen pf-anim-in">
      <PFHeader left={<PFBackBtn onClick={() => go('leads')} />} title="Deleted leads" subtitle="Recoverable — managers & admins only" />
      <div className="pf-scroll" style={{ padding: '0 16px 24px' }}>
        {rows === null ? (
          <div style={{ fontSize: 12.5, color: 'var(--pf-ink-3)', padding: '20px 0' }}>Loading…</div>
        ) : rows.length === 0 ? (
          <div style={{ fontSize: 12.5, color: 'var(--pf-ink-3)', padding: '20px 0', textAlign: 'center' }}>Nothing deleted recently.</div>
        ) : (
          <div className="pf-card" style={{ padding: 0, overflow: 'hidden' }}>
            {rows.map((r, i) => (
              <div key={r.id} className="pf-row" style={{ padding: '12px 14px', borderTop: i ? '1px solid var(--pf-line-2)' : 0, alignItems: 'flex-start' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13.5, fontWeight: 600 }}>{r.name || r.id}</div>
                  <div style={{ fontSize: 11.5, color: 'var(--pf-ink-4)', marginTop: 2 }}>{r.phone || r.email || '—'} · {r.stage || 'New'}</div>
                  <div style={{ fontSize: 11, color: 'var(--pf-ink-3)', marginTop: 4 }}>
                    Deleted by {r.deletedBy || 'unknown'} · {r.deletedAt ? new Date(r.deletedAt).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : ''}
                  </div>
                </div>
                {confirmId === r.id ? (
                  <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                    <button className="pf-btn pf-btn-sm" disabled={restoring === r.id} onClick={() => doRestore(r.id)}>
                      {restoring === r.id ? 'Restoring…' : 'Confirm'}
                    </button>
                    <button className="pf-btn pf-btn-ghost pf-btn-sm" onClick={() => setConfirmId(null)}>Cancel</button>
                  </div>
                ) : (
                  <button className="pf-btn pf-btn-light pf-btn-sm" style={{ flexShrink: 0 }} onClick={() => setConfirmId(r.id)}>
                    <PFIcon name="history" size={12} /> Restore
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
        {toast && (
          <div style={{
            position: 'fixed', bottom: 90, left: '50%', transform: 'translateX(-50%)',
            background: 'var(--pf-ink)', color: '#fff', padding: '10px 18px', borderRadius: 999,
            fontSize: 13, fontWeight: 500, zIndex: 100, boxShadow: '0 8px 24px rgba(0,0,0,0.25)',
          }}>{toast}</div>
        )}
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// WHATSAPP CHECK-IN — personalized templates (name/requirement/last visit
// filled in server-side), pick one, review/edit, send via the same wa.me +
// logging + auto-follow-up pipeline as every other WhatsApp send.
// ─────────────────────────────────────────────────────────────
const WhatsAppCheckinSheet = ({ lead, onClose }) => {
  const [templates, setTemplates] = React.useState(null); // null = loading
  const [selected, setSelected] = React.useState(null);
  const [draft, setDraft] = React.useState('');
  const [sending, setSending] = React.useState(false);

  React.useEffect(() => {
    if (window.SRAPI && typeof window.SRAPI.getWhatsAppTemplates === 'function') {
      window.SRAPI.getWhatsAppTemplates(lead.id).then(setTemplates).catch(() => setTemplates([]));
    } else {
      setTemplates([]);
    }
  }, [lead.id]);

  const pick = (t) => { setSelected(t.kind); setDraft(t.message); };

  const send = async () => {
    if (!draft.trim()) return;
    if (!lead.phone) { alert('This lead has no phone number on file.'); return; }
    setSending(true);
    try {
      const waUrl = `https://wa.me/${String(lead.phone).replace(/[^0-9]/g, '')}?text=${encodeURIComponent(draft.trim())}`;
      if (window.SRAPI && typeof window.SRAPI.sendWhatsApp === 'function') {
        // Logs the send + creates the admin-configured follow-up cadence
        // (see PUT /api/workspaces/current/whatsapp-config).
        window.SRAPI.sendWhatsApp(lead.id, selected || 'checkin', draft.trim(), selected || 'checkin').catch(() => {});
      }
      window.open(waUrl, '_blank');
      onClose();
    } finally {
      setSending(false);
    }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 100, background: 'rgba(0,0,0,0.45)', display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}>
      <div style={{ background: '#fff', borderRadius: '20px 20px 0 0', padding: '18px 16px 24px', maxHeight: '80vh', display: 'flex', flexDirection: 'column' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <div style={{ fontWeight: 700, fontSize: 15 }}>WhatsApp check-in — {lead.name}</div>
          <button onClick={onClose} style={{ background: 'transparent', border: 0, cursor: 'pointer' }}><PFIcon name="close" size={18} color="var(--pf-ink-3)" /></button>
        </div>
        {/* flex:1 + minHeight:0 are both required. Without minHeight:0 this
            item keeps the flex default min-height:auto, so it refuses to
            shrink below its content, overflowY never engages, and the sheet
            grows past its own maxHeight — pushing the send button off the
            bottom of the screen. Confirmed on a 1080x2340 device: the button
            reported bounds [0,0][0,0] and no amount of scrolling reached it,
            so a WhatsApp check-in could not be sent at all. */}
        <div style={{ overflowY: 'auto', marginBottom: 12, flex: 1, minHeight: 0 }}>
          {templates === null ? (
            <div style={{ fontSize: 12.5, color: 'var(--pf-ink-3)', padding: '12px 0' }}>Loading templates…</div>
          ) : templates.length === 0 ? (
            <div style={{ fontSize: 12.5, color: 'var(--pf-ink-3)', padding: '12px 0' }}>No templates available.</div>
          ) : (
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 12 }}>
              {templates.map(t => (
                <button key={t.kind} onClick={() => pick(t)} style={{
                  padding: '7px 12px', borderRadius: 999,
                  background: selected === t.kind ? 'var(--pf-green)' : 'var(--pf-surface)',
                  color: selected === t.kind ? '#fff' : 'var(--pf-ink-2)',
                  border: `1px solid ${selected === t.kind ? 'var(--pf-green)' : 'var(--pf-line)'}`,
                  fontSize: 12, fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit',
                }}>{t.label}</button>
              ))}
            </div>
          )}
          <textarea className="pf-textarea" value={draft} onChange={e => setDraft(e.target.value)}
            placeholder="Pick a template above, or type your own…" style={{ minHeight: 100 }} />
        </div>
        <button className="pf-btn pf-btn-block" style={{ flexShrink: 0 }} disabled={sending || !draft.trim()} onClick={send}>
          {sending ? 'Opening WhatsApp…' : 'Send via WhatsApp'}
        </button>
      </div>
    </div>
  );
};

Object.assign(window, { PFScreenLeads, PFScreenLead, PFScreenAddLead, PFScreenDeletedLeads });



// ===== 08-pf-screens-3.jsx =====
/* global React, PFIcon, PFHeader, PFBackBtn, PF_DATA, iconBtn, linkBtn, SectionTitle */

// ─────────────────────────────────────────────────────────────
// CALL SCREEN (active call with live script + notes + qualification + send sheet)
// ─────────────────────────────────────────────────────────────
const PFScreenCallActive = ({ go, leadId }) => {
  const lead = PF_DATA.leads.find(l => l.id === leadId) || PF_DATA.leads[0];
  const [seconds, setSeconds] = React.useState(0);
  const [recording, setRecording] = React.useState(true);
  const [uploadStatus, setUploadStatus] = React.useState(null); // null | 'uploading' | 'saved' | 'error'
  const mediaRecRef  = React.useRef(null);
  const audioChunks  = React.useRef([]);
  const startTimeRef = React.useRef(Date.now());

  // Start MediaRecorder as soon as the call screen mounts
  React.useEffect(() => {
    let mr = null;
    // Why capture failed, reported rather than swallowed. Both branches below
    // used to fail silently — the guard simply skipped, and the catch was
    // empty — so a device that cannot record looked identical to one that
    // recorded fine, and the screen went on to claim the recording was saved.
    const reportCapture = (stage, detail) => {
      try { setUploadStatus('error'); } catch (e) {}
      try {
        const base = (window.SRAPI && window.SRAPI._base) || '';
        fetch(base + '/api/errors/frontend', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            source: 'call-recording',
            level: 'error',
            message: 'capture unavailable: ' + stage + ': ' + detail,
            page: '/call-active',
            payload: {
              hasMediaRecorder: typeof MediaRecorder !== 'undefined',
              hasMediaDevices: !!navigator.mediaDevices,
              hasGetUserMedia: !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia),
              isSecureContext: window.isSecureContext,
              origin: location.origin,
              ua: navigator.userAgent,
            },
          }),
        }).catch(function () {});
      } catch (e) {}
    };

    if (!(typeof MediaRecorder !== 'undefined' && navigator.mediaDevices && navigator.mediaDevices.getUserMedia)) {
      reportCapture('unsupported',
        'MediaRecorder=' + (typeof MediaRecorder !== 'undefined') +
        ' mediaDevices=' + !!navigator.mediaDevices +
        ' getUserMedia=' + !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia) +
        ' secureContext=' + window.isSecureContext);
    }

    if (typeof MediaRecorder !== 'undefined' && navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
      navigator.mediaDevices.getUserMedia({ audio: SR_AUDIO_CONSTRAINTS }).then(stream => {
        const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus') ? 'audio/webm;codecs=opus'
                       : MediaRecorder.isTypeSupported('audio/mp4') ? 'audio/mp4'
                       : '';
        mr = new MediaRecorder(stream, Object.assign(
        { audioBitsPerSecond: SR_AUDIO_BITS_PER_SECOND },
        mimeType ? { mimeType } : null
      ));
        mr.ondataavailable = e => { if (e.data && e.data.size > 0) audioChunks.current.push(e.data); };
        mr.start(1000); // collect chunks every second
        mediaRecRef.current = mr;
      }).catch(err => reportCapture('getUserMedia', (err && (err.name + ': ' + err.message)) || String(err)));
    }
    return () => {
      if (mr && mr.state !== 'inactive') mr.stop();
      mr?.stream?.getTracks().forEach(t => t.stop());
    };
  }, []);

  // Upload helper — called after recorder stops
  async function uploadAudioBlob(blob, ext) {
    setUploadStatus('uploading');
    try {
      const durationSec = Math.round((Date.now() - startTimeRef.current) / 1000);
      const fd = new FormData();
      fd.append('audio', blob, `call_${Date.now()}.${ext}`);
      if (lead.id)      fd.append('lead_id',      lead.id);
      const clid = lead.callLogId || lead.call_log_id;
      if (clid) fd.append('call_log_id', clid);
      fd.append('duration_sec', durationSec);
      const base = (window.SRAPI && window.SRAPI._base) || '';
      const token = localStorage.getItem('sr_access_token') || '';
      const resp = await fetch(`${base}/api/audio-intelligence/upload`, {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: fd,
      });
      if (!resp.ok) throw new Error(await resp.text());
      setUploadStatus('saved');
    } catch (e) {
      console.warn('[recording] upload failed:', e.message);
      setUploadStatus('error');
    }
  }

  function stopAndUpload() {
    const mr = mediaRecRef.current;
    if (!mr || mr.state === 'inactive') return;
    mr.onstop = () => {
      const mimeType = mr.mimeType || 'audio/webm';
      const ext = mimeType.includes('mp4') ? 'm4a' : 'webm';
      const blob = new Blob(audioChunks.current, { type: mimeType });
      if (blob.size > 1000) uploadAudioBlob(blob, ext);
      mr.stream && mr.stream.getTracks().forEach(t => t.stop());
    };
    mr.stop();
  }

  function toggleRecording() {
    const mr = mediaRecRef.current;
    if (mr) {
      if (recording && mr.state === 'recording') mr.pause();
      else if (!recording && mr.state === 'paused') mr.resume();
    }
    setRecording(r => !r);
  }
  // This app has no real telephony bridge (Exotel or otherwise) — every
  // call opens the device's native dialer, full stop. This previously
  // pretended to "connect via Exotel" first by calling SRApi.callLead,
  // a client method that was never defined anywhere, guaranteeing that
  // branch could never actually run — every call silently landed on this
  // exact native-dialer fallback regardless. Simplified to be honest about
  // the one real path instead of dead code standing in for a fake one.
  React.useEffect(() => {
    const t = setTimeout(() => window.callNative && window.callNative(lead.phone), 300);
    return () => clearTimeout(t);
  }, [lead.phone]);
  const [scriptIdx, setScriptIdx] = React.useState(0);
  const [scriptLang, setScriptLang] = React.useState('hinglish');
  const [note, setNote] = React.useState('');
  const [showDisposition, setShowDisposition] = React.useState(false);
  const [confirmEnd, setConfirmEnd]           = React.useState(false);
  const [qual, setQual] = React.useState({ budget: null, intent: null, timeline: null });
  const [timelineOther, setTimelineOther] = React.useState('');
  const [sendSheet, setSendSheet] = React.useState(false);
  const [panel, setPanel] = React.useState('script'); // 'script' | 'qualify'
  const [scripts, setScripts] = React.useState(PF_DATA.scripts || []);

  React.useEffect(() => {
    const t = setInterval(() => setSeconds(s => s + 1), 1000);
    return () => clearInterval(t);
  }, []);

  // Fetch live scripts from backend on mount; fall back to PF_DATA.scripts
  React.useEffect(() => {
    if (!window.SRAPI || !window.SRAPI._token) return;
    window.SRAPI._get('/api/mobile/scripts').then(res => {
      if (res && Array.isArray(res.scripts) && res.scripts.length > 0) {
        setScripts(res.scripts);
        PF_DATA.scripts = res.scripts; // keep in sync for nav script buttons
      }
    }).catch(() => {});
  }, []);

  const mm = String(Math.floor(seconds / 60)).padStart(2, '0');
  const ss = String(seconds % 60).padStart(2, '0');
  const script = scripts[scriptIdx];
  // Script text is written with {agentName}/{leadName} tokens (both the
  // demo seed and real telecalling_scripts rows use this convention) —
  // previously rendered verbatim, so every agent's script literally read
  // "main Vinay bol raha hoon" regardless of who was actually on the call.
  const agentFirstName = (() => {
    const u = (window.SRAPI && typeof window.SRAPI.getUser === 'function') ? window.SRAPI.getUser() : null;
    const name = (u && u.name) || (PF_DATA.user && PF_DATA.user.name) || '';
    return name.split(' ')[0] || 'the team';
  })();
  const leadFirstName = (lead.name || '').split(' ')[0] || 'there';
  const interpolateScript = (s) => (s || '')
    .replace(/\{agentName\}/g, agentFirstName)
    .replace(/\{leadName\}/g, leadFirstName);
  const qualScore = Object.values(qual).filter(v => v === 'yes').length;

  if (showDisposition) {
    return <PFScreenDisposition go={go} lead={lead} duration={`${mm}:${ss}`} qual={qual} uploadStatus={uploadStatus} />;
  }

  return (
    <div className="pf-screen pf-anim-in" style={{ background: '#0b3a24', color: '#fff' }}>
      {/* Top bar */}
      <div style={{ padding: '12px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <button onClick={() => go('lead', lead.id)} style={{
          width: 36, height: 36, borderRadius: '50%', background: 'rgba(255,255,255,0.1)',
          border: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', cursor: 'pointer',
        }}>
          <PFIcon name="chevD" size={18} color="#fff" />
        </button>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 10.5, letterSpacing: '0.16em', opacity: 0.7, fontWeight: 600 }}>ON CALL · OBJECTIVE: BOOK SITE VISIT</div>
          <div style={{ fontFamily: 'var(--pf-mono)', fontSize: 13, fontWeight: 500, marginTop: 2 }}>{mm}:{ss}</div>
        </div>
        {uploadStatus === 'uploading' ? (
          <div style={{ display:'flex', alignItems:'center', gap:5, fontSize:11, fontWeight:600, color:'#fbbf24' }}>
            <span style={{ width:8, height:8, borderRadius:'50%', background:'#fbbf24', animation:'pfRec 1s infinite' }} />
            SAVING…
          </div>
        ) : uploadStatus === 'saved' ? (
          <div style={{ fontSize:11, fontWeight:600, color:'#34d399' }}>✓ SAVED</div>
        ) : uploadStatus === 'error' ? (
          <div style={{ fontSize:11, fontWeight:600, color:'#f87171' }}>⚠ ERR</div>
        ) : recording ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, fontWeight: 600 }}>
            <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#e26b5a', animation: 'pfRec 1s infinite' }} />
            REC
          </div>
        ) : <div style={{ width: 36 }} />}
      </div>

      {/* Contact */}
      <div style={{ textAlign: 'center', padding: '8px 16px 14px' }}>
        <div style={{
          width: 68, height: 68, margin: '0 auto', borderRadius: '50%',
          background: 'rgba(255,255,255,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontFamily: 'var(--pf-serif)', fontSize: 26, fontWeight: 600,
        }}>{lead.initials}</div>
        <div style={{ fontFamily: 'var(--pf-serif)', fontSize: 22, fontWeight: 600, marginTop: 10 }}>{lead.name}</div>
        <div style={{ opacity: 0.7, fontSize: 12.5, marginTop: 2 }}>{lead.phone} · {lead.project}</div>
        {/* Dialer status — always the native dialer, see effect above */}
        <div style={{
          display: 'inline-flex', alignItems: 'center', gap: 6, marginTop: 8,
          padding: '4px 12px', borderRadius: 999,
          background: 'rgba(251,191,36,0.2)', border: '1px solid rgba(251,191,36,0.4)',
        }}>
          <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#fbbf24', display: 'inline-block' }} />
          <span style={{ fontSize: 11, fontWeight: 600, color: 'rgba(255,255,255,0.85)' }}>Opening native dialer…</span>
        </div>
        {/* ADDITION 1: native dialer fallback button — always visible */}
        <div style={{ marginTop: 8 }}>
          <button onClick={() => window.callNative && window.callNative(lead.phone)} style={{
            background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.25)',
            borderRadius: 999, padding: '6px 16px', color: '#fff', cursor: 'pointer',
            fontFamily: 'inherit', fontSize: 12, fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 6,
          }}>
            📞 Call via native dialer
          </button>
        </div>
      </div>

      {/* Panel toggle */}
      <div style={{ padding: '0 16px 10px' }}>
        <div style={{ display: 'flex', background: 'rgba(255,255,255,0.08)', borderRadius: 10, padding: 3 }}>
          {[['script', 'Script'], ['qualify', `Qualify · ${qualScore}/3`]].map(([id, label]) => (
            <button key={id} onClick={() => setPanel(id)} style={{
              flex: 1, padding: '7px 10px', borderRadius: 8, border: 0, fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
              background: panel === id ? 'rgba(255,255,255,0.95)' : 'transparent',
              color: panel === id ? 'var(--pf-ink)' : 'rgba(255,255,255,0.75)',
            }}>{label}</button>
          ))}
        </div>
      </div>

      {/* Panel */}
      <div className="pf-scroll" style={{ padding: '0 16px' }}>
        {panel === 'script' ? (
          <div style={{ background: '#fffdf5', color: 'var(--pf-ink)', borderRadius: 18, padding: 14, border: '1px solid #e9dfb8' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <PFIcon name="book" size={14} color="var(--pf-green)" />
                <span style={{ fontSize: 10.5, letterSpacing: '0.1em', color: 'var(--pf-green)', fontWeight: 700 }}>LIVE SCRIPT</span>
              </div>
              <div style={{ display: 'flex', background: 'var(--pf-line-2)', borderRadius: 8, padding: 2 }}>
                {[['hinglish', 'हिं+EN'], ['english', 'EN']].map(([id, label]) => (
                  <button key={id} onClick={() => setScriptLang(id)} style={{
                    padding: '4px 10px', borderRadius: 6, border: 0, fontSize: 11, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
                    background: scriptLang === id ? 'var(--pf-surface)' : 'transparent',
                    color: scriptLang === id ? 'var(--pf-ink)' : 'var(--pf-ink-3)',
                  }}>{label}</button>
                ))}
              </div>
            </div>
            <div style={{ fontFamily: 'var(--pf-serif)', fontSize: 15, fontWeight: 600, marginBottom: 8 }}>{script.title}</div>
            <div style={{ fontSize: 14, lineHeight: 1.5, color: 'var(--pf-ink-2)' }}>
              {interpolateScript(scriptLang === 'hinglish' ? script.hinglish : script.english)}
            </div>
            <div style={{ display: 'flex', gap: 6, marginTop: 12 }}>
              <button className="pf-btn pf-btn-light pf-btn-sm" disabled={scriptIdx === 0}
                onClick={() => setScriptIdx(i => Math.max(0, i - 1))}
                style={{ flex: 1, opacity: scriptIdx === 0 ? 0.4 : 1 }}>
                <PFIcon name="chevL" size={12} /> Prev
              </button>
              <button className="pf-btn pf-btn-light pf-btn-sm" disabled={scriptIdx >= scripts.length - 1}
                onClick={() => setScriptIdx(i => Math.min(scripts.length - 1, i + 1))}
                style={{ flex: 1, opacity: scriptIdx >= scripts.length - 1 ? 0.4 : 1 }}>
                Next <PFIcon name="chevR" size={12} />
              </button>
            </div>
          </div>
        ) : (
          <div style={{ background: '#fffdf5', color: 'var(--pf-ink)', borderRadius: 18, padding: 14, border: '1px solid #e9dfb8' }}>
            <div style={{ fontSize: 10.5, letterSpacing: '0.1em', color: 'var(--pf-green)', fontWeight: 700, marginBottom: 10 }}>
              QUALIFY WHILE TALKING
            </div>
            {[
              { k: 'budget', q: 'Budget matches?', hint: 'Ask: "Aap ka budget range kya hai?"' },
              { k: 'intent', q: 'Genuinely interested?', hint: 'Gauge tone, questions about specifics' },
            ].map(({ k, q, hint }) => (
              <div key={k} style={{ marginBottom: 10 }}>
                <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 2 }}>{q}</div>
                <div style={{ fontSize: 11, color: 'var(--pf-ink-3)', marginBottom: 6 }}>{hint}</div>
                <div style={{ display: 'flex', gap: 6 }}>
                  {['yes', 'maybe', 'no'].map(v => (
                    <button key={v} onClick={() => setQual(p => ({ ...p, [k]: v }))} style={{
                      flex: 1, padding: '7px 10px', borderRadius: 8, fontSize: 11.5, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
                      border: `1px solid ${qual[k] === v ? 'var(--pf-green)' : 'var(--pf-line)'}`,
                      background: qual[k] === v ? 'var(--pf-green)' : 'var(--pf-surface)',
                      color: qual[k] === v ? '#fff' : 'var(--pf-ink-2)',
                      textTransform: 'capitalize',
                    }}>{v}</button>
                  ))}
                </div>
              </div>
            ))}
            {/* Timeline — custom options instead of yes/maybe/no */}
            <div style={{ marginBottom: 10 }}>
              <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 2 }}>When do you plan to buy?</div>
              <div style={{ fontSize: 11, color: 'var(--pf-ink-3)', marginBottom: 6 }}>Ask: "Kab tak plan kar rahe hain?"</div>
              <div style={{ display: 'flex', gap: 6 }}>
                {['1 month', '1–3 months', 'Other'].map(v => (
                  <button key={v} onClick={() => { setQual(p => ({ ...p, timeline: v })); if (v !== 'Other') setTimelineOther(''); }} style={{
                    flex: 1, padding: '7px 6px', borderRadius: 8, fontSize: 11, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
                    border: `1px solid ${qual.timeline === v ? 'var(--pf-green)' : 'var(--pf-line)'}`,
                    background: qual.timeline === v ? 'var(--pf-green)' : 'var(--pf-surface)',
                    color: qual.timeline === v ? '#fff' : 'var(--pf-ink-2)',
                  }}>{v}</button>
                ))}
              </div>
              {qual.timeline === 'Other' && (
                <input
                  value={timelineOther}
                  onChange={e => setTimelineOther(e.target.value)}
                  placeholder="e.g. 6 months, after Diwali…"
                  style={{
                    marginTop: 8, width: '100%', padding: '8px 10px', borderRadius: 8, fontSize: 12.5,
                    border: '1px solid var(--pf-line)', background: 'var(--pf-surface)',
                    color: 'var(--pf-ink)', fontFamily: 'inherit',
                  }}
                />
              )}
            </div>
          </div>
        )}

        {/* Send / templates quick actions */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginTop: 10 }}>
          <button onClick={() => setSendSheet(true)} style={{
            background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.18)',
            borderRadius: 12, padding: '10px 12px', color: '#fff', cursor: 'pointer', fontFamily: 'inherit',
            display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, fontWeight: 600,
          }}>
            <PFIcon name="doc" size={16} color="#fff" /> Send brochure
          </button>
          <button onClick={() => setSendSheet(true)} style={{
            background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.18)',
            borderRadius: 12, padding: '10px 12px', color: '#fff', cursor: 'pointer', fontFamily: 'inherit',
            display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, fontWeight: 600,
          }}>
            <PFIcon name="chat" size={16} color="#fff" /> Send template
          </button>
        </div>

        {/* Quick notes */}
        <div style={{ marginTop: 10, background: 'rgba(255,255,255,0.08)', borderRadius: 14, padding: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
            <PFIcon name="note" size={14} color="#fff" />
            <span style={{ fontSize: 10.5, letterSpacing: '0.1em', fontWeight: 700, opacity: 0.8 }}>QUICK NOTES</span>
          </div>
          <textarea value={note} onChange={e => setNote(e.target.value)}
            placeholder="Jot down what they said…"
            style={{
              width: '100%', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.15)',
              borderRadius: 10, color: '#fff', padding: 10, fontFamily: 'inherit', fontSize: 13, resize: 'none', minHeight: 54,
            }} />
          <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
            {['Interested', 'SV booked', 'Price objection', 'Callback', 'Loan query'].map(t => (
              <button key={t} onClick={() => setNote(n => (n ? n + ' · ' : '') + t)} style={{
                padding: '4px 10px', borderRadius: 999, border: '1px solid rgba(255,255,255,0.2)',
                background: 'rgba(255,255,255,0.05)', color: '#fff', fontSize: 11, fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit',
              }}>+ {t}</button>
            ))}
          </div>
        </div>
        <div style={{ height: 12 }} />
      </div>

      {/* Call controls — this screen hands the call off to the native dialer
          (see PFScreenCallActive above); there's no in-app audio bridge to
          mute, and no "more options" menu behind anything real, so those two
          controls used to render with no onClick at all. A dead button
          mid-call is worse than no button — removed rather than left as a
          decorative no-op. */}
      {/* Bottom padding must clear the system navigation bar, which is drawn
          translucently over the WebView: with a flat 20px the hangup button
          sat underneath it, so taps near its lower half were swallowed by the
          nav bar instead of the button (confirmed on device — a tap at the
          button's centre did nothing while one slightly higher worked).
          .pf-tabbar already does this; these controls did not. */}
      <div style={{ padding: '10px 16px max(20px, env(safe-area-inset-bottom))', borderTop: '1px solid rgba(255,255,255,0.08)' }}>
        <div style={{ display: 'flex', justifyContent: 'center', gap: 16, alignItems: 'center' }}>
          <CtlBtn icon="record" label={recording ? 'Pause' : 'Record'}
            active={recording} onClick={toggleRecording} />
          <button onClick={() => setConfirmEnd(true)} style={{
            width: 60, height: 60, borderRadius: '50%', background: '#e26b5a', border: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
            boxShadow: '0 8px 24px rgba(226,107,90,0.4)',
          }}>
            <PFIcon name="phone" size={22} color="#fff" strokeWidth={2} />
          </button>
          <CtlBtn icon="doc" label="Send" onClick={() => setSendSheet(true)} />
        </div>
      </div>

      {sendSheet && <SendSheet lead={lead} onClose={() => setSendSheet(false)} />}

      {/* End call confirmation dialog */}
      {confirmEnd && (
        <div style={{ position:'fixed', inset:0, zIndex:100, display:'flex', alignItems:'flex-end', justifyContent:'center' }}>
          <div style={{ position:'absolute', inset:0, background:'rgba(0,0,0,0.6)' }} onClick={() => setConfirmEnd(false)} />
          <div style={{ position:'relative', background:'#1a1a1a', borderRadius:'20px 20px 0 0', width:'100%', padding:'20px 20px 36px' }}>
            <div style={{ width:36, height:4, background:'rgba(255,255,255,0.2)', borderRadius:2, margin:'0 auto 18px' }} />
            <div style={{ fontSize:16, fontWeight:700, color:'#fff', textAlign:'center', marginBottom:6 }}>End call?</div>
            <div style={{ fontSize:13, color:'rgba(255,255,255,0.55)', textAlign:'center', marginBottom:20 }}>
              {lead.name} · {String(Math.floor(seconds/60)).padStart(2,'0')}:{String(seconds%60).padStart(2,'0')} elapsed
            </div>
            <button onClick={() => { setConfirmEnd(false); stopAndUpload(); setShowDisposition(true); }}
              style={{ width:'100%', padding:'14px', borderRadius:14, background:'#e26b5a', border:'none', color:'#fff', fontWeight:700, fontSize:15, cursor:'pointer', marginBottom:10 }}>
              End call
            </button>
            <button onClick={() => setConfirmEnd(false)}
              style={{ width:'100%', padding:'14px', borderRadius:14, background:'rgba(255,255,255,0.1)', border:'none', color:'#fff', fontWeight:600, fontSize:15, cursor:'pointer' }}>
              Keep talking
            </button>
          </div>
        </div>
      )}

      <style>{`@keyframes pfRec { 0%, 100% { opacity: 1 } 50% { opacity: 0.3 } }`}</style>
    </div>
  );
};

// Does a delivered creative asset's project match the lead's project of
// interest? Real lead.project is free text like "Whispering Oaks · 3BHK";
// asset.project is the creative request's project_name, e.g. "Whispering
// Oaks" — substring match either direction covers both shapes.
function projectsMatch(leadProject, assetProject) {
  if (!leadProject || !assetProject) return false;
  const a = leadProject.toLowerCase();
  const b = assetProject.toLowerCase();
  return a.includes(b) || b.includes(a);
}

const SendSheet = ({ lead, onClose }) => {
  const [tab, setTab] = React.useState('brochure');
  const [selected, setSelected] = React.useState(null);
  const [brochures, setBrochures] = React.useState(null); // null = loading
  const [confirmSend, setConfirmSend] = React.useState(null); // { it, phone, msg } | null
  const [sending, setSending] = React.useState(false);

  // Real delivered creative assets — same source Assets screen uses (see
  // 10-pf-screens-5.jsx). "Send brochure" previously listed a hardcoded
  // fake file array and sent a broken placeholder link
  // (skaledesk.com/brochures/{id}, which resolves nowhere) while claiming
  // "auto-logged" underneath with nothing actually being logged anywhere.
  React.useEffect(() => {
    let cancelled = false;
    async function load() {
      if (!window.SRAPI || !window.SRAPI._token) { setBrochures([]); return; }
      try {
        const rows = await window.SRAPI._get('/api/creative/requests?status=delivered');
        const withDeliverables = (Array.isArray(rows) ? rows : []).filter(r => Number(r.deliverable_count) > 0);
        const details = await Promise.all(
          withDeliverables.map(r => window.SRAPI._get('/api/creative/requests/' + r.id).catch(() => null))
        );
        const files = details.filter(Boolean).flatMap(r =>
          (r.files || []).filter(f => f.file_role === 'deliverable' || f.file_role === 'revision_deliverable')
            .map(f => ({ id: f.id, title: r.title, sub: f.filename, url: f.url, project: r.project_name, icon: 'doc' }))
        );
        if (!cancelled) {
          setBrochures(files);
          // Default-select the brochure for the lead's own project — a
          // presales call is usually about one specific project, but the
          // list stays fully editable below for when the lead's actually
          // interested in something else and gets pitched a different one.
          const match = files.find(f => projectsMatch(lead.project, f.project));
          if (match) setSelected(match.id);
        }
      } catch (e) {
        if (!cancelled) setBrochures([]);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [lead.project]);

  const msgTemplates = PF_DATA.templates.filter(t => t.kind === 'msg');
  const items = tab === 'brochure' ? (brochures || []) : msgTemplates;

  const buildSend = () => {
    if (!selected) return null;
    const it = items.find(i => i.id === selected);
    if (!it) return null;
    const phone = (lead.phone || '').replace(/\D/g, '');
    let msg;
    if (tab === 'brochure') {
      msg = `Hi ${lead.name} 👋\n\nAs discussed, here is the *${it.project || it.title}* brochure for your reference.\n\n📄 *${it.sub}*\n\n${it.url}\n\nFeel free to call me for any questions. Looking forward to arranging your site visit! 🏡`;
    } else {
      msg = (it?.sub || '').replace('{name}', lead.name);
    }
    return { it, phone, msg };
  };

  const doSend = async () => {
    if (!confirmSend) return;
    const { it, phone, msg } = confirmSend;
    setSending(true);
    try {
      if (window.SRAPI && typeof window.SRAPI.shareAsset === 'function') {
        await window.SRAPI.shareAsset(lead.id, it.id, it.sub || it.title, 'whatsapp', tab === 'msg' ? it.id : null, msg).catch(() => {});
      }
    } finally {
      const url = phone ? `https://wa.me/${phone}?text=${encodeURIComponent(msg)}` : `https://wa.me/?text=${encodeURIComponent(msg)}`;
      window.open(url, '_blank');
      setSending(false);
      setConfirmSend(null);
      onClose();
    }
  };

  return (
    <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'flex-end', zIndex: 30 }}
      onClick={onClose}>
      <div style={{ background: '#fffdf5', color: 'var(--pf-ink)', width: '100%', borderRadius: '20px 20px 0 0', padding: 16, maxHeight: '78%', display: 'flex', flexDirection: 'column' }}
        onClick={e => e.stopPropagation()}>
        <div style={{ width: 36, height: 4, background: 'var(--pf-line)', borderRadius: 2, margin: '0 auto 10px' }} />
        <div style={{ fontFamily: 'var(--pf-serif)', fontSize: 18, fontWeight: 600, marginBottom: 2 }}>Send to {lead.name}</div>
        <div style={{ fontSize: 12, color: 'var(--pf-ink-3)', marginBottom: 12 }}>Sent via WhatsApp · logged to this lead's activity</div>
        <div style={{ display: 'flex', background: 'var(--pf-line-2)', borderRadius: 10, padding: 3, marginBottom: 12 }}>
          {[['brochure', 'Brochures'], ['msg', 'Message templates']].map(([id, l]) => (
            <button key={id} onClick={() => { setTab(id); setSelected(null); }} style={{
              flex: 1, padding: '7px 10px', borderRadius: 8, border: 0, fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
              background: tab === id ? 'var(--pf-surface)' : 'transparent',
              color: tab === id ? 'var(--pf-ink)' : 'var(--pf-ink-3)',
            }}>{l}</button>
          ))}
        </div>
        <div style={{ overflow: 'auto', flex: 1 }}>
          {tab === 'brochure' && brochures === null && (
            <div style={{ padding: '20px 4px', textAlign: 'center', color: 'var(--pf-ink-3)', fontSize: 12.5 }}>Loading brochures…</div>
          )}
          {tab === 'brochure' && brochures !== null && brochures.length === 0 && (
            <div style={{ padding: '20px 4px', textAlign: 'center', color: 'var(--pf-ink-3)', fontSize: 12.5 }}>No approved brochures yet. Request one from the Assets tab.</div>
          )}
          {items.map(it => (
            <div key={it.id} onClick={() => setSelected(it.id)} style={{
              display: 'flex', gap: 10, padding: 10, borderRadius: 12, marginBottom: 6, cursor: 'pointer',
              background: selected === it.id ? 'var(--pf-green-tint)' : 'transparent',
              border: `1px solid ${selected === it.id ? 'var(--pf-green-tint-2)' : 'var(--pf-line)'}`,
            }}>
              <div style={{
                width: 36, height: 36, borderRadius: 10, flex: '0 0 36px',
                background: tab === 'brochure' ? 'var(--pf-blue-tint)' : 'var(--pf-green-tint)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <PFIcon name={it.icon} size={16} color={tab === 'brochure' ? 'var(--pf-blue)' : 'var(--pf-green)'} />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <div style={{ fontWeight: 600, fontSize: 13 }}>{tab === 'brochure' ? it.project : it.title}</div>
                  {tab === 'brochure' && projectsMatch(lead.project, it.project) && (
                    <span style={{ fontSize: 9.5, fontWeight: 700, color: 'var(--pf-green)', background: 'var(--pf-green-tint)', padding: '1px 6px', borderRadius: 6 }}>THIS LEAD</span>
                  )}
                </div>
                <div style={{ fontSize: 11.5, color: 'var(--pf-ink-3)', marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{tab === 'brochure' ? it.sub : it.sub}</div>
              </div>
              {selected === it.id && <PFIcon name="check" size={16} color="var(--pf-green)" />}
            </div>
          ))}
        </div>
        <div style={{ display:'flex', gap:8, marginTop:10 }}>
          <button className="pf-btn pf-btn-block" disabled={!selected} style={{ flex:2 }} onClick={() => setConfirmSend(buildSend())}>
            <PFIcon name="send" size={14} color="#fff" /> Send via WhatsApp
          </button>
          <button onClick={() => {
            if (!selected) return;
            const it = items.find(i => i.id === selected);
            const s = encodeURIComponent(tab === 'brochure' ? it.project : it.title);
            const b = encodeURIComponent(tab === 'brochure'
              ? `Hi ${lead.name},\n\nAs discussed, please find the ${it.project} brochure here:\n${it.url}\n\nLet me know if you have any questions.\n\nBest regards`
              : (it?.sub || '').replace('{name}', lead.name));
            window.location.href = `mailto:?subject=${s}&body=${b}`;
            onClose();
          }}
            disabled={!selected}
            style={{ flex:1, display:'flex', alignItems:'center', justifyContent:'center', gap:6, padding:'12px', borderRadius:12, background: selected ? '#eff6ff' : '#f3f4f6', border:`1px solid ${selected ? '#bfdbfe' : '#e5e7eb'}`, color: selected ? '#1d4ed8' : '#9ca3af', fontWeight:700, fontSize:12.5, cursor: selected ? 'pointer' : 'default', fontFamily:'inherit' }}>
            ✉️ Email
          </button>
        </div>
      </div>
      {confirmSend && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 40, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}
          onClick={() => !sending && setConfirmSend(null)}>
          <div style={{ background: '#fffdf5', color: 'var(--pf-ink)', borderRadius: 16, padding: 20, width: '100%', maxWidth: 340 }}
            onClick={e => e.stopPropagation()}>
            <div style={{ fontFamily: 'var(--pf-serif)', fontSize: 16, fontWeight: 600, marginBottom: 8 }}>Confirm send</div>
            <div style={{ fontSize: 13, color: 'var(--pf-ink-2)', lineHeight: 1.5, marginBottom: 16 }}>
              Send <strong>{tab === 'brochure' ? `${confirmSend.it.project} brochure (${confirmSend.it.sub})` : confirmSend.it.title}</strong> to <strong>{lead.name}</strong> via WhatsApp?
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={() => setConfirmSend(null)} disabled={sending} style={{ flex: 1, padding: '11px', borderRadius: 10, background: 'var(--pf-line-2)', border: 0, color: 'var(--pf-ink-2)', fontWeight: 600, fontSize: 13, cursor: 'pointer', fontFamily: 'inherit' }}>Cancel</button>
              <button onClick={doSend} disabled={sending} className="pf-btn" style={{ flex: 1 }}>{sending ? 'Sending…' : 'Confirm & send'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const CtlBtn = ({ icon, label, active, onClick }) => (
  <button onClick={onClick} style={{
    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, background: 'transparent', border: 0,
    color: '#fff', cursor: 'pointer', fontFamily: 'inherit',
  }}>
    <div style={{
      width: 46, height: 46, borderRadius: '50%',
      background: active ? 'rgba(226,107,90,0.2)' : 'rgba(255,255,255,0.1)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      border: active ? '1px solid rgba(226,107,90,0.5)' : '1px solid transparent',
    }}>
      <PFIcon name={icon} size={18} color={active ? '#e26b5a' : '#fff'} />
    </div>
    <span style={{ fontSize: 10.5, opacity: 0.8 }}>{label}</span>
  </button>
);

// ─────────────────────────────────────────────────────────────
// POST-CALL DISPOSITION
// ─────────────────────────────────────────────────────────────
const PFScreenDisposition = ({ go, lead, duration, qual, uploadStatus }) => {
  const [status, setStatus] = React.useState('Interested');
  const [nextStage, setNextStage] = React.useState('Site Visit Scheduled');
  const [svBooked, setSvBooked] = React.useState(false);
  const [svDate, setSvDate] = React.useState(() => { const d = new Date(); d.setDate(d.getDate() + 2); return d.toISOString().split('T')[0]; });
  const [svTime, setSvTime] = React.useState('11:00');
  const [nextTouchpoint, setNextTouchpoint] = React.useState('Tomorrow 11:00 AM');
  const [customDate, setCustomDate] = React.useState('');
  const [customTime, setCustomTime] = React.useState('');
  const [note, setNote] = React.useState('');
  const [saving, setSaving] = React.useState(false);
  const [saved, setSaved] = React.useState(false);
  const [saveError, setSaveError] = React.useState('');
  const statuses = ['Interested', 'Qualified', 'Not Interested', 'Callback', 'No Answer', 'Wrong Number'];
  const stages = ['New Lead', 'Qualified', 'Site Visit Scheduled', 'Negotiation', 'Booked'];
  const qScore = qual ? Object.values(qual).filter(v => ['yes', '1 month', '1–3 months'].includes(v)).length : 0;

  // Turns the "Next touchpoint" selection into an actual date the backend
  // can store, instead of the label alone.
  const followUpDateISO = () => {
    if (nextTouchpoint === 'Custom…') {
      if (!customDate) return null;
      return new Date(`${customDate}T${customTime || '09:00'}`).toISOString();
    }
    if (nextTouchpoint === 'In 2 hours') return new Date(Date.now() + 2 * 3600 * 1000).toISOString();
    if (nextTouchpoint === 'Today 5:00 PM') { const d = new Date(); d.setHours(17, 0, 0, 0); return d.toISOString(); }
    if (nextTouchpoint === 'Tomorrow 11:00 AM') { const d = new Date(); d.setDate(d.getDate() + 1); d.setHours(11, 0, 0, 0); return d.toISOString(); }
    return null;
  };

  const handleSave = async () => {
    if (!window.SRAPI || typeof window.SRAPI.logCall !== 'function') {
      setSaveError('Not connected — could not save this call. Check your connection and try again.');
      return;
    }
    setSaving(true);
    setSaveError('');
    try {
      await window.SRAPI.logCall(lead.id, {
        outcome: status,
        notes: note.trim(),
        duration,
        followUpDate: followUpDateISO(),
        followUpTask: nextTouchpoint === 'Custom…'
          ? (customDate ? `Follow up ${customDate} ${customTime}` : '')
          : nextTouchpoint,
        qualification: qual,
      });
      if (nextStage && typeof window.SRAPI.updateLeadStage === 'function') {
        // Non-fatal — the call itself is already logged even if the stage
        // move fails (e.g. an invalid transition), so don't block on it.
        await window.SRAPI.updateLeadStage(lead.id, nextStage).catch(() => {});
      }
      if (window.SRAPI.sync) await window.SRAPI.sync().catch(() => {});
      setSaving(false);
      setSaved(true);
      setTimeout(() => go('calls'), 1400);
    } catch (e) {
      setSaving(false);
      setSaveError((e && e.message) || 'Could not save — please try again.');
    }
  };

  return (
    <div className="pf-screen pf-anim-in">
      <PFHeader
        left={<PFBackBtn onClick={() => go('calls')} />}
        title="Call ended"
        subtitle={`${lead.name} · ${duration}`}
      />
      <div className="pf-scroll" style={{ padding: '0 16px 24px' }}>
        {/* Objective card */}
        <div style={{
          background: svBooked ? 'linear-gradient(135deg, #0b3a24, #135c38)' : 'var(--pf-surface)',
          color: svBooked ? '#fff' : 'var(--pf-ink)',
          border: svBooked ? 0 : '1px solid var(--pf-line)',
          borderRadius: 16, padding: 14, marginBottom: 12,
        }}>
          <div style={{ fontSize: 10.5, letterSpacing: '0.12em', fontWeight: 700, opacity: svBooked ? 0.8 : 0.6, marginBottom: 6 }}>
            CALL OBJECTIVE
          </div>
          <div style={{ fontFamily: 'var(--pf-serif)', fontSize: 18, fontWeight: 600 }}>
            {svBooked ? '✓ Site visit booked' : 'Site visit not yet booked'}
          </div>
          {svBooked ? (
            <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
              <input type="date" value={svDate} onChange={e => setSvDate(e.target.value)}
                style={{ flex: 1, padding: '8px 10px', borderRadius: 10, border: '1px solid rgba(255,255,255,0.3)', background: 'rgba(255,255,255,0.12)', color: '#fff', fontSize: 13, fontFamily: 'inherit', colorScheme: 'dark' }} />
              <input type="time" value={svTime} onChange={e => setSvTime(e.target.value)}
                style={{ width: 100, padding: '8px 10px', borderRadius: 10, border: '1px solid rgba(255,255,255,0.3)', background: 'rgba(255,255,255,0.12)', color: '#fff', fontSize: 13, fontFamily: 'inherit', colorScheme: 'dark' }} />
            </div>
          ) : (
            <div style={{ fontSize: 12.5, opacity: 0.7, marginTop: 4 }}>Primary goal of this call — mark once scheduled.</div>
          )}
          <div style={{ display: 'flex', gap: 6, marginTop: 10 }}>
            <button onClick={() => setSvBooked(true)} style={{
              flex: 1, padding: '8px 10px', borderRadius: 10, border: 0, fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
              background: svBooked ? 'rgba(255,255,255,0.2)' : 'var(--pf-green)', color: '#fff',
            }}>{svBooked ? 'Booked ✓' : 'Mark as booked'}</button>
            <button onClick={() => setSvBooked(false)} style={{
              flex: 1, padding: '8px 10px', borderRadius: 10, fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
              background: 'transparent', border: `1px solid ${svBooked ? 'rgba(255,255,255,0.3)' : 'var(--pf-line)'}`,
              color: svBooked ? '#fff' : 'var(--pf-ink-2)',
            }}>Not yet</button>
          </div>
        </div>

        {/* Qualification score */}
        <div className="pf-card" style={{ marginBottom: 14 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
            <div style={{ fontSize: 11, letterSpacing: '0.1em', fontWeight: 700, color: 'var(--pf-ink-3)' }}>QUALIFICATION</div>
            <div style={{ fontFamily: 'var(--pf-serif)', fontSize: 18, fontWeight: 600, color: qScore >= 2 ? 'var(--pf-green)' : 'var(--pf-amber)' }}>
              {qScore}/3
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 6 }}>
            {[['Budget', qual?.budget], ['Intent', qual?.intent], ['Timeline', qual?.timeline]].map(([l, v]) => (
              <div key={l} style={{
                padding: '8px 6px', borderRadius: 10, textAlign: 'center',
                background: v === 'yes' ? 'var(--pf-green-tint)' : v === 'no' ? '#fbe9e5' : 'var(--pf-amber-tint)',
                border: `1px solid ${v === 'yes' ? 'var(--pf-green-tint-2)' : v === 'no' ? '#f3c9c1' : 'rgba(200,140,40,0.3)'}`,
              }}>
                <div style={{ fontSize: 10.5, color: 'var(--pf-ink-3)', fontWeight: 600 }}>{l}</div>
                <div style={{ fontSize: 12.5, fontWeight: 700, marginTop: 2, textTransform: 'capitalize',
                  color: v === 'yes' ? 'var(--pf-green-ink)' : v === 'no' ? 'var(--pf-red)' : 'var(--pf-amber)' }}>
                  {v || '—'}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* This used to claim "Recording saved / Auto-transcribing…" with a
            green tick and a play button, unconditionally — it was static
            markup, not bound to anything. Verified on device: no audio was
            captured, no upload request ever reached the API, and no
            call_recordings row existed, yet the screen still reported the
            recording as saved and transcribing. Report the real state. */}
        {uploadStatus && (
          <div className="pf-card-tinted" style={{ marginBottom: 14, display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{
              width: 36, height: 36, borderRadius: 12,
              background: uploadStatus === 'error' ? '#dc2626' : 'var(--pf-green)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <PFIcon name={uploadStatus === 'error' ? 'close' : 'check'} size={18} color="#fff" strokeWidth={2.2} />
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 600, fontSize: 13 }}>
                {uploadStatus === 'uploading' ? 'Uploading recording…'
                  : uploadStatus === 'saved' ? 'Recording saved'
                  : 'Recording not saved'}
              </div>
              <div style={{ fontSize: 11.5, color: 'var(--pf-ink-3)', marginTop: 2 }}>
                {uploadStatus === 'saved' ? 'Queued for transcription'
                  : uploadStatus === 'error' ? 'Upload failed — the call itself is still logged'
                  : 'Please keep the app open'}
              </div>
            </div>
            {uploadStatus === 'saved' && <PFIcon name="play" size={18} color="var(--pf-green)" />}
          </div>
        )}

        <div className="pf-field">
          <div className="pf-label">Disposition</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {statuses.map(s => (
              <button key={s} onClick={() => setStatus(s)} style={{
                padding: '8px 14px', borderRadius: 999,
                background: status === s ? 'var(--pf-green)' : 'var(--pf-surface)',
                color: status === s ? '#fff' : 'var(--pf-ink-2)',
                border: `1px solid ${status === s ? 'var(--pf-green)' : 'var(--pf-line)'}`,
                fontSize: 12.5, fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit',
              }}>{s}</button>
            ))}
          </div>
        </div>

        <div className="pf-field">
          <div className="pf-label">Move to stage</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {stages.map(s => (
              <button key={s} onClick={() => setNextStage(s)} style={{
                padding: '8px 14px', borderRadius: 999,
                background: nextStage === s ? 'var(--pf-green-tint)' : 'var(--pf-surface)',
                color: nextStage === s ? 'var(--pf-green-ink)' : 'var(--pf-ink-2)',
                border: `1px solid ${nextStage === s ? 'var(--pf-green-tint-2)' : 'var(--pf-line)'}`,
                fontSize: 12.5, fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit',
              }}>{s}</button>
            ))}
          </div>
        </div>

        <div className="pf-field">
          <div className="pf-label">Summary note</div>
          <textarea className="pf-textarea" value={note} onChange={e => setNote(e.target.value)}
            placeholder="What was discussed, objections raised, next steps…" />
        </div>

        <div className="pf-field">
          <div className="pf-label">Next touchpoint</div>
          <select className="pf-select" value={nextTouchpoint} onChange={e => setNextTouchpoint(e.target.value)}>
            <option>In 2 hours</option>
            <option>Today 5:00 PM</option>
            <option>Tomorrow 11:00 AM</option>
            <option>Custom…</option>
          </select>
          {nextTouchpoint === 'Custom…' && (
            <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
              <input type="date" value={customDate} onChange={e => setCustomDate(e.target.value)}
                style={{ flex: 1, padding: '10px 12px', borderRadius: 10, border: '1px solid var(--pf-line)', background: 'var(--pf-surface)', color: 'var(--pf-ink)', fontSize: 13, fontFamily: 'inherit' }} />
              <input type="time" value={customTime} onChange={e => setCustomTime(e.target.value)}
                style={{ width: 110, padding: '10px 12px', borderRadius: 10, border: '1px solid var(--pf-line)', background: 'var(--pf-surface)', color: 'var(--pf-ink)', fontSize: 13, fontFamily: 'inherit' }} />
            </div>
          )}
        </div>

        {saveError && (
          <div style={{ color: 'var(--pf-red)', fontSize: 12.5, marginBottom: 10 }}>{saveError}</div>
        )}
        {saved && (
          <div style={{ background: 'var(--pf-green-tint)', border: '1px solid var(--pf-green-tint-2)', borderRadius: 14, padding: '12px 14px', marginBottom: 12 }}>
            <div style={{ fontWeight: 700, fontSize: 13, color: 'var(--pf-green-ink)', marginBottom: 6 }}>Call logged</div>
            {[
              `Disposition saved — ${status}`,
              nextStage ? `Lead moved to ${nextStage}` : null,
              svBooked ? 'Reminder: confirm this site visit from the lead’s Site Visit tab so the client actually gets notified' : null,
            ].filter(Boolean).map(msg => (
              <div key={msg} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                <PFIcon name="check" size={13} color="var(--pf-green)" strokeWidth={2.5} />
                <span style={{ fontSize: 12.5, color: 'var(--pf-ink-2)' }}>{msg}</span>
              </div>
            ))}
          </div>
        )}
        <button className="pf-btn pf-btn-block" disabled={saving || saved} onClick={handleSave}>
          {saving ? 'Saving…' : saved ? 'Saved' : 'Save & continue'}
        </button>
        <button className="pf-btn pf-btn-ghost pf-btn-block" style={{ marginTop: 8 }} onClick={() => go('chat')}>
          <PFIcon name="pin" size={14} color="var(--pf-green)" />
          Ping manager about this
        </button>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// CALLS HUB (targets + streak + leaderboard + history)
// ─────────────────────────────────────────────────────────────
// ── Recording player modal ───────────────────────────────────────────────────
const RecordingPlayer = ({ call, onClose }) => {
  const [playing, setPlaying]   = React.useState(false);
  const [progress, setProgress] = React.useState(0);
  const [elapsed, setElapsed]   = React.useState(0);
  const audioRef = React.useRef(null);
  const timerRef = React.useRef(null);

  const totalSec = React.useMemo(() => {
    const m = (call.duration || '').match(/(\d+)m\s*(\d+)s/);
    return m ? parseInt(m[1]) * 60 + parseInt(m[2]) : 120;
  }, [call.duration]);

  const fmt = s => `${String(Math.floor(s/60)).padStart(2,'0')}:${String(Math.floor(s)%60).padStart(2,'0')}`;

  // Initialise the real Audio element once, wiring sync events
  const getAudio = React.useCallback(() => {
    if (audioRef.current) return audioRef.current;
    const a = new Audio(call.recording_url);
    a.ontimeupdate = () => {
      const dur = a.duration || totalSec;
      setElapsed(Math.floor(a.currentTime));
      setProgress((a.currentTime / dur) * 100);
    };
    a.onended = () => {
      setPlaying(false);
      setElapsed(totalSec);
      setProgress(100);
    };
    a.onerror = () => {
      // Real audio failed — fall back to demo timer
      audioRef.current = null;
    };
    audioRef.current = a;
    return a;
  }, [call.recording_url, totalSec]);

  const toggle = () => {
    if (call.recording_url) {
      const a = getAudio();
      if (a) {
        playing ? a.pause() : a.play().catch(() => {
          // Autoplay blocked or load error — fall through to demo timer
        });
        setPlaying(p => !p);
        return;
      }
    }
    // Demo mode: drive progress with a local interval
    if (!playing) {
      timerRef.current = setInterval(() => {
        setElapsed(e => {
          const next = e + 1;
          setProgress((next / totalSec) * 100);
          if (next >= totalSec) { clearInterval(timerRef.current); setPlaying(false); return totalSec; }
          return next;
        });
      }, 1000);
    } else {
      clearInterval(timerRef.current);
    }
    setPlaying(p => !p);
  };

  React.useEffect(() => () => {
    clearInterval(timerRef.current);
    if (audioRef.current) { audioRef.current.pause(); audioRef.current.ontimeupdate = null; audioRef.current.onended = null; }
  }, []);

  return (
    <div style={{ position:'fixed', inset:0, zIndex:400, display:'flex', flexDirection:'column', justifyContent:'flex-end' }}>
      <div style={{ position:'absolute', inset:0, background:'rgba(0,0,0,0.5)' }} onClick={onClose} />
      <div style={{ position:'relative', background:'var(--pf-surface)', borderRadius:'20px 20px 0 0', padding:'20px 20px 36px' }}>
        <div style={{ width:36, height:4, background:'var(--pf-line)', borderRadius:2, margin:'0 auto 18px' }} />
        <div style={{ fontWeight:700, fontSize:15, marginBottom:2 }}>{call.who}</div>
        <div style={{ fontSize:12, color:'var(--pf-ink-3)', marginBottom:16 }}>{call.when} · {call.duration}</div>
        {/* Waveform placeholder */}
        <div style={{ height:48, background:'var(--pf-bg)', borderRadius:10, marginBottom:12, display:'flex', alignItems:'center', padding:'0 12px', gap:2, overflow:'hidden' }}>
          {Array.from({length:40}).map((_,i) => (
            <div key={i} style={{ flex:1, background: (i/40)*100 < progress ? 'var(--pf-green)' : 'var(--pf-line)', borderRadius:2, height: 8+Math.sin(i*0.7)*12+'px', transition:'background 0.2s' }} />
          ))}
        </div>
        {/* Time */}
        <div style={{ display:'flex', justifyContent:'space-between', fontSize:11.5, color:'var(--pf-ink-3)', marginBottom:16 }}>
          <span>{fmt(elapsed)}</span><span>-{fmt(totalSec - elapsed)}</span>
        </div>
        {/* Controls */}
        <div style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:24, marginBottom:16 }}>
          <button onClick={() => { setElapsed(0); setProgress(0); if(audioRef.current) audioRef.current.currentTime=0; }}
            style={{ background:'transparent', border:'none', cursor:'pointer', color:'var(--pf-ink-2)', display:'flex', flexDirection:'column', alignItems:'center', gap:4, fontSize:10.5 }}>
            <PFIcon name="skipBack" size={22} />
          </button>
          <button onClick={toggle} style={{ width:56, height:56, borderRadius:'50%', background:'var(--pf-green)', border:'none', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center' }}>
            <PFIcon name={playing ? 'pause' : 'play'} size={22} color="#fff" />
          </button>
          <button onClick={() => { const s=Math.min(totalSec,elapsed+15); setElapsed(s); setProgress((s/totalSec)*100); if(audioRef.current) audioRef.current.currentTime+=15; }}
            style={{ background:'transparent', border:'none', cursor:'pointer', color:'var(--pf-ink-2)', display:'flex', flexDirection:'column', alignItems:'center', gap:4, fontSize:10.5 }}>
            <PFIcon name="skipFwd" size={22} />
          </button>
        </div>
        {!call.recording_url && (
          <div style={{ fontSize:11.5, color:'var(--pf-ink-3)', textAlign:'center', marginTop:4 }}>
            Demo mode — real recording available after backend deployment
          </div>
        )}
      </div>
    </div>
  );
};

// ── Call Queue component ──────────────────────────────────────────────────────
const QUEUE_LEADS = [
  { id: 'l1', name: 'Rohan Mehta',   initials: 'RM', project: 'Whispering Oaks · 3BHK', stage: 'Qualified',   addedAgo: '8 min ago',  temp: 'hot'  },
  { id: 'l4', name: 'Meera Iyer',    initials: 'MI', project: 'Rivercrest · 2BHK',      stage: 'New Lead',    addedAgo: '22 min ago', temp: 'warm' },
  { id: 'l8', name: 'Sameer Joshi',  initials: 'SJ', project: 'Meadowview · 2BHK',      stage: 'Qualified',   addedAgo: '35 min ago', temp: 'warm' },
  { id: 'l7', name: 'Rhea Kapoor',   initials: 'RK', project: 'Rivercrest · 1BHK',      stage: 'New Lead',    addedAgo: '1 hr ago',   temp: 'cold' },
  { id: 'l6', name: 'Patel Family',  initials: 'PF', project: 'Meadowview · 3BHK',      stage: 'Negotiation', addedAgo: '1 hr ago',   temp: 'hot'  },
];
const tempColor = t => t === 'hot' ? '#ef4444' : t === 'warm' ? '#f59e0b' : '#94a3b8';

const CallQueue = ({ go, leads }) => {
  const [expanded, setExpanded] = React.useState(false);
  const [queue, setQueue] = React.useState(leads || QUEUE_LEADS);
  // Re-seed the queue whenever the caller supplies a fresh real list (e.g.
  // once PF_DATA.leads is populated post-sync) — the seed array otherwise
  // stays frozen in local state from first render.
  React.useEffect(() => { if (leads) setQueue(leads); }, [leads]);
  const [dragIdx, setDragIdx] = React.useState(null);
  const [overIdx, setOverIdx] = React.useState(null);
  const [autoMode, setAutoMode] = React.useState(false);

  const autoPrioritise = () => {
    const order = { hot: 0, warm: 1, cold: 2 };
    setQueue(q => [...q].sort((a, b) => order[a.temp] - order[b.temp]));
    setAutoMode(true);
  };

  const onDragStart = i => setDragIdx(i);
  const onDragOver = (e, i) => { e.preventDefault(); setOverIdx(i); };
  const onDrop = (e, i) => {
    e.preventDefault();
    if (dragIdx === null || dragIdx === i) { setDragIdx(null); setOverIdx(null); return; }
    const next = [...queue];
    const [moved] = next.splice(dragIdx, 1);
    next.splice(i, 0, moved);
    setQueue(next);
    setDragIdx(null); setOverIdx(null);
  };

  return (
    <div className="pf-card" style={{ marginBottom: 14, padding: 0, overflow: 'hidden' }}>
      {/* Header row */}
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', padding: '12px 14px' }}>
        <div style={{ width: 42, height: 42, borderRadius: 12, background: 'var(--pf-green)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <PFIcon name="phone" size={20} color="#fff" />
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontFamily: 'var(--pf-serif)', fontSize: 15, fontWeight: 600 }}>{queue.length ? 'Call queue ready' : 'Call queue empty'}</div>
          <div style={{ fontSize: 12, color: 'var(--pf-ink-3)', marginTop: 2 }}>
            {queue.length ? `${queue.length} leads waiting · est. ${queue.length * 5} min` : 'No leads currently need a follow-up call'}
          </div>
        </div>
        {queue.length > 0 && (
          <button style={{ padding: '6px 10px', borderRadius: 8, border: '1px solid var(--pf-line)', background: 'transparent', fontSize: 12, fontWeight: 600, color: 'var(--pf-ink-2)', cursor: 'pointer', fontFamily: 'inherit' }}
            onClick={() => setExpanded(e => !e)}>
            {expanded ? 'Hide' : 'View'}
          </button>
        )}
        {queue.length > 0 && (
          <button className="pf-btn pf-btn-sm" onClick={() => go('callActive', queue[0].id)}>
            Start <PFIcon name="chevR" size={12} color="#fff" />
          </button>
        )}
      </div>

      {expanded && (
        <div style={{ borderTop: '1px solid var(--pf-line)' }}>
          {/* Auto-prioritise */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', borderBottom: '1px solid var(--pf-line-2)' }}>
            <div style={{ fontSize: 11, color: 'var(--pf-ink-3)', fontWeight: 600 }}>
              {autoMode ? 'Auto-prioritised by temperature' : 'Drag to reorder · or auto-prioritise'}
            </div>
            <button onClick={autoPrioritise} style={{
              padding: '5px 10px', borderRadius: 8, border: `1px solid ${autoMode ? 'var(--pf-green-tint-2)' : 'var(--pf-line)'}`,
              background: autoMode ? 'var(--pf-green-tint)' : 'transparent',
              color: autoMode ? 'var(--pf-green-ink)' : 'var(--pf-ink-2)',
              fontSize: 11, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
            }}>Auto-prioritise</button>
          </div>
          {/* Lead list */}
          {queue.map((lead, i) => (
            <div key={lead.id}
              draggable
              onDragStart={() => onDragStart(i)}
              onDragOver={e => onDragOver(e, i)}
              onDrop={e => onDrop(e, i)}
              style={{
                display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px',
                background: overIdx === i ? 'var(--pf-green-tint)' : 'transparent',
                borderBottom: '1px solid var(--pf-line-2)', cursor: 'grab',
                transition: 'background 0.15s',
              }}>
              {/* Drag handle + rank */}
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, flexShrink: 0, opacity: 0.4 }}>
                <div style={{ fontSize: 9, fontWeight: 700, color: 'var(--pf-ink-3)' }}>{i + 1}</div>
                <PFIcon name="more" size={12} color="var(--pf-ink-3)" />
              </div>
              {/* Avatar */}
              <div style={{ width: 34, height: 34, borderRadius: '50%', background: 'var(--pf-line-2)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, color: 'var(--pf-ink-2)', flexShrink: 0 }}>{lead.initials}</div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600, fontSize: 13 }}>{lead.name}</div>
                <div style={{ fontSize: 11, color: 'var(--pf-ink-3)', marginTop: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{lead.project}</div>
              </div>
              <div style={{ textAlign: 'right', flexShrink: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 2, justifyContent: 'flex-end' }}>
                  <span style={{ width: 7, height: 7, borderRadius: '50%', background: tempColor(lead.temp), display: 'inline-block' }} />
                  <span style={{ fontSize: 11, fontWeight: 600, color: tempColor(lead.temp), textTransform: 'capitalize' }}>{lead.temp}</span>
                </div>
                <div style={{ fontSize: 10.5, color: 'var(--pf-ink-4)' }}>Added {lead.addedAgo}</div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const PFScreenCalls = ({ go }) => {
  const [range, setRange] = React.useState('day');
  const [playingCall, setPlayingCall] = React.useState(null);
  const [filterOpen, setFilterOpen] = React.useState(false);
  const [dispositionFilter, setDispositionFilter] = React.useState('all');
  const [recordedOnly, setRecordedOnly] = React.useState(false);
  // PF_DATA.teleTargets is the hardcoded prototype seed — every stat on this
  // screen (objective, streak, CONN/TALK, leaderboard, recent calls) used to
  // read from it unconditionally with no backend behind any of it. `stats`/
  // `history` hold the real GET /api/mobile/telecalling-stats and
  // GET /api/mobile/calls/history responses once fetched; null until then,
  // in which case the seed is still shown as an honest loading/demo state
  // rather than a blank screen.
  const [stats, setStats] = React.useState(null);
  const [tick, forceTick] = React.useReducer(x => x + 1, 0);
  React.useEffect(() => {
    const fn = () => forceTick();
    document.addEventListener('sr:sync', fn);
    return () => document.removeEventListener('sr:sync', fn);
  }, []);
  // Self-heal: sr:sync fires once right after login resolves. If this component
  // mounts after that event (a very common race), tick never increments again
  // and the screen stays on seed data for the whole session. If the token is
  // already set at mount, do nothing — the effects below will fetch normally.
  // If not, poll briefly until it arrives and then fire a tick so the fetch
  // effects re-run with a valid token.
  React.useEffect(() => {
    if (window.SRAPI && window.SRAPI._token) return;
    const id = setInterval(() => {
      if (window.SRAPI && window.SRAPI._token) { clearInterval(id); forceTick(); }
    }, 200);
    return () => clearInterval(id);
  }, []);
  const [history, setHistory] = React.useState(null);
  React.useEffect(() => {
    if (!window.SRAPI) return;
    if (!window.SRAPI._token) { const t = localStorage.getItem('sr_access_token'); if (!t) return; window.SRAPI._token = t; }
    let cancelled = false;
    window.SRAPI._get('/api/mobile/telecalling-stats?range=' + range)
      .then(res => { if (!cancelled) setStats(res); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [range, tick]);
  React.useEffect(() => {
    if (!window.SRAPI) return;
    if (!window.SRAPI._token) { const t = localStorage.getItem('sr_access_token'); if (!t) return; window.SRAPI._token = t; }
    window.SRAPI._get('/api/mobile/calls/history?limit=20')
      .then(res => { if (res && res.history) setHistory(res.history); })
      .catch(() => {});
  }, [tick]);

  const hasLiveStats = !!stats;
  const seed = PF_DATA.teleTargets[range];
  const svBooked   = hasLiveStats ? stats.svBooked   : seed.svBooked;
  const svTarget   = hasLiveStats ? stats.svTarget   : seed.svTarget; // null once live — no real day/week quota system exists
  const calls      = hasLiveStats ? stats.calls      : seed.calls;
  const callTarget = hasLiveStats ? stats.callTarget : seed.callTarget;
  const conn       = hasLiveStats ? stats.conn       : seed.conn;
  const talkTime   = hasLiveStats ? stats.talkTime   : seed.talkTime;
  const streak     = hasLiveStats ? stats.streak     : PF_DATA.teleTargets.streak;
  const leaderboard = hasLiveStats ? stats.leaderboard : PF_DATA.teleTargets.leaderboard;
  const callHistory = history || PF_DATA.callHistory;
  const dispositionOptions = Array.from(new Set(callHistory.map(c => c.disposition).filter(Boolean)));
  const filteredHistory = callHistory
    .filter(c => dispositionFilter === 'all' || c.disposition === dispositionFilter)
    .filter(c => !recordedOnly || c.recorded);
  const filtersActive = dispositionFilter !== 'all' || recordedOnly;

  const svPct = svTarget ? Math.round((svBooked / svTarget) * 100) : null;
  const callPct = callTarget ? Math.round((calls / callTarget) * 100) : null;
  const days = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];

  // PF_DATA.kpi is only ever set by a successful SRAPI.sync() (see Home's
  // Phase 1 fix) — used the same way here to know PF_DATA.leads holds real
  // leads rather than the QUEUE_LEADS demo seed. Queue = leads not yet
  // called today and not already booked/lost, hottest first.
  const hasLiveLeads = !!(PF_DATA.kpi && PF_DATA.kpi.total_leads !== undefined);
  const realQueue = hasLiveLeads ? PF_DATA.leads
    .filter(l => !['Booked', 'Lost'].includes(l.stage) && (!l.lastCall || new Date(l.lastCall).toDateString() !== new Date().toDateString()))
    .sort((a, b) => (b.hot ? 1 : 0) - (a.hot ? 1 : 0))
    .slice(0, 8)
    .map(l => ({
      id: l.id,
      name: l.name,
      initials: (l.name || '').split(' ').filter(Boolean).slice(0, 2).map(w => w[0]).join('').toUpperCase() || '?',
      project: l.project || '',
      stage: l.stage,
      addedAgo: l.lastCall ? '' : 'New lead',
      temp: l.hot ? 'hot' : 'warm',
    })) : null;

  return (
    <div className="pf-screen pf-anim-in">
      <PFHeader
        title="Telecalling"
        subtitle={`Book site visits · ${new Date().toLocaleDateString('en-IN',{day:'numeric',month:'short'})}`}
        right={
          <button style={{ ...iconBtn, position: 'relative' }} onClick={() => setFilterOpen(true)}>
            <PFIcon name="filter" size={18} />
            {filtersActive && (
              <span style={{ position: 'absolute', top: 6, right: 6, width: 7, height: 7, borderRadius: '50%', background: 'var(--pf-green)', border: '1.5px solid var(--pf-surface)' }} />
            )}
          </button>
        }
      />
      <div className="pf-scroll" style={{ padding: '0 16px 20px' }}>
        {/* Range toggle */}
        <div style={{ display: 'flex', background: 'var(--pf-line-2)', borderRadius: 10, padding: 3, marginBottom: 12 }}>
          {[['day', 'Today'], ['week', 'This week']].map(([id, l]) => (
            <button key={id} onClick={() => setRange(id)} style={{
              flex: 1, padding: '7px 10px', borderRadius: 8, border: 0, fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
              background: range === id ? 'var(--pf-surface)' : 'transparent',
              color: range === id ? 'var(--pf-ink)' : 'var(--pf-ink-3)',
            }}>{l}</button>
          ))}
        </div>

        {/* Primary objective card */}
        <div style={{
          background: 'linear-gradient(135deg, #0b3a24 0%, #135c38 100%)',
          color: '#fff', borderRadius: 18, padding: 16, marginBottom: 12, position: 'relative', overflow: 'hidden',
        }}>
          <div style={{
            position: 'absolute', right: -20, top: -20, width: 120, height: 120, borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(255,255,255,0.12), transparent 70%)',
          }} />
          <div style={{ fontSize: 10.5, letterSpacing: '0.14em', opacity: 0.7, fontWeight: 600 }}>OBJECTIVE · BOOK SITE VISITS</div>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginTop: 6 }}>
            <div style={{ fontFamily: 'var(--pf-serif)', fontSize: 42, fontWeight: 600, letterSpacing: '-0.01em' }}>{svBooked}</div>
            <div style={{ fontSize: 14, opacity: 0.7 }}>
              {svTarget ? `of ${svTarget} ${range === 'day' ? 'today' : 'this week'}` : (range === 'day' ? 'today' : 'this week')}
            </div>
          </div>
          {svTarget ? (
            <>
              <div style={{ marginTop: 10, height: 8, background: 'rgba(255,255,255,0.15)', borderRadius: 4, overflow: 'hidden' }}>
                <div style={{ width: `${Math.min(100, svPct)}%`, height: '100%', background: '#f5c26b', borderRadius: 4 }} />
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6, fontSize: 11, opacity: 0.75 }}>
                <span>{svPct}% to target</span>
                <span>{svTarget - svBooked} more to go</span>
              </div>
            </>
          ) : (
            <div style={{ marginTop: 10, fontSize: 11, opacity: 0.7 }}>No target set for this period</div>
          )}
        </div>

        {/* Streak + secondary metrics */}
        <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr', gap: 8, marginBottom: 12 }}>
          <div className="pf-card-tinted" style={{ padding: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
              <span style={{ fontSize: 16 }}>🔥</span>
              <div style={{ fontSize: 10.5, letterSpacing: '0.1em', fontWeight: 700, color: 'var(--pf-green-ink)' }}>STREAK</div>
            </div>
            <div style={{ fontFamily: 'var(--pf-serif)', fontSize: 22, fontWeight: 600 }}>{streak.current} days</div>
            <div style={{ fontSize: 11, color: 'var(--pf-ink-3)', marginTop: 2 }}>
              {hasLiveStats ? 'calling daily' : 'hitting SV target'} · best {streak.best}
            </div>
            <div style={{ display: 'flex', gap: 4, marginTop: 10 }}>
              {streak.fireDays.map((lit, i) => (
                <div key={i} style={{
                  flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3,
                }}>
                  <div style={{
                    width: '100%', height: 20, borderRadius: 4,
                    background: lit ? 'var(--pf-green)' : 'var(--pf-line)',
                    opacity: i === 6 && !lit ? 0.3 : 1,
                  }} />
                  <div style={{ fontSize: 9, color: 'var(--pf-ink-4)', fontWeight: 600 }}>{days[i]}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="pf-card" style={{ padding: 12 }}>
            <div style={{ fontSize: 10.5, letterSpacing: '0.1em', fontWeight: 700, color: 'var(--pf-ink-3)', marginBottom: 6 }}>CALLS</div>
            <div style={{ fontFamily: 'var(--pf-serif)', fontSize: 22, fontWeight: 600 }}>{calls}{callTarget ? <span style={{ fontSize: 13, color: 'var(--pf-ink-3)', fontWeight: 500 }}>/{callTarget}</span> : null}</div>
            {callTarget && (
              <div style={{ marginTop: 6, height: 5, background: 'var(--pf-line)', borderRadius: 3, overflow: 'hidden' }}>
                <div style={{ width: `${Math.min(100, callPct)}%`, height: '100%', background: 'var(--pf-green)' }} />
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8, fontSize: 11 }}>
              <div>
                <div style={{ color: 'var(--pf-ink-4)', fontSize: 9.5, fontWeight: 600, letterSpacing: '0.08em' }}>CONN</div>
                <div style={{ fontWeight: 600, fontSize: 12 }}>{conn}%</div>
              </div>
              <div>
                <div style={{ color: 'var(--pf-ink-4)', fontSize: 9.5, fontWeight: 600, letterSpacing: '0.08em' }}>TALK</div>
                <div style={{ fontWeight: 600, fontSize: 12 }}>{talkTime}</div>
              </div>
            </div>
          </div>
        </div>

        {/* Leaderboard */}
        <div className="pf-card" style={{ padding: 12, marginBottom: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
            <div style={{ fontSize: 10.5, letterSpacing: '0.1em', fontWeight: 700, color: 'var(--pf-ink-3)' }}>LEADERBOARD · SV BOOKED</div>
            <div style={{ fontSize: 11, color: 'var(--pf-ink-4)' }}>Bhawani Group</div>
          </div>
          {leaderboard.map(row => (
            <div key={row.rank} style={{
              display: 'flex', alignItems: 'center', gap: 10, padding: '7px 8px', borderRadius: 8,
              background: row.you ? 'var(--pf-green-tint)' : 'transparent',
              marginBottom: 2,
            }}>
              <div style={{
                width: 22, height: 22, borderRadius: '50%', fontFamily: 'var(--pf-serif)',
                background: row.rank === 1 ? '#f5c26b' : 'var(--pf-line)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 11, fontWeight: 700, color: row.rank === 1 ? '#6b4a1b' : 'var(--pf-ink-2)',
              }}>{row.rank}</div>
              <div style={{ flex: 1, fontSize: 13, fontWeight: row.you ? 700 : 500, color: row.you ? 'var(--pf-green-ink)' : 'var(--pf-ink)' }}>
                {row.name}
              </div>
              <div style={{ fontFamily: 'var(--pf-serif)', fontSize: 15, fontWeight: 600 }}>{row.sv}</div>
            </div>
          ))}
        </div>

        {/* Queue CTA */}
        <CallQueue go={go} leads={realQueue} />


        <SectionTitle title="Recent calls" />
        {filteredHistory.length === 0 ? (
          <div className="pf-card" style={{ padding: 20, textAlign: 'center', color: 'var(--pf-ink-3)', fontSize: 12.5 }}>
            No calls match this filter.
          </div>
        ) : (
        <div className="pf-card" style={{ padding: 0, overflow: 'hidden' }}>
          {filteredHistory.map(c => (
            <div key={c.id} className="pf-row">
              <div style={{
                width: 36, height: 36, borderRadius: '50%',
                background: c.direction === 'in' ? 'var(--pf-blue-tint)' : 'var(--pf-green-tint)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <PFIcon name="phone" size={16} color={c.direction === 'in' ? 'var(--pf-blue)' : 'var(--pf-green)'} />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600, fontSize: 14 }}>{c.who}</div>
                <div style={{ fontSize: 11.5, color: 'var(--pf-ink-3)', marginTop: 2 }}>
                  {c.when} · {c.duration}
                </div>
                <div style={{ marginTop: 4, display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                  <span className="pf-chip pf-chip-neutral" style={{ fontSize: 10.5, padding: '2px 8px' }}>{c.disposition}</span>
                  {c.recorded && (
                    <span style={{ fontSize: 10.5, color: 'var(--pf-ink-4)', display: 'flex', alignItems: 'center', gap: 3 }}>
                      <PFIcon name="record" size={10} color="#e26b5a" strokeWidth={2.5} />
                      Rec
                    </span>
                  )}
                </div>
              </div>
              {c.recorded && (
                <button style={iconBtn} onClick={() => setPlayingCall(c)}><PFIcon name="play" size={14} color="var(--pf-green)" /></button>
              )}
            </div>
          ))}
        </div>
        )}
      </div>
      {playingCall && <RecordingPlayer call={playingCall} onClose={() => setPlayingCall(null)} />}
      {filterOpen && (
        <CallsFilterSheet
          dispositionOptions={dispositionOptions}
          dispositionFilter={dispositionFilter}
          setDispositionFilter={setDispositionFilter}
          recordedOnly={recordedOnly}
          setRecordedOnly={setRecordedOnly}
          onClose={() => setFilterOpen(false)}
        />
      )}
    </div>
  );
};

const CallsFilterSheet = ({ dispositionOptions, dispositionFilter, setDispositionFilter, recordedOnly, setRecordedOnly, onClose }) => (
  <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'flex-end', zIndex: 30 }}
    onClick={onClose}>
    <div style={{ background: '#fffdf5', color: 'var(--pf-ink)', width: '100%', borderRadius: '20px 20px 0 0', padding: 16, maxHeight: '70%', display: 'flex', flexDirection: 'column' }}
      onClick={e => e.stopPropagation()}>
      <div style={{ width: 36, height: 4, background: 'var(--pf-line)', borderRadius: 2, margin: '0 auto 10px' }} />
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <div style={{ fontFamily: 'var(--pf-serif)', fontSize: 18, fontWeight: 600 }}>Filter recent calls</div>
        <button onClick={() => { setDispositionFilter('all'); setRecordedOnly(false); }} style={{ background: 'transparent', border: 0, color: 'var(--pf-ink-3)', fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>Reset</button>
      </div>
      <div style={{ fontSize: 10.5, letterSpacing: '0.1em', fontWeight: 700, color: 'var(--pf-ink-3)', marginBottom: 8 }}>DISPOSITION</div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 16 }}>
        {['all', ...dispositionOptions].map(d => (
          <button key={d} onClick={() => setDispositionFilter(d)} style={{
            padding: '6px 12px', borderRadius: 20, fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
            background: dispositionFilter === d ? 'var(--pf-green)' : 'var(--pf-line-2)',
            color: dispositionFilter === d ? '#fff' : 'var(--pf-ink-2)',
            border: 0,
          }}>{d === 'all' ? 'All' : d}</button>
        ))}
      </div>
      <div onClick={() => setRecordedOnly(v => !v)} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 2px', cursor: 'pointer' }}>
        <div style={{ fontSize: 13, fontWeight: 600 }}>Recorded calls only</div>
        <div style={{
          width: 40, height: 24, borderRadius: 12, background: recordedOnly ? 'var(--pf-green)' : 'var(--pf-line)',
          position: 'relative', transition: 'background 0.15s',
        }}>
          <div style={{
            position: 'absolute', top: 2, left: recordedOnly ? 18 : 2, width: 20, height: 20, borderRadius: '50%',
            background: '#fff', transition: 'left 0.15s', boxShadow: '0 1px 2px rgba(0,0,0,0.2)',
          }} />
        </div>
      </div>
      <button className="pf-btn pf-btn-block" style={{ marginTop: 16 }} onClick={onClose}>Apply</button>
    </div>
  </div>
);

const KPI = ({ label, val, sub }) => (
  <div className="pf-card" style={{ padding: 12 }}>
    <div style={{ fontSize: 10, letterSpacing: '0.12em', color: 'var(--pf-ink-3)', fontWeight: 600 }}>{label.toUpperCase()}</div>
    <div style={{ fontFamily: 'var(--pf-serif)', fontSize: 22, fontWeight: 600, marginTop: 2 }}>{val}</div>
    <div style={{ fontSize: 11, color: 'var(--pf-ink-4)', marginTop: 1 }}>{sub}</div>
  </div>
);

Object.assign(window, { PFScreenCallActive, PFScreenDisposition, PFScreenCalls, KPI });



// ===== 09-pf-screens-4.jsx =====
/* global React, PFIcon, PFHeader, PFBackBtn, PF_DATA, iconBtn, SectionTitle */

// ─────────────────────────────────────────────────────────────
// TASKS
// ─────────────────────────────────────────────────────────────
const MANAGER_ROLES = ['superadmin', 'admin', 'business_owner', 'sales_head', 'manager'];
const isManagerRole = (role) => MANAGER_ROLES.includes(String(role || '').toLowerCase().replace(/\s+/g, '_'));

const PFScreenTasks = ({ go, role }) => {
  // Merge base tasks with TASK_STORE overlay; re-render on 'sr:task-updated'.
  const buildTasks = () => PF_DATA.tasks.map(t => Object.assign({}, t, (window.TASK_STORE || {})[t.id] || {}));
  const [tasks, setTasks] = React.useState(buildTasks);
  const [filter, setFilter] = React.useState('Today');
  const [addOpen, setAddOpen] = React.useState(false);
  React.useEffect(() => {
    const onUpdate = () => setTasks(buildTasks());
    document.addEventListener('sr:task-updated', onUpdate);
    return () => document.removeEventListener('sr:task-updated', onUpdate);
  }, []);
  const open = tasks.filter(t => !t.done);
  const done = tasks.filter(t => t.done);

  const toggle = id => {
    const cur = (window.TASK_STORE && window.TASK_STORE[id]) || tasks.find(t => t.id === id) || {};
    const next = !cur.done;
    patchTask(id, { done: next, completedAt: next ? new Date().toISOString() : null });
  };

  return (
    <div className="pf-screen pf-anim-in">
      <PFHeader
        title="Tasks"
        subtitle={`${open.length} open · ${done.length} done today`}
        left={<PFBackBtn onClick={() => go('home')} />}
        right={
          <div style={{ display: 'flex', gap: 6 }}>
            {isManagerRole(role) && (
              <button style={iconBtn} onClick={() => go('teamTasks')} aria-label="Team tasks"><PFIcon name="leads" size={18} /></button>
            )}
            <button style={iconBtn} onClick={() => setAddOpen(true)} aria-label="New task"><PFIcon name="plus" size={18} /></button>
          </div>
        }
      />
      <div style={{ padding: '2px 16px 10px' }}>
        <div style={{ display: 'flex', gap: 6 }}>
          {['Today', 'This week', 'Overdue', 'All'].map(f => (
            <button key={f} onClick={() => setFilter(f)} style={{
              padding: '6px 12px', borderRadius: 999,
              background: filter === f ? 'var(--pf-green)' : 'var(--pf-surface)',
              color: filter === f ? '#fff' : 'var(--pf-ink-2)',
              border: `1px solid ${filter === f ? 'var(--pf-green)' : 'var(--pf-line)'}`,
              fontSize: 12, fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit',
            }}>{f}</button>
          ))}
        </div>
      </div>
      <div className="pf-scroll" style={{ padding: '0 16px 24px' }}>
        <div style={{ fontSize: 10.5, letterSpacing: '0.12em', color: 'var(--pf-ink-3)', fontWeight: 600, margin: '8px 2px' }}>
          OPEN · {open.length}
        </div>
        {open.map(t => (
          <div key={t.id} className="pf-task" data-done={t.done} onClick={() => go('taskDetail', t.id)}>
            <div className="pf-check" onClick={(e) => { e.stopPropagation(); toggle(t.id); }}>{t.done && <PFIcon name="check" size={14} color="#fff" strokeWidth={2.5} />}</div>
            <div style={{ flex: 1 }}>
              <div className="pf-task-title">{t.title}</div>
              <div className="pf-task-meta">
                <span style={{ color: t.due.includes('AM') && parseInt(t.due) < 11 ? 'var(--pf-red)' : 'var(--pf-ink-3)' }}>
                  {t.due}
                </span>
                {' · '}{t.client}
              </div>
            </div>
            <TaskKindChip kind={t.kind} />
          </div>
        ))}

        {done.length > 0 && (
          <>
            <div style={{ fontSize: 10.5, letterSpacing: '0.12em', color: 'var(--pf-ink-3)', fontWeight: 600, margin: '16px 2px 8px' }}>
              COMPLETED · {done.length}
            </div>
            {done.map(t => (
              <div key={t.id} className="pf-task" data-done={t.done} onClick={() => toggle(t.id)}>
                <div className="pf-check"><PFIcon name="check" size={14} color="#fff" strokeWidth={2.5} /></div>
                <div style={{ flex: 1 }}>
                  <div className="pf-task-title">{t.title}</div>
                  <div className="pf-task-meta">{t.due} · {t.client}</div>
                </div>
              </div>
            ))}
          </>
        )}
      </div>
      {addOpen && <AddTaskSheet onClose={() => setAddOpen(false)} />}
    </div>
  );
};

const AddTaskSheet = ({ onClose, initialLeadId }) => {
  const [title, setTitle] = React.useState('');
  const [kind, setKind] = React.useState('call');
  const [priority, setPriority] = React.useState('Medium');
  const [due, setDue] = React.useState('');
  const [leadId, setLeadId] = React.useState(initialLeadId || '');
  const [leadQuery, setLeadQuery] = React.useState('');
  const [leadPickerOpen, setLeadPickerOpen] = React.useState(!initialLeadId);
  const [notes, setNotes] = React.useState('');
  const [saving, setSaving] = React.useState(false);
  const [err, setErr] = React.useState('');
  const selectedLead = PF_DATA.leads.find(l => l.id === leadId);
  const filteredLeads = React.useMemo(() => {
    const q = leadQuery.trim().toLowerCase();
    const list = q ? PF_DATA.leads.filter(l => l.name.toLowerCase().includes(q)) : PF_DATA.leads;
    return list.slice(0, 30);
  }, [leadQuery]);
  const kinds = [
    { id: 'call', label: 'Call' },
    { id: 'doc', label: 'Document' },
    { id: 'visit', label: 'Site visit' },
    { id: 'form', label: 'Profile' },
    { id: 'chat', label: 'Ping manager' },
  ];

  const submit = async () => {
    if (!title.trim()) { setErr('Title is required'); return; }
    setSaving(true);
    setErr('');
    try {
      if (!window.SRAPI || typeof window.SRAPI.createTask !== 'function' || !window.SRAPI._token) {
        throw new Error('Sign in required to create a task');
      }
      const lead = PF_DATA.leads.find(l => l.id === leadId);
      const res = await window.SRAPI.createTask({ title: title.trim(), kind, priority, due: due.trim(), leadId: leadId || null, notes: notes.trim() });
      PF_DATA.tasks.push({
        id: res.id, title: title.trim(), kind, priority, due: due.trim() || 'No due time',
        client: lead ? lead.name : '', leadId: leadId || null, done: false,
      });
      document.dispatchEvent(new CustomEvent('sr:task-updated', { detail: { created: true } }));
      onClose();
    } catch (e) {
      setErr(e?.message || 'Could not create task — please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'flex-end', zIndex: 30 }}
      onClick={onClose}>
      <div style={{ background: '#fffdf5', color: 'var(--pf-ink)', width: '100%', borderRadius: '20px 20px 0 0', padding: 16, maxHeight: '85%', overflow: 'auto' }}
        onClick={e => e.stopPropagation()}>
        <div style={{ width: 36, height: 4, background: 'var(--pf-line)', borderRadius: 2, margin: '0 auto 10px' }} />
        <div style={{ fontFamily: 'var(--pf-serif)', fontSize: 18, fontWeight: 600, marginBottom: 12 }}>New task</div>

        {/* Lead first — pick who this task is about before filling in the details */}
        <div className="pf-field">
          <div className="pf-label">Lead (optional)</div>
          {!leadPickerOpen && selectedLead ? (
            <div onClick={() => setLeadPickerOpen(true)} style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer',
              padding: '10px 13px', borderRadius: 10, background: 'var(--pf-green-tint)', border: '1px solid var(--pf-green)',
            }}>
              <span style={{ fontSize: 13.5, fontWeight: 700, color: 'var(--pf-green)' }}>{selectedLead.name}</span>
              <span style={{ fontSize: 11.5, color: 'var(--pf-green)' }}>Change</span>
            </div>
          ) : !leadPickerOpen ? (
            <div onClick={() => setLeadPickerOpen(true)} style={{
              cursor: 'pointer', padding: '10px 13px', borderRadius: 10, background: 'var(--pf-line-2)', color: 'var(--pf-ink-3)', fontSize: 13,
            }}>No lead — general task. Tap to pick one</div>
          ) : (
            <>
              <input className="pf-input" value={leadQuery} onChange={e => setLeadQuery(e.target.value)}
                placeholder="Search leads by name…" autoFocus />
              <div style={{ maxHeight: 160, overflowY: 'auto', marginTop: 6, border: '1px solid var(--pf-line)', borderRadius: 10 }}>
                <div onClick={() => { setLeadId(''); setLeadPickerOpen(false); }} style={{
                  padding: '9px 12px', fontSize: 13, color: 'var(--pf-ink-3)', cursor: 'pointer', borderBottom: '1px solid var(--pf-line)',
                }}>None — general task</div>
                {filteredLeads.map(l => (
                  <div key={l.id} onClick={() => { setLeadId(l.id); setLeadPickerOpen(false); setLeadQuery(''); }} style={{
                    padding: '9px 12px', fontSize: 13.5, color: 'var(--pf-ink)', cursor: 'pointer', borderBottom: '1px solid var(--pf-line)',
                  }}>{l.name}</div>
                ))}
                {filteredLeads.length === 0 && (
                  <div style={{ padding: '9px 12px', fontSize: 12.5, color: 'var(--pf-ink-3)' }}>No leads match</div>
                )}
              </div>
            </>
          )}
        </div>

        <div className="pf-field">
          <div className="pf-label">Title</div>
          <input className="pf-input" value={title} onChange={e => setTitle(e.target.value)} placeholder="e.g. Call back Rohan re: floor plan" />
        </div>

        <div className="pf-field">
          <div className="pf-label">Type</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {kinds.map(k => (
              <button key={k.id} onClick={() => setKind(k.id)} style={{
                padding: '6px 12px', borderRadius: 20, fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', border: 0,
                background: kind === k.id ? 'var(--pf-green)' : 'var(--pf-line-2)',
                color: kind === k.id ? '#fff' : 'var(--pf-ink-2)',
              }}>{k.label}</button>
            ))}
          </div>
        </div>

        <div className="pf-field">
          <div className="pf-label">Priority</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {['Low', 'Medium', 'High', 'Critical'].map(p => {
              const on = priority === p;
              const c = p === 'Critical' ? 'var(--pf-red)' : p === 'High' ? 'var(--pf-amber)' : p === 'Medium' ? 'var(--pf-green)' : 'var(--pf-ink-3)';
              return (
                <button key={p} onClick={() => setPriority(p)} style={{
                  padding: '6px 12px', borderRadius: 20, fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit',
                  border: `1.5px solid ${on ? c : 'var(--pf-line)'}`, background: on ? c + '18' : '#fff', color: on ? c : 'var(--pf-ink-2)',
                }}>{p}</button>
              );
            })}
          </div>
        </div>

        <div className="pf-field">
          <div className="pf-label">Due</div>
          <input className="pf-input" value={due} onChange={e => setDue(e.target.value)} placeholder="e.g. Today 5:00 PM" />
        </div>

        <div className="pf-field">
          <div className="pf-label">Notes (optional)</div>
          <textarea className="pf-textarea" value={notes} onChange={e => setNotes(e.target.value)} placeholder="Any context…" />
        </div>

        {err && <div style={{ fontSize: 12, color: 'var(--pf-red)', marginBottom: 10 }}>{err}</div>}
        <button className="pf-btn pf-btn-block" disabled={saving} onClick={submit}>{saving ? 'Creating…' : 'Create task'}</button>
        <button onClick={onClose} style={{ width: '100%', padding: '12px', borderRadius: 12, background: 'transparent', border: 0, color: 'var(--pf-ink-3)', fontWeight: 600, fontSize: 13, cursor: 'pointer', fontFamily: 'inherit', marginTop: 4 }}>Cancel</button>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// TEAM TASKS — manager accountability view. Backed by the real,
// already-built GET /api/tasks/sla-status?scope=team (live SLA status
// computed server-side) and GET /api/tasks/stats (per-owner breakdown).
// Gated to admin-tier roles client-side by the caller (PFScreenTasks);
// the backend also enforces this (403 for non-admin scope=team).
// ─────────────────────────────────────────────────────────────
const FLAG_META = {
  priority_conflict: { label: 'Priority Conflict', icon: '⚖️', color: 'var(--pf-amber)' },
  escalation:         { label: 'Escalation',        icon: '🚩', color: 'var(--pf-red)' },
  stuck:              { label: "Stuck",             icon: '🧱', color: 'var(--pf-ink-2)' },
};

const ManagerFlagsPanel = ({ onCountChange }) => {
  const [flags, setFlags] = React.useState(null);
  const [err, setErr] = React.useState('');
  const [resolvingId, setResolvingId] = React.useState(null);
  const [note, setNote] = React.useState('');
  const [editingTask, setEditingTask] = React.useState(null);

  const load = React.useCallback(() => {
    if (!window.SRAPI || typeof window.SRAPI.fetchManagerFlags !== 'function') { setErr('Sign in required'); return; }
    window.SRAPI.fetchManagerFlags('open').then(rows => {
      setFlags(rows);
      if (onCountChange) onCountChange(rows.length);
    }).catch(e => setErr(e?.message || 'Could not load flags'));
  }, [onCountChange]);

  React.useEffect(() => {
    load();
    document.addEventListener('sr:notification', load);
    return () => document.removeEventListener('sr:notification', load);
  }, [load]);

  const resolve = (id) => {
    if (!window.SRAPI || typeof window.SRAPI.resolveManagerFlag !== 'function') return;
    window.SRAPI.resolveManagerFlag(id, note).then(() => { setResolvingId(null); setNote(''); load(); }).catch(() => {});
  };

  return (
    <>
      {err && <div style={{ fontSize: 12.5, color: 'var(--pf-red)', padding: '8px 0' }}>{err}</div>}
      {flags === null && !err && <div style={{ padding: '24px 0', textAlign: 'center', color: 'var(--pf-ink-3)', fontSize: 13 }}>Loading…</div>}
      {flags !== null && flags.length === 0 && (
        <div style={{ padding: '24px 0', textAlign: 'center', color: 'var(--pf-ink-3)', fontSize: 13 }}>Nothing open — team is unblocked</div>
      )}
      {(flags || []).map(f => {
        const meta = FLAG_META[f.flag_type] || { label: f.flag_type, icon: '🔔', color: 'var(--pf-ink-2)' };
        return (
          <div key={f.id} style={{ padding: '11px 12px', borderRadius: 10, background: 'var(--pf-surface)', border: `1px solid ${f.severity === 'urgent' ? 'var(--pf-red)' : 'var(--pf-line)'}`, marginBottom: 8 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
              <span style={{ fontSize: 14 }}>{meta.icon}</span>
              <span style={{ fontSize: 12, fontWeight: 800, color: meta.color }}>{meta.label}</span>
              {f.severity === 'urgent' && <span style={{ fontSize: 10, fontWeight: 800, color: '#fff', background: 'var(--pf-red)', padding: '1px 7px', borderRadius: 20 }}>URGENT</span>}
              <span style={{ flex: 1 }} />
              <span style={{ fontSize: 11, color: 'var(--pf-ink-3)' }}>{f.raised_by_name}</span>
            </div>
            <div style={{ fontSize: 13, color: 'var(--pf-ink)', marginBottom: 8 }}>{f.message}</div>

            {f.flag_type === 'priority_conflict' && f.context && (f.context.taskAId || f.context.taskBId) && (
              <div style={{ display: 'flex', gap: 6, marginBottom: 8, flexWrap: 'wrap' }}>
                {f.context.taskAId && (
                  <button onClick={() => setEditingTask({ id: f.context.taskAId, title: f.context.taskA, priority: 'Medium', due: '', _needsFetch: true })}
                    style={{ padding: '6px 10px', borderRadius: 8, background: 'var(--pf-blue-tint)', border: 0, color: 'var(--pf-blue)', fontSize: 11.5, fontWeight: 700, cursor: 'pointer' }}>
                    Edit "{f.context.taskA}"
                  </button>
                )}
                {f.context.taskBId && (
                  <button onClick={() => setEditingTask({ id: f.context.taskBId, title: f.context.taskB, priority: 'Medium', due: '', _needsFetch: true })}
                    style={{ padding: '6px 10px', borderRadius: 8, background: 'var(--pf-blue-tint)', border: 0, color: 'var(--pf-blue)', fontSize: 11.5, fontWeight: 700, cursor: 'pointer' }}>
                    Edit "{f.context.taskB}"
                  </button>
                )}
              </div>
            )}

            {resolvingId === f.id ? (
              <div style={{ display: 'flex', gap: 6 }}>
                <input className="pf-input" value={note} onChange={e => setNote(e.target.value)} placeholder="Resolution note (optional)"
                  style={{ flex: 1, fontSize: 12.5, padding: '7px 10px' }} autoFocus />
                <button onClick={() => resolve(f.id)} style={{ padding: '7px 12px', borderRadius: 8, background: 'var(--pf-green)', border: 0, color: '#fff', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>Resolve</button>
              </div>
            ) : (
              <button onClick={() => { setResolvingId(f.id); setNote(''); }} style={{ padding: '6px 12px', borderRadius: 8, background: 'var(--pf-line-2)', border: 0, color: 'var(--pf-ink-2)', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>Resolve</button>
            )}
          </div>
        );
      })}
      {editingTask && <EditTaskSheet task={editingTask} onClose={() => setEditingTask(null)} onSaved={load} />}
    </>
  );
};

const EditTaskSheet = ({ task, onClose, onSaved }) => {
  const [title, setTitle] = React.useState(task.title || '');
  const [priority, setPriority] = React.useState(task.priority || 'Medium');
  const [due, setDue] = React.useState(task.due || '');
  const [saving, setSaving] = React.useState(false);
  const [err, setErr] = React.useState('');
  const [loaded, setLoaded] = React.useState(!task._needsFetch);

  // task._needsFetch: the caller only knows the task's id (e.g. a manager
  // flag's "Edit <name>" button only has whatever raw text the reporter
  // typed, not the task's real title) — fetch the real record instead of
  // saving that guess back over the actual title. Without this, saving
  // would silently overwrite the task's real title with the flag's label.
  React.useEffect(() => {
    if (!task._needsFetch) return;
    if (!window.SRAPI || typeof window.SRAPI.fetchTask !== 'function') { setLoaded(true); return; }
    window.SRAPI.fetchTask(task.id).then(t => {
      if (t) { setTitle(t.title || ''); setPriority(t.priority || 'Medium'); setDue(t.due || ''); }
      setLoaded(true);
    }).catch(() => setLoaded(true));
  }, [task.id]);

  const save = () => {
    if (!title.trim()) { setErr('Title is required'); return; }
    if (!window.SRAPI || typeof window.SRAPI.updateTask !== 'function') { setErr('Sign in required'); return; }
    setSaving(true); setErr('');
    window.SRAPI.updateTask(task.id, { title: title.trim(), priority, due: due.trim() })
      .then(() => { onSaved(); onClose(); })
      .catch(e => setErr(e?.message || 'Could not save'))
      .finally(() => setSaving(false));
  };

  return (
    <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'flex-end', zIndex: 40 }} onClick={onClose}>
      <div style={{ background: '#fffdf5', color: 'var(--pf-ink)', width: '100%', borderRadius: '20px 20px 0 0', padding: 16 }} onClick={e => e.stopPropagation()}>
        <div style={{ width: 36, height: 4, background: 'var(--pf-line)', borderRadius: 2, margin: '0 auto 10px' }} />
        <div style={{ fontFamily: 'var(--pf-serif)', fontSize: 18, fontWeight: 600, marginBottom: 12 }}>Edit task</div>

        {!loaded && <div style={{ fontSize: 12.5, color: 'var(--pf-ink-3)', marginBottom: 10 }}>Loading current details…</div>}

        <div className="pf-field">
          <div className="pf-label">Title</div>
          <input className="pf-input" value={title} onChange={e => setTitle(e.target.value)} disabled={!loaded} />
        </div>

        <div className="pf-field">
          <div className="pf-label">Priority</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {['Low', 'Medium', 'High', 'Critical'].map(p => {
              const on = priority === p;
              const c = p === 'Critical' ? 'var(--pf-red)' : p === 'High' ? 'var(--pf-amber)' : p === 'Medium' ? 'var(--pf-green)' : 'var(--pf-ink-3)';
              return (
                <button key={p} onClick={() => setPriority(p)} style={{
                  padding: '6px 12px', borderRadius: 20, fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit',
                  border: `1.5px solid ${on ? c : 'var(--pf-line)'}`, background: on ? c + '18' : '#fff', color: on ? c : 'var(--pf-ink-2)',
                }}>{p}</button>
              );
            })}
          </div>
        </div>

        <div className="pf-field">
          <div className="pf-label">Due</div>
          <input className="pf-input" value={due} onChange={e => setDue(e.target.value)} placeholder="e.g. Today 5:00 PM" />
        </div>

        {err && <div style={{ fontSize: 12, color: 'var(--pf-red)', marginBottom: 10 }}>{err}</div>}
        <button className="pf-btn pf-btn-block" disabled={saving || !loaded} onClick={save}>{saving ? 'Saving…' : 'Save'}</button>
        <button onClick={onClose} style={{ width: '100%', padding: '12px', borderRadius: 12, background: 'transparent', border: 0, color: 'var(--pf-ink-3)', fontWeight: 600, fontSize: 13, cursor: 'pointer', fontFamily: 'inherit', marginTop: 4 }}>Cancel</button>
      </div>
    </div>
  );
};

const PFScreenTeamTasks = ({ go }) => {
  const [tab, setTab] = React.useState('tasks');
  const [flagCount, setFlagCount] = React.useState(0);
  const [rows, setRows] = React.useState(null);
  const [stats, setStats] = React.useState(null);
  const [filter, setFilter] = React.useState('all');
  const [err, setErr] = React.useState('');
  const [editingTask, setEditingTask] = React.useState(null);

  const load = React.useCallback(() => {
    if (!window.SRAPI || typeof window.SRAPI.fetchTeamTasks !== 'function') {
      setErr('Sign in required'); return;
    }
    window.SRAPI.fetchTeamTasks('team').then(setRows).catch(e => setErr(e?.message || 'Could not load team tasks'));
    window.SRAPI.fetchTaskStats().then(setStats).catch(() => {});
    if (typeof window.SRAPI.fetchManagerFlags === 'function') {
      window.SRAPI.fetchManagerFlags('open').then(rows => setFlagCount(rows.length)).catch(() => {});
    }
  }, []);

  React.useEffect(() => {
    load();
    document.addEventListener('sr:task-updated', load);
    return () => document.removeEventListener('sr:task-updated', load);
  }, [load]);

  const SLA_META = {
    breached:      { label: 'Overdue',    color: 'var(--pf-red)' },
    at_risk:       { label: 'At risk',    color: 'var(--pf-amber)' },
    on_track:      { label: 'On track',   color: 'var(--pf-green)' },
    done_on_time:  { label: 'Done',       color: 'var(--pf-ink-3)' },
    done_late:     { label: 'Done late',  color: 'var(--pf-amber)' },
    no_sla:        { label: '—',          color: 'var(--pf-ink-3)' },
  };

  const filtered = (rows || []).filter(t => {
    if (filter === 'all') return true;
    if (filter === 'unresolved') return t.status !== 'Done';
    if (filter === 'breached') return t.sla_status === 'breached';
    if (filter === 'done') return t.status === 'Done';
    return true;
  });

  const FILTERS = [
    { k: 'all', label: 'All' },
    { k: 'unresolved', label: 'Pending' },
    { k: 'breached', label: 'Overdue' },
    { k: 'done', label: 'Done' },
  ];
  const counts = {
    all: (rows || []).length,
    unresolved: (rows || []).filter(t => t.status !== 'Done').length,
    breached: (rows || []).filter(t => t.sla_status === 'breached').length,
    done: (rows || []).filter(t => t.status === 'Done').length,
  };

  return (
    <div className="pf-screen pf-anim-in">
      <PFHeader title="Team Tasks" subtitle="Accountability across the team" left={<PFBackBtn onClick={() => go('tasks')} />} />
      <div style={{ padding: '10px 16px 0', display: 'flex', gap: 6 }}>
        {[{ k: 'tasks', label: 'Tasks' }, { k: 'flags', label: `Flags${flagCount ? ` (${flagCount})` : ''}` }].map(t => (
          <button key={t.k} onClick={() => setTab(t.k)} style={{
            flex: 1, padding: '8px 0', borderRadius: 10,
            background: tab === t.k ? 'var(--pf-green)' : 'var(--pf-surface)',
            color: tab === t.k ? '#fff' : 'var(--pf-ink-2)',
            border: `1px solid ${tab === t.k ? 'var(--pf-green)' : 'var(--pf-line)'}`,
            fontSize: 12.5, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit',
          }}>{t.label}</button>
        ))}
      </div>
      <div className="pf-scroll" style={{ padding: '10px 16px 24px' }}>
        {tab === 'flags' ? <ManagerFlagsPanel onCountChange={setFlagCount} /> : <>
        {err && <div style={{ fontSize: 12.5, color: 'var(--pf-red)', padding: '8px 0' }}>{err}</div>}

        {stats && stats.byOwner && stats.byOwner.length > 0 && (
          <>
            <div style={{ fontSize: 10.5, letterSpacing: '0.12em', color: 'var(--pf-ink-3)', fontWeight: 600, margin: '10px 2px 8px' }}>BY OWNER</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 16 }}>
              {stats.byOwner.map(o => {
                const total = Number(o.total) || 0;
                const done = Number(o.done) || 0;
                const highOpen = Number(o.high_open) || 0;
                return (
                  <div key={o.owner} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 12px', borderRadius: 10, background: 'var(--pf-surface)', border: '1px solid var(--pf-line)' }}>
                    <div style={{ flex: 1, fontSize: 13, fontWeight: 700 }}>{o.owner || 'Unassigned'}</div>
                    <div style={{ fontSize: 11.5, color: 'var(--pf-ink-3)' }}>{done}/{total} done</div>
                    {highOpen > 0 && (
                      <span style={{ fontSize: 10.5, fontWeight: 800, color: 'var(--pf-red)', background: 'var(--pf-red-tint, #fee2e2)', padding: '2px 7px', borderRadius: 20 }}>{highOpen} high</span>
                    )}
                  </div>
                );
              })}
            </div>
          </>
        )}

        <div style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
          {FILTERS.map(f => (
            <button key={f.k} onClick={() => setFilter(f.k)} style={{
              padding: '6px 12px', borderRadius: 999,
              background: filter === f.k ? 'var(--pf-green)' : 'var(--pf-surface)',
              color: filter === f.k ? '#fff' : 'var(--pf-ink-2)',
              border: `1px solid ${filter === f.k ? 'var(--pf-green)' : 'var(--pf-line)'}`,
              fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
            }}>{f.label} ({counts[f.k] || 0})</button>
          ))}
        </div>

        {rows === null && !err && <div style={{ padding: '24px 0', textAlign: 'center', color: 'var(--pf-ink-3)', fontSize: 13 }}>Loading…</div>}
        {rows !== null && filtered.length === 0 && (
          <div style={{ padding: '24px 0', textAlign: 'center', color: 'var(--pf-ink-3)', fontSize: 13 }}>Nothing here</div>
        )}
        {filtered.map(t => {
          const meta = SLA_META[t.sla_status] || SLA_META.no_sla;
          return (
            <div key={t.id} className="pf-task" onClick={() => setEditingTask(t)} style={{ cursor: 'pointer' }}>
              <div style={{ flex: 1 }}>
                <div className="pf-task-title">{t.title}</div>
                <div className="pf-task-meta">{t.owner || 'Unassigned'} · {t.priority || 'Medium'} · {t.due || 'No due time'}</div>
              </div>
              <span style={{ fontSize: 10.5, fontWeight: 800, color: meta.color, whiteSpace: 'nowrap' }}>{meta.label}</span>
            </div>
          );
        })}
        </>}
      </div>
      {editingTask && <EditTaskSheet task={editingTask} onClose={() => setEditingTask(null)} onSaved={load} />}
    </div>
  );
};

const TaskKindChip = ({ kind }) => {
  const map = {
    call: { icon: 'phone', color: 'var(--pf-green)', bg: 'var(--pf-green-tint)' },
    doc: { icon: 'doc', color: 'var(--pf-blue)', bg: 'var(--pf-blue-tint)' },
    visit: { icon: 'pin', color: 'var(--pf-amber)', bg: 'var(--pf-amber-tint)' },
    form: { icon: 'note', color: 'var(--pf-ink-2)', bg: '#ece7da' },
    chat: { icon: 'chat', color: 'var(--pf-green)', bg: 'var(--pf-green-tint)' },
  };
  const m = map[kind] || map.doc;
  return (
    <div style={{
      width: 28, height: 28, borderRadius: 8, background: m.bg,
      display: 'flex', alignItems: 'center', justifyContent: 'center', flex: '0 0 28px',
    }}>
      <PFIcon name={m.icon} size={14} color={m.color} />
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// EVIDENCE UPLOAD
// ─────────────────────────────────────────────────────────────
const PFScreenEvidence = ({ go, leadId }) => {
  const lead = PF_DATA.leads.find(l => l.id === leadId) || PF_DATA.leads[0];
  const [kind, setKind] = React.useState('Site visit photos');
  const [captured, setCaptured] = React.useState([]);
  const [caption, setCaption] = React.useState('');
  const [toast, setToast] = React.useState(null);
  const [submitting, setSubmitting] = React.useState(false);
  const cameraRef = React.useRef();
  const galleryRef = React.useRef();
  const showToast = (m) => { setToast(m); setTimeout(() => setToast(null), 1800); };

  // Voice note capture — the "Voice note" evidence type showed the exact
  // same Camera/Gallery buttons as every other type, with no actual
  // recorder; the only way to attach audio was picking an existing file
  // from the gallery, silently. Reuses the same MediaRecorder approach
  // already used for call recording (08-pf-screens-3.jsx).
  const [recording, setRecording] = React.useState(false);
  const [recSeconds, setRecSeconds] = React.useState(0);
  const mediaRecRef = React.useRef(null);
  const audioChunksRef = React.useRef([]);
  const recTimerRef = React.useRef(null);

  const startRecording = async () => {
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      showToast('Microphone not available on this device');
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: SR_AUDIO_CONSTRAINTS });
      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus') ? 'audio/webm;codecs=opus'
        : MediaRecorder.isTypeSupported('audio/mp4') ? 'audio/mp4' : '';
      const mr = new MediaRecorder(stream, Object.assign(
        { audioBitsPerSecond: SR_AUDIO_BITS_PER_SECOND },
        mimeType ? { mimeType } : null
      ));
      audioChunksRef.current = [];
      mr.ondataavailable = e => { if (e.data && e.data.size > 0) audioChunksRef.current.push(e.data); };
      mr.onstop = () => {
        stream.getTracks().forEach(t => t.stop());
        clearInterval(recTimerRef.current);
        const blobType = mr.mimeType || 'audio/webm';
        const ext = blobType.includes('mp4') ? 'm4a' : 'webm';
        const blob = new Blob(audioChunksRef.current, { type: blobType });
        if (blob.size > 500) {
          const filename = `voice_note_${Date.now()}.${ext}`;
          const file = new File([blob], filename, { type: blobType });
          setCaptured(prev => [{
            file, name: filename, size: (blob.size / 1024 / 1024).toFixed(1) + ' MB',
            when: 'Just now', kind: 'doc', url: URL.createObjectURL(blob),
          }, ...prev]);
        }
      };
      mr.start();
      mediaRecRef.current = mr;
      setRecSeconds(0);
      recTimerRef.current = setInterval(() => setRecSeconds(s => s + 1), 1000);
      setRecording(true);
    } catch (e) {
      showToast('Microphone permission denied');
    }
  };

  const stopRecording = () => {
    const mr = mediaRecRef.current;
    if (mr && mr.state !== 'inactive') mr.stop();
    setRecording(false);
  };

  React.useEffect(() => () => {
    clearInterval(recTimerRef.current);
    const mr = mediaRecRef.current;
    if (mr && mr.state !== 'inactive') mr.stop();
  }, []);

  // Files are held locally (with the real File object, for later upload)
  // until "Upload to lead file" is tapped — that's when they're actually
  // sent, so the caption typed below applies to what gets stored.
  const handleFiles = (e) => {
    const files = Array.from(e.target.files || []);
    const newItems = files.map(f => ({
      file: f, name: f.name, size: (f.size/1024/1024).toFixed(1)+' MB',
      when: 'Just now', kind: f.type.startsWith('image/') ? 'image' : 'doc',
      url: URL.createObjectURL(f),
    }));
    setCaptured(prev => [...newItems, ...prev]);
    e.target.value = '';
  };
  const kinds = [
    { id: 'Site visit photos', icon: 'camera' },
    { id: 'Signed documents', icon: 'doc' },
    { id: 'Cheque / payment', icon: 'rupee' },
    { id: 'Voice note', icon: 'mic' },
  ];

  const [uploads, setUploads] = React.useState(null); // null = loading
  const loadUploads = React.useCallback(() => {
    if (window.SRAPI && typeof window.SRAPI.getLeadEvidence === 'function') {
      window.SRAPI.getLeadEvidence(lead.id)
        .then(rows => setUploads(rows.map(r => ({
          name: r.filename,
          size: r.file_size ? (r.file_size / 1024 / 1024).toFixed(1) + ' MB' : '',
          when: new Date(r.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' }),
          kind: (r.mime_type || '').startsWith('image/') ? 'image' : 'doc',
          url: r.url,
        }))))
        .catch(() => setUploads([]));
    } else {
      setUploads([]);
    }
  }, [lead.id]);
  React.useEffect(() => { loadUploads(); }, [loadUploads]);

  return (
    <div className="pf-screen pf-anim-in">
      <PFHeader
        left={<PFBackBtn onClick={() => go('lead', lead.id)} />}
        title="Upload evidence"
        subtitle={lead.name}
      />
      <div className="pf-scroll" style={{ padding: '0 16px 24px' }}>
        <div className="pf-label" style={{ marginBottom: 8 }}>Evidence type</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 16 }}>
          {kinds.map(k => (
            <button key={k.id} onClick={() => setKind(k.id)} style={{
              background: kind === k.id ? 'var(--pf-green-tint)' : 'var(--pf-surface)',
              color: kind === k.id ? 'var(--pf-green-ink)' : 'var(--pf-ink-2)',
              border: `1px solid ${kind === k.id ? 'var(--pf-green-tint-2)' : 'var(--pf-line)'}`,
              borderRadius: 14, padding: '12px 10px', display: 'flex', alignItems: 'center', gap: 10,
              cursor: 'pointer', fontFamily: 'inherit', fontWeight: kind === k.id ? 600 : 500, fontSize: 13, textAlign: 'left',
            }}>
              <PFIcon name={k.icon} size={18} color="var(--pf-green)" />
              {k.id}
            </button>
          ))}
        </div>

        {/* Dropzone */}
        <div style={{
          border: '1.5px dashed var(--pf-green-tint-2)',
          background: 'var(--pf-green-tint)',
          borderRadius: 16, padding: 18, textAlign: 'center', marginBottom: 14,
        }}>
          {kind === 'Voice note' ? (
            <>
              <div style={{
                width: 48, height: 48, margin: '0 auto 10px', borderRadius: 14,
                background: recording ? '#dc2626' : 'var(--pf-green)', display: 'flex', alignItems: 'center', justifyContent: 'center',
                animation: recording ? 'pfRec 1s infinite' : 'none',
              }}>
                <PFIcon name="mic" size={22} color="#fff" />
              </div>
              <div style={{ fontFamily: 'var(--pf-serif)', fontSize: 16, fontWeight: 600 }}>
                {recording ? `Recording… ${String(Math.floor(recSeconds / 60)).padStart(2, '0')}:${String(recSeconds % 60).padStart(2, '0')}` : 'Record a voice note'}
              </div>
              <div style={{ fontSize: 12, color: 'var(--pf-ink-3)', marginTop: 4, marginBottom: 12 }}>
                {recording ? 'Tap stop when done' : 'Or choose an existing audio file'}
              </div>
              <input ref={galleryRef} type="file" accept="audio/*" multiple style={{display:'none'}} onChange={handleFiles} />
              <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
                {recording ? (
                  <button className="pf-btn pf-btn-sm" style={{ background: '#dc2626' }} onClick={stopRecording}><PFIcon name="record" size={14} color="#fff" /> Stop</button>
                ) : (
                  <>
                    <button className="pf-btn pf-btn-sm" onClick={startRecording}><PFIcon name="mic" size={14} color="#fff" /> Record</button>
                    <button className="pf-btn pf-btn-ghost pf-btn-sm" onClick={() => galleryRef.current && galleryRef.current.click()}><PFIcon name="upload" size={14} color="var(--pf-green)" /> Choose file</button>
                  </>
                )}
              </div>
              <style>{`@keyframes pfRec { 0%, 100% { opacity: 1 } 50% { opacity: 0.5 } }`}</style>
            </>
          ) : (
            <>
              <div style={{
                width: 48, height: 48, margin: '0 auto 10px', borderRadius: 14,
                background: 'var(--pf-green)', display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <PFIcon name="camera" size={22} color="#fff" />
              </div>
              <div style={{ fontFamily: 'var(--pf-serif)', fontSize: 16, fontWeight: 600 }}>Capture or select</div>
              <div style={{ fontSize: 12, color: 'var(--pf-ink-3)', marginTop: 4, marginBottom: 12 }}>
                JPG, PNG, PDF · up to 25 MB each
              </div>
              {/* Hidden file inputs — Camera (capture) + Gallery */}
              <input ref={cameraRef} type="file" accept="image/*,video/*" capture="environment" multiple style={{display:'none'}} onChange={handleFiles} />
              <input ref={galleryRef} type="file" accept="image/*,application/pdf" multiple style={{display:'none'}} onChange={handleFiles} />
              <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
                <button className="pf-btn pf-btn-sm" onClick={() => cameraRef.current && cameraRef.current.click()}><PFIcon name="camera" size={14} color="#fff" /> Camera</button>
                <button className="pf-btn pf-btn-ghost pf-btn-sm" onClick={() => galleryRef.current && galleryRef.current.click()}><PFIcon name="upload" size={14} color="var(--pf-green)" /> Gallery</button>
              </div>
            </>
          )}
        </div>

        {/* Current upload preview */}
        <SectionTitle title="Just added" style={{ marginTop: 4 }} />
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 16 }}>
          {captured.map((f, i) => (
            <div key={i} style={{ height:110, borderRadius:10, overflow:'hidden', background:'#f3f4f6', position:'relative' }}>
              {f.kind === 'image'
                ? <img src={f.url} style={{ width:'100%', height:'100%', objectFit:'cover' }} alt={f.name} />
                : <div style={{ display:'flex', alignItems:'center', justifyContent:'center', height:'100%', fontSize:12, color:'var(--pf-ink-3)', padding:8, textAlign:'center' }}>{f.name}</div>
              }
              <div style={{ position:'absolute', bottom:4, left:4, background:'rgba(0,0,0,0.55)', borderRadius:4, padding:'2px 6px', fontSize:9.5, color:'#fff' }}>{f.uploaded ? '✓ saved' : 'pending upload'}</div>
            </div>
          ))}
          <button onClick={() => galleryRef.current && galleryRef.current.click()} style={{
            height: 110, border: '1px dashed var(--pf-line)', borderRadius: 10, background: 'var(--pf-surface)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 4,
            color: 'var(--pf-ink-3)', cursor: 'pointer', fontFamily: 'inherit',
          }}>
            <PFIcon name="plus" size={22} />
            <span style={{ fontSize: 11 }}>Add more</span>
          </button>
        </div>

        <div className="pf-field">
          <div className="pf-label">Caption (optional)</div>
          <textarea className="pf-textarea" value={caption} onChange={(e) => setCaption(e.target.value)}
            placeholder="e.g. T-2, 14th floor site visit — east-facing units shown" />
        </div>

        <button
          className="pf-btn pf-btn-block"
          disabled={submitting || captured.length === 0}
          style={captured.length === 0 ? { opacity: 0.5, cursor: 'not-allowed' } : undefined}
          onClick={async () => {
            if (captured.length === 0) { showToast('Capture or select a file first'); return; }
            if (!window.SRAPI || typeof window.SRAPI.uploadEvidence !== 'function') {
              showToast('Not signed in — cannot upload'); return;
            }
            setSubmitting(true);
            let failures = 0;
            let lastError = '';
            for (const item of captured) {
              if (item.uploaded) continue;
              try {
                await window.SRAPI.uploadEvidence(lead.id, item.file, { kind, caption });
                item.uploaded = true;
              } catch (e) {
                failures++;
                lastError = (e && e.message) ? e.message : 'unknown error';
              }
            }
            setCaptured(prev => [...prev]); // re-render to reflect .uploaded flags
            setSubmitting(false);
            if (failures > 0) {
              alert(`${failures} file(s) failed to upload: ${lastError}`);
            } else {
              showToast('✓ Uploaded to lead file');
              loadUploads();
              setTimeout(() => go('lead', lead.id), 900);
            }
          }}
        >{submitting ? 'Uploading…' : 'Upload to lead file'}</button>
        {toast && (
          <div style={{
            position: 'fixed', bottom: 90, left: '50%', transform: 'translateX(-50%)',
            background: 'var(--pf-ink)', color: '#fff', padding: '10px 18px', borderRadius: 999,
            fontSize: 13, fontWeight: 500, zIndex: 100, boxShadow: '0 8px 24px rgba(0,0,0,0.25)',
          }}>{toast}</div>
        )}

        <SectionTitle title="Previously uploaded" style={{ marginTop: 20 }} />
        {uploads === null ? (
          <div style={{ fontSize: 12.5, color: 'var(--pf-ink-3)', padding: '8px 2px' }}>Loading…</div>
        ) : uploads.length === 0 ? (
          <div style={{ fontSize: 12.5, color: 'var(--pf-ink-3)', padding: '8px 2px' }}>Nothing uploaded yet.</div>
        ) : (
          <div className="pf-card" style={{ padding: 0, overflow: 'hidden' }}>
            {uploads.map((u, i) => (
              <a key={i} href={u.url} target="_blank" rel="noreferrer" className="pf-row"
                style={{ padding: '10px 14px', textDecoration: 'none', color: 'inherit' }}>
                <div style={{
                  width: 36, height: 36, borderRadius: 10, flex: '0 0 36px',
                  background: u.kind === 'image' ? 'var(--pf-amber-tint)' : 'var(--pf-blue-tint)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <PFIcon name={u.kind === 'image' ? 'camera' : 'doc'} size={16}
                    color={u.kind === 'image' ? 'var(--pf-amber)' : 'var(--pf-blue)'} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{u.name}</div>
                  <div style={{ fontSize: 11, color: 'var(--pf-ink-4)', marginTop: 2 }}>{u.size} · {u.when}</div>
                </div>
                <PFIcon name="chevR" size={14} color="var(--pf-ink-4)" />
              </a>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

Object.assign(window, { PFScreenTasks, PFScreenTeamTasks, PFScreenEvidence });



// ===== 10-pf-screens-5.jsx =====
/* global React, PFIcon, PFHeader, PFBackBtn, PF_DATA, iconBtn, SectionTitle */

// ─────────────────────────────────────────────────────────────
// PROFILING FORM
// ─────────────────────────────────────────────────────────────
/* ─────────────────────────────────────────────────────────────
 * PROFILE STORE
 *   • Lives at window.PROFILE_STORE — single source of truth for
 *     customer profiling answers, keyed by leadId.
 *   • Persists to localStorage('sr_profiles_v1') so reloads keep state.
 *   • Saves remotely via window.SRAPI.submitProfiling when present.
 *   • Dispatches DOM event 'sr:profile-updated' so any screen
 *     (Lead detail, chat ping card, super-admin web view) can react.
 * ───────────────────────────────────────────────────────────── */
const PROFILE_LS_KEY = 'sr_profiles_v1';
window.PROFILE_STORE = window.PROFILE_STORE || (() => {
  try { return JSON.parse(localStorage.getItem(PROFILE_LS_KEY) || '{}'); }
  catch (e) { return {}; }
})();

const DEFAULT_PROFILE = {
  // Personal
  name: '', mobile: '', email: '', pan: '',
  // Family
  maritalStatus: 'Married', familySize: '4 members', coApplicant: '',
  // Financial
  employer: '', designation: '', currency: '₹', annualIncome: '',
  fundingType: 'Mixed', loanPct: 70,
  // Intent
  purchaseIntent: 'Self-use', timeline: '30–60 days', concerns: '',
};

function loadProfile(leadId) {
  return Object.assign({}, DEFAULT_PROFILE, window.PROFILE_STORE[leadId] || {});
}
function saveProfile(leadId, data) {
  window.PROFILE_STORE[leadId] = Object.assign({}, window.PROFILE_STORE[leadId] || {}, data, {
    _updatedAt: new Date().toISOString(),
  });
  try { localStorage.setItem(PROFILE_LS_KEY, JSON.stringify(window.PROFILE_STORE)); } catch (e) {}
  // Remote sync (best-effort, prototype tolerates offline)
  if (window.SRAPI && typeof window.SRAPI.submitProfiling === 'function') {
    try { window.SRAPI.submitProfiling(leadId, window.PROFILE_STORE[leadId]); }
    catch (e) { /* ignore — local store stays authoritative */ }
  }
  document.dispatchEvent(new CustomEvent('sr:profile-updated', {
    detail: { leadId, profile: window.PROFILE_STORE[leadId] },
  }));
}
Object.assign(window, { loadProfile, saveProfile });

// Previously seeded hardcoded fake PII (PAN, employer, income) into
// window.PROFILE_STORE for any lead ID literally equal to 'l1'/'l3' on
// every app load. That's fine for the two fixed demo leads in prototype
// mode, but the Add Lead screen lets an admin freely edit the suggested
// Lead ID to anything — including 'l1' — so a real lead could have
// fabricated financial data silently injected into its profile. Removed;
// loadProfile() already falls back to DEFAULT_PROFILE's neutral values for
// any lead with no saved profiling data, demo or real.

const PFScreenProfiling = ({ go, leadId }) => {
  const lead = PF_DATA.leads.find(l => l.id === leadId) || PF_DATA.leads[0];
  // Was React.useState(2) — the form always opened straight at step 3
  // (Financial), silently skipping past Personal (PAN) and Family
  // (co-applicant) on every normal entry. Those fields were never missing
  // from the form, just never reachable without noticing the tiny
  // progress-bar segments are individually clickable.
  const [step, setStep] = React.useState(0);
  // Pre-fill from what's already on file for this lead — previously
  // always started blank (even name/phone we already have from lead
  // creation), forcing the agent to retype info the app already had.
  // Saved profiling answers (if any) still take priority; this only
  // fills gaps loadProfile's DEFAULT_PROFILE left empty. Every field
  // stays a normal editable input, so a typo in the original lead
  // record can still be corrected here.
  const [form, setForm] = React.useState(() => {
    const saved = loadProfile(lead.id);
    return {
      ...saved,
      name: saved.name || lead.name || '',
      mobile: saved.mobile || lead.phone || '',
      email: saved.email || lead.email || '',
    };
  });
  const [saved, setSaved] = React.useState(false);
  const steps = ['Personal', 'Family', 'Financial', 'Intent'];

  // Single update helper used by every field
  const update = (patch) => setForm(f => Object.assign({}, f, patch));

  // On mount: if local store has no data for this lead, fetch from backend so the
  // form pre-fills correctly on any device (not just the one where it was first filled).
  React.useEffect(() => {
    const hasLocal = window.PROFILE_STORE && window.PROFILE_STORE[lead.id] &&
      Object.keys(window.PROFILE_STORE[lead.id]).some(k => !k.startsWith('_') && window.PROFILE_STORE[lead.id][k]);
    if (!hasLocal && window.SRAPI && typeof window.SRAPI.fetchLeadProfiling === 'function') {
      window.SRAPI.fetchLeadProfiling(lead.id).then(res => {
        if (res && res.profiling && Object.keys(res.profiling).length) {
          setForm(f => Object.assign({}, f, res.profiling));
        }
      }).catch(() => {});
    }
  }, [lead.id]);

  // Persist on every change so partial entries aren't lost; remote sync only on Save.
  React.useEffect(() => {
    window.PROFILE_STORE[lead.id] = Object.assign({}, window.PROFILE_STORE[lead.id] || {}, form);
    try { localStorage.setItem(PROFILE_LS_KEY, JSON.stringify(window.PROFILE_STORE)); } catch (e) {}
  }, [form, lead.id]);

  const handleSave = () => {
    saveProfile(lead.id, form);
    setSaved(true);
    setTimeout(() => { setSaved(false); go('lead', lead.id); }, 700);
  };

  return (
    <div className="pf-screen pf-anim-in">
      <PFHeader
        left={<PFBackBtn onClick={() => go('lead', lead.id)} />}
        title="Customer profiling"
        subtitle={lead.name}
      />
      <div style={{ padding: '0 16px 12px' }}>
        <div style={{ display: 'flex', gap: 4, marginBottom: 8 }}>
          {steps.map((s, i) => (
            <div key={s} onClick={() => setStep(i)} style={{
              flex: 1, height: 4, borderRadius: 2, cursor: 'pointer',
              background: i <= step ? 'var(--pf-green)' : 'var(--pf-line)',
            }} />
          ))}
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: 'var(--pf-ink-3)' }}>
          <span>Step {step + 1} of {steps.length}</span>
          <span style={{ fontWeight: 600, color: 'var(--pf-green)' }}>{steps[step]}</span>
        </div>
      </div>

      <div className="pf-scroll" style={{ padding: '4px 16px 20px' }}>
        {step === 0 && <PersonalStep form={form} update={update} />}
        {step === 1 && <FamilyStep    form={form} update={update} />}
        {step === 2 && <FinancialStep form={form} update={update} />}
        {step === 3 && <IntentStep    form={form} update={update} />}
      </div>

      <div style={{ padding: '10px 16px 14px', borderTop: '1px solid var(--pf-line)', background: 'var(--pf-bg)', display: 'flex', gap: 8 }}>
        <button className="pf-btn pf-btn-ghost" style={{ flex: 1 }}
          onClick={() => setStep(s => Math.max(0, s - 1))}
          disabled={step === 0}>Back</button>
        <button className="pf-btn" style={{ flex: 2, background: saved ? 'var(--pf-green-deep, #0b3a24)' : undefined }}
          onClick={() => step < 3 ? setStep(s => s + 1) : handleSave()}>
          {step < 3 ? 'Continue' : (saved ? '✓ Saved' : 'Save profile')}
        </button>
      </div>
    </div>
  );
};

const FieldGroup = ({ children }) => <div className="pf-card" style={{ marginBottom: 12 }}>{children}</div>;

const PersonalStep = ({ form, update }) => (
  <FieldGroup>
    <div className="pf-field">
      <div className="pf-label">Full name</div>
      <input className="pf-input" value={form.name} onChange={e => update({ name: e.target.value })} placeholder="Full name" />
    </div>
    <div className="pf-field">
      <div className="pf-label">Mobile</div>
      <input className="pf-input" value={form.mobile} onChange={e => update({ mobile: e.target.value })} placeholder="+91 ..." />
    </div>
    <div className="pf-field">
      <div className="pf-label">Email</div>
      <input className="pf-input" value={form.email} onChange={e => update({ email: e.target.value })} placeholder="name@mail.com" />
    </div>
    <div className="pf-field" style={{ marginBottom: 0 }}>
      <div className="pf-label">PAN</div>
      <input className="pf-input" value={form.pan} onChange={e => update({ pan: e.target.value.toUpperCase() })} placeholder="ABCDE1234F" maxLength={10} />
    </div>
  </FieldGroup>
);

const FamilyStep = ({ form, update }) => (
  <FieldGroup>
    <div className="pf-field">
      <div className="pf-label">Marital status</div>
      <select className="pf-select" value={form.maritalStatus} onChange={e => update({ maritalStatus: e.target.value })}>
        <option>Single</option><option>Married</option><option>Divorced</option><option>Widowed</option>
      </select>
    </div>
    <div className="pf-field">
      <div className="pf-label">Family size</div>
      <input className="pf-input" value={form.familySize} onChange={e => update({ familySize: e.target.value })} placeholder="e.g. 4 members" />
    </div>
    <div className="pf-field" style={{ marginBottom: 0 }}>
      <div className="pf-label">Co-applicant</div>
      <input className="pf-input" value={form.coApplicant} onChange={e => update({ coApplicant: e.target.value })} placeholder="Priya Mehta (spouse)" />
    </div>
  </FieldGroup>
);

const FinancialStep = ({ form, update }) => {
  const loan = Math.max(0, Math.min(100, Number(form.loanPct) || 0));
  const own  = 100 - loan;
  const fundingOpts = ['All own funds', 'Mostly loan', 'Mixed', 'Undecided'];
  // Auto-snap loan% when the funding type toggles, but only if user hasn't customised.
  const applyFundingType = (t) => {
    const snap = { 'All own funds': 0, 'Mostly loan': 80, 'Mixed': 50, 'Undecided': loan };
    update({ fundingType: t, loanPct: snap[t] });
  };
  return (
    <>
      <FieldGroup>
        <div className="pf-field">
          <div className="pf-label">Employer</div>
          <input className="pf-input" value={form.employer} onChange={e => update({ employer: e.target.value })} placeholder="Company name" />
        </div>
        <div className="pf-field">
          <div className="pf-label">Designation</div>
          <input className="pf-input" value={form.designation} onChange={e => update({ designation: e.target.value })} placeholder="Title / role" />
        </div>
        <div className="pf-field" style={{ marginBottom: 0 }}>
          <div className="pf-label">Annual income</div>
          <div style={{ display: 'flex', gap: 6 }}>
            <select className="pf-select" style={{ flex: 0, width: 86 }} value={form.currency} onChange={e => update({ currency: e.target.value })}>
              <option>₹</option><option>$</option><option>€</option><option>£</option><option>AED</option>
            </select>
            <input className="pf-input" style={{ flex: 1 }} value={form.annualIncome} onChange={e => update({ annualIncome: e.target.value })} placeholder="48,00,000" inputMode="numeric" />
          </div>
        </div>
      </FieldGroup>
      <FieldGroup>
        <div className="pf-label" style={{ marginBottom: 8 }}>Funding mix</div>
        <div style={{ display: 'flex', gap: 6, marginBottom: 8, flexWrap: 'wrap' }}>
          {fundingOpts.map(o => {
            const on = form.fundingType === o;
            return (
              <button key={o} onClick={() => applyFundingType(o)} style={{
                padding: '8px 10px', borderRadius: 999, fontSize: 11.5, fontWeight: 500,
                border: '1px solid ' + (on ? 'var(--pf-green)' : 'var(--pf-line)'),
                background: on ? 'var(--pf-green-tint)' : 'var(--pf-surface)',
                color: on ? 'var(--pf-green-ink)' : 'var(--pf-ink-2)',
                cursor: 'pointer', fontFamily: 'inherit',
              }}>{o}</button>
            );
          })}
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: 'var(--pf-ink-3)', marginTop: 6 }}>
          <span>Loan: <strong style={{ color: 'var(--pf-ink)' }}>{loan}%</strong></span>
          <span>Own: <strong style={{ color: 'var(--pf-ink)' }}>{own}%</strong></span>
        </div>
        <input type="range" min="0" max="100" step="5" value={loan}
          onChange={e => update({ loanPct: Number(e.target.value), fundingType: Number(e.target.value) === 0 ? 'All own funds' : Number(e.target.value) >= 75 ? 'Mostly loan' : 'Mixed' })}
          style={{ width: '100%', marginTop: 8, accentColor: 'var(--pf-green)' }} />
        <div style={{ height: 8, borderRadius: 4, background: 'var(--pf-line-2)', marginTop: 4, overflow: 'hidden', display: 'flex' }}>
          <div style={{ width: `${loan}%`, background: 'var(--pf-green)', transition: 'width .2s' }} />
          <div style={{ width: `${own}%`,  background: 'var(--pf-amber)', transition: 'width .2s' }} />
        </div>
        <div className="pf-field" style={{ marginTop: 14, marginBottom: 0 }}>
          <div className="pf-label">Estimated loan amount</div>
          <input className="pf-input" value={form.loanAmount || ''} onChange={e => update({ loanAmount: e.target.value })} placeholder={`e.g. ${Math.round(((Number((form.annualIncome||'').replace(/[^0-9.]/g,'')) || 0) * (loan/100) * 6)).toLocaleString('en-IN')}`} inputMode="numeric" />
        </div>
      </FieldGroup>
    </>
  );
};

const IntentStep = ({ form, update }) => {
  const timelineOpts = ['Immediate', '30–60 days', '3 months', '6 months+'];
  return (
    <>
      <FieldGroup>
        <div className="pf-field">
          <div className="pf-label">Purchase intent</div>
          <select className="pf-select" value={form.purchaseIntent} onChange={e => update({ purchaseIntent: e.target.value })}>
            <option>Self-use</option><option>Investment</option><option>Both</option><option>Gift</option>
          </select>
        </div>
        <div className="pf-field">
          <div className="pf-label">Timeline</div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {timelineOpts.map(o => {
              const on = form.timeline === o;
              return (
                <button key={o} onClick={() => update({ timeline: o })} style={{
                  padding: '8px 12px', borderRadius: 999, fontSize: 12,
                  border: '1px solid ' + (on ? 'var(--pf-green)' : 'var(--pf-line)'),
                  background: on ? 'var(--pf-green-tint)' : 'var(--pf-surface)',
                  color: on ? 'var(--pf-green-ink)' : 'var(--pf-ink-2)',
                  cursor: 'pointer', fontFamily: 'inherit', fontWeight: 500,
                }}>{o}</button>
              );
            })}
          </div>
        </div>
        <div className="pf-field" style={{ marginBottom: 0 }}>
          <div className="pf-label">Top concerns</div>
          <textarea className="pf-textarea" value={form.concerns} onChange={e => update({ concerns: e.target.value })} placeholder="What's on the customer's mind? (e.g. school proximity, RERA, floor preference)" />
        </div>
      </FieldGroup>
    </>
  );
};

// ─────────────────────────────────────────────────────────────
// LEARN / TRAINING
// ─────────────────────────────────────────────────────────────
const PFScreenLearn = ({ go }) => {
  const [cat, setCat] = React.useState('All');
  const [searchOpen, setSearchOpen] = React.useState(false);
  const [q, setQ] = React.useState('');
  const cats = ['All', 'Core', 'Project', 'Compliance', 'Reference'];
  const items = PF_DATA.training
    .filter(t => cat === 'All' || t.tag === cat)
    .filter(t => !q || t.title.toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="pf-screen pf-anim-in">
      <PFHeader title="Training" subtitle="Scripts, videos & playbooks"
        left={<PFBackBtn onClick={() => go('home')} />}
        right={
          <button style={iconBtn} onClick={() => setSearchOpen(v => !v)} aria-label="Search training">
            <PFIcon name={searchOpen ? 'close' : 'search'} size={18} />
          </button>
        } />
      {searchOpen && (
        <div style={{ padding: '0 16px 10px' }}>
          <div style={{ position: 'relative' }}>
            <input
              autoFocus
              value={q} onChange={e => setQ(e.target.value)}
              placeholder="Search training materials…"
              className="pf-input"
              style={{ paddingLeft: 36, fontSize: 13.5, background: 'var(--pf-surface)' }}
            />
            <div style={{ position: 'absolute', left: 12, top: 13 }}>
              <PFIcon name="search" size={16} color="var(--pf-ink-4)" />
            </div>
          </div>
        </div>
      )}
      <div className="pf-scroll" style={{ padding: '0 16px 20px' }}>
        {/* Continue watching */}
        <div className="pf-card-tinted" style={{ marginBottom: 14 }}>
          <div style={{ display: 'flex', gap: 12 }}>
            <div style={{
              width: 70, height: 70, borderRadius: 12, flex: '0 0 70px',
              background: 'linear-gradient(135deg, #1a6b44, #0b3a24)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff',
            }}>
              <PFIcon name="play" size={26} color="#fff" />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 10.5, letterSpacing: '0.1em', color: 'var(--pf-green)', fontWeight: 700 }}>CONTINUE</div>
              <div style={{ fontFamily: 'var(--pf-serif)', fontSize: 15, fontWeight: 600, marginTop: 2 }}>Handling objections — Advanced</div>
              <div style={{ fontSize: 11.5, color: 'var(--pf-ink-3)', marginTop: 4 }}>6 min left · 65%</div>
              <div style={{ height: 4, borderRadius: 2, background: 'rgba(15,81,50,0.15)', marginTop: 6, overflow: 'hidden' }}>
                <div style={{ width: '65%', height: '100%', background: 'var(--pf-green)' }} />
              </div>
            </div>
          </div>
        </div>

        {/* Scripts shortcut */}
        <div className="pf-card" style={{ marginBottom: 14, padding: 0, overflow: 'hidden' }}>
          <div style={{ padding: '12px 14px 8px', display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
            <div style={{ fontFamily: 'var(--pf-serif)', fontSize: 15, fontWeight: 600 }}>Call scripts</div>
            <span style={{ fontSize: 11.5, color: 'var(--pf-ink-3)' }}>हिं + EN</span>
          </div>
          {PF_DATA.scripts.map((s, i) => (
            <div key={s.id} style={{
              display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px',
              borderTop: '1px solid var(--pf-line-2)', cursor: 'pointer',
            }}>
              <div style={{
                width: 28, height: 28, borderRadius: 8, background: 'var(--pf-green-tint)',
                display: 'flex', alignItems: 'center', justifyContent: 'center', flex: '0 0 28px',
              }}>
                <PFIcon name="book" size={14} color="var(--pf-green)" />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 500 }}>{s.title}</div>
                <div style={{ fontSize: 11.5, color: 'var(--pf-ink-3)', marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {s.hinglish.slice(0, 60)}…
                </div>
              </div>
              <PFIcon name="chevR" size={14} color="var(--pf-ink-4)" />
            </div>
          ))}
        </div>

        <SectionTitle title="All materials" />
        <div style={{ display: 'flex', gap: 6, marginBottom: 12, overflowX: 'auto' }}>
          {cats.map(c => (
            <button key={c} onClick={() => setCat(c)} style={{
              padding: '6px 12px', borderRadius: 999, whiteSpace: 'nowrap',
              background: cat === c ? 'var(--pf-green)' : 'var(--pf-surface)',
              color: cat === c ? '#fff' : 'var(--pf-ink-2)',
              border: `1px solid ${cat === c ? 'var(--pf-green)' : 'var(--pf-line)'}`,
              fontSize: 12, fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit',
            }}>{c}</button>
          ))}
        </div>

        <div style={{ display: 'grid', gap: 10 }}>
          {items.length === 0 && (
            <div style={{ padding: 30, textAlign: 'center', color: 'var(--pf-ink-4)', fontSize: 13 }}>No materials match.</div>
          )}
          {items.map(t => (
            <div key={t.id} className="pf-card" style={{ padding: 0, overflow: 'hidden', display: 'flex', gap: 12, alignItems: 'center' }}>
              <div style={{
                width: 80, height: 80, flex: '0 0 80px',
                background: t.kind === 'video'
                  ? 'linear-gradient(135deg, #1a6b44, #0b3a24)'
                  : 'linear-gradient(135deg, #ebe5d4, #d9cfb2)',
                display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative',
              }}>
                <PFIcon name={t.kind === 'video' ? 'video' : 'doc'} size={24}
                  color={t.kind === 'video' ? '#fff' : 'var(--pf-ink-2)'} />
                {t.progress >= 1 && (
                  <div style={{
                    position: 'absolute', top: 6, right: 6, width: 18, height: 18, borderRadius: '50%',
                    background: 'var(--pf-green)', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <PFIcon name="check" size={12} color="#fff" strokeWidth={2.5} />
                  </div>
                )}
              </div>
              <div style={{ flex: 1, padding: '10px 14px 10px 0' }}>
                <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 3 }}>
                  <span className="pf-chip pf-chip-neutral" style={{ fontSize: 10, padding: '2px 6px' }}>{t.tag}</span>
                  <span style={{ fontSize: 11, color: 'var(--pf-ink-4)' }}>{t.duration}</span>
                </div>
                <div style={{ fontSize: 13.5, fontWeight: 500, lineHeight: 1.3 }}>{t.title}</div>
                {t.progress > 0 && t.progress < 1 && (
                  <div style={{ height: 3, borderRadius: 2, background: 'var(--pf-line-2)', marginTop: 8, overflow: 'hidden' }}>
                    <div style={{ width: `${t.progress * 100}%`, height: '100%', background: 'var(--pf-green)' }} />
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// window.REALTY_CMDS is keyed by human-readable role titles ("Business
// Owner", "Sales Manager", ...), but the JWT/workspace role is a raw string
// like "superadmin"/"admin" that never matches any of those keys — so an
// actual account owner silently fell back to the generic, mostly-fake
// SLASH_CMDS list and never saw /followup, /remind, /meeting, /priority,
// /escalate, /stuck, /drive, etc. at all. Map admin-tier raw roles onto the
// closest REALTY_CMDS key instead of leaving the lookup to miss silently.
const resolveRealtyRoleKey = (role) => {
  const norm = String(role || '').toLowerCase().replace(/\s+/g, '_');
  if (window.REALTY_CMDS && window.REALTY_CMDS[role]) return role;
  if (['superadmin', 'admin', 'business_owner', 'owner'].includes(norm)) return 'Business Owner';
  if (['sales_head'].includes(norm)) return 'Sales Head';
  if (['sales_manager', 'manager'].includes(norm)) return 'Sales Manager';
  // Telecallers get the Presales command set (call-focused, no finance/profitability commands)
  if (['telecaller', 'presales', 'presales_executive', 'caller'].includes(norm)) return 'Presales Executive';
  return role;
};

// ─────────────────────────────────────────────────────────────
// CHAT / PING MANAGER
// ─────────────────────────────────────────────────────────────
const PFScreenChat = ({ go, role = 'Agent', fromLeadId }) => {
  const fromLead = fromLeadId ? (PF_DATA.leads.find(l => l.id === fromLeadId) || null) : null;
  const scrollRef = React.useRef();
  const SEED_MSGS = [
    { id:'c1', from:'Ankit Sharma', avatar:'AS', me:false, msg:"Morning — how's the Mehta lead looking?", tags:[], cards:[], time:'9:42' },
    { id:'c2', from:'You', avatar:'ME', me:true, msg:"Call at 10. He's serious, wants east-facing 3BHK in T-2.", tags:[], cards:[], time:'9:45' },
    { id:'c3', from:'You', avatar:'ME', me:true, msg:"He's asking for 6% discount + 2 covered parking. Current offer is 4% + 1 parking. Can we stretch?",
      tags:[{ type:'lead', id:'l1', label:'Rohan Mehta', meta:'Whispering Oaks · 3BHK' }], cards:[], time:'10:48' },
    { id:'c4', from:'Ankit Sharma', avatar:'AS', me:false, msg:"Check his profile — funding mix?", tags:[], cards:[], time:'10:49' },
    { id:'c5', from:'You', avatar:'ME', me:true, msg:"70% loan, 30% own. Income ₹48L annual. PAN verified.", tags:[], cards:[], time:'10:50' },
    { id:'c6', from:'Ankit Sharma', avatar:'AS', me:false, msg:"Okay — you can go to 5% + 2 parking IF he books today. Not a rupee more.", tags:[], cards:[], time:'10:52' },
    { id:'c7', from:'Ankit Sharma', avatar:'AS', me:false, msg:"Push for site visit this weekend with family.", tags:[], cards:[], time:'10:52' },
  ];
  const [activeThread, setActiveThread] = React.useState('team');
  const [notifOpen, setNotifOpen]       = React.useState(false);
  const [assistantThread, setAssistantThread] = React.useState(null);
  const [assistantMsgs, setAssistantMsgs]     = React.useState([]);
  const [assistantLoading, setAssistantLoading] = React.useState(false);
  // Demo seed — NEVER on by default.
  //
  // This used to run unconditionally on mount: getChatThreads() hydrates from
  // the server asynchronously and returns an empty placeholder synchronously,
  // so the "is it empty?" test was always true on first mount and the canned
  // thread was always installed. On a real but empty workspace the fabricated
  // conversation simply stayed — inventing a colleague, a lead, a discount
  // negotiation and a customer's income and PAN status, all visually
  // indistinguishable from genuine team chat. Confirmed on device against a
  // live workspace.
  //
  // Seed only when explicitly asked for (window.SR_DEMO_CHAT = true), and only
  // once hydration has actually completed and come back empty.
  React.useEffect(() => {
    if (!window.SR_DEMO_CHAT) return;
    const seedIfStillEmpty = () => {
      const threads = window.getChatThreads('realty');
      if (!threads['team'].messages || threads['team'].messages.length === 0) {
        threads['team'].messages = SEED_MSGS;
        forceUpdate();
      }
    };
    document.addEventListener('sr:threads-updated', seedIfStillEmpty);
    return () => document.removeEventListener('sr:threads-updated', seedIfStillEmpty);
  }, []);
  const [, forceUpdate] = React.useReducer(x => x+1, 0);
  React.useEffect(() => {
    const h = () => forceUpdate();
    document.addEventListener('sr:threads-updated', h);
    return () => document.removeEventListener('sr:threads-updated', h);
  }, []);
  const currentMsgs = (window.getChatThreads('realty')[activeThread] || {}).messages || [];

  React.useEffect(() => {
    if (activeThread !== 'assistant') return;
    if (!window.SRAPI || !window.SRAPI._token) return;
    setAssistantLoading(true);
    window.SRAPI._get('/api/mobile/assistant/thread')
      .then(res => {
        const data = res?.data || res;
        setAssistantThread(data?.thread || null);
        const msgs = (data?.messages || []).map(m => {
          let proposal = null;
          if (m.cards) {
            try {
              const cards = typeof m.cards === 'string' ? JSON.parse(m.cards) : m.cards;
              const pc = cards && cards.find(c => c.type === 'assistant_proposal');
              if (pc) proposal = pc;
            } catch {}
          }
          return { ...m, proposal, me: m.sender_id !== 'assistant' };
        });
        setAssistantMsgs(msgs);
      })
      .catch(() => {})
      .finally(() => setAssistantLoading(false));
  }, [activeThread]);

  // Use live team from SRAPI.fetchTeam() if available, fall back to demo names
  const [teamList, setTeamList] = React.useState(window.SR_TEAM || null);
  React.useEffect(() => {
    const h = (e) => setTeamList(e.detail.members);
    document.addEventListener('sr:team-loaded', h);
    return () => document.removeEventListener('sr:team-loaded', h);
  }, []);
  const REALTY_TEAM = [
    { id:'ankit',  name:'Ankit Sharma',  meta:'Manager' },
    { id:'priya',  name:'Priya Menon',   meta:'Senior Agent' },
    { id:'rahul',  name:'Rahul Singh',   meta:'Field Agent' },
    { id:'kavya',  name:'Kavya Nair',    meta:'Agent' },
    { id:'deepak', name:'Deepak Iyer',   meta:'Agent' },
  ];
  const liveTeam = teamList && teamList.length > 0 ? teamList : REALTY_TEAM;
  const ds = {
    _ns:      'realty',
    lead:     PF_DATA.leads.map(l => ({ id:l.id, name:l.name, meta:(l.project||'')+(l.budget?' · '+l.budget:'') })),
    client:   PF_DATA.leads.map(l => ({ id:l.id, name:l.name, meta:l.project||'' })),
    // Real tasks only — a fake hardcoded demo list used to be mixed in here,
    // indistinguishable in the picker from real ones, so a status update or
    // nudge on a "task" could silently be acting on data that didn't exist.
    task:     PF_DATA.tasks.map(t => ({ id:t.id, name:t.title, meta:t.due+' · '+t.priority, assignee:t.owner||'' })),
    employee: liveTeam,
    nudge:    liveTeam,
    update: [
      { id:'site', name:'Site Visit Update', meta:'' },
      { id:'booking', name:'Booking Confirmation', meta:'' },
      { id:'price', name:'Price Approval Request', meta:'' },
    ],
  };
  const botReply = React.useCallback((text) => {
    setTimeout(() => {
      const bot = { id:`bot${Date.now()}`, from:'SkaleAI', avatar:'AI', me:false, msg:text, tags:[], cards:[], time:'Now', isBot:true };
      window.addThreadMessage('realty', activeThread, bot);
      setTimeout(() => scrollRef.current && (scrollRef.current.scrollTop = scrollRef.current.scrollHeight), 60);
    }, 700);
  }, [activeThread]);

  // Local fallback responses (used when backend is unreachable)
  const _d = () => PF_DATA.today;
  const BOT_RESPONSES_LOCAL = {
    // ── Core pipeline & revenue (live-upgraded) ───────────────────────────────
    pipeline:          () => { const d = _d(); return `📊 *Pipeline Summary*\nActive value: ${d.pipeline} across ${PF_DATA.leads.length} leads.\nCalls: ${d.calls.done}/${d.calls.target} · Visits: ${d.visits.done}/${d.visits.scheduled} · Hot leads: ${d.hotLeads}`; },
    revenue:           () => `💰 *Revenue Overview*\nPipeline: ${_d().pipeline}\nBookings this month: ₹2.1 Cr · Collections: ₹1.4 Cr`,
    forecast:          () => `🔮 *30-day Forecast*\nProjected closures: 3 leads · Est. revenue: ₹5.2 Cr\nRisk: 2 leads in negotiation stalled > 7 days`,
    salesdashboard:    () => `📈 *Sales Dashboard*\nTeam calls today: 42 · SVs booked: 8 · Conversions: 2\nTop performer: Priya Rao (4 SVs)`,
    targets:           () => { const d = _d(); return `🎯 *Targets*\nSite visits: ${d.visits.done}/${d.visits.scheduled} today · Calls: ${d.calls.done}/${d.calls.target}\nMonthly SV target: 18 · Achieved: 12 (67%)`; },
    leaderboard:       () => `🏆 *Leaderboard (SV Booked)*\n1. Priya Rao — 4\n2. You — 2\n3. Arjun Mehra — 2\n4. Kavya Nair — 1`,
    // ── Reports & analytics ───────────────────────────────────────────────────
    conversion:        () => `🔄 *Conversion Funnel*\nNew→Qualified: 64% · Qualified→SV: 48% · SV→Booking: 22%\nDropout: 180 leads this month disqualified before SV`,
    teamperformance:   () => `👥 *Team Performance*\nCalls: 214 · SVs: 38 · Bookings: 5 · Avg connect rate: 34%\nLaggards: Ravi (8 calls), Suresh (11 calls)`,
    bookingreport:     () => `📝 *Booking Report*\nThis month: 3 bookings · Value: ₹5.8 Cr\nProjects: Meadowview (2), Whispering Oaks (1)`,
    lostanalysis:      () => `📉 *Lost Lead Analysis*\nTop reason: Price objection (38%) · Competition (24%) · Timeline (19%)\nRisk: 26 hot leads with no follow-up in 5+ days`,
    sitevisitreport:   () => `🏗️ *Site Visit Report*\nScheduled: ${_d().visits.scheduled} · Completed: ${_d().visits.done} · No-shows: 2\nConversion post-SV: 18% this week`,
    projectwise:       () => `🏗️ *Project-wise Performance*\nMeadowview: 12 SVs · 2 bookings · ₹3.1 Cr\nWhispering Oaks: 8 SVs · 1 booking · ₹2.7 Cr\nRivercrest: 4 SVs · 0 bookings ⚠️`,
    profitability:     () => `💹 *Profitability Report*\nMeadowview: 24% margin · Whispering Oaks: 21% · Rivercrest: 18%\nBlended GM: 22.1% vs target 25% ⚠️`,
    campaignroi:       () => `📣 *Campaign ROI*\nMeta: 4.3x ROI · Google: 6.1x · CP: 8.2x\nTotal spend: ₹6.2 L · Leads generated: 430`,
    review:            () => `📋 *Performance Review*\nTop: Priya Rao (4 SVs, 1 booking) · Ravi Singh (2 SVs, 0 bookings ⚠️)\nCoaching flag: 2 executives below target`,
    followupaudit:     () => `🔍 *Follow-up Audit*\nAjay: 17 missed · Rohan: 12 missed · Amit: 9 missed\nRisk: 26 leads may be lost due to no follow-up`,
    campaignlead:      () => `📣 *Campaign Leads*\nMeta — Jun campaign: 84 leads · 12 qualified · 3 SVs booked\nCPL: ₹465 · Lead quality score: 72/100`,
    regionwise:        () => `🗺️ *Region-wise Report*\nNorth Pune: 22 SVs · 3 bookings · South Pune: 18 SVs · 2 bookings\nPCMC: 8 SVs · 0 bookings ⚠️`,
    salesmanagerreport:() => `👤 *Sales Manager Report*\nPriya (Mgr): Team — 18 SVs · 3 bookings · Conversion: 16.7%\nRahul (Mgr): Team — 12 SVs · 1 booking · Conversion: 8.3% ⚠️`,
    escalation:        () => `🚨 *Open Escalations*\nCampaign B CPL +32% · Collections delayed ₹28L · Rivercrest inventory <15%\n3 escalations need response today`,
    // ── Ops & quick-reference ─────────────────────────────────────────────────
    inventory:         () => `🏗️ *Inventory Status*\nWhispering Oaks: 4 units left (T-2, T-3) · Meadowview: 7 units · Rivercrest: 12 units`,
    pricing:           () => `💲 *Current Pricing*\nWhispering Oaks 3BHK: ₹11,500/sqft\nMeadowview 2BHK: ₹9,200/sqft\nRivercrest 2BHK: ₹8,800/sqft`,
    leadsource:        () => `🔗 *Lead Source Analysis*\nMeta: 42% · Google: 28% · CP: 18% · Walk-in: 12%\nBest quality: CP leads (score 90) · Worst: Meta (score 72)`,
    costperlead:       () => `💰 *Cost Per Lead*\nMeta CPL: ₹465 · Google CPL: ₹380 · CP CPL: ₹220\nBlended CPL: ₹368 · Target: ₹350 ⚠️`,
    landingpage:       () => `🌐 *Landing Page Performance*\nMeadowview page: 4,200 visits · 310 leads · 7.4% CVR\nWhispering Oaks: 2,800 visits · 120 leads · 4.3% CVR`,
    leadquality:       () => `⭐ *Lead Quality Summary*\nMeta: 72/100 · Google: 83/100 · CP: 90/100\nLow-quality flags this month: 48 leads`,
    leadvolume:        () => `📊 *Lead Volume*\nToday: 14 new leads · This week: 82 · This month: 320\nTrend: -8% vs last month ⚠️`,
    funnel:            () => `🔀 *Conversion Funnel*\n1,000 Leads → 420 Qualified → 120 SVs → 28 Bookings\nLeakage: Qualified→SV stage (71%) is the biggest dropout`,
    salesperformance:  () => `📈 *Sales Performance*\nTeam bookings: 5 · Revenue: ₹9.8 Cr · vs target: 65% ⚠️\nHigh performers: Priya (1 booking), Kavya (1 booking)`,
    projecthealth:     () => `🏗️ *Project Health*\nMeadowview: 🟢 On track · Whispering Oaks: 🟡 Inventory low · Rivercrest: 🔴 0 bookings this month`,
    investorview:      () => `🏦 *Investor Snapshot*\nTotal bookings: ₹28.4 Cr YTD · Collections: ₹19.1 Cr · Pending: ₹9.3 Cr\nUnsold inventory value: ₹42 Cr`,
    alerts:            () => `🔔 *Business Alerts*\nInventory: Whispering Oaks < 15% remaining\nCampaign: Meta CPL up 32% this week\nCollections: ₹28L overdue 30+ days`,
    booking:           () => `🏠 *Bookings Overview*\nThis month: 3 bookings · Value: ₹5.8 Cr · Token collected: ₹45L\nPending docs: 1 booking · Pending loan: 1 booking`,
    collections:       () => `💳 *Collections & Receivables*\nCollected: ₹8.1 Cr · Pending: ₹28L overdue ⚠️\n3 bookings overdue 30+ days — escalation recommended`,
    outstanding:       () => `⚠️ *Outstanding Payments*\nTotal outstanding: ₹1.2 Cr · Overdue > 30 days: ₹28L\nHighest pending: Arjun Sharma (₹18L, 47 days overdue)`,
    // ── Category-specific overrides for shared cmd names ─────────────────────
    forecast_strategy: () => `🔮 *Strategic Forecast*\nNext quarter pipeline: ₹18 Cr · New project launch: 2 planned\nCapacity risk: Sales team at 94% utilisation`,
    dashboard_overview:() => `📊 *Executive Dashboard*\nRevenue: ₹9.8 Cr · Collections: ₹8.1 Cr · Open escalations: 3\nInv. health: 2 projects green, 1 red`,
    progress_learning: () => `📈 *Learning Progress*\nModule completions: 72% · Pending assessments: 4\nAction plans active: 3 of 5`,
    assessment_reports:() => `📝 *Assessment Summary*\nPre-program avg: 62 · Post-program avg: 83\nImprovements: +21 pts · 2 participants need re-assessment`,
    attendance_reports:() => `✅ *Attendance Report*\nThis batch: 88% avg attendance · Absences flagged: 3\nAt-risk: Vikram Singh (2 consecutive absences)`,
    completion_reports:() => `🏁 *Completion Report*\nProgram A: 91% complete · Program B: 67% ⚠️\nIncomplete: 4 participants — nudge recommended`,
    feedback_comm:     () => `💬 *Feedback Summary*\nProgram rating: 4.3/5 · Trainer rating: 4.6/5\nTop theme: "More role-play exercises needed"`,
    review_reports:    () => `🔍 *Review Report*\nParticipants reviewed this month: 12 · Avg score: 78/100\nCoaching flags: 3 participants below threshold`,
  };

  // Commands that benefit from live backend data
  const LIVE_COMMANDS = new Set([
    'pipeline', 'revenue', 'forecast', 'targets', 'leaderboard', 'salesdashboard',
    'teamperformance', 'conversion', 'bookingreport', 'lostanalysis', 'followupaudit',
    'collections', 'outstanding', 'alerts', 'escalation', 'leadvolume', 'leadquality',
    'funnel', 'dashboard', 'booking', 'leadsource', 'regionwise', 'landingpage',
  ]);

  const fetchLiveSignals = async () => {
    if (!window.SRAPI || !window.SRAPI._token) return null;
    try {
      const res = await window.SRAPI._get('/api/intelligence/signals');
      return res && res.data ? res.data : res;
    } catch { return null; }
  };

  const formatLiveReply = (cmd, s) => {
    const fmt = n => n >= 1e7 ? `₹${(n/1e7).toFixed(1)} Cr` : n >= 1e5 ? `₹${(n/1e5).toFixed(1)} L` : `₹${n}`;
    switch (cmd) {
      case 'pipeline':
      case 'revenue':
        return `📊 *Live Pipeline*\nActive value: ${fmt(s.revenuePotential)} · Hot leads: ${s.extremelyHotCount}\nStuck deals: ${s.stuckDealsCount} (value: ${fmt(s.stuckDealsValue || 0)})\nOverdue follow-ups: ${s.overdueCount}`;
      case 'targets':
        return `🎯 *Live Targets*\nHot leads: ${s.extremelyHotCount} · Overdue: ${s.overdueCount}\nStuck in negotiation: ${s.stuckDealsCount}`;
      case 'leaderboard':
      case 'salesdashboard':
        if (!s.topOpportunities || !s.topOpportunities.length) return null;
        const top = s.topOpportunities.slice(0, 3).map((l, i) => `${i+1}. ${l.name} — ${l.stage} (heat: ${l.heat})`).join('\n');
        return `🏆 *Top Opportunities*\n${top}`;
      case 'forecast':
        return `🔮 *Live Forecast*\nPipeline at risk: ${s.stuckDealsCount} stuck deals worth ${fmt(s.stuckDealsValue || 0)}\nHot leads to close: ${s.extremelyHotCount}`;
      // ── The 11 below were already in LIVE_COMMANDS (someone intended
      // them to be live) but formatLiveReply had no case for them, so
      // they silently fell through to the hardcoded BOT_RESPONSES_LOCAL
      // demo string every single time. Backend now computes all of this
      // in getCriticalSignals() (see intelligence.js) from real lead/
      // collections data instead. ──
      case 'teamperformance': {
        if (!s.teamPerformance || !s.teamPerformance.length) return null;
        const rows = s.teamPerformance.slice(0, 5)
          .map(t => `${t.owner}: ${t.booked}/${t.total} booked (${t.conversionPct}%)`).join('\n');
        return `👥 *Team Performance*\n${rows}`;
      }
      case 'conversion': {
        if (!s.stageFunnel) return null;
        const order = ['new', 'contacted', 'qualified', 'site_visit_done', 'negotiation', 'booked'];
        const parts = order.filter(k => s.stageFunnel[k]).map(k => `${k}: ${s.stageFunnel[k]}`);
        return `🔄 *Live Conversion Funnel*\n${parts.join(' → ')}`;
      }
      case 'bookingreport': {
        if (!s.bookingReport) return null;
        const b = s.bookingReport;
        return `📝 *Booking Report*\nThis month: ${b.countThisMonth} booking(s) · Value: ${fmt(b.valueThisMonth)}\nTotal bookings: ${b.countTotal}`;
      }
      case 'lostanalysis': {
        if (!s.lostAnalysis) return null;
        const la = s.lostAnalysis;
        const reasons = la.topReasons.map(r => `${r.reason} (${r.count})`).join(' · ') || 'No data yet';
        return `📉 *Lost Lead Analysis*\nTotal lost: ${la.totalLost}\nTop reasons: ${reasons}`;
      }
      case 'followupaudit': {
        if (!s.followupAudit) return null;
        const fa = s.followupAudit;
        const rows = fa.byOwner.map(o => `${o.owner}: ${o.count} missed`).join('\n') || 'No overdue follow-ups 🎉';
        return `🔍 *Follow-up Audit*\n${rows}\nTotal overdue: ${fa.totalOverdue}`;
      }
      case 'leadvolume': {
        if (!s.leadVolume) return null;
        const lv = s.leadVolume;
        return `📊 *Lead Volume*\nToday: ${lv.today} · This week: ${lv.thisWeek} · This month: ${lv.thisMonth}`;
      }
      case 'leadquality': {
        if (!s.leadQuality || !s.leadQuality.length) return null;
        const rows = s.leadQuality.slice(0, 5).map(q => `${q.source}: ${q.avgScore}/100 (${q.count} leads)`).join('\n');
        return `⭐ *Lead Quality by Source*\n${rows}`;
      }
      case 'collections': {
        if (!s.collectionsReport) return null;
        return `💳 *Collections*\nCollected: ${fmt(s.collectionsReport.collected)}\nOutstanding: ${fmt((s.outstandingReport && s.outstandingReport.outstanding) || 0)}`;
      }
      case 'outstanding': {
        if (!s.outstandingReport) return null;
        return `⚠️ *Outstanding Payments*\nTotal outstanding: ${fmt(s.outstandingReport.outstanding)}`;
      }
      case 'alerts': {
        if (!s.alerts) return null;
        if (!s.alerts.length) return `🔔 *Business Alerts*\nNo active alerts — all clear ✓`;
        return `🔔 *Business Alerts*\n${s.alerts.map(a => `• ${a}`).join('\n')}`;
      }
      case 'escalation': {
        if (!s.escalations) return null;
        if (!s.escalations.length) return `🚨 *Open Escalations*\nNo open escalations right now.`;
        const rows = s.escalations.map(e => `${e.name} — ${e.stage} (${fmt(Number(e.budget) || 0)})`).join('\n');
        return `🚨 *Open Escalations*\n${rows}`;
      }
      // funnel/dashboard/booking/leadsource all reuse fields already
      // computed above — no separate endpoint needed for these.
      case 'funnel': {
        if (!s.stageFunnel) return null;
        const order = ['new', 'contacted', 'qualified', 'site_visit_done', 'negotiation', 'booked'];
        const parts = order.filter(k => s.stageFunnel[k]).map(k => `${k}: ${s.stageFunnel[k]}`);
        return `🔀 *Conversion Funnel*\n${parts.join(' → ')}`;
      }
      case 'dashboard': {
        return `📊 *Dashboard*\nActive pipeline: ${fmt(s.revenuePotential)} · Hot leads: ${s.extremelyHotCount}\nBookings this month: ${(s.bookingReport && s.bookingReport.countThisMonth) || 0}\nOpen alerts: ${(s.alerts && s.alerts.length) || 0}`;
      }
      case 'booking': {
        if (!s.bookingReport) return null;
        const b = s.bookingReport;
        return `🏠 *Bookings Overview*\nThis month: ${b.countThisMonth} booking(s) · Value: ${fmt(b.valueThisMonth)}\nTotal bookings: ${b.countTotal}`;
      }
      case 'regionwise': {
        if (!s.areaInsights || !s.areaInsights.length) return null;
        const rows = s.areaInsights.map(a => `${a.area}: score ${a.score} · ${a.leads} leads`).join('\n');
        return `🗺️ *Region-wise Report*\n${rows}`;
      }
      case 'leadsource': {
        if (!s.leadQuality || !s.leadQuality.length) return null;
        const rows = s.leadQuality.slice(0, 5).map(q => `${q.source}: ${q.count} leads (avg score ${q.avgScore})`).join('\n');
        return `🔗 *Lead Source Analysis*\n${rows}`;
      }
      case 'landingpage': {
        if (!s.landingPagePerformance || !s.landingPagePerformance.length) return null;
        const rows = s.landingPagePerformance.slice(0, 5)
          .map(p => `${p.page}: ${p.leads} leads · ${p.bookings} booked (${p.conversionPct}%)`).join('\n');
        return `🌐 *Landing Page Performance*\n${rows}`;
      }
      default: return null;
    }
  };

  // ── Commands whose live data comes from a different endpoint than
  // /api/intelligence/signals (marketing spend, inventory levels, KPI
  // targets — none of that lives on the leads table). Each fetches its own
  // endpoint and formats independently; falls back to the same
  // BOT_RESPONSES_LOCAL mock as everything else on any failure. ──
  const OTHER_LIVE_COMMANDS = {
    inventory: {
      endpoint: '/api/inventory/summary',
      format: (d) => {
        if (!d || !d.totals) return null;
        const t = d.totals;
        return `🏗️ *Inventory Status*\nAvailable: ${t.total_available || 0} · Sold: ${t.total_sold || 0} · On hold: ${t.total_on_hold || 0}\nTotal units: ${t.total_units || 0}`;
      },
    },
    budget: {
      endpoint: '/api/marketing/budgets',
      format: (d) => {
        if (!d || !d.budgets) return null;
        if (!d.budgets.length) return `💳 *Marketing Budget*\nNo budget set for this period.`;
        const rows = d.budgets.slice(0, 5)
          .map(b => `${b.channel_label || b.channel_id}: ₹${Number(b.spent || 0).toLocaleString('en-IN')} / ₹${Number(b.budget_amount || 0).toLocaleString('en-IN')}`).join('\n');
        return `💳 *Marketing Budget*\n${rows}`;
      },
    },
    campaignroi: {
      endpoint: '/api/marketing/cpl-trend',
      format: (d) => {
        if (!d || !d.months || !d.months.length) return null;
        const latest = d.months[d.months.length - 1];
        const best = d.best_channel ? `Best: ${d.best_channel.label} (₹${d.best_channel.cost_per_lead}/lead)` : '';
        return `📣 *Campaign ROI*\nSpend: ₹${latest.total_spend.toLocaleString('en-IN')} · Leads: ${latest.total_leads} · Bookings: ${latest.total_bookings}\nBlended CPL: ₹${latest.blended_cpl}${best ? ` · ${best}` : ''}`;
      },
    },
    costperlead: {
      endpoint: '/api/marketing/cpl-trend',
      format: (d) => {
        if (!d || !d.months || !d.months.length) return null;
        const latest = d.months[d.months.length - 1];
        const chg = d.mom_cpl_change_pct != null ? ` (${d.mom_cpl_change_pct > 0 ? '+' : ''}${d.mom_cpl_change_pct}% vs last month)` : '';
        return `💰 *Cost Per Lead*\nBlended CPL: ₹${latest.blended_cpl}${chg}\n${(latest.channels || []).slice(0, 4).map(c => `${c.label}: ₹${c.cost_per_lead}`).join(' · ')}`;
      },
    },
    campaignlead: {
      endpoint: '/api/marketing/summary',
      format: (d) => {
        if (!d) return null;
        return `📣 *Campaign Leads*\nLeads this period: ${d.total_leads || 0} · Bookings: ${d.total_bookings || 0}\nSpend: ₹${Number(d.total_spend || 0).toLocaleString('en-IN')} · CPL: ₹${d.overall_cpl || 0}`;
      },
    },
    salesmanagerreport: {
      endpoint: '/api/kpi/team-summary',
      format: (d) => {
        if (!d || !d.team || !d.team.length) return null;
        const rows = d.team.slice(0, 5)
          .map(t => `${t.agent_name || t.agent_id}: ${t.actual_bookings || 0}/${t.target_bookings || 0} bookings (${t.bookings_pct || 0}%)`).join('\n');
        return `👤 *Sales Manager Report*\n${rows}`;
      },
    },
    salesperformance: {
      endpoint: '/api/kpi/team-summary',
      format: (d) => {
        if (!d || !d.totals) return null;
        const t = d.totals;
        return `📈 *Sales Performance*\nTeam bookings: ${t.actual_bookings}/${t.target_bookings} · Revenue: ₹${Number(t.actual_revenue || 0).toLocaleString('en-IN')}\nCalls: ${t.actual_calls}/${t.target_calls} · Visits: ${t.actual_visits}/${t.target_visits}`;
      },
    },
    review: {
      endpoint: '/api/kpi/team-summary',
      format: (d) => {
        if (!d || !d.team || !d.team.length) return null;
        const sorted = [...d.team].sort((a, b) => (b.revenue_pct || 0) - (a.revenue_pct || 0));
        const top = sorted[0], bottom = sorted[sorted.length - 1];
        return `📋 *Performance Review*\nTop: ${top.agent_name || top.agent_id} (${top.revenue_pct || 0}% of target)\nNeeds coaching: ${bottom.agent_name || bottom.agent_id} (${bottom.revenue_pct || 0}% of target)`;
      },
    },
    // projects.js returns total_units/sold_units/development_status/
    // handover_date per project already merged from project_library +
    // projects tables — no per-project N+1 calls needed for a workspace
    // summary.
    projectwise: {
      endpoint: '/api/projects',
      format: (d) => {
        const projects = d && d.data;
        if (!projects || !projects.length) return null;
        const rows = projects.slice(0, 5).map(p => `${p.name}: ${p.sold_units}/${p.total_units} sold`).join('\n');
        return `🏗️ *Project-wise Performance*\n${rows}`;
      },
    },
    projecthealth: {
      endpoint: '/api/projects',
      format: (d) => {
        const projects = d && d.data;
        if (!projects || !projects.length) return null;
        const today = new Date();
        const rows = projects.slice(0, 5).map(p => {
          const overdue = p.handover_date && new Date(p.handover_date) < today && p.construction_progress < 100;
          const flag = overdue ? '🔴' : p.construction_progress >= 75 ? '🟢' : '🟡';
          return `${p.name}: ${flag} ${p.development_status} (${p.construction_progress}%)`;
        }).join('\n');
        return `🏗️ *Project Health*\n${rows}`;
      },
    },
    // Portfolio snapshot from real unit counts (v_inventory_summary) — not
    // true profitability (no cost-basis data exists in this schema, see
    // 'profitability' staying mock below), just unsold-inventory unit
    // counts by category, which is what an investor view is asking for.
    investorview: {
      endpoint: '/api/inventory/summary',
      format: (d) => {
        if (!d || !d.totals) return null;
        const t = d.totals;
        return `🏦 *Investor Snapshot*\nTotal units: ${t.total_units || 0} · Sold: ${t.total_sold || 0} · Available: ${t.total_available || 0}\nInvestor-category units: ${t.investor_total || 0} (${t.investor_available || 0} available)`;
      },
    },
    // Cost-sheet is per-project; this shows the first project's sheet as a
    // representative sample rather than guessing at a workspace-wide
    // blend (pricing genuinely varies per project/configuration, blending
    // it would be misleading).
    pricing: {
      endpoint: '/api/projects',
      format: async (d) => {
        const projects = d && d.data;
        if (!projects || !projects.length) return null;
        const first = projects[0];
        try {
          const res = await window.SRAPI._get(`/api/projects/${first.id}/cost-sheet`);
          const sheet = (res && res.data) ? res.data.cost_sheet : res.cost_sheet;
          if (!sheet || !sheet.length) return null;
          const rows = sheet.slice(0, 4).map(r => `${r.configuration}: ₹${Number(r.base_rate).toLocaleString('en-IN')}/sqft`).join('\n');
          return `💲 *Current Pricing — ${first.name}*\n${rows}`;
        } catch { return null; }
      },
    },
    // Real margin: project_costs (manually entered per-sqft basis) vs
    // actual transacted revenue on sold units — see migration 149 +
    // GET /api/projects/profitability. Shows nothing (falls back to the
    // mock string) for any project with no cost data entered yet, rather
    // than a misleading 0%/blank margin.
    // Financing (loan/interest run-rate) and opportunity cost of capital on
    // unsold stock — migration 150 — are appended per project only when the
    // admin has actually entered a loan or a cost-of-capital rate; no
    // invented figures for projects that haven't set either.
    profitability: {
      endpoint: '/api/projects/profitability',
      format: (d) => {
        const projects = d && d.projects;
        if (!projects || !projects.length) return null;
        const withData = projects.filter(p => p.has_sales_data && p.margin_pct != null);
        if (!withData.length) return null;
        const rows = withData.slice(0, 5).map(p => {
          let row = `${p.project_name}: ${p.margin_pct}% margin (${p.units_sold} sold)`;
          if (p.monthly_interest_run_rate) row += `\n  💰 ₹${p.monthly_interest_run_rate.toLocaleString('en-IN')}/mo interest (₹${Number(p.loan_amount).toLocaleString('en-IN')} @ ${p.interest_rate_pct}%)`;
          if (p.monthly_opportunity_cost) row += `\n  ⏳ ₹${p.monthly_opportunity_cost.toLocaleString('en-IN')}/mo opportunity cost (${p.unsold_sqft.toLocaleString('en-IN')} sqft unsold @ ${p.cost_of_capital_pct}%)`;
          return row;
        }).join('\n');
        return `💹 *Profitability Report*\n${rows}`;
      },
    },
  };

  const fetchAndFormatOther = async (cmd) => {
    const conf = OTHER_LIVE_COMMANDS[cmd];
    if (!conf || !window.SRAPI || !window.SRAPI._token) return null;
    try {
      const res = await window.SRAPI._get(conf.endpoint);
      const data = res && res.data ? res.data : res;
      return conf.format(data);
    } catch { return null; }
  };

  const handleSlashCommand = async (cmd, cards = []) => {
    if (OTHER_LIVE_COMMANDS[cmd]) {
      const liveMsg = await fetchAndFormatOther(cmd);
      if (liveMsg) { botReply(liveMsg); return; }
    } else if (LIVE_COMMANDS.has(cmd) && window.SRAPI && window.SRAPI._token) {
      const signals = await fetchLiveSignals();
      if (signals) {
        const liveMsg = formatLiveReply(cmd, signals);
        if (liveMsg) { botReply(liveMsg); return; }
      }
    }
    const local = BOT_RESPONSES_LOCAL[cmd];
    if (local) { botReply(local()); return; }
    // Fallback: check flow card cmd if the top-level cmd had no match
    cards.forEach(c => {
      if (c.cardType === 'flow' && c.cmd && BOT_RESPONSES_LOCAL[c.cmd]) {
        botReply(BOT_RESPONSES_LOCAL[c.cmd]());
      }
    });
  };

  const send = (text, tags = [], cards = []) => {
    if (!text && tags.length === 0 && cards.length === 0) return;
    const msg = { id:`u${Date.now()}`, from:'You', avatar:'ME', me:true, msg:text, tags, cards, time:'Now' };
    window.addThreadMessage('realty', activeThread, msg);
    setTimeout(() => scrollRef.current && (scrollRef.current.scrollTop = scrollRef.current.scrollHeight), 60);
    const cmdMatch = (text || '').trim().match(/^\/(\w+)$/);
    if (cmdMatch) {
      handleSlashCommand(cmdMatch[1], []);
    } else if (cards.length > 0) {
      cards.forEach(c => {
        if (c.cardType === 'flow' && c.cmd) handleSlashCommand(c.cmd, [c]);
      });
    }
  };
  const sendAssistant = async (text) => {
    if (!text?.trim() || !window.SRAPI || !window.SRAPI._token) return;
    const userMsg = { id:`u${Date.now()}`, sender_id:'me', sender_name:'You', body:text, me:true, created_at:new Date().toISOString() };
    setAssistantMsgs(prev => [...prev, userMsg]);
    setTimeout(() => scrollRef.current && (scrollRef.current.scrollTop = scrollRef.current.scrollHeight), 60);
    try {
      const res = await window.SRAPI._post('/api/mobile/assistant/message', { message: text, threadId: assistantThread?.id });
      const result = res?.data || res;
      const replyMsg = {
        id: result.replyMessageId || `a${Date.now()}`,
        sender_id: 'assistant', sender_name: 'Assistant',
        body: result.type === 'proposal' ? result.label : (result.text || result.question || ''),
        proposal: result.type === 'proposal' ? result : null,
        me: false, created_at: new Date().toISOString(),
      };
      setAssistantMsgs(prev => [...prev, replyMsg]);
      if (result.upgradePrompt) {
        setAssistantMsgs(prev => [...prev, {
          id:`up${Date.now()}`, sender_id:'assistant', sender_name:'Assistant',
          body: result.upgradePrompt, me:false, created_at:new Date().toISOString(),
        }]);
      }
    } catch {
      setAssistantMsgs(prev => [...prev, {
        id:`err${Date.now()}`, sender_id:'assistant', sender_name:'Assistant',
        body:'Could not reach the assistant. Please try again.', me:false, created_at:new Date().toISOString(),
      }]);
    }
    setTimeout(() => scrollRef.current && (scrollRef.current.scrollTop = scrollRef.current.scrollHeight), 100);
  };

  const confirmProposal = async (pendingId, msgId) => {
    if (!window.SRAPI || !window.SRAPI._token) return;
    setAssistantMsgs(prev => prev.map(m => m.id === msgId ? { ...m, confirming:true } : m));
    try {
      const res = await window.SRAPI._post(`/api/mobile/assistant/confirm/${pendingId}`, {});
      const result = res?.data || res;
      setAssistantMsgs(prev => prev.map(m => m.id === msgId
        ? { ...m, confirming:false, proposal:{ ...m.proposal, status:'confirmed' } } : m));
      const doneText = result.result?.title
        ? `Created: "${result.result.title}"`
        : (result.result?.text || 'Done.');
      setAssistantMsgs(prev => [...prev, {
        id:`ok${Date.now()}`, sender_id:'assistant', sender_name:'Assistant',
        body:doneText, me:false, created_at:new Date().toISOString(),
      }]);
    } catch {
      setAssistantMsgs(prev => prev.map(m => m.id === msgId ? { ...m, confirming:false } : m));
    }
    setTimeout(() => scrollRef.current && (scrollRef.current.scrollTop = scrollRef.current.scrollHeight), 100);
  };

  const cancelProposal = async (pendingId, msgId) => {
    if (!window.SRAPI || !window.SRAPI._token) return;
    try { await window.SRAPI._post(`/api/mobile/assistant/cancel/${pendingId}`, {}); } catch {}
    setAssistantMsgs(prev => prev.map(m => m.id === msgId
      ? { ...m, proposal:{ ...m.proposal, status:'cancelled' } } : m));
  };

  const TEAM_NAMES = ['Ankit Sharma','Priya Menon','Rahul Singh','Kavya Nair','Deepak Iyer'];
  // For DM threads, filter tasks to only show tasks assigned to that person (unless manager)
  const isManager = role === 'Manager' || role === 'Business Owner';
  const dmPerson = activeThread !== 'team' ? (window.getChatThreads('realty')[activeThread]||{}).name : null;
  const filteredDs = React.useMemo(() => {
    if (!dmPerson) return ds;
    return { ...ds, task: isManager ? ds.task : ds.task.filter(t => t.assignee === dmPerson || !t.assignee) };
  }, [activeThread, dmPerson, isManager]);
  return (
    <div className="pf-screen pf-anim-in" style={{ display:'flex', flexDirection:'column', position:'relative' }}>
      <PFHeader title="Chat" subtitle="SkaleRealty Team"
        left={fromLead
          ? <PFBackBtn onClick={() => go('lead', fromLead.id)} />
          : <div className="pf-avatar" style={{ width:36, height:36, fontSize:12, background:'var(--pf-amber-tint)', color:'var(--pf-amber)' }}>AS</div>}
        right={<window.NotifBell ns="realty" user={role} accent="var(--pf-green)" onClick={() => setNotifOpen(true)} />} />
      {/* Thread switcher */}
      {window.ChatThreadSwitcher && <window.ChatThreadSwitcher ns="realty" accent="var(--pf-green)" activeThread={activeThread} onSwitch={setActiveThread} role={role} teamNames={TEAM_NAMES} />}
      {/* AI Assistant tab */}
      <div style={{ display:'flex', padding:'0 14px', borderBottom:'1px solid var(--pf-line)', background:'var(--pf-bg)' }}>
        <button onClick={() => setActiveThread(activeThread === 'assistant' ? 'team' : 'assistant')}
          style={{ padding:'6px 12px', fontSize:12, fontWeight:600, border:'none', cursor:'pointer', borderRadius:'6px 6px 0 0',
            background: activeThread === 'assistant' ? 'var(--pf-green)' : 'transparent',
            color: activeThread === 'assistant' ? '#fff' : 'var(--pf-ink-3)' }}>
          AI Assistant
        </button>
      </div>
      {fromLead && (
        <div style={{ padding:'5px 14px', background:'var(--pf-blue-tint)', borderBottom:'1px solid var(--pf-line)', fontSize:11.5, color:'var(--pf-blue)', fontWeight:600 }}>
          Re: {fromLead.name} · {fromLead.project}
        </div>
      )}
      {activeThread === 'assistant' ? (
        <div style={{ padding:'5px 14px', background:'var(--pf-green-tint)', borderBottom:'1px solid var(--pf-green-tint-2)', fontSize:11.5, color:'var(--pf-green)', fontWeight:600 }}>
          Ask me to create a task, set a reminder, or log an update
        </div>
      ) : (
        <div style={{ padding:'5px 14px', background:'var(--pf-green-tint)', borderBottom:'1px solid var(--pf-green-tint-2)', fontSize:11.5, color:'var(--pf-green)', fontWeight:600 }}>
          Type <strong>/</strong> to assign tasks, nudge, update status, add notes
        </div>
      )}
      {activeThread === 'assistant' ? (
        <div ref={scrollRef} className="pf-scroll" style={{ padding:'8px 14px 14px', display:'flex', flexDirection:'column', gap:6 }}>
          {assistantLoading && <div style={{ padding:'24px 0', textAlign:'center', color:'#9ca3af', fontSize:13 }}>Loading…</div>}
          {!assistantLoading && assistantMsgs.length === 0 && !window.SRAPI?._token && (
            <div style={{ padding:'32px 0', textAlign:'center', color:'#9ca3af', fontSize:13 }}>Sign in to use the AI Assistant</div>
          )}
          {!assistantLoading && assistantMsgs.length === 0 && window.SRAPI?._token && (
            <div style={{ padding:'32px 0', textAlign:'center', color:'#9ca3af', fontSize:13 }}>Ask me to create a task, set a reminder, or log an update</div>
          )}
          {assistantMsgs.map(m => (
            <div key={m.id} style={{ alignSelf: m.me ? 'flex-end' : 'flex-start', maxWidth:'85%' }}>
              {!m.me && <div style={{ fontSize:10.5, color:'var(--pf-ink-3)', marginBottom:2, fontWeight:600 }}>Assistant</div>}
              {m.proposal ? (
                <div style={{ background:'var(--pf-surface)', border:'1.5px solid var(--pf-green)', borderRadius:12, padding:'10px 12px', fontSize:13 }}>
                  <div style={{ fontWeight:600, color:'var(--pf-ink)', marginBottom:6 }}>{m.proposal.label || m.body}</div>
                  {m.proposal.usedFallback && m.proposal.fallbackNote && (
                    <div style={{ fontSize:11, color:'var(--pf-ink-3)', marginBottom:6 }}>{m.proposal.fallbackNote}</div>
                  )}
                  {m.proposal.status === 'confirmed' && (
                    <div style={{ fontSize:11.5, color:'var(--pf-green)', fontWeight:600 }}>Confirmed</div>
                  )}
                  {m.proposal.status === 'cancelled' && (
                    <div style={{ fontSize:11.5, color:'var(--pf-ink-3)' }}>Cancelled</div>
                  )}
                  {!m.proposal.status && (
                    <div style={{ display:'flex', gap:8, marginTop:4 }}>
                      <button disabled={m.confirming} onClick={() => confirmProposal(m.proposal.pendingId, m.id)}
                        style={{ flex:1, padding:'7px 0', background:'var(--pf-green)', color:'#fff', border:'none', borderRadius:8, fontWeight:600, fontSize:13, cursor:'pointer', opacity: m.confirming ? 0.6 : 1 }}>
                        {m.confirming ? 'Confirming…' : 'Confirm'}
                      </button>
                      <button onClick={() => cancelProposal(m.proposal.pendingId, m.id)}
                        style={{ flex:1, padding:'7px 0', background:'transparent', color:'var(--pf-ink-3)', border:'1px solid var(--pf-line)', borderRadius:8, fontWeight:600, fontSize:13, cursor:'pointer' }}>
                        Cancel
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <div style={{ padding:'8px 12px', borderRadius: m.me ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
                  background: m.me ? 'var(--pf-green)' : 'var(--pf-surface)',
                  color: m.me ? '#fff' : 'var(--pf-ink)',
                  border: '1px solid ' + (m.me ? 'transparent' : 'var(--pf-line)'),
                  fontSize:13.5, lineHeight:1.4 }}>
                  {m.body}
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div ref={scrollRef} className="pf-scroll" style={{ padding:'8px 14px 14px', display:'flex', flexDirection:'column', gap:4 }}>
          <Day label="Today" />
          {currentMsgs.map(m => window.ChatBubble
            ? <window.ChatBubble key={m.id} msg={m} accent="var(--pf-green)" />
            : <div key={m.id} style={{ padding:'8px 12px', borderRadius:12, background: m.me ? 'var(--pf-green)' : 'var(--pf-surface)', color: m.me ? '#fff' : 'var(--pf-ink)', fontSize:13.5, alignSelf: m.me ? 'flex-end' : 'flex-start', maxWidth:'80%', marginBottom:4 }}>{m.msg}</div>
          )}
          {currentMsgs.length === 0 && (
            <div style={{ padding:'32px 0', textAlign:'center', color:'#9ca3af', fontSize:13 }}>Start your conversation</div>
          )}
        </div>
      )}
      <window.ChatComposer accent="var(--pf-green)"
        placeholder={activeThread === 'assistant' ? 'Ask the assistant…' : 'Message… (type / to tag)'}
        onSend={activeThread === 'assistant' ? (text) => sendAssistant(text) : send}
        dataSources={activeThread === 'assistant' ? undefined : filteredDs}
        commands={activeThread === 'assistant' ? undefined : (window.REALTY_CMDS && window.REALTY_CMDS[resolveRealtyRoleKey(role)] ? window.REALTY_CMDS[resolveRealtyRoleKey(role)] : undefined)}
        role={role} leadId={fromLead ? fromLead.id : null} />
      {notifOpen && <window.NotifPanel ns="realty" user={role} accent="var(--pf-green)" onClose={() => setNotifOpen(false)} />}
    </div>
  );
};

const Msg = ({ me, text, time, emphasis }) => (
  <div style={{
    alignSelf: me ? 'flex-end' : 'flex-start',
    maxWidth: '82%',
    background: me ? 'var(--pf-green-tint)' : 'var(--pf-surface)',
    color: 'var(--pf-ink)',
    border: '1px solid ' + (me ? 'var(--pf-green-tint-2)' : 'var(--pf-line)'),
    borderRadius: me ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
    padding: '8px 12px',
    fontSize: 13.5, lineHeight: 1.4,
    fontWeight: emphasis ? 600 : 400,
  }}>
    {text}
    <div style={{ fontSize: 10, color: 'var(--pf-ink-4)', textAlign: 'right', marginTop: 2 }}>{time}</div>
  </div>
);

const Day = ({ label }) => (
  <div style={{ alignSelf: 'center', fontSize: 10.5, letterSpacing: '0.12em', color: 'var(--pf-ink-3)', fontWeight: 600, margin: '6px 0' }}>
    {label.toUpperCase()}
  </div>
);

// ─────────────────────────────────────────────────────────────────────────────
// SCREEN: Creative Assets (read-only viewer for field agents)
// Shows all 'delivered' creative files from the backend creative_requests module
// Field agents can preview images, open PDFs, and share files with clients
// ─────────────────────────────────────────────────────────────────────────────
const PFScreenAssets = ({ go }) => {
  const [assets, setAssets]     = React.useState([]);
  const [loading, setLoading]   = React.useState(true);
  // Distinguishes "never had a token, this is legitimately prototype/demo
  // mode" from "had a token but the request actually failed" (expired auth,
  // network error, etc.) — both used to fall back to the exact same
  // DEMO_ASSETS silently, so an expired token looked identical to a normal
  // demo session instead of surfacing as an error anywhere.
  const [loadError, setLoadError] = React.useState(null);
  const [filter, setFilter]     = React.useState('all');
  const [preview, setPreview]   = React.useState(null);
  const [search, setSearch]     = React.useState('');
  const [showReq, setShowReq]   = React.useState(false);
  const [reqSent, setReqSent]   = React.useState(false);
  const [shareSheet, setShare]  = React.useState(null); // asset to share
  const [reqForm, setReqForm]   = React.useState({ type:'brochure', project:'', notes:'' });
  const [, force]               = React.useReducer(x => x + 1, 0);

  const TYPES = [
    { id:'all', label:'All' }, { id:'logo', label:'Logos' }, { id:'brochure', label:'Brochures' },
    { id:'banner', label:'Banners' }, { id:'social', label:'Social' }, { id:'video', label:'Videos' },
  ];
  const TYPE_COLORS = {
    logo:'#7c3aed', brochure:'#1d4ed8', banner:'#059669', social:'#db2777', video:'#dc2626', email:'#d97706', other:'#64748b',
  };
  const DEMO_ASSETS = [
    { id:'a1', filename:'Rivercrest_Logo_Final.png', url:'', mime_type:'image/png', request_type:'logo', project:'Rivercrest', uploaded_at:'Apr 28', file_size:245000,
      demoGradient:'linear-gradient(135deg,#7c3aed22,#7c3aed44)', demoLabel:'Logo · Primary Mark' },
    { id:'a2', filename:'Rivercrest_Brochure_v3.pdf', url:'', mime_type:'application/pdf', request_type:'brochure', project:'Rivercrest', uploaded_at:'Apr 26', file_size:4200000,
      demoGradient:'linear-gradient(135deg,#1d4ed822,#1d4ed844)', demoLabel:'12-page brochure · A4' },
    { id:'a3', filename:'WhisperingOaks_Banner_1080.jpg', url:'', mime_type:'image/jpeg', request_type:'banner', project:'Whispering Oaks', uploaded_at:'Apr 25', file_size:890000,
      demoGradient:'linear-gradient(135deg,#05966922,#05966944)', demoLabel:'Digital Banner · 1920×1080' },
    { id:'a4', filename:'WO_Instagram_Carousel.zip', url:'', mime_type:'application/zip', request_type:'social', project:'Whispering Oaks', uploaded_at:'Apr 22', file_size:7800000,
      demoGradient:'linear-gradient(135deg,#db277722,#db277744)', demoLabel:'Instagram Carousel · 5 slides' },
    { id:'a5', filename:'Rivercrest_Site_Visit_Tour.mp4', url:'', mime_type:'video/mp4', request_type:'video', project:'Rivercrest', uploaded_at:'Apr 20', file_size:98000000,
      demoGradient:'linear-gradient(135deg,#dc262622,#dc262644)', demoLabel:'Video Tour · 3 min 40 sec' },
  ];

  const loadAssets = React.useCallback(() => {
    async function load() {
      setLoading(true);
      setLoadError(null);
      try {
        if (window.SRAPI && window.SRAPI._token) {
          // GET /api/creative/requests returns a bare array of request rows
          // with only aggregate counts (file_count, deliverable_count) —
          // never embedded file objects. This screen used to read
          // resp.requests (undefined on a bare array) and r.files
          // (never present on the list response), so it silently showed
          // zero assets regardless of how much real delivered work existed.
          // Fetch full detail (which does include `files`) for just the
          // requests that actually have a deliverable, per deliverable_count.
          const rows = await window.SRAPI._get('/api/creative/requests?status=delivered');
          const withDeliverables = (Array.isArray(rows) ? rows : []).filter(r => Number(r.deliverable_count) > 0);
          const details = await Promise.all(
            withDeliverables.map(r => window.SRAPI._get('/api/creative/requests/' + r.id).catch(() => null))
          );
          const data = details.filter(Boolean).flatMap(r =>
            (r.files || []).filter(f => f.file_role === 'deliverable' || f.file_role === 'revision_deliverable')
              .map(f => ({ ...f, request_title: r.title, request_type: r.type, project: r.project_name }))
          );
          setAssets(data);
        } else {
          // No token at all — genuinely a prototype/demo session, not a
          // failure, so the demo showcase is the honest thing to show.
          setAssets(DEMO_ASSETS);
        }
      } catch (e) {
        // Had a token; the request itself failed (expired session, network,
        // server error). Surface it instead of quietly showing fake files
        // that look identical to a real, working demo.
        console.warn('[Assets]', e.message);
        setAssets([]);
        setLoadError(e.message || 'Could not load assets');
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);
  React.useEffect(() => { loadAssets(); }, [loadAssets]);

  const fmt = b => !b ? '' : b >= 1e6 ? (b/1e6).toFixed(1)+' MB' : b >= 1e3 ? (b/1e3).toFixed(0)+' KB' : b+' B';
  const mimeIcon = m => !m ? 'doc' : m.startsWith('image/') ? 'gallery' : m === 'application/pdf' ? 'pdf' : m.startsWith('video/') ? 'video' : m.includes('zip') ? 'box' : 'doc';
  // Always route through the share sheet. The OS share dialog has no lead or
  // channel context, so taking it skipped shareAsset() entirely — on a real
  // iPhone navigator.share always exists, so every iOS share went untracked:
  // no timeline entry, no auto-created follow-up task. Android never had the
  // shortcut and tracked correctly.
  const share = a => setShare(a);

  const filtered = assets.filter(a => {
    if (filter !== 'all' && a.request_type !== filter) return false;
    if (search && !a.filename.toLowerCase().includes(search.toLowerCase()) &&
        !(a.project||'').toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  // Demo preview panel — renders a stylised mock instead of actual file
  const DemoPreview = ({ asset, onClose }) => {
    const c = TYPE_COLORS[asset.request_type] || '#64748b';
    return (
      <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,.92)', zIndex:200, display:'flex', flexDirection:'column' }}>
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'12px 16px', background:'rgba(0,0,0,.6)' }}>
          <div style={{ color:'#fff', fontSize:13.5, fontWeight:600, flex:1, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{asset.filename}</div>
          <button onClick={onClose} style={{ color:'#fff', background:'transparent', border:'none', padding:6, cursor:'pointer', fontSize:22 }}>✕</button>
        </div>
        <div style={{ flex:1, display:'flex', alignItems:'center', justifyContent:'center', padding:20 }}>
          <div style={{ width:'100%', maxWidth:360, borderRadius:16, background:asset.demoGradient||'rgba(255,255,255,.05)', border:`2px solid ${c}44`, padding:28, textAlign:'center' }}>
            <div style={{ fontSize:56, marginBottom:12 }}>
              {asset.request_type === 'logo' ? '🎨' : asset.request_type === 'video' ? '🎬' : asset.request_type === 'social' ? '📱' : '📄'}
            </div>
            <div style={{ color:'#fff', fontSize:16, fontWeight:700, marginBottom:6 }}>{asset.project}</div>
            <div style={{ color:'rgba(255,255,255,.7)', fontSize:13, marginBottom:16 }}>{asset.demoLabel}</div>
            <div style={{ background:`${c}22`, border:`1px solid ${c}44`, borderRadius:10, padding:'10px 14px', color:'rgba(255,255,255,.55)', fontSize:12 }}>
              This is a demo preview. In live mode, the actual file renders here.
            </div>
          </div>
        </div>
        <div style={{ padding:'10px 16px 20px', display:'flex', gap:10, background:'rgba(0,0,0,.6)' }}>
          <button onClick={() => { setShare(asset); onClose(); }} style={{
            flex:1, padding:'10px 0', borderRadius:10, background:'rgba(255,255,255,.12)',
            border:'1px solid rgba(255,255,255,.2)', color:'#fff',
            fontFamily:'var(--pf-sans)', fontSize:13, fontWeight:600, cursor:'pointer',
            display:'flex', alignItems:'center', justifyContent:'center', gap:6,
          }}>
            <PFIcon name="share" size={15} color="#fff" /> Share with Client
          </button>
          <button onClick={onClose} style={{
            width:44, padding:'10px 0', borderRadius:10, background:'rgba(255,255,255,.08)',
            border:'1px solid rgba(255,255,255,.15)', color:'rgba(255,255,255,.7)',
            fontFamily:'var(--pf-sans)', fontSize:12, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center',
          }}>
            <PFIcon name="close" size={16} color="rgba(255,255,255,.7)" />
          </button>
        </div>
      </div>
    );
  };

  // Share sheet
  const ShareSheet = ({ asset, onClose, preSelectedLead }) => {
    const [step, setStep]         = React.useState('lead');   // lead | channel | template | done
    const [selLead, setSelLead]   = React.useState(preSelectedLead || null);
    const [channel, setChannel]   = React.useState('whatsapp');
    const [templates, setTpls]    = React.useState([]);
    const [selTpl, setSelTpl]     = React.useState(null);
    const [msgBody, setMsgBody]   = React.useState('');
    const [copied, setCopied]     = React.useState(false);
    const [sending, setSending]   = React.useState(false);
    const [suggestion, setSug]    = React.useState(null);

    const link = asset.url || `https://assets.skalerealty.com/demo/${asset.id}`;

    React.useEffect(() => {
      // Load templates + suggestion
      if (window.SRAPI) {
        window.SRAPI.getShareTemplates().then(ts => {
          const filtered = ts.filter(t => t.asset_type === 'any' || asset.filename?.toLowerCase().includes(t.asset_type));
          setTpls(filtered);
          if (filtered.length > 0) { setSelTpl(filtered[0]); buildMsg(filtered[0], selLead); }
        }).catch(() => {});
      }
    }, []);

    React.useEffect(() => {
      if (selLead && window.SRAPI) {
        window.SRAPI.getNextSuggested(selLead.id).then(r => setSug(r.suggestion)).catch(() => {});
      }
    }, [selLead]);

    const fillVars = (tpl, lead) => {
      if (!tpl) return '';
      const agentName  = window.SRAPI?._agentName  || 'Your Agent';
      const agentPhone = window.SRAPI?._agentPhone || '';
      const project    = lead?.project || asset.project || 'our project';
      return tpl.body
        .replace(/{{client_name}}/g, lead?.name || 'there')
        .replace(/{{agent_name}}/g, agentName)
        .replace(/{{agent_phone}}/g, agentPhone)
        .replace(/{{asset_name}}/g, asset.filename || 'the asset')
        .replace(/{{project}}/g, project)
        .replace(/{{link}}/g, link)
        .replace(/{{project_address}}/g, lead?.address || project);
    };

    const buildMsg = (tpl, lead) => {
      setMsgBody(fillVars(tpl, lead || selLead));
    };

    const handleTplSelect = (tpl) => {
      setSelTpl(tpl);
      buildMsg(tpl, selLead);
    };

    const handleLeadSelect = (lead) => {
      setSelLead(lead);
      if (selTpl) buildMsg(selTpl, lead);
      setStep('channel');
    };

    const handleSend = async () => {
      setSending(true);
      const finalMsg = msgBody || `Hi, please find ${asset.filename}: ${link}`;
      try {
        if (channel === 'whatsapp') {
          const phone = selLead?.phone?.replace(/[^0-9]/g,'') || '';
          if (phone) window.open(`https://wa.me/${phone}?text=${encodeURIComponent(finalMsg)}`, '_blank');
          else window.open(`https://wa.me/?text=${encodeURIComponent(finalMsg)}`, '_blank');
        } else if (channel === 'email') {
          const subj = selTpl?.subject || `SkaleRealty: ${asset.filename}`;
          window.location.href = `mailto:${selLead?.email || ''}?subject=${encodeURIComponent(subj)}&body=${encodeURIComponent(finalMsg)}`;
        } else if (channel === 'link') {
          try { navigator.clipboard.writeText(link); } catch(e) {}
          setCopied(true);
        }
        // Log share event
        if (window.SRAPI && selLead) {
          await window.SRAPI.shareAsset(selLead.id, asset.id, asset.filename, channel, selTpl?.id || null, finalMsg).catch(() => {});
        }
        setStep('done');
      } finally { setSending(false); }
    };

    const LEADS = PF_DATA.leads.slice(0, 6);

    return (
      <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,.5)', zIndex:300, display:'flex', alignItems:'flex-end' }}>
        <div style={{ background:'#fff', width:'100%', borderRadius:'18px 18px 0 0', padding:'20px 18px 36px', maxHeight:'88vh', overflow:'auto' }}>
          <div style={{ width:40, height:4, background:'#e5e7eb', borderRadius:2, margin:'0 auto 14px' }} />

          {step === 'done' ? (
            <div style={{ textAlign:'center', padding:'24px 0' }}>
              <div style={{ fontSize:44 }}>✅</div>
              <div style={{ fontWeight:800, fontSize:16, color:'#111827', marginTop:10 }}>Shared &amp; Logged</div>
              <div style={{ fontSize:13, color:'#6b7280', marginTop:6 }}>
                {asset.filename} → {selLead?.name} via {channel}
              </div>
              <div style={{ fontSize:12, color:'var(--pf-green)', marginTop:8, fontWeight:600 }}>
                ✓ Added to lead timeline · follow-up task auto-created
              </div>
              {suggestion && (
                <div style={{ marginTop:16, padding:'12px 14px', borderRadius:12, background:'#f0fdf4', border:'1px solid #bbf7d0', textAlign:'left' }}>
                  <div style={{ fontSize:11, fontWeight:700, color:'#15803d', textTransform:'uppercase', letterSpacing:'.05em', marginBottom:4 }}>💡 Send next</div>
                  <div style={{ fontSize:13, color:'#166534' }}>{suggestion.reason}</div>
                </div>
              )}
              <button onClick={onClose} style={{ marginTop:20, width:'100%', padding:'12px', borderRadius:12, background:'var(--pf-green)', border:'none', color:'#fff', fontWeight:700, fontSize:14, cursor:'pointer' }}>Done</button>
            </div>

          ) : step === 'lead' ? (
            <>
              <div style={{ fontWeight:800, fontSize:16, color:'#111827', marginBottom:4 }}>Share: {asset.filename}</div>
              <div style={{ fontSize:12.5, color:'#6b7280', marginBottom:16 }}>Select a lead to send this to</div>
              {suggestion && (
                <div style={{ padding:'10px 12px', borderRadius:10, background:'#fffbeb', border:'1px solid #fde68a', fontSize:12.5, color:'#92400e', marginBottom:14, display:'flex', gap:8, alignItems:'flex-start' }}>
                  <span>💡</span><span><strong>Suggested:</strong> {suggestion.reason}</span>
                </div>
              )}
              <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                {LEADS.map(l => (
                  <button key={l.id} onClick={() => handleLeadSelect(l)} style={{
                    display:'flex', alignItems:'center', gap:12, padding:'10px 14px', borderRadius:12,
                    background:'#f9fafb', border:'1px solid #e5e7eb', cursor:'pointer', textAlign:'left',
                  }}>
                    <div style={{ width:36, height:36, borderRadius:99, background:'var(--pf-green)', display:'flex', alignItems:'center', justifyContent:'center', color:'#fff', fontSize:13, fontWeight:700, flexShrink:0 }}>
                      {l.name.charAt(0)}
                    </div>
                    <div style={{ flex:1 }}>
                      <div style={{ fontWeight:600, fontSize:13.5, color:'#111827' }}>{l.name}</div>
                      <div style={{ fontSize:11.5, color:'#9ca3af' }}>{l.stage} · {l.lastTouch}</div>
                    </div>
                    <PFIcon name="arrowR" size={14} color="#d1d5db" />
                  </button>
                ))}
              </div>
              <button onClick={onClose} style={{ width:'100%', padding:'12px', borderRadius:12, background:'#f3f4f6', border:'none', color:'#374151', fontWeight:700, fontSize:14, cursor:'pointer', marginTop:12 }}>Cancel</button>
            </>

          ) : (
            <>
              {/* Lead header */}
              <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:16, padding:'10px 12px', borderRadius:12, background:'#f9fafb', border:'1px solid #e5e7eb' }}>
                <div style={{ width:34, height:34, borderRadius:99, background:'var(--pf-green)', display:'flex', alignItems:'center', justifyContent:'center', color:'#fff', fontSize:13, fontWeight:700 }}>
                  {selLead?.name?.charAt(0)}
                </div>
                <div style={{ flex:1 }}>
                  <div style={{ fontWeight:700, fontSize:13.5 }}>{selLead?.name}</div>
                  <div style={{ fontSize:11.5, color:'#6b7280' }}>{selLead?.phone}</div>
                </div>
                <button onClick={() => setStep('lead')} style={{ fontSize:11, color:'var(--pf-green)', fontWeight:600, background:'none', border:'none', cursor:'pointer' }}>Change</button>
              </div>

              {/* Channel selector */}
              <div style={{ fontWeight:700, fontSize:12, color:'#374151', textTransform:'uppercase', letterSpacing:'.06em', marginBottom:8 }}>Send via</div>
              <div style={{ display:'flex', gap:8, marginBottom:16 }}>
                {[
                  { id:'whatsapp', emoji:'💬', label:'WhatsApp', bg:'#f0fdf4', border:'#bbf7d0', color:'#15803d' },
                  { id:'email',    emoji:'✉️', label:'Email',    bg:'#eff6ff', border:'#bfdbfe', color:'#1d4ed8' },
                  { id:'link',     emoji:'🔗', label:'Copy Link',bg:'#f9fafb', border:'#e5e7eb', color:'#374151' },
                ].map(c => (
                  <button key={c.id} onClick={() => setChannel(c.id)} style={{
                    flex:1, display:'flex', flexDirection:'column', alignItems:'center', gap:5,
                    padding:'10px 6px', borderRadius:12,
                    background: channel===c.id ? c.bg : '#fafafa',
                    border: `2px solid ${channel===c.id ? c.border : '#e5e7eb'}`,
                    cursor:'pointer', transition:'all .15s',
                  }}>
                    <span style={{ fontSize:22 }}>{c.emoji}</span>
                    <span style={{ fontSize:11, fontWeight:700, color: channel===c.id ? c.color : '#6b7280' }}>{c.label}</span>
                  </button>
                ))}
              </div>

              {/* Template selector */}
              {templates.length > 0 && (
                <>
                  <div style={{ fontWeight:700, fontSize:12, color:'#374151', textTransform:'uppercase', letterSpacing:'.06em', marginBottom:8 }}>Template</div>
                  <div style={{ display:'flex', gap:6, overflowX:'auto', paddingBottom:6, marginBottom:10 }}>
                    {templates.filter(t => t.channel === channel || channel === 'link').map(t => (
                      <button key={t.id} onClick={() => handleTplSelect(t)} style={{
                        padding:'6px 12px', borderRadius:20, whiteSpace:'nowrap', flexShrink:0,
                        background: selTpl?.id===t.id ? 'var(--pf-green)' : '#f3f4f6',
                        color: selTpl?.id===t.id ? '#fff' : '#374151',
                        border: 'none', fontSize:12, fontWeight:600, cursor:'pointer',
                      }}>{t.name}</button>
                    ))}
                    <button onClick={() => { setSelTpl(null); setMsgBody(''); }} style={{
                      padding:'6px 12px', borderRadius:20, whiteSpace:'nowrap', flexShrink:0,
                      background: !selTpl ? 'var(--pf-green)' : '#f3f4f6',
                      color: !selTpl ? '#fff' : '#374151',
                      border:'none', fontSize:12, fontWeight:600, cursor:'pointer',
                    }}>Custom</button>
                  </div>
                </>
              )}

              {/* Message preview / editor */}
              {(channel === 'whatsapp' || channel === 'email') && (
                <>
                  <div style={{ fontWeight:700, fontSize:12, color:'#374151', textTransform:'uppercase', letterSpacing:'.06em', marginBottom:6 }}>Message</div>
                  <textarea
                    value={msgBody}
                    onChange={e => setMsgBody(e.target.value)}
                    placeholder={`Hi ${selLead?.name || ''}, please find the asset here: ${link}`}
                    rows={5}
                    style={{ width:'100%', boxSizing:'border-box', border:'1.5px solid #e5e7eb', borderRadius:10, padding:'10px 12px', fontSize:13, color:'#111827', resize:'vertical', outline:'none', fontFamily:'var(--pf-sans)', marginBottom:12 }}
                  />
                </>
              )}

              <button onClick={handleSend} disabled={sending} style={{
                width:'100%', padding:'13px', borderRadius:12, border:'none',
                background: sending ? '#9aa29d' : 'var(--pf-green)',
                color:'#fff', fontWeight:700, fontSize:14, cursor: sending ? 'wait' : 'pointer',
                display:'flex', alignItems:'center', justifyContent:'center', gap:8, marginBottom:8,
              }}>
                {sending
                  ? 'Sending…'
                  : channel === 'link' ? '🔗 Copy link & log' : `${channel === 'whatsapp' ? '💬' : '✉️'} Send & log`
                }
              </button>
              <button onClick={onClose} style={{ width:'100%', padding:'12px', borderRadius:12, background:'#f3f4f6', border:'none', color:'#374151', fontWeight:700, fontSize:14, cursor:'pointer' }}>Cancel</button>
            </>
          )}
        </div>
      </div>
    );
  };

  // Request asset sheet
  const ReqSheet = ({ onClose }) => {
    const [f, setF] = React.useState({ type:'brochure', project:'Rivercrest Heights', notes:'' });
    const [submitting, setSubmitting] = React.useState(false);
    const [submitErr, setSubmitErr] = React.useState('');
    const submit = async () => {
      setSubmitting(true);
      setSubmitErr('');
      try {
        if (window.SRAPI && typeof window.SRAPI._post === 'function') {
          await window.SRAPI._post('/api/creative/requests', {
            title: `${f.type.charAt(0).toUpperCase() + f.type.slice(1)} — ${f.project}`,
            type: f.type,
            project_name: f.project,
            description: f.notes || null,
          });
        }
        setReqSent(true);
        setTimeout(() => { setReqSent(false); onClose(); }, 2200);
      } catch (e) {
        setSubmitErr(e?.message || 'Could not send request — please try again.');
      } finally {
        setSubmitting(false);
      }
    };
    return (
      <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,.5)', zIndex:300, display:'flex', alignItems:'flex-end' }}>
        <div style={{ background:'#fff', width:'100%', borderRadius:'18px 18px 0 0', padding:'20px 18px 36px', maxHeight:'85vh', overflow:'auto' }}>
          <div style={{ textAlign:'center', marginBottom:16 }}>
            <div style={{ width:40, height:4, background:'#e5e7eb', borderRadius:2, margin:'0 auto 14px' }} />
            <div style={{ fontWeight:800, fontSize:16, color:'#111827' }}>Request Creative Asset</div>
          </div>
          {reqSent ? (
            <div style={{ textAlign:'center', padding:'30px 0' }}>
              <div style={{ fontSize:46 }}>✅</div>
              <div style={{ fontWeight:700, fontSize:15, color:'#111827', marginTop:10 }}>Request Submitted!</div>
              <div style={{ color:'#6b7280', fontSize:13, marginTop:6 }}>The creative team will update you within 24 hrs.</div>
            </div>
          ) : (
            <>
              <label style={{ fontSize:12.5, fontWeight:700, color:'#374151', display:'block', marginBottom:6 }}>Asset Type</label>
              <div style={{ display:'flex', flexWrap:'wrap', gap:8, marginBottom:14 }}>
                {['logo','brochure','banner','social','video','email'].map(t => (
                  <button key={t} onClick={() => setF(p=>({...p,type:t}))} style={{ padding:'6px 14px', borderRadius:20, border:`1.5px solid ${f.type===t?'var(--pf-green)':'#e5e7eb'}`, background: f.type===t?'var(--pf-green-tint)':'#fff', color: f.type===t?'var(--pf-green)':'#374151', fontSize:12.5, fontWeight:600, cursor:'pointer', textTransform:'capitalize' }}>{t}</button>
                ))}
              </div>
              <label style={{ fontSize:12.5, fontWeight:700, color:'#374151', display:'block', marginBottom:6 }}>Project</label>
              <input value={f.project} onChange={e => setF(p=>({...p,project:e.target.value}))} placeholder="Project name"
                style={{ width:'100%', boxSizing:'border-box', border:'1px solid #e5e7eb', borderRadius:10, padding:'10px 12px', fontSize:13.5, color:'#111827', marginBottom:14, outline:'none', fontFamily:'var(--pf-sans)' }} />
              <label style={{ fontSize:12.5, fontWeight:700, color:'#374151', display:'block', marginBottom:6 }}>Notes / Brief</label>
              <textarea value={f.notes} onChange={e => setF(p=>({...p,notes:e.target.value}))} placeholder="Describe the creative requirement, target audience, dimensions, etc."
                rows={4} style={{ width:'100%', boxSizing:'border-box', border:'1px solid #e5e7eb', borderRadius:10, padding:'10px 12px', fontSize:13, color:'#111827', marginBottom:16, resize:'none', outline:'none', fontFamily:'var(--pf-sans)' }} />
              {submitErr && <div style={{ fontSize:12, color:'#dc2626', marginBottom:10 }}>{submitErr}</div>}
              <button onClick={submit} disabled={submitting} style={{ width:'100%', padding:'13px', borderRadius:12, background:'var(--pf-green)', border:'none', color:'#fff', fontWeight:800, fontSize:15, cursor: submitting ? 'default' : 'pointer', opacity: submitting ? 0.7 : 1, marginBottom:10 }}>{submitting ? 'Sending…' : 'Submit Request'}</button>
              <button onClick={onClose} style={{ width:'100%', padding:'12px', borderRadius:12, background:'#f3f4f6', border:'none', color:'#374151', fontWeight:700, fontSize:14, cursor:'pointer' }}>Cancel</button>
            </>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="pf-screen">
      <PFHeader
        title="Creative Assets"
        subtitle={`${filtered.length} approved file${filtered.length !== 1 ? 's' : ''}`}
        right={
          <button onClick={() => setShowReq(true)} style={{ ...iconBtn, width:'auto', height:'auto', whiteSpace:'nowrap', background:'var(--pf-green)', borderRadius:8, padding:'7px 13px', display:'flex', alignItems:'center', gap:4, color:'#fff', border:'none', cursor:'pointer', fontSize:12.5, fontWeight:700 }}>
            + Request
          </button>
        }
      />

      {/* Search */}
      <div style={{ padding:'8px 16px 0' }}>
        <div style={{ background:'var(--pf-surface)', border:'1px solid var(--pf-line)', borderRadius:10, display:'flex', alignItems:'center', gap:8, padding:'7px 12px' }}>
          <PFIcon name="search" size={15} color="var(--pf-ink-4)" />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search assets or project…"
            style={{ flex:1, border:'none', outline:'none', background:'transparent', fontFamily:'var(--pf-sans)', fontSize:13.5, color:'var(--pf-ink)' }} />
          {search && <button onClick={() => setSearch('')} style={{ border:'none', background:'none', cursor:'pointer', color:'var(--pf-ink-4)', fontSize:16, padding:0, lineHeight:1 }}>✕</button>}
        </div>
      </div>

      {/* Type filter chips */}
      <div style={{ display:'flex', gap:6, padding:'10px 16px 6px', overflowX:'auto', WebkitOverflowScrolling:'touch', scrollbarWidth:'none' }}>
        {TYPES.map(t => (
          <button key={t.id} onClick={() => setFilter(t.id)} style={{
            padding:'5px 13px', borderRadius:20, border:'1.5px solid',
            borderColor: filter===t.id ? 'var(--pf-green)' : 'var(--pf-line)',
            background: filter===t.id ? 'var(--pf-green-tint)' : 'transparent',
            color: filter===t.id ? 'var(--pf-green)' : 'var(--pf-ink-3)',
            fontFamily:'var(--pf-sans)', fontSize:12.5, fontWeight:600,
            whiteSpace:'nowrap', cursor:'pointer', flexShrink:0,
          }}>{t.label}</button>
        ))}
      </div>

      <div className="pf-scroll" style={{ padding:'6px 16px 90px' }}>
        {loading && [1,2,3].map(i => (
          <div key={i} style={{ background:'var(--pf-surface)', border:'1px solid var(--pf-line)', borderRadius:14, padding:14, marginBottom:10, opacity:0.5 }}>
            <div style={{ height:14, background:'var(--pf-line)', borderRadius:6, width:'60%', marginBottom:8 }} />
            <div style={{ height:11, background:'var(--pf-line)', borderRadius:6, width:'40%' }} />
          </div>
        ))}

        {!loading && loadError && (
          <div style={{ textAlign:'center', paddingTop:60 }}>
            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" style={{ margin: '0 auto', display: 'block' }}>
              <path d="M12 3l10 18H2z" /><path d="M12 10v4" /><circle cx="12" cy="17.3" r="0.9" fill="#dc2626" stroke="none" />
            </svg>
            <div style={{ marginTop:12, color:'var(--pf-ink)', fontSize:14, fontWeight:600 }}>Couldn't load assets</div>
            <div style={{ color:'var(--pf-ink-4)', fontSize:12.5, marginTop:4, padding:'0 24px' }}>{loadError}</div>
            <button onClick={loadAssets} style={{ marginTop:16, padding:'10px 22px', borderRadius:10, background:'var(--pf-green)', border:'none', color:'#fff', fontWeight:700, fontSize:13.5, cursor:'pointer' }}>Retry</button>
          </div>
        )}

        {!loading && !loadError && filtered.length === 0 && (
          <div style={{ textAlign:'center', paddingTop:60 }}>
            <PFIcon name="gallery" size={40} color="var(--pf-line)" />
            <div style={{ marginTop:12, color:'var(--pf-ink-3)', fontSize:14 }}>No assets found</div>
            <div style={{ color:'var(--pf-ink-4)', fontSize:12.5, marginTop:4 }}>Approved creative files appear here</div>
            <button onClick={() => setShowReq(true)} style={{ marginTop:16, padding:'10px 22px', borderRadius:10, background:'var(--pf-green)', border:'none', color:'#fff', fontWeight:700, fontSize:13.5, cursor:'pointer' }}>+ Request Asset</button>
          </div>
        )}

        {!loading && filtered.map(a => {
          const tc = TYPE_COLORS[a.request_type] || '#64748b';
          return (
            <div key={a.id} style={{ background:'var(--pf-surface)', border:'1px solid var(--pf-line)', borderRadius:14, marginBottom:10, boxShadow:'var(--pf-shadow-sm)', overflow:'hidden' }}>
              {/* Colour band thumbnail */}
              <div onClick={() => setPreview(a)} style={{ height:80, background:a.demoGradient||`${tc}11`, borderBottom:'1px solid var(--pf-line)', display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer', gap:12 }}>
                <PFIcon name={mimeIcon(a.mime_type)} size={32} color={tc} />
                <div style={{ fontSize:12.5, color:tc, fontWeight:700 }}>{a.demoLabel || a.filename}</div>
              </div>
              {/* Info row */}
              <div style={{ padding:'10px 14px 0' }}>
                <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', marginBottom:6 }}>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ fontWeight:700, fontSize:13, color:'var(--pf-ink)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{a.filename}</div>
                    <div style={{ fontSize:11.5, color:'var(--pf-ink-3)', marginTop:2 }}>
                      {a.project} · {a.uploaded_at} {a.file_size ? <span style={{ color:'var(--pf-ink-4)' }}> · {fmt(a.file_size)}</span> : null}
                    </div>
                  </div>
                  <div style={{ padding:'3px 9px', borderRadius:20, background:tc+'15', color:tc, fontSize:10.5, fontWeight:700, flexShrink:0, border:'1px solid '+tc+'30', textTransform:'capitalize', marginLeft:8 }}>{a.request_type}</div>
                </div>
              </div>
              {/* Action buttons */}
              <div style={{ display:'flex', gap:0, borderTop:'1px solid var(--pf-line)', marginTop:10 }}>
                <button onClick={() => setPreview(a)} style={{ flex:1, padding:'10px 0', border:'none', background:'transparent', color:'var(--pf-green)', fontFamily:'var(--pf-sans)', fontSize:12.5, fontWeight:700, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', gap:5 }}>
                  <PFIcon name="eye" size={14} color="var(--pf-green)" /> Preview
                </button>
                <div style={{ width:1, background:'var(--pf-line)' }} />
                <button onClick={() => share(a)} style={{ flex:1, padding:'10px 0', border:'none', background:'transparent', color:'var(--pf-ink-3)', fontFamily:'var(--pf-sans)', fontSize:12.5, fontWeight:600, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', gap:5 }}>
                  <PFIcon name="share" size={14} color="var(--pf-ink-3)" /> Share
                </button>
                <div style={{ width:1, background:'var(--pf-line)' }} />
                <button onClick={() => { if(a.url) window.open(a.url,'_blank'); else alert('File download not available in demo mode.'); }} style={{ flex:1, padding:'10px 0', border:'none', background:'transparent', color:'var(--pf-ink-3)', fontFamily:'var(--pf-sans)', fontSize:12.5, fontWeight:600, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', gap:5 }}>
                  <PFIcon name="download" size={14} color="var(--pf-ink-3)" /> Download
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {preview && (preview.url ? (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,.92)', zIndex:200, display:'flex', flexDirection:'column' }}>
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'12px 16px', background:'rgba(0,0,0,.6)' }}>
            <div style={{ color:'#fff', fontSize:13.5, fontWeight:600, flex:1, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{preview.filename}</div>
            <button onClick={() => setPreview(null)} style={{ color:'#fff', background:'transparent', border:'none', padding:6, cursor:'pointer', fontSize:22 }}>✕</button>
          </div>
          <div style={{ flex:1, overflow:'auto', display:'flex', alignItems:'center', justifyContent:'center', padding:16 }}>
            {preview.mime_type && preview.mime_type.startsWith('image/') ? (
              <img src={preview.url} alt={preview.filename} style={{ maxWidth:'100%', maxHeight:'100%', borderRadius:8 }} />
            ) : preview.mime_type === 'application/pdf' ? (
              <iframe src={preview.url} style={{ width:'100%', height:'100%', border:'none', borderRadius:8, background:'#fff' }} title={preview.filename} />
            ) : (
              <div style={{ textAlign:'center', color:'#fff' }}>
                <PFIcon name="doc" size={48} color="rgba(255,255,255,.4)" />
                <div style={{ marginTop:12, fontSize:14 }}>Preview not available for this type.</div>
              </div>
            )}
          </div>
          <div style={{ padding:'10px 16px 20px', display:'flex', gap:10, background:'rgba(0,0,0,.6)' }}>
            <button onClick={() => { setShare(preview); setPreview(null); }} style={{ flex:1, padding:'10px 0', borderRadius:10, background:'rgba(255,255,255,.12)', border:'1px solid rgba(255,255,255,.2)', color:'#fff', fontFamily:'var(--pf-sans)', fontSize:13, fontWeight:600, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', gap:6 }}>
              <PFIcon name="share" size={15} color="#fff" /> Share with Client
            </button>
          </div>
        </div>
      ) : <DemoPreview asset={preview} onClose={() => setPreview(null)} />)}

      {shareSheet && <ShareSheet asset={shareSheet} onClose={() => setShare(null)} />}
      {showReq && <ReqSheet onClose={() => setShowReq(false)} />}
    </div>
  );
};

Object.assign(window, { PFScreenProfiling, PFScreenLearn, PFScreenChat, PFScreenAssets });



// ===== 11-pf-screens-6.jsx =====
/* global React, PFIcon, PFHeader, PFBackBtn, PF_DATA, iconBtn, SectionTitle */

// ─────────────────────────────────────────────────────────────
// SITE VISIT — check-in, walkthrough, photos, outcome
// ─────────────────────────────────────────────────────────────
const PFScreenSiteVisit = ({ go, leadId }) => {
  const lead = PF_DATA.leads.find(l => l.id === leadId) || PF_DATA.leads[0];
  const [phase, setPhase] = React.useState('checkin'); // checkin | walkthrough | outcome
  const [checkedIn, setCheckedIn] = React.useState(false);

  // ADDITION 4: geo-fencing state
  const [userCoords, setUserCoords] = React.useState(null); // { lat, lng }
  const [geoStatus, setGeoStatus] = React.useState('idle'); // 'idle'|'locating'|'ok'|'denied'|'unavailable'
  const [geoWarningModal, setGeoWarningModal] = React.useState(null); // { distKm }

  // Real per-project site coordinates (projects.site_lat/site_lng/geofence_radius_m,
  // set on the admin dashboard) fetched via GET /api/leads/:id → projectSite.
  // lead.project_coords stays as a fallback only for the two hardcoded demo
  // leads in PF_DATA — any real lead has no project_coords, so without this
  // fetch the screen used to silently compare against a hardcoded Mumbai
  // coordinate for every real lead.
  const [projectSite, setProjectSite] = React.useState(null);
  React.useEffect(() => {
    if (!window.SRAPI || typeof window.SRAPI._get !== 'function') return;
    window.SRAPI._get('/api/leads/' + lead.id)
      .then(res => { if (res && res.projectSite) setProjectSite(res.projectSite); })
      .catch(() => {});
  }, [lead.id]);

  const siteCoords = (projectSite && projectSite.lat != null && projectSite.lng != null)
    ? { lat: projectSite.lat, lng: projectSite.lng }
    : (lead.project_coords || null);
  // Radius: real per-project value from the backend, else the demo lead's
  // fallback, else 500m.
  const GEO_FENCE_M = (projectSite && projectSite.radiusM) ||
    (lead.project_coords && lead.project_coords.fence_radius_m) || 500;

  // Update location chip every 30 s
  React.useEffect(() => {
    if (!navigator.geolocation) {
      setGeoStatus('unavailable');
      return;
    }
    const fetchPos = () => {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          setUserCoords({ lat: pos.coords.latitude, lng: pos.coords.longitude });
          setGeoStatus('ok');
        },
        () => setGeoStatus('denied'),
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 30000 }
      );
    };
    fetchPos();
    const interval = setInterval(fetchPos, 30000);
    return () => clearInterval(interval);
  }, []);

  // Called when "Check in now" is tapped — geo-verifies location against
  // the real project site coords when known; posts a genuine GPS-verified
  // attendance record via /api/attendance/checkin (not the site-visit
  // outcome endpoint, which has no lat/lng columns).
  const handleCheckIn = () => {
    if (!navigator.geolocation) {
      setCheckedIn(true);
      return;
    }
    setGeoStatus('locating');
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const coords = { lat: pos.coords.latitude, lng: pos.coords.longitude };
        setUserCoords(coords);
        setGeoStatus('ok');
        if (!siteCoords) {
          // No real site coordinates on file for this project — nothing to
          // verify against, so check in without a fence prompt rather than
          // comparing against a fabricated location.
          window.SRAPI?.checkInAttendance?.({ ...coords, projectId: projectSite?.projectId }).catch(() => {});
          setCheckedIn(true);
          return;
        }
        const distKm = haversineKm(coords.lat, coords.lng, siteCoords.lat, siteCoords.lng);
        if (distKm * 1000 <= GEO_FENCE_M) {
          // Within fence — check in normally
          window.SRAPI?.checkInAttendance?.({ ...coords, projectId: projectSite?.projectId }).catch(() => {});
          setCheckedIn(true);
        } else {
          // Outside fence — show warning modal
          setGeoWarningModal({ distKm: distKm.toFixed(2), coords });
        }
      },
      () => {
        setGeoStatus('denied');
        setCheckedIn(true); // allow without geo
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  // Confirm check-in despite being far from site
  const confirmCheckInAnyway = () => {
    if (geoWarningModal?.coords) {
      window.SRAPI?.checkInAttendance?.({ ...geoWarningModal.coords, projectId: projectSite?.projectId }).catch(() => {});
    }
    setGeoWarningModal(null);
    setCheckedIn(true);
  };

  if (phase === 'outcome') return <SiteVisitOutcome go={go} lead={lead} onBack={() => setPhase('walkthrough')} />;
  if (phase === 'walkthrough') return <SiteVisitWalkthrough go={go} lead={lead} onNext={() => setPhase('outcome')} onBack={() => setPhase('checkin')} />;

  return (
    <div className="pf-screen pf-anim-in">
      <PFHeader
        left={<PFBackBtn onClick={() => go('lead', lead.id)} />}
        title="Site visit"
        subtitle={`${lead.name} · ${lead.project.split(' · ')[0]}`}
      />
      <div className="pf-scroll" style={{ padding: '0 16px 24px' }}>
        {/* Visit card */}
        <div className="pf-card-tinted" style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 10.5, letterSpacing: '0.12em', color: 'var(--pf-green)', fontWeight: 700 }}>SCHEDULED VISIT</div>
          <div style={{ fontFamily: 'var(--pf-serif)', fontSize: 20, fontWeight: 600, marginTop: 4 }}>Today · 1:30 PM</div>
          <div style={{ fontSize: 12.5, color: 'var(--pf-ink-2)', marginTop: 4, lineHeight: 1.5 }}>
            Whispering Oaks, T-2 · 14th floor sample flat<br/>
            Survey No. 42, Baner Road, Pune 411045
          </div>
          <div style={{ display: 'flex', gap: 6, marginTop: 10 }}>
            <button className="pf-btn pf-btn-sm pf-btn-light" onClick={() => {
              const coords = siteCoords;
              const addr = encodeURIComponent((projectSite && projectSite.label) || (lead.project_coords && lead.project_coords.address) || lead.project);
              const query = coords ? `${coords.lat},${coords.lng}` : addr;
              window.open(`https://maps.google.com/?q=${query}`, '_blank');
            }}><PFIcon name="pin" size={12} color="var(--pf-green)" /> Directions</button>
            <button className="pf-btn pf-btn-sm pf-btn-light" onClick={() => { if(lead.phone) window.location.href=`tel:${lead.phone.replace(/\s/g,'')}`; }}><PFIcon name="phone" size={12} color="var(--pf-green)" /> Call lead</button>
          </div>
        </div>

        {/* ADDITION 4: Location chip — updates every 30 s */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
          <span className="pf-chip pf-chip-neutral" style={{ fontSize: 11, gap: 4 }}>
            <PFIcon name="pin" size={12} color="var(--pf-ink-3)" />
            {geoStatus === 'ok' && userCoords
              ? `📍 ${userCoords.lat.toFixed(4)}, ${userCoords.lng.toFixed(4)}`
              : geoStatus === 'locating' ? 'Locating…'
              : geoStatus === 'denied' ? 'Location off'
              : geoStatus === 'unavailable' ? 'Location unavailable'
              : 'Location off'}
          </span>
        </div>

        {/* ADDITION 4: geo-denied info banner */}
        {(geoStatus === 'denied' || geoStatus === 'unavailable') && (
          <div style={{
            background: 'var(--pf-blue-tint)', border: '1px solid var(--pf-blue)',
            borderRadius: 12, padding: '10px 14px', marginBottom: 14,
            fontSize: 12.5, color: 'var(--pf-blue)', lineHeight: 1.5,
          }}>
            Location unavailable — visit will be logged without geo-verification.
          </div>
        )}

        {/* Check-in */}
        <div className="pf-card" style={{ marginBottom: 14, textAlign: 'center' }}>
          <div style={{
            width: 72, height: 72, margin: '4px auto 10px', borderRadius: '50%',
            background: checkedIn ? 'var(--pf-green)' : 'var(--pf-green-tint)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            border: checkedIn ? 0 : '2px dashed var(--pf-green-tint-2)',
            transition: 'all 0.2s',
          }}>
            {geoStatus === 'locating'
              ? <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="var(--pf-green)" strokeWidth="2" strokeLinecap="round"><path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"><animateTransform attributeName="transform" type="rotate" from="0 12 12" to="360 12 12" dur="0.75s" repeatCount="indefinite"/></path></svg>
              : <PFIcon name={checkedIn ? 'check' : 'pin'} size={30} color={checkedIn ? '#fff' : 'var(--pf-green)'} strokeWidth={checkedIn ? 2.4 : 1.7} />
            }
          </div>
          <div style={{ fontFamily: 'var(--pf-serif)', fontSize: 17, fontWeight: 600 }}>
            {checkedIn ? 'Checked in' : geoStatus === 'locating' ? 'Getting your location…' : 'Check in when you arrive'}
          </div>
          <div style={{ fontSize: 12, color: 'var(--pf-ink-3)', marginTop: 4 }}>
            {checkedIn ? 'GPS verified · ' + new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : 'Auto-verified via GPS'}
          </div>
          {!checkedIn ? (
            <button className="pf-btn pf-btn-block" style={{ marginTop: 14 }}
              onClick={handleCheckIn} disabled={geoStatus === 'locating'}>
              <PFIcon name="pin" size={14} color="#fff" />
              {geoStatus === 'locating' ? 'Locating…' : 'Check in now'}
            </button>
          ) : (
            <button className="pf-btn pf-btn-block" style={{ marginTop: 14 }} onClick={() => setPhase('walkthrough')}>
              Start walkthrough →
            </button>
          )}
        </div>

        {/* ADDITION 4: Geo-fencing warning modal — user is too far from site */}
        {geoWarningModal && (
          <div style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 40, padding: 24,
          }}>
            <div style={{
              background: 'var(--pf-surface)', borderRadius: 20, padding: 20, maxWidth: 340, width: '100%',
              boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
            }}>
              <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 10 }}>
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#e2a03f" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 3l10 18H2z" />
                  <path d="M12 10v4" />
                  <circle cx="12" cy="17.3" r="0.9" fill="#e2a03f" stroke="none" />
                </svg>
              </div>
              <div style={{ fontFamily: 'var(--pf-serif)', fontSize: 18, fontWeight: 600, textAlign: 'center', marginBottom: 8 }}>
                Not at site
              </div>
              <div style={{ fontSize: 13.5, color: 'var(--pf-ink-2)', lineHeight: 1.55, textAlign: 'center', marginBottom: 20 }}>
                You appear to be <strong>{geoWarningModal.distKm} km</strong> from the site.
                This visit may be flagged for review. Submit anyway or cancel?
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="pf-btn pf-btn-ghost pf-btn-block" onClick={() => setGeoWarningModal(null)}>
                  Cancel
                </button>
                <button className="pf-btn pf-btn-block" onClick={confirmCheckInAnyway}>
                  Submit anyway
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Pre-visit checklist */}
        <SectionTitle title="Pre-visit checklist" />
        <div style={{ marginBottom: 14 }}>
          {[
            { id: 'p1', text: 'Confirmed client arrival (SMS sent)', done: true },
            { id: 'p2', text: 'Sample flat keys collected from reception', done: true },
            { id: 'p3', text: 'Brochure + pricing sheet in hand', done: true },
            { id: 'p4', text: 'Welcome drink / chai arranged', done: false },
          ].map(t => (
            <div key={t.id} className="pf-task" data-done={t.done}>
              <div className="pf-check">{t.done && <PFIcon name="check" size={14} color="#fff" strokeWidth={2.5} />}</div>
              <div style={{ flex: 1 }}><div className="pf-task-title">{t.text}</div></div>
            </div>
          ))}
        </div>

        {/* Pre-sales: Confirm site visit + sync to sales exec */}
        <SiteVisitConfirmPanel lead={lead} />
      </div>
    </div>
  );
};

const SiteVisitConfirmPanel = ({ lead, startOpen, onDone }) => {
  const [open, setOpen] = React.useState(!!startOpen);
  const [date, setDate] = React.useState('');
  const [time, setTime] = React.useState('11:00 AM');
  const [project, setProject] = React.useState(lead?.project || '');
  const [address, setAddress] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const [confirmed, setConfirmed] = React.useState(false);
  const [projects, setProjects] = React.useState(null); // null = not loaded yet, [] = loaded empty

  React.useEffect(() => {
    if (!open || projects !== null) return;
    if (!window.SRAPI || typeof window.SRAPI.getProjects !== 'function') { setProjects([]); return; }
    window.SRAPI.getProjects()
      .then(list => {
        setProjects(Array.isArray(list) ? list : []);
        // Pre-fill address if the lead's project already matches one on file
        const match = (list || []).find(p => p.name === (lead?.project || project));
        if (match) setAddress(match.address || match.location || '');
      })
      .catch(() => setProjects([]));
  }, [open]);

  const handleProjectChange = (name) => {
    setProject(name);
    const match = (projects || []).find(p => p.name === name);
    if (match) setAddress(match.address || match.location || '');
  };

  const handleConfirm = async () => {
    if (!date || !time) return;
    setLoading(true);
    try {
      const result = await window.SRAPI.confirmSiteVisit({
        leadId: lead?.id,
        visitDate: date,
        visitTime: time,
        project: project || lead?.project,
        projectAddress: address
      });
      setConfirmed(true);
      setOpen(false);
      // Open the pre-filled WhatsApp confirmation (with Maps link) for the
      // agent to review and send — there is no automated WhatsApp Business
      // API here, this deep-link is how every "send" in this app works.
      if (result && result.waUrl && typeof window !== 'undefined') {
        window.open(result.waUrl, '_blank');
        if (window.SRAPI && typeof window.SRAPI.sendWhatsApp === 'function') {
          window.SRAPI.sendWhatsApp(lead?.id, 'visit_confirmation',
            `Visit confirmation for ${date} ${time}${project ? ' at ' + project : ''}`,
            'visit_confirmation').catch(() => {});
        }
      }
      if (onDone) onDone(result);
    } catch (e) {
      alert('Could not confirm: ' + (e && e.message ? e.message : 'please try again.'));
    } finally {
      setLoading(false);
    }
  };

  if (!open) return (
    <div style={{ margin: '16px 0', padding: '14px 16px', background: confirmed ? 'rgba(52,199,89,0.1)' : 'rgba(0,122,255,0.08)', borderRadius: 14, border: `1px solid ${confirmed ? 'rgba(52,199,89,0.3)' : 'rgba(0,122,255,0.2)'}` }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div>
          <div style={{ fontWeight: 600, fontSize: 14, color: confirmed ? '#34C759' : '#007AFF' }}>
            {confirmed ? '✅ Site visit confirmed' : '📅 Confirm site visit'}
          </div>
          <div style={{ fontSize: 12, color: '#8E8E93', marginTop: 2 }}>
            {confirmed ? 'WhatsApp sent · synced to exec calendar' : 'Send confirmation to client & sync calendar'}
          </div>
        </div>
        {!confirmed && (
          <button onClick={() => setOpen(true)} style={{ background: '#007AFF', color: '#fff', border: 'none', borderRadius: 10, padding: '8px 14px', fontSize: 13, fontWeight: 600 }}>
            Confirm
          </button>
        )}
      </div>
    </div>
  );

  return (
    <div style={{ margin: '16px 0', padding: '16px', background: 'rgba(0,0,0,0.04)', borderRadius: 16, border: '1px solid rgba(0,0,0,0.08)' }}>
      <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 14 }}>📅 Site Visit Confirmation</div>
      <div style={{ marginBottom: 10 }}>
        <div style={{ fontSize: 12, color: '#8E8E93', marginBottom: 4 }}>Visit Date *</div>
        <input type="date" value={date} onChange={e => setDate(e.target.value)}
          style={{ width: '100%', padding: '10px 12px', borderRadius: 10, border: '1px solid rgba(0,0,0,0.15)', fontSize: 14, background: '#fff', boxSizing: 'border-box' }} />
      </div>
      <div style={{ marginBottom: 10 }}>
        <div style={{ fontSize: 12, color: '#8E8E93', marginBottom: 4 }}>Visit Time</div>
        <select value={time} onChange={e => setTime(e.target.value)}
          style={{ width: '100%', padding: '10px 12px', borderRadius: 10, border: '1px solid rgba(0,0,0,0.15)', fontSize: 14, background: '#fff', boxSizing: 'border-box' }}>
          {['09:00 AM','10:00 AM','11:00 AM','12:00 PM','01:00 PM','02:00 PM','03:00 PM','04:00 PM','05:00 PM','06:00 PM'].map(t => (
            <option key={t} value={t}>{t}</option>
          ))}
        </select>
      </div>
      <div style={{ marginBottom: 10 }}>
        <div style={{ fontSize: 12, color: '#8E8E93', marginBottom: 4 }}>Project Name</div>
        {projects && projects.length > 0 ? (
          <select value={project} onChange={e => handleProjectChange(e.target.value)}
            style={{ width: '100%', padding: '10px 12px', borderRadius: 10, border: '1px solid rgba(0,0,0,0.15)', fontSize: 14, background: '#fff', boxSizing: 'border-box' }}>
            <option value="">Select a project…</option>
            {projects.map(p => <option key={p.id || p.name} value={p.name}>{p.name}</option>)}
          </select>
        ) : (
          <input value={project} onChange={e => setProject(e.target.value)} placeholder={lead?.project || 'e.g. Horizon Towers'}
            style={{ width: '100%', padding: '10px 12px', borderRadius: 10, border: '1px solid rgba(0,0,0,0.15)', fontSize: 14, background: '#fff', boxSizing: 'border-box' }} />
        )}
      </div>
      <div style={{ marginBottom: 14 }}>
        <div style={{ fontSize: 12, color: '#8E8E93', marginBottom: 4 }}>Site Address</div>
        <input value={address} onChange={e => setAddress(e.target.value)} placeholder="e.g. Survey No. 42, Baner Rd, Pune"
          style={{ width: '100%', padding: '10px 12px', borderRadius: 10, border: '1px solid rgba(0,0,0,0.15)', fontSize: 14, background: '#fff', boxSizing: 'border-box' }} />
      </div>
      <div style={{ display: 'flex', gap: 10 }}>
        <button onClick={() => setOpen(false)} style={{ flex: 1, padding: '11px', borderRadius: 12, border: '1px solid rgba(0,0,0,0.15)', background: '#fff', fontSize: 14, fontWeight: 600 }}>
          Cancel
        </button>
        <button onClick={handleConfirm} disabled={!date || loading} style={{ flex: 2, padding: '11px', borderRadius: 12, border: 'none', background: date ? '#34C759' : '#ccc', color: '#fff', fontSize: 14, fontWeight: 700 }}>
          {loading ? 'Confirming…' : '✅ Confirm & Send WhatsApp'}
        </button>
      </div>
    </div>
  );
};

const SiteVisitWalkthrough = ({ go, lead, onNext, onBack }) => {
  const [stops, setStops] = React.useState([
    { id: 's1', name: 'Main entrance & lobby', done: true, photos: 2 },
    { id: 's2', name: 'Sample flat — living', done: true, photos: 4 },
    { id: 's3', name: 'Master bedroom & balcony', done: true, photos: 3 },
    { id: 's4', name: 'Kitchen + utility', done: false, photos: 0 },
    { id: 's5', name: 'Amenities — clubhouse, pool', done: false, photos: 0 },
    { id: 's6', name: 'Parking + entry/exit', done: false, photos: 0 },
  ]);
  const toggle = id => setStops(s => s.map(x => x.id === id ? { ...x, done: !x.done, photos: !x.done ? x.photos + 2 : x.photos } : x));
  const done = stops.filter(s => s.done).length;

  return (
    <div className="pf-screen pf-anim-in">
      <PFHeader
        left={<button onClick={onBack} style={iconBtn}><PFIcon name="chevL" size={18} /></button>}
        title="Walkthrough"
        subtitle={`${lead.name} · ${done}/${stops.length} stops`}
      />
      <div style={{ padding: '0 16px 10px' }}>
        <div style={{ height: 4, background: 'var(--pf-line)', borderRadius: 2, overflow: 'hidden' }}>
          <div style={{ width: `${(done / stops.length) * 100}%`, height: '100%', background: 'var(--pf-green)', transition: 'width 0.25s' }} />
        </div>
      </div>
      <div className="pf-scroll" style={{ padding: '4px 16px 20px' }}>
        {stops.map((s, i) => (
          <div key={s.id} className="pf-card" style={{
            marginBottom: 8, padding: 12, display: 'flex', alignItems: 'center', gap: 12,
            cursor: 'pointer', opacity: s.done ? 1 : 0.95,
          }} onClick={() => toggle(s.id)}>
            <div style={{
              width: 32, height: 32, borderRadius: '50%',
              background: s.done ? 'var(--pf-green)' : 'var(--pf-green-tint)',
              color: s.done ? '#fff' : 'var(--pf-green)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontWeight: 600, fontSize: 13, flex: '0 0 32px',
            }}>
              {s.done ? <PFIcon name="check" size={16} color="#fff" strokeWidth={2.5} /> : i + 1}
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 500, fontSize: 14, textDecoration: s.done ? 'line-through' : 'none', color: s.done ? 'var(--pf-ink-3)' : 'var(--pf-ink)' }}>{s.name}</div>
              <div style={{ fontSize: 11.5, color: 'var(--pf-ink-4)', marginTop: 2 }}>
                {s.photos > 0 ? `${s.photos} photos captured` : 'Tap to mark complete'}
              </div>
            </div>
            <button style={{
              ...iconBtn, width: 34, height: 34, background: 'var(--pf-green-tint)', borderColor: 'var(--pf-green-tint-2)',
            }} onClick={(e) => { e.stopPropagation(); }}>
              <PFIcon name="camera" size={14} color="var(--pf-green)" />
            </button>
          </div>
        ))}

        <div className="pf-card" style={{ marginTop: 12 }}>
          <div className="pf-label">Live notes</div>
          <textarea className="pf-textarea" defaultValue="Mrs. Mehta loved the east balcony view. Asked specifically about modular kitchen package (add-on). Kids liked the clubhouse." />
        </div>
      </div>
      <div style={{ padding: '10px 16px 14px', borderTop: '1px solid var(--pf-line)', background: 'var(--pf-bg)' }}>
        <button className="pf-btn pf-btn-block" onClick={onNext}>End visit → Log outcome</button>
      </div>
    </div>
  );
};

const SiteVisitOutcome = ({ go, lead, onBack }) => {
  const [interest, setInterest] = React.useState('Very interested');
  const [next, setNext] = React.useState('Send quote');
  const [objections, setObjections] = React.useState(['Price', 'Possession date']);
  const [saving, setSaving] = React.useState(false);
  const [toast, setToast] = React.useState(null);
  const [showSchedule, setShowSchedule] = React.useState(false);
  const showToast = (m) => { setToast(m); setTimeout(() => setToast(null), 2000); };
  const toggleObjection = (o) => {
    if (o === 'None') { setObjections(['None']); return; }
    setObjections(prev => {
      const withoutNone = prev.filter(x => x !== 'None');
      return withoutNone.includes(o) ? withoutNone.filter(x => x !== o) : [...withoutNone, o];
    });
  };

  const summaryRef = React.useRef(null);

  const handleSave = async () => {
    if (next === 'Schedule 2nd visit') { setShowSchedule(true); return; }
    setSaving(true);
    const payload = {
      interest, objections, nextStep: next,
      summary: summaryRef.current ? summaryRef.current.value : '',
    };
    try {
      if (window.SRAPI && typeof window.SRAPI.submitVisit === 'function') {
        await window.SRAPI.submitVisit(lead.id, payload);
      }
      if (next === 'Ping manager' && window.SRAPI && typeof window.SRAPI.pingManager === 'function') {
        await window.SRAPI.pingManager(lead.id,
          `Visit outcome for ${lead.name}: ${interest}. Needs manager input — ${objections.join(', ') || 'no objections'}.`,
          payload
        ).catch(() => {});
      }
    } catch (e) {
      showToast('Could not save — will retry');
    } finally {
      setSaving(false);
    }
    if (next === 'Ping manager') { go('chat'); return; }
    showToast(next === 'Send quote' ? '✓ Saved — quote follow-up queued'
      : next === 'Token collection' ? '✓ Saved — token collection flagged'
      : '✓ Outcome saved');
    setTimeout(() => go('lead', lead.id), 700);
  };

  const handleScheduled = () => {
    setShowSchedule(false);
    showToast('✓ 2nd visit scheduled — reminders on');
    setTimeout(() => go('lead', lead.id), 700);
  };

  if (showSchedule) {
    return (
      <div className="pf-screen pf-anim-in">
        <PFHeader
          left={<button onClick={() => setShowSchedule(false)} style={iconBtn}><PFIcon name="chevL" size={18} /></button>}
          title="Schedule 2nd visit"
          subtitle={lead.name}
        />
        <div className="pf-scroll" style={{ padding: '0 16px 20px' }}>
          <SiteVisitConfirmPanel lead={lead} onDone={handleScheduled} />
        </div>
      </div>
    );
  }

  return (
    <div className="pf-screen pf-anim-in">
      <PFHeader
        left={<button onClick={onBack} style={iconBtn}><PFIcon name="chevL" size={18} /></button>}
        title="Visit outcome"
        subtitle={lead.name}
      />
      <div className="pf-scroll" style={{ padding: '0 16px 20px' }}>
        <div className="pf-card-tinted" style={{ marginBottom: 14, display: 'flex', gap: 10, alignItems: 'center' }}>
          <PFIcon name="clock" size={18} color="var(--pf-green)" />
          <div style={{ fontSize: 13, color: 'var(--pf-ink-2)' }}>Visit duration · <strong>52 min</strong> · 12 photos + 2 voice notes</div>
        </div>

        <div className="pf-field">
          <div className="pf-label">Client interest level</div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {['Very interested', 'Lukewarm', 'Not interested', 'Wants 2nd visit'].map(o => (
              <button key={o} onClick={() => setInterest(o)} style={{
                padding: '8px 14px', borderRadius: 999,
                background: interest === o ? 'var(--pf-green)' : 'var(--pf-surface)',
                color: interest === o ? '#fff' : 'var(--pf-ink-2)',
                border: `1px solid ${interest === o ? 'var(--pf-green)' : 'var(--pf-line)'}`,
                fontSize: 12.5, fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit',
              }}>{o}</button>
            ))}
          </div>
        </div>

        <div className="pf-field">
          <div className="pf-label">Units shortlisted</div>
          <div className="pf-card" style={{ padding: 0, overflow: 'hidden' }}>
            {[
              ['T-2, 1403', '3BHK · 1,480 sqft · East', '₹1.95 Cr', true],
              ['T-2, 1203', '3BHK · 1,480 sqft · East', '₹1.88 Cr', false],
              ['T-3, 1504', '3BHK · 1,520 sqft · North', '₹2.05 Cr', false],
            ].map(([unit, meta, price, chosen], i) => (
              <div key={i} style={{
                display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px',
                borderTop: i ? '1px solid var(--pf-line-2)' : 0,
              }}>
                <div style={{
                  width: 20, height: 20, borderRadius: '50%', flex: '0 0 20px',
                  border: `2px solid ${chosen ? 'var(--pf-green)' : 'var(--pf-line)'}`,
                  background: chosen ? 'var(--pf-green)' : 'transparent',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  {chosen && <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#fff' }} />}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 600 }}>{unit}</div>
                  <div style={{ fontSize: 11.5, color: 'var(--pf-ink-3)' }}>{meta}</div>
                </div>
                <div style={{ fontFamily: 'var(--pf-serif)', fontSize: 14, fontWeight: 600 }}>{price}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="pf-field">
          <div className="pf-label">Objections raised</div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {['Price', 'Loan', 'Possession date', 'Size', 'School distance', 'None'].map((o) => {
              const active = objections.includes(o);
              return (
                <button key={o} onClick={() => toggleObjection(o)} className="pf-chip pf-chip-neutral" style={{
                  background: active ? 'var(--pf-amber-tint)' : '#f0ede4',
                  borderColor: active ? '#ebd9bf' : 'var(--pf-line)',
                  color: active ? 'var(--pf-amber)' : 'var(--pf-ink-2)',
                  fontSize: 12, padding: '5px 12px', cursor: 'pointer', fontFamily: 'inherit',
                  fontWeight: active ? 600 : 500,
                }}>{o}</button>
              );
            })}
          </div>
        </div>

        <div className="pf-field">
          <div className="pf-label">Next step</div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {['Send quote', 'Schedule 2nd visit', 'Token collection', 'Ping manager', 'Wait'].map(o => (
              <button key={o} onClick={() => setNext(o)} style={{
                padding: '8px 14px', borderRadius: 999,
                background: next === o ? 'var(--pf-green-tint)' : 'var(--pf-surface)',
                color: next === o ? 'var(--pf-green-ink)' : 'var(--pf-ink-2)',
                border: `1px solid ${next === o ? 'var(--pf-green-tint-2)' : 'var(--pf-line)'}`,
                fontSize: 12.5, fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit',
              }}>{o}</button>
            ))}
          </div>
        </div>

        <div className="pf-field">
          <div className="pf-label">Summary for CRM</div>
          <textarea ref={summaryRef} className="pf-textarea" style={{ minHeight: 96 }} defaultValue="Family-of-4 visited with spouse & 2 kids. Strong preference for T-2, 1403 (east-facing, 14th floor). Priced above budget by ~8% — wants 5% discount + waived modular kitchen charges. Ready to token if terms agreed by weekend." />
        </div>

        <button className="pf-btn pf-btn-block" disabled={saving} onClick={handleSave}>
          {saving ? 'Saving…' : next === 'Schedule 2nd visit' ? 'Continue → Schedule visit' : 'Save & move to Negotiation'}
        </button>
        <button className="pf-btn pf-btn-ghost pf-btn-block" style={{ marginTop: 8 }} onClick={() => go('chat')}>
          <PFIcon name="pin" size={14} color="var(--pf-green)" /> Ping manager — price approval
        </button>
        {toast && (
          <div style={{
            position: 'fixed', bottom: 90, left: '50%', transform: 'translateX(-50%)',
            background: 'var(--pf-ink)', color: '#fff', padding: '10px 18px', borderRadius: 999,
            fontSize: 13, fontWeight: 500, zIndex: 100, boxShadow: '0 8px 24px rgba(0,0,0,0.25)',
          }}>{toast}</div>
        )}
      </div>
    </div>
  );
};

Object.assign(window, { PFScreenSiteVisit });



// ===== 12-pf-screens-7.jsx =====
/* global React, PFIcon, PFHeader, PFBackBtn, PF_DATA, iconBtn, SectionTitle */

// ─────────────────────────────────────────────────────────────
// TASK DETAIL — full execution flow for a single task
// ─────────────────────────────────────────────────────────────
/* ─────────────────────────────────────────────────────────────
 * TASK STORE
 *   Lives at window.TASK_STORE — per-task overlay (done, snoozedUntil,
 *   rescheduledTo, assignee, notes, completedAt). Reads/merges with
 *   the base PF_DATA.tasks list. Persisted to localStorage and synced
 *   to backend via SRAPI.completeTask / SRAPI.updateTask when present.
 *   Dispatches 'sr:task-updated' so any screen re-renders live.
 * ───────────────────────────────────────────────────────────── */
const TASK_LS_KEY = 'sr_tasks_v1';
window.TASK_STORE = window.TASK_STORE || (() => {
  try { return JSON.parse(localStorage.getItem(TASK_LS_KEY) || '{}'); }
  catch (e) { return {}; }
})();

function getTask(taskId) {
  const base = PF_DATA.tasks.find(t => t.id === taskId) || PF_DATA.tasks[0];
  const overlay = window.TASK_STORE[taskId] || {};
  return Object.assign({}, base, overlay);
}
function patchTask(taskId, patch) {
  window.TASK_STORE[taskId] = Object.assign({}, window.TASK_STORE[taskId] || {}, patch, {
    _updatedAt: new Date().toISOString(),
  });
  try { localStorage.setItem(TASK_LS_KEY, JSON.stringify(window.TASK_STORE)); } catch (e) {}
  // Best-effort remote sync. The local overlay uses UI-only field names
  // (assignee, snoozedFrom, cancelled, ...) that don't match the backend's
  // task columns — translate rather than forward the raw patch, which
  // previously either sent the wrong field name (assignee vs. the real
  // `owner` column, so reassigns silently landed nowhere) or, for
  // completions, passed the whole patch object as completeTask's `notes`
  // string argument instead of actual note text.
  if (window.SRAPI) {
    try {
      if (patch.done && typeof window.SRAPI.completeTask === 'function') {
        window.SRAPI.completeTask(taskId, patch.notes || '');
      } else if (typeof window.SRAPI.updateTask === 'function') {
        const remote = {};
        if (patch.due !== undefined) remote.due = patch.due;
        if (patch.assignee !== undefined) remote.owner = patch.assignee;
        if (patch.notes !== undefined) remote.notes = patch.notes;
        if (patch.cancelled) remote.status = 'Cancelled';
        if (Object.keys(remote).length) window.SRAPI.updateTask(taskId, remote);
      }
    } catch (e) { /* offline tolerated */ }
  }
  document.dispatchEvent(new CustomEvent('sr:task-updated', {
    detail: { taskId, task: getTask(taskId), patch },
  }));
}
Object.assign(window, { getTask, patchTask });

// Loaded on demand, not at module eval: SRAPI has no token yet on a fresh
// login, so fetching here left the demo names in place and a reassign wrote a
// name no real user has into tasks.owner — which the backend filters on by
// display name, so the task vanished from every list. Null until a real list
// arrives, so callers can refuse rather than guess.
let SR_ASSIGNEES = null;
async function loadAssignees() {
  if (SR_ASSIGNEES) return SR_ASSIGNEES;
  if (!window.SRAPI || !window.SRAPI._token) return null;
  try {
    const res = await window.SRAPI._get('/api/mobile/team-members');
    const names = (res && Array.isArray(res.members) ? res.members : [])
      .map(m => m.name).filter(Boolean);
    if (names.length) SR_ASSIGNEES = names;
  } catch (e) { /* offline — stays null */ }
  return SR_ASSIGNEES;
}

/* ── Time helpers ── */
function bumpDue(due, hours) {
  // Returns ISO-8601 datetime hours from now (or from existing ISO due if valid)
  const base = due && !isNaN(Date.parse(due)) ? new Date(due) : new Date();
  base.setTime(base.getTime() + hours * 60 * 60 * 1000);
  return base.toISOString();
}

// Convert a relative-time label to an ISO-8601 string
function labelToISO(label) {
  const now = new Date();
  if (label === 'Later today') {
    now.setHours(now.getHours() + 3, 0, 0, 0);
    return now.toISOString();
  }
  const tomorrow = new Date(now); tomorrow.setDate(tomorrow.getDate() + 1);
  if (label === 'Tomorrow 10:00 AM') { tomorrow.setHours(10, 0, 0, 0); return tomorrow.toISOString(); }
  if (label === 'Tomorrow 3:00 PM')  { tomorrow.setHours(15, 0, 0, 0); return tomorrow.toISOString(); }
  const dayAfter = new Date(now); dayAfter.setDate(dayAfter.getDate() + 2);
  if (label === 'Day after, 11:00 AM') { dayAfter.setHours(11, 0, 0, 0); return dayAfter.toISOString(); }
  return new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString();
}

const PFScreenTaskDetail = ({ go, taskId }) => {
  const [task, setTask]   = React.useState(() => getTask(taskId));
  const [step, setStep]   = React.useState(0);
  const [notes, setNotes] = React.useState(task.notes || '');
  const [toast, setToast] = React.useState(null);
  // Was `|| PF_DATA.leads[0]` — a task created with no lead linked (e.g.
  // "Linked lead: None") silently showed whatever lead happened to be
  // first in the array as if it were genuinely attached, with no way to
  // tell from the screen that it wasn't real. Prefer the real leadId
  // (real tasks); fall back to matching by client name (demo seed, which
  // has no leadId field); otherwise this task genuinely has no lead.
  const lead = (task.leadId ? PF_DATA.leads.find(l => l.id === task.leadId) : null)
    || (task.client ? PF_DATA.leads.find(l => l.name === task.client) : null)
    || null;

  // Re-read whenever the store changes for this task
  React.useEffect(() => {
    const onUpdate = (e) => { if (e.detail && e.detail.taskId === taskId) setTask(getTask(taskId)); };
    document.addEventListener('sr:task-updated', onUpdate);
    return () => document.removeEventListener('sr:task-updated', onUpdate);
  }, [taskId]);

  // Save notes on blur (avoids spamming the store on every keystroke)
  const flushNotes = () => { if (notes !== (task.notes || '')) patchTask(taskId, { notes }); };

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(null), 1800); };

  const handleSnooze = () => {
    const newDue = bumpDue(task.due, 1);
    patchTask(taskId, { due: newDue, snoozedFrom: task.due });
    showToast(`Snoozed until ${newDue}`);
  };
  const handleReschedule = () => {
    const opts = ['Later today', 'Tomorrow 10:00 AM', 'Tomorrow 3:00 PM', 'Day after, 11:00 AM'];
    const pick = window.prompt('Reschedule to:\n' + opts.map((o, i) => `${i+1}. ${o}`).join('\n') + '\n\nEnter 1-4:', '1');
    if (pick === null) return;
    const idx = Math.max(1, Math.min(4, parseInt(pick, 10) || 1)) - 1;
    patchTask(taskId, { due: labelToISO(opts[idx]), rescheduledFrom: task.due });
    showToast(`Rescheduled to ${opts[idx]}`);
  };
  const handleReassign = async () => {
    const names = await loadAssignees();
    if (!names) {
      showToast('Cannot reassign — team list unavailable. Check your connection.');
      return;
    }
    const list = names.map((n, i) => `${i+1}. ${n}`).join('\n');
    const pick = window.prompt('Reassign to:\n' + list + '\n\nEnter number:', '1');
    if (pick === null) return;
    const idx = Math.max(1, Math.min(names.length, parseInt(pick, 10) || 1)) - 1;
    patchTask(taskId, { assignee: names[idx] });
    showToast(`Reassigned to ${names[idx]}`);
  };
  // Tasks that require evidence before marking done
  const EVIDENCE_REQUIRED_KINDS = new Set(['doc', 'visit', 'booking', 'token']);
  const isCritical = task.priority === 'High' || EVIDENCE_REQUIRED_KINDS.has(task.kind);
  const hasEvidence = !!(task.evidenceUploaded || (window.TASK_STORE && window.TASK_STORE[taskId] && window.TASK_STORE[taskId].evidenceUploaded));

  const handleMarkDone = () => {
    // Evidence gate for doc/visit/booking tasks
    if (EVIDENCE_REQUIRED_KINDS.has(task.kind) && !hasEvidence) {
      showToast('Upload evidence first before marking done');
      setTimeout(() => go('evidence', lead.id), 900);
      return;
    }
    // Confirmation for critical / High-priority tasks
    if (isCritical && !window.confirm(`Mark "${task.title}" as complete?\n\nYour manager will be notified immediately.`)) return;
    flushNotes();
    const completedAt = new Date().toISOString();
    patchTask(taskId, { done: true, completedAt, awaitingVerification: isCritical, completedBy: 'Vinay Joshi' });
    // Sync to backend
    if (window.SRAPI && typeof window.SRAPI.completeTask === 'function') {
      window.SRAPI.completeTask(taskId, { notes, completedAt }).catch(() => {});
    }
    // Notify manager for critical tasks
    if (isCritical && lead && window.SRAPI && typeof window.SRAPI.pingManager === 'function') {
      window.SRAPI.pingManager(lead.id,
        `✅ Task completed: "${task.title}" — awaiting your verification.`,
        { taskId, completedAt, leadName: lead.name, priority: task.priority }
      ).catch(() => {});
    }
    showToast(isCritical ? '✓ Done — manager notified' : '✓ Marked as done');
    setTimeout(() => go('tasks'), 800);
  };
  const handleCancel = () => {
    if (isCritical && !window.confirm(`Cancel this critical task: "${task.title}"?\n\nThis will be logged.`)) return;
    patchTask(taskId, { done: false, cancelled: true, cancelledAt: new Date().toISOString() });
    showToast('Task cancelled');
    setTimeout(() => go('tasks'), 700);
  };
  const handleReopen = () => {
    patchTask(taskId, { done: false, completedAt: null, awaitingVerification: false, cancelled: false });
    showToast('Reopened');
  };

  // call/doc/visit/form all navigate into a lead-scoped screen — if this
  // task has no real lead attached, that CTA has nothing to open.
  const needsLead = () => { showToast('No lead linked to this task'); };
  const meta = {
    call: { title: 'Complete call task', icon: 'phone', color: 'var(--pf-green)', cta: 'Start call', go: () => lead ? go('callActive', lead.id) : needsLead(),
      steps: ['Review client context', 'Load script', 'Make the call', 'Log disposition'] },
    doc: { title: 'Upload document', icon: 'doc', color: 'var(--pf-blue)', cta: 'Open evidence', go: () => lead ? go('evidence', lead.id) : needsLead(),
      steps: ['Check requirement', 'Capture or attach', 'Caption & tag', 'Submit'] },
    visit: { title: 'Site visit task', icon: 'pin', color: 'var(--pf-amber)', cta: 'Start visit', go: () => lead ? go('siteVisit', lead.id) : needsLead(),
      steps: ['Confirm arrival', 'Check in on site', 'Walkthrough', 'Log outcome'] },
    form: { title: 'Profile update', icon: 'note', color: 'var(--pf-ink-2)', cta: 'Open profile', go: () => lead ? go('profiling', lead.id) : needsLead(),
      steps: ['Gather info from client', 'Fill sections', 'Verify', 'Save'] },
    chat: { title: 'Ping manager', icon: 'chat', color: 'var(--pf-green)', cta: 'Open chat', go: () => go('chat'),
      steps: ['Draft context', 'Attach lead card', 'Send ping', 'Await response'] },
  };
  const m = meta[task.kind] || meta.doc;

  return (
    <div className="pf-screen pf-anim-in">
      <PFHeader
        left={<PFBackBtn onClick={() => go('tasks')} />}
        title={m.title}
        subtitle={task.due}
      />
      <div className="pf-scroll" style={{ padding: '0 16px 24px' }}>
        {/* Hero */}
        <div className="pf-card" style={{ marginBottom: 12, display: 'flex', gap: 12, alignItems: 'flex-start', opacity: task.done ? 0.7 : 1 }}>
          <div style={{
            width: 44, height: 44, borderRadius: 12, flex: '0 0 44px',
            background: `color-mix(in oklch, ${m.color} 12%, white)`,
            border: `1px solid color-mix(in oklch, ${m.color} 22%, white)`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <PFIcon name={m.icon} size={20} color={m.color} />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontFamily: 'var(--pf-serif)', fontSize: 17, fontWeight: 700, lineHeight: 1.25, textDecoration: task.done ? 'line-through' : 'none' }}>{task.title}</div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 8 }}>
              <span className="pf-chip" style={{ fontSize: 10.5 }}>
                <PFIcon name="clock" size={11} color="var(--pf-green-ink)" /> {task.due}
              </span>
              {task.client && <span className="pf-chip pf-chip-neutral" style={{ fontSize: 10.5 }}>{task.client}</span>}
              {task.assignee && <span className="pf-chip pf-chip-blue" style={{ fontSize: 10.5 }}>→ {task.assignee.replace(' (me)', '')}</span>}
              {task.done && <span className="pf-chip" style={{ fontSize: 10.5, background: 'var(--pf-green)', color: '#fff' }}>✓ Done</span>}
            </div>
          </div>
        </div>

        {/* Lead context — only shown when this task actually has one linked;
            previously always rendered via a `|| PF_DATA.leads[0]` fallback,
            so a task with no lead (e.g. "Linked lead: None" at creation)
            showed a random unrelated lead as if genuinely attached. */}
        {lead && (
        <div className="pf-card-tinted" style={{ marginBottom: 14, display: 'flex', gap: 10, alignItems: 'center', cursor: 'pointer' }}
          onClick={() => go('lead', lead.id)}>
          <div className="pf-avatar" style={{ width: 36, height: 36, fontSize: 13 }}>{lead.initials}</div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 600, fontSize: 13.5 }}>{lead.name}</div>
            <div style={{ fontSize: 11.5, color: 'var(--pf-ink-3)', marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {lead.project} · {lead.budget}
            </div>
          </div>
          <PFIcon name="chevR" size={16} color="var(--pf-green)" />
        </div>
        )}

        {/* Steps */}
        <SectionTitle title="Steps" />
        <div style={{ position: 'relative', paddingLeft: 22, marginBottom: 14 }}>
          <div style={{ position: 'absolute', left: 9, top: 8, bottom: 8, width: 1, background: 'var(--pf-line)' }} />
          {m.steps.map((s, i) => (
            <div key={i} style={{ marginBottom: 10, position: 'relative', cursor: 'pointer' }}
              onClick={() => setStep(i)}>
              <div style={{
                position: 'absolute', left: -18, top: 2, width: 18, height: 18, borderRadius: '50%',
                background: i <= step ? 'var(--pf-green)' : '#fff',
                border: `2px solid ${i <= step ? 'var(--pf-green)' : 'var(--pf-line)'}`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 9, fontWeight: 700, color: i <= step ? '#fff' : 'var(--pf-ink-4)',
              }}>{i < step ? <PFIcon name="check" size={10} color="#fff" strokeWidth={3} /> : i + 1}</div>
              <div style={{
                fontSize: 13, fontWeight: i === step ? 700 : 500,
                color: i < step ? 'var(--pf-ink-3)' : 'var(--pf-ink)',
                textDecoration: i < step ? 'line-through' : 'none',
                lineHeight: 1.4, paddingTop: 1,
              }}>{s}</div>
            </div>
          ))}
          {step < m.steps.length - 1 && !task.done && (
            <button className="pf-btn pf-btn-light pf-btn-sm" style={{ marginTop: 6 }}
              onClick={() => setStep(s => Math.min(m.steps.length - 1, s + 1))}>
              Advance step →
            </button>
          )}
        </div>

        {/* Quick notes (controlled, persisted on blur) */}
        <div className="pf-field">
          <div className="pf-label">Notes for this task</div>
          <textarea className="pf-textarea"
            value={notes}
            onChange={e => setNotes(e.target.value)}
            onBlur={flushNotes}
            placeholder="Any context, reminders, or client-specific notes…" />
        </div>

        {/* Reminder + reassign */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
          <button className="pf-btn pf-btn-light pf-btn-sm" style={{ flex: 1 }} onClick={handleSnooze} disabled={task.done}>
            <PFIcon name="clock" size={12} color="var(--pf-green)" /> Snooze 1h
          </button>
          <button className="pf-btn pf-btn-light pf-btn-sm" style={{ flex: 1 }} onClick={handleReschedule} disabled={task.done}>
            <PFIcon name="calendar" size={12} color="var(--pf-green)" /> Reschedule
          </button>
          <button className="pf-btn pf-btn-light pf-btn-sm" style={{ flex: 1 }} onClick={handleReassign} disabled={task.done}>
            <PFIcon name="share" size={12} color="var(--pf-green)" /> Reassign
          </button>
        </div>

        <button className="pf-btn pf-btn-block" onClick={() => { flushNotes(); m.go(); }} disabled={task.done}>
          <PFIcon name={m.icon} size={14} color="#fff" /> {m.cta}
        </button>
        {!task.done && !task.cancelled ? (
          <div style={{ display:'flex', gap:8, marginTop:8 }}>
            <button className="pf-btn pf-btn-ghost pf-btn-block" style={{ flex:2 }} onClick={handleMarkDone}>
              <PFIcon name="check" size={14} color="var(--pf-green)" /> Mark as done
            </button>
            <button onClick={handleCancel} style={{ flex:1, padding:'12px', borderRadius:12, background:'#fef2f2', border:'1px solid #fca5a5', color:'#dc2626', fontWeight:700, fontSize:13, cursor:'pointer', fontFamily:'inherit' }}>
              Cancel
            </button>
          </div>
        ) : (
          <div>
            {task.awaitingVerification && (
              <div style={{ background:'#fef9c3', border:'1px solid #fde047', borderRadius:10, padding:'8px 12px', marginTop:8, fontSize:12.5, color:'#854d0e', fontWeight:600 }}>
                ⏳ Awaiting manager verification
              </div>
            )}
            {task.cancelled && (
              <div style={{ background:'#fef2f2', border:'1px solid #fca5a5', borderRadius:10, padding:'8px 12px', marginTop:8, fontSize:12.5, color:'#dc2626', fontWeight:600 }}>
                ✕ Task cancelled
              </div>
            )}
            <button className="pf-btn pf-btn-ghost pf-btn-block" style={{ marginTop: 8 }} onClick={handleReopen}>
              <PFIcon name="retry" size={14} color="var(--pf-green)" /> Reopen task
            </button>
          </div>
        )}
      </div>

      {toast && (
        <div style={{
          position: 'absolute', bottom: 80, left: '50%', transform: 'translateX(-50%)',
          background: 'var(--pf-ink)', color: '#fff', padding: '10px 18px', borderRadius: 22,
          fontSize: 12.5, fontWeight: 600, boxShadow: 'var(--pf-shadow)', zIndex: 50,
          whiteSpace: 'nowrap',
        }}>{toast}</div>
      )}
    </div>
  );
};

Object.assign(window, { PFScreenTaskDetail });



// ===== 13-pf-login.jsx =====
/* global React */

const PFScreenLogin = ({ onLogin, platform }) => {
  const [email, setEmail] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [showPass, setShowPass] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [pkLoading, setPkLoading] = React.useState(false);
  const [loginError, setLoginError] = React.useState('');
  const passkeySupported = !!(window.PublicKeyCredential);

  const handlePasskeyLogin = async () => {
    if (!passkeySupported) return;
    setPkLoading(true);
    setLoginError('');
    try {
      const base = (window.SRAPI && window.SRAPI._base) || 'http://localhost:3001/api';
      // 1. Get options
      const optRes = await fetch(`${base}/auth/passkey/login-options`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email || undefined }),
      });
      if (!optRes.ok) throw new Error('Could not start passkey login');
      const options = await optRes.json();

      // 2. Decode challenge for native credential API
      const challengeBytes = Uint8Array.from(atob(options.challenge.replace(/-/g,'+').replace(/_/g,'/')), c => c.charCodeAt(0));
      const allowList = (options.allowCredentials || []).map(c => ({
        ...c,
        id: Uint8Array.from(atob(c.id.replace(/-/g,'+').replace(/_/g,'/')), ch => ch.charCodeAt(0)),
      }));

      const assertion = await navigator.credentials.get({
        publicKey: {
          challenge: challengeBytes,
          rpId: options.rpId,
          allowCredentials: allowList,
          userVerification: options.userVerification || 'preferred',
          timeout: options.timeout || 60000,
        }
      });

      // 3. Encode response
      const toBase64url = buf => btoa(String.fromCharCode(...new Uint8Array(buf))).replace(/\+/g,'-').replace(/\//g,'_').replace(/=/g,'');
      const credential = {
        id: assertion.id,
        rawId: toBase64url(assertion.rawId),
        type: assertion.type,
        response: {
          authenticatorData: toBase64url(assertion.response.authenticatorData),
          clientDataJSON: toBase64url(assertion.response.clientDataJSON),
          signature: toBase64url(assertion.response.signature),
          userHandle: assertion.response.userHandle ? toBase64url(assertion.response.userHandle) : null,
        },
      };

      // 4. Verify
      const verRes = await fetch(`${base}/auth/passkey/login-verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ credential }),
      });
      if (!verRes.ok) throw new Error((await verRes.json().catch(() => ({}))).error || 'Passkey login failed');
      const data = await verRes.json();

      // Store tokens in SRAPI. The refresh token must be saved too — without
      // it, _tryRefresh() has nothing to redeem once the short-lived access
      // token expires, and every request after that throws a guaranteed
      // "Session expired" even though the passkey login itself succeeded.
      if (window.SRAPI) {
        window.SRAPI._token = data.accessToken;
        try {
          localStorage.setItem('sr_access_token', data.accessToken);
          if (data.refreshToken) localStorage.setItem('sr_refresh_token', data.refreshToken);
          if (data.user) localStorage.setItem('sr_user', JSON.stringify(data.user));
          if (data.industry) localStorage.setItem('sr_industry', data.industry);
          if (data.role) localStorage.setItem('sr_role', data.role);
          if (data.workspaceId) {
            localStorage.setItem('workspace_id', data.workspaceId);
            window.SRAPI._workspaceId = data.workspaceId;
          }
        } catch {}
        await window.SRAPI.sync();
        const ctx = window.SRAPI.getIndustryContext();
        setPkLoading(false);
        onLogin(ctx);
      } else {
        setPkLoading(false);
        onLogin({ industry: data.industry || 'realty', role: data.role || 'sales_executive' });
      }
    } catch (err) {
      if (err && err.name !== 'NotAllowedError') setLoginError(err.message || 'Passkey login failed');
      setPkLoading(false);
    }
  };

  const handleLogin = async () => {
    if (!email || !password) return;
    setLoading(true);
    setLoginError('');
    try {
      // Try live backend first; fall back to prototype mode if unreachable
      if (window.SRAPI) {
        await window.SRAPI.login(email, password);
        await window.SRAPI.sync();
        // Backend resolved industry + role from email-domain workspace config.
        // Pass the context up so the App skips the manual industry picker.
        const ctx = window.SRAPI.getIndustryContext();
        setLoading(false);
        onLogin(ctx);  // ctx = { industry, role } or null
        return;
      }
      setLoading(false);
      onLogin(null); // prototype mode — no context, show picker
    } catch (err) {
      setLoading(false);
      // A genuine auth failure (wrong password, unknown email — 401/403)
      // must show a real error, not silently swap in fake demo data with
      // zero indication anything went wrong. Only fall back to prototype
      // mode for connectivity problems (network down, CORS, backend
      // unreachable) where there was never a real credential to check.
      if (err && (err.status === 401 || err.status === 403)) {
        setLoginError(err.message || 'Incorrect email or password.');
        return;
      }
      console.warn('[SRAPI] Entering prototype mode:', err?.message ?? 'unknown error');
      onLogin(null); // prototype mode — show manual industry picker
    }
  };

  const handleKey = (e) => { if (e.key === 'Enter') handleLogin(); };

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {/* ── Hero ──
          Was a navy-blue gradient (#0C1A2E→#1B3D61) with a gold accent that
          matched neither this app's own --pf-green brand color nor the
          native app's login screen (dark green + gold). Rebuilt on
          --pf-green (#0f5132) so this, the native pre-auth screen, and
          every other green CTA/link in this same app are one consistent
          identity instead of three different color schemes. */}
      <div style={{
        background: 'linear-gradient(160deg, #0b3a24 0%, #0f5132 55%, #14603d 100%)',
        paddingTop: platform === 'ios' ? 'calc(24px + env(safe-area-inset-top))' : '24px',
        paddingLeft: 22, paddingRight: 22, paddingBottom: 22,
        position: 'relative', overflow: 'hidden', flexShrink: 0,
      }}>
        {/* dot-grid */}
        <div style={{
          position: 'absolute', inset: 0, pointerEvents: 'none',
          backgroundImage: 'radial-gradient(rgba(255,255,255,.55) 1px, transparent 1px)',
          backgroundSize: '22px 22px', opacity: .09,
        }} />
        {/* Logo */}
        <div style={{ position: 'relative', display: 'flex', alignItems: 'center', marginBottom: 22 }}>
          <img
            src="./skaledesk-logo-white.png?v=2"
            alt="SkaleDesk"
            style={{ height: 32, width: 'auto', objectFit: 'contain' }}
          />
        </div>
        {/* Headline */}
        <div style={{ position: 'relative', marginBottom: 18 }}>
          <div style={{ fontFamily: 'var(--pf-serif)', fontSize: 25, fontWeight: 600, lineHeight: 1.12, color: '#fff', letterSpacing: '-.5px', marginBottom: 8 }}>
            Close deals faster,<br/>with <span style={{ fontStyle: 'italic', color: '#E2C07A' }}>full clarity</span>
          </div>
          <div style={{ fontSize: 13, color: 'rgba(255,255,255,.48)', lineHeight: 1.55 }}>
            Telecalling, pipeline & site visits — all in one app.
          </div>
        </div>
        {/* Stats strip */}
        <div style={{ position: 'relative', display: 'flex', gap: 8 }}>
          {[
            { label: 'Pipeline', value: '₹3.2 Cr', sub: 'Active value' },
            { label: 'Hot leads', value: '12', sub: '4 pending visits' },
            { label: 'Avg response', value: '18 min', sub: 'Call SLA' },
          ].map(s => (
            <div key={s.label} style={{
              flex: 1,
              background: 'rgba(255,255,255,.05)', border: '1px solid rgba(255,255,255,.09)',
              borderRadius: 10, padding: '8px 9px',
            }}>
              <div style={{ fontSize: 9, color: 'rgba(255,255,255,.38)', textTransform: 'uppercase', letterSpacing: '.1em' }}>{s.label}</div>
              <div style={{ fontFamily: 'var(--pf-serif)', fontSize: 16, color: '#fff', fontWeight: 600, lineHeight: 1.15, marginTop: 3 }}>{s.value}</div>
              <div style={{ fontSize: 9.5, color: 'rgba(255,255,255,.42)', marginTop: 2 }}>{s.sub}</div>
            </div>
          ))}
        </div>
      </div>

      {/* ── Form ── */}
      <div style={{ flex: 1, background: '#fff', padding: '24px 20px 20px', overflowY: 'auto' }}>
        <div style={{ marginBottom: 22 }}>
          <div style={{ fontFamily: 'var(--pf-serif)', fontSize: 22, fontWeight: 600, color: '#0b3a24', letterSpacing: '-.4px', lineHeight: 1.1 }}>
            Welcome back
          </div>
          <div style={{ fontSize: 13.5, color: '#6B7280', marginTop: 5 }}>Sign in to your account</div>
        </div>

        {/* Google SSO */}
        <button onClick={() => {
          const base = (window.SRAPI && window.SRAPI._base) || 'https://api.skaledesk.com';
          window.location.href = base + '/api/auth/oauth/google';
        }} style={{
          width: '100%', padding: '12px 16px', border: '1.5px solid #E5E7EB', borderRadius: 12,
          background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
          gap: 10, fontFamily: 'var(--pf-sans)', fontWeight: 600, fontSize: 14,
          color: '#374151', cursor: 'pointer', marginBottom: 10, boxSizing: 'border-box',
        }}>
          <svg width="18" height="18" viewBox="0 0 18 18">
            <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.875 2.684-6.615z" fill="#4285F4"/>
            <path d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z" fill="#34A853"/>
            <path d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332z" fill="#FBBC05"/>
            <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z" fill="#EA4335"/>
          </svg>
          Continue with Google
        </button>

        {/* Passkey */}
        {passkeySupported && (
          <button onClick={handlePasskeyLogin} disabled={pkLoading} style={{
            width: '100%', padding: '12px 16px', border: '1.5px solid #D1FAE5', borderRadius: 12,
            background: pkLoading ? '#f0fdf4' : 'linear-gradient(135deg,#f0fdf4 0%,#dcfce7 100%)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            gap: 10, fontFamily: 'var(--pf-sans)', fontWeight: 600, fontSize: 14,
            color: '#15803d', cursor: pkLoading ? 'wait' : 'pointer', marginBottom: 16, boxSizing: 'border-box',
            boxShadow: '0 1px 4px rgba(21,128,61,0.08)',
          }}>
            {pkLoading ? (
              <>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                  <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83">
                    <animateTransform attributeName="transform" type="rotate" from="0 12 12" to="360 12 12" dur="0.75s" repeatCount="indefinite"/>
                  </path>
                </svg>
                Verifying…
              </>
            ) : (
              <>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 10a2 2 0 1 0 0-4 2 2 0 0 0 0 4z"/>
                  <path d="M12 10v2"/>
                  <path d="M10.5 16l-1-3h5l-1 3"/>
                  <path d="M8 20.5A8.5 8.5 0 1 1 20.5 12"/>
                  <path d="M16 16l2 2 4-4"/>
                </svg>
                Sign in with Passkey · Face ID / Fingerprint
              </>
            )}
          </button>
        )}

        {/* Divider */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
          <div style={{ flex: 1, height: 1, background: '#E5E7EB' }} />
          <span style={{ fontSize: 12, color: '#9CA3AF', fontWeight: 500 }}>or sign in with email</span>
          <div style={{ flex: 1, height: 1, background: '#E5E7EB' }} />
        </div>

        {/* Email */}
        <div style={{ marginBottom: 12 }}>
          <label style={{ display: 'block', fontSize: 11.5, fontWeight: 600, color: '#374151', marginBottom: 6, letterSpacing: '.04em', textTransform: 'uppercase' }}>
            Work Email
          </label>
          <input
            type="email" placeholder="you@company.com"
            value={email} onChange={e => setEmail(e.target.value)} onKeyDown={handleKey}
            style={{
              display: 'block', width: '100%', padding: '12px 14px',
              border: '1.5px solid #E5E7EB', borderRadius: 12,
              fontFamily: 'var(--pf-sans)', fontSize: 15, color: '#111827',
              background: '#fff', outline: 'none', boxSizing: 'border-box',
            }}
          />
        </div>

        {/* Password */}
        <div style={{ marginBottom: 8 }}>
          <label style={{ display: 'block', fontSize: 11.5, fontWeight: 600, color: '#374151', marginBottom: 6, letterSpacing: '.04em', textTransform: 'uppercase' }}>
            Password
          </label>
          <div style={{ position: 'relative' }}>
            <input
              type={showPass ? 'text' : 'password'} placeholder="••••••••"
              value={password} onChange={e => setPassword(e.target.value)} onKeyDown={handleKey}
              style={{
                display: 'block', width: '100%', padding: '12px 44px 12px 14px',
                border: '1.5px solid #E5E7EB', borderRadius: 12,
                fontFamily: 'var(--pf-sans)', fontSize: 15, color: '#111827',
                background: '#fff', outline: 'none', boxSizing: 'border-box',
              }}
            />
            <button onClick={() => setShowPass(p => !p)} style={{
              position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)',
              background: 'none', border: 'none', cursor: 'pointer', color: '#9CA3AF', padding: 4, lineHeight: 0,
            }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                {showPass
                  ? <><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/><path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/><line x1="1" y1="1" x2="23" y2="23"/></>
                  : <><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></>
                }
              </svg>
            </button>
          </div>
        </div>

        {/* Forgot — deep-links to the real password-reset flow on
            work.skaledesk.com (this mobile app has no reset flow of its
            own). Previously had no onClick at all. */}
        <div style={{ textAlign: 'right', marginBottom: 20 }}>
          <span
            onClick={() => {
              const url = 'https://work.skaledesk.com/forgot' + (email ? '?email=' + encodeURIComponent(email) : '');
              window.open(url, '_blank');
            }}
            style={{ fontSize: 13, color: '#0b3a24', fontWeight: 600, cursor: 'pointer' }}>Forgot password?</span>
        </div>

        {/* Inline error */}
        {loginError && (
          <div style={{
            marginBottom: 12, padding: '10px 14px', borderRadius: 10,
            background: '#fef2f2', border: '1px solid #fecaca',
            fontSize: 13, color: '#b91c1c', lineHeight: 1.45,
          }}>
            {loginError}
          </div>
        )}

        {/* CTA */}
        <button onClick={handleLogin} style={{
          width: '100%', padding: '14px 16px', boxSizing: 'border-box',
          background: (!email || !password) ? '#9aa29d' : 'var(--pf-green)',
          color: '#fff', border: 'none', borderRadius: 12,
          fontFamily: 'var(--pf-sans)', fontWeight: 600, fontSize: 15,
          cursor: (!email || !password) ? 'default' : 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
          transition: 'background .15s',
        }}>
          {loading
            ? <><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"><animateTransform attributeName="transform" type="rotate" from="0 12 12" to="360 12 12" dur="0.75s" repeatCount="indefinite"/></path></svg>Signing in…</>
            : 'Sign In →'
          }
        </button>

        {/* Footer */}
        <div style={{ textAlign: 'center', marginTop: 20, fontSize: 13, color: '#6B7280' }}>
          New here?{' '}
          <span style={{ color: 'var(--pf-green)', fontWeight: 600, cursor: 'pointer' }}>Contact your admin</span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'center', gap: 20, marginTop: 22, paddingBottom: 4 }}>
          {['8,400+ agents', '₹42 Cr closed', '4.9 ★'].map(t => (
            <span key={t} style={{ fontSize: 11, color: '#9CA3AF', fontWeight: 500 }}>{t}</span>
          ))}
        </div>
      </div>
    </div>
  );
};

Object.assign(window, { PFScreenLogin });



// ===== 14-pf-industry-select.jsx =====
/* global React */

const INDUSTRY_META = [
  { id: 'realestate',    label: 'Real Estate',    icon: '🏠', color: '#0C1A2E',
    roles: ['Sales Head','Sales Executive','Pre-Sales','Manager','Admin'],
    desc: 'Leads, pipeline & site visits' },
  { id: 'skalecap',      label: 'SkaleCap ILIS',  icon: '🧠', color: '#1E3A5F',
    roles: ['Participant','Manager','HR / L&D','Admin'],
    desc: '7-intelligence assessments & learning paths' },
  { id: 'healthcare',    label: 'Healthcare',      icon: '🏥', color: '#0369a1',
    roles: ['Front Desk','Doctor','Nurse','Manager','Admin'],
    desc: 'Patients, schedules & care tasks' },
  { id: 'dermatology',   label: 'Dermatology',     icon: '✨', color: '#7c3aed',
    roles: ['Front Desk','Therapist','Dermatologist','Manager'],
    desc: 'Clients, treatments & gallery' },
  { id: 'manufacturing', label: 'Manufacturing',   icon: '🏭', color: '#b45309',
    roles: ['Operator','Supervisor','QC Inspector','Manager'],
    desc: 'Production, quality & maintenance' },
];

const PFIndustrySelector = ({ onSelect }) => {
  const [step, setStep] = React.useState('industry'); // 'industry' | 'role'
  const [chosenInd, setChosenInd] = React.useState(null);

  if (step === 'role') {
    const meta = INDUSTRY_META.find(i => i.id === chosenInd);
    return (
      <div style={{ height:'100%', display:'flex', flexDirection:'column', background:'#f9fafb' }}>
        <div style={{ padding:'20px 20px 14px', background:'#fff', borderBottom:'1px solid #e5e7eb' }}>
          <button onClick={() => setStep('industry')}
            style={{ border:'none', background:'none', color:'#6b7280', fontSize:13, cursor:'pointer', padding:0, marginBottom:10, display:'flex', alignItems:'center', gap:4 }}>
            ← Back
          </button>
          <div style={{ fontSize:22, fontWeight:800, color:'#111827' }}>{meta.icon} {meta.label}</div>
          <div style={{ fontSize:12, color:'#6b7280', marginTop:4 }}>Select your role to continue</div>
        </div>
        <div style={{ flex:1, overflow:'auto', padding:'16px 16px 32px' }}>
          {meta.roles.map(role => (
            <div key={role} onClick={() => onSelect(chosenInd, role)}
              style={{ background:'#fff', borderRadius:14, padding:'16px 18px', marginBottom:10, cursor:'pointer',
                border:`2px solid ${meta.color}22`, boxShadow:'0 1px 6px rgba(0,0,0,0.05)',
                display:'flex', alignItems:'center', justifyContent:'space-between',
                transition:'all .15s' }}>
              <div>
                <div style={{ fontWeight:700, fontSize:16, color:'#111827' }}>{role}</div>
                <div style={{ fontSize:11.5, color:'#9ca3af', marginTop:3 }}>{meta.label} · Tap to enter</div>
              </div>
              <div style={{ width:36, height:36, borderRadius:10, background:meta.color+'18',
                display:'flex', alignItems:'center', justifyContent:'center',
                color:meta.color, fontWeight:800, fontSize:18 }}>›</div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div style={{ height:'100%', display:'flex', flexDirection:'column', background:'#f9fafb' }}>
      <div style={{ padding:'28px 20px 16px', background:'#fff', borderBottom:'1px solid #e5e7eb' }}>
        <div style={{ fontSize:11, fontWeight:700, color:'#9ca3af', letterSpacing:'0.12em', textTransform:'uppercase', marginBottom:6 }}>Prototype Mode</div>
        <div style={{ fontSize:26, fontWeight:800, color:'#111827', lineHeight:1.2 }}>Choose Your{'\n'}Industry</div>
        <div style={{ fontSize:12.5, color:'#6b7280', marginTop:8 }}>In production, this screen is skipped — industry &amp; role are resolved automatically from your email domain.</div>
      </div>
      <div style={{ flex:1, overflow:'auto', padding:'20px 16px 40px' }}>
        {INDUSTRY_META.map(ind => (
          <div key={ind.id} onClick={() => { setChosenInd(ind.id); setStep('role'); }}
            style={{ background:'#fff', borderRadius:18, padding:'18px 18px', marginBottom:14, cursor:'pointer',
              boxShadow:'0 2px 12px rgba(0,0,0,0.07)', border:`2px solid ${ind.color}18` }}>
            <div style={{ display:'flex', alignItems:'center', gap:14 }}>
              <div style={{ width:56, height:56, borderRadius:16, background:ind.color+'16',
                display:'flex', alignItems:'center', justifyContent:'center', fontSize:28, flexShrink:0 }}>
                {ind.icon}
              </div>
              <div style={{ flex:1 }}>
                <div style={{ fontWeight:800, fontSize:17, color:'#111827' }}>{ind.label}</div>
                <div style={{ fontSize:12, color:'#6b7280', marginTop:3 }}>{ind.desc}</div>
                <div style={{ display:'flex', flexWrap:'wrap', gap:4, marginTop:8 }}>
                  {ind.roles.slice(0,3).map(r => (
                    <span key={r} style={{ fontSize:10.5, fontWeight:600, background:ind.color+'14', color:ind.color,
                      borderRadius:20, padding:'2px 8px' }}>{r}</span>
                  ))}
                  {ind.roles.length > 3 && <span style={{ fontSize:10.5, color:'#9ca3af' }}>+{ind.roles.length-3} more</span>}
                </div>
              </div>
              <div style={{ color:'#d1d5db', fontSize:22 }}>›</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

Object.assign(window, { PFIndustrySelector, INDUSTRY_META });


// ===== 15-industry-kit.jsx =====
/* global React */
// Line-art icons used by Healthcare/Dermatology/Manufacturing blocks.
// Each emoji in those blocks was replaced with IndIcon('<name>') at build time.
const _IND_PATHS = {
  home:    <><path d="M3 11l9-7 9 7v9a1 1 0 01-1 1h-5v-7H10v7H5a1 1 0 01-1-1v-9z" strokeLinejoin="round"/></>,
  team:    <><circle cx="9" cy="9" r="3"/><circle cx="17" cy="10" r="2.4"/><path d="M3 19c.5-3 2.7-5 6-5s5.5 2 6 5M15 19c.4-2 1.7-3 4-3s3.2 1 3.5 3"/></>,
  person:  <><circle cx="12" cy="8.5" r="3.5"/><path d="M5 20c.7-3.5 3.5-6 7-6s6.3 2.5 7 6"/></>,
  msg:     <><path d="M4 5a2 2 0 012-2h12a2 2 0 012 2v9a2 2 0 01-2 2H9l-5 4V5z" strokeLinejoin="round"/></>,
  cal:     <><rect x="3" y="5" width="18" height="16" rx="2"/><path d="M3 10h18M8 3v4M16 3v4"/></>,
  search:  <><circle cx="11" cy="11" r="7"/><path d="M20.5 20.5L17 17" strokeLinecap="round"/></>,
  alert:   <><path d="M12 3l10 18H2L12 3z" strokeLinejoin="round"/><path d="M12 10v5M12 18v.5" strokeLinecap="round"/></>,
  warn:    <><path d="M12 3l10 18H2L12 3z" strokeLinejoin="round"/><path d="M12 10v5M12 18v.5" strokeLinecap="round"/></>,
  bolt:    <><path d="M13 2L4 13h6l-1 9 9-12h-6l1-8z" strokeLinejoin="round"/></>,
  check:   <><circle cx="12" cy="12" r="9"/><path d="M8 12l3 3 5-6" strokeLinecap="round" strokeLinejoin="round"/></>,
  plus:    <><path d="M12 5v14M5 12h14" strokeLinecap="round"/></>,
  arrowR:  <><path d="M5 12h14M13 6l6 6-6 6" strokeLinecap="round" strokeLinejoin="round"/></>,
  pin:     <><path d="M12 21s7-7 7-12a7 7 0 10-14 0c0 5 7 12 7 12z"/><circle cx="12" cy="9" r="2.5"/></>,
  box:     <><path d="M3 7l9-4 9 4-9 4-9-4z"/><path d="M3 7v10l9 4 9-4V7"/><path d="M12 11v10"/></>,
  retry:   <><path d="M1 4v6h6M23 20v-6h-6"/><path d="M20.5 9A9 9 0 005.2 5.2L1 10M23 14l-4.2 4.8A9 9 0 013.5 15" strokeLinecap="round"/></>,
  wrench:  <><path d="M14.7 6.3a4 4 0 015.6 5.6L13 19.2 4.8 11l7.3-7.3a4 4 0 015.6 5.6" strokeLinejoin="round"/><circle cx="6.5" cy="17.5" r="1.5" fill="currentColor"/></>,
  clock:   <><circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2" strokeLinecap="round"/></>,
  heart:   <><path d="M12 20s-7-4.5-7-10a4 4 0 017-2.5A4 4 0 0119 10c0 5.5-7 10-7 10z" strokeLinejoin="round"/></>,
  star:    <><path d="M12 3l2.6 6 6.4.6-4.8 4.3 1.4 6.3L12 17l-5.6 3.2 1.4-6.3L3 9.6l6.4-.6L12 3z" strokeLinejoin="round"/></>,
  image:   <><rect x="3" y="5" width="18" height="14" rx="2"/><circle cx="9" cy="11" r="2"/><path d="M3 17l5-4 4 3 4-3 5 4"/></>,
  face:    <><circle cx="12" cy="12" r="9"/><circle cx="9" cy="10" r=".9" fill="currentColor"/><circle cx="15" cy="10" r=".9" fill="currentColor"/><path d="M8.5 14.5c.8 1.2 2 2 3.5 2s2.7-.8 3.5-2"/></>,
  bell:    <><path d="M6 9a6 6 0 0112 0v4l1.5 2.5h-15L6 13V9z"/><path d="M9.5 19a2.5 2.5 0 005 0"/></>,
  send:    <><path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z" strokeLinejoin="round"/></>,
};
function IndIcon(name, opts) {
  const { size = 16, c = 'currentColor', sw = 1.8 } = opts || {};
  return React.createElement('svg', {
    width: size, height: size, viewBox: '0 0 24 24', fill: 'none',
    stroke: c, strokeWidth: sw, strokeLinecap: 'round', strokeLinejoin: 'round',
    style: { display: 'inline-block', verticalAlign: 'middle', flexShrink: 0 },
  }, _IND_PATHS[name] || null);
}
window.IndIcon = IndIcon;
window.IND_FONT = "'Lato', -apple-system, system-ui, sans-serif";

/* ─────────────────────────────────────────────────────────────
 * IND_TASK_STORE  — overlay for industry task lists (HC / Derma / MFG).
 *   keyed by `${ns}:${id}` so namespaces don't collide.
 *   shape:   { done, completedAt, due, snoozedFrom, rescheduledFrom, assignee, notes, _updatedAt }
 *   persisted to localStorage('sr_ind_tasks_v1')
 *   dispatches DOM event 'sr:ind-task-updated'
 * ───────────────────────────────────────────────────────────── */
window.IND_TASK_STORE = window.IND_TASK_STORE || (() => {
  try { return JSON.parse(localStorage.getItem('sr_ind_tasks_v1') || '{}'); }
  catch (e) { return {}; }
})();
window.indTaskGet = function (ns, base) {
  const overlay = window.IND_TASK_STORE[ns + ':' + base.id] || {};
  // legacy boolean form: { __bool: true } means just a done flag from earlier code
  if (typeof overlay === 'boolean') return Object.assign({}, base, { done: overlay });
  return Object.assign({}, base, overlay);
};
window.indTaskPatch = function (ns, id, patch) {
  const key = ns + ':' + id;
  const cur = (typeof window.IND_TASK_STORE[key] === 'object') ? window.IND_TASK_STORE[key] : {};
  window.IND_TASK_STORE[key] = Object.assign({}, cur, patch, { _updatedAt: new Date().toISOString() });
  try { localStorage.setItem('sr_ind_tasks_v1', JSON.stringify(window.IND_TASK_STORE)); } catch (e) {}
  if (window.SRAPI && typeof window.SRAPI.updateTask === 'function') {
    try { window.SRAPI.updateTask(ns + ':' + id, window.IND_TASK_STORE[key]); } catch (e) {}
  }
  // Trickle notification when assignee changes
  if (patch.assignee && window.notifyChain) {
    // Find task title from created tasks
    const created = (window.IND_CREATED_TASKS[ns] || []).find(t => t.id === id);
    const title = (created && created.title) || id;
    window.notifyChain(ns, patch.assignee, title, 'task_assigned', patch.assignedBy || 'Manager');
  }
  // Notify when status → Done/Resolved
  if ((patch.status === 'Done' || patch.status === 'Resolved') && window.pushNotif) {
    const created = (window.IND_CREATED_TASKS[ns] || []).find(t => t.id === id);
    const title = (created && created.title) || id;
    const mgr = (window.ORG_HIERARCHY[ns] || {})._roles || {};
    const assignee = (created && created.assignee) || cur.assignee || '';
    const people = (window.ORG_HIERARCHY[ns] || {})._people || {};
    const role = people[assignee] || assignee;
    const manager = mgr[role];
    if (manager) window.pushNotif(ns, manager, { type:'task_done', icon:'✅', title, msg:`"${title}" marked ${patch.status} by ${assignee}`, from: assignee });
  }
  document.dispatchEvent(new CustomEvent('sr:ind-task-updated', { detail: { ns, id, patch, task: window.IND_TASK_STORE[key] } }));
};
window.indTaskDone = function (ns, id) {
  const v = window.IND_TASK_STORE[ns + ':' + id];
  if (typeof v === 'boolean') return v;
  return !!(v && v.done);
};
window.indTaskToggle = function (ns, id) {
  const next = !window.indTaskDone(ns, id);
  window.indTaskPatch(ns, id, { done: next, completedAt: next ? new Date().toISOString() : null });
};

// Bump a due-time forward N hours; tolerant of strings like "10:30 AM", "Today", "Tomorrow", "Now"
window.indBumpDue = function (due, hours) {
  const m = (due || '').match(/^(\d{1,2}):(\d{2})\s*(AM|PM)?$/i);
  if (!m) {
    // Non-clock strings: just append "+1h"
    return /\+\d+h$/.test(due) ? due.replace(/\+(\d+)h$/, (s, n) => `+${(+n) + hours}h`) : `${due} +${hours}h`;
  }
  let h = parseInt(m[1], 10), min = parseInt(m[2], 10);
  const ap = (m[3] || '').toUpperCase();
  if (ap === 'PM' && h < 12) h += 12;
  if (ap === 'AM' && h === 12) h = 0;
  h = (h + hours) % 24;
  const newAp = h >= 12 ? 'PM' : 'AM';
  let h12 = h % 12; if (h12 === 0) h12 = 12;
  return `${h12}:${String(min).padStart(2, '0')} ${newAp}`;
};

const IND_ASSIGNEES = ['Me', 'Dr. Mehra', 'Dr. Kapoor', 'Ria T.', 'Front Desk', 'Manager', 'Admin'];

/* ─────────────────────────────────────────────────────────────
 * IND_CREATED_TASKS  — user-added tasks, keyed by ns → array
 * NUDGE_STORE        — pending nudges, keyed by `${ns}:${role}` → array
 *   nudge = { id, taskId, from, message, sentAt, dismissed }
 * Both persisted to localStorage; events fired so any screen reacts.
 * ───────────────────────────────────────────────────────────── */
window.IND_CREATED_TASKS = window.IND_CREATED_TASKS || (() => {
  try { return JSON.parse(localStorage.getItem('sr_ind_created_v1') || '{}'); }
  catch (e) { return {}; }
})();
window.NUDGE_STORE = window.NUDGE_STORE || (() => {
  try { return JSON.parse(localStorage.getItem('sr_nudges_v1') || '{}'); }
  catch (e) { return {}; }
})();

window.addIndTask = function (ns, task) {
  const id = 'ut_' + Date.now().toString(36) + '_' + Math.random().toString(36).slice(2, 5);
  const record = Object.assign({ id, _createdAt: new Date().toISOString(), _created: true, status: task.status || 'Open' }, task);
  window.IND_CREATED_TASKS[ns] = (window.IND_CREATED_TASKS[ns] || []).concat([record]);
  try { localStorage.setItem('sr_ind_created_v1', JSON.stringify(window.IND_CREATED_TASKS)); } catch (e) {}
  // If task has an assignee, trickle notification
  if (record.assignee && !record.openToAll) {
    window.notifyChain && window.notifyChain(ns, record.assignee, record.title, 'task_assigned', record.assignedBy || 'System');
  }
  document.dispatchEvent(new CustomEvent('sr:ind-task-updated', { detail: { ns, id, patch: record, task: record } }));
  return record;
};
window.listIndCreated = function (ns) { return window.IND_CREATED_TASKS[ns] || []; };

window.sendNudge = function (ns, targetRole, taskId, message, from) {
  const key = ns + ':' + targetRole;
  const nudge = {
    id: 'n_' + Date.now().toString(36) + '_' + Math.random().toString(36).slice(2, 5),
    taskId, message: message || 'Please complete this task',
    from: from || 'Manager', sentAt: new Date().toISOString(), dismissed: false,
  };
  window.NUDGE_STORE[key] = (window.NUDGE_STORE[key] || []).concat([nudge]);
  try { localStorage.setItem('sr_nudges_v1', JSON.stringify(window.NUDGE_STORE)); } catch (e) {}
  document.dispatchEvent(new CustomEvent('sr:nudge', { detail: { ns, targetRole, nudge } }));
  return nudge;
};
window.getNudges = function (ns, role) {
  const arr = (window.NUDGE_STORE[ns + ':' + role] || []).filter(n => !n.dismissed);
  // Manager role sees nudges to all roles too if requested via 'Manager'
  return arr;
};
window.dismissNudge = function (ns, role, nudgeId) {
  const key = ns + ':' + role;
  const arr = window.NUDGE_STORE[key] || [];
  const next = arr.map(n => n.id === nudgeId ? Object.assign({}, n, { dismissed: true }) : n);
  window.NUDGE_STORE[key] = next;
  try { localStorage.setItem('sr_nudges_v1', JSON.stringify(window.NUDGE_STORE)); } catch (e) {}
  document.dispatchEvent(new CustomEvent('sr:nudge', { detail: { ns, targetRole: role, dismissed: nudgeId } }));
};

/* ─────────────────────────────────────────────────────────────
 *  ORG_HIERARCHY  — defines manager chain per namespace
 *  NOTIF_STORE    — in-app notifications per user
 *  CHAT_THREADS   — multi-thread chat store
 * ───────────────────────────────────────────────────────────── */
window.ORG_HIERARCHY = {
  realty: {
    _roles: { Agent:'Manager','Field Agent':'Manager','Senior Agent':'Manager','Manager':'Business Owner','Business Owner':null },
    _people: { 'Priya Menon':'Senior Agent','Rahul Singh':'Field Agent','Kavya Nair':'Agent','Deepak Iyer':'Agent','Ankit Sharma':'Manager' },
    _head: 'Business Owner',
  },
  hc: {
    _roles: { Nurse:'Doctor','Front Desk':'Manager',Doctor:'Manager',Manager:'Head',Head:null },
    _people: {}, _head:'Head',
  },
  derm: {
    _roles: { Therapist:'Manager',Dermatologist:'Manager','Front Desk':'Manager',Manager:'Head',Head:null },
    _people: {}, _head:'Head',
  },
  mfg: {
    _roles: { Technician:'Supervisor',Supervisor:'Manager',Manager:'Head',Head:null },
    _people: {}, _head:'Head',
  },
};

window.NOTIF_STORE = window.NOTIF_STORE || (() => {
  try { return JSON.parse(localStorage.getItem('sr_notifs_v1') || '{}'); } catch(e) { return {}; }
})();

window.pushNotif = function(ns, toUser, notif) {
  const key = ns + ':' + toUser;
  const rec = Object.assign({ id:'notif_'+Date.now().toString(36)+'_'+Math.random().toString(36).slice(2,4), sentAt:new Date().toISOString(), read:false }, notif);
  window.NOTIF_STORE[key] = (window.NOTIF_STORE[key] || []).concat([rec]);
  try { localStorage.setItem('sr_notifs_v1', JSON.stringify(window.NOTIF_STORE)); } catch(e) {}
  document.dispatchEvent(new CustomEvent('sr:notification', { detail: { ns, toUser, notif: rec } }));
  return rec;
};
window.getNotifs = function(ns, user) {
  return (window.NOTIF_STORE[ns + ':' + user] || []).filter(n => !n.read);
};
window.markNotifRead = function(ns, user, notifId) {
  const key = ns + ':' + user;
  window.NOTIF_STORE[key] = (window.NOTIF_STORE[key] || []).map(n => n.id === notifId ? Object.assign({},n,{read:true}) : n);
  try { localStorage.setItem('sr_notifs_v1', JSON.stringify(window.NOTIF_STORE)); } catch(e) {}
  document.dispatchEvent(new CustomEvent('sr:notification', { detail: { ns, toUser:user } }));
};
window.markAllNotifsRead = function(ns, user) {
  const key = ns + ':' + user;
  window.NOTIF_STORE[key] = (window.NOTIF_STORE[key] || []).map(n => Object.assign({},n,{read:true}));
  try { localStorage.setItem('sr_notifs_v1', JSON.stringify(window.NOTIF_STORE)); } catch(e) {}
  document.dispatchEvent(new CustomEvent('sr:notification', { detail: { ns, toUser:user } }));
};

// Notify assignee + walk up org chain
window.notifyChain = function(ns, assigneeName, taskTitle, type, assignedBy) {
  const hier = window.ORG_HIERARCHY[ns] || {};
  const people = hier._people || {};
  const roles  = hier._roles  || {};
  const role   = people[assigneeName] || assigneeName;
  let cur = roles[role];
  // Assignee
  window.pushNotif(ns, assigneeName, { type:'task_assigned', icon:'✓', title:taskTitle,
    msg:`Assigned to you by ${assignedBy||'Manager'}`, from:assignedBy||'Manager' });
  // Walk up chain (max 3 levels)
  let level = 0;
  while (cur && level < 3) {
    window.pushNotif(ns, cur, { type:'task_trickle', icon:'📋', title:taskTitle,
      msg:`Task assigned: "${taskTitle}" → ${assigneeName}`, from:'System', chain: level+1 });
    cur = roles[cur];
    level++;
  }
};

// Unified delayed/nudged task flags
window.isTaskDelayed = function(task) {
  if (task.done || task.status === 'Done' || task.status === 'Resolved') return false;
  if (!task.due && !task.dueDate) return false;
  const due = task.dueDate || task.due || '';
  if (due === 'Now' || due === 'Today') {
    // Consider "Today" overdue if it's been set and not done
    return !!(task._createdAt && (Date.now() - new Date(task._createdAt).getTime()) > 24*60*60*1000);
  }
  if (due.includes('Overdue') || due.includes('overdue')) return true;
  return false;
};

window.isTaskNearDeadline = function(task) {
  if (task.done || task.status === 'Done' || task.status === 'Resolved') return false;
  const due = task.dueDate || task.due || '';
  return due === 'Today' || due === 'Now' || due.includes('Today');
};

// Get dashboard task list with computed flags
window.getTaskDashboard = function(ns, viewRole) {
  const hier = window.ORG_HIERARCHY[ns] || {};
  const roles = hier._roles || {};
  const created = window.IND_CREATED_TASKS[ns] || [];
  // Merge with overlays
  const tasks = created.map(t => {
    const overlay = window.IND_TASK_STORE[ns + ':' + t.id] || {};
    const merged = Object.assign({}, t, overlay);
    const nudgeKey = ns + ':' + (merged.assignee || merged.role || '');
    const nudges = (window.NUDGE_STORE[nudgeKey] || []).filter(n => !n.dismissed && n.taskId === merged.id);
    return Object.assign(merged, {
      isDelayed:      window.isTaskDelayed(merged),
      isNudged:       nudges.length > 0,
      nudgeCount:     nudges.length,
      isNearDeadline: window.isTaskNearDeadline(merged),
    });
  });
  // For manager/owner: show all; for others: show own tasks
  if (viewRole === 'Manager' || viewRole === 'Business Owner' || viewRole === 'Head' || viewRole === 'Supervisor') return tasks;
  return tasks.filter(t => t.role === viewRole || t.assignee === viewRole);
};

/* ── Multi-thread chat store ─────────────────────────────────────────────────
 * Was purely client-side (window.CHAT_THREADS, a plain in-memory object) —
 * messages were never sent anywhere and vanished on app restart. Now
 * hydrates from and persists to the backend (migration 127), while keeping
 * this same synchronous local-cache shape so every industry screen that
 * already reads it (realty/healthcare/dermatology/manufacturing) keeps
 * working unmodified. The local dict key for the team thread stays the
 * literal 'team' (every screen hardcodes that), but its .id field gets
 * remapped to the real backend thread id once hydrated, so sends go to the
 * right place.
 * ───────────────────────────────────────────────────────────────────────── */
window.CHAT_THREADS = window.CHAT_THREADS || {};
window._chatHydrated = window._chatHydrated || {};
window.getChatThreads = function(ns, currentRole) {
  if (!window.CHAT_THREADS[ns]) {
    window.CHAT_THREADS[ns] = {
      'team': { id:'team', name:'Team', type:'group', icon:'👥', unread:0, messages:[] },
    };
  }
  if (!window._chatHydrated[ns] && window.SRAPI && typeof window.SRAPI.getChatThreads === 'function') {
    window._chatHydrated[ns] = true;
    window.SRAPI.getChatThreads().then(function (rows) {
      var myName = (window.SRAPI.getUser && window.SRAPI.getUser() || {}).name;
      (rows || []).forEach(function (r) {
        var key = r.type === 'team' ? 'team' : r.id;
        var existing = window.CHAT_THREADS[ns][key] || {};
        window.CHAT_THREADS[ns][key] = Object.assign({}, existing, {
          id: r.id,
          name: r.type === 'team' ? (existing.name || 'Team') : r.name,
          type: r.type === 'team' ? 'group' : 'dm',
          icon: existing.icon || (r.type === 'team' ? '👥' : '👤'),
          unread: existing.unread || 0,
          messages: (r.messages || []).map(function (m) {
            return Object.assign({}, m, { me: m.from === myName });
          }),
        });
      });
      document.dispatchEvent(new CustomEvent('sr:threads-updated', { detail: { ns } }));
    }).catch(function () {});
  }
  return window.CHAT_THREADS[ns];
};
window.addChatThread = function(ns, thread) {
  const threads = window.getChatThreads(ns);
  threads[thread.id] = thread;
  document.dispatchEvent(new CustomEvent('sr:threads-updated', { detail: { ns } }));
  // Persist the DM thread to the backend so it survives reload and the
  // other person can see it too — find-or-create is idempotent.
  if (thread.type === 'dm' && window.SRAPI && typeof window.SRAPI.createDmThread === 'function') {
    window.SRAPI.createDmThread(thread.name).then(function (real) {
      if (real && real.id && real.id !== thread.id) {
        threads[thread.id].id = real.id; // remap to the real backend id for sends
      }
    }).catch(function () {});
  }
  return thread;
};
window.addThreadMessage = function(ns, threadId, msg) {
  const threads = window.getChatThreads(ns);
  if (!threads[threadId]) return;
  // Persist outgoing (our own) messages — incoming ones arrive already
  // tagged _fromServer by the WS handler and shouldn't be re-sent.
  if (msg.me && !msg._fromServer && window.SRAPI && typeof window.SRAPI.sendChatMessage === 'function') {
    const realId = threads[threadId].id || threadId;
    window.SRAPI.sendChatMessage(realId, msg.msg, msg.tags, msg.cards).catch(function () {});
  }
  threads[threadId].messages = [...(threads[threadId].messages || []), msg];
  if (!msg.me) threads[threadId].unread = (threads[threadId].unread || 0) + 1;
  document.dispatchEvent(new CustomEvent('sr:threads-updated', { detail: { ns } }));
};

/* ─────────────────────────────────────────────────────────────
 *  IndTaskCreate — bottom-sheet form to add a new task
 *  Props: { ns, accent, roles, defaultRole, onClose, onCreate }
 * ───────────────────────────────────────────────────────────── */
window.IndTaskCreate = function IndTaskCreate({ ns, accent, roles, defaultRole, onClose, onCreate }) {
  const [title, setTitle]       = React.useState('');
  const [priority, setPriority] = React.useState('Medium');
  const [due, setDue]           = React.useState('Today');
  const [assignRole, setRole]   = React.useState(defaultRole || (roles && roles[0]) || 'Me');
  const [notify, setNotify]     = React.useState(true);
  const priorities = ['Low', 'Medium', 'High', 'Critical'];
  const dueOpts    = ['Now', 'Today', 'Tomorrow', 'This week', 'Next week'];

  const canSave = title.trim().length > 0;
  const save = () => {
    if (!canSave) return;
    const task = {
      title: title.trim(),
      priority,
      due,
      role: assignRole,
      _notify: notify,
    };
    const created = window.addIndTask(ns, task);
    if (onCreate) onCreate(created);
    onClose();
  };

  return (
    <div style={{ position:'absolute', inset:0, background:'rgba(15,20,30,0.45)', zIndex:200, display:'flex', alignItems:'flex-end' }} onClick={onClose}>
      <div onClick={e => e.stopPropagation()} style={{ width:'100%', background:'#fff', borderRadius:'18px 18px 0 0', padding:'18px 18px 22px', maxHeight:'88%', overflowY:'auto', boxShadow:'0 -4px 24px rgba(0,0,0,0.18)' }}>
        <div style={{ width:42, height:4, borderRadius:2, background:'#e5e7eb', margin:'0 auto 14px' }} />
        <div style={{ fontSize:17, fontWeight:800, color:'#111827', marginBottom:14 }}>New Task</div>

        {/* Title */}
        <div style={{ marginBottom:12 }}>
          <div style={{ fontSize:10.5, fontWeight:700, color:'#9ca3af', letterSpacing:'0.08em', textTransform:'uppercase', marginBottom:6 }}>Task</div>
          <input value={title} onChange={e => setTitle(e.target.value)} placeholder="What needs to be done?"
            style={{ width:'100%', background:'#fff', border:'1px solid #e5e7eb', borderRadius:10, padding:'11px 13px', fontSize:14, outline:'none', boxSizing:'border-box', fontFamily:'inherit', color:'#111827' }} autoFocus />
        </div>

        {/* Priority */}
        <div style={{ marginBottom:12 }}>
          <div style={{ fontSize:10.5, fontWeight:700, color:'#9ca3af', letterSpacing:'0.08em', textTransform:'uppercase', marginBottom:6 }}>Priority</div>
          <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
            {priorities.map(p => {
              const on = priority === p;
              const c = p === 'Critical' ? '#dc2626' : p === 'High' ? '#ca8a04' : p === 'Medium' ? accent : '#9ca3af';
              return (
                <button key={p} onClick={() => setPriority(p)} style={{
                  padding:'7px 14px', borderRadius:99, fontSize:12, fontWeight:700, cursor:'pointer', fontFamily:'inherit',
                  border:`1.5px solid ${on ? c : '#e5e7eb'}`, background: on ? c + '18' : '#fff', color: on ? c : '#6b7280',
                }}>{p}</button>
              );
            })}
          </div>
        </div>

        {/* Due */}
        <div style={{ marginBottom:12 }}>
          <div style={{ fontSize:10.5, fontWeight:700, color:'#9ca3af', letterSpacing:'0.08em', textTransform:'uppercase', marginBottom:6 }}>Due</div>
          <div style={{ display:'flex', gap:6, flexWrap:'wrap', marginBottom:8 }}>
            {dueOpts.map(d => {
              const on = due === d;
              return (
                <button key={d} onClick={() => setDue(d)} style={{
                  padding:'7px 12px', borderRadius:99, fontSize:12, fontWeight:600, cursor:'pointer', fontFamily:'inherit',
                  border:`1.5px solid ${on ? accent : '#e5e7eb'}`, background: on ? accent + '18' : '#fff', color: on ? accent : '#6b7280',
                }}>{d}</button>
              );
            })}
          </div>
          <input value={due} onChange={e => setDue(e.target.value)} placeholder="Or type a custom time (e.g. 10:30 AM)"
            style={{ width:'100%', background:'#fff', border:'1px solid #e5e7eb', borderRadius:10, padding:'9px 12px', fontSize:13, outline:'none', boxSizing:'border-box', fontFamily:'inherit', color:'#111827' }} />
        </div>

        {/* Assign role */}
        {(roles && roles.length > 0) && (
          <div style={{ marginBottom:12 }}>
            <div style={{ fontSize:10.5, fontWeight:700, color:'#9ca3af', letterSpacing:'0.08em', textTransform:'uppercase', marginBottom:6 }}>Assign to</div>
            <select value={assignRole} onChange={e => setRole(e.target.value)}
              style={{ width:'100%', background:'#fff', border:'1px solid #e5e7eb', borderRadius:10, padding:'10px 12px', fontSize:13.5, outline:'none', boxSizing:'border-box', fontFamily:'inherit', color:'#111827' }}>
              {roles.map(r => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>
        )}

        {/* Notify */}
        <div onClick={() => setNotify(n => !n)} style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:18, padding:'12px 14px', background:'#f9fafb', borderRadius:10, cursor:'pointer' }}>
          <div>
            <div style={{ fontSize:13, fontWeight:700, color:'#111827' }}>Send notification</div>
            <div style={{ fontSize:11.5, color:'#6b7280', marginTop:2 }}>Notify the assigned person right away</div>
          </div>
          <div style={{ width:38, height:22, borderRadius:99, background: notify ? accent : '#e5e7eb', position:'relative', transition:'background .2s' }}>
            <div style={{ position:'absolute', top:2, left: notify ? 18 : 2, width:18, height:18, borderRadius:'50%', background:'#fff', boxShadow:'0 1px 3px rgba(0,0,0,0.18)', transition:'left .2s' }} />
          </div>
        </div>

        {/* Actions */}
        <div style={{ display:'flex', gap:8 }}>
          <button onClick={onClose} style={{ flex:1, border:'1px solid #e5e7eb', background:'#fff', color:'#6b7280', borderRadius:10, padding:'12px', fontSize:13.5, fontWeight:700, cursor:'pointer', fontFamily:'inherit' }}>Cancel</button>
          <button onClick={save} disabled={!canSave} style={{ flex:2, border:'none', background: canSave ? accent : '#d1d5db', color:'#fff', borderRadius:10, padding:'12px', fontSize:13.5, fontWeight:700, cursor: canSave ? 'pointer' : 'default', fontFamily:'inherit' }}>Add task</button>
        </div>
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────
 *  IndNudgeBanner — shows pending nudges for the current role
 *  Props: { ns, role, accent }
 * ───────────────────────────────────────────────────────────── */
window.IndNudgeBanner = function IndNudgeBanner({ ns, role, accent }) {
  const [, force] = React.useReducer(x => x + 1, 0);
  React.useEffect(() => {
    const fn = (e) => { if (e.detail.ns === ns) force(); };
    document.addEventListener('sr:nudge', fn);
    return () => document.removeEventListener('sr:nudge', fn);
  }, [ns]);
  const nudges = window.getNudges(ns, role);
  if (!nudges.length) return null;
  return (
    <div style={{ padding:'10px 14px 0' }}>
      {nudges.slice(0, 3).map(n => (
        <div key={n.id} style={{ background: accent + '12', border:`1px solid ${accent}40`, borderRadius:12, padding:'10px 12px', marginBottom:8, display:'flex', alignItems:'flex-start', gap:10 }}>
          <div style={{ width:30, height:30, borderRadius:8, background: accent, color:'#fff', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
            {window.IndIcon('bell',{c:'#fff',size:15})}
          </div>
          <div style={{ flex:1, minWidth:0 }}>
            <div style={{ fontSize:11, fontWeight:700, color: accent, letterSpacing:'0.08em', textTransform:'uppercase' }}>Nudge from {n.from}</div>
            <div style={{ fontSize:13, fontWeight:600, color:'#111827', marginTop:2, lineHeight:1.35 }}>{n.message}</div>
          </div>
          <button onClick={() => window.dismissNudge(ns, role, n.id)} style={{ border:'none', background:'transparent', color: accent, fontSize:18, fontWeight:700, cursor:'pointer', padding:'0 4px' }}>×</button>
        </div>
      ))}
    </div>
  );
};


/* Hook returning [task, patcher] for an industry task — re-renders on store updates */
window.useIndTask = function (ns, baseTask) {
  const [task, setTask] = React.useState(() => window.indTaskGet(ns, baseTask));
  React.useEffect(() => {
    const onUpdate = (e) => {
      if (e.detail && e.detail.ns === ns && e.detail.id === baseTask.id) {
        setTask(window.indTaskGet(ns, baseTask));
      }
    };
    document.addEventListener('sr:ind-task-updated', onUpdate);
    return () => document.removeEventListener('sr:ind-task-updated', onUpdate);
  }, [ns, baseTask.id]);
  const patch = (p) => window.indTaskPatch(ns, baseTask.id, p);
  return [task, patch];
};

/* ─────────────────────────────────────────────────────────────
 *  IndTaskDetail — reusable task detail flow for any industry
 *  Props: { task, accent, ns, onBack }
 * ───────────────────────────────────────────────────────────── */
window.IndTaskDetail = function IndTaskDetail({ task: baseTask, accent, ns, onBack }) {
  const [task, patch] = window.useIndTask(ns, baseTask);
  const [notes, setNotes] = React.useState(task.notes || '');
  const [toast, setToast] = React.useState(null);
  const flushNotes = () => { if (notes !== (task.notes || '')) patch({ notes }); };
  const show = (m) => { setToast(m); setTimeout(() => setToast(null), 1800); };

  const onSnooze = () => {
    const newDue = window.indBumpDue(task.due, 1);
    patch({ due: newDue, snoozedFrom: task.due });
    show(`Snoozed → ${newDue}`);
  };
  const onReschedule = () => {
    const opts = ['Later today', 'Tomorrow morning', 'Tomorrow afternoon', 'Next week'];
    const pick = window.prompt('Reschedule to:\n' + opts.map((o, i) => `${i+1}. ${o}`).join('\n'), '1');
    const idx = Math.max(1, Math.min(opts.length, parseInt(pick, 10) || 1)) - 1;
    patch({ due: opts[idx], rescheduledFrom: task.due });
    show(`Rescheduled → ${opts[idx]}`);
  };
  const onReassign = () => {
    const list = IND_ASSIGNEES.map((n, i) => `${i+1}. ${n}`).join('\n');
    const pick = window.prompt('Reassign to:\n' + list, '1');
    const idx = Math.max(1, Math.min(IND_ASSIGNEES.length, parseInt(pick, 10) || 1)) - 1;
    patch({ assignee: IND_ASSIGNEES[idx] });
    show(`Reassigned → ${IND_ASSIGNEES[idx]}`);
  };
  const onDone = () => {
    flushNotes();
    patch({ done: true, completedAt: new Date().toISOString() });
    show('✓ Marked as done');
    setTimeout(onBack, 700);
  };
  const onReopen = () => { patch({ done: false, completedAt: null }); show('Reopened'); };

  const tint = accent + '14';
  const accentSoft = accent + '22';

  return (
    <div style={{ flex:1, display:'flex', flexDirection:'column', overflow:'hidden', background:'#f7f7f4', position:'relative' }}>
      {/* Header */}
      <div style={{ padding:'12px 14px 10px', background:'#fff', borderBottom:'1px solid #f3f4f6', display:'flex', alignItems:'center', gap:10 }}>
        <button onClick={onBack} style={{ background:'none', border:'none', cursor:'pointer', padding:4, color:accent, display:'flex' }}>
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M15 6l-6 6 6 6"/></svg>
        </button>
        <div style={{ flex:1 }}>
          <div style={{ fontSize:11, fontWeight:700, color:accent, letterSpacing:'0.08em', textTransform:'uppercase' }}>Task · {task.due}</div>
          <div style={{ fontSize:14, fontWeight:800, color:'#111827', marginTop:1, lineHeight:1.3, textDecoration: task.done ? 'line-through' : 'none' }}>{task.title}</div>
        </div>
        {task.done && <span style={{ fontSize:10.5, fontWeight:700, background:accent, color:'#fff', borderRadius:20, padding:'3px 10px' }}>✓ Done</span>}
      </div>

      <div style={{ flex:1, overflow:'auto', padding:'14px 14px 110px' }}>
        {/* meta */}
        <div style={{ background:'#fff', borderRadius:14, padding:'12px 14px', boxShadow:'0 1px 4px rgba(0,0,0,0.05)' }}>
          {task.priority && (
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'8px 0', borderBottom:'1px solid #f3f4f6' }}>
              <div style={{ fontSize:10.5, fontWeight:700, color:'#9ca3af', letterSpacing:'0.08em', textTransform:'uppercase' }}>Priority</div>
              <div style={{ fontSize:12.5, fontWeight:700, color:accent, background:tint, borderRadius:20, padding:'2px 10px' }}>{task.priority}</div>
            </div>
          )}
          {task.for && (
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'8px 0', borderBottom: task.assignee ? '1px solid #f3f4f6' : 'none' }}>
              <div style={{ fontSize:10.5, fontWeight:700, color:'#9ca3af', letterSpacing:'0.08em', textTransform:'uppercase' }}>For role</div>
              <div style={{ fontSize:12.5, fontWeight:600, color:'#111827' }}>{task.for}</div>
            </div>
          )}
          {task.assignee && (
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'8px 0' }}>
              <div style={{ fontSize:10.5, fontWeight:700, color:'#9ca3af', letterSpacing:'0.08em', textTransform:'uppercase' }}>Assigned to</div>
              <div style={{ fontSize:12.5, fontWeight:600, color:'#111827' }}>{task.assignee}</div>
            </div>
          )}
          {task.snoozedFrom && (
            <div style={{ fontSize:11, color:'#9ca3af', paddingTop:8 }}>Snoozed from {task.snoozedFrom}</div>
          )}
          {task.rescheduledFrom && (
            <div style={{ fontSize:11, color:'#9ca3af', paddingTop:4 }}>Was: {task.rescheduledFrom}</div>
          )}
        </div>

        {/* notes */}
        <div style={{ marginTop:14 }}>
          <div style={{ fontSize:10.5, fontWeight:700, color:'#9ca3af', letterSpacing:'0.08em', textTransform:'uppercase', marginBottom:6, padding:'0 2px' }}>Notes</div>
          <textarea value={notes} onChange={e => setNotes(e.target.value)} onBlur={flushNotes}
            placeholder="Add context, blockers, or follow-ups…"
            style={{ width:'100%', minHeight:90, background:'#fff', border:'1px solid #e5e7eb', borderRadius:10, padding:'10px 12px', fontSize:13, color:'#111827', outline:'none', resize:'vertical', fontFamily:'inherit', boxSizing:'border-box' }} />
        </div>

        {/* secondary actions */}
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:8, marginTop:14 }}>
          <button onClick={onSnooze} disabled={task.done} style={{ border:`1px solid ${accent}`, background:'#fff', color:accent, borderRadius:10, padding:'9px', fontSize:12, fontWeight:700, cursor: task.done ? 'default' : 'pointer', opacity: task.done ? 0.5 : 1, fontFamily:'inherit', display:'flex', alignItems:'center', justifyContent:'center', gap:5 }}>
            {window.IndIcon('clock',{c:accent,size:13})} Snooze
          </button>
          <button onClick={onReschedule} disabled={task.done} style={{ border:`1px solid ${accent}`, background:'#fff', color:accent, borderRadius:10, padding:'9px', fontSize:12, fontWeight:700, cursor: task.done ? 'default' : 'pointer', opacity: task.done ? 0.5 : 1, fontFamily:'inherit', display:'flex', alignItems:'center', justifyContent:'center', gap:5 }}>
            {window.IndIcon('cal',{c:accent,size:13})} Reschedule
          </button>
          <button onClick={onReassign} disabled={task.done} style={{ border:`1px solid ${accent}`, background:'#fff', color:accent, borderRadius:10, padding:'9px', fontSize:12, fontWeight:700, cursor: task.done ? 'default' : 'pointer', opacity: task.done ? 0.5 : 1, fontFamily:'inherit', display:'flex', alignItems:'center', justifyContent:'center', gap:5 }}>
            {window.IndIcon('person',{c:accent,size:13})} Reassign
          </button>
        </div>
      </div>

      {/* footer primary */}
      <div style={{ position:'absolute', left:0, right:0, bottom:0, padding:'10px 14px 14px', background:'#fff', borderTop:'1px solid #f3f4f6' }}>
        {!task.done ? (
          <button onClick={onDone} style={{ width:'100%', border:'none', background:accent, color:'#fff', borderRadius:12, padding:'13px', fontSize:14, fontWeight:700, cursor:'pointer', fontFamily:'inherit', display:'flex', alignItems:'center', justifyContent:'center', gap:6 }}>
            {window.IndIcon('check',{c:'#fff',size:15,sw:2.5})} Mark as done
          </button>
        ) : (
          <button onClick={onReopen} style={{ width:'100%', border:`1px solid ${accent}`, background:'#fff', color:accent, borderRadius:12, padding:'13px', fontSize:14, fontWeight:700, cursor:'pointer', fontFamily:'inherit', display:'flex', alignItems:'center', justifyContent:'center', gap:6 }}>
            {window.IndIcon('retry',{c:accent,size:14})} Reopen task
          </button>
        )}
      </div>

      {toast && (
        <div style={{ position:'absolute', bottom:78, left:'50%', transform:'translateX(-50%)', background:'#111827', color:'#fff', padding:'10px 18px', borderRadius:22, fontSize:12.5, fontWeight:600, boxShadow:'0 4px 14px rgba(0,0,0,0.18)', zIndex:50, whiteSpace:'nowrap' }}>{toast}</div>
      )}
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────
 *  NotifPanel — slide-up notification panel (shared)
 *  Props: { ns, user, accent, onClose }
 * ───────────────────────────────────────────────────────────── */
window.NotifPanel = function NotifPanel({ ns, user, accent = '#16a34a', onClose }) {
  const [notifs, setNotifs] = React.useState(() => window.getNotifs(ns, user));
  React.useEffect(() => {
    const h = () => setNotifs(window.getNotifs(ns, user));
    document.addEventListener('sr:notification', h);
    return () => document.removeEventListener('sr:notification', h);
  }, [ns, user]);
  const iconFor = (type) => ({ task_assigned:'✓', task_trickle:'📋', task_done:'✅', nudge:'🔔' }[type] || '📣');
  const colorFor = (type) => ({ task_assigned: accent, task_trickle:'#6b7280', task_done:'#16a34a', nudge:'#ea580c' }[type] || '#374151');
  return (
    <div style={{ position:'absolute', inset:0, background:'rgba(0,0,0,0.35)', zIndex:300, display:'flex', alignItems:'flex-end' }} onClick={onClose}>
      <div onClick={e => e.stopPropagation()} style={{ width:'100%', maxHeight:'72%', background:'#fff', borderRadius:'20px 20px 0 0', display:'flex', flexDirection:'column', boxShadow:'0 -4px 24px rgba(0,0,0,0.18)' }}>
        <div style={{ padding:'14px 18px 10px', display:'flex', alignItems:'center', borderBottom:'1px solid #f3f4f6' }}>
          <div style={{ fontSize:16, fontWeight:800, color:'#111827', flex:1 }}>Notifications</div>
          {notifs.length > 0 && <button onClick={() => { window.markAllNotifsRead(ns, user); setNotifs([]); }} style={{ fontSize:12, color:accent, fontWeight:700, border:'none', background:'none', cursor:'pointer', marginRight:10 }}>Mark all read</button>}
          <button onClick={onClose} style={{ border:'none', background:'none', cursor:'pointer', color:'#9ca3af', fontSize:20 }}>✕</button>
        </div>
        <div style={{ overflowY:'auto', flex:1 }}>
          {notifs.length === 0 && <div style={{ padding:'32px 18px', textAlign:'center', color:'#9ca3af', fontSize:13 }}>All caught up ✓</div>}
          {notifs.map(n => (
            <div key={n.id} onClick={() => { window.markNotifRead(ns, user, n.id); setNotifs(window.getNotifs(ns, user)); }}
              style={{ display:'flex', gap:12, padding:'12px 18px', borderBottom:'1px solid #f9fafb', cursor:'pointer', background: n.read ? '#fff' : accent+'08' }}>
              <div style={{ width:36, height:36, borderRadius:99, background:colorFor(n.type)+'18', display:'flex', alignItems:'center', justifyContent:'center', fontSize:16, flexShrink:0 }}>
                {iconFor(n.type)}
              </div>
              <div style={{ flex:1, minWidth:0 }}>
                <div style={{ fontSize:13, fontWeight:700, color:'#111827', marginBottom:2 }}>{n.title}</div>
                <div style={{ fontSize:12, color:'#6b7280', lineHeight:1.4 }}>{n.msg}</div>
                <div style={{ fontSize:10.5, color:'#9ca3af', marginTop:3 }}>{n.from} · {n.sentAt ? new Date(n.sentAt).toLocaleTimeString([],{hour:'2-digit',minute:'2-digit'}) : 'Now'}</div>
              </div>
              {!n.read && <div style={{ width:8, height:8, borderRadius:99, background:accent, alignSelf:'center', flexShrink:0 }} />}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────
 *  NotifBell — header bell with badge
 *  Props: { ns, user, accent, onClick }
 * ───────────────────────────────────────────────────────────── */
window.NotifBell = function NotifBell({ ns, user, accent = '#16a34a', onClick }) {
  const [count, setCount] = React.useState(() => window.getNotifs(ns, user).length);
  React.useEffect(() => {
    const h = () => setCount(window.getNotifs(ns, user).length);
    document.addEventListener('sr:notification', h);
    return () => document.removeEventListener('sr:notification', h);
  }, [ns, user]);
  return (
    <button onClick={onClick} style={{ position:'relative', width:36, height:36, borderRadius:10, background:count>0?accent+'15':'#f3f4f6', border:'none', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center' }}>
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={count>0?accent:'#6b7280'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/>
      </svg>
      {count > 0 && <span style={{ position:'absolute', top:4, right:4, width:16, height:16, borderRadius:99, background:'#ef4444', color:'#fff', fontSize:9, fontWeight:800, display:'flex', alignItems:'center', justifyContent:'center' }}>{count > 9 ? '9+' : count}</span>}
    </button>
  );
};

/* ─────────────────────────────────────────────────────────────
 *  TaskOverview — manager/owner bird's-eye task dashboard
 *  Props: { ns, accent, role, onBack, teamNames }
 * ───────────────────────────────────────────────────────────── */
window.TaskOverview = function TaskOverview({ ns, accent = '#16a34a', role, onBack, teamNames = [] }) {
  const [filter, setFilter] = React.useState('all'); // all|delayed|near|critical|done
  const [tasks, setTasks]   = React.useState(() => window.getTaskDashboard(ns, role));
  const [actionTask, setActionTask] = React.useState(null); // task for action sheet
  const [notifOpen, setNotifOpen]   = React.useState(false);

  React.useEffect(() => {
    const h = () => setTasks(window.getTaskDashboard(ns, role));
    document.addEventListener('sr:ind-task-updated', h);
    document.addEventListener('sr:nudge', h);
    return () => { document.removeEventListener('sr:ind-task-updated', h); document.removeEventListener('sr:nudge', h); };
  }, [ns, role]);

  const PRIO_COLOR = { Critical:'#dc2626', High:'#ea580c', Medium:'#ca8a04', Low:'#9ca3af' };
  const STATUS_COLOR = { Open:'#6b7280','In Progress':'#2563eb',Blocked:'#dc2626',Done:'#16a34a',Resolved:'#2563eb' };

  const filtered = tasks.filter(t => {
    if (filter === 'delayed')  return t.isDelayed;
    if (filter === 'near')     return t.isNearDeadline && !t.isDelayed;
    if (filter === 'critical') return t.priority === 'Critical' && !t.done && t.status !== 'Done' && t.status !== 'Resolved';
    if (filter === 'done')     return t.done || t.status === 'Done' || t.status === 'Resolved';
    return true;
  });

  const counts = {
    all:      tasks.length,
    delayed:  tasks.filter(t => t.isDelayed).length,
    near:     tasks.filter(t => t.isNearDeadline && !t.isDelayed).length,
    critical: tasks.filter(t => t.priority==='Critical' && !t.done && t.status!=='Done').length,
    done:     tasks.filter(t => t.done || t.status==='Done' || t.status==='Resolved').length,
  };

  const FILTERS = [
    { k:'all',      label:'All',       color:'#374151' },
    { k:'delayed',  label:'Delayed',   icon:'warn',  color:'#dc2626' },
    { k:'near',     label:'Due Soon',  icon:'clock', color:'#ca8a04' },
    { k:'critical', label:'Critical',  icon:'alert', color:'#dc2626' },
    { k:'done',     label:'Done',      icon:'check', color:'#16a34a' },
  ];

  const rowBg = (t) => {
    if (t.isDelayed || t.isNudged) return '#fff5f5';
    if (t.isNearDeadline) return '#fffbeb';
    if (t.done || t.status === 'Done' || t.status === 'Resolved') return '#f9fafb';
    return '#fff';
  };
  const rowBorder = (t) => {
    if (t.isDelayed || t.isNudged) return '#fca5a5';
    if (t.isNearDeadline) return '#fde68a';
    return '#f3f4f6';
  };

  const doNudge = (task) => {
    const msg = `Please update on: "${task.title}"`;
    window.sendNudge && window.sendNudge(ns, task.assignee || task.role || 'Team', task.id, msg, role);
    window.indTaskPatch(ns, task.id, { nudgeCount: (task.nudgeCount||0)+1, lastNudgedAt: new Date().toISOString() });
    setActionTask(null);
    setTasks(window.getTaskDashboard(ns, role));
  };

  const doAskStatus = (task) => {
    window.sendNudge && window.sendNudge(ns, task.assignee || task.role || 'Team', task.id, 'Status update requested', role);
    setActionTask(null);
  };

  return (
    <div style={{ flex:1, display:'flex', flexDirection:'column', background:'#f9fafb', overflow:'hidden' }}>
      {/* Header */}
      <div style={{ padding:'14px 16px 10px', background:'#fff', borderBottom:'1px solid #f3f4f6', display:'flex', alignItems:'center', gap:10 }}>
        <button onClick={onBack} style={{ border:'none', background:'none', cursor:'pointer', fontSize:20, padding:0, color:'#6b7280' }}>←</button>
        <div style={{ flex:1 }}>
          <div style={{ fontSize:17, fontWeight:800, color:'#111827' }}>Task Overview</div>
          <div style={{ fontSize:11.5, color:'#9ca3af' }}>{role} · {tasks.length} total tasks</div>
        </div>
        <window.NotifBell ns={ns} user={role} accent={accent} onClick={() => setNotifOpen(true)} />
      </div>

      {/* Summary chips */}
      <div style={{ display:'flex', gap:8, padding:'10px 14px', overflowX:'auto', background:'#fff', borderBottom:'1px solid #f3f4f6' }}>
        {FILTERS.map(f => (
          <button key={f.k} onClick={() => setFilter(f.k)}
            style={{ flexShrink:0, padding:'6px 14px', borderRadius:20, border:`1px solid ${filter===f.k ? f.color : '#e5e7eb'}`,
              background: filter===f.k ? f.color+'18' : '#f9fafb',
              color: filter===f.k ? f.color : '#6b7280', fontWeight:700, fontSize:12.5, cursor:'pointer',
              display:'flex', alignItems:'center', gap:5 }}>
            {f.icon && window.IndIcon(f.icon, { c: filter===f.k ? f.color : '#9ca3af', size:12 })}
            {f.label}
            <span style={{ background: filter===f.k ? f.color : '#e5e7eb', color: filter===f.k ? '#fff' : '#6b7280',
              fontSize:10, fontWeight:800, padding:'1px 6px', borderRadius:99, minWidth:16, textAlign:'center' }}>
              {counts[f.k]}
            </span>
          </button>
        ))}
      </div>

      {/* Task list */}
      <div style={{ flex:1, overflowY:'auto', padding:'8px 12px' }}>
        {filtered.length === 0 && <div style={{ padding:'40px 0', textAlign:'center', color:'#9ca3af', fontSize:13 }}>No tasks in this category</div>}
        {filtered.map(t => {
          const pc = PRIO_COLOR[t.priority] || '#9ca3af';
          const sc = STATUS_COLOR[t.status || (t.done ? 'Done' : 'Open')] || '#6b7280';
          const delayed = t.isDelayed || t.isNudged;
          return (
            <div key={t.id} onClick={() => setActionTask(t)}
              style={{ background:rowBg(t), border:`1px solid ${rowBorder(t)}`, borderRadius:14, padding:'11px 14px', marginBottom:8,
                borderLeft:`3px solid ${delayed ? '#ef4444' : t.isNearDeadline ? '#f59e0b' : accent}`, cursor:'pointer' }}>
              <div style={{ display:'flex', alignItems:'flex-start', gap:8, marginBottom:5 }}>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ fontSize:13.5, fontWeight:700, color: delayed ? '#dc2626' : '#111827', lineHeight:1.35 }}>{t.title}</div>
                  {t.assignee && <div style={{ fontSize:11.5, color:'#6b7280', marginTop:2 }}>→ {t.assignee}</div>}
                </div>
                {t.priority && <span style={{ fontSize:10, fontWeight:800, padding:'2px 7px', borderRadius:20, background:pc+'18', color:pc, border:`1px solid ${pc}40`, flexShrink:0 }}>{t.priority}</span>}
              </div>
              <div style={{ display:'flex', flexWrap:'wrap', gap:5, alignItems:'center' }}>
                <span style={{ fontSize:11, padding:'2px 8px', borderRadius:20, background:sc+'15', color:sc, fontWeight:700 }}>{t.status || (t.done ? 'Done' : 'Open')}</span>
                {t.due && <span style={{ fontSize:11, color:'#9ca3af', display:'flex', alignItems:'center', gap:3 }}>{window.IndIcon('cal',{c:'#9ca3af',size:11})} {t.due}</span>}
                {delayed && <span style={{ fontSize:11, color:'#ef4444', fontWeight:700, display:'flex', alignItems:'center', gap:3 }}>{window.IndIcon('warn',{c:'#ef4444',size:11})} {t.isNudged ? `Nudged ×${t.nudgeCount}` : 'Delayed'}</span>}
                {t.isNearDeadline && !delayed && <span style={{ fontSize:11, color:'#f59e0b', fontWeight:700, display:'flex', alignItems:'center', gap:3 }}>{window.IndIcon('clock',{c:'#f59e0b',size:11})} Due Soon</span>}
                <span style={{ marginLeft:'auto', fontSize:11, color:accent, fontWeight:700 }}>Tap → </span>
              </div>
            </div>
          );
        })}
      </div>

      {/* Action sheet for a task */}
      {actionTask && (
        <div style={{ position:'absolute', inset:0, background:'rgba(0,0,0,0.35)', zIndex:200, display:'flex', alignItems:'flex-end' }} onClick={() => setActionTask(null)}>
          <div onClick={e => e.stopPropagation()} style={{ width:'100%', background:'#fff', borderRadius:'20px 20px 0 0', padding:'18px 18px 28px', boxShadow:'0 -4px 24px rgba(0,0,0,0.18)' }}>
            <div style={{ width:42, height:4, borderRadius:2, background:'#e5e7eb', margin:'0 auto 14px' }} />
            <div style={{ fontSize:15, fontWeight:800, color:'#111827', marginBottom:4 }}>{actionTask.title}</div>
            <div style={{ fontSize:12, color:'#9ca3af', marginBottom:16 }}>
              {actionTask.assignee && `→ ${actionTask.assignee}  ·  `}
              {actionTask.status || (actionTask.done ? 'Done' : 'Open')}
              {actionTask.priority && `  ·  ${actionTask.priority}`}
            </div>
            {[
              { icon:'bell',   label:'Nudge assignee',  action: () => doNudge(actionTask),     color:'#ea580c' },
              { icon:'msg',    label:'Ask for status',  action: () => doAskStatus(actionTask), color:'#2563eb' },
              { icon:'arrowR', label:'Reassign',        action: () => { window.indTaskPatch(ns, actionTask.id, { status:'Blocked' }); setActionTask(null); }, color:'#7e22ce' },
              { icon:'check',  label:'Mark Done',       action: () => { window.indTaskPatch(ns, actionTask.id, { done:true, status:'Done', completedAt:new Date().toISOString() }); setActionTask(null); setTasks(window.getTaskDashboard(ns, role)); }, color:'#16a34a' },
            ].map(a => (
              <button key={a.label} onClick={a.action}
                style={{ width:'100%', display:'flex', alignItems:'center', gap:12, padding:'14px 16px', border:'1px solid #f3f4f6', borderRadius:14, marginBottom:8, background:'#fff', cursor:'pointer', textAlign:'left' }}>
                {window.IndIcon(a.icon, { c:a.color, size:20 })}
                <span style={{ fontSize:14, fontWeight:700, color:a.color }}>{a.label}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Notif panel */}
      {notifOpen && <window.NotifPanel ns={ns} user={role} accent={accent} onClose={() => setNotifOpen(false)} />}
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────
 *  ChatThreadSwitcher — horizontal thread bar for chat screens
 *  Props: { ns, accent, activeThread, onSwitch, role }
 * ───────────────────────────────────────────────────────────── */
window.ChatThreadSwitcher = function ChatThreadSwitcher({ ns, accent, activeThread, onSwitch, role, teamNames = [] }) {
  // NOTE (fix for L-0793): the switcher must key off the LOCAL DICT KEY
  // ('team', 'dm_x', ...), never t.id — t.id gets remapped to the real
  // backend thread id once SRAPI hydration resolves (see the comment above
  // window.CHAT_THREADS), but getChatThreads()[activeThread] and
  // addThreadMessage() always look threads up by the local dict key. Using
  // t.id here made re-selecting an already-hydrated thread (or a DM whose
  // id got remapped after createDmThread resolved) silently lose the
  // thread: messages stopped rendering and sends were dropped.
  const [threads, setThreads] = React.useState(() => Object.entries(window.getChatThreads(ns, role)));
  const [showNew, setShowNew] = React.useState(false);

  React.useEffect(() => {
    const h = () => setThreads(Object.entries(window.getChatThreads(ns, role)));
    document.addEventListener('sr:threads-updated', h);
    return () => document.removeEventListener('sr:threads-updated', h);
  }, [ns, role]);

  const createDM = (name) => {
    const id = 'dm_' + name.toLowerCase().replace(/\s+/g,'_');
    window.addChatThread(ns, { id, name, type:'dm', icon:'👤', unread:0, messages:[] });
    onSwitch(id);
    setShowNew(false);
  };

  return (
    <div style={{ background:'#fff', borderBottom:'1px solid #f3f4f6', flexShrink:0 }}>
      <div style={{ display:'flex', gap:6, padding:'8px 12px', overflowX:'auto' }}>
        {threads.map(([key, t]) => (
          <button key={key} onClick={() => onSwitch(key)}
            style={{ flexShrink:0, display:'flex', alignItems:'center', gap:5, padding:'6px 12px', borderRadius:20,
              border:`1px solid ${activeThread===key ? accent : '#e5e7eb'}`,
              background: activeThread===key ? accent+'15' : '#f9fafb',
              color: activeThread===key ? accent : '#6b7280',
              fontWeight: activeThread===key ? 800 : 600, fontSize:12.5, cursor:'pointer', position:'relative' }}>
            <span>{t.icon}</span> {t.name}
            {t.unread > 0 && <span style={{ background:'#ef4444', color:'#fff', fontSize:9, fontWeight:800, padding:'1px 5px', borderRadius:99, minWidth:14, textAlign:'center' }}>{t.unread}</span>}
          </button>
        ))}
        <button onClick={() => setShowNew(true)}
          style={{ flexShrink:0, padding:'6px 12px', borderRadius:20, border:'1px dashed #d1d5db', background:'transparent', color:'#9ca3af', fontSize:12.5, cursor:'pointer' }}>
          + New Thread
        </button>
      </div>
      {showNew && (
        <div style={{ padding:'0 12px 10px' }}>
          <div style={{ fontSize:11, color:'#9ca3af', fontWeight:700, marginBottom:6, marginTop:2 }}>Start a direct thread with:</div>
          <div style={{ display:'flex', flexWrap:'wrap', gap:5 }}>
            {teamNames.map(n => (
              <button key={n} onClick={() => createDM(n)}
                style={{ padding:'5px 12px', borderRadius:20, border:`1px solid ${accent}40`, background:accent+'10', color:accent, fontSize:12, fontWeight:700, cursor:'pointer' }}>
                {n}
              </button>
            ))}
            <button onClick={() => setShowNew(false)}
              style={{ padding:'5px 12px', borderRadius:20, border:'1px solid #e5e7eb', background:'#fff', color:'#9ca3af', fontSize:12, cursor:'pointer' }}>
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
};


// ===== 16-pf-search.jsx =====
// ── SEARCH_META: options per industry × role ─────────────────────────────────
const SEARCH_META = {
  realty: {
    default: {
      chips: ['Lead', 'Follow-up', 'Site Visit', 'Booking'],
      options: [
        { cat:'Lead',          icon:'👤', desc:'Search by name or phone' },
        { cat:'Customer',      icon:'🏠', desc:'Verified buyers & walkthroughs' },
        { cat:'Site Visit',    icon:'📍', desc:'Scheduled & completed visits' },
        { cat:'Follow-up',     icon:'📞', desc:'Pending calls & callbacks' },
        { cat:'Booking',       icon:'📝', desc:'Confirmed bookings' },
        { cat:'Property Unit', icon:'🏗️', desc:'Units, floors & availability' },
        { cat:'Documents',     icon:'📄', desc:'Agreements, KYC & brochures' },
        { cat:'Notes',         icon:'🗒️', desc:'Internal notes & annotations' },
        { cat:'AI Search',     icon:'🤖', desc:'Ask anything in plain English' },
      ],
    },
    'Presales Executive': {
      chips: ['Hot Leads', 'Lead Source', 'Call History', 'Qualified Leads'],
      options: [
        { cat:'Hot Leads',        icon:'🔥', desc:'Score ≥ 80 · highest priority' },
        { cat:'Cold Leads',       icon:'🧊', desc:'Stale or unresponsive' },
        { cat:'Qualified Leads',  icon:'✅', desc:'Budget & timeline confirmed' },
        { cat:'Unassigned Leads', icon:'📥', desc:'Not yet assigned to an executive' },
        { cat:'Lead Source',      icon:'📡', desc:'Meta, Google, Walk-in, CP…' },
        { cat:'Lead Score',       icon:'📊', desc:'Score bands & history' },
        { cat:'Call History',     icon:'📞', desc:'Past calls for a lead' },
        { cat:'AI Search',        icon:'🤖', desc:'Ask anything in plain English' },
      ],
    },
    'Channel Partner': {
      chips: ['My Leads', 'Bookings', 'Commission', 'Inventory'],
      options: [
        { cat:'My Leads',   icon:'👤', desc:'Leads you registered' },
        { cat:'Bookings',   icon:'📝', desc:'Your confirmed bookings' },
        { cat:'Commission', icon:'💰', desc:'Earned & pending payouts' },
        { cat:'Payments',   icon:'💳', desc:'Payment history & status' },
        { cat:'Projects',   icon:'🏗️', desc:'Active project listings' },
        { cat:'Inventory',  icon:'🏠', desc:'Available units per project' },
        { cat:'Offers',     icon:'🎯', desc:'Current developer offers' },
        { cat:'AI Search',  icon:'🤖', desc:'Ask anything in plain English' },
      ],
    },
    'Sales Manager': {
      chips: ['Leads', 'Team', 'Pipeline', 'Bookings'],
      options: [
        { cat:'Leads',          icon:'👤', desc:'All leads across your team' },
        { cat:'Team Members',   icon:'👥', desc:'Team activity & assignments' },
        { cat:'Pipeline',       icon:'📊', desc:'Stage-wise deal breakdown' },
        { cat:'Lost Leads',     icon:'❌', desc:'Dropped with reasons' },
        { cat:'Site Visits',    icon:'📍', desc:'Scheduled & done visits' },
        { cat:'Bookings',       icon:'📝', desc:'Confirmed bookings this period' },
        { cat:'Performance',    icon:'🏆', desc:'Bookings & conversions by exec' },
        { cat:'Campaign Leads', icon:'📣', desc:'Leads by campaign source' },
        { cat:'Reports',        icon:'📈', desc:'Summary & trend reports' },
        { cat:'AI Search',      icon:'🤖', desc:'Ask anything in plain English' },
      ],
    },
    'Marketing Head': {
      chips: ['Campaigns', 'Lead Sources', 'ROI', 'Ads'],
      options: [
        { cat:'Campaigns',     icon:'📣', desc:'Meta, Google, events & more' },
        { cat:'Lead Sources',  icon:'📡', desc:'Quality by source' },
        { cat:'Lead Quality',  icon:'⭐', desc:'Score distribution & trends' },
        { cat:'Ads',           icon:'🎯', desc:'Active ad creatives & spend' },
        { cat:'Creatives',     icon:'🎨', desc:'Banners, videos & copy' },
        { cat:'Landing Pages', icon:'🌐', desc:'Conversion rates by page' },
        { cat:'Events',        icon:'🗓️', desc:'Exhibitions & walk-in events' },
        { cat:'Agencies',      icon:'🤝', desc:'Channel partners & agencies' },
        { cat:'Budgets',       icon:'💰', desc:'Spend tracking & allocation' },
        { cat:'ROI Reports',   icon:'📈', desc:'Cost per lead & per booking' },
        { cat:'AI Search',     icon:'🤖', desc:'Ask anything in plain English' },
      ],
    },
    'Business Owner': {
      chips: ['Revenue', 'Bookings', 'Collections', 'Forecasts'],
      options: [
        { cat:'Revenue',          icon:'💰', desc:'Total collections vs target' },
        { cat:'Projects',         icon:'🏗️', desc:'All active projects' },
        { cat:'Bookings',         icon:'📝', desc:'Bookings count & value' },
        { cat:'Collections',      icon:'💳', desc:'Cash received this period' },
        { cat:'Outstanding',      icon:'⚠️', desc:'Pending dues & overdue' },
        { cat:'Profitability',    icon:'📊', desc:'Cost vs revenue by project' },
        { cat:'Campaign ROI',     icon:'📣', desc:'Marketing spend vs bookings' },
        { cat:'Team Performance', icon:'🏆', desc:'Exec-wise KPI summary' },
        { cat:'Alerts',           icon:'🔔', desc:'Critical items needing action' },
        { cat:'Forecasts',        icon:'🔮', desc:'Next 30/60/90 day outlook' },
        { cat:'AI Search',        icon:'🤖', desc:'Ask anything in plain English' },
      ],
    },
  },
  skalecap: {
    default: {
      chips: ['Participants', 'Batches', 'Assessments', 'Attendance'],
      options: [
        { cat:'Participants', icon:'👤', desc:'Search by name or batch' },
        { cat:'Batches',      icon:'📚', desc:'Active & completed cohorts' },
        { cat:'Assessments',  icon:'📋', desc:'Scores & completion status' },
        { cat:'Attendance',   icon:'📅', desc:'Attendance records' },
        { cat:'Action Plans', icon:'🎯', desc:'Participant action plans' },
        { cat:'Reflections',  icon:'💭', desc:'Session reflections' },
        { cat:'Feedback',     icon:'⭐', desc:'Trainer & peer feedback' },
        { cat:'Certificates', icon:'🏆', desc:'Issued certificates' },
        { cat:'Reports',      icon:'📈', desc:'Completion & engagement' },
        { cat:'AI Search',    icon:'🤖', desc:'Ask anything in plain English' },
      ],
    },
    'Participant': {
      chips: ['Courses', 'Assessments', 'Certificates', 'Resources'],
      options: [
        { cat:'Courses',           icon:'🎓', desc:'Enrolled & available courses' },
        { cat:'Assessments',       icon:'📋', desc:'Your scores & attempts' },
        { cat:'Certificates',      icon:'🏆', desc:'Your earned certificates' },
        { cat:'Resources',         icon:'📖', desc:'Videos, worksheets & tools' },
        { cat:'Action Plans',      icon:'🎯', desc:'Your learning goals' },
        { cat:'Coach Conversations', icon:'💬', desc:'Past coaching notes' },
        { cat:'AI Search',         icon:'🤖', desc:'Ask anything in plain English' },
      ],
    },
    'L&D Head': {
      chips: ['Capability Gaps', 'ROI', 'Talent Review', 'Forecasts'],
      options: [
        { cat:'Capability Gaps', icon:'⚠️', desc:'Organisation-wide skill gaps' },
        { cat:'Behavior Change',  icon:'🔄', desc:'Pre/post behavior assessment' },
        { cat:'ROI',              icon:'💰', desc:'Training ROI & impact' },
        { cat:'Programs',         icon:'📚', desc:'All L&D programs' },
        { cat:'Trainers',         icon:'👨‍🏫', desc:'Trainer profiles & ratings' },
        { cat:'Skills',           icon:'🧠', desc:'Skills mapping & coverage' },
        { cat:'Talent Review',    icon:'🌟', desc:'High-potential employees' },
        { cat:'Succession',       icon:'🏢', desc:'Succession pipeline' },
        { cat:'Forecasts',        icon:'🔮', desc:'Future capability needs' },
        { cat:'Reports',          icon:'📈', desc:'Comprehensive L&D reports' },
        { cat:'AI Search',        icon:'🤖', desc:'Ask anything in plain English' },
      ],
    },
  },
  healthcare: {
    default: {
      chips: ['Patient', 'Prescription', 'Lab Results', 'Appointments'],
      options: [
        { cat:'Patient',      icon:'👤', desc:'Search by name, ID or condition' },
        { cat:'Prescription', icon:'💊', desc:'Active & past prescriptions' },
        { cat:'Diagnosis',    icon:'🩺', desc:'Diagnosis codes & history' },
        { cat:'Lab Results',  icon:'🔬', desc:'Test results & trends' },
        { cat:'Radiology',    icon:'🩻', desc:'X-ray, MRI, CT scans' },
        { cat:'Appointments', icon:'📅', desc:'Scheduled & past appointments' },
        { cat:'Case History', icon:'📋', desc:'Full patient case history' },
        { cat:'AI Search',    icon:'🤖', desc:'Ask anything in plain English' },
      ],
    },
    'Nurse': {
      chips: ['Patients', 'Medication', 'Tasks', 'Rounds'],
      options: [
        { cat:'Patients',    icon:'👤', desc:'Patients on your ward' },
        { cat:'Medication',  icon:'💊', desc:'Medication schedules & logs' },
        { cat:'Tasks',       icon:'✅', desc:'Nursing tasks & assignments' },
        { cat:'Rounds',      icon:'🚶', desc:'Round schedules & notes' },
        { cat:'Incidents',   icon:'⚠️', desc:'Reported incidents' },
        { cat:'Shift Notes', icon:'📝', desc:'Handover & shift notes' },
        { cat:'AI Search',   icon:'🤖', desc:'Ask anything in plain English' },
      ],
    },
    'Front Desk': {
      chips: ['Appointments', 'Patients', 'Bills', 'Payments'],
      options: [
        { cat:'Appointments', icon:'📅', desc:'Today & upcoming' },
        { cat:'Patients',     icon:'👤', desc:'Patient registration & records' },
        { cat:'Bills',        icon:'🧾', desc:'Generated bills' },
        { cat:'Payments',     icon:'💳', desc:'Payment status & history' },
        { cat:'Insurance',    icon:'🛡️', desc:'Claims & authorisations' },
        { cat:'Admissions',   icon:'🏥', desc:'In-patient admissions' },
        { cat:'Doctors',      icon:'👨‍⚕️', desc:'Doctor schedules & availability' },
        { cat:'AI Search',    icon:'🤖', desc:'Ask anything in plain English' },
      ],
    },
  },
  dermatology: {
    default: {
      chips: ['Patients', 'Sessions', 'Packages', 'Follow-ups'],
      options: [
        { cat:'Patients',   icon:'👤', desc:'Search by name or phone' },
        { cat:'Sessions',   icon:'💆', desc:'Therapy sessions & notes' },
        { cat:'Packages',   icon:'📦', desc:'Packages sold & remaining' },
        { cat:'Photos',     icon:'📸', desc:'Before/after treatment photos' },
        { cat:'Treatments', icon:'✨', desc:'Procedures & protocols' },
        { cat:'Reviews',    icon:'⭐', desc:'Patient feedback & ratings' },
        { cat:'Follow-ups', icon:'📞', desc:'Upcoming follow-up calls' },
        { cat:'AI Search',  icon:'🤖', desc:'Ask anything in plain English' },
      ],
    },
    'Dermatologist': {
      chips: ['Patients', 'Diagnoses', 'Treatments', 'Prescriptions'],
      options: [
        { cat:'Patients',         icon:'👤', desc:'Full patient records' },
        { cat:'Diagnoses',        icon:'🩺', desc:'Skin conditions & history' },
        { cat:'Treatments',       icon:'✨', desc:'Treatment plans & outcomes' },
        { cat:'Prescriptions',    icon:'💊', desc:'Active prescriptions' },
        { cat:'Before/After Photos', icon:'📸', desc:'Progress documentation' },
        { cat:'Reviews',          icon:'⭐', desc:'Patient feedback' },
        { cat:'AI Search',        icon:'🤖', desc:'Ask anything in plain English' },
      ],
    },
    'Manager': {
      chips: ['Revenue', 'Packages', 'Conversions', 'Occupancy'],
      options: [
        { cat:'Revenue',     icon:'💰', desc:'Daily, weekly & monthly revenue' },
        { cat:'Packages',    icon:'📦', desc:'Package sales & utilisation' },
        { cat:'Conversions', icon:'📊', desc:'Consultation to sale conversion' },
        { cat:'Renewals',    icon:'🔄', desc:'Package renewals & reminders' },
        { cat:'Therapists',  icon:'👩‍⚕️', desc:'Therapist performance' },
        { cat:'Campaigns',   icon:'📣', desc:'Marketing campaigns' },
        { cat:'Occupancy',   icon:'🪑', desc:'Room & chair occupancy' },
        { cat:'AI Search',   icon:'🤖', desc:'Ask anything in plain English' },
      ],
    },
  },
  manufacturing: {
    default: {
      chips: ['Tasks', 'Machines', 'Breakdowns', 'SOPs'],
      options: [
        { cat:'Tasks',       icon:'✅', desc:'Open & assigned tasks' },
        { cat:'Machines',    icon:'⚙️', desc:'Machine status & OEE' },
        { cat:'Breakdowns',  icon:'🔧', desc:'Past & active breakdowns' },
        { cat:'Checklists',  icon:'📋', desc:'Inspection checklists' },
        { cat:'SOPs',        icon:'📖', desc:'Standard operating procedures' },
        { cat:'Incidents',   icon:'⚠️', desc:'Safety & quality incidents' },
        { cat:'Spare Parts', icon:'🔩', desc:'Part inventory & reorder' },
        { cat:'AI Search',   icon:'🤖', desc:'Ask anything in plain English' },
      ],
    },
    'Supervisor': {
      chips: ['Technicians', 'Production', 'Quality', 'Downtime'],
      options: [
        { cat:'Technicians', icon:'👷', desc:'Technician tasks & status' },
        { cat:'Tasks',       icon:'✅', desc:'All open work orders' },
        { cat:'Machines',    icon:'⚙️', desc:'Machine health & alerts' },
        { cat:'Production',  icon:'🏭', desc:'Output vs target' },
        { cat:'Quality',     icon:'🎯', desc:'Defects, yield & rejections' },
        { cat:'Downtime',    icon:'⏱️', desc:'Planned & unplanned stops' },
        { cat:'Incidents',   icon:'⚠️', desc:'Safety & near-miss events' },
        { cat:'Safety',      icon:'🦺', desc:'PPE & safety compliance' },
        { cat:'AI Search',   icon:'🤖', desc:'Ask anything in plain English' },
      ],
    },
    'Manager': {
      chips: ['Production', 'OEE', 'Cost', 'Quality'],
      options: [
        { cat:'Production',  icon:'🏭', desc:'Output, throughput & losses' },
        { cat:'OEE',         icon:'📊', desc:'Availability × Performance × Quality' },
        { cat:'Downtime',    icon:'⏱️', desc:'MTTR, MTBF & trends' },
        { cat:'Yield',       icon:'🎯', desc:'First-pass yield by line' },
        { cat:'Quality',     icon:'✅', desc:'Defect rates & NCR reports' },
        { cat:'Maintenance', icon:'🔧', desc:'Preventive & corrective MO' },
        { cat:'Inventory',   icon:'📦', desc:'Raw material & WIP stock' },
        { cat:'Cost',        icon:'💰', desc:'Cost per unit & variances' },
        { cat:'Safety',      icon:'🦺', desc:'Incident frequency & compliance' },
        { cat:'AI Search',   icon:'🤖', desc:'Ask anything in plain English' },
      ],
    },
  },
};

function getSearchOptions(industry, role) {
  const indMap = SEARCH_META[industry] || SEARCH_META.realty;
  return indMap[role] || indMap.default || { chips: [], options: [] };
}

// ── Execute a search query against PF_DATA (local) ───────────────────────────
function executeSearch(cat, query, industry) {
  const data = window.PF_DATA || {};
  const q    = (query || '').toLowerCase().trim();
  const catL = (cat   || '').toLowerCase();

  // ── Lead / Customer / Patient searches ──────────────────────────────────
  const leadCats = ['lead','customer','patient','patients','my leads','hot leads','cold leads','qualified leads','unassigned leads'];
  if (leadCats.includes(catL)) {
    let leads = data.leads || [];
    if (catL === 'hot leads')        leads = leads.filter(l => (l.score||0) >= 80 || (l.status||'').toLowerCase().includes('hot'));
    if (catL === 'cold leads')       leads = leads.filter(l => (l.score||0) <  40 || (l.status||'').toLowerCase().includes('cold'));
    if (catL === 'qualified leads')  leads = leads.filter(l => (l.status||'').toLowerCase().includes('qualif'));
    if (catL === 'unassigned leads') leads = leads.filter(l => !l.owner && !l.assigned_to);
    if (q) leads = leads.filter(l =>
      (l.name||'').toLowerCase().includes(q) ||
      (l.phone||'').toLowerCase().includes(q) ||
      (l.project||'').toLowerCase().includes(q));
    return { type:'leads', items: leads.slice(0,6), total: leads.length };
  }

  // ── Task / Follow-up / Assignment ────────────────────────────────────────
  const taskCats = ['task','tasks','follow-up','follow-ups','site visit','site visits','rounds','medication'];
  if (taskCats.includes(catL)) {
    let tasks = data.tasks || [];
    if (q) tasks = tasks.filter(t =>
      (t.title||'').toLowerCase().includes(q) ||
      (t.owner||'').toLowerCase().includes(q) ||
      (t.type||'').toLowerCase().includes(q));
    return { type:'tasks', items: tasks.slice(0,6), total: tasks.length };
  }

  // ── Batches ──────────────────────────────────────────────────────────────
  if (['batches','batch'].includes(catL)) {
    const sc   = window.SR_SC_DATA || {};
    let batches = sc.batches || [];
    if (q) batches = batches.filter(b => (b.name||'').toLowerCase().includes(q));
    return { type:'batches', items: batches.slice(0,6), total: batches.length };
  }

  // ── Participants ─────────────────────────────────────────────────────────
  if (['participants','participant'].includes(catL)) {
    const sc = window.SR_SC_DATA || {};
    let pp = sc.participants || [];
    if (q) pp = pp.filter(p => (p.name||'').toLowerCase().includes(q));
    return { type:'participants', items: pp.slice(0,6), total: pp.length };
  }

  // ── Team / Performance ───────────────────────────────────────────────────
  if (['team members','performance','technicians','therapists','trainers'].includes(catL)) {
    let team = window.SR_TEAM || [];
    if (q) team = team.filter(m => (m.name||'').toLowerCase().includes(q) || (m.role||'').toLowerCase().includes(q));
    return { type:'team', items: team.slice(0,6), total: team.length };
  }

  // ── AI Search ────────────────────────────────────────────────────────────
  if (catL === 'ai search' || q.startsWith('why ') || q.startsWith('how ') || q.startsWith('what ') || q.startsWith('show me')) {
    return { type:'ai', query: query || cat };
  }

  // ── Default: empty state ─────────────────────────────────────────────────
  return { type:'empty', cat, query };
}

// ── SearchResultPanel ─────────────────────────────────────────────────────────
const RESULT_ACTIONS = {
  leads:        ['/call',    '/followup', '/sitevisit', '/note'],
  tasks:        ['/complete','/reassign', '/note'],
  batches:      ['/attendance','/score'],
  participants: ['/score',   '/nudge',    '/observation'],
  team:         ['/nudge',   '/task'],
};

const LEAD_STATUS_COLOR = s => {
  const sl = (s||'').toLowerCase();
  if (sl.includes('hot')    || sl.includes('booked')) return { bg:'#fef2f2', c:'#dc2626' };
  if (sl.includes('warm')   || sl.includes('qualif')) return { bg:'#fefce8', c:'#ca8a04' };
  if (sl.includes('cold'))  return { bg:'#eff6ff', c:'#2563eb' };
  return { bg:'#f0fdf4', c:'#16a34a' };
};

function SearchResultPanel({ cat, query, industry, role, accent, onQuickAction }) {
  const [result,  setResult]  = React.useState(null);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    if (!cat) return;
    setLoading(true);
    const local = executeSearch(cat, query, industry);
    setResult(local);
    setLoading(false);

    // Backend live search (non-blocking)
    if (window.SRAPI && query) {
      const token = localStorage.getItem('access_token');
      const wsId  = localStorage.getItem('workspace_id');
      fetch(window.SRAPI._baseUrl + '/api/mobile/search?q=' + encodeURIComponent(query) + '&cat=' + encodeURIComponent(cat), {
        headers: {
          ...(token ? { Authorization: 'Bearer ' + token } : {}),
          ...(wsId  ? { 'x-workspace-id': wsId }          : {}),
        }
      }).then(r => r.ok ? r.json() : null).then(d => {
        if (d && d.results && d.results.length) setResult({ type: d.type || local.type, items: d.results, total: d.total || d.results.length });
      }).catch(() => {});
    }
  }, [cat, query]);

  const ac = accent || '#16a34a';

  if (loading) return (
    <div style={{ padding:'14px 12px', textAlign:'center', color:'#9ca3af', fontSize:13 }}>Searching…</div>
  );
  if (!result) return null;

  const actions = RESULT_ACTIONS[result.type] || [];

  return (
    <div>
      {/* Count badge */}
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:8 }}>
        <div style={{ fontSize:12, fontWeight:800, color:'#6b7280', letterSpacing:'0.07em', textTransform:'uppercase' }}>
          {cat} Results
        </div>
        {result.total > 0 && (
          <span style={{ fontSize:12, fontWeight:800, padding:'2px 9px', borderRadius:10,
            background: ac + '18', color: ac }}>
            {result.total} found
          </span>
        )}
      </div>

      {/* Result items */}
      <div style={{ borderRadius:10, border:'1px solid #e5e7eb', overflow:'hidden', background:'#fff' }}>
        {result.type === 'empty' && (
          <div style={{ padding:'14px', textAlign:'center', color:'#9ca3af', fontSize:13 }}>
            No {cat.toLowerCase()} found{query ? ' for "' + query + '"' : ''}
          </div>
        )}

        {result.type === 'ai' && (
          <div style={{ padding:'14px' }}>
            <div style={{ fontSize:12, fontWeight:800, color:'#7c3aed', marginBottom:6 }}>🤖 AI Analysis</div>
            <div style={{ fontSize:13, color:'#374151', lineHeight:1.6 }}>
              Analysing: <em>"{result.query}"</em>
            </div>
            <div style={{ marginTop:8, fontSize:11.5, color:'#9ca3af' }}>
              Connect to live backend for real-time AI insights across all data.
            </div>
          </div>
        )}

        {result.type === 'leads' && (result.items||[]).map((l, i) => {
          const sc = LEAD_STATUS_COLOR(l.status || l.stage);
          return (
            <div key={i} style={{ display:'flex', alignItems:'center', gap:10, padding:'10px 12px',
              borderBottom: i < result.items.length-1 ? '1px solid #f3f4f6' : 'none' }}>
              <div style={{ width:36, height:36, borderRadius:10, flexShrink:0,
                background: ac + '18', display:'flex', alignItems:'center', justifyContent:'center',
                fontWeight:800, fontSize:13, color:ac }}>
                {(l.name||'?').charAt(0).toUpperCase()}
              </div>
              <div style={{ flex:1, minWidth:0 }}>
                <div style={{ fontWeight:700, fontSize:13, color:'#111827',
                  overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{l.name||'Unknown'}</div>
                <div style={{ fontSize:11, color:'#9ca3af', marginTop:2 }}>
                  {l.phone && <span style={{ marginRight:8 }}>📞 {l.phone}</span>}
                  {l.project && <span>{l.project}</span>}
                </div>
              </div>
              {(l.status || l.stage) && (
                <span style={{ flexShrink:0, fontSize:10.5, fontWeight:700, padding:'2px 8px', borderRadius:10,
                  background:sc.bg, color:sc.c }}>
                  {l.status || l.stage}
                </span>
              )}
              {l.score != null && (
                <span style={{ flexShrink:0, fontSize:13, fontWeight:900, color:'#d97706' }}>{l.score}</span>
              )}
            </div>
          );
        })}

        {result.type === 'tasks' && (result.items||[]).map((t, i) => (
          <div key={i} style={{ display:'flex', alignItems:'center', gap:10, padding:'10px 12px',
            borderBottom: i < result.items.length-1 ? '1px solid #f3f4f6' : 'none' }}>
            <div style={{ width:32, height:32, borderRadius:8, flexShrink:0,
              background:'#f0fdf4', display:'flex', alignItems:'center', justifyContent:'center',
              fontSize:15 }}>✅</div>
            <div style={{ flex:1, minWidth:0 }}>
              <div style={{ fontWeight:700, fontSize:13, color:'#111827',
                overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{t.title||t.name||'Task'}</div>
              <div style={{ fontSize:11, color:'#9ca3af', marginTop:2 }}>
                {t.owner && <span style={{ marginRight:8 }}>👤 {t.owner}</span>}
                {t.due_date && <span>📅 {t.due_date}</span>}
              </div>
            </div>
            {t.priority && (
              <span style={{ flexShrink:0, fontSize:10.5, fontWeight:700, padding:'2px 7px', borderRadius:8,
                background: t.priority==='High' ? '#fef2f2' : '#f9fafb',
                color:      t.priority==='High' ? '#dc2626' : '#6b7280' }}>
                {t.priority}
              </span>
            )}
          </div>
        ))}

        {result.type === 'participants' && (result.items||[]).map((p, i) => (
          <div key={i} style={{ display:'flex', alignItems:'center', gap:10, padding:'10px 12px',
            borderBottom: i < result.items.length-1 ? '1px solid #f3f4f6' : 'none' }}>
            <div style={{ width:34, height:34, borderRadius:10, flexShrink:0,
              background: ac + '18', display:'flex', alignItems:'center', justifyContent:'center',
              fontWeight:800, fontSize:13, color:ac }}>
              {(p.name||'?').charAt(0).toUpperCase()}
            </div>
            <div style={{ flex:1, minWidth:0 }}>
              <div style={{ fontWeight:700, fontSize:13 }}>{p.name}</div>
              <div style={{ fontSize:11, color:'#9ca3af', marginTop:2 }}>
                {p.batch && <span style={{ marginRight:8 }}>📚 {p.batch}</span>}
                {p.score != null && <span>Score: {p.score}%</span>}
              </div>
            </div>
          </div>
        ))}

        {result.type === 'team' && (result.items||[]).map((m, i) => (
          <div key={i} style={{ display:'flex', alignItems:'center', gap:10, padding:'10px 12px',
            borderBottom: i < result.items.length-1 ? '1px solid #f3f4f6' : 'none' }}>
            <div style={{ width:34, height:34, borderRadius:10, flexShrink:0,
              background: ac + '18', display:'flex', alignItems:'center', justifyContent:'center',
              fontWeight:800, fontSize:13, color:ac }}>
              {(m.name||'?').charAt(0).toUpperCase()}
            </div>
            <div style={{ flex:1, minWidth:0 }}>
              <div style={{ fontWeight:700, fontSize:13 }}>{m.name}</div>
              <div style={{ fontSize:11, color:'#9ca3af' }}>{m.role||'Team Member'}</div>
            </div>
          </div>
        ))}

        {result.type === 'batches' && (result.items||[]).map((b, i) => (
          <div key={i} style={{ display:'flex', alignItems:'center', gap:10, padding:'10px 12px',
            borderBottom: i < result.items.length-1 ? '1px solid #f3f4f6' : 'none' }}>
            <div style={{ fontSize:22 }}>📚</div>
            <div style={{ flex:1, minWidth:0 }}>
              <div style={{ fontWeight:700, fontSize:13 }}>{b.name||b.label||'Batch'}</div>
              <div style={{ fontSize:11, color:'#9ca3af' }}>{b.status||b.meta||''}</div>
            </div>
          </div>
        ))}

        {((result.items||[]).length === 0 && !['empty','ai'].includes(result.type)) && (
          <div style={{ padding:'14px', textAlign:'center', color:'#9ca3af', fontSize:13 }}>
            No results{query ? ' for "' + query + '"' : ''}
          </div>
        )}
      </div>

      {/* Quick action chips */}
      {actions.length > 0 && (result.items||[]).length > 0 && (
        <div style={{ display:'flex', gap:5, marginTop:8, flexWrap:'wrap' }}>
          {actions.map(a => (
            <button key={a} onClick={() => onQuickAction && onQuickAction(a)}
              style={{ fontSize:11, fontWeight:700, padding:'4px 10px', borderRadius:8,
                border:'1px solid ' + ac + '44', background: ac + '12', color: ac,
                cursor:'pointer' }}>
              {a}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

window.PF_SEARCH = { SEARCH_META, getSearchOptions, executeSearch, SearchResultPanel };


// ===== 17-chat-composer.jsx =====
/* global React */
'use strict';

// ══════════════════════════════════════════════════════════════════════════
// KPI STORE — task metrics per employee, persisted to localStorage
// ══════════════════════════════════════════════════════════════════════════
window.KPI_STORE = (() => { try { return JSON.parse(localStorage.getItem('sr_kpi_v1') || '{}'); } catch(e) { return {}; } })();
window.kpiTrack = (employee, event, meta) => {
  if (!employee || !event) return;
  const s = window.KPI_STORE;
  if (!s[employee]) s[employee] = { assigned:0, completed:0, nudged:0, reassigned:0, pickedUp:0, overdue:0, taskLog:[] };
  const r = s[employee];
  r[event] = (r[event] || 0) + 1;
  if (meta) {
    r.taskLog = [...(r.taskLog||[]).slice(-49), { event, ...meta, at: new Date().toISOString() }];
  }
  r.lastUpdated = new Date().toISOString();
  try { localStorage.setItem('sr_kpi_v1', JSON.stringify(s)); } catch(e) {}
  document.dispatchEvent(new CustomEvent('sr:kpi-updated', { detail: { employee, event } }));
};

// ── Tag colour palette ──────────────────────────────────────────────────────
const CHAT_TAG_META = {
  client:   { bg:'#eff6ff', border:'#bfdbfe', text:'#1d4ed8', icon:'👤' },
  lead:     { bg:'#eff6ff', border:'#bfdbfe', text:'#1d4ed8', icon:'👤' },
  order:    { bg:'#fef3c7', border:'#fde68a', text:'#92400e', icon:'📦' },
  task:     { bg:'#f0fdf4', border:'#bbf7d0', text:'#166534', icon:'✓'  },
  notes:    { bg:'#faf5ff', border:'#e9d5ff', text:'#6b21a8', icon:'📝' },
  nudge:    { bg:'#fff7ed', border:'#fed7aa', text:'#9a3412', icon:'🔔' },
  reminder: { bg:'#fdf2f8', border:'#fbcfe8', text:'#9d174d', icon:'⏰' },
  link:     { bg:'#f8fafc', border:'#cbd5e1', text:'#475569', icon:'🔗' },
  status:   { bg:'#f0f9ff', border:'#bae6fd', text:'#0369a1', icon:'📊' },
  reassign: { bg:'#fdf4ff', border:'#f0abfc', text:'#7e22ce', icon:'↗'  },
  addwork:  { bg:'#fff1f2', border:'#fecdd3', text:'#9f1239', icon:'＋' },
  update:   { bg:'#f0fdf4', border:'#6ee7b7', text:'#065f46', icon:'🔄' },
};

// ── Slash commands registry ─────────────────────────────────────────────────
const SLASH_CMDS = [
  { cmd:'task',      label:'Assign Task',    desc:'Assign a task to a team member'           },
  { cmd:'addwork',   label:'Add Open Work',  desc:'Post a task anyone can pick up'           },
  { cmd:'status',    label:'Status Update',  desc:'Update status on a task'                  },
  { cmd:'reassign',  label:'Reassign Task',  desc:'Move a task to a different person'        },
  { cmd:'nudge',     label:'Nudge',          desc:'Nudge a colleague about a task'           },
  { cmd:'askstatus', label:'Ask Status',     desc:'Ask assigned person for task status'      },
  { cmd:'client',    label:'Client / Lead',  desc:'Claim or assign a client / lead'         },
  { cmd:'notes',     label:'Note / Doc',     desc:'Attach a rich note or document'          },
  { cmd:'link',      label:'Link',           desc:'Attach a URL or document link'           },
  { cmd:'order',     label:'Order / Ticket', desc:'Tag a work order or case'                },
  { cmd:'reminder',  label:'Reminder',       desc:'Schedule a follow-up'                    },
  { cmd:'search',    label:'Search',         desc:'Search leads, people, tasks, and more',  icon:'🔍', cat:'ops' },
];

const REMINDER_OPTS   = ['In 30 min','In 1 hour','In 2 hours','Tomorrow 9 AM','End of day'];
const DURATION_OPTS   = ['15 min','30 min','1 hour','2 hours','4 hours','1 day','2+ days'];
const COMPLEXITY_OPTS = ['Low','Medium','High','Critical'];
const STATUS_OPTS     = ['Open','In Progress','Blocked','Done','Resolved'];
const STATUS_COLORS   = { Open:'#6b7280','In Progress':'#ca8a04',Blocked:'#dc2626',Done:'#16a34a',Resolved:'#2563eb' };
const COMPLEX_COLORS  = { Low:'#9ca3af',Medium:'#2563eb',High:'#ca8a04',Critical:'#dc2626' };

// ── Command category colours ──────────────────────────────────────────────────
const CMD_CAT = {
  clinical:  { bg:'#f0fdf4', border:'#bbf7d0', text:'#166534' },
  procedure: { bg:'#fdf4ff', border:'#f0abfc', text:'#7e22ce' },
  booking:   { bg:'#eff6ff', border:'#bfdbfe', text:'#1d4ed8' },
  billing:   { bg:'#fff7ed', border:'#fed7aa', text:'#9a3412' },
  sales:     { bg:'#fef3c7', border:'#fde68a', text:'#92400e' },
  comm:      { bg:'#fdf2f8', border:'#fbcfe8', text:'#9d174d' },
  ops:       { bg:'#f0f9ff', border:'#bae6fd', text:'#0369a1' },
  reports:   { bg:'#faf5ff', border:'#e9d5ff', text:'#6b21a8' },
  finance:   { bg:'#f0fdf4', border:'#6ee7b7', text:'#065f46' },
  marketing: { bg:'#fff1f2', border:'#fecdd3', text:'#9f1239' },
  team:      { bg:'#eff6ff', border:'#bfdbfe', text:'#1d4ed8' },
  work:      { bg:'#f0fdf4', border:'#bbf7d0', text:'#166534' },
  issue:     { bg:'#fff7ed', border:'#fed7aa', text:'#9a3412' },
  safety:    { bg:'#fff1f2', border:'#fecdd3', text:'#9f1239' },
  quality:   { bg:'#f0f9ff', border:'#bae6fd', text:'#0369a1' },
  kpi:       { bg:'#faf5ff', border:'#e9d5ff', text:'#6b21a8' },
  admin:     { bg:'#f8fafc', border:'#cbd5e1', text:'#475569' },
  ai:        { bg:'#f5f3ff', border:'#ddd6fe', text:'#5b21b6' },
};

// ── Cross-industry AI commands ────────────────────────────────────────────────
const AI_CMDS = [
  { cmd:'ask',            label:'Ask AI',           desc:'Ask the AI assistant anything',     icon:'🤖', cat:'ai' },
  { cmd:'summarize',      label:'Summarize',         desc:'Summarize recent updates',           icon:'📋', cat:'ai' },
  { cmd:'nextbestaction', label:'Next Best Action',  desc:'Get AI-suggested next step',         icon:'💡', cat:'ai' },
  { cmd:'risk',           label:'Risk',              desc:'Highlight risks or blockers',        icon:'⚠️', cat:'ai' },
  { cmd:'priority',       label:'Prioritize',        desc:'Re-prioritize open work',            icon:'🎯', cat:'ai' },
  // /forecast and /revenue are role-gated — only injected for heads, managers and owners
  // (defined explicitly in REALTY_CMDS/LD_CMDS for those roles)
];

// ── Dermatology role-based commands ──────────────────────────────────────────
window.DERM_CMDS = {
  Therapist: [
    { cmd:'skinanalysis', label:'Skin Analysis',  desc:'Run skin analysis for client',   icon:'🔬', cat:'clinical', fields:[{key:'client',placeholder:'Client name'},{key:'notes',placeholder:'Observations…',multiline:true}] },
    { cmd:'routine',      label:'Routine',         desc:'Prescribe skincare routine',     icon:'🧴', cat:'clinical', fields:[{key:'client',placeholder:'Client name'},{key:'routine',placeholder:'AM / PM routine…',multiline:true}] },
    { cmd:'treatment',    label:'Treatment',       desc:'Log a treatment session',        icon:'💆', cat:'clinical', fields:[{key:'client',placeholder:'Client name'},{key:'type',placeholder:'Treatment type'},{key:'notes',placeholder:'Notes…',multiline:true}] },
    { cmd:'outcome',      label:'Outcome',         desc:'Record treatment outcome',       icon:'✅', cat:'clinical', fields:[{key:'client',placeholder:'Client name'},{key:'result',placeholder:'Outcome notes…',multiline:true}] },
    { cmd:'gallery',      label:'Gallery',         desc:'Attach progress photos',         icon:'🖼️', cat:'clinical' },
    { cmd:'followup',     label:'Follow-up',       desc:'Schedule a follow-up',           icon:'📅', cat:'comm',    fields:[{key:'client',placeholder:'Client name'},{key:'when',placeholder:'Date / time'}] },
    { cmd:'remind',       label:'Remind',          desc:'Send a client reminder',         icon:'🔔', cat:'comm',    fields:[{key:'client',placeholder:'Client name'},{key:'msg',placeholder:'Reminder message'}] },
    { cmd:'feedback',     label:'Feedback',        desc:'Request client feedback',        icon:'⭐', cat:'comm' },
    { cmd:'review',       label:'Review',          desc:'Review session notes',           icon:'📝', cat:'admin' },
    { cmd:'note',         label:'Note',            desc:'Add a clinical note',            icon:'📋', cat:'admin',   fields:[{key:'note',placeholder:'Clinical note…',multiline:true}] },
  ],
  Dermatologist: [
    { cmd:'patient',       label:'Patient',         desc:'Pull up patient record',         icon:'👤', cat:'clinical', fields:[{key:'name',placeholder:'Patient name'}] },
    { cmd:'consult',       label:'Consult',         desc:'Log a consultation',             icon:'🩺', cat:'clinical', fields:[{key:'patient',placeholder:'Patient name'},{key:'notes',placeholder:'Consultation notes…',multiline:true}] },
    { cmd:'diagnosis',     label:'Diagnosis',       desc:'Record diagnosis',               icon:'🔍', cat:'clinical', fields:[{key:'patient',placeholder:'Patient name'},{key:'diagnosis',placeholder:'Diagnosis details'}] },
    { cmd:'treatment',     label:'Treatment',       desc:'Plan treatment',                 icon:'💆', cat:'clinical', fields:[{key:'patient',placeholder:'Patient name'},{key:'plan',placeholder:'Treatment plan…',multiline:true}] },
    { cmd:'prescription',  label:'Prescription',    desc:'Write prescription',             icon:'💊', cat:'clinical', fields:[{key:'patient',placeholder:'Patient name'},{key:'drug',placeholder:'Drug / dosage'},{key:'instructions',placeholder:'Instructions'}] },
    { cmd:'laser',         label:'Laser',           desc:'Log laser procedure',            icon:'⚡', cat:'procedure', fields:[{key:'patient',placeholder:'Patient name'},{key:'area',placeholder:'Area & settings'}] },
    { cmd:'peel',          label:'Peel',            desc:'Log chemical peel',              icon:'🧪', cat:'procedure', fields:[{key:'patient',placeholder:'Patient name'},{key:'type',placeholder:'Peel type & depth'}] },
    { cmd:'prp',           label:'PRP',             desc:'Log PRP treatment',              icon:'🩸', cat:'procedure', fields:[{key:'patient',placeholder:'Patient name'},{key:'area',placeholder:'Area treated'}] },
    { cmd:'botox',         label:'Botox',           desc:'Log Botox injection',            icon:'💉', cat:'procedure', fields:[{key:'patient',placeholder:'Patient name'},{key:'units',placeholder:'Units & area'}] },
    { cmd:'filler',        label:'Filler',          desc:'Log filler treatment',           icon:'💉', cat:'procedure', fields:[{key:'patient',placeholder:'Patient name'},{key:'area',placeholder:'Area & volume'}] },
    { cmd:'followup',      label:'Follow-up',       desc:'Schedule follow-up',             icon:'📅', cat:'comm',     fields:[{key:'patient',placeholder:'Patient name'},{key:'when',placeholder:'Date / time'}] },
    { cmd:'progressphoto', label:'Progress Photo',  desc:'Attach progress photo',          icon:'📸', cat:'clinical' },
    { cmd:'beforeafter',   label:'Before / After',  desc:'Compare before/after photos',    icon:'🔄', cat:'clinical' },
    { cmd:'review',        label:'Review',          desc:'Review case notes',              icon:'📝', cat:'admin' },
    { cmd:'notes',         label:'Notes',           desc:'Add clinical notes',             icon:'📋', cat:'admin',   fields:[{key:'note',placeholder:'Clinical notes…',multiline:true}] },
  ],
  'Front Desk': [
    { cmd:'appointment',          label:'Appointment',          desc:'Book/check appointment',         icon:'📅', cat:'booking', fields:[{key:'client',placeholder:'Client name'},{key:'doctor',placeholder:'Doctor / Therapist'},{key:'when',placeholder:'Date & time'}] },
    { cmd:'register',             label:'Register',             desc:'Register new patient',           icon:'✍️', cat:'booking', fields:[{key:'name',placeholder:'Patient name'},{key:'phone',placeholder:'Phone number'}] },
    { cmd:'searchpatient',        label:'Search Patient',       desc:'Look up a patient',              icon:'🔍', cat:'booking', fields:[{key:'query',placeholder:'Name or phone'}] },
    { cmd:'cancel',               label:'Cancel',               desc:'Cancel appointment',             icon:'❌', cat:'booking', fields:[{key:'client',placeholder:'Client name'},{key:'reason',placeholder:'Reason'}] },
    { cmd:'reschedule',           label:'Reschedule',           desc:'Reschedule appointment',         icon:'🔁', cat:'booking', fields:[{key:'client',placeholder:'Client name'},{key:'newtime',placeholder:'New date & time'}] },
    { cmd:'payment',              label:'Payment',              desc:'Record payment',                 icon:'💳', cat:'billing', fields:[{key:'client',placeholder:'Client name'},{key:'amount',placeholder:'Amount (₹)'},{key:'mode',placeholder:'Cash / Card / UPI'}] },
    { cmd:'invoice',              label:'Invoice',              desc:'Generate invoice',               icon:'🧾', cat:'billing', fields:[{key:'client',placeholder:'Client name'},{key:'amount',placeholder:'Amount (₹)'}] },
    { cmd:'package',              label:'Package',              desc:'Assign or view packages',        icon:'📦', cat:'sales' },
    { cmd:'renew',                label:'Renew',                desc:'Renew a package',                icon:'🔄', cat:'sales' },
    { cmd:'offer',                label:'Offer',                desc:'Apply offer or discount',        icon:'🎁', cat:'sales' },
    { cmd:'session',              label:'Session',              desc:'Log a treatment session',        icon:'🗓️', cat:'ops' },
    { cmd:'queue',                label:'Queue',                desc:'View today queue',               icon:'👥', cat:'ops' },
    { cmd:'doctoravailability',   label:'Doctor Avail.',        desc:'Check doctor slots',             icon:'🩺', cat:'ops' },
    { cmd:'therapistavailability',label:'Therapist Avail.',     desc:'Check therapist slots',          icon:'💆', cat:'ops' },
    { cmd:'remind',               label:'Remind',               desc:'Send appointment reminder',      icon:'🔔', cat:'comm' },
    { cmd:'feedback',             label:'Feedback',             desc:'Collect client feedback',        icon:'⭐', cat:'comm' },
    { cmd:'followup',             label:'Follow-up',            desc:'Schedule follow-up call',        icon:'📞', cat:'comm' },
    { cmd:'dashboard',            label:'Dashboard',            desc:'View clinic dashboard',          icon:'📊', cat:'reports' },
    { cmd:'report',               label:'Report',               desc:'Generate a report',              icon:'📈', cat:'reports' },
    { cmd:'export',               label:'Export',               desc:'Export data',                    icon:'⬇️', cat:'reports' },
  ],
  Manager: [
    { cmd:'revenue',              label:'Revenue',              desc:'Revenue overview',               icon:'💰', cat:'finance' },
    { cmd:'packageperformance',   label:'Package Performance',  desc:'Track package sales',            icon:'📦', cat:'finance' },
    { cmd:'renewals',             label:'Renewals',             desc:'Renewal pipeline',               icon:'🔄', cat:'finance' },
    { cmd:'conversion',           label:'Conversion',           desc:'Lead-to-patient conversion',     icon:'📊', cat:'finance' },
    { cmd:'therapistperformance', label:'Therapist KPIs',       desc:'Therapist performance report',   icon:'💆', cat:'team' },
    { cmd:'doctorperformance',    label:'Doctor KPIs',          desc:'Doctor performance report',      icon:'🩺', cat:'team' },
    { cmd:'occupancy',            label:'Occupancy',            desc:'Room / slot occupancy rate',     icon:'🏥', cat:'ops' },
    { cmd:'inventory',            label:'Inventory',            desc:'Check product inventory',        icon:'📋', cat:'ops' },
    { cmd:'escalation',           label:'Escalation',           desc:'View escalations',               icon:'🚨', cat:'ops' },
    { cmd:'feedback',             label:'Feedback',             desc:'Aggregate feedback',             icon:'⭐', cat:'comm' },
    { cmd:'campaignroi',          label:'Campaign ROI',         desc:'Marketing campaign results',     icon:'📣', cat:'marketing' },
    { cmd:'dashboard',            label:'Dashboard',            desc:'Manager dashboard',              icon:'📊', cat:'reports' },
    { cmd:'report',               label:'Report',               desc:'Full report',                    icon:'📈', cat:'reports' },
    { cmd:'forecast',             label:'Forecast',             desc:'Revenue / capacity forecast',    icon:'🔮', cat:'reports' },
    { cmd:'export',               label:'Export',               desc:'Export reports',                 icon:'⬇️', cat:'reports' },
  ],
  Admin: [
    { cmd:'users',       label:'Users',       desc:'Manage user accounts',   icon:'👥', cat:'admin' },
    { cmd:'roles',       label:'Roles',       desc:'Manage roles & access',  icon:'🔑', cat:'admin' },
    { cmd:'settings',    label:'Settings',    desc:'System settings',        icon:'⚙️', cat:'admin' },
    { cmd:'permissions', label:'Permissions', desc:'Set permissions',        icon:'🔒', cat:'admin' },
    { cmd:'audit',       label:'Audit',       desc:'View audit logs',        icon:'📋', cat:'admin' },
    { cmd:'masters',     label:'Masters',     desc:'Manage master data',     icon:'🗂️', cat:'admin' },
    { cmd:'logs',        label:'Logs',        desc:'System logs',            icon:'📄', cat:'admin' },
    { cmd:'dashboard',   label:'Dashboard',   desc:'Admin dashboard',        icon:'📊', cat:'admin' },
  ],
};

// ── Manufacturing role-based commands ─────────────────────────────────────────
window.MFG_CMDS = {
  Technician: [
    { cmd:'start',      label:'Start',      desc:'Start a job or task',          icon:'▶️', cat:'work',   fields:[{key:'task',placeholder:'Task / machine name'}] },
    { cmd:'pause',      label:'Pause',      desc:'Pause current task',           icon:'⏸️', cat:'work',   fields:[{key:'reason',placeholder:'Reason for pause'}] },
    { cmd:'complete',   label:'Complete',   desc:'Mark task complete',           icon:'✅', cat:'work',   fields:[{key:'task',placeholder:'Task name'},{key:'notes',placeholder:'Completion notes'}] },
    { cmd:'breakdown',  label:'Breakdown',  desc:'Report machine breakdown',     icon:'🔧', cat:'issue',  fields:[{key:'machine',placeholder:'Machine / line'},{key:'issue',placeholder:'Describe breakdown…',multiline:true}] },
    { cmd:'issue',      label:'Issue',      desc:'Log a production issue',       icon:'⚠️', cat:'issue',  fields:[{key:'desc',placeholder:'Describe the issue…',multiline:true}] },
    { cmd:'escalate',   label:'Escalate',   desc:'Escalate to supervisor',       icon:'🚨', cat:'issue',  fields:[{key:'issue',placeholder:'What needs escalation?',multiline:true}] },
    { cmd:'downtime',   label:'Downtime',   desc:'Log downtime',                 icon:'⏱️', cat:'issue',  fields:[{key:'machine',placeholder:'Machine'},{key:'duration',placeholder:'Duration'},{key:'reason',placeholder:'Reason'}] },
    { cmd:'checklist',  label:'Checklist',  desc:'Run a safety checklist',       icon:'☑️', cat:'safety' },
    { cmd:'safety',     label:'Safety',     desc:'Log safety observation',       icon:'🦺', cat:'safety', fields:[{key:'obs',placeholder:'Safety observation…',multiline:true}] },
    { cmd:'inspection', label:'Inspection', desc:'Record inspection result',     icon:'🔍', cat:'safety' },
    { cmd:'spares',     label:'Spares',     desc:'Request spare parts',          icon:'🔩', cat:'ops',    fields:[{key:'part',placeholder:'Part name / code'},{key:'qty',placeholder:'Quantity'}] },
    { cmd:'note',       label:'Note',       desc:'Add a work note',              icon:'📝', cat:'ops',    fields:[{key:'note',placeholder:'Note…',multiline:true}] },
    { cmd:'handover',   label:'Handover',   desc:'Shift handover notes',         icon:'🤝', cat:'ops',    fields:[{key:'notes',placeholder:'Handover notes…',multiline:true}] },
    { cmd:'help',       label:'Help',       desc:'Request assistance',           icon:'🆘', cat:'ops',    fields:[{key:'issue',placeholder:'What do you need help with?'}] },
    { cmd:'task',       label:'Task',       desc:'View or update assigned task', icon:'✓',  cat:'work' },
  ],
  Supervisor: [
    { cmd:'assign',      label:'Assign',       desc:'Assign task to technician',   icon:'👤', cat:'team' },
    { cmd:'reassign',    label:'Reassign',      desc:'Reassign a task',             icon:'↗',  cat:'team' },
    { cmd:'nudge',       label:'Nudge',         desc:'Nudge a team member',         icon:'🔔', cat:'team' },
    { cmd:'production',  label:'Production',    desc:'Log production update',       icon:'🏭', cat:'ops',    fields:[{key:'line',placeholder:'Line / unit'},{key:'qty',placeholder:'Units produced'},{key:'notes',placeholder:'Notes'}] },
    { cmd:'target',      label:'Target',        desc:'Set or view shift target',    icon:'🎯', cat:'ops' },
    { cmd:'achievement', label:'Achievement',   desc:'Record achievement vs target',icon:'📊', cat:'ops' },
    { cmd:'downtime',    label:'Downtime',      desc:'Log line downtime',           icon:'⏱️', cat:'issue',  fields:[{key:'line',placeholder:'Line'},{key:'duration',placeholder:'Duration'},{key:'reason',placeholder:'Cause'}] },
    { cmd:'breakdown',   label:'Breakdown',     desc:'Escalate machine breakdown',  icon:'🔧', cat:'issue' },
    { cmd:'quality',     label:'Quality',       desc:'Log quality check',           icon:'🔍', cat:'quality',fields:[{key:'batch',placeholder:'Batch / lot'},{key:'result',placeholder:'Pass/Fail + notes'}] },
    { cmd:'safety',      label:'Safety',        desc:'Log safety incident',         icon:'🦺', cat:'safety' },
    { cmd:'attendance',  label:'Attendance',    desc:'Mark shift attendance',       icon:'✅', cat:'team' },
    { cmd:'shift',       label:'Shift',         desc:'Shift summary',               icon:'🔄', cat:'ops',    fields:[{key:'summary',placeholder:'Shift summary…',multiline:true}] },
    { cmd:'incident',    label:'Incident',      desc:'Report an incident',          icon:'🚨', cat:'safety', fields:[{key:'desc',placeholder:'Describe the incident…',multiline:true}] },
    { cmd:'escalate',    label:'Escalate',      desc:'Escalate to manager',         icon:'📢', cat:'issue' },
    { cmd:'report',      label:'Report',        desc:'Generate shift report',       icon:'📈', cat:'reports' },
  ],
  Manager: [
    { cmd:'dashboard',    label:'Dashboard',    desc:'Plant dashboard',                icon:'📊', cat:'reports' },
    { cmd:'production',   label:'Production',   desc:'Production overview',            icon:'🏭', cat:'ops' },
    { cmd:'oee',          label:'OEE',          desc:'Overall Equipment Effectiveness',icon:'⚙️', cat:'kpi' },
    { cmd:'downtime',     label:'Downtime',     desc:'Downtime analysis',              icon:'⏱️', cat:'kpi' },
    { cmd:'quality',      label:'Quality',      desc:'Quality metrics',                icon:'🔍', cat:'quality' },
    { cmd:'rejection',    label:'Rejection',    desc:'Rejection / scrap rate',         icon:'❌', cat:'quality' },
    { cmd:'yield',        label:'Yield',        desc:'Production yield',               icon:'📈', cat:'quality' },
    { cmd:'maintenance',  label:'Maintenance',  desc:'Maintenance schedule',           icon:'🔧', cat:'ops' },
    { cmd:'inventory',    label:'Inventory',    desc:'Inventory levels',               icon:'📦', cat:'ops' },
    { cmd:'safety',       label:'Safety',       desc:'Safety status',                  icon:'🦺', cat:'safety' },
    { cmd:'incident',     label:'Incident',     desc:'Incident log',                   icon:'🚨', cat:'safety' },
    { cmd:'cost',         label:'Cost',         desc:'Cost analysis',                  icon:'💰', cat:'finance' },
    { cmd:'productivity', label:'Productivity', desc:'Workforce productivity',         icon:'👥', cat:'kpi' },
    { cmd:'targets',      label:'Targets',      desc:'Set / review targets',           icon:'🎯', cat:'ops' },
    { cmd:'escalation',   label:'Escalation',   desc:'View escalations',               icon:'📢', cat:'ops' },
    { cmd:'approval',     label:'Approval',     desc:'Approve requests',               icon:'✅', cat:'ops' },
    { cmd:'planthealth',  label:'Plant Health', desc:'Overall plant health score',     icon:'🏭', cat:'kpi' },
    { cmd:'forecast',     label:'Forecast',     desc:'Production forecast',            icon:'🔮', cat:'reports' },
    { cmd:'report',       label:'Report',       desc:'Generate report',                icon:'📄', cat:'reports' },
    { cmd:'export',       label:'Export',       desc:'Export data',                    icon:'⬇️', cat:'reports' },
  ],
};

// ── Healthcare (HIMS) role-based commands ────────────────────────────────────
window.HC_CMDS = {
  Nurse: [
    { cmd:'vitals',     label:'Vitals',         desc:'Record patient vitals',          icon:'❤️', cat:'clinical', fields:[{key:'patient',placeholder:'Patient name'},{key:'bp',placeholder:'BP'},{key:'temp',placeholder:'Temp'},{key:'spo2',placeholder:'SpO2'}] },
    { cmd:'observation',label:'Observation',    desc:'Log clinical observation',       icon:'🔍', cat:'clinical', fields:[{key:'patient',placeholder:'Patient name'},{key:'obs',placeholder:'Observation…',multiline:true}] },
    { cmd:'medication', label:'Medication',     desc:'Log medication given',           icon:'💊', cat:'clinical', fields:[{key:'patient',placeholder:'Patient name'},{key:'drug',placeholder:'Drug & dose'},{key:'time',placeholder:'Time given'}] },
    { cmd:'note',       label:'Note',           desc:'Add nursing note',               icon:'📝', cat:'admin',    fields:[{key:'note',placeholder:'Nursing note…',multiline:true}] },
    { cmd:'escalate',   label:'Escalate',       desc:'Escalate to doctor',             icon:'🚨', cat:'ops',      fields:[{key:'patient',placeholder:'Patient name'},{key:'reason',placeholder:'Reason for escalation'}] },
    { cmd:'followup',   label:'Follow-up',      desc:'Schedule follow-up',             icon:'📅', cat:'comm',     fields:[{key:'patient',placeholder:'Patient name'},{key:'when',placeholder:'Date / time'}] },
    { cmd:'handover',   label:'Handover',       desc:'Shift handover notes',           icon:'🤝', cat:'ops',      fields:[{key:'notes',placeholder:'Handover notes…',multiline:true}] },
    { cmd:'task',       label:'Task',           desc:'View or pick up task',           icon:'✓',  cat:'ops' },
    { cmd:'remind',     label:'Remind',         desc:'Set a reminder',                 icon:'🔔', cat:'comm',     fields:[{key:'msg',placeholder:'Reminder message'},{key:'when',placeholder:'When'}] },
    { cmd:'report',     label:'Report',         desc:'File incident report',           icon:'📋', cat:'admin',    fields:[{key:'incident',placeholder:'Describe the incident…',multiline:true}] },
  ],
  Doctor: [
    { cmd:'consult',    label:'Consult',        desc:'Log a consultation',             icon:'🩺', cat:'clinical', fields:[{key:'patient',placeholder:'Patient name'},{key:'notes',placeholder:'Consultation notes…',multiline:true}] },
    { cmd:'diagnosis',  label:'Diagnosis',      desc:'Record diagnosis',               icon:'🔍', cat:'clinical', fields:[{key:'patient',placeholder:'Patient name'},{key:'diagnosis',placeholder:'Diagnosis'}] },
    { cmd:'prescription',label:'Prescription', desc:'Write prescription',             icon:'💊', cat:'clinical', fields:[{key:'patient',placeholder:'Patient name'},{key:'drug',placeholder:'Drug / dosage'},{key:'instructions',placeholder:'Instructions'}] },
    { cmd:'admission',  label:'Admission',      desc:'Admit patient',                  icon:'🏥', cat:'clinical', fields:[{key:'patient',placeholder:'Patient name'},{key:'ward',placeholder:'Ward / bed'},{key:'reason',placeholder:'Reason'}] },
    { cmd:'discharge',  label:'Discharge',      desc:'Discharge patient',              icon:'🚪', cat:'clinical', fields:[{key:'patient',placeholder:'Patient name'},{key:'instructions',placeholder:'Discharge instructions'}] },
    { cmd:'lab',        label:'Lab Order',      desc:'Order lab tests',                icon:'🧪', cat:'clinical', fields:[{key:'patient',placeholder:'Patient name'},{key:'tests',placeholder:'Tests required'}] },
    { cmd:'imaging',    label:'Imaging',        desc:'Order imaging',                  icon:'🖥️', cat:'clinical', fields:[{key:'patient',placeholder:'Patient name'},{key:'type',placeholder:'X-Ray / MRI / CT / USG'}] },
    { cmd:'referral',   label:'Referral',       desc:'Refer to specialist',            icon:'↗', cat:'clinical',  fields:[{key:'patient',placeholder:'Patient name'},{key:'specialist',placeholder:'Specialist type'},{key:'reason',placeholder:'Reason'}] },
    { cmd:'followup',   label:'Follow-up',      desc:'Schedule follow-up',             icon:'📅', cat:'comm',     fields:[{key:'patient',placeholder:'Patient name'},{key:'when',placeholder:'Date / time'}] },
    { cmd:'note',       label:'Note',           desc:'Add clinical note',              icon:'📝', cat:'admin',    fields:[{key:'note',placeholder:'Clinical note…',multiline:true}] },
    { cmd:'review',     label:'Review',         desc:'Review case file',               icon:'📋', cat:'admin' },
    { cmd:'escalate',   label:'Escalate',       desc:'Escalate critical case',         icon:'🚨', cat:'ops',      fields:[{key:'patient',placeholder:'Patient name'},{key:'concern',placeholder:'Clinical concern'}] },
    { cmd:'oncall',     label:'On-call',        desc:'Mark on-call status',            icon:'📞', cat:'ops' },
    { cmd:'task',       label:'Task',           desc:'View or update task',            icon:'✓',  cat:'ops' },
    { cmd:'report',     label:'Report',         desc:'Generate clinical report',       icon:'📈', cat:'admin' },
  ],
  'Front Desk': [
    { cmd:'appointment',label:'Appointment',   desc:'Book appointment',               icon:'📅', cat:'booking',  fields:[{key:'patient',placeholder:'Patient name'},{key:'doctor',placeholder:'Doctor'},{key:'when',placeholder:'Date & time'}] },
    { cmd:'register',   label:'Register',       desc:'Register new patient',           icon:'✍️', cat:'booking',  fields:[{key:'name',placeholder:'Patient name'},{key:'phone',placeholder:'Phone'}] },
    { cmd:'search',     label:'Search Patient', desc:'Look up a patient',              icon:'🔍', cat:'booking',  fields:[{key:'query',placeholder:'Name or phone'}] },
    { cmd:'cancel',     label:'Cancel',         desc:'Cancel appointment',             icon:'❌', cat:'booking',  fields:[{key:'patient',placeholder:'Patient name'},{key:'reason',placeholder:'Reason'}] },
    { cmd:'reschedule', label:'Reschedule',     desc:'Reschedule appointment',         icon:'🔁', cat:'booking',  fields:[{key:'patient',placeholder:'Patient name'},{key:'newtime',placeholder:'New date & time'}] },
    { cmd:'queue',      label:'Queue',          desc:'View today queue',               icon:'👥', cat:'ops' },
    { cmd:'payment',    label:'Payment',        desc:'Record payment',                 icon:'💳', cat:'billing',  fields:[{key:'patient',placeholder:'Patient name'},{key:'amount',placeholder:'Amount (₹)'},{key:'mode',placeholder:'Cash / Card / UPI'}] },
    { cmd:'invoice',    label:'Invoice',        desc:'Generate invoice',               icon:'🧾', cat:'billing',  fields:[{key:'patient',placeholder:'Patient name'},{key:'amount',placeholder:'Amount (₹)'}] },
    { cmd:'availability',label:'Availability', desc:'Check doctor availability',      icon:'🩺', cat:'ops' },
    { cmd:'remind',     label:'Remind',         desc:'Send reminder',                  icon:'🔔', cat:'comm',     fields:[{key:'patient',placeholder:'Patient name'},{key:'msg',placeholder:'Message'}] },
    { cmd:'feedback',   label:'Feedback',       desc:'Collect feedback',               icon:'⭐', cat:'comm' },
    { cmd:'report',     label:'Report',         desc:'Generate report',                icon:'📈', cat:'reports' },
    { cmd:'task',       label:'Task',           desc:'View or pick up task',           icon:'✓',  cat:'ops' },
  ],
  Manager: [
    { cmd:'dashboard',  label:'Dashboard',      desc:'Hospital dashboard',             icon:'📊', cat:'reports' },
    { cmd:'census',     label:'Census',         desc:'Patient census',                 icon:'🏥', cat:'ops' },
    { cmd:'occupancy',  label:'Occupancy',      desc:'Bed occupancy report',           icon:'🛏️', cat:'ops' },
    { cmd:'opd',        label:'OPD',            desc:'OPD stats',                      icon:'👥', cat:'ops' },
    { cmd:'revenue',    label:'Revenue',        desc:'Revenue overview',               icon:'💰', cat:'finance' },
    { cmd:'billing',    label:'Billing',        desc:'Billing summary',                icon:'🧾', cat:'finance' },
    { cmd:'staffing',   label:'Staffing',       desc:'Staff attendance & shifts',      icon:'👤', cat:'team' },
    { cmd:'doctorkpi',  label:'Doctor KPIs',    desc:'Doctor performance',             icon:'🩺', cat:'team' },
    { cmd:'escalation', label:'Escalation',     desc:'View escalations',               icon:'🚨', cat:'ops' },
    { cmd:'inventory',  label:'Inventory',      desc:'Medical inventory',              icon:'📦', cat:'ops' },
    { cmd:'feedback',   label:'Feedback',       desc:'Patient feedback',               icon:'⭐', cat:'comm' },
    { cmd:'report',     label:'Report',         desc:'Full report',                    icon:'📈', cat:'reports' },
    { cmd:'forecast',   label:'Forecast',       desc:'Capacity forecast',              icon:'🔮', cat:'reports' },
    { cmd:'export',     label:'Export',         desc:'Export data',                    icon:'⬇️', cat:'reports' },
  ],
  Admin: [
    { cmd:'users',      label:'Users',          desc:'Manage user accounts',           icon:'👥', cat:'admin' },
    { cmd:'roles',      label:'Roles',          desc:'Manage roles & access',          icon:'🔑', cat:'admin' },
    { cmd:'settings',   label:'Settings',       desc:'System settings',                icon:'⚙️', cat:'admin' },
    { cmd:'permissions',label:'Permissions',    desc:'Set permissions',                icon:'🔒', cat:'admin' },
    { cmd:'audit',      label:'Audit',          desc:'View audit logs',                icon:'📋', cat:'admin' },
    { cmd:'masters',    label:'Masters',        desc:'Manage master data',             icon:'🗂️', cat:'admin' },
    { cmd:'logs',       label:'Logs',           desc:'System logs',                    icon:'📄', cat:'admin' },
    { cmd:'dashboard',  label:'Dashboard',      desc:'Admin dashboard',                icon:'📊', cat:'admin' },
  ],
};


window.REALTY_CMDS = {
  'Sales Executive': [
    { cmd:'newlead',        label:'New Lead',          desc:'Create lead in <30 seconds',                icon:'➕', cat:'leads',
      flow:[ {ask:'Lead Name?',key:'name'}, {ask:'Mobile Number?',key:'phone'}, {ask:'Project Interested In?',key:'project'}, {ask:'Source?',key:'source',opts:['Meta','Google','CP','Walk-in']} ],
      ai:['Duplicate check','Lead scoring','Geo tagging','Source attribution'],
      result:{title:'Lead Created ✓', action:'Schedule Follow-up →'} },
    { cmd:'assign',         label:'Assign Lead',       desc:'Assign a lead to a team member',            icon:'👤', cat:'leads',
      flow:[ {ask:'Lead Name?',key:'lead',dsKey:'lead'}, {ask:'Assign to?',key:'to'} ],
      result:{title:'Lead Assigned ✓'} },
    { cmd:'leadstatus',     label:'Lead Status',       desc:'View or update lead status',                icon:'📊', cat:'leads',
      flow:[ {ask:'Select Lead:',key:'lead',dsKey:'lead'}, {ask:'What do you want to do?',key:'action',opts:['Update Status','View Details','Edit Info','Add Note']}, {ask:'New Status?',key:'status',opts:['🔥 Hot','🌡️ Warm','🧊 Cold','❌ Lost','🚫 Disqualified']} ],
      ai:['Score updated','Pipeline refreshed'],
      result:{title:'Lead Updated ✓'} },
    { cmd:'followup',       label:'Follow-up',         desc:'Schedule a follow-up',                      icon:'📅', cat:'leads',
      flow:[ {ask:'Lead Name?',key:'lead',dsKey:'lead'}, {ask:'Date & Time?',key:'when'}, {ask:'Note (optional)?',key:'note'} ],
      ai:['Talking points','Objection handling','Follow-up script'],
      result:{title:'Follow-up Scheduled ✓', action:'View Lead Profile →'} },
    { cmd:'remind',         label:'Remind',            desc:'Set a reminder — for yourself, a lead, or a task', icon:'⏰', cat:'ops',
      flow:[ {ask:'Remind about?',key:'about',opts:['Just me','A lead','A task']},
             {ask:'Which one? (skip if "Just me")',key:'target'},
             {ask:'Reminder Text?',key:'message'},
             {ask:'When?',key:'when',opts:['In 30 mins','In 1 hour','Tomorrow 9am','Tomorrow 11am']} ],
      result:{title:'Reminder Set ✓'} },
    { cmd:'nudge',          label:'Nudge',             desc:'Nudge a colleague about a lead',            icon:'👋', cat:'comm',
      flow:[ {ask:'Team Member?',key:'person'}, {ask:'Message?',key:'message'} ],
      result:{title:'Nudge Sent ✓'} },
    { cmd:'call',           label:'Log Call',          desc:'Log a call with a lead',                    icon:'📞', cat:'comm',
      flow:[ {ask:'Lead Name?',key:'lead',dsKey:'lead'}, {ask:'Call Type?',key:'type',opts:['First call','Follow-up','Clarification','Demo']}, {ask:'Outcome?',key:'outcome'} ],
      ai:['Call quality score','Next best action'],
      result:{title:'Call Logged ✓'} },
    { cmd:'whatsapp',       label:'WhatsApp',          desc:'Log a WhatsApp interaction',                icon:'💬', cat:'comm',
      flow:[ {ask:'Lead Name?',key:'lead',dsKey:'lead'}, {ask:'Message Summary?',key:'message'} ],
      result:{title:'WhatsApp Logged ✓'} },
    { cmd:'sitevisit',      label:'Site Visit',        desc:'Schedule a site visit',                     icon:'🏗️', cat:'visits',
      flow:[ {ask:'Lead Name?',key:'lead',dsKey:'lead'}, {ask:'Date?',key:'date'}, {ask:'Time?',key:'time',opts:['10:00 AM','11:00 AM','12:00 PM','2:00 PM','3:00 PM','4:00 PM']}, {ask:'Pickup Required?',key:'pickup',opts:['Yes — arrange pickup','No — self-drive']} ],
      ai:['Visit probability','No-show risk'],
      result:{title:'Site Visit Scheduled ✓', action:'Reminders sent to lead, driver & manager'} },
    { cmd:'reschedulevisit',label:'Reschedule Visit',  desc:'Reschedule a site visit',                   icon:'🔄', cat:'visits',
      flow:[ {ask:'Lead Name?',key:'lead',dsKey:'lead'}, {ask:'New Date & Time?',key:'newdate'}, {ask:'Reason?',key:'reason'} ],
      result:{title:'Visit Rescheduled ✓'} },
    { cmd:'visitdone',      label:'Visit Done',        desc:'Mark a site visit as completed',            icon:'✅', cat:'visits',
      flow:[ {ask:'Lead Name?',key:'lead',dsKey:'lead'}, {ask:'Visit Notes?',key:'notes'} ],
      ai:['Visit summary generated','Score updated'],
      result:{title:'Visit Marked Complete ✓', action:'Log feedback →'} },
    { cmd:'visitfeedback',  label:'Visit Feedback',    desc:'Log feedback from a site visit',            icon:'📝', cat:'visits',
      flow:[ {ask:'Lead Name?',key:'lead',dsKey:'lead'}, {ask:'Interested?',key:'interest',opts:['🔥 Hot — Ready to book','🌡️ Warm — Needs more info','🧊 Cold — Not interested']}, {ask:'Budget?',key:'budget'}, {ask:'Next Step?',key:'nextstep',opts:['Book Now','Schedule second visit','Send proposal','Follow up next week','Close as lost']} ],
      ai:['Visit summary','Lead score update','Recommended action'],
      result:{title:'Feedback Logged ✓', action:'Send proposal →'} },
    { cmd:'hotlead',        label:'Hot Lead',          desc:'Flag a lead as high priority',              icon:'🔥', cat:'leads',
      flow:[ {ask:'Lead Name?',key:'lead',dsKey:'lead'}, {ask:'Why Hot?',key:'reason'} ],
      ai:['Priority queue updated','Manager notified'],
      result:{title:'Flagged as Hot Lead ✓'} },
    { cmd:'booking',        label:'Booking',           desc:'Record a booking',                          icon:'🏠', cat:'booking',
      flow:[ {ask:'Lead Name?',key:'lead',dsKey:'lead'}, {ask:'Unit Number?',key:'unit'}, {ask:'Token Amount?',key:'amount'}, {ask:'Documents Received?',key:'docs',opts:['Yes — all docs','Partial — follow up needed','No — pending']} ],
      ai:['Inventory blocked','Manager notified','Finance notified'],
      result:{title:'Booking Completed ✓ 🎉', action:'Generate booking confirmation →'} },
    { cmd:'note',           label:'Note',              desc:'Add a note to a lead',                      icon:'📋', cat:'ops',
      flow:[ {ask:'Note?',key:'note'} ],
      result:{title:'Note Saved ✓'} },
    { cmd:'drive',          label:'Drive',             desc:"Attach a file from this lead's Drive folder", icon:'📁', cat:'ops', flow:[] },
    { cmd:'priority',       label:'Priority Conflict', desc:'Ask your manager to prioritize between two clashing tasks', icon:'⚖️', cat:'ops',
      flow:[ {ask:'First task/event?',key:'taskA'}, {ask:'Conflicts with?',key:'taskB'}, {ask:'Any context? (optional)',key:'note'} ] },
    { cmd:'escalate',       label:'Escalate',          desc:'Escalate an issue, event, or client to your manager', icon:'🚩', cat:'ops',
      flow:[ {ask:'What are you escalating?',key:'subject'}, {ask:'Urgency?',key:'severity',opts:['Normal','Urgent']}, {ask:'Details?',key:'message'} ] },
    { cmd:'stuck',          label:'I\'m Stuck',        desc:'Flag that you\'re blocked on a task or client', icon:'🧱', cat:'ops',
      flow:[ {ask:'Which task or client?',key:'subject'}, {ask:"What's blocking you?",key:'reason'} ] },
    { cmd:'search',         label:'Search',            desc:'Find a lead, sales person, or presales contact', icon:'🔍', cat:'ops', flow:[], result:{title:'Search'} },
  ],
  'Presales Executive': [
    { cmd:'newlead',        label:'New Lead',          desc:'Register a new inbound lead',               icon:'➕', cat:'leads',
      flow:[ {ask:'Lead Name?',key:'name'}, {ask:'Phone?',key:'phone'}, {ask:'Source?',key:'source',opts:['Meta','Google','WhatsApp','Referral','Walk-in','IVR']} ],
      ai:['Duplicate check','Source attribution','Initial scoring'],
      result:{title:'Lead Registered ✓', action:'Qualify this lead →'} },
    { cmd:'leadsource',     label:'Lead Source',       desc:'Tag the source of a lead',                  icon:'🔗', cat:'leads',
      flow:[ {ask:'Lead Name?',key:'lead',dsKey:'lead'}, {ask:'Source?',key:'source',opts:['Meta','Google','CP','Referral','Walk-in','Portal','WhatsApp','IVR']} ],
      result:{title:'Source Tagged ✓'} },
    { cmd:'qualify',        label:'Qualify',           desc:'Mark a lead as qualified',                  icon:'✅', cat:'leads',
      flow:[ {ask:'Lead Name?',key:'lead',dsKey:'lead'}, {ask:'Budget Range?',key:'budget',opts:['Under ₹50L','₹50L–1Cr','₹1Cr–2Cr','₹2Cr+']}, {ask:'Authority (Decision Maker)?',key:'authority',opts:['Yes — sole decision maker','Yes — with family','Influencer only']}, {ask:'Need (project match)?',key:'need',opts:['Strong match','Partial match','Weak match']}, {ask:'Timeline?',key:'timeline',opts:['Ready now','1–3 months','3–6 months','6 months+']} ],
      ai:['BANT score: 82/100','Recommended: Move to Sales Team'],
      result:{title:'Lead Qualified ✓', action:'Hand over to Sales →'} },
    { cmd:'disqualify',     label:'Disqualify',        desc:'Mark a lead as not qualified',              icon:'❌', cat:'leads',
      flow:[ {ask:'Lead Name?',key:'lead',dsKey:'lead'}, {ask:'Reason?',key:'reason',opts:['Budget too low','Not decision maker','Wrong location','Already bought','Investor only','Duplicate']} ],
      result:{title:'Lead Disqualified ✓'} },
    { cmd:'call',           label:'Log Call',          desc:'Log a call outcome',                        icon:'📞', cat:'comm',
      flow:[ {ask:'Lead Name?',key:'lead',dsKey:'lead'}, {ask:'Call Outcome?',key:'outcome',opts:['Interested — follow up','Not interested','Wrong number','No answer','Call back requested']} ],
      result:{title:'Call Logged ✓'} },
    { cmd:'followup',       label:'Follow-up',         desc:'Schedule follow-up with a lead',            icon:'📅', cat:'leads',
      flow:[ {ask:'Lead Name?',key:'lead',dsKey:'lead'}, {ask:'Date & Time?',key:'when'} ],
      ai:['Follow-up script generated'],
      result:{title:'Follow-up Scheduled ✓'} },
    { cmd:'schedulecall',   label:'Schedule Call',     desc:'Book a call with a lead',                   icon:'📲', cat:'comm',
      flow:[ {ask:'Lead Name?',key:'lead',dsKey:'lead'}, {ask:'Date?',key:'date'}, {ask:'Time?',key:'time',opts:['9:00 AM','10:00 AM','11:00 AM','12:00 PM','2:00 PM','3:00 PM','4:00 PM','5:00 PM']} ],
      result:{title:'Call Scheduled ✓', action:'Confirmation sent to lead'} },
    { cmd:'remind',         label:'Remind',            desc:'Set a reminder — for yourself, a lead, or a task', icon:'⏰', cat:'ops',
      flow:[ {ask:'Remind about?',key:'about',opts:['Just me','A lead','A task']},
             {ask:'Which one? (skip if "Just me")',key:'target'},
             {ask:'Reminder Text?',key:'message'},
             {ask:'When?',key:'when',opts:['In 30 mins','In 1 hour','Tomorrow 9am','Tomorrow 11am']} ],
      result:{title:'Reminder Set ✓'} },
    { cmd:'leadscore',      label:'Lead Score',        desc:'View lead quality score',                   icon:'⭐', cat:'leads',
      flow:[ {ask:'Lead Name?',key:'lead',dsKey:'lead'} ],
      ai:['Intent: High','Engagement: Medium','Budget Match: High','Score: 78/100'],
      result:{title:'Lead Score Report ✓'} },
    { cmd:'sitevisit',      label:'Site Visit',        desc:'Schedule a site visit',                     icon:'🏗️', cat:'visits',
      flow:[ {ask:'Lead Name?',key:'lead',dsKey:'lead'}, {ask:'Date?',key:'date'}, {ask:'Project?',key:'project'} ],
      result:{title:'Site Visit Scheduled ✓'} },
    { cmd:'hotlead',        label:'Hot Lead',          desc:'Flag lead as hot priority',                 icon:'🔥', cat:'leads',
      flow:[ {ask:'Lead Name?',key:'lead',dsKey:'lead'}, {ask:'Reason?',key:'reason'} ],
      result:{title:'Flagged as Hot Lead ✓'} },
    { cmd:'coldlead',       label:'Cold Lead',         desc:'Flag lead as low priority',                 icon:'🧊', cat:'leads',
      flow:[ {ask:'Lead Name?',key:'lead',dsKey:'lead'}, {ask:'Reason?',key:'reason'} ],
      result:{title:'Moved to Cold ✓'} },
    { cmd:'note',           label:'Note',              desc:'Add a note',                                icon:'📋', cat:'ops',
      flow:[ {ask:'Note?',key:'note'} ],
      result:{title:'Note Saved ✓'} },
    { cmd:'nudge',          label:'Nudge',             desc:'Nudge a colleague',                         icon:'👋', cat:'comm',
      flow:[ {ask:'Person?',key:'person'}, {ask:'Message?',key:'message'} ],
      result:{title:'Nudge Sent ✓'} },
    { cmd:'handover',       label:'Handover',          desc:'Hand a lead over to sales',                 icon:'🤝', cat:'leads',
      flow:[ {ask:'Select Lead:',key:'lead',dsKey:'lead'}, {ask:'Handover to?',key:'handto',dsKey:'employee'}, {ask:'Handover Notes?',key:'notes'} ],
      ai:['Handover trail created','Sales notified'],
      result:{title:'Lead Transferred ✓', action:'Auto trail created'} },
    { cmd:'targets',        label:'My Targets',        desc:"View today's call & conversion targets",    icon:'🎯', cat:'ops',
      flow:[ {ask:'Period?',key:'period',opts:['Today','This week','This month']} ],
      ai:['Calls today: 18/25','Connects: 6/12','Qualifications: 2/4','Site Visits: 1/3','Hot Leads: 3/5'],
      result:{title:'Target Summary', mock:true} },
    { cmd:'drive',   label:'Drive',  desc:"Attach a file from this lead's Drive folder", icon:'📁', cat:'ops', flow:[] },
    { cmd:'priority',       label:'Priority Conflict', desc:'Ask your manager to prioritize between two clashing tasks', icon:'⚖️', cat:'ops',
      flow:[ {ask:'First task/event?',key:'taskA'}, {ask:'Conflicts with?',key:'taskB'}, {ask:'Any context? (optional)',key:'note'} ] },
    { cmd:'escalate',       label:'Escalate',          desc:'Escalate an issue, event, or client to your manager', icon:'🚩', cat:'ops',
      flow:[ {ask:'What are you escalating?',key:'subject'}, {ask:'Urgency?',key:'severity',opts:['Normal','Urgent']}, {ask:'Details?',key:'message'} ] },
    { cmd:'stuck',          label:'I\'m Stuck',        desc:'Flag that you\'re blocked on a task or client', icon:'🧱', cat:'ops',
      flow:[ {ask:'Which task or client?',key:'subject'}, {ask:"What's blocking you?",key:'reason'} ] },
    { cmd:'search', label:'Search', desc:'Find a lead, team member, or contact', icon:'🔍', cat:'ops', flow:[], result:{title:'Search'} },
  ],
  'Channel Partner': [
    { cmd:'newlead',        label:'New Lead',          desc:'Refer a new lead',                          icon:'➕', cat:'leads',
      flow:[ {ask:'Lead Name?',key:'name'}, {ask:'Phone?',key:'phone'}, {ask:'Project Interest?',key:'project'} ],
      ai:['CP attribution logged','Commission trail started'],
      result:{title:'Referral Submitted ✓'} },
    { cmd:'leadstatus',     label:'Lead Status',       desc:'Check status of a referred lead',           icon:'📊', cat:'leads',
      flow:[ {ask:'Lead Name?',key:'lead',dsKey:'lead'} ],
      result:{title:'Lead Status'} },
    { cmd:'sitevisit',      label:'Site Visit',        desc:'Request a site visit',                      icon:'🏗️', cat:'visits',
      flow:[ {ask:'Lead Name?',key:'lead',dsKey:'lead'}, {ask:'Preferred Date?',key:'date'}, {ask:'Project?',key:'project'} ],
      result:{title:'Visit Requested ✓'} },
    { cmd:'booking',        label:'Booking',           desc:'Log a booking from your lead',              icon:'🏠', cat:'booking',
      flow:[ {ask:'Lead Name?',key:'lead',dsKey:'lead'}, {ask:'Unit Number?',key:'unit'} ],
      ai:['Commission calculated','Finance notified'],
      result:{title:'Booking Logged ✓ 🎉', action:'View commission status →'} },
    { cmd:'commission',     label:'Commission',        desc:'Check or raise commission status',          icon:'💰', cat:'finance',
      flow:[ {ask:'Booking / Lead Reference?',key:'booking'}, {ask:'Expected Amount?',key:'amount'} ],
      result:{title:'Commission Request Raised ✓'} },
    { cmd:'paymentstatus',  label:'Payment Status',    desc:'Check payment status of a booking',         icon:'💳', cat:'finance',
      flow:[ {ask:'Booking Reference?',key:'booking'} ],
      result:{title:'Payment Status'} },
    { cmd:'documents',      label:'Documents',         desc:'Request or upload documents',               icon:'📂', cat:'ops',
      flow:[ {ask:'Lead Name?',key:'lead',dsKey:'lead'}, {ask:'Document Type?',key:'doctype',opts:['KYC','PAN Card','Income Proof','Bank Statement','Agreement']} ],
      result:{title:'Document Request Sent ✓'} },
    { cmd:'followup',       label:'Follow-up',         desc:'Schedule a follow-up',                      icon:'📅', cat:'leads',
      flow:[ {ask:'Lead Name?',key:'lead',dsKey:'lead'}, {ask:'When?',key:'when'} ],
      result:{title:'Follow-up Scheduled ✓'} },
    { cmd:'brochure',       label:'Brochure',          desc:'Request project brochure',                  icon:'📄', cat:'ops',
      flow:[ {ask:'Project Name?',key:'project'} ],
      result:{title:'Brochure Requested ✓', action:'Will be shared via WhatsApp'} },
    { cmd:'inventory',      label:'Inventory',         desc:'Check available inventory',                 icon:'🏢', cat:'ops',
      flow:[ {ask:'Project?',key:'project'}, {ask:'Unit Type?',key:'type',opts:['1BHK','2BHK','3BHK','4BHK','Villa','Studio']} ],
      result:{title:'Inventory Status'} },
    { cmd:'offers',         label:'Offers',            desc:'View current project offers',               icon:'🎁', cat:'ops',
      flow:[ {ask:'Project Name?',key:'project'} ],
      result:{title:'Current Offers'} },
    { cmd:'contactmanager', label:'Contact Manager',   desc:'Message the sales manager',                 icon:'📩', cat:'comm',
      flow:[ {ask:'Your Message?',key:'message'}, {ask:'Urgency?',key:'urgency',opts:['High — urgent','Normal']} ],
      result:{title:'Manager Notified ✓'} },
    { cmd:'search', label:'Search', desc:'Find a lead, booking, or team member', icon:'🔍', cat:'ops', flow:[], result:{title:'Search'} },
  ],
  'Sales Manager': [
    { cmd:'dashboard',      label:'Dashboard',         desc:'View team sales dashboard',                 icon:'📊', cat:'reports',
      flow:[], ai:['New Leads: 420','Qualified: 155','Site Visits: 82','Bookings: 21','Conversion: 5%'],
      result:{title:'Team Dashboard', mock:true} },
    { cmd:'assign',         label:'Assign Lead',       desc:'Assign a lead to an executive',             icon:'👤', cat:'leads',
      flow:[ {ask:'Lead Name?',key:'lead',dsKey:'lead'}, {ask:'Assign to (Executive)?',key:'to'} ],
      result:{title:'Lead Assigned ✓'} },
    { cmd:'reassign',       label:'Reassign',          desc:'Move a lead between executives',            icon:'🔄', cat:'leads',
      flow:[ {ask:'Lead Name?',key:'lead',dsKey:'lead'}, {ask:'From?',key:'from'}, {ask:'To?',key:'to'} ],
      result:{title:'Lead Reassigned ✓'} },
    { cmd:'nudge',          label:'Nudge',             desc:'Nudge a team member',                       icon:'👋', cat:'comm',
      flow:[ {ask:'Team Member?',key:'person'}, {ask:'Message?',key:'message'} ],
      result:{title:'Nudge Sent ✓'} },
    { cmd:'escalate',       label:'Escalate',          desc:'Escalate a lead or issue',                  icon:'🚨', cat:'ops',
      flow:[ {ask:'Lead / Issue?',key:'lead'}, {ask:'Escalate to?',key:'to'}, {ask:'Reason?',key:'reason'} ],
      result:{title:'Escalation Raised ✓'} },
    { cmd:'teamperformance',label:'Team Performance',  desc:'View team performance report',              icon:'👥', cat:'reports',
      flow:[ {ask:'Period?',key:'period',opts:['Today','This week','This month','Last month']} ],
      ai:['Leads assigned','Follow-up quality','Visits completed','Bookings','Conversion rate'],
      result:{title:'Team Performance Report', mock:true} },
    { cmd:'pipeline',       label:'Pipeline',          desc:'View sales pipeline',                       icon:'🔀', cat:'reports',
      flow:[], ai:['New Leads: 420','Qualified: 155','Site Visits: 82','Bookings: 21','Conversion: 5%','⚠️ Visit conversion below target'],
      result:{title:'Pipeline Summary', mock:true} },
    { cmd:'conversion',     label:'Conversion',        desc:'Check conversion rates',                    icon:'📈', cat:'reports',
      flow:[ {ask:'Period?',key:'period',opts:['This week','This month','This quarter']} ],
      result:{title:'Conversion Report', mock:true} },
    { cmd:'sitevisitreport',label:'Visit Report',      desc:'Site visit summary report',                 icon:'🏗️', cat:'reports',
      flow:[ {ask:'Period?',key:'period',opts:['Today','This week','This month']}, {ask:'Agent? (leave blank for all)',key:'agent'} ],
      result:{title:'Site Visit Report', mock:true} },
    { cmd:'bookingreport',  label:'Booking Report',    desc:'Booking summary report',                    icon:'🏠', cat:'reports',
      flow:[ {ask:'Period?',key:'period',opts:['This week','This month','This quarter']} ],
      result:{title:'Booking Report', mock:true} },
    { cmd:'lostanalysis',   label:'Lost Analysis',     desc:'Analyse lost leads',                        icon:'📉', cat:'reports',
      flow:[ {ask:'Period?',key:'period',opts:['This week','This month','This quarter']} ],
      ai:['Top loss reason: Price','Second: Competition','Risk: 26 hot leads may churn'],
      result:{title:'Lost Lead Analysis', mock:true} },
    { cmd:'forecast',       label:'Forecast',          desc:'View sales forecast',                       icon:'🔮', cat:'reports',
      flow:[ {ask:'Period?',key:'period',opts:['This month','Next month','This quarter']} ],
      ai:['Projected Revenue: ₹5.2 Cr','Confidence: 82%'],
      result:{title:'Sales Forecast', mock:true} },
    { cmd:'review',         label:'Review',            desc:'Review an executive performance',           icon:'📋', cat:'ops',
      flow:[ {ask:'Executive Name?',key:'agent'}, {ask:'Period?',key:'period',opts:['This week','This month']} ],
      ai:['Leads assigned','Follow-up quality','Conversion','Coaching suggestions'],
      result:{title:'Performance Review Generated', mock:true} },
    { cmd:'followupaudit',  label:'Follow-up Audit',   desc:'Audit follow-up activities',                icon:'🔍', cat:'ops',
      flow:[ {ask:'Agent? (leave blank for all)',key:'agent'} ],
      ai:['Ajay: 17 missed','Rohan: 12 missed','Amit: 9 missed','Risk: 26 leads may be lost'],
      result:{title:'Follow-up Audit ⚠️', mock:true} },
    { cmd:'hotlead',        label:'Hot Lead',          desc:'Flag a lead as high priority',              icon:'🔥', cat:'leads',
      flow:[ {ask:'Lead Name?',key:'lead',dsKey:'lead'}, {ask:'Priority Reason?',key:'priority'} ],
      result:{title:'Flagged as Hot Lead ✓'} },
    { cmd:'approval',       label:'Approval',          desc:'Approve or reject a request',               icon:'✅', cat:'ops',
      flow:[ {ask:'Request Detail?',key:'request'}, {ask:'Decision?',key:'action',opts:['Approve','Reject','Escalate','Counter offer']} ],
      result:{title:'Approval Recorded ✓'} },
    { cmd:'inventory',      label:'Inventory',         desc:'View inventory status',                     icon:'🏢', cat:'ops',
      flow:[ {ask:'Project?',key:'project'}, {ask:'Unit Type?',key:'type',opts:['All','1BHK','2BHK','3BHK','4BHK','Villa']} ],
      result:{title:'Inventory Status', mock:true} },
    { cmd:'campaignlead',   label:'Campaign Lead',     desc:'View leads from a campaign',                icon:'📣', cat:'leads',
      flow:[ {ask:'Campaign Name?',key:'campaign'} ],
      result:{title:'Campaign Lead Report', mock:true} },
    { cmd:'report',         label:'Report',            desc:'Generate a report',                         icon:'📑', cat:'reports',
      flow:[ {ask:'Report Type?',key:'type',opts:['Daily summary','Weekly','Pipeline','Conversion','Lost analysis']}, {ask:'Period?',key:'period',opts:['Today','This week','This month']} ],
      result:{title:'Report Generated ✓', action:'Download →'} },
    { cmd:'export',         label:'Export',            desc:'Export data',                               icon:'⬇️', cat:'ops',
      flow:[ {ask:'Data Type?',key:'type',opts:['Leads','Visits','Bookings','Team performance']}, {ask:'Format?',key:'format',opts:['PDF','Excel','CSV']} ],
      result:{title:'Export Started ✓', action:'Will be emailed shortly'} },
    { cmd:'remind',         label:'Remind',            desc:'Set a reminder — for yourself, a lead, or a task', icon:'⏰', cat:'ops',
      flow:[ {ask:'Remind about?',key:'about',opts:['Just me','A lead','A task']},
             {ask:'Which one? (skip if "Just me")',key:'target'},
             {ask:'Reminder Text?',key:'message'},
             {ask:'When?',key:'when',opts:['In 30 mins','In 1 hour','Tomorrow 9am','Tomorrow 11am']} ],
      result:{title:'Reminder Set ✓'} },
    { cmd:'meeting',        label:'Meeting',           desc:'Schedule a meeting with one or more team members', icon:'📅', cat:'ops',
      flow:[ {ask:'Meeting Title?',key:'title'},
             {ask:'With whom? (names, comma-separated)',key:'attendees'},
             {ask:'Date & Time? (e.g. 25 Sep 3:00 PM)',key:'when'},
             {ask:'How?',key:'type',opts:['Google Meet','Zoom','In person']},
             {ask:'Location (if in person, else skip)',key:'location'} ],
      result:{title:'Scheduling…'} },
    { cmd:'drive',          label:'Drive',             desc:"Attach a file from this lead's Drive folder", icon:'📁', cat:'ops', flow:[] },
    { cmd:'search', label:'Search', desc:'Find a lead, task, or team member', icon:'🔍', cat:'ops', flow:[], result:{title:'Search'} },
  ],
  'Sales Head': [
    { cmd:'revenue',        label:'Revenue',           desc:'View revenue summary',                      icon:'💰', cat:'finance',
      flow:[ {ask:'Period?',key:'period',opts:['This month','This quarter','This year']} ],
      ai:['Total: ₹5.2 Cr','vs Target: 87%','vs Last month: +12%'],
      result:{title:'Revenue Summary', mock:true} },
    { cmd:'forecast',       label:'Forecast',          desc:'Revenue forecast',                          icon:'🔮', cat:'finance',
      flow:[ {ask:'Period?',key:'period',opts:['This month','Next month','This quarter']} ],
      ai:['Projected: ₹5.2 Cr','Confidence: 82%','Pipeline velocity: High'],
      result:{title:'Revenue Forecast', mock:true} },
    { cmd:'pipeline',       label:'Pipeline',          desc:'Sales pipeline overview',                   icon:'🔀', cat:'reports',
      flow:[], ai:['Total in pipeline: ₹24 Cr','Hot leads: 42','Visits this week: 18'],
      result:{title:'Pipeline Overview', mock:true} },
    { cmd:'salesdashboard', label:'Sales Dashboard',   desc:'Full sales dashboard',                      icon:'📊', cat:'reports',
      flow:[],
      result:{title:'Sales Dashboard', mock:true} },
    { cmd:'conversion',     label:'Conversion',        desc:'Conversion rate analysis',                  icon:'📈', cat:'reports',
      flow:[ {ask:'Period?',key:'period',opts:['This month','This quarter','This year']} ],
      result:{title:'Conversion Analysis', mock:true} },
    { cmd:'bookingreport',  label:'Booking Report',    desc:'Booking summary',                           icon:'🏠', cat:'reports',
      flow:[ {ask:'Period?',key:'period',opts:['This week','This month','This quarter']} ],
      result:{title:'Booking Report', mock:true} },
    { cmd:'inventory',      label:'Inventory',         desc:'Current inventory status',                  icon:'🏢', cat:'ops',
      flow:[ {ask:'Project? (all if blank)',key:'project'} ],
      result:{title:'Inventory Status', mock:true} },
    { cmd:'projectwise',    label:'Project-wise',      desc:'Performance by project',                    icon:'🏗️', cat:'reports',
      flow:[ {ask:'Period?',key:'period',opts:['This month','This quarter']} ],
      result:{title:'Project-wise Report', mock:true} },
    { cmd:'regionwise',     label:'Region-wise',       desc:'Performance by region',                     icon:'🗺️', cat:'reports',
      flow:[ {ask:'Period?',key:'period',opts:['This month','This quarter']} ],
      result:{title:'Region-wise Report', mock:true} },
    { cmd:'salesmanagerreport',label:'Manager Report', desc:'Sales manager performance',                 icon:'👤', cat:'reports',
      flow:[ {ask:'Period?',key:'period',opts:['This week','This month']} ],
      result:{title:'Sales Manager Report', mock:true} },
    { cmd:'campaignroi',    label:'Campaign ROI',      desc:'ROI on marketing campaigns',                icon:'📣', cat:'reports',
      flow:[ {ask:'Period?',key:'period',opts:['This month','This quarter']} ],
      ai:['Meta: 4.3x ROI','Google: 6.1x ROI','CP: 8.2x ROI'],
      result:{title:'Campaign ROI Report', mock:true} },
    { cmd:'escalation',     label:'Escalation',        desc:'View and handle escalations',               icon:'🚨', cat:'ops',
      flow:[ {ask:'Escalation ID / Lead? (blank for all)',key:'id'} ],
      result:{title:'Open Escalations', mock:true} },
    { cmd:'review',         label:'Review',            desc:'Performance review',                        icon:'📋', cat:'ops',
      flow:[ {ask:'Manager Name?',key:'manager'}, {ask:'Period?',key:'period',opts:['This month','Last month','This quarter']} ],
      result:{title:'Manager Review', mock:true} },
    { cmd:'approve',        label:'Approve',           desc:'Approve a request',                         icon:'✅', cat:'ops',
      flow:[ {ask:'Request Detail?',key:'request'}, {ask:'Decision?',key:'action',opts:['Approve','Reject','Escalate']} ],
      result:{title:'Approval Recorded ✓'} },
    { cmd:'pricing',        label:'Pricing',           desc:'View or update pricing',                    icon:'🏷️', cat:'ops',
      flow:[ {ask:'Project?',key:'project'}, {ask:'Unit Type?',key:'unit',opts:['All','1BHK','2BHK','3BHK','4BHK']} ],
      ai:['Tower B: Increase 2%','Tower D: Offer Discount'],
      result:{title:'Pricing Overview', mock:true} },
    { cmd:'discountapproval',label:'Discount Approval',desc:'Approve a discount request',               icon:'💸', cat:'finance',
      flow:[ {ask:'Customer Name?',key:'lead'}, {ask:'Discount Requested?',key:'discount'}, {ask:'Decision?',key:'action',opts:['Approve','Reject','Counter offer']} ],
      ai:['Margin impact calculated','GM notified'],
      result:{title:'Discount Decision Recorded ✓'} },
    { cmd:'leaderboard',    label:'Leaderboard',       desc:'Team leaderboard',                          icon:'🏆', cat:'reports',
      flow:[ {ask:'Period?',key:'period',opts:['This week','This month','This quarter']} ],
      result:{title:'Team Leaderboard', mock:true} },
    { cmd:'report',         label:'Report',            desc:'Generate a report',                         icon:'📑', cat:'reports',
      flow:[ {ask:'Report Type?',key:'type',opts:['Revenue','Pipeline','Conversion','Booking','Lost analysis']}, {ask:'Period?',key:'period',opts:['This week','This month','This quarter']} ],
      result:{title:'Report Generated ✓', action:'Download →'} },
    { cmd:'export',         label:'Export',            desc:'Export data',                               icon:'⬇️', cat:'ops',
      flow:[ {ask:'Data Type?',key:'type',opts:['Revenue','Leads','Visits','Bookings','Team']}, {ask:'Format?',key:'format',opts:['PDF','Excel','CSV']} ],
      result:{title:'Export Started ✓'} },
    { cmd:'targets',        label:'Targets',           desc:'View or update sales targets',              icon:'🎯', cat:'ops',
      flow:[ {ask:'Team / Region?',key:'team'}, {ask:'Period?',key:'period',opts:['This month','This quarter']} ],
      result:{title:'Targets Overview', mock:true} },
    { cmd:'remind',         label:'Remind',            desc:'Set a reminder — for yourself, a lead, or a task', icon:'⏰', cat:'ops',
      flow:[ {ask:'Remind about?',key:'about',opts:['Just me','A lead','A task']},
             {ask:'Which one? (skip if "Just me")',key:'target'},
             {ask:'Reminder Text?',key:'message'},
             {ask:'When?',key:'when',opts:['In 30 mins','In 1 hour','Tomorrow 9am','Tomorrow 11am']} ],
      result:{title:'Reminder Set ✓'} },
    { cmd:'meeting',        label:'Meeting',           desc:'Schedule a meeting with one or more team members', icon:'📅', cat:'ops',
      flow:[ {ask:'Meeting Title?',key:'title'},
             {ask:'With whom? (names, comma-separated)',key:'attendees'},
             {ask:'Date & Time? (e.g. 25 Sep 3:00 PM)',key:'when'},
             {ask:'How?',key:'type',opts:['Google Meet','Zoom','In person']},
             {ask:'Location (if in person, else skip)',key:'location'} ],
      result:{title:'Scheduling…'} },
    { cmd:'drive',          label:'Drive',             desc:"Attach a file from this lead's Drive folder", icon:'📁', cat:'ops', flow:[] },
    { cmd:'search', label:'Search', desc:'Find a lead, booking, or team member', icon:'🔍', cat:'ops', flow:[], result:{title:'Search'} },
  ],
  'Marketing Head': [
    { cmd:'campaign',       label:'Campaign',          desc:'Create or view a campaign',                 icon:'📣', cat:'campaign',
      flow:[ {ask:'Campaign Name?',key:'name'}, {ask:'Objective?',key:'objective',opts:['Lead Generation','Site Visits','Brand Awareness','Remarketing']}, {ask:'Budget?',key:'budget'} ],
      ai:['Audience targeting suggested','Landing page linked'],
      result:{title:'Campaign Created ✓', action:'Launch now →'} },
    { cmd:'campaignroi',    label:'Campaign ROI',      desc:'ROI report for a campaign',                 icon:'📈', cat:'reports',
      flow:[ {ask:'Campaign Name?',key:'campaign'}, {ask:'Period?',key:'period',opts:['This month','This quarter']} ],
      ai:['Spend: ₹2,00,000','Leads: 430','Bookings: 7','ROI: 4.3x','Recommendation: Increase budget'],
      result:{title:'Campaign ROI Report', mock:true} },
    { cmd:'leadsource',     label:'Lead Source',       desc:'Analyse lead sources',                     icon:'🔗', cat:'reports',
      flow:[ {ask:'Period?',key:'period',opts:['This week','This month','This quarter']} ],
      ai:['Meta: 42%','Google: 28%','CP: 18%','Walk-in: 12%'],
      result:{title:'Lead Source Analysis', mock:true} },
    { cmd:'costperlead',    label:'Cost Per Lead',     desc:'Cost per lead by channel',                  icon:'💰', cat:'reports',
      flow:[ {ask:'Channel? (all if blank)',key:'channel'}, {ask:'Period?',key:'period',opts:['This month','This quarter']} ],
      ai:['Meta CPL: ₹465','Google CPL: ₹380','CP CPL: ₹220'],
      result:{title:'Cost Per Lead Report', mock:true} },
    { cmd:'landingpage',    label:'Landing Page',      desc:'View or create a landing page',             icon:'🌐', cat:'campaign',
      flow:[ {ask:'Project?',key:'project'}, {ask:'Action?',key:'action',opts:['View performance','Create new','Update CTA','A/B test']} ],
      result:{title:'Landing Page', mock:true} },
    { cmd:'creative',       label:'Creative',          desc:'Request or review a creative',              icon:'🎨', cat:'campaign',
      flow:[ {ask:'Creative Type?',key:'type',opts:['Banner','Video','Social post','Email','WhatsApp image']}, {ask:'Project?',key:'project'}, {ask:'Brief?',key:'brief'} ],
      result:{title:'Creative Request Sent ✓'} },
    { cmd:'leadquality',    label:'Lead Quality',      desc:'Lead quality analysis',                     icon:'⭐', cat:'reports',
      flow:[ {ask:'Campaign? (all if blank)',key:'campaign'}, {ask:'Period?',key:'period',opts:['This week','This month']} ],
      ai:['Meta Leads: 72/100','Google Leads: 83/100','CP Leads: 90/100'],
      result:{title:'Lead Quality Report', mock:true} },
    { cmd:'utm',            label:'UTM',               desc:'Generate UTM parameters',                   icon:'🔍', cat:'campaign',
      flow:[ {ask:'Campaign Name?',key:'campaign'}, {ask:'Source?',key:'source',opts:['meta','google','email','whatsapp','portal']}, {ask:'Medium?',key:'medium',opts:['cpc','organic','social','email','referral']} ],
      result:{title:'UTM Link Generated ✓', action:'Copy link →'} },
    { cmd:'leadvolume',     label:'Lead Volume',       desc:'Lead volume by channel and period',         icon:'📊', cat:'reports',
      flow:[ {ask:'Period?',key:'period',opts:['Today','This week','This month']} ],
      result:{title:'Lead Volume Report', mock:true} },
    { cmd:'funnel',         label:'Funnel',            desc:'Conversion funnel report',                  icon:'🔀', cat:'reports',
      flow:[ {ask:'Period?',key:'period',opts:['This week','This month','This quarter']} ],
      ai:['1000 Leads','420 Qualified','120 Visits','28 Bookings','Leakage: Qualified→Visit (71%)'],
      result:{title:'Lead Funnel Report', mock:true} },
    { cmd:'remarketing',    label:'Remarketing',       desc:'Create or view remarketing campaign',       icon:'🔄', cat:'campaign',
      flow:[ {ask:'Audience Segment?',key:'audience',opts:['Visited but not qualified','Qualified but no visit','Visited but no booking','Lost leads']}, {ask:'Channel?',key:'channel',opts:['Meta','Google','WhatsApp']} ],
      result:{title:'Remarketing Campaign Set ✓'} },
    { cmd:'event',          label:'Event',             desc:'Create or promote an event',                icon:'📅', cat:'campaign',
      flow:[ {ask:'Event Name?',key:'name'}, {ask:'Date?',key:'date'}, {ask:'Type?',key:'type',opts:['Open House','Site Walk','Virtual Tour','Panel Discussion','Launch Event']} ],
      result:{title:'Event Created ✓', action:'Promote now →'} },
    { cmd:'brochure',       label:'Brochure',          desc:'Request or share a brochure',               icon:'📄', cat:'ops',
      flow:[ {ask:'Project?',key:'project'} ],
      result:{title:'Brochure Shared ✓'} },
    { cmd:'inventory',      label:'Inventory',         desc:'Check current inventory',                   icon:'🏢', cat:'ops',
      flow:[ {ask:'Project?',key:'project'} ],
      result:{title:'Inventory Status', mock:true} },
    { cmd:'report',         label:'Report',            desc:'Generate marketing report',                 icon:'📑', cat:'reports',
      flow:[ {ask:'Report Type?',key:'type',opts:['Campaign performance','Lead quality','Channel ROI','Funnel']}, {ask:'Period?',key:'period',opts:['This week','This month','This quarter']} ],
      result:{title:'Report Generated ✓', action:'Download →'} },
    { cmd:'dashboard',      label:'Dashboard',         desc:'Marketing dashboard',                       icon:'📊', cat:'reports',
      flow:[], ai:['Total Leads: 1,240','Cost Per Lead: ₹412','Campaign Count: 8 active','Top Source: Meta (43%)','Conversion: 2.3%'],
      result:{title:'Marketing Dashboard', mock:true} },
    { cmd:'agency',         label:'Agency',            desc:'Agency brief or review',                    icon:'🤝', cat:'ops',
      flow:[ {ask:'Agency Name?',key:'agency'}, {ask:'Brief Summary?',key:'brief'} ],
      result:{title:'Agency Brief Sent ✓'} },
    { cmd:'budget',         label:'Budget',            desc:'Marketing budget overview',                 icon:'💳', cat:'finance',
      flow:[ {ask:'Period?',key:'period',opts:['This month','This quarter']}, {ask:'Channel? (all if blank)',key:'channel'} ],
      result:{title:'Budget Overview', mock:true} },
    { cmd:'approve',        label:'Approve',           desc:'Approve a marketing request',               icon:'✅', cat:'ops',
      flow:[ {ask:'Request Detail?',key:'request'}, {ask:'Decision?',key:'action',opts:['Approve','Reject','Revise']} ],
      result:{title:'Approval Recorded ✓'} },
    { cmd:'export',         label:'Export',            desc:'Export data',                               icon:'⬇️', cat:'ops',
      flow:[ {ask:'Data Type?',key:'type',opts:['Leads','Campaign data','Channel performance']}, {ask:'Format?',key:'format',opts:['PDF','Excel','CSV']} ],
      result:{title:'Export Started ✓'} },
    { cmd:'search', label:'Search', desc:'Find a campaign, lead, or team member', icon:'🔍', cat:'ops', flow:[], result:{title:'Search'} },
  ],
  'Business Owner': [
    { cmd:'dashboard',      label:'Dashboard',         desc:'Business overview dashboard',               icon:'📊', cat:'overview',
      flow:[], ai:['Revenue','Collections','Sales','Marketing','Project Status','Cash Flow'],
      result:{title:'Executive Dashboard', mock:true} },
    { cmd:'revenue',        label:'Revenue',           desc:'Revenue summary',                           icon:'💰', cat:'finance',
      flow:[ {ask:'Period?',key:'period',opts:['This month','This quarter','This year']} ],
      ai:['Booked: ₹12.4 Cr','Collected: ₹8.1 Cr','Target: ₹15 Cr','Achievement: 83%'],
      result:{title:'Revenue Summary', mock:true} },
    { cmd:'forecast',       label:'Forecast',          desc:'Revenue & sales forecast',                  icon:'🔮', cat:'finance',
      flow:[ {ask:'Period?',key:'period',opts:['This month','This quarter','This year']} ],
      ai:['Projected: ₹5.2 Cr','Confidence: 82%'],
      result:{title:'Revenue Forecast', mock:true} },
    { cmd:'booking',        label:'Bookings',          desc:'Booking overview',                          icon:'🏠', cat:'sales',
      flow:[ {ask:'Period?',key:'period',opts:['This week','This month','This quarter']} ],
      result:{title:'Booking Overview', mock:true} },
    { cmd:'inventory',      label:'Inventory',         desc:'Inventory status',                          icon:'🏢', cat:'ops',
      flow:[ {ask:'Project? (all if blank)',key:'project'} ],
      ai:['Project A: 15% remaining ⚠️','Project B: 43% remaining'],
      result:{title:'Inventory Status', mock:true} },
    { cmd:'salesperformance',label:'Sales Performance',desc:'Sales team performance',                   icon:'📈', cat:'reports',
      flow:[ {ask:'Period?',key:'period',opts:['This week','This month','This quarter']} ],
      result:{title:'Sales Performance', mock:true} },
    { cmd:'campaignroi',    label:'Campaign ROI',      desc:'Marketing campaign ROI',                    icon:'📣', cat:'reports',
      flow:[ {ask:'Period?',key:'period',opts:['This month','This quarter']} ],
      ai:['Meta: 4.3x','Google: 6.1x','CP: 8.2x'],
      result:{title:'Campaign ROI', mock:true} },
    { cmd:'leadquality',    label:'Lead Quality',      desc:'Lead quality summary',                      icon:'⭐', cat:'reports',
      flow:[ {ask:'Period?',key:'period',opts:['This week','This month']} ],
      result:{title:'Lead Quality Summary', mock:true} },
    { cmd:'collections',    label:'Collections',       desc:'Collections & receivables',                 icon:'💳', cat:'finance',
      flow:[ {ask:'Period?',key:'period',opts:['This month','This quarter']} ],
      ai:['Collected: ₹8.1 Cr','Pending: ₹28L overdue ⚠️'],
      result:{title:'Collections Report', mock:true} },
    { cmd:'outstanding',    label:'Outstanding',       desc:'Outstanding payments',                      icon:'⚠️', cat:'finance',
      flow:[ {ask:'Period?',key:'period',opts:['This month','This quarter']} ],
      ai:['Collections delayed ₹28 lakh','3 bookings overdue 30+ days'],
      result:{title:'Outstanding Payments ⚠️', mock:true} },
    { cmd:'projecthealth',  label:'Project Health',    desc:'Project-wise health report',                icon:'🏗️', cat:'reports',
      flow:[ {ask:'Project? (all if blank)',key:'project'} ],
      result:{title:'Project Health', mock:true} },
    { cmd:'profitability',  label:'Profitability',     desc:'Profitability report',                      icon:'💹', cat:'finance',
      flow:[ {ask:'Project?',key:'project'}, {ask:'Period?',key:'period',opts:['This month','This quarter','This year']} ],
      result:{title:'Profitability Report', mock:true} },
    { cmd:'teamperformance',label:'Team Performance',  desc:'Team productivity overview',                icon:'👥', cat:'reports',
      flow:[ {ask:'Period?',key:'period',opts:['This week','This month','This quarter']} ],
      result:{title:'Team Performance', mock:true} },
    { cmd:'escalation',     label:'Escalations',       desc:'View open escalations',                     icon:'🚨', cat:'ops',
      flow:[], ai:['Campaign B CPL increased 32%','Collections delayed ₹28 lakh','Project A inventory below 15%'],
      result:{title:'Open Escalations', mock:true} },
    { cmd:'approval',       label:'Approval',          desc:'Approve a pending request',                 icon:'✅', cat:'ops',
      flow:[ {ask:'Request Detail?',key:'request'}, {ask:'Decision?',key:'action',opts:['Approve','Reject','Escalate']} ],
      result:{title:'Approval Recorded ✓'} },
    { cmd:'report',         label:'Report',            desc:'Generate a business report',                icon:'📑', cat:'reports',
      flow:[ {ask:'Report Type?',key:'type',opts:['Executive summary','Revenue','Sales','Marketing','Project health']}, {ask:'Period?',key:'period',opts:['This week','This month','This quarter']} ],
      result:{title:'Report Generated ✓', action:'Download →'} },
    { cmd:'export',         label:'Export',            desc:'Export data',                               icon:'⬇️', cat:'ops',
      flow:[ {ask:'Data Type?',key:'type',opts:['Revenue','Bookings','Team','Marketing']}, {ask:'Format?',key:'format',opts:['PDF','Excel','CSV']} ],
      result:{title:'Export Started ✓'} },
    { cmd:'targets',        label:'Targets',           desc:'View or update targets',                    icon:'🎯', cat:'ops',
      flow:[ {ask:'Team / Project?',key:'segment'}, {ask:'Period?',key:'period',opts:['This month','This quarter']} ],
      result:{title:'Targets Overview', mock:true} },
    { cmd:'investorview',   label:'Investor View',     desc:'Investor-ready snapshot',                   icon:'🏦', cat:'finance',
      flow:[ {ask:'Period?',key:'period',opts:['This month','This quarter','This year']} ],
      result:{title:'Investor Snapshot', mock:true} },
    { cmd:'alerts',         label:'Alerts',            desc:'View critical business alerts',             icon:'🔔', cat:'ops',
      flow:[], ai:['Project A inventory below 15%','Campaign B CPL increased 32%','Collections delayed ₹28 lakh'],
      result:{title:'Business Alerts 🔔', mock:true} },
    { cmd:'remind',         label:'Remind',            desc:'Set a reminder — for yourself, a lead, or a task', icon:'⏰', cat:'ops',
      flow:[ {ask:'Remind about?',key:'about',opts:['Just me','A lead','A task']},
             {ask:'Which one? (skip if "Just me")',key:'target'},
             {ask:'Reminder Text?',key:'message'},
             {ask:'When?',key:'when',opts:['In 30 mins','In 1 hour','Tomorrow 9am','Tomorrow 11am']} ],
      result:{title:'Reminder Set ✓'} },
    { cmd:'meeting',        label:'Meeting',           desc:'Schedule a meeting with one or more team members', icon:'📅', cat:'ops',
      flow:[ {ask:'Meeting Title?',key:'title'},
             {ask:'With whom? (names, comma-separated)',key:'attendees'},
             {ask:'Date & Time? (e.g. 25 Sep 3:00 PM)',key:'when'},
             {ask:'How?',key:'type',opts:['Google Meet','Zoom','In person']},
             {ask:'Location (if in person, else skip)',key:'location'} ],
      result:{title:'Scheduling…'} },
    { cmd:'drive',          label:'Drive',             desc:"Attach a file from this lead's Drive folder", icon:'📁', cat:'ops', flow:[] },
  ],
};

window.LD_CMDS = {
  'Trainer': [
    { cmd:'batch',       label:'Batch',         desc:'View or manage a training batch',        icon:'👥', cat:'program',
      flow:[ {ask:'Select Batch:',key:'batch',dsKey:'batch'}, {ask:'Action?',key:'action',opts:['View progress','Start session','Mark complete','View attendance','View roster']} ],
      result:{title:'Batch Overview', mock:true} },
    { cmd:'attendance',  label:'Attendance',    desc:'Mark or review attendance',              icon:'✅', cat:'program',
      flow:[ {ask:'Select Batch:',key:'batch',dsKey:'batch'}, {ask:'Date?',key:'date',opts:['Today','Yesterday','Custom']} ],
      ai:['Participant list loaded','Absentee notification ready'],
      result:{title:'Attendance Marked ✓', action:'Send absentee notifications →'} },
    { cmd:'assessment',  label:'Assessment',    desc:'Create or review an assessment',         icon:'📝', cat:'learning',
      flow:[ {ask:'Batch Name?',key:'batch'}, {ask:'Assessment Type?',key:'type',opts:['Pre-program','Post-program','Behaviour check','Quiz','Assignment']} ],
      result:{title:'Assessment Assigned ✓'} },
    { cmd:'score',       label:'Score',         desc:'Update a participant score',             icon:'⭐', cat:'learning',
      flow:[ {ask:'Select Participant:',key:'participant',dsKey:'employee'}, {ask:'Score?',key:'score'} ],
      ai:['Amit: 92','Raj: 88','Sunita: 84','Learning gaps: Decision Making, Conflict Management'],
      result:{title:'Score Updated ✓'} },
    { cmd:'coach',       label:'Coach',         desc:'Log a coaching conversation',            icon:'🎯', cat:'coaching',
      flow:[ {ask:'Select Participant:',key:'participant',dsKey:'employee'}, {ask:'Coaching Notes?',key:'notes'} ],
      ai:['Coaching goals recommended','30-day plan suggested'],
      result:{title:'Coaching Session Logged ✓'} },
    { cmd:'actionplan',  label:'Action Plan',   desc:'Create or update an action plan',        icon:'📋', cat:'coaching',
      flow:[ {ask:'Participant Name?',key:'participant'}, {ask:'Goal?',key:'goal'} ],
      ai:['Week 1: Observe meetings','Week 2: Apply questioning framework','Week 3: Stakeholder mapping','Week 4: Review outcomes'],
      result:{title:'30-Day Action Plan Created ✓'} },
    { cmd:'reflection',  label:'Reflection',    desc:'Log a reflection for a participant',     icon:'💭', cat:'coaching',
      flow:[ {ask:'Participant Name?',key:'participant'}, {ask:'What did they learn?',key:'learn'}, {ask:'What will they apply?',key:'apply'}, {ask:'What challenges do they foresee?',key:'challenges'} ],
      ai:['Sentiment: Positive','Engagement score updated'],
      result:{title:'Reflection Logged ✓'} },
    { cmd:'feedback',    label:'Feedback',      desc:'Give feedback to a participant',         icon:'💬', cat:'comm',
      flow:[ {ask:'Participant Name?',key:'participant'}, {ask:'Feedback?',key:'feedback'} ],
      result:{title:'Feedback Sent ✓'} },
    { cmd:'completion',  label:'Completion',    desc:'Mark module/session as complete',        icon:'🏁', cat:'program',
      flow:[ {ask:'Batch Name?',key:'batch'}, {ask:'Module / Session?',key:'module'} ],
      result:{title:'Module Marked Complete ✓'} },
    { cmd:'progress',    label:'Progress',      desc:'View participant progress',              icon:'📈', cat:'reports',
      flow:[ {ask:'Participant Name?',key:'participant'} ],
      ai:['Completion: 72%','Assessments: 4/5','Action Plans: 3/5'],
      result:{title:'Progress Report', mock:true} },
    { cmd:'nudge',       label:'Nudge',         desc:'Nudge a participant to complete a task', icon:'👋', cat:'comm',
      flow:[ {ask:'Select Batch:',key:'batch',dsKey:'batch'}, {ask:'Select Participant:',key:'participant',dsKey:'employee'}, {ask:'What should they complete?',key:'task',opts:['Assignment','Assessment','Reflection','Action Plan','Attendance','Session Recording']}, {ask:'Message (optional)?',key:'message'} ],
      result:{title:'Nudge Sent ✓'} },
    { cmd:'observation', label:'Observation',   desc:'Log a participant observation for their progress report', icon:'🔎', cat:'coaching',
      flow:[ {ask:'Select Batch:',key:'batch',dsKey:'batch'}, {ask:'Select Participant:',key:'participant',dsKey:'employee'}, {ask:'Observation Type?',key:'type',opts:['Behaviour in session','Communication skills','Leadership behaviour','Team collaboration','Application of learning','Resistance / disengagement']}, {ask:'Observation Notes:',key:'notes'}, {ask:'Visibility?',key:'visibility',opts:['Add to progress report','Coach only — not shared','Shared with participant','Flag for L&D Manager']} ],
      ai:['Linked to competency framework','Tagged to current module','Added to participant progress timeline'],
      result:{title:'Observation Logged ✓', action:'View Progress Report →'} },
    { cmd:'review',      label:'Review',        desc:'Review participant progress',            icon:'🔍', cat:'reports',
      flow:[ {ask:'Participant Name?',key:'participant'}, {ask:'Period?',key:'period',opts:['This week','This module','Full program']} ],
      result:{title:'Participant Review Generated', mock:true} },
    { cmd:'note',        label:'Note',          desc:'Add a training note',                    icon:'📝', cat:'ops',
      flow:[ {ask:'Note?',key:'note'} ],
      result:{title:'Note Saved ✓'} },
    { cmd:'report',      label:'Report',        desc:'Generate batch/trainer report',          icon:'📑', cat:'reports',
      flow:[ {ask:'Batch Name?',key:'batch'}, {ask:'Report Type?',key:'type',opts:['Attendance','Assessment results','Completion','Engagement']} ],
      result:{title:'Report Generated ✓', action:'Download →'} },
    { cmd:'certificate', label:'Certificate',   desc:'Issue a certificate',                    icon:'🎓', cat:'program',
      flow:[ {ask:'Issue for?',key:'scope',opts:['Individual participant','Entire batch']}, {ask:'Select Batch:',key:'batch',dsKey:'batch'}, {ask:'Select Participant (skip if batch):',key:'participant',dsKey:'employee'}, {ask:'Certificate format available?',key:'format',opts:['Yes — use existing format','No format — create new','No format — trigger admin to set up']} ],
      ai:['Checking: Attendance ✓','Checking: Assessment ✓','Checking: Completion ✓','Certificate format validated'],
      result:{title:'Certificate Issued ✓ 🎓', action:'Send to participant →'} },
    { cmd:'search', label:'Search', desc:'Find a participant, batch, or module', icon:'🔍', cat:'ops', flow:[], result:{title:'Search'} },
  ],
  'Participant': [
    { cmd:'calendar',    label:'Calendar',      desc:'View your learning calendar',            icon:'📅', cat:'learning',
      flow:[], ai:['June 5 — Leadership Essentials','June 12 — Communication Lab','June 19 — Coaching Conversations'],
      result:{title:'Your Learning Calendar', mock:true} },
    { cmd:'attendance',  label:'Attendance',    desc:'Mark your attendance',                   icon:'✅', cat:'learning',
      flow:[ {ask:'Date (today if blank)?',key:'date',opts:['Today','Yesterday']} ],
      result:{title:'Attendance Marked ✓'} },
    { cmd:'assessment',  label:'Assessment',    desc:'View or submit an assessment',           icon:'📝', cat:'learning',
      flow:[ {ask:'Assessment Type?',key:'type',opts:['Pre-program','Post-program','Quiz','Assignment']} ],
      result:{title:'Assessment Opened', mock:true} },
    { cmd:'myscore',     label:'My Score',      desc:'View your scores',                       icon:'⭐', cat:'learning',
      flow:[], ai:['Pre-program: 62','Post-program: 83','Improvement: +21 points 🎉'],
      result:{title:'My Assessment History', mock:true} },
    { cmd:'progress',    label:'Progress',      desc:'View your learning progress',            icon:'📈', cat:'learning',
      flow:[], ai:['Completion: 72%','Assessments: 4/5','Action Plans: 3/5'],
      result:{title:'My Progress', mock:true} },
    { cmd:'actionplan',  label:'Action Plan',   desc:'View or update your action plan',        icon:'📋', cat:'growth',
      flow:[ {ask:'Update or note (leave blank to view)?',key:'plan'} ],
      result:{title:'Action Plan', mock:true} },
    { cmd:'reflection',  label:'Reflection',    desc:'Submit a reflection',                    icon:'💭', cat:'growth',
      flow:[ {ask:'What did you learn?',key:'learn'}, {ask:'What will you apply?',key:'apply'}, {ask:'What challenges do you foresee?',key:'challenges'} ],
      result:{title:'Reflection Submitted ✓'} },
    { cmd:'feedback',    label:'Feedback',      desc:'Give feedback on a session',             icon:'💬', cat:'comm',
      flow:[ {ask:'Session Name?',key:'session'}, {ask:'Your Feedback?',key:'feedback'}, {ask:'Rating?',key:'rating',opts:['⭐⭐⭐⭐⭐ Excellent','⭐⭐⭐⭐ Good','⭐⭐⭐ Average','⭐⭐ Below expectations']} ],
      result:{title:'Feedback Submitted ✓'} },
    { cmd:'certificate', label:'Certificate',   desc:'View or download your certificate',      icon:'🎓', cat:'learning',
      flow:[ {ask:'Certificate format available?',key:'format',opts:['Yes — download now','No format available — request admin to set it up','Not sure — check with trainer']} ],
      ai:['Checking eligibility…','Attendance: ✓','Assessment: ✓','Completion: ✓','You are eligible!'],
      result:{title:'Certificate Ready 🎓', action:'Download →'} },
    { cmd:'resources',   label:'Resources',     desc:'Search learning resources',              icon:'📚', cat:'learning',
      flow:[ {ask:'Topic or keyword?',key:'topic'} ],
      ai:['Videos matched','Worksheets','Articles','Simulations'],
      result:{title:'Recommended Resources', mock:true} },
    { cmd:'coach',       label:'Coach',         desc:'Ask your coach a question',              icon:'🎯', cat:'coaching',
      flow:[ {ask:'Your question?',key:'question'} ],
      ai:['Framework suggested','Practice exercise recommended'],
      result:{title:'Coach Response', mock:true} },
    { cmd:'ask',         label:'Ask',           desc:'Ask the trainer a question',             icon:'❓', cat:'comm',
      flow:[ {ask:'Your question?',key:'question'} ],
      result:{title:'Question Sent to Trainer ✓'} },
    { cmd:'help',        label:'Help',          desc:'Get help on a topic',                    icon:'🆘', cat:'comm',
      flow:[ {ask:'Topic or issue?',key:'topic'} ],
      result:{title:'Help Request Sent ✓'} },
    { cmd:'search', label:'Search', desc:'Find a session, resource, or module', icon:'🔍', cat:'ops', flow:[], result:{title:'Search'} },
  ],
  'L&D Manager': [
    { cmd:'program',        label:'Program',         desc:'View or create a learning program',       icon:'📚', cat:'program',
      flow:[ {ask:'Program Name?',key:'name'}, {ask:'Action?',key:'action',opts:['View','Create new','Update','Archive']} ],
      result:{title:'Program Overview', mock:true} },
    { cmd:'batch',          label:'Batch',           desc:'View or manage batches',                  icon:'👥', cat:'program',
      flow:[ {ask:'Select Batch:',key:'batch',dsKey:'batch'}, {ask:'Action?',key:'action',opts:['View progress','Start','Close','View roster']} ],
      result:{title:'Batch Overview', mock:true} },
    { cmd:'trainer',        label:'Trainer',         desc:'View or assign a trainer',                icon:'🎓', cat:'program',
      flow:[ {ask:'Trainer Name?',key:'name'}, {ask:'Action?',key:'action',opts:['Assign to batch','View performance','Review feedback']} ],
      result:{title:'Trainer Overview', mock:true} },
    { cmd:'attendance',     label:'Attendance',      desc:'View attendance report',                  icon:'✅', cat:'reports',
      flow:[ {ask:'Batch Name?',key:'batch'}, {ask:'Period?',key:'period',opts:['Today','This week','This module','Full program']} ],
      result:{title:'Attendance Report', mock:true} },
    { cmd:'assessment',     label:'Assessment',      desc:'View assessment results',                 icon:'📝', cat:'reports',
      flow:[ {ask:'Batch Name?',key:'batch'}, {ask:'Assessment Type?',key:'type',opts:['Pre','Post','Behaviour','All']} ],
      result:{title:'Assessment Results', mock:true} },
    { cmd:'completion',     label:'Completion',      desc:'Completion rate report',                  icon:'🏁', cat:'reports',
      flow:[ {ask:'Program Name?',key:'program'}, {ask:'Period?',key:'period',opts:['This month','This quarter','Full program']} ],
      result:{title:'Completion Report', mock:true} },
    { cmd:'engagement',     label:'Engagement',      desc:'Learner engagement metrics',              icon:'📊', cat:'reports',
      flow:[ {ask:'Batch Name?',key:'batch'} ],
      ai:['Program A: 88%','Program B: 62% ⚠️','Risk groups flagged'],
      result:{title:'Engagement Report', mock:true} },
    { cmd:'nudge',          label:'Nudge',           desc:'Nudge a participant or trainer',          icon:'👋', cat:'comm',
      flow:[ {ask:'Select Batch:',key:'batch',dsKey:'batch'}, {ask:'Select Person:',key:'person',dsKey:'employee'}, {ask:'What should they complete?',key:'task',opts:['Assignment','Assessment','Reflection','Action Plan','Attendance','Session Recording']}, {ask:'Message (optional)?',key:'message'} ],
      result:{title:'Nudge Sent ✓'} },
    { cmd:'feedback',       label:'Feedback',        desc:'View feedback summary',                   icon:'💬', cat:'reports',
      flow:[ {ask:'Batch Name?',key:'batch'}, {ask:'Feedback Type?',key:'type',opts:['Participant on program','Participant on trainer','Manager on behaviour change']} ],
      result:{title:'Feedback Summary', mock:true} },
    { cmd:'managerfeedback',label:'Manager Feedback',desc:'View manager feedback on participants',   icon:'📋', cat:'reports',
      flow:[ {ask:'Participant Name?',key:'participant'}, {ask:'Manager Name?',key:'manager'} ],
      ai:['Behaviour change','Skill improvement','Application examples'],
      result:{title:'Manager Feedback Report', mock:true} },
    { cmd:'report',         label:'Report',          desc:'Generate L&D report',                     icon:'📑', cat:'reports',
      flow:[ {ask:'Report Type?',key:'type',opts:['Attendance','Completion','Assessment','Engagement','ROI']}, {ask:'Period?',key:'period',opts:['This month','This quarter','This year']} ],
      result:{title:'Report Generated ✓', action:'Download →'} },
    { cmd:'dashboard',      label:'Dashboard',       desc:'L&D overview dashboard',                  icon:'📊', cat:'overview',
      flow:[],
      result:{title:'L&D Dashboard', mock:true} },
    { cmd:'budget',         label:'Budget',          desc:'L&D budget overview',                     icon:'💳', cat:'finance',
      flow:[ {ask:'Program? (all if blank)',key:'program'} ],
      result:{title:'L&D Budget Overview', mock:true} },
    { cmd:'certificate',    label:'Certificate',     desc:'Issue or view certificates',              icon:'🎓', cat:'program',
      flow:[ {ask:'Issue for?',key:'scope',opts:['Individual participant','Entire batch','View issued certificates']}, {ask:'Select Batch:',key:'batch',dsKey:'batch'}, {ask:'Select Participant (skip if batch):',key:'participant',dsKey:'employee'}, {ask:'Certificate format available?',key:'format',opts:['Yes — use existing format','No format — create new','No format — trigger admin to set up']} ],
      ai:['Checking: Attendance ✓','Checking: Assessment ✓','Checking: Completion ✓'],
      result:{title:'Certificate Issued ✓ 🎓', action:'Send to participant →'} },
    { cmd:'export',         label:'Export',          desc:'Export L&D data',                         icon:'⬇️', cat:'ops',
      flow:[ {ask:'Data Type?',key:'type',opts:['Attendance','Scores','Completion','Engagement']}, {ask:'Format?',key:'format',opts:['PDF','Excel','CSV']} ],
      result:{title:'Export Started ✓'} },
    { cmd:'search', label:'Search', desc:'Find a participant, batch, or module', icon:'🔍', cat:'ops', flow:[], result:{title:'Search'} },
  ],
  'L&D Head': [
    { cmd:'dashboard',           label:'Dashboard',           desc:'L&D executive dashboard',                icon:'📊', cat:'overview',
      flow:[], result:{title:'L&D Executive Dashboard', mock:true} },
    { cmd:'learninghours',       label:'Learning Hours',      desc:'Total learning hours by segment',         icon:'⏱️', cat:'reports',
      flow:[ {ask:'Period?',key:'period',opts:['This month','This quarter','This year']}, {ask:'Segment?',key:'segment',opts:['All','Sales','Operations','Leadership','Customer Success']} ],
      result:{title:'Learning Hours Report', mock:true} },
    { cmd:'completion',          label:'Completion',          desc:'Program completion rates',                icon:'🏁', cat:'reports',
      flow:[ {ask:'Program? (all if blank)',key:'program'}, {ask:'Period?',key:'period',opts:['This month','This quarter','This year']} ],
      result:{title:'Completion Report', mock:true} },
    { cmd:'engagement',          label:'Engagement',          desc:'Learner engagement analysis',             icon:'💡', cat:'reports',
      flow:[ {ask:'Period?',key:'period',opts:['This month','This quarter']} ],
      result:{title:'Engagement Analysis', mock:true} },
    { cmd:'capabilitygap',       label:'Capability Gap',      desc:'Capability gap analysis',                 icon:'🔍', cat:'strategy',
      flow:[ {ask:'Department?',key:'department',opts:['Sales','Operations','Leadership','Customer Success','All']} ],
      ai:['Gap 1: Communication','Gap 2: Decision Making','Gap 3: Coaching','Gap 4: Collaboration'],
      result:{title:'Capability Gap Analysis', mock:true} },
    { cmd:'skills',              label:'Skills',              desc:'Skills inventory and gaps',               icon:'🧠', cat:'strategy',
      flow:[ {ask:'Department?',key:'department',opts:['Sales','Operations','Leadership','All']} ],
      result:{title:'Skills Inventory', mock:true} },
    { cmd:'assessment',          label:'Assessment',          desc:'Assessment results overview',             icon:'📝', cat:'reports',
      flow:[ {ask:'Program?',key:'program'}, {ask:'Type?',key:'type',opts:['Pre','Post','Behaviour change','All']} ],
      result:{title:'Assessment Overview', mock:true} },
    { cmd:'behaviorchange',      label:'Behavior Change',     desc:'Post-training behavior change metrics',   icon:'🔄', cat:'reports',
      flow:[ {ask:'Program?',key:'program'}, {ask:'Period?',key:'period',opts:['Post 30 days','Post 60 days','Post 90 days']} ],
      ai:['Before Program: Baseline','After Program: +27%','Behaviour Change Index: +27%'],
      result:{title:'Behaviour Change Report', mock:true} },
    { cmd:'roi',                 label:'ROI',                 desc:'Learning ROI calculation',                icon:'💹', cat:'finance',
      flow:[ {ask:'Program?',key:'program'}, {ask:'Period?',key:'period',opts:['Post 30 days','Post 90 days','This year']} ],
      ai:['Training Cost: ₹8 lakh','Productivity Gain: ₹24 lakh','ROI: 3x'],
      result:{title:'Learning ROI', mock:true} },
    { cmd:'managerfeedback',     label:'Manager Feedback',    desc:'Manager feedback on participants',        icon:'📋', cat:'reports',
      flow:[ {ask:'Period?',key:'period',opts:['This month','This quarter']} ],
      result:{title:'Manager Feedback Report', mock:true} },
    { cmd:'trainerperformance',  label:'Trainer Performance', desc:'Trainer effectiveness report',            icon:'🎓', cat:'reports',
      flow:[ {ask:'Period?',key:'period',opts:['This month','This quarter']} ],
      result:{title:'Trainer Performance Report', mock:true} },
    { cmd:'programeffectiveness',label:'Program Effectiveness',desc:'Program effectiveness report',          icon:'📈', cat:'reports',
      flow:[ {ask:'Program Name?',key:'program'} ],
      result:{title:'Program Effectiveness Report', mock:true} },
    { cmd:'budget',              label:'Budget',              desc:'L&D budget vs actuals',                   icon:'💳', cat:'finance',
      flow:[ {ask:'Period?',key:'period',opts:['This month','This quarter','This year']} ],
      result:{title:'L&D Budget vs Actuals', mock:true} },
    { cmd:'nomination',          label:'Nomination',          desc:'Nomination management',                   icon:'📩', cat:'program',
      flow:[ {ask:'Program?',key:'program'}, {ask:'Batch?',key:'batch'} ],
      result:{title:'Nominations Overview', mock:true} },
    { cmd:'succession',          label:'Succession',          desc:'Succession planning input',               icon:'🏆', cat:'strategy',
      flow:[ {ask:'Role?',key:'role'}, {ask:'Talent Pool?',key:'pool'} ],
      ai:['HiPos identified','Successor readiness scored','Development needs mapped'],
      result:{title:'Succession Plan', mock:true} },
    { cmd:'talentreview',        label:'Talent Review',       desc:'Talent review data',                      icon:'⭐', cat:'strategy',
      flow:[ {ask:'Period?',key:'period',opts:['Q1','Q2','Q3','Q4','Annual']} ],
      ai:['Performance × Potential matrix','Recommended actions per quadrant'],
      result:{title:'9-Box Talent Review', mock:true} },
    { cmd:'report',              label:'Report',              desc:'Generate strategic L&D report',           icon:'📑', cat:'reports',
      flow:[ {ask:'Report Type?',key:'type',opts:['Executive summary','Completion','ROI','Capability gap','Behaviour change']}, {ask:'Period?',key:'period',opts:['This quarter','This year']} ],
      result:{title:'Report Generated ✓', action:'Download →'} },
    { cmd:'export',              label:'Export',              desc:'Export data',                             icon:'⬇️', cat:'ops',
      flow:[ {ask:'Data Type?',key:'type',opts:['Completion','Scores','Engagement','ROI','Budget']}, {ask:'Format?',key:'format',opts:['PDF','Excel','CSV']} ],
      result:{title:'Export Started ✓'} },
    { cmd:'approval',            label:'Approval',            desc:'Approve a learning request or spend',     icon:'✅', cat:'ops',
      flow:[ {ask:'Request Detail?',key:'request'}, {ask:'Decision?',key:'action',opts:['Approve','Reject','Revise budget']} ],
      result:{title:'Approval Recorded ✓'} },
    { cmd:'forecast',            label:'Forecast',            desc:'L&D needs and budget forecast',           icon:'🔮', cat:'strategy',
      flow:[ {ask:'Period?',key:'period',opts:['Next quarter','Next year']} ],
      result:{title:'L&D Forecast', mock:true} },
    { cmd:'search', label:'Search', desc:'Find a participant, trainer, batch, or module', icon:'🔍', cat:'ops', flow:[], result:{title:'Search'} },
  ],
};

// ── Rich card components ─────────────────────────────────────────────────────
const NoteCard = ({ card }) => (
  <div style={{ background:'#faf5ff', border:'1px solid #e9d5ff', borderRadius:14, padding:'11px 14px', marginBottom:4, maxWidth:280 }}>
    <div style={{ fontSize:11, fontWeight:700, color:'#6b21a8', marginBottom:5, display:'flex', alignItems:'center', gap:5 }}>
      <span>📝</span><span>NOTE</span>
    </div>
    <div style={{ fontSize:13.5, color:'#1f2937', lineHeight:1.5 }}>{card.text}</div>
    {card.title && <div style={{ fontSize:11.5, color:'#7c3aed', fontWeight:600, marginTop:4 }}>{card.title}</div>}
  </div>
);

const LinkCard = ({ card }) => (
  <div style={{ background:'#f8fafc', border:'1px solid #cbd5e1', borderRadius:14, padding:'11px 14px', marginBottom:4, maxWidth:280 }}>
    <div style={{ fontSize:11, fontWeight:700, color:'#475569', marginBottom:5, display:'flex', alignItems:'center', gap:5 }}>
      <span>🔗</span><span>LINK</span>
    </div>
    <div style={{ fontSize:13, color:'#2563eb', wordBreak:'break-all', lineHeight:1.45 }}>{card.url}</div>
    {card.title && <div style={{ fontSize:12, color:'#64748b', marginTop:4, fontStyle:'italic' }}>{card.title}</div>}
  </div>
);

const TaskCard = ({ card, accent, onPickUp }) => {
  const [picked, setPicked] = React.useState(false);
  const complexColor = COMPLEX_COLORS[card.complexity] || '#9ca3af';
  const isOpen = card.openToAll;
  return (
    <div style={{ background:'#f0fdf4', border:`1px solid ${isOpen ? '#fecdd3' : '#bbf7d0'}`, borderRadius:14, padding:'11px 14px', marginBottom:4, maxWidth:280 }}>
      <div style={{ fontSize:11, fontWeight:700, color: isOpen ? '#9f1239' : '#166534', marginBottom:5, display:'flex', alignItems:'center', gap:5 }}>
        <span>{isOpen ? '＋' : '✓'}</span><span>{isOpen ? 'OPEN WORK' : 'TASK'}</span>
        {card.complexity && <span style={{ marginLeft:'auto', fontSize:10, padding:'2px 7px', borderRadius:20, background:complexColor+'20', color:complexColor, border:`1px solid ${complexColor}40`, fontWeight:700 }}>{card.complexity}</span>}
      </div>
      <div style={{ fontSize:13.5, color:'#1f2937', fontWeight:600, lineHeight:1.4, marginBottom:4 }}>{card.title}</div>
      {card.notes && <div style={{ fontSize:12, color:'#4b5563', lineHeight:1.4, marginBottom:4 }}>{card.notes}</div>}
      <div style={{ display:'flex', flexWrap:'wrap', gap:6, marginTop:4 }}>
        {card.assignee && !isOpen && <span style={{ fontSize:11, color:'#166534', background:'#dcfce7', padding:'2px 8px', borderRadius:20 }}>👤 {card.assignee}</span>}
        {card.duration && <span style={{ fontSize:11, color:'#0369a1', background:'#e0f2fe', padding:'2px 8px', borderRadius:20 }}>⏱ {card.duration}</span>}
        {card.dueDate && <span style={{ fontSize:11, color:'#6b7280', background:'#f3f4f6', padding:'2px 8px', borderRadius:20 }}>📅 {card.dueDate}</span>}
      </div>
      {isOpen && !picked && (
        <button onClick={() => { setPicked(true); onPickUp && onPickUp(card); }}
          style={{ marginTop:8, width:'100%', padding:'7px 0', borderRadius:10, background:'#9f1239', border:'none', color:'#fff', fontWeight:700, fontSize:12.5, cursor:'pointer' }}>
          Pick Up Task →
        </button>
      )}
      {isOpen && picked && (
        <div style={{ marginTop:8, textAlign:'center', fontSize:12, color:'#16a34a', fontWeight:700 }}>✓ Picked up!</div>
      )}
    </div>
  );
};

const StatusCard = ({ card, accent }) => {
  const sc = STATUS_COLORS[card.status] || '#6b7280';
  return (
    <div style={{ background:'#f0f9ff', border:'1px solid #bae6fd', borderRadius:14, padding:'11px 14px', marginBottom:4, maxWidth:280 }}>
      <div style={{ fontSize:11, fontWeight:700, color:'#0369a1', marginBottom:5, display:'flex', alignItems:'center', gap:5 }}>
        <span>📊</span><span>STATUS UPDATE</span>
      </div>
      <div style={{ fontSize:13, color:'#1f2937', marginBottom:6 }}>{card.taskTitle}</div>
      <span style={{ fontSize:12, fontWeight:700, padding:'3px 10px', borderRadius:20, background:sc+'18', color:sc, border:`1px solid ${sc}40` }}>{card.status}</span>
      {card.note && <div style={{ fontSize:12, color:'#4b5563', marginTop:6 }}>{card.note}</div>}
    </div>
  );
};

const NudgeCard = ({ card, accent }) => (
  <div style={{ background:'#fff7ed', border:'1px solid #fed7aa', borderRadius:14, padding:'11px 14px', marginBottom:4, maxWidth:280 }}>
    <div style={{ fontSize:11, fontWeight:700, color:'#9a3412', marginBottom:5, display:'flex', alignItems:'center', gap:5 }}>
      <span>🔔</span><span>NUDGE</span>
    </div>
    <div style={{ fontSize:13, color:'#1f2937', fontWeight:600 }}>→ {card.to}</div>
    {card.taskTitle && <div style={{ fontSize:12, color:'#4b5563', marginTop:3 }}>re: {card.taskTitle}</div>}
    {card.message && <div style={{ fontSize:13, color:'#7c2d12', marginTop:5, fontStyle:'italic' }}>"{card.message}"</div>}
  </div>
);

const ReassignCard = ({ card }) => (
  <div style={{ background:'#fdf4ff', border:'1px solid #f0abfc', borderRadius:14, padding:'11px 14px', marginBottom:4, maxWidth:280 }}>
    <div style={{ fontSize:11, fontWeight:700, color:'#7e22ce', marginBottom:5, display:'flex', alignItems:'center', gap:5 }}>
      <span>↗</span><span>REASSIGNED</span>
    </div>
    <div style={{ fontSize:13, color:'#1f2937', fontWeight:600, marginBottom:3 }}>{card.taskTitle}</div>
    <div style={{ fontSize:12, color:'#6b7280' }}>
      <span style={{ color:'#9ca3af' }}>{card.from}</span>
      <span style={{ margin:'0 6px', color:'#a855f7' }}>→</span>
      <span style={{ color:'#7e22ce', fontWeight:700 }}>{card.to}</span>
    </div>
  </div>
);

const ReminderCard = ({ card }) => (
  <div style={{ background:'#fdf2f8', border:'1px solid #fbcfe8', borderRadius:14, padding:'11px 14px', marginBottom:4, maxWidth:280 }}>
    <div style={{ fontSize:11, fontWeight:700, color:'#9d174d', marginBottom:5, display:'flex', alignItems:'center', gap:5 }}>
      <span>⏰</span><span>REMINDER SET</span>
    </div>
    <div style={{ fontSize:13.5, color:'#1f2937', fontWeight:600 }}>{card.when}</div>
    {card.note && <div style={{ fontSize:12, color:'#6b7280', marginTop:3 }}>{card.note}</div>}
  </div>
);

// ── ActionCard — generic command card for role-specific commands ──────────────
const ActionCard = ({ card }) => {
  const colors = CMD_CAT[card.cat] || CMD_CAT.ops;
  const fieldEntries = card.fields ? Object.entries(card.fields).filter(([,v]) => v) : [];
  return (
    <div style={{ background:colors.bg, border:`1px solid ${colors.border}`, borderRadius:14, padding:'11px 14px', marginBottom:4, maxWidth:300 }}>
      <div style={{ fontSize:11, fontWeight:700, color:colors.text, marginBottom: fieldEntries.length ? 6 : 0, display:'flex', alignItems:'center', gap:5 }}>
        <span>{card.icon || '⚡'}</span>
        <span>/{card.cmd?.toUpperCase()}</span>
        <span style={{ fontWeight:500, opacity:.7, marginLeft:2 }}>· {card.label}</span>
      </div>
      {fieldEntries.map(([k,v]) => (
        <div key={k} style={{ fontSize:12.5, color:'#1f2937', marginBottom:3, lineHeight:1.4 }}>
          <span style={{ color:colors.text, fontWeight:700, textTransform:'capitalize' }}>{k}: </span>
          <span>{v}</span>
        </div>
      ))}
    </div>
  );
};

const ClaimCard = ({ card, onClaim }) => {
  const [claimed, setClaimed] = React.useState(!!card.claimedBy);
  return (
    <div style={{ background:'#f0fdf4', border:'1px solid #bbf7d0', borderRadius:14, padding:'11px 14px', marginBottom:4, maxWidth:280 }}>
      <div style={{ fontSize:11, fontWeight:700, color:'#166534', marginBottom:5, display:'flex', alignItems:'center', gap:5 }}>
        <span>👤</span><span>{card.assignedTo ? 'LEAD ASSIGNED' : 'CLIENT / LEAD'}</span>
      </div>
      <div style={{ fontSize:13.5, color:'#1f2937', fontWeight:600, marginBottom:3 }}>{card.name}</div>
      {card.meta && <div style={{ fontSize:12, color:'#4b5563', marginBottom:6 }}>{card.meta}</div>}
      {card.assignedTo
        ? <div style={{ fontSize:12, color:'#166534', fontWeight:700 }}>✓ Assigned to {card.assignedTo}</div>
        : claimed
          ? <div style={{ fontSize:12, color:'#166534', fontWeight:700 }}>✓ Claimed by You</div>
          : (
            <button onClick={() => { setClaimed(true); onClaim && onClaim(card); }}
              style={{ width:'100%', padding:'7px 0', borderRadius:10, background:'#166534', border:'none', color:'#fff', fontWeight:700, fontSize:12.5, cursor:'pointer' }}>
              Claim — Add to My List →
            </button>
          )
      }
    </div>
  );
};

const AskStatusCard = ({ card, accent }) => (
  <div style={{ background:'#fffbeb', border:'1px solid #fde68a', borderRadius:14, padding:'11px 14px', marginBottom:4, maxWidth:280 }}>
    <div style={{ fontSize:11, fontWeight:700, color:'#92400e', marginBottom:5, display:'flex', alignItems:'center', gap:5 }}>
      <span>❓</span><span>STATUS REQUEST</span>
    </div>
    <div style={{ fontSize:13, color:'#1f2937', marginBottom:3 }}>
      <span style={{ fontWeight:600 }}>{card.to}</span> — please share status on:
    </div>
    <div style={{ fontSize:13.5, color:'#1f2937', fontWeight:600, marginBottom:4 }}>{card.taskTitle}</div>
    <div style={{ fontSize:11, color:'#92400e', fontStyle:'italic' }}>Sent via chat · awaiting reply</div>
  </div>
);

const FlowCard = ({ card, accent }) => {
  const [actionDone, setActionDone] = React.useState(false);
  const isErr = !!(card.result && card.result.error);
  const col = isErr ? { bg:'#fef2f2', border:'#fecaca', text:'#991b1b' } : (CMD_CAT[card.cat] || CMD_CAT.ops || { bg:'#f0fdf4', border:'#bbf7d0', text:'#166534' });
  const hasMock = card.result && card.result.mock;
  return (
    <div style={{ background:col.bg, border:`1px solid ${col.border}`, borderRadius:14, padding:'12px 14px', maxWidth:300, marginBottom:4 }}>
      {/* Header */}
      <div style={{ display:'flex', alignItems:'center', gap:6, marginBottom:8 }}>
        <span style={{ fontSize:16 }}>{card.icon || '📋'}</span>
        <span style={{ fontSize:11, fontWeight:800, color:col.text, letterSpacing:'0.08em', textTransform:'uppercase' }}>{card.label}</span>
        {isErr
          ? <span style={{ marginLeft:'auto', fontSize:11, color:'#dc2626', fontWeight:700, background:'#fee2e2', padding:'1px 7px', borderRadius:8 }}>Not saved</span>
          : <span style={{ marginLeft:'auto', fontSize:11, color:'#16a34a', fontWeight:700, background:'#dcfce7', padding:'1px 7px', borderRadius:8 }}>✓ Done</span>}
      </div>
      {/* Collected data */}
      {card.flow && card.flow.length > 0 && (
        <div style={{ background:'rgba(255,255,255,0.7)', borderRadius:8, padding:'8px 10px', marginBottom:8 }}>
          {card.flow.filter(s => card.data && card.data[s.key]).map(s => (
            <div key={s.key} style={{ display:'flex', fontSize:12, marginBottom:3, gap:6 }}>
              <span style={{ color:'#6b7280', flexShrink:0, minWidth:72 }}>{s.ask.replace('?','')}</span>
              <span style={{ color:'#111827', fontWeight:600 }}>{card.data[s.key]}</span>
            </div>
          ))}
        </div>
      )}
      {/* AI insights */}
      {card.ai && card.ai.length > 0 && (
        <div style={{ marginBottom:8 }}>
          <div style={{ fontSize:10, fontWeight:700, color:col.text, marginBottom:4, display:'flex', alignItems:'center', gap:4 }}>
            <span>✨</span><span>AI INSIGHTS</span>
          </div>
          <div style={{ display:'flex', flexWrap:'wrap', gap:4 }}>
            {card.ai.map((a,i) => (
              <span key={i} style={{ fontSize:11, background:'rgba(255,255,255,0.8)', color:col.text, borderRadius:6, padding:'2px 8px', fontWeight:600, border:`1px solid ${col.border}` }}>{a}</span>
            ))}
          </div>
        </div>
      )}
      {/* Result */}
      {card.result && (
        <div style={{ borderTop:`1px solid ${col.border}`, paddingTop:8 }}>
          {card.result.title && (
            <div style={{ fontSize:13, fontWeight:700, color:col.text, marginBottom:4 }}>{card.result.title}</div>
          )}
          {hasMock && (
            <div style={{ fontSize:11, color:'#6b7280', fontStyle:'italic', marginBottom:4 }}>Live data will load here in production</div>
          )}
          {isErr && card.result.message && (
            <div style={{ fontSize:12.5, color:col.text, lineHeight:1.4 }}>{card.result.message}</div>
          )}
          {card.result.action && !actionDone && (
            <button onClick={() => setActionDone(true)}
              style={{ width:'100%', padding:'7px 0', borderRadius:10, background:col.text, border:'none', color:'#fff', fontWeight:700, fontSize:12.5, cursor:'pointer', marginTop:4 }}>
              {card.result.action}
            </button>
          )}
          {actionDone && (
            <div style={{ fontSize:12, color:col.text, fontWeight:700 }}>✓ Done</div>
          )}
        </div>
      )}
    </div>
  );
};

// ── CMD_RELEVANCE: frequency-ordered command lists per role ──────────────────
// Commands are shown in this order in the slash-command picker.
// Lower index = shown first = used more often by that role.
const CMD_RELEVANCE = {
  // ── Realty ────────────────────────────────────────────────────────────────
  'Sales Executive':     ['call','followup','newlead','sitevisit','booking','hotlead','visitfeedback',
                          'visitdone','leadstatus','note','reschedulevisit','assign','whatsapp','remind',
                          'nudge','leadscore','qualify','search'],
  'Presales Executive':  ['call','qualify','followup','newlead','leadscore','hotlead','targets','sitevisit',
                          'schedulecall','coldlead','disqualify','leadsource','note','nudge','handover','search'],
  'Channel Partner':     ['newlead','leadstatus','sitevisit','booking','commission','inventory','offers',
                          'paymentstatus','followup','brochure','documents','contactmanager','search'],
  'Sales Manager':       ['pipeline','nudge','assign','teamperformance','followupaudit','conversion',
                          'dashboard','lostanalysis','hotlead','reassign','escalate','review','approval',
                          'sitevisitreport','bookingreport','forecast','targets','inventory','campaignlead',
                          'report','export','search'],
  'Sales Head':          ['revenue','forecast','pipeline','salesdashboard','conversion','bookingreport',
                          'teamperformance','salesperformance','targets','inventory','campaignroi','projectwise',
                          'regionwise','escalation','approval','discountapproval','pricing','leaderboard',
                          'report','export','search'],
  'Marketing Head':      ['campaignroi','campaign','leadquality','leadsource','costperlead','funnel','dashboard',
                          'remarketing','budget','event','landingpage','creative','utm','leadvolume',
                          'inventory','agency','approve','export','report','search'],
  'Business Owner':      ['dashboard','revenue','forecast','booking','collections','teamperformance',
                          'salesperformance','campaignroi','escalation','outstanding','alerts','targets',
                          'inventory','projecthealth','profitability','approval','leadquality','investorview',
                          'export','report','search'],
  // ── SkaleCap / L&D ────────────────────────────────────────────────────────
  'Trainer':             ['attendance','score','observation','nudge','certificate','session','batch',
                          'dashboard','reflection','targets','report','search'],
  'Participant':         ['assess','report','learn','certificate','reflection','actionplan','search'],
  'L&D Manager':         ['dashboard','batchreport','completion','engagement','targets','nudge','certificate',
                          'trainer','tna','report','search'],
  'L&D Head':            ['dashboard','roi','gaps','succession','talentreview','programs','forecast',
                          'report','search'],
};

// ── Apply relevance sort to REALTY_CMDS and LD_CMDS at startup ───────────────
(function sortCmdsByRelevance() {
  const sort = (registry) => {
    if (!registry) return;
    Object.keys(registry).forEach(role => {
      const order = CMD_RELEVANCE[role];
      if (!order || !Array.isArray(registry[role])) return;
      registry[role] = registry[role].slice().sort((a, b) => {
        // /search always last
        if (a.cmd === 'search') return 1;
        if (b.cmd === 'search') return -1;
        const ia = order.indexOf(a.cmd);
        const ib = order.indexOf(b.cmd);
        const pa = ia === -1 ? 500 : ia;
        const pb = ib === -1 ? 500 : ib;
        return pa - pb;
      });
    });
  };
  sort(window.REALTY_CMDS);
  sort(window.LD_CMDS);
})();

// ── HEAD_ROLES: roles that may see /forecast and /revenue ────────────────────
// ChatComposer filters AI_CMDS additions based on this set.
window.HEAD_ROLES = new Set([
  'Sales Head','Sales Manager','Marketing Head','Business Owner',
  'L&D Head','L&D Manager','Manager','Admin',
  'Doctor',           // clinical heads see forecast for patient trends
  'Dermatologist',    // clinical lead
]);

// ── ChatTagPill — renders a single tag chip ──────────────────────────────────

const ChatTagPill = ({ tag, onTap }) => {
  const m = CHAT_TAG_META[tag.type] || CHAT_TAG_META.link;
  return (
    <span onClick={onTap} style={{
      display:'inline-flex', alignItems:'center', gap:5, padding:'4px 10px',
      borderRadius:20, border:`1px solid ${m.border}`, background:m.bg,
      color:m.text, fontSize:12, fontWeight:700, cursor: onTap ? 'pointer' : 'default',
      whiteSpace:'nowrap', userSelect:'none', WebkitUserSelect:'none',
    }}>
      <span style={{fontSize:11}}>{m.icon}</span>
      <span style={{opacity:.6, fontSize:10}}>/{tag.type}</span>
      {' '}{tag.label}
      {tag.meta && <span style={{opacity:.55, fontWeight:500}}> · {tag.meta}</span>}
    </span>
  );
};

// ── ChatBubble — renders one chat message with tags + rich cards ─────────────
const ChatBubble = ({ msg, accent = '#16a34a', onPickUp }) => {
  const isMe = msg.me || msg.from === 'You';
  const tags  = msg.tags  || [];
  const cards = msg.cards || [];
  return (
    <div style={{ marginBottom:14, display:'flex', gap:10, alignItems:'flex-start',
      flexDirection: isMe ? 'row-reverse' : 'row' }}>
      <div style={{ width:34, height:34, borderRadius:99, flexShrink:0,
        background: isMe ? accent : (accent + '22'),
        display:'flex', alignItems:'center', justifyContent:'center',
        fontWeight:800, fontSize:11, color: isMe ? '#fff' : accent }}>
        {msg.avatar || (isMe ? 'ME' : (msg.from||'?').charAt(0))}
      </div>
      <div style={{ maxWidth:'82%', display:'flex', flexDirection:'column', gap:3,
        alignItems: isMe ? 'flex-end' : 'flex-start' }}>
        {!isMe && <div style={{ fontSize:11, fontWeight:700, color:'#6b7280', marginBottom:1 }}>{msg.from}</div>}
        {(msg.msg || msg.text) && (
          <div style={{
            background: isMe ? accent : '#fff', color: isMe ? '#fff' : '#1f2937',
            borderRadius: isMe ? '14px 14px 4px 14px' : '14px 14px 14px 4px',
            padding:'10px 13px', fontSize:13.5, lineHeight:1.45,
            boxShadow:'0 1px 4px rgba(0,0,0,0.07)',
          }}>
            {msg.msg || msg.text}
          </div>
        )}
        {/* Rich cards */}
        {cards.map((card,i) => {
          if (card.cardType==='note')     return <NoteCard key={i} card={card} />;
          if (card.cardType==='link')     return <LinkCard key={i} card={card} />;
          if (card.cardType==='task' || card.cardType==='addwork') return <TaskCard key={i} card={card} accent={accent} onPickUp={onPickUp} />;
          if (card.cardType==='status')   return <StatusCard key={i} card={card} accent={accent} />;
          if (card.cardType==='nudge')    return <NudgeCard key={i} card={card} accent={accent} />;
          if (card.cardType==='reassign') return <ReassignCard key={i} card={card} />;
          if (card.cardType==='reminder') return <ReminderCard key={i} card={card} />;
          if (card.cardType==='action')    return <ActionCard key={i} card={card} />;
          if (card.cardType==='claim')     return <ClaimCard key={i} card={card} />;
        if (card.cardType==='flow')      return <FlowCard key={i} card={card} accent={accent} />;
          if (card.cardType==='askstatus') return <AskStatusCard key={i} card={card} accent={accent} />;
          return null;
        })}
        {/* Simple tag pills */}
        {tags.length > 0 && (
          <div style={{ display:'flex', flexWrap:'wrap', gap:4, justifyContent: isMe ? 'flex-end' : 'flex-start', maxWidth:'100%' }}>
            {tags.map((t,i) => <ChatTagPill key={i} tag={t} />)}
          </div>
        )}
        <div style={{ fontSize:10, color:'#9ca3af', textAlign: isMe ? 'right' : 'left' }}>{msg.time}</div>
      </div>
    </div>
  );
};

// ── NudgeTaskPicker — step-2 task picker for /nudge with status tabs ─────────
const NudgeTaskPicker = ({ items, onSelect, accent }) => {
  const [statusFilter, setStatusFilter] = React.useState('all');
  const STATUS_TABS = ['all', 'Open', 'In Progress', 'Blocked', 'Done', 'Resolved'];
  const SC = STATUS_COLORS;
  const filtered = statusFilter === 'all' ? items : items.filter(i => {
    const meta = (i.meta || '').toLowerCase();
    return meta.includes(statusFilter.toLowerCase()) || i.status === statusFilter;
  });
  return (
    <div>
      {/* Status filter tabs */}
      <div style={{ display:'flex', gap:5, padding:'7px 12px', overflowX:'auto', borderBottom:'1px solid #f3f4f6' }}>
        {STATUS_TABS.map(s => {
          const sc = SC[s] || '#6b7280';
          const active = statusFilter === s;
          return (
            <button key={s} onClick={() => setStatusFilter(s)}
              style={{ flexShrink:0, padding:'4px 10px', borderRadius:20, border:`1px solid ${active ? sc : '#e5e7eb'}`,
                background: active ? sc+'18' : '#f9fafb',
                color: active ? sc : '#9ca3af', fontSize:11.5, fontWeight: active ? 800 : 600, cursor:'pointer' }}>
              {s === 'all' ? 'All' : s}
            </button>
          );
        })}
      </div>
      {/* Skip option */}
      <div onClick={() => onSelect({ id:'skip', label:'No specific task', meta:'General nudge' })}
        style={{ display:'flex', alignItems:'center', gap:10, padding:'10px 16px', cursor:'pointer', borderBottom:'1px solid #f9fafb', background:'#fffbeb' }}
        onMouseEnter={e => e.currentTarget.style.background='#fef9c3'}
        onMouseLeave={e => e.currentTarget.style.background='#fffbeb'}>
        <div style={{ flex:1, fontSize:13, color:'#92400e', fontWeight:700 }}>No specific task — general nudge</div>
        <span style={{ fontSize:11, color:accent, fontWeight:700 }}>Select →</span>
      </div>
      <div style={{ maxHeight:180, overflowY:'auto' }}>
        {filtered.length === 0 && <div style={{ padding:'12px 16px', color:'#9ca3af', fontSize:13 }}>No tasks with status "{statusFilter}"</div>}
        {filtered.map(item => {
          // Try to detect status from meta
          const metaLower = (item.meta || '').toLowerCase();
          let badge = null;
          if (metaLower.includes('overdue') || item.isDelayed) badge = { label:'Delayed', color:'#dc2626' };
          else if (metaLower.includes('today') || metaLower.includes('now')) badge = { label:'Due Today', color:'#f59e0b' };
          else if (metaLower.includes('blocked')) badge = { label:'Blocked', color:'#dc2626' };
          else if (metaLower.includes('done') || metaLower.includes('resolved')) badge = { label:'Done', color:'#16a34a' };
          else if (metaLower.includes('in progress')) badge = { label:'In Progress', color:'#2563eb' };
          return (
            <div key={item.id} onClick={() => onSelect(item)}
              style={{ display:'flex', alignItems:'center', gap:10, padding:'10px 16px', cursor:'pointer', borderBottom:'1px solid #f9fafb' }}
              onMouseEnter={e => e.currentTarget.style.background='#f9fafb'}
              onMouseLeave={e => e.currentTarget.style.background='transparent'}>
              <div style={{ flex:1, minWidth:0 }}>
                <div style={{ fontWeight:600, fontSize:13.5, color:'#111827', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{item.label}</div>
                {item.meta && <div style={{ fontSize:11.5, color:'#9ca3af' }}>{item.meta}</div>}
              </div>
              {badge && <span style={{ fontSize:10, fontWeight:800, padding:'2px 7px', borderRadius:20, background:badge.color+'18', color:badge.color, border:`1px solid ${badge.color}40`, flexShrink:0 }}>{badge.label}</span>}
              <span style={{ fontSize:11, color:accent, fontWeight:700 }}>Nudge →</span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ── ChatComposer — full slash-command input bar with rich card flows ──────────
const ChatComposer = ({ accent = '#16a34a', placeholder = 'Message…', onSend, dataSources = {}, commands, role, leadId }) => {
  const [input, setInput]           = React.useState('');
  const [mode, setMode]             = React.useState(null); // null|'cmds'|'sub1'|'sub2'|'sub3'|'notes'|'link'|'drive'|'reminder'|'addwork'
  const [driveFiles, setDriveFiles] = React.useState(null); // null=loading, []=empty/error, [...]=loaded
  const [driveErr, setDriveErr]     = React.useState('');
  const [activeCmd, setActiveCmd]   = React.useState(null);
  const [cmdFilter, setCmdFilter]   = React.useState('');
  const [subFilter, setSubFilter]   = React.useState('');
  const [freeText, setFreeText]     = React.useState('');
  const [step1, setStep1]           = React.useState(null); // first selection (task or person)
  const [pendingTags, setPending]   = React.useState([]);
  const [pendingCards, setPendingC] = React.useState([]);
  // addwork form fields
  const [awTitle, setAwTitle]       = React.useState('');
  const [awDur, setAwDur]           = React.useState('');
  const [awCmplx, setAwCmplx]       = React.useState('');
  const [awNotes, setAwNotes]       = React.useState('');
  const [actionCmd, setActionCmd]   = React.useState(null); // full cmd definition for action mode
  const [actionData, setActionData] = React.useState({});   // { fieldKey: value }
  const [convStep, setConvStep]    = React.useState(0);
  const [convData, setConvData]    = React.useState({});
  const [searchCat,    setSearchCat]    = React.useState(null);  // selected search category
  const [searchQuery,  setSearchQuery]  = React.useState('');    // typed query
  const [searchStep,   setSearchStep]   = React.useState('cat'); // 'cat' | 'query' | 'result'

  const inputRef = React.useRef();
  const subRef   = React.useRef();

  // Append AI_CMDS, but gate /forecast + /revenue to head/manager roles only
  const _headRole = window.HEAD_ROLES && window.HEAD_ROLES.has(role);
  const _aiFiltered = AI_CMDS.filter(c => {
    if (c.cmd === 'forecast' || c.cmd === 'revenue') return _headRole;
    return true;
  });
  const allCmds = commands ? [...commands, ..._aiFiltered] : SLASH_CMDS;
  const cmds = allCmds.filter(c => !cmdFilter || c.cmd.startsWith(cmdFilter) || c.label.toLowerCase().includes(cmdFilter.toLowerCase()));

  const getSubItems = (cmdKey, filter) => {
    const ds = dataSources || {};
    const stdMap = {
      client:   (ds.client   || ds.lead  || []).map(i => ({ id:i.id||i.name, label:i.name||i.label, meta:i.meta||i.project||i.role||'' })),
      lead:     (ds.lead     || ds.client|| []).map(i => ({ id:i.id||i.name, label:i.name||i.label, meta:i.meta||i.project||'' })),
      order:    (ds.order    || []).map(i => ({ id:i.id, label:i.name||i.label||i.code||i.title, meta:i.meta||i.status||'' })),
      task:     (ds.task     || []).map(i => ({ id:i.id||i.title, label:i.title||i.name, meta:i.meta||i.assignee||i.priority||'' })),
      employee: (ds.employee || []).map(i => ({ id:i.id||i.name, label:i.name, meta:i.meta||i.role||'' })),
      patient:  (ds.patient  || ds.lead  || []).map(i => ({ id:i.id||i.name, label:i.name||i.label, meta:i.meta||i.condition||i.ward||'' })),
      batch:    (ds.batch    || []).map(i => ({ id:i.id||i.name, label:i.name||i.label, meta:i.meta||i.status||'' })),
      module:   (ds.module   || []).map(i => ({ id:i.id||i.name, label:i.name||i.title||i.label, meta:i.meta||i.type||'' })),
      product:  (ds.product  || []).map(i => ({ id:i.id||i.name, label:i.name||i.label, meta:i.meta||i.sku||'' })),
      process:  (ds.process  || []).map(i => ({ id:i.id||i.name, label:i.name||i.label, meta:i.meta||i.status||'' })),
    };
    // Fall back to any matching dsKey in dataSources
    const raw = stdMap[cmdKey] || (ds[cmdKey] ? ds[cmdKey].map(i => ({ id:i.id||i.name||i.title, label:i.name||i.title||i.label||String(i.id||''), meta:i.meta||i.status||i.role||'' })) : []);
    if (!filter) return raw;
    const q = filter.toLowerCase();
    return raw.filter(i => i.label.toLowerCase().includes(q) || (i.meta||'').toLowerCase().includes(q));
  };

  const sub1Items = React.useMemo(() => {
    const key = { task:'task', addwork:null, status:'task', reassign:'task', nudge:'employee',
                  askstatus:'task', client:'client', lead:'client', order:'order' }[activeCmd] || 'task';
    return getSubItems(key, subFilter);
  }, [activeCmd, subFilter, dataSources]);

  const sub2Items = React.useMemo(() => {
    if (activeCmd === 'client' || activeCmd === 'lead') {
      return [
        { id:'claim',  label:'Claim for Me',   meta:'Add to my list immediately' },
        { id:'assign', label:'Assign to…',      meta:'Pick a team member to assign' },
      ];
    }
    const key = { task:'employee', reassign:'employee' }[activeCmd] || null;
    if (!key) return STATUS_OPTS.map(s => ({ id:s, label:s, meta:'' }));
    return getSubItems(key, subFilter);
  }, [activeCmd, subFilter, dataSources]);

  // sub3: employee picker for 'assign to' branch of client/lead
  const sub3Items = React.useMemo(() => getSubItems('employee', subFilter), [subFilter, dataSources]);

  const openCmds = (filter='') => { setMode('cmds'); setCmdFilter(filter); setSubFilter(''); };

  const handleInput = (val) => {
    setInput(val);
    const lastToken = val.split(' ').pop();
    if (lastToken === '/') { openCmds(''); return; }
    if (lastToken.startsWith('/') && lastToken.length > 1) { openCmds(lastToken.slice(1)); return; }
    if (mode === 'cmds') setMode(null);
  };

  const stripSlash = () => {
    const parts = input.split(' ');
    if (parts[parts.length-1].startsWith('/')) parts.pop();
    setInput(parts.join(' ').trim());
  };

  const selectCmd = (cmdObj) => {
    const cmd = typeof cmdObj === 'object' ? cmdObj.cmd : cmdObj;
    const def = typeof cmdObj === 'object' ? cmdObj : null;
    setActiveCmd(cmd); stripSlash(); setSubFilter(''); setStep1(null);
    if (cmd === 'notes')    { setMode('notes');    setFreeText(''); }
    else if (cmd === 'link')     { setMode('link');     setFreeText(''); }
    else if (cmd === 'drive')    { setMode('drive');    loadDriveFiles(); }
    else if (cmd === 'reminder') { setMode('reminder'); }
    else if (cmd === 'addwork')  { setMode('addwork');  setAwTitle(''); setAwDur(''); setAwCmplx(''); setAwNotes(''); }
    else if (cmd === 'task' || cmd === 'status' || cmd === 'reassign' || cmd === 'nudge' || cmd === 'askstatus' || cmd === 'client' || cmd === 'lead') { setMode('sub1'); }
    else if (cmd === 'search') { setMode('search'); setSubFilter(''); setSearchCat(null); setSearchQuery(''); setSearchStep('cat'); }
    else if (def && def.flow !== undefined) {
      if (def.flow.length > 0) {
        setMode('conv'); setActionCmd(def); setConvStep(0); setConvData({});
      } else {
        // Zero-step flow — instant card (dashboard, alerts, etc.)
        addCard({ cardType:'flow', cmd:def.cmd, icon:def.icon||'📋', label:def.label,
                  cat:def.cat||'ops', data:{}, flow:[], ai:def.ai||[],
                  result:def.result||{ title:def.label } });
      }
    } else if (def && def.fields && def.fields.length > 0) {
      // Role-specific command with form fields
      setMode('action'); setActionCmd(def); setActionData({});
    } else {
      // Simple command — immediately create ActionCard (no fields needed)
      if (def && !def.fields) {
        addCard({ cardType:'action', cmd:def.cmd, label:def.label, icon:def.icon, cat:def.cat, fields:{} });
      } else {
        setMode('sub1');
      }
    }
    setTimeout(() => subRef.current && subRef.current.focus(), 50);
  };

  const addSimpleTag = (tag) => {
    setPending(p => [...p, tag]);
    setMode(null); setActiveCmd(null); setSubFilter(''); setFreeText(''); setStep1(null);
    setTimeout(() => inputRef.current && inputRef.current.focus(), 50);
  };

  const addCard = (card) => {
    const _cid = 'c' + Date.now() + Math.random().toString(36).slice(2, 6);
    setPendingC(p => [...p, { ...card, _cid }]);
    setMode(null); setActiveCmd(null); setSubFilter(''); setFreeText(''); setStep1(null);
    setTimeout(() => inputRef.current && inputRef.current.focus(), 50);

    // ── "/priority", "/escalate", "/stuck" — real manager flags ──────────
    if (card.cardType === 'flow' && ['priority', 'escalate', 'stuck'].includes(card.cmd)
        && window.SRAPI && typeof window.SRAPI.raiseManagerFlag === 'function') {
      const d = card.data || {};
      let payload;
      if (card.cmd === 'priority') {
        payload = {
          flagType: 'priority_conflict', subjectLabel: d.taskA,
          message: `Conflict between "${d.taskA || 'task A'}" and "${d.taskB || 'task B'}"${d.note ? ' — ' + d.note : ''}`,
          context: { taskA: d.taskA, taskB: d.taskB },
        };
      } else if (card.cmd === 'escalate') {
        payload = {
          flagType: 'escalation', severity: d.severity === 'Urgent' ? 'urgent' : 'normal',
          subjectLabel: d.subject, message: d.message || 'Needs attention',
        };
      } else {
        payload = { flagType: 'stuck', subjectLabel: d.subject, message: d.reason || 'Blocked' };
      }
      window.SRAPI.raiseManagerFlag(payload).then(res => {
        setPendingC(p => p.map(c => c._cid === _cid ? { ...c, result: { title: 'Sent to manager ✓', action: `${res.notifiedManagers || 0} manager(s) notified` } } : c));
      }).catch(e => {
        setPendingC(p => p.map(c => c._cid === _cid ? { ...c, result: { title: 'Could not send', action: e?.message || 'Try again' } } : c));
      });
      return;
    }

    // ── "/meeting" — real scheduling via Google Meet / Zoom / offline ────
    if (card.cardType === 'flow' && card.cmd === 'meeting' && window.SRAPI && typeof window.SRAPI.scheduleMeeting === 'function') {
      const d = card.data || {};
      const TYPE_MAP = { 'Google Meet': 'meet', 'Zoom': 'zoom', 'In person': 'offline' };
      window.SRAPI.scheduleMeeting({
        title: d.title || 'Meeting',
        attendees: (d.attendees || '').split(',').map(s => s.trim()).filter(Boolean),
        when: d.when, type: TYPE_MAP[d.type] || 'offline', location: d.location || '',
      }).then(res => {
        const patch = res.joinUrl
          ? { title: 'Meeting Scheduled ✓', action: `Join: ${res.joinUrl}` }
          : { title: 'Meeting Scheduled ✓', action: d.location ? `Location: ${d.location}` : 'In person' };
        if (res.unresolvedAttendees && res.unresolvedAttendees.length) {
          patch.action += ` · Not found: ${res.unresolvedAttendees.join(', ')}`;
        }
        setPendingC(p => p.map(c => c._cid === _cid ? { ...c, result: patch } : c));
      }).catch(e => {
        setPendingC(p => p.map(c => c._cid === _cid ? { ...c, result: { title: 'Could not schedule meeting', action: e?.message || 'Try again' } } : c));
      });
      return;
    }

    // ── Persist FlowCard to backend ──────────────────────────────────────
    if (card.cardType === 'flow' && window.SRAPI && typeof window.SRAPI.submitFlowCard === 'function') {
      const industry = (dataSources && dataSources._ns) || 'realty';
      const backendIndustry = industry === 'realty' ? 'realty' : 'ld';
      window.SRAPI.submitFlowCard(card.cmd, card.data || {}, backendIndustry).then(res => {
        if (res && res.success === false) {
          setPendingC(p => p.map(c => c._cid === _cid ? { ...c, result: { title: 'Could not save', message: res.error, error: true } } : c));
        }
      }).catch(() => {});
    }
  };

  const loadDriveFiles = () => {
    setDriveFiles(null); setDriveErr('');
    if (!leadId) { setDriveErr('Open this chat from a lead to access their Drive files.'); setDriveFiles([]); return; }
    if (!window.SRAPI || typeof window.SRAPI.fetchLeadDrive !== 'function') {
      setDriveErr('Sign in required'); setDriveFiles([]); return;
    }
    window.SRAPI.fetchLeadDrive(leadId).then(res => {
      if (!res || !res.connected) { setDriveErr('Google Drive is not connected for this workspace.'); setDriveFiles([]); return; }
      const files = [
        ...(res.rootFiles || []),
        ...((res.subFolders || []).flatMap(sf => sf.files || [])),
      ];
      setDriveFiles(files);
      if (files.length === 0) setDriveErr('No files in this lead’s Drive folder yet.');
    }).catch(e => { setDriveErr(e?.message || 'Could not load Drive files'); setDriveFiles([]); });
  };

  const attachDriveFile = (file) => {
    addCard({ cardType:'link', url: file.webViewLink, title: file.name });
  };

  const removeTag  = (i) => setPending(p => p.filter((_,j) => j !== i));
  const removeCard = (i) => setPendingC(p => p.filter((_,j) => j !== i));

  const handleSub1 = (item) => {
    if (activeCmd === 'order') {
      addSimpleTag({ type:activeCmd, id:item.id, label:item.label, meta:item.meta });
      return;
    }
    if (activeCmd === 'client' || activeCmd === 'lead') {
      // Step 1 = pick client/lead → go to step 2 to Claim or Assign
      setStep1(item); setSubFilter(''); setMode('sub2');
      return;
    }
    if (activeCmd === 'askstatus') {
      // Step 1 = pick task → send a real cross-device status request to the assignee
      const assignee = item.assignee || item.meta || '';
      if (assignee && window.SRAPI && typeof window.SRAPI.sendNotify === 'function') {
        window.SRAPI.sendNotify({
          toName: assignee, type: 'askstatus', title: 'Status requested',
          body: `Please share a status update on: "${item.label}"`, taskId: item.id,
        }).catch(() => {});
      }
      addCard({ cardType:'askstatus', to: assignee || 'Assignee', taskTitle: item.label, taskId: item.id });
      return;
    }
    // two-step commands
    setStep1(item); setSubFilter('');
    setMode('sub2');
  };

  const handleSub2 = (item) => {
    const now = new Date().toLocaleTimeString([],{hour:'2-digit',minute:'2-digit'});
    if (activeCmd === 'client' || activeCmd === 'lead') {
      // item: { id:'claim', label:'Claim for Me' } or { id:'assign', label:'Assign to…' → leads to sub3 }
      if (item.id === 'claim') {
        const card = { cardType:'claim', name:step1.label, meta:step1.meta, claimedBy:'You' };
        addCard(card);
      } else if (item.id === 'assign') {
        // Would need sub3 for pick-person; for simplicity show employee picker inline
        setMode('sub3');
      }
      return;
    }
    if (activeCmd === 'task') {
      const card = { cardType:'task', title:step1.label, assignee:item.label, id:'t'+Date.now() };
      if (window.SRAPI && typeof window.SRAPI.sendNotify === 'function') {
        window.SRAPI.sendNotify({ toName:item.label, type:'task_assigned', title:step1.label,
          body:`Assigned to you`, taskId:step1.id }).catch(() => {});
      }
      window.kpiTrack && window.kpiTrack(item.label, 'assigned', { task:step1.label });
      addCard(card);
    } else if (activeCmd === 'reassign') {
      const card = { cardType:'reassign', taskTitle:step1.label, from:step1.meta||'Unassigned', to:item.label };
      const realTaskId = (dataSources.task || []).some(t => t.id === step1.id && String(t.id).length > 8) ? step1.id : null;
      if (realTaskId && window.SRAPI && typeof window.SRAPI.updateTask === 'function') {
        window.SRAPI.updateTask(realTaskId, { owner: item.label }).catch(() => {});
      }
      if (window.SRAPI && typeof window.SRAPI.sendNotify === 'function') {
        window.SRAPI.sendNotify({ toName:item.label, type:'task_assigned', title:step1.label,
          body:`Reassigned to you`, taskId:step1.id }).catch(() => {});
      }
      window.kpiTrack && window.kpiTrack(item.label, 'assigned', { task:step1.label });
      window.kpiTrack && window.kpiTrack(step1.meta, 'reassigned', { task:step1.label });
      addCard(card);
    } else if (activeCmd === 'status') {
      const card = { cardType:'status', taskTitle:step1.label, status:item.label };
      const STATUS_TO_BACKEND = { Open:'Pending', 'In Progress':'In Progress', Blocked:'Blocked', Done:'Done', Resolved:'Done' };
      if (window.SRAPI && typeof window.SRAPI.updateTask === 'function') {
        window.SRAPI.updateTask(step1.id, { status: STATUS_TO_BACKEND[item.label] || item.label }).catch(() => {});
      }
      if (item.label === 'Done' || item.label === 'Resolved') {
        window.kpiTrack && window.kpiTrack(step1.meta||'Unknown', 'completed', { task:step1.label });
      }
      addCard(card);
    } else if (activeCmd === 'nudge') {
      const card = { cardType:'nudge', to:step1.label, taskTitle:item.id!=='skip' ? item.label : '', message:'Please update on this task.' };
      if (window.SRAPI && typeof window.SRAPI.sendNotify === 'function') {
        window.SRAPI.sendNotify({ toName: step1.label, type:'nudge', title:'Nudge',
          body: card.taskTitle ? `Please update on: "${card.taskTitle}"` : card.message }).catch(() => {});
      }
      window.kpiTrack && window.kpiTrack(step1.label, 'nudged', { task:item.label });
      addCard(card);
    }
  };

  const handleSub3 = (item) => {
    // Sub3: assign client/lead to a specific person
    const card = { cardType:'claim', name:step1.label, meta:step1.meta, assignedTo:item.label };
    addCard(card);
  };


  const handleConvAnswer = (val) => {
    if (!val || !val.trim()) return;
    const step = actionCmd.flow[convStep];
    const newData = { ...convData, [step.key]: val.trim() };
    setConvData(newData);
    if (convStep + 1 < actionCmd.flow.length) {
      setConvStep(s => s + 1);
    } else {
      addCard({
        cardType:'flow', cmd:actionCmd.cmd, icon:actionCmd.icon||'📋', label:actionCmd.label,
        cat:actionCmd.cat||'ops', data:newData, flow:actionCmd.flow,
        ai:actionCmd.ai||[], result:actionCmd.result||{ title:actionCmd.label+' ✓' },
      });
    }
  };

  const send = () => {
    if (!input.trim() && pendingTags.length === 0 && pendingCards.length === 0) return;
    onSend && onSend(input.trim(), [...pendingTags], [...pendingCards]);
    setInput(''); setPending([]); setPendingC([]); setMode(null); setActiveCmd(null); setStep1(null);
  };

  const dismiss = () => { setMode(null); setActiveCmd(null); setSubFilter(''); setFreeText(''); setStep1(null); setActionCmd(null); setActionData({}); };

  const SubHeader = ({ label, color }) => (
    <div style={{ padding:'8px 12px', display:'flex', alignItems:'center', gap:8, borderBottom:'1px solid #f3f4f6' }}>
      <span style={{ fontSize:13, color:color||accent, fontWeight:800 }}>{label}</span>
      <input ref={subRef} value={subFilter} onChange={e => setSubFilter(e.target.value)}
        placeholder="Search…"
        style={{ flex:1, border:'none', outline:'none', fontSize:13, color:'#111827', background:'transparent' }} />
      <button onClick={dismiss} style={{ border:'none', background:'none', cursor:'pointer', color:'#9ca3af', fontSize:18, padding:0, lineHeight:1 }}>✕</button>
    </div>
  );

  const SubList = ({ items, onSelect, emptyLabel }) => (
    <div style={{ maxHeight:200, overflow:'auto' }}>
      {items.length === 0 && <div style={{ padding:'12px 16px', color:'#9ca3af', fontSize:13 }}>{emptyLabel || 'No items found'}</div>}
      {items.map(item => (
        <div key={item.id} onClick={() => onSelect(item)}
          style={{ display:'flex', alignItems:'center', gap:10, padding:'10px 16px', cursor:'pointer', borderBottom:'1px solid #f9fafb' }}
          onMouseEnter={e => e.currentTarget.style.background='#f9fafb'}
          onMouseLeave={e => e.currentTarget.style.background='transparent'}>
          <div style={{ flex:1, minWidth:0 }}>
            <div style={{ fontWeight:600, fontSize:13.5, color:'#111827', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{item.label}</div>
            {item.meta && <div style={{ fontSize:11.5, color:'#9ca3af' }}>{item.meta}</div>}
          </div>
          <span style={{ fontSize:11, color:accent, fontWeight:700 }}>Select →</span>
        </div>
      ))}
    </div>
  );

  return (
    <div style={{ background:'#fff', borderTop:'1px solid #f3f4f6', flexShrink:0 }}>

      {/* ── Pending cards preview ────────────────────────────────── */}
      {pendingCards.length > 0 && (
        <div style={{ padding:'8px 12px 0', display:'flex', flexWrap:'wrap', gap:6 }}>
          {pendingCards.map((c,i) => (
            <span key={i} onClick={() => removeCard(i)} style={{
              display:'inline-flex', alignItems:'center', gap:4, padding:'4px 10px',
              borderRadius:20, background:(CHAT_TAG_META[c.cardType]||CHAT_TAG_META.link).bg,
              border:`1px solid ${(CHAT_TAG_META[c.cardType]||CHAT_TAG_META.link).border}`,
              color:(CHAT_TAG_META[c.cardType]||CHAT_TAG_META.link).text,
              fontSize:12, fontWeight:700, cursor:'pointer',
            }}>
              {(CHAT_TAG_META[c.cardType]||CHAT_TAG_META.link).icon} {c.title||c.taskTitle||c.when||c.url||'card'}
              <span style={{opacity:.5, marginLeft:2, fontSize:13, fontWeight:300}}>✕</span>
            </span>
          ))}
        </div>
      )}

      {/* ── Pending tag chips ────────────────────────────────────── */}
      {pendingTags.length > 0 && (
        <div style={{ padding:'8px 12px 0', display:'flex', flexWrap:'wrap', gap:5 }}>
          {pendingTags.map((t,i) => (
            <span key={i} onClick={() => removeTag(i)} style={{
              display:'inline-flex', alignItems:'center', gap:4, padding:'4px 10px',
              borderRadius:20, background:(CHAT_TAG_META[t.type]||CHAT_TAG_META.link).bg,
              border:`1px solid ${(CHAT_TAG_META[t.type]||CHAT_TAG_META.link).border}`,
              color:(CHAT_TAG_META[t.type]||CHAT_TAG_META.link).text,
              fontSize:12, fontWeight:700, cursor:'pointer',
            }}>
              {(CHAT_TAG_META[t.type]||CHAT_TAG_META.link).icon} {t.label}
              <span style={{opacity:.5, marginLeft:2, fontSize:13, fontWeight:300}}>✕</span>
            </span>
          ))}
        </div>
      )}

      {/* ── Slash command picker ──────────────────────────────────── */}
      {mode === 'cmds' && (
        <div style={{ maxHeight:260, overflow:'auto', borderBottom:'1px solid #f3f4f6' }}>
          {cmds.length === 0 && <div style={{ padding:'12px 16px', color:'#9ca3af', fontSize:13 }}>No commands match "/{cmdFilter}"</div>}
          {cmds.map(c => {
            const catCol = CMD_CAT[c.cat] || CHAT_TAG_META[c.cmd] || CHAT_TAG_META.link;
            const iconBg = catCol.bg; const iconBorder = catCol.border; const iconText = catCol.text;
            const icon = c.icon || (CHAT_TAG_META[c.cmd]||CHAT_TAG_META.link).icon;
            return (
              <div key={c.cmd} onClick={() => selectCmd(c)}
                style={{ display:'flex', alignItems:'center', gap:12, padding:'10px 16px', cursor:'pointer', borderBottom:'1px solid #f9fafb' }}
                onMouseEnter={e => e.currentTarget.style.background='#f9fafb'}
                onMouseLeave={e => e.currentTarget.style.background='transparent'}>
                <div style={{ width:36, height:36, borderRadius:10,
                  background:iconBg, border:`1px solid ${iconBorder}`,
                  display:'flex', alignItems:'center', justifyContent:'center', fontSize:16, flexShrink:0, color:iconText }}>
                  {icon}
                </div>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ fontWeight:700, fontSize:13, color:'#111827' }}>
                    <span style={{ color:iconText, fontFamily:'monospace', fontSize:12 }}>/{c.cmd}</span>
                    <span style={{ color:'#374151', marginLeft:6 }}>{c.label}</span>
                  </div>
                  <div style={{ fontSize:11.5, color:'#9ca3af' }}>{c.desc}</div>
                </div>
                {c.flow   && c.flow.length > 0 && <span style={{ fontSize:10, color:iconText, background:iconBg, padding:'2px 6px', borderRadius:8, fontWeight:700, flexShrink:0 }}>guided</span>}
                {c.flow   && c.flow.length === 0 && <span style={{ fontSize:10, color:iconText, background:iconBg, padding:'2px 6px', borderRadius:8, fontWeight:700, flexShrink:0 }}>auto</span>}
                {c.fields && !c.flow && <span style={{ fontSize:10, color:iconText, background:iconBg, padding:'2px 6px', borderRadius:8, fontWeight:700, flexShrink:0 }}>form</span>}
              </div>
            );
          })}
        </div>
      )}

      {/* ── Step 1: pick task or person ───────────────────────────── */}
      {mode === 'sub1' && (
        <div style={{ borderBottom:'1px solid #f3f4f6' }}>
          <SubHeader label={`/${activeCmd} — ${ (activeCmd==='task'||activeCmd==='status'||activeCmd==='reassign'||activeCmd==='askstatus') ? 'Pick task' : (activeCmd==='client'||activeCmd==='lead') ? 'Pick client / lead' : 'Pick person' }`} />
          <SubList items={sub1Items} onSelect={handleSub1} emptyLabel={`No items found`} />
        </div>
      )}

      {/* ── Step 2: pick assignee / status / nudge task ─────────── */}
      {mode === 'sub2' && (
        <div style={{ borderBottom:'1px solid #f3f4f6' }}>
          <SubHeader
            label={`/${activeCmd} — ${step1 ? `"${step1.label.slice(0,20)}" → ` : ''}${activeCmd==='status'?'Pick status':activeCmd==='nudge'?'Pick task to nudge about':(activeCmd==='client'||activeCmd==='lead')?'Claim or assign':'Pick assignee'}`}
            color={accent}
          />
          {(activeCmd === 'client' || activeCmd === 'lead') ? (
            <SubList items={sub2Items} onSelect={handleSub2} />
          ) : activeCmd === 'status' ? (
            <div style={{ padding:'10px 12px', display:'flex', flexWrap:'wrap', gap:6 }}>
              {STATUS_OPTS.map(s => {
                const sc = STATUS_COLORS[s]||'#6b7280';
                return (
                  <button key={s} onClick={() => handleSub2({ id:s, label:s, meta:'' })}
                    style={{ padding:'7px 14px', borderRadius:20, border:`1px solid ${sc}40`, background:sc+'18', color:sc, fontSize:12.5, fontWeight:700, cursor:'pointer' }}>
                    {s}
                  </button>
                );
              })}
            </div>
          ) : activeCmd === 'nudge' ? (
            // Nudge: task picker with status filter tabs
            <NudgeTaskPicker items={sub2Items} onSelect={handleSub2} accent={accent} />
          ) : (
            <SubList items={sub2Items} onSelect={handleSub2} />
          )}
        </div>
      )}

      {/* ── Sub3: pick employee to assign client/lead ─────────────── */}
      {mode === 'sub3' && (
        <div style={{ borderBottom:'1px solid #f3f4f6' }}>
          <SubHeader label={`/client — "${(step1||{}).label||''}": Assign to`} color={accent} />
          <SubList items={sub3Items} onSelect={handleSub3} emptyLabel="No team members found" />
        </div>
      )}

      {/* ── Notes ─────────────────────────────────────────────────── */}
      {mode === 'notes' && (
        <div style={{ padding:'10px 12px', borderBottom:'1px solid #f3f4f6' }}>
          <div style={{ display:'flex', alignItems:'center', gap:6, marginBottom:8 }}>
            <span style={{ fontSize:13, color:'#6b21a8', fontWeight:700 }}>/notes</span>
            <span style={{ flex:1 }} />
            <button onClick={dismiss} style={{ border:'none', background:'none', cursor:'pointer', color:'#9ca3af', fontSize:18, padding:0, lineHeight:1 }}>✕</button>
          </div>
          <textarea ref={subRef} value={freeText} onChange={e => setFreeText(e.target.value)} placeholder="Type your note…" rows={3}
            style={{ width:'100%', boxSizing:'border-box', border:'1px solid #e9d5ff', borderRadius:10, padding:'8px 12px', fontSize:13, resize:'none', outline:'none', color:'#111827', background:'#faf5ff' }} />
          <button onClick={() => freeText.trim() && addCard({ cardType:'note', text:freeText.trim() })}
            style={{ marginTop:8, padding:'7px 16px', borderRadius:10, background:'#6b21a8', border:'none', color:'#fff', fontWeight:700, fontSize:13, cursor:'pointer' }}>Attach Note</button>
        </div>
      )}

      {/* ── Link ──────────────────────────────────────────────────── */}
      {mode === 'link' && (
        <div style={{ padding:'10px 12px', borderBottom:'1px solid #f3f4f6' }}>
          <div style={{ display:'flex', alignItems:'center', gap:6, marginBottom:8 }}>
            <span style={{ fontSize:13, color:'#475569', fontWeight:700 }}>/link</span>
            <span style={{ flex:1 }} />
            <button onClick={dismiss} style={{ border:'none', background:'none', cursor:'pointer', color:'#9ca3af', fontSize:18, padding:0, lineHeight:1 }}>✕</button>
          </div>
          <input ref={subRef} value={freeText} onChange={e => setFreeText(e.target.value)} placeholder="Paste URL or document link…"
            style={{ width:'100%', boxSizing:'border-box', border:'1px solid #cbd5e1', borderRadius:10, padding:'9px 12px', fontSize:13, outline:'none', color:'#111827' }} />
          <button onClick={() => freeText.trim() && addCard({ cardType:'link', url:freeText.trim() })}
            style={{ marginTop:8, padding:'7px 16px', borderRadius:10, background:'#475569', border:'none', color:'#fff', fontWeight:700, fontSize:13, cursor:'pointer' }}>Attach Link</button>
        </div>
      )}

      {/* ── Drive — attach a real file from this lead's Drive folder ── */}
      {mode === 'drive' && (
        <div style={{ padding:'10px 12px', borderBottom:'1px solid #f3f4f6' }}>
          <div style={{ display:'flex', alignItems:'center', gap:6, marginBottom:8 }}>
            <span style={{ fontSize:13, color:'#475569', fontWeight:700 }}>/drive</span>
            <span style={{ flex:1 }} />
            <button onClick={dismiss} style={{ border:'none', background:'none', cursor:'pointer', color:'#9ca3af', fontSize:18, padding:0, lineHeight:1 }}>✕</button>
          </div>
          {driveFiles === null && <div style={{ padding:'10px 0', color:'#9ca3af', fontSize:13 }}>Loading…</div>}
          {driveErr && <div style={{ padding:'6px 0', color:'#9ca3af', fontSize:12.5 }}>{driveErr}</div>}
          {driveFiles && driveFiles.length > 0 && (
            <div style={{ maxHeight:220, overflowY:'auto' }}>
              {driveFiles.map(f => (
                <div key={f.id} onClick={() => attachDriveFile(f)} style={{
                  padding:'9px 8px', borderBottom:'1px solid #f3f4f6', cursor:'pointer',
                  display:'flex', alignItems:'center', gap:8,
                }}>
                  <span style={{ fontSize:16 }}>📄</span>
                  <span style={{ flex:1, fontSize:13, color:'#111827' }}>{f.name}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Reminder ──────────────────────────────────────────────── */}
      {mode === 'reminder' && (
        <div style={{ padding:'10px 12px', borderBottom:'1px solid #f3f4f6' }}>
          <div style={{ display:'flex', alignItems:'center', gap:6, marginBottom:8 }}>
            <span style={{ fontSize:13, color:'#9d174d', fontWeight:700 }}>/reminder</span>
            <span style={{ flex:1 }} />
            <button onClick={dismiss} style={{ border:'none', background:'none', cursor:'pointer', color:'#9ca3af', fontSize:18, padding:0, lineHeight:1 }}>✕</button>
          </div>
          <div style={{ display:'flex', flexWrap:'wrap', gap:6 }}>
            {REMINDER_OPTS.map(opt => (
              <button key={opt} onClick={() => addCard({ cardType:'reminder', when:opt })}
                style={{ padding:'7px 14px', borderRadius:20, border:'1px solid #fbcfe8', background:'#fdf2f8', color:'#9d174d', fontSize:12.5, fontWeight:700, cursor:'pointer' }}>
                ⏰ {opt}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* ── Conversational flow (step-by-step Q&A) ─────────────────── */}
      {mode === 'conv' && actionCmd && actionCmd.flow && (() => {
        const col = CMD_CAT[actionCmd.cat] || CMD_CAT.ops;
        const curStep = actionCmd.flow[convStep] || {};
        return (
          <div style={{ borderBottom:'1px solid #f3f4f6' }}>
            {/* Header */}
            <div style={{ padding:'8px 12px 6px', display:'flex', alignItems:'center', gap:6, borderBottom:'1px solid #f9fafb' }}>
              <span style={{ fontSize:14 }}>{actionCmd.icon}</span>
              <span style={{ fontSize:12.5, color:col.text, fontWeight:800 }}>/{actionCmd.cmd}</span>
              <span style={{ fontSize:11.5, color:'#6b7280', marginLeft:2 }}>{actionCmd.label}</span>
              <span style={{ fontSize:10, color:'#9ca3af', marginLeft:'auto' }}>Step {convStep+1}/{actionCmd.flow.length}</span>
              <button onClick={dismiss} style={{ border:'none', background:'none', cursor:'pointer', color:'#9ca3af', fontSize:18, padding:0, lineHeight:1, marginLeft:6 }}>✕</button>
            </div>
            {/* Previous Q&A transcript */}
            {convStep > 0 && (
              <div style={{ padding:'6px 12px 0', maxHeight:110, overflowY:'auto' }}>
                {actionCmd.flow.slice(0, convStep).map((s, i) => (
                  <div key={i} style={{ marginBottom:5 }}>
                    <div style={{ fontSize:11.5, color:'#6b7280', marginBottom:1 }}>🤖 {s.ask}</div>
                    <div style={{ fontSize:12.5, color:'#111827', fontWeight:600, paddingLeft:4, borderLeft:`2px solid ${col.border}` }}>
                      {convData[s.key] || '—'}
                    </div>
                  </div>
                ))}
              </div>
            )}
            {/* Current question */}
            <div style={{ padding:'8px 12px 4px' }}>
              <div style={{ fontSize:13.5, fontWeight:700, color:col.text, marginBottom:8 }}>🤖 {curStep.ask}</div>
              {/* Option pills */}
              {curStep.opts && (
                <div style={{ display:'flex', flexWrap:'wrap', gap:6, marginBottom:8 }}>
                  {curStep.opts.map((opt, i) => (
                    <button key={i} onClick={() => handleConvAnswer(opt)}
                      style={{ padding:'6px 13px', borderRadius:20, border:`1px solid ${col.border}`, background:col.bg, color:col.text, fontSize:12.5, fontWeight:600, cursor:'pointer', textAlign:'left' }}>
                      {opt}
                    </button>
                  ))}
                </div>
              )}
              {/* Data-source picker (dsKey) */}
              {!curStep.opts && curStep.dsKey && (() => {
                const dsItems = getSubItems(curStep.dsKey, subFilter);
                return (
                  <div>
                    <div style={{ display:'flex', gap:6, marginBottom:6 }}>
                      <input ref={subRef} value={subFilter} onChange={e => setSubFilter(e.target.value)}
                        autoFocus placeholder={`Search ${curStep.dsKey}s…`}
                        style={{ flex:1, border:`1px solid ${col.border}`, borderRadius:10, padding:'7px 12px', fontSize:13, outline:'none', color:'#111827', background:col.bg }} />
                      <button onClick={dismiss} style={{ border:'none', background:'none', cursor:'pointer', color:'#9ca3af', fontSize:18, padding:'0 6px', lineHeight:1 }}>✕</button>
                    </div>
                    <div style={{ maxHeight:160, overflowY:'auto', borderRadius:10, border:`1px solid ${col.border}`, background:'#fff' }}>
                      {dsItems.length === 0 && <div style={{ padding:'10px 14px', color:'#9ca3af', fontSize:13 }}>No {curStep.dsKey}s found</div>}
                      {dsItems.map(item => (
                        <div key={item.id} onClick={() => { handleConvAnswer(item.label); setSubFilter(''); }}
                          style={{ display:'flex', alignItems:'center', gap:8, padding:'9px 14px', cursor:'pointer', borderBottom:`1px solid ${col.bg}` }}
                          onMouseEnter={e=>e.currentTarget.style.background=col.bg} onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
                          <div style={{ flex:1, minWidth:0 }}>
                            <div style={{ fontSize:13, fontWeight:600, color:'#111827', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{item.label}</div>
                            {item.meta && <div style={{ fontSize:11.5, color:'#9ca3af' }}>{item.meta}</div>}
                          </div>
                          <span style={{ fontSize:11, color:col.text, fontWeight:700 }}>Select →</span>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })()}
              {/* Free-text input */}
              {!curStep.opts && !curStep.dsKey && (
                <div style={{ display:'flex', gap:6 }}>
                  <input
                    autoFocus
                    value={freeText} onChange={e => setFreeText(e.target.value)}
                    onKeyDown={e => { if(e.key==='Enter' && freeText.trim()){ handleConvAnswer(freeText); setFreeText(''); } }}
                    placeholder="Type your answer…"
                    style={{ flex:1, border:`1px solid ${col.border}`, borderRadius:10, padding:'8px 12px', fontSize:13, outline:'none', color:'#111827', background:col.bg }} />
                  <button onClick={() => { if(freeText.trim()){ handleConvAnswer(freeText); setFreeText(''); } }}
                    style={{ padding:'8px 14px', borderRadius:10, background:col.text, border:'none', color:'#fff', fontWeight:700, fontSize:13, cursor:'pointer' }}>→</button>
                </div>
              )}
            </div>
          </div>
        );
      })()}

      {/* ── Universal Search ─────────────────────────────────────────── */}
      {mode === 'search' && (() => {
        const ds  = dataSources || {};
        const ind = ds._ns || 'realty';
        const { chips, options } = (window.PF_SEARCH
          ? window.PF_SEARCH.getSearchOptions(ind, role)
          : { chips:[], options:[] });

        // ── Step 1: Category picker ──────────────────────────────────────────
        if (searchStep === 'cat') {
          const q = subFilter.toLowerCase();
          const filtered = q
            ? options.filter(o => o.cat.toLowerCase().includes(q) || o.desc.toLowerCase().includes(q))
            : options;
          return (
            <div style={{ borderBottom:'1px solid #f3f4f6', maxHeight:360, overflowY:'auto' }}>
              {/* Header */}
              <div style={{ display:'flex', alignItems:'center', gap:6, padding:'10px 12px 8px', position:'sticky', top:0, background:'#fff', zIndex:1 }}>
                <span style={{ fontSize:13, fontWeight:800, color:accent }}>🔍 What would you like to search?</span>
                <span style={{ flex:1 }} />
                <button onClick={dismiss} style={{ border:'none', background:'none', cursor:'pointer', color:'#9ca3af', fontSize:18, padding:0, lineHeight:1 }}>✕</button>
              </div>
              {/* Quick chips */}
              {chips.length > 0 && !q && (
                <div style={{ display:'flex', gap:6, flexWrap:'wrap', padding:'0 12px 10px' }}>
                  {chips.map(chip => (
                    <button key={chip} onClick={() => { setSearchCat(chip); setSearchStep('query'); setSubFilter(''); }}
                      style={{ display:'flex', alignItems:'center', gap:4, padding:'5px 12px', borderRadius:20,
                        border:'1px solid ' + accent + '44', background: accent + '12', color:accent,
                        fontSize:12, fontWeight:700, cursor:'pointer', whiteSpace:'nowrap' }}>
                      🔍 {chip}
                    </button>
                  ))}
                </div>
              )}
              {/* Filter input */}
              <div style={{ padding:'0 12px 8px' }}>
                <input ref={subRef} autoFocus value={subFilter} onChange={e => setSubFilter(e.target.value)}
                  placeholder="Filter options…"
                  style={{ width:'100%', boxSizing:'border-box', border:'1px solid #e5e7eb', borderRadius:10,
                    padding:'7px 12px', fontSize:13, outline:'none', color:'#111827' }} />
              </div>
              {/* Full options list */}
              {filtered.map((opt, i) => (
                <div key={opt.cat} onClick={() => { setSearchCat(opt.cat); setSearchStep('query'); setSubFilter(''); }}
                  style={{ display:'flex', alignItems:'center', gap:10, padding:'9px 12px',
                    borderTop: i === 0 ? '1px solid #f3f4f6' : 'none',
                    borderBottom:'1px solid #f9fafb', cursor:'pointer',
                    background:'transparent' }}
                  onMouseEnter={e=>e.currentTarget.style.background='#f9fafb'}
                  onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
                  <span style={{ fontSize:20, width:28, textAlign:'center', flexShrink:0 }}>{opt.icon}</span>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ fontWeight:700, fontSize:13, color:'#111827' }}>{opt.cat}</div>
                    <div style={{ fontSize:11, color:'#9ca3af', marginTop:1 }}>{opt.desc}</div>
                  </div>
                  <span style={{ color:'#d1d5db', fontSize:16, flexShrink:0 }}>›</span>
                </div>
              ))}
              {filtered.length === 0 && q && (
                <div style={{ padding:'14px 12px', color:'#9ca3af', fontSize:13 }}>No options match "{subFilter}"</div>
              )}
            </div>
          );
        }

        // ── Step 2: Query input ──────────────────────────────────────────────
        if (searchStep === 'query') {
          return (
            <div style={{ borderBottom:'1px solid #f3f4f6' }}>
              {/* Header with back button */}
              <div style={{ display:'flex', alignItems:'center', gap:8, padding:'10px 12px 8px' }}>
                <button onClick={() => { setSearchStep('cat'); setSubFilter(''); setSearchQuery(''); }}
                  style={{ border:'none', background:'none', cursor:'pointer', color:'#6b7280', fontSize:13, padding:0, display:'flex', alignItems:'center', gap:3 }}>
                  ← Back
                </button>
                <span style={{ fontSize:13, fontWeight:800, color:accent }}>
                  🔍 Search {searchCat}
                </span>
                <span style={{ flex:1 }} />
                <button onClick={dismiss} style={{ border:'none', background:'none', cursor:'pointer', color:'#9ca3af', fontSize:18, padding:0, lineHeight:1 }}>✕</button>
              </div>
              {/* Query input */}
              <div style={{ padding:'0 12px 10px', display:'flex', gap:6 }}>
                <input ref={subRef} autoFocus value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter' && searchQuery.trim()) setSearchStep('result'); }}
                  placeholder={'Search ' + searchCat + '…'}
                  style={{ flex:1, border:'1px solid ' + accent + '55', borderRadius:10,
                    padding:'8px 12px', fontSize:13, outline:'none', color:'#111827' }} />
                <button onClick={() => setSearchStep('result')}
                  style={{ flexShrink:0, padding:'8px 14px', borderRadius:10, border:'none',
                    background:accent, color:'#fff', fontWeight:700, fontSize:13, cursor:'pointer' }}>
                  Go
                </button>
              </div>
              {/* Hint: AI search */}
              {searchCat === 'AI Search' && (
                <div style={{ margin:'0 12px 10px', padding:'8px 10px', borderRadius:8,
                  background:'#f5f3ff', border:'1px solid #ddd6fe' }}>
                  <div style={{ fontSize:12, color:'#7c3aed', fontWeight:700, marginBottom:3 }}>🤖 AI Search</div>
                  <div style={{ fontSize:11.5, color:'#6b7280' }}>
                    Ask anything: "Why are bookings down?", "Show hot leads this week", "Who hasn't been called?"
                  </div>
                </div>
              )}
            </div>
          );
        }

        // ── Step 3: Results ──────────────────────────────────────────────────
        if (searchStep === 'result') {
          return (
            <div style={{ borderBottom:'1px solid #f3f4f6', maxHeight:380, overflowY:'auto' }}>
              {/* Header with back + new search */}
              <div style={{ display:'flex', alignItems:'center', gap:6, padding:'10px 12px 8px', position:'sticky', top:0, background:'#fff', zIndex:1 }}>
                <button onClick={() => { setSearchStep('query'); }}
                  style={{ border:'none', background:'none', cursor:'pointer', color:'#6b7280', fontSize:13, padding:0, display:'flex', alignItems:'center', gap:3 }}>
                  ← Back
                </button>
                <span style={{ fontSize:12, fontWeight:700, color:'#111827' }}>
                  {searchCat}{searchQuery ? ' · ' + searchQuery : ''}
                </span>
                <span style={{ flex:1 }} />
                <button onClick={() => { setSearchStep('cat'); setSearchCat(null); setSearchQuery(''); setSubFilter(''); }}
                  style={{ fontSize:12, color:accent, fontWeight:700, border:'none', background:'none', cursor:'pointer' }}>
                  New Search
                </button>
                <button onClick={dismiss} style={{ border:'none', background:'none', cursor:'pointer', color:'#9ca3af', fontSize:18, padding:'0 0 0 6px', lineHeight:1 }}>✕</button>
              </div>
              {/* Results panel */}
              <div style={{ padding:'0 12px 12px' }}>
                {window.PF_SEARCH && React.createElement(window.PF_SEARCH.SearchResultPanel, {
                  cat:    searchCat,
                  query:  searchQuery,
                  industry: ind,
                  role:   role,
                  accent: accent,
                  onQuickAction: (cmd) => {
                    dismiss();
                    setTimeout(() => {
                      setInput(cmd + ' ');
                      const lastToken = cmd;
                      setMode('cmds'); setCmdFilter(lastToken.slice(1));
                    }, 60);
                  }
                })}
              </div>
            </div>
          );
        }

        return null;
      })()}

      {/* ── Action form (role-specific commands with fields) ─────────── */}
      {mode === 'action' && actionCmd && (() => {
        const colors = CMD_CAT[actionCmd.cat] || CMD_CAT.ops;
        return (
          <div style={{ padding:'12px 14px', borderBottom:'1px solid #f3f4f6' }}>
            <div style={{ display:'flex', alignItems:'center', gap:6, marginBottom:10 }}>
              <span style={{ fontSize:15 }}>{actionCmd.icon}</span>
              <span style={{ fontSize:13, color:colors.text, fontWeight:700 }}>/{actionCmd.cmd}</span>
              <span style={{ fontSize:12, color:'#9ca3af', marginLeft:2 }}>{actionCmd.label}</span>
              <span style={{ flex:1 }} />
              <button onClick={dismiss} style={{ border:'none', background:'none', cursor:'pointer', color:'#9ca3af', fontSize:18, padding:0, lineHeight:1 }}>✕</button>
            </div>
            {actionCmd.fields.map((f,i) => f.multiline ? (
              <textarea key={f.key} ref={i===0?subRef:undefined} value={actionData[f.key]||''} onChange={e => setActionData(d => ({...d,[f.key]:e.target.value}))}
                placeholder={f.placeholder} rows={2}
                style={{ width:'100%', boxSizing:'border-box', border:`1px solid ${colors.border}`, borderRadius:10, padding:'8px 12px', fontSize:13, resize:'none', outline:'none', color:'#111827', background:colors.bg, marginBottom:7 }} />
            ) : (
              <input key={f.key} ref={i===0?subRef:undefined} value={actionData[f.key]||''} onChange={e => setActionData(d => ({...d,[f.key]:e.target.value}))}
                placeholder={f.placeholder}
                style={{ width:'100%', boxSizing:'border-box', border:`1px solid ${colors.border}`, borderRadius:10, padding:'9px 12px', fontSize:13, outline:'none', color:'#111827', background:colors.bg, marginBottom:7 }} />
            ))}
            <button
              onClick={() => {
                const card = { cardType:'action', cmd:actionCmd.cmd, label:actionCmd.label, icon:actionCmd.icon, cat:actionCmd.cat, fields:{...actionData} };
                addCard(card);
              }}
              style={{ width:'100%', padding:'9px 0', borderRadius:10, background:colors.text, border:'none', color:'#fff', fontWeight:700, fontSize:13.5, cursor:'pointer' }}>
              Submit /{actionCmd.cmd}
            </button>
          </div>
        );
      })()}

      {/* ── Add Open Work form ─────────────────────────────────────── */}
      {mode === 'addwork' && (
        <div style={{ padding:'12px 14px', borderBottom:'1px solid #f3f4f6' }}>
          <div style={{ display:'flex', alignItems:'center', gap:6, marginBottom:10 }}>
            <span style={{ fontSize:13, color:'#9f1239', fontWeight:700 }}>＋ Add Open Work</span>
            <span style={{ flex:1 }} />
            <button onClick={dismiss} style={{ border:'none', background:'none', cursor:'pointer', color:'#9ca3af', fontSize:18, padding:0, lineHeight:1 }}>✕</button>
          </div>
          <input ref={subRef} value={awTitle} onChange={e => setAwTitle(e.target.value)} placeholder="Task title (required)"
            style={{ width:'100%', boxSizing:'border-box', border:'1px solid #fecdd3', borderRadius:10, padding:'9px 12px', fontSize:13, outline:'none', color:'#111827', marginBottom:8 }} />
          <select value={awDur} onChange={e => setAwDur(e.target.value)}
            style={{ width:'100%', boxSizing:'border-box', border:'1px solid #e5e7eb', borderRadius:10, padding:'9px 12px', fontSize:13, outline:'none', color: awDur ? '#111827' : '#9ca3af', marginBottom:8, background:'#fff' }}>
            <option value="">Duration…</option>
            {DURATION_OPTS.map(d => <option key={d} value={d}>{d}</option>)}
          </select>
          <div style={{ display:'flex', flexWrap:'wrap', gap:5, marginBottom:8 }}>
            {COMPLEXITY_OPTS.map(c => {
              const cc = COMPLEX_COLORS[c];
              const sel = awCmplx === c;
              return (
                <button key={c} onClick={() => setAwCmplx(c)}
                  style={{ padding:'5px 12px', borderRadius:20, border:`1px solid ${cc}`, background: sel ? cc : cc+'18', color: sel ? '#fff' : cc, fontSize:12, fontWeight:700, cursor:'pointer', transition:'all .12s' }}>
                  {c}
                </button>
              );
            })}
          </div>
          <textarea value={awNotes} onChange={e => setAwNotes(e.target.value)} placeholder="Additional notes…" rows={2}
            style={{ width:'100%', boxSizing:'border-box', border:'1px solid #e5e7eb', borderRadius:10, padding:'8px 12px', fontSize:13, resize:'none', outline:'none', color:'#111827', marginBottom:8 }} />
          <button
            disabled={!awTitle.trim()}
            onClick={() => {
              if (!awTitle.trim()) return;
              const card = { cardType:'addwork', title:awTitle.trim(), duration:awDur, complexity:awCmplx, notes:awNotes.trim(), openToAll:true, id:'aw'+Date.now() };
              window.addIndTask && window.addIndTask('realty', { title:card.title, duration:awDur, complexity:awCmplx, notes:awNotes.trim(), openToAll:true, status:'Open' });
              addCard(card);
            }}
            style={{ width:'100%', padding:'9px 0', borderRadius:10, background: awTitle.trim() ? '#9f1239' : '#d1d5db', border:'none', color:'#fff', fontWeight:700, fontSize:13.5, cursor: awTitle.trim() ? 'pointer' : 'default' }}>
            Post Open Work
          </button>
        </div>
      )}

      {/* ── Main input row ────────────────────────────────────────── */}
      <div style={{ padding:'10px 12px 20px', display:'flex', gap:8, alignItems:'center' }}>
        <button onClick={() => openCmds('')}
          style={{ width:38, height:38, borderRadius:12, background:'#f3f4f6', border:'none', cursor:'pointer',
            display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0,
            color:'#6b7280', fontSize:22, fontWeight:300, lineHeight:1 }}>+</button>
        <input ref={inputRef} value={input}
          onChange={e => handleInput(e.target.value)}
          onKeyDown={e => { if(e.key==='Enter' && !e.shiftKey) send(); if(e.key==='Escape') dismiss(); }}
          placeholder={placeholder}
          style={{ flex:1, background:'#f3f4f6', border:'none', borderRadius:22, padding:'10px 16px', fontSize:13.5, color:'#111827', outline:'none' }} />
        <button onClick={send} style={{ width:40, height:40, borderRadius:99, background:accent, border:'none', cursor:'pointer',
          display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/>
          </svg>
        </button>
      </div>
    </div>
  );
};

Object.assign(window, { ChatTagPill, ChatBubble, ChatComposer, NoteCard, LinkCard, TaskCard, StatusCard, NudgeCard, ReassignCard, ReminderCard, ActionCard, ClaimCard, AskStatusCard, FlowCard, AI_CMDS, CMD_CAT });


// ===== 18-pf-healthcare.jsx =====
/* global React */

const HC_DATA = {
  stats: { patients: 248, todayAppts: 32, pending: 8, critical: 2, beds: '76%', revenue: '₹4.2L' },
  patients: [
    { id:'p1', name:'Meera Sharma',   age:45, condition:'Hypertension',   doctor:'Dr. Kapoor', ward:'OPD',   status:'Active',   phone:'98765-43210', next:'May 15' },
    { id:'p2', name:'Rakesh Verma',   age:62, condition:'Diabetes T2',    doctor:'Dr. Singh',  ward:'OPD',   status:'Active',   phone:'87654-32109', next:'May 20' },
    { id:'p3', name:'Sunita Patel',   age:38, condition:'Asthma',         doctor:'Dr. Kapoor', ward:'OPD',   status:'Follow-up',phone:'76543-21098', next:'May 12' },
    { id:'p4', name:'Arjun Mehta',    age:71, condition:'Cardiac Care',   doctor:'Dr. Rao',    ward:'ICU',   status:'Critical', phone:'65432-10987', next:'May 11' },
    { id:'p5', name:'Kavita Joshi',   age:29, condition:'Post-Op',        doctor:'Dr. Singh',  ward:'Ward B',status:'Stable',   phone:'54321-09876', next:'May 13' },
    { id:'p6', name:'Ramesh Nair',    age:55, condition:'Orthopaedics',   doctor:'Dr. Bhat',   ward:'OPD',   status:'Active',   phone:'43210-98765', next:'May 14' },
  ],
  appointments: [
    { id:'a1', time:'09:00', patient:'Meera Sharma', doctor:'Dr. Kapoor', type:'Follow-up',    room:'OPD-3',     status:'Confirmed' },
    { id:'a2', time:'09:30', patient:'Sunita Patel', doctor:'Dr. Kapoor', type:'Review',       room:'OPD-3',     status:'Confirmed' },
    { id:'a3', time:'10:00', patient:'Walk-in',      doctor:'Dr. Singh',  type:'First Visit',  room:'OPD-5',     status:'Waiting'   },
    { id:'a4', time:'10:30', patient:'Rakesh Verma', doctor:'Dr. Singh',  type:'Lab Review',   room:'OPD-5',     status:'Confirmed' },
    { id:'a5', time:'11:00', patient:'Ramesh Nair',  doctor:'Dr. Bhat',   type:'Physio',       room:'PT-1',      status:'Confirmed' },
    { id:'a6', time:'11:30', patient:'Kavita Joshi', doctor:'Dr. Singh',  type:'Post-Op Check',room:'Ward B',    status:'Scheduled' },
    { id:'a7', time:'14:00', patient:'Arjun Mehta',  doctor:'Dr. Rao',    type:'ECG Review',   room:'Cardio-1',  status:'Confirmed' },
    { id:'a8', time:'15:30', patient:'New Patient',  doctor:'Dr. Kapoor', type:'Consultation', room:'OPD-3',     status:'Walk-in'   },
  ],
  tasks: [
    { id:'t1', title:'Update records — Meera Sharma',   priority:'High',     due:'Today',     status:'Pending',     for:'Front Desk' },
    { id:'t2', title:'Lab reports ready — Rakesh Verma',priority:'High',     due:'Today',     status:'Pending',     for:'Nurse'      },
    { id:'t3', title:'Medication refill — Sunita Patel',priority:'Medium',   due:'Today',     status:'Pending',     for:'Nurse'      },
    { id:'t4', title:'ICU vitals — Arjun Mehta',        priority:'Critical', due:'Now',       status:'In Progress', for:'Nurse'      },
    { id:'t5', title:'Discharge papers — Kavita Joshi', priority:'Medium',   due:'Tomorrow',  status:'Pending',     for:'Front Desk' },
    { id:'t6', title:'Monthly pharmacy audit',          priority:'Low',      due:'This week', status:'Pending',     for:'Admin'      },
    { id:'t7', title:'Staff roster — June',             priority:'Medium',   due:'This week', status:'Pending',     for:'Manager'    },
    { id:'t8', title:'NABH document renewal',           priority:'High',     due:'May 20',    status:'Pending',     for:'Admin'      },
  ],
  chat: [
    { id:'c1', from:'Dr. Kapoor',    avatar:'DK', msg:'OPD-3 running 20 min late today, please inform patients.', time:'9:02 AM', unread:true },
    { id:'c2', from:'Nursing',       avatar:'NS', msg:'ICU Bed 4 vitals uploaded to system.',                     time:'9:15 AM', unread:true },
    { id:'c3', from:'Lab',           avatar:'LB', msg:'Reports for Rakesh Verma ready for collection.',           time:'9:40 AM', unread:false },
    { id:'c4', from:'Dr. Rao',       avatar:'DR', msg:'Can someone schedule an urgent echo for Arjun Mehta?',    time:'10:05 AM',unread:true },
    { id:'c5', from:'Admin',         avatar:'AD', msg:'Reminder: Fire drill at 4 PM sharp. All staff attend.',   time:'10:30 AM',unread:false },
  ],
};

const HC_ACCENT = '#2a5d7f';
const HC_BG = '#eff6ff';

const statusColor = s => ({ Active:'#16a34a', 'Follow-up':'#ca8a04', Critical:'#dc2626', Stable:'#2a5d7f', Confirmed:'#16a34a', Waiting:'#ca8a04', Scheduled:'#6b7280', 'Walk-in':'#6e5191', 'In Progress':'#2a5d7f', Pending:'#6b7280', High:'#ca8a04', Medium:'#2a5d7f', Critical2:'#dc2626', Low:'#9ca3af' }[s] || '#6b7280');
const statusBg   = s => statusColor(s) + '18';

/* ── Home ── */
const HCScreenHome = ({ role, go, user }) => {
  const s = HC_DATA.stats;
  const today = HC_DATA.appointments.slice(0, 4);
  const myTasks = HC_DATA.tasks.filter(t => role === 'Manager' || role === 'Admin' ? true : t.for === role).slice(0, 3);
  const [profileOpen, setProfileOpen] = React.useState(false);
  return (
    <div style={{ flex:1, overflow:'auto', padding:'0 0 20px' }}>
      {/* Header */}
      <div style={{ background:HC_ACCENT, padding:'16px 20px 20px', color:'#fff', display:'flex', alignItems:'flex-start', justifyContent:'space-between' }}>
        <div>
          <div style={{ fontSize:12, opacity:.8, marginBottom:4 }}>Good morning · {role}</div>
          <div style={{ fontSize:22, fontWeight:800 }}>SkaleDesk Health</div>
          <div style={{ fontSize:12, opacity:.75, marginTop:2 }}>City Care Hospital · Today, {new Date().toLocaleDateString('en-IN',{weekday:'short',day:'numeric',month:'short'})}</div>
        </div>
        {/* Same avatar → native "More" bridge as realty (see OPEN_MORE in
            SyncBridge.ts) so this industry no longer needs its own 6th tab
            just for sign-out/profile access. */}
        {user && (
          <button
            onClick={() => {
              if (IS_NATIVE_IOS && window.ReactNativeWebView) {
                window.ReactNativeWebView.postMessage(JSON.stringify({ type: 'OPEN_MORE' }));
              } else {
                setProfileOpen(true);
              }
            }}
            style={{ background:'rgba(255,255,255,0.18)', border:'none', borderRadius:'50%', width:36, height:36,
              display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer', color:'#fff',
              fontSize:13, fontWeight:700, flexShrink:0 }}
          >{user.initials}</button>
        )}
      </div>
      {profileOpen && <ProfileSheet user={user} onClose={() => setProfileOpen(false)} />}
      {/* Stats row */}
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:10, padding:'14px 14px 4px' }}>
        {[
          { label:'Patients', value:s.patients, icon:IndIcon('team') },
          { label:"Today's Appts", value:s.todayAppts, icon:IndIcon('cal') },
          { label:'Critical', value:s.critical, icon:IndIcon('alert'), alert:true },
        ].map(stat => (
          <div key={stat.label} style={{ background:'#fff', borderRadius:14, padding:'12px 10px', textAlign:'center',
            boxShadow:'0 1px 8px rgba(0,0,0,0.06)', border: stat.alert ? '1.5px solid #fca5a522' : 'none' }}>
            <div style={{ fontSize:20 }}>{stat.icon}</div>
            <div style={{ fontSize:22, fontWeight:800, color: stat.alert ? '#dc2626' : '#111827', marginTop:2 }}>{stat.value}</div>
            <div style={{ fontSize:10, color:'#9ca3af', marginTop:1 }}>{stat.label}</div>
          </div>
        ))}
      </div>
      {/* Quick actions */}
      <div style={{ padding:'14px 14px 4px' }}>
        <div style={{ fontSize:13, fontWeight:700, color:'#374151', marginBottom:10 }}>Quick Actions</div>
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
          {[
            { label:'New Patient', icon:IndIcon('plus'), tab:'patients' },
            { label:'Schedule', icon:IndIcon('cal'), tab:'schedule' },
            { label:'Tasks', icon:IndIcon('check'), tab:'tasks' },
            { label:'Overview', icon:IndIcon('check'), tab:'overview' },
            { label:'Chat', icon:IndIcon('msg'), tab:'chat' },
          ].map(a => (
            <div key={a.label} onClick={() => go(a.tab)}
              style={{ background:'#fff', borderRadius:14, padding:'14px 14px', cursor:'pointer',
                boxShadow:'0 1px 6px rgba(0,0,0,0.06)', display:'flex', alignItems:'center', gap:10 }}>
              <span style={{ fontSize:22 }}>{a.icon}</span>
              <span style={{ fontWeight:600, fontSize:13.5, color:'#1f2937' }}>{a.label}</span>
            </div>
          ))}
        </div>
      </div>
      {/* Today schedule snippet */}
      <div style={{ padding:'14px 14px 4px' }}>
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:10 }}>
          <div style={{ fontSize:13, fontWeight:700, color:'#374151' }}>Today's Schedule</div>
          <button onClick={() => go('schedule')} style={{ border:'none', background:'none', color:HC_ACCENT, fontSize:12, fontWeight:600, cursor:'pointer', padding:0 }}>See all</button>
        </div>
        {today.map(a => (
          <div key={a.id} style={{ background:'#fff', borderRadius:12, padding:'12px 14px', marginBottom:8,
            display:'flex', alignItems:'center', gap:12, boxShadow:'0 1px 4px rgba(0,0,0,0.05)' }}>
            <div style={{ fontWeight:800, fontSize:15, color:HC_ACCENT, minWidth:40 }}>{a.time}</div>
            <div style={{ flex:1 }}>
              <div style={{ fontWeight:600, fontSize:13, color:'#111827' }}>{a.patient}</div>
              <div style={{ fontSize:11, color:'#6b7280', marginTop:2 }}>{a.type} · {a.room}</div>
            </div>
            <span style={{ fontSize:10.5, fontWeight:700, background:statusBg(a.status), color:statusColor(a.status),
              borderRadius:20, padding:'3px 8px' }}>{a.status}</span>
          </div>
        ))}
      </div>
      {/* My Tasks */}
      {myTasks.length > 0 && (
        <div style={{ padding:'14px 14px 4px' }}>
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:10 }}>
            <div style={{ fontSize:13, fontWeight:700, color:'#374151' }}>Pending Tasks</div>
            <button onClick={() => go('tasks')} style={{ border:'none', background:'none', color:HC_ACCENT, fontSize:12, fontWeight:600, cursor:'pointer', padding:0 }}>See all</button>
          </div>
          {myTasks.map(t => (
            <div key={t.id} style={{ background:'#fff', borderRadius:12, padding:'11px 14px', marginBottom:8,
              display:'flex', alignItems:'center', gap:10, boxShadow:'0 1px 4px rgba(0,0,0,0.05)' }}>
              <div style={{ width:8, height:8, borderRadius:99, background:statusColor(t.priority), flexShrink:0 }} />
              <div style={{ flex:1, fontSize:13, fontWeight:500, color:'#1f2937' }}>{t.title}</div>
              <div style={{ fontSize:10.5, color:'#9ca3af' }}>{t.due}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

/* ── Patients ── */
const HCScreenPatients = ({ role, go }) => {
  const [q, setQ] = React.useState('');
  const list = HC_DATA.patients.filter(p => !q || p.name.toLowerCase().includes(q.toLowerCase()) || p.condition.toLowerCase().includes(q.toLowerCase()));
  return (
    <div style={{ flex:1, display:'flex', flexDirection:'column', overflow:'hidden' }}>
      <div style={{ padding:'14px 16px 10px', background:'#fff', borderBottom:'1px solid #f3f4f6' }}>
        <div style={{ fontSize:18, fontWeight:800, color:'#111827', marginBottom:10 }}>Patients</div>
        <div style={{ position:'relative' }}>
          <span style={{ position:'absolute', left:12, top:'50%', transform:'translateY(-50%)', color:'#9ca3af', fontSize:14 }}>{IndIcon('search')}</span>
          <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search patients..."
            style={{ width:'100%', background:'#f3f4f6', border:'none', borderRadius:10, padding:'9px 12px 9px 34px',
              fontSize:13.5, color:'#111827', outline:'none', boxSizing:'border-box' }} />
        </div>
      </div>
      <div style={{ flex:1, overflow:'auto', padding:'10px 14px 20px' }}>
        {list.map(p => (
          <div key={p.id} style={{ background:'#fff', borderRadius:14, padding:'14px 16px', marginBottom:10,
            boxShadow:'0 1px 6px rgba(0,0,0,0.05)', border:`1.5px solid ${statusBg(p.status)}` }}>
            <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between' }}>
              <div>
                <div style={{ fontWeight:700, fontSize:15, color:'#111827' }}>{p.name}</div>
                <div style={{ fontSize:12, color:'#6b7280', marginTop:2 }}>{p.age} yrs · {p.condition}</div>
                <div style={{ fontSize:11.5, color:'#9ca3af', marginTop:3 }}>Dr: {p.doctor} · Ward: {p.ward}</div>
              </div>
              <span style={{ fontSize:10.5, fontWeight:700, background:statusBg(p.status), color:statusColor(p.status),
                borderRadius:20, padding:'3px 9px', flexShrink:0, marginLeft:8 }}>{p.status}</span>
            </div>
            <div style={{ display:'flex', justifyContent:'space-between', marginTop:10, paddingTop:10, borderTop:'1px solid #f3f4f6' }}>
              <div style={{ fontSize:11.5, color:'#6b7280' }}>Next: <strong style={{ color:'#374151' }}>{p.next}</strong></div>
              <div style={{ display:'flex', gap:8 }}>
                <button style={{ border:`1px solid ${HC_ACCENT}`, borderRadius:8, padding:'4px 12px',
                  fontSize:11.5, color:HC_ACCENT, background:'none', cursor:'pointer', fontWeight:600 }}>View</button>
                <button style={{ border:'none', borderRadius:8, padding:'4px 12px',
                  fontSize:11.5, color:'#fff', background:HC_ACCENT, cursor:'pointer', fontWeight:600 }}>Call</button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

/* ── Schedule ── */
const HCScreenSchedule = ({ role }) => {
  const [view, setView] = React.useState('list');
  const appts = HC_DATA.appointments;
  const slots = ['09:00','09:30','10:00','10:30','11:00','11:30','14:00','15:30'];
  return (
    <div style={{ flex:1, display:'flex', flexDirection:'column', overflow:'hidden' }}>
      <div style={{ padding:'14px 16px 10px', background:'#fff', borderBottom:'1px solid #f3f4f6' }}>
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:10 }}>
          <div style={{ fontSize:18, fontWeight:800, color:'#111827' }}>Schedule</div>
          <div style={{ display:'flex', background:'#f3f4f6', borderRadius:10, padding:2 }}>
            {['list','grid'].map(v => (
              <button key={v} onClick={() => setView(v)}
                style={{ border:'none', borderRadius:8, padding:'5px 12px', fontSize:12, fontWeight:600, cursor:'pointer',
                  background: view===v ? '#fff' : 'transparent', color: view===v ? '#111827' : '#9ca3af',
                  boxShadow: view===v ? '0 1px 4px rgba(0,0,0,0.1)' : 'none' }}>
                {v === 'list' ? '≡ List' : '▦ Grid'}
              </button>
            ))}
          </div>
        </div>
        <div style={{ fontSize:12, color:'#6b7280' }}>Today — {new Date().toLocaleDateString('en-IN',{weekday:'long',day:'numeric',month:'long'})}</div>
      </div>
      <div style={{ flex:1, overflow:'auto', padding:'12px 14px 24px' }}>
        {view === 'list' ? appts.map(a => (
          <div key={a.id} style={{ background:'#fff', borderRadius:14, padding:'12px 16px', marginBottom:10,
            display:'flex', alignItems:'center', gap:12, boxShadow:'0 1px 6px rgba(0,0,0,0.05)',
            borderLeft:`4px solid ${statusColor(a.status)}` }}>
            <div style={{ textAlign:'center', minWidth:42 }}>
              <div style={{ fontWeight:800, fontSize:14, color:HC_ACCENT }}>{a.time.split(':')[0]}</div>
              <div style={{ fontSize:11, color:'#9ca3af' }}>:{a.time.split(':')[1]}</div>
            </div>
            <div style={{ flex:1 }}>
              <div style={{ fontWeight:700, fontSize:14, color:'#111827' }}>{a.patient}</div>
              <div style={{ fontSize:11.5, color:'#6b7280', marginTop:2 }}>{a.type} · {a.room}</div>
              <div style={{ fontSize:11, color:'#9ca3af', marginTop:1 }}>{a.doctor}</div>
            </div>
            <span style={{ fontSize:10.5, fontWeight:700, background:statusBg(a.status), color:statusColor(a.status),
              borderRadius:20, padding:'3px 9px' }}>{a.status}</span>
          </div>
        )) : (
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8 }}>
            {appts.map(a => (
              <div key={a.id} style={{ background:'#fff', borderRadius:12, padding:'12px 12px',
                boxShadow:'0 1px 6px rgba(0,0,0,0.05)', borderTop:`3px solid ${statusColor(a.status)}` }}>
                <div style={{ fontWeight:800, fontSize:13, color:HC_ACCENT }}>{a.time}</div>
                <div style={{ fontWeight:700, fontSize:12.5, color:'#111827', marginTop:4, lineHeight:1.3 }}>{a.patient}</div>
                <div style={{ fontSize:11, color:'#6b7280', marginTop:3 }}>{a.type}</div>
                <div style={{ fontSize:10.5, color:'#9ca3af', marginTop:2 }}>{a.room}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

/* ── Tasks ── (HC uses shared IND_TASK_STORE + IndTaskDetail from industry-kit) */
const HC_ROLES = ['Front Desk', 'Doctor', 'Nurse', 'Manager', 'Admin'];

const HCScreenTasks = ({ role }) => {
  const [sel, setSel]         = React.useState(null);
  const [showAdd, setShowAdd] = React.useState(false);
  const [view, setView]       = React.useState('mine');
  const [, force] = React.useReducer(x => x + 1, 0);
  React.useEffect(() => {
    const fn = (e) => { if (e.detail.ns === 'hc') force(); };
    document.addEventListener('sr:ind-task-updated', fn);
    document.addEventListener('sr:nudge', fn);
    return () => {
      document.removeEventListener('sr:ind-task-updated', fn);
      document.removeEventListener('sr:nudge', fn);
    };
  }, []);
  const isManager = role === 'Manager' || role === 'Admin';
  // Normalise user-created tasks (which use `role`) to HC's `for` field.
  const created = window.listIndCreated('hc').map(t => Object.assign({}, t, { for: t.for || t.role }));
  const base = HC_DATA.tasks.concat(created);
  const live = base.map(t => window.indTaskGet('hc', t));
  const visible = (isManager && view === 'team') ? live : live.filter(t => !t.for || t.for === role || (isManager && view === 'mine' && t.for === 'Manager'));
  const isDone = (t) => !!t.done;
  const onToggle = (e, id) => { e.stopPropagation(); window.indTaskToggle('hc', id); };
  const onNudge = (e, t) => {
    e.stopPropagation();
    const msg = window.prompt(`Nudge ${t.for || 'team'} about:\n"${t.title}"\n\nMessage:`, "Please prioritise this today.");
    if (!msg) return;
    window.sendNudge('hc', t.for || 'Nurse', t.id, msg, 'Manager');
    alert(`Nudge sent to ${t.for || 'team'}.`);
  };
  if (sel) return <window.IndTaskDetail task={sel} accent={HC_ACCENT} ns="hc" onBack={() => setSel(null)} />;

  const byRole = {};
  if (isManager && view === 'team') {
    visible.forEach(t => { const k = t.for || 'Unassigned'; (byRole[k] = byRole[k] || []).push(t); });
  }
  const counts = { open: visible.filter(t => !isDone(t)).length, done: visible.filter(isDone).length, total: visible.length };

  const renderRow = (t) => (
    <div key={t.id} onClick={() => setSel(t)}
      style={{ background:'#fff', borderRadius:14, padding:'14px 16px', marginBottom:10, cursor:'pointer',
        boxShadow:'0 1px 6px rgba(0,0,0,0.05)', opacity: isDone(t) ? 0.55 : 1,
        display:'flex', alignItems:'flex-start', gap:12 }}>
      <div onClick={(e) => onToggle(e, t.id)} style={{ width:22, height:22, borderRadius:6, border:`2px solid ${isDone(t) ? HC_ACCENT : '#d1d5db'}`,
        background: isDone(t) ? HC_ACCENT : '#fff', display:'flex', alignItems:'center', justifyContent:'center',
        flexShrink:0, marginTop:1, cursor:'pointer' }}>
        {isDone(t) && <span style={{ color:'#fff', display:'flex' }}>{IndIcon('check',{c:'#fff',size:12,sw:3})}</span>}
      </div>
      <div style={{ flex:1, minWidth:0 }}>
        <div style={{ fontWeight:600, fontSize:13.5, color:'#111827', textDecoration: isDone(t) ? 'line-through' : 'none' }}>{t.title}</div>
        <div style={{ display:'flex', gap:8, marginTop:6, flexWrap:'wrap', alignItems:'center' }}>
          <span style={{ fontSize:10.5, fontWeight:700, background:statusBg(t.priority), color:statusColor(t.priority), borderRadius:20, padding:'2px 8px' }}>{t.priority}</span>
          <span style={{ fontSize:10.5, color:'#9ca3af' }}>Due: {t.due}</span>
          {t.for && <span style={{ fontSize:10.5, color:'#9ca3af' }}>→ {t.for}</span>}
          {t._created && <span style={{ fontSize:10, color:'#16a34a', fontWeight:700, background:'#dcfce7', borderRadius:10, padding:'1px 7px' }}>New</span>}
        </div>
      </div>
      {isManager && !isDone(t) && (
        <button onClick={(e) => onNudge(e, t)} style={{ flexShrink:0, border:`1px solid ${HC_ACCENT}`, background:'#fff', color:HC_ACCENT, borderRadius:8, padding:'5px 10px', fontSize:11, fontWeight:700, cursor:'pointer', fontFamily:'inherit', display:'flex', alignItems:'center', gap:4, alignSelf:'center' }}>
          {IndIcon('bell',{c:HC_ACCENT,size:11})} Nudge
        </button>
      )}
    </div>
  );

  return (
    <div style={{ flex:1, display:'flex', flexDirection:'column', overflow:'hidden', position:'relative' }}>
      <div style={{ padding:'14px 16px 10px', background:'#fff', borderBottom:'1px solid #f3f4f6' }}>
        <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', gap:8 }}>
          <div>
            <div style={{ fontSize:18, fontWeight:800, color:'#111827' }}>Tasks</div>
            <div style={{ fontSize:12, color:'#6b7280', marginTop:3 }}>{counts.done}/{counts.total} completed{isManager && view==='team' ? ' · team view' : ''}</div>
          </div>
          <button onClick={() => setShowAdd(true)} style={{ width:36, height:36, borderRadius:'50%', border:'none', background:HC_ACCENT, color:'#fff', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', boxShadow:'0 2px 8px '+HC_ACCENT+'66', fontFamily:'inherit' }} title="New task">
            {IndIcon('plus',{c:'#fff',size:18,sw:2.4})}
          </button>
        </div>
        {isManager && (
          <div style={{ display:'flex', gap:6, marginTop:10 }}>
            <button onClick={() => setView('mine')} style={{ border:'none', borderRadius:99, padding:'5px 12px', fontSize:11.5, fontWeight:700, cursor:'pointer', fontFamily:'inherit', background: view==='mine' ? HC_ACCENT : '#f3f4f6', color: view==='mine' ? '#fff' : '#6b7280' }}>My Tasks</button>
            <button onClick={() => setView('team')} style={{ border:'none', borderRadius:99, padding:'5px 12px', fontSize:11.5, fontWeight:700, cursor:'pointer', fontFamily:'inherit', background: view==='team' ? HC_ACCENT : '#f3f4f6', color: view==='team' ? '#fff' : '#6b7280' }}>Team Pending</button>
          </div>
        )}
      </div>

      <window.IndNudgeBanner ns="hc" role={role} accent={HC_ACCENT} />

      <div style={{ flex:1, overflow:'auto', padding:'12px 14px 24px' }}>
        {(isManager && view === 'team') ? (
          Object.entries(byRole).map(([r, items]) => (
            <div key={r} style={{ marginBottom:14 }}>
              <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'0 2px 6px' }}>
                <div style={{ fontSize:11, fontWeight:800, color:'#6b7280', letterSpacing:'0.1em', textTransform:'uppercase' }}>{r}</div>
                <div style={{ fontSize:11, color:'#9ca3af' }}>{items.filter(t=>!isDone(t)).length} pending · {items.length} total</div>
              </div>
              {items.map(renderRow)}
            </div>
          ))
        ) : (
          visible.map(renderRow)
        )}
        {visible.length === 0 && (
          <div style={{ textAlign:'center', padding:'40px 20px', color:'#9ca3af' }}>
            <div style={{ display:'inline-flex', width:54, height:54, borderRadius:99, background:'#f3f4f6', alignItems:'center', justifyContent:'center', marginBottom:10 }}>
              {IndIcon('check',{c:'#9ca3af',size:24})}
            </div>
            <div style={{ fontSize:13, fontWeight:600 }}>No tasks yet</div>
            <div style={{ fontSize:11.5, marginTop:4 }}>Tap + to add one</div>
          </div>
        )}
      </div>

      {showAdd && (
        <window.IndTaskCreate
          ns="hc" accent={HC_ACCENT}
          roles={HC_ROLES} defaultRole={isManager ? 'Nurse' : role}
          onClose={() => setShowAdd(false)}
          onCreate={(t) => { if (t._notify && t.role && t.role !== role) { window.sendNudge('hc', t.role, t.id, `New task assigned: ${t.title}`, role || 'Manager'); } }}
        />
      )}
    </div>
  );
};

/* ── Chat ── */
const HCScreenChat = ({ role }) => {
  const scrollRef = React.useRef();
  const [activeThread, setActiveThread] = React.useState('team');
  const [notifOpen, setNotifOpen]       = React.useState(false);
  // Seed team thread once
  React.useEffect(() => {
    const threads = window.getChatThreads('hc');
    if (!threads['team'].messages || threads['team'].messages.length === 0) {
      threads['team'].messages = HC_DATA.chat.map(c => ({ ...c, tags:[], cards:[] }));
    }
  }, []);
  const [, forceUpdate] = React.useReducer(x => x+1, 0);
  React.useEffect(() => {
    const h = () => forceUpdate();
    document.addEventListener('sr:threads-updated', h);
    return () => document.removeEventListener('sr:threads-updated', h);
  }, []);
  const currentMsgs = (window.getChatThreads('hc')[activeThread] || {}).messages || [];
  // Live team from SRAPI.fetchTeam(), fallback to role list
  const [hcTeamList, setHcTeamList] = React.useState(window.SR_TEAM || null);
  React.useEffect(() => {
    const h = (e) => setHcTeamList(e.detail.members);
    document.addEventListener('sr:team-loaded', h);
    return () => document.removeEventListener('sr:team-loaded', h);
  }, []);
  const HC_DEMO_TASKS = [
    { id:'hct1', name:'ICU Bed 4 — vitals check', meta:'Dr. Rao · Urgent', assignee:'Dr. Rao' },
    { id:'hct2', name:'Schedule echo for Arjun Mehta', meta:'Unassigned · Pending', assignee:'' },
    { id:'hct3', name:'OPD-3 patient rescheduling', meta:'Admin · Today', assignee:'Admin' },
    { id:'hct4', name:'Lab reports — Rakesh Verma', meta:'Lab · Ready', assignee:'Lab' },
    { id:'hct5', name:'Fire drill staff briefing', meta:'Admin · 4 PM', assignee:'Admin' },
  ];
  const HC_TEAM = HC_ROLES.map((r,i) => ({ id:'hc'+i, name:r, meta:'Healthcare' }));
  const liveHCTeam = hcTeamList && hcTeamList.length > 0 ? hcTeamList : HC_TEAM;
  const HC_NAMES = HC_ROLES;
  const allTasks = [...HC_DEMO_TASKS, ...(window.listIndCreated ? window.listIndCreated('hc') : []).map(t => ({ id:t.id, name:t.title||t.name, meta:t.due||t.meta||'', assignee:t.assignee||'' })), ...(window.PF_DATA && window.PF_DATA.tasks ? window.PF_DATA.tasks.map(t => ({ id:t.id, name:t.title, meta:(t.due||'TBD')+' · '+(t.priority||''), assignee:t.assignee||'' })) : [])];
  const isManager = role === 'Manager' || role === 'Head' || role === 'Admin';
  const dmPerson  = activeThread !== 'team' ? (window.getChatThreads('hc')[activeThread]||{}).name : null;
  const livePatients = window.PF_DATA && window.PF_DATA.leads && window.PF_DATA.leads.length
    ? window.PF_DATA.leads.map(l => ({ id:l.id, name:l.name, meta:(l.stage||'OPD')+' · '+(l.source||'') }))
    : HC_DATA.patients.map(p => ({ id:p.id, name:p.name, meta:p.ward+' · '+p.status }));
  const ds = {
    _ns:      'hc',
    client:   livePatients,
    task:     dmPerson && !isManager ? allTasks.filter(t => t.assignee === dmPerson) : allTasks,
    employee: liveHCTeam,
    nudge:    liveHCTeam,
  };
  const send = (text, tags = [], cards = []) => {
    if (!text && tags.length === 0 && cards.length === 0) return;
    window.addThreadMessage('hc', activeThread, { id:`u${Date.now()}`, from:'You', avatar:'ME', me:true, msg:text, tags, cards, time:'Now', unread:false });
    setTimeout(() => scrollRef.current && (scrollRef.current.scrollTop = scrollRef.current.scrollHeight), 60);
  };
  return (
    <div style={{ flex:1, display:'flex', flexDirection:'column', overflow:'hidden', position:'relative' }}>
      <div style={{ padding:'12px 16px 10px', background:'#fff', borderBottom:'1px solid #f3f4f6', display:'flex', alignItems:'center', gap:8 }}>
        <div style={{ flex:1 }}>
          <div style={{ fontSize:18, fontWeight:800, color:'#111827' }}>Team Chat</div>
          <div style={{ fontSize:12, color:'#6b7280', marginTop:1 }}>City Care Hospital · Ward Group</div>
        </div>
        {window.NotifBell && <window.NotifBell ns="hc" user={role||'Nurse'} accent={HC_ACCENT} onClick={() => setNotifOpen(true)} />}
      </div>
      {window.ChatThreadSwitcher && <window.ChatThreadSwitcher ns="hc" accent={HC_ACCENT} activeThread={activeThread} onSwitch={setActiveThread} role={role} teamNames={HC_NAMES} />}
      <div ref={scrollRef} style={{ flex:1, overflow:'auto', padding:'14px 14px 8px' }}>
        {currentMsgs.map(c => <window.ChatBubble key={c.id} msg={c} accent={HC_ACCENT} />)}
        {currentMsgs.length === 0 && <div style={{ padding:'32px 0', textAlign:'center', color:'#9ca3af', fontSize:13 }}>Start your conversation</div>}
      </div>
      <window.ChatComposer accent={HC_ACCENT} placeholder="Message team… (type / to tag)" onSend={send} dataSources={ds}
        commands={window.HC_CMDS && window.HC_CMDS[role] ? window.HC_CMDS[role] : undefined} role={role} />
      {notifOpen && window.NotifPanel && <window.NotifPanel ns="hc" user={role||'Nurse'} accent={HC_ACCENT} onClose={() => setNotifOpen(false)} />}
    </div>
  );
};

/* ── Tab bar ── */
const HCTabBar = ({ active, onChange }) => (
  <div style={{ display:'flex', background:'#fff', borderTop:'1px solid #e5e7eb', paddingBottom:6, flexShrink:0 }}>
    {[
      { id:'home',     icon:IndIcon('home'), label:'Home'    },
      { id:'patients', icon:IndIcon('team'), label:'Patients'},
      { id:'schedule', icon:IndIcon('cal'), label:'Schedule'},
      { id:'tasks',    icon:IndIcon('check'), label:'Tasks'   },
      { id:'chat',     icon:IndIcon('msg'), label:'Chat'    },
    ].map(t => (
      <div key={t.id} onClick={() => onChange(t.id)}
        style={{ flex:1, display:'flex', flexDirection:'column', alignItems:'center', padding:'8px 0 2px', cursor:'pointer',
          color: active===t.id ? HC_ACCENT : '#9ca3af' }}>
        <span style={{ fontSize:21 }}>{t.icon}</span>
        <span style={{ fontSize:10, fontWeight: active===t.id ? 700 : 400, marginTop:2 }}>{t.label}</span>
        {active===t.id && <div style={{ width:16, height:2.5, background:HC_ACCENT, borderRadius:2, marginTop:3 }} />}
      </div>
    ))}
  </div>
);

// Maps a URL path (each native tab's fixed WebView path — see RootTabs.tsx)
// to the matching screen. Same pattern as PATH_TAB_MAP in 25-pf-app.jsx.
const HC_PATH_TAB_MAP = { '/': 'home', '/patients': 'patients', '/schedule': 'schedule', '/tasks': 'tasks', '/chat': 'chat' };
function hcInitialTab() {
  const path = (typeof window !== 'undefined' && window.location && window.location.pathname) || '/';
  return HC_PATH_TAB_MAP[path] || 'home';
}

/* ── App ── */
const HCApp = ({ role, platform, onBack, user }) => {
  const [tab, setTab] = React.useState(hcInitialTab);
  // Was a hardcoded 54px guess regardless of context — wrong both inside
  // the native wrapper (double-pads on top of the real device's own inset)
  // and in a plain mobile browser tab (no safe-area inset to simulate
  // there either). env(safe-area-inset-top) resolves to the real inset
  // when one exists and 0 otherwise, same technique already used in the
  // realty app (25-pf-app.jsx).
  const topPad = platform === 'ios' ? 'env(safe-area-inset-top)' : 8;
  let Screen;
  switch (tab) {
    case 'patients': Screen = <HCScreenPatients role={role} go={setTab} />; break;
    case 'schedule': Screen = <HCScreenSchedule role={role} />; break;
    case 'tasks':    Screen = <HCScreenTasks role={role} />; break;
    case 'overview': Screen = window.TaskOverview ? <window.TaskOverview ns="hc" accent={HC_ACCENT} role={role} onBack={() => setTab('home')} teamNames={HC_ROLES} /> : null; break;
    case 'chat':     Screen = <HCScreenChat role={role} />; break;
    default:         Screen = <HCScreenHome role={role} go={setTab} user={user} />;
  }
  return (
    <div style={{ height:'100%', display:'flex', flexDirection:'column', background:'#f4f6f8', paddingTop: topPad , fontFamily: window.IND_FONT}}>
      <div style={{ flex:1, minHeight:0, display:'flex', flexDirection:'column', overflow:'hidden', position:'relative' }}>
        {Screen}
      </div>
      {/* Native wrapper now has matching tabs for all 5 screens (see
          RootTabs.tsx) with path-based routing above, so it's safe to hide
          this in-page bar there — same treatment realty got. */}
      {!IS_NATIVE_IOS && <HCTabBar active={tab} onChange={setTab} />}
    </div>
  );
};

Object.assign(window, { HCApp });


// ===== 19-pf-dermatology.jsx =====
/* global React */

const DERM_DATA = {
  stats: { clients: 186, todaySessions: 24, treatments: 8, revenue: '₹2.8L', satisfaction: '4.8★' },
  clients: [
    { id:'c1', name:'Priya Sharma',    age:32, concern:'Acne Treatment',      therapist:'Dr. Mehra',   sessions:8,  next:'May 12', status:'Active',    skin:'Combination' },
    { id:'c2', name:'Ananya Gupta',    age:28, concern:'Pigmentation',        therapist:'Ria T.',      sessions:5,  next:'May 14', status:'Active',    skin:'Dry'         },
    { id:'c3', name:'Vikram Singh',    age:45, concern:'Anti-Ageing',         therapist:'Dr. Mehra',   sessions:12, next:'May 15', status:'Ongoing',   skin:'Normal'      },
    { id:'c4', name:'Pooja Nair',      age:35, concern:'Laser Hair Removal',  therapist:'Ria T.',      sessions:3,  next:'May 16', status:'Active',    skin:'Sensitive'   },
    { id:'c5', name:'Karan Mehta',     age:29, concern:'HydraFacial',         therapist:'Sonal B.',    sessions:2,  next:'May 13', status:'New',       skin:'Oily'        },
    { id:'c6', name:'Deepika Patel',   age:40, concern:'Stretch Marks',       therapist:'Dr. Mehra',   sessions:6,  next:'May 17', status:'Active',    skin:'Normal'      },
  ],
  treatments: [
    { id:'t1', time:'09:30', client:'Priya Sharma',  type:'Carbon Peel',          therapist:'Ria T.',    room:'T-1', duration:'60 min', status:'Confirmed' },
    { id:'t2', time:'10:30', client:'Ananya Gupta',  type:'Pigmentation Peel',    therapist:'Ria T.',    room:'T-1', duration:'45 min', status:'Confirmed' },
    { id:'t3', time:'11:00', client:'Vikram Singh',  type:'Botox',                therapist:'Dr. Mehra', room:'T-2', duration:'30 min', status:'Confirmed' },
    { id:'t4', time:'12:00', client:'Walk-in',       type:'Consultation',         therapist:'Dr. Mehra', room:'OPD', duration:'20 min', status:'Waiting'   },
    { id:'t5', time:'14:00', client:'Pooja Nair',    type:'Laser Hair Removal',   therapist:'Sonal B.',  room:'T-3', duration:'45 min', status:'Confirmed' },
    { id:'t6', time:'15:00', client:'Karan Mehta',   type:'HydraFacial',          therapist:'Sonal B.',  room:'T-2', duration:'75 min', status:'Scheduled' },
    { id:'t7', time:'16:00', client:'Deepika Patel', type:'Carboxytherapy',       therapist:'Dr. Mehra', room:'T-2', duration:'60 min', status:'Confirmed' },
  ],
  gallery: [
    { id:'g1', client:'Priya Sharma',  treatment:'Acne Treatment', sessions:8,  before:IndIcon('face'), after:IndIcon('face'), improvement:'85%', date:'May 2026' },
    { id:'g2', client:'Ananya Gupta',  treatment:'Pigmentation',   sessions:5,  before:IndIcon('face'), after:IndIcon('face'), improvement:'60%', date:'Apr 2026' },
    { id:'g3', client:'Vikram Singh',  treatment:'Anti-Ageing',    sessions:12, before:IndIcon('face'), after:IndIcon('face'), improvement:'78%', date:'Mar 2026' },
    { id:'g4', client:'Pooja Nair',    treatment:'Laser Hair',     sessions:3,  before:IndIcon('face'), after:IndIcon('face'), improvement:'90%', date:'May 2026' },
  ],
  chat: [
    { id:'m1', from:'Dr. Mehra',   avatar:'DM', msg:"New carbon peel protocol ready — check T-2 setup.", time:'9:05 AM', unread:true  },
    { id:'m2', from:'Ria (Thera)', avatar:'RT', msg:'Priya Sharma patch test was clear, good to proceed.', time:'9:20 AM', unread:true  },
    { id:'m3', from:'Front Desk',  avatar:'FD', msg:'Walk-in at 12:00 — consultation added to Dr. Mehra.',  time:'11:50 AM',unread:false },
    { id:'m4', from:'Manager',     avatar:'MG', msg:'Monthly review meeting tomorrow at 10 AM.',             time:'10:30 AM',unread:false },
  ],
  tasks: [
    { id:'tk1', title:'Prepare T-1 for carbon peel',           role:'Therapist',      due:'09:15',    priority:'High',   done:false },
    { id:'tk2', title:'Topical anaesthetic — Pooja Nair',      role:'Therapist',      due:'13:30',    priority:'High',   done:false },
    { id:'tk3', title:'Upload before-after — Karan Mehta',     role:'Therapist',      due:'Today',    priority:'Medium', done:false },
    { id:'tk4', title:'Review treatment plan — Vikram Singh',  role:'Dermatologist',  due:'10:45',    priority:'High',   done:false },
    { id:'tk5', title:'Sign-off on Botox dosage',              role:'Dermatologist',  due:'10:50',    priority:'Critical',done:false},
    { id:'tk6', title:'Confirm appointments for tomorrow',     role:'Front Desk',     due:'Today',    priority:'Medium', done:false },
    { id:'tk7', title:'Restock hyaluronic acid serum',         role:'Front Desk',     due:'Today',    priority:'Low',    done:false },
    { id:'tk8', title:'Staff appraisal reports',               role:'Manager',        due:'May 15',   priority:'Medium', done:false },
  ],
};

const DERM_ACCENT = '#6e5191';

const dStatusColor = s => ({
  Active:'#16a34a', Ongoing:'#2a5d7f', New:'#6e5191', Confirmed:'#16a34a',
  Waiting:'#ca8a04', Scheduled:'#6b7280', High:'#ca8a04', Critical:'#dc2626', Medium:'#2a5d7f', Low:'#9ca3af'
}[s] || '#6b7280');
const dStatusBg = s => dStatusColor(s) + '18';

/* ── Home ── */
const DermScreenHome = ({ role, go, user }) => {
  const s = DERM_DATA.stats;
  const today = DERM_DATA.treatments.slice(0, 4);
  const [profileOpen, setProfileOpen] = React.useState(false);
  return (
    <div style={{ flex:1, overflow:'auto', paddingBottom:20 }}>
      <div style={{ background:'linear-gradient(135deg, #6e5191, #9d5ff5)', padding:'16px 20px 22px', color:'#fff', display:'flex', alignItems:'flex-start', justifyContent:'space-between' }}>
        <div>
          <div style={{ fontSize:12, opacity:.8, marginBottom:4 }}>Good morning · {role}</div>
          <div style={{ fontSize:22, fontWeight:800 }}>Glow Derma</div>
          <div style={{ fontSize:12, opacity:.75, marginTop:2 }}>Studio · Today, {new Date().toLocaleDateString('en-IN',{weekday:'short',day:'numeric',month:'short'})}</div>
        </div>
        {user && (
          <button
            onClick={() => {
              if (IS_NATIVE_IOS && window.ReactNativeWebView) {
                window.ReactNativeWebView.postMessage(JSON.stringify({ type: 'OPEN_MORE' }));
              } else {
                setProfileOpen(true);
              }
            }}
            style={{ background:'rgba(255,255,255,0.18)', border:'none', borderRadius:'50%', width:36, height:36,
              display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer', color:'#fff',
              fontSize:13, fontWeight:700, flexShrink:0 }}
          >{user.initials}</button>
        )}
      </div>
      {profileOpen && <ProfileSheet user={user} onClose={() => setProfileOpen(false)} />}
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:10, padding:'14px 14px 4px' }}>
        {[
          { label:'Clients', value:s.clients, icon:IndIcon('team',{size:22,c:DERM_ACCENT}) },
          { label:"Sessions", value:s.todaySessions, icon:IndIcon('cal',{size:22,c:DERM_ACCENT}) },
          { label:'Rating', value:s.satisfaction, icon:IndIcon('star',{size:22,c:DERM_ACCENT}) },
        ].map(stat => (
          <div key={stat.label} style={{ background:'#fff', borderRadius:14, padding:'12px 10px', textAlign:'center', boxShadow:'0 1px 8px rgba(0,0,0,0.06)' }}>
            <div style={{ fontSize:20 }}>{stat.icon}</div>
            <div style={{ fontSize:18, fontWeight:800, color:'#111827', marginTop:2 }}>{stat.value}</div>
            <div style={{ fontSize:10, color:'#9ca3af', marginTop:1 }}>{stat.label}</div>
          </div>
        ))}
      </div>
      <div style={{ padding:'14px 14px 4px' }}>
        <div style={{ fontSize:13, fontWeight:700, color:'#374151', marginBottom:10 }}>Quick Actions</div>
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
          {[
            { label:"Today's Sessions", icon:IndIcon('cal',{size:22,c:DERM_ACCENT}), tab:'treatments' },
            { label:'Clients', icon:IndIcon('person',{size:22,c:DERM_ACCENT}), tab:'clients' },
            { label:'Gallery', icon:IndIcon('image',{size:22,c:DERM_ACCENT}), tab:'gallery' },
            { label:'Overview', icon:IndIcon('check',{size:22,c:DERM_ACCENT}), tab:'overview' },
            { label:'Chat', icon:IndIcon('msg',{size:22,c:DERM_ACCENT}), tab:'chat' },
          ].map(a => (
            <div key={a.label} onClick={() => go(a.tab)}
              style={{ background:'#fff', borderRadius:14, padding:'14px 14px', cursor:'pointer', boxShadow:'0 1px 6px rgba(0,0,0,0.06)', display:'flex', alignItems:'center', gap:10 }}>
              <span style={{ fontSize:22 }}>{a.icon}</span>
              <span style={{ fontWeight:600, fontSize:13, color:'#1f2937' }}>{a.label}</span>
            </div>
          ))}
        </div>
      </div>
      <div style={{ padding:'14px 14px 4px' }}>
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:10 }}>
          <div style={{ fontSize:13, fontWeight:700, color:'#374151' }}>Today's Sessions</div>
          <button onClick={() => go('treatments')} style={{ border:'none', background:'none', color:DERM_ACCENT, fontSize:12, fontWeight:600, cursor:'pointer', padding:0 }}>See all</button>
        </div>
        {today.map(t => (
          <div key={t.id} style={{ background:'#fff', borderRadius:12, padding:'12px 14px', marginBottom:8,
            display:'flex', alignItems:'center', gap:12, boxShadow:'0 1px 4px rgba(0,0,0,0.05)' }}>
            <div style={{ fontWeight:800, fontSize:14, color:DERM_ACCENT, minWidth:42 }}>{t.time}</div>
            <div style={{ flex:1 }}>
              <div style={{ fontWeight:600, fontSize:13, color:'#111827' }}>{t.client}</div>
              <div style={{ fontSize:11, color:'#6b7280', marginTop:2 }}>{t.type} · {t.room}</div>
            </div>
            <span style={{ fontSize:10.5, fontWeight:700, background:dStatusBg(t.status), color:dStatusColor(t.status), borderRadius:20, padding:'3px 8px' }}>{t.status}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

/* ── Clients ── */
/* ── Skin-tone gradient placeholder for client/before-after images ── */
const dInitials = (n) => n.split(' ').map(s => s[0]).slice(0,2).join('').toUpperCase();
const dHue = (id) => {
  let h = 0; for (let i=0;i<id.length;i++) h = (h*31 + id.charCodeAt(i)) % 360;
  return h;
};
const DermPhoto = ({ id, name, size=56, rounded=99 }) => {
  const h = dHue(id || name || 'x');
  return (
    <div style={{
      width:size, height:size, borderRadius:rounded, flexShrink:0,
      background: `linear-gradient(135deg, hsl(${h}, 22%, 78%) 0%, hsl(${(h+30)%360}, 18%, 62%) 100%)`,
      display:'flex', alignItems:'center', justifyContent:'center',
      color:'#fff', fontWeight:700, fontSize: size*0.32, letterSpacing:'-0.02em',
      boxShadow:'inset 0 1px 4px rgba(0,0,0,0.08)',
    }}>{dInitials(name || '?')}</div>
  );
};

/* ── Client detail (sub-flow) ── */
const DermClientDetail = ({ client, onBack }) => {
  const history = (DERM_DATA.treatments || []).filter(t => t.client === client.name);
  const past = [
    { date:'Apr 28', type:'Carbon Peel',     therapist:'Ria T.',    notes:'Mild redness, resolved 24h.' },
    { date:'Apr 14', type:'Consultation',    therapist:'Dr. Mehra', notes:'Started 8-session course.'   },
    { date:'Mar 30', type:'Patch Test',      therapist:'Ria T.',    notes:'Clear.'                       },
  ];
  return (
    <div style={{ flex:1, display:'flex', flexDirection:'column', overflow:'hidden', background:'#f7f4f8' }}>
      <div style={{ padding:'12px 14px 10px', background:'#fff', borderBottom:'1px solid #f3f4f6', display:'flex', alignItems:'center', gap:10 }}>
        <button onClick={onBack} style={{ background:'none', border:'none', cursor:'pointer', padding:4, color:DERM_ACCENT, display:'flex' }}>
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M15 6l-6 6 6 6"/></svg>
        </button>
        <div style={{ fontSize:15, fontWeight:800, color:'#111827' }}>Client Profile</div>
      </div>
      <div style={{ flex:1, overflow:'auto', padding:'14px 14px 24px' }}>
        {/* identity card */}
        <div style={{ background:'#fff', borderRadius:16, padding:'18px 16px', boxShadow:'0 1px 6px rgba(0,0,0,0.05)', display:'flex', gap:14, alignItems:'center' }}>
          <DermPhoto id={client.id} name={client.name} size={66} />
          <div style={{ flex:1, minWidth:0 }}>
            <div style={{ fontWeight:800, fontSize:17, color:'#111827' }}>{client.name}</div>
            <div style={{ fontSize:12.5, color:'#6b7280', marginTop:3 }}>{client.age} yrs · {client.skin} skin</div>
            <div style={{ display:'flex', gap:6, marginTop:8 }}>
              <span style={{ fontSize:10.5, fontWeight:700, background:dStatusBg(client.status), color:dStatusColor(client.status), borderRadius:20, padding:'3px 10px' }}>{client.status}</span>
              <span style={{ fontSize:10.5, fontWeight:600, color:'#6b7280', background:'#f3f4f6', borderRadius:20, padding:'3px 10px' }}>{client.sessions} sessions</span>
            </div>
          </div>
        </div>

        {/* concern + therapist */}
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10, marginTop:12 }}>
          <div style={{ background:'#fff', borderRadius:12, padding:'12px 14px', boxShadow:'0 1px 4px rgba(0,0,0,0.04)' }}>
            <div style={{ fontSize:10.5, fontWeight:700, color:'#9ca3af', letterSpacing:'0.08em', textTransform:'uppercase' }}>Primary Concern</div>
            <div style={{ fontSize:13.5, fontWeight:700, color:'#111827', marginTop:4 }}>{client.concern}</div>
          </div>
          <div style={{ background:'#fff', borderRadius:12, padding:'12px 14px', boxShadow:'0 1px 4px rgba(0,0,0,0.04)' }}>
            <div style={{ fontSize:10.5, fontWeight:700, color:'#9ca3af', letterSpacing:'0.08em', textTransform:'uppercase' }}>Therapist</div>
            <div style={{ fontSize:13.5, fontWeight:700, color:'#111827', marginTop:4 }}>{client.therapist}</div>
          </div>
        </div>

        {/* upcoming */}
        <div style={{ background:'#fff', borderRadius:12, padding:'14px 14px', marginTop:12, boxShadow:'0 1px 4px rgba(0,0,0,0.04)', display:'flex', alignItems:'center', gap:12 }}>
          <div style={{ width:36, height:36, borderRadius:10, background:DERM_ACCENT+'18', color:DERM_ACCENT, display:'flex', alignItems:'center', justifyContent:'center' }}>{IndIcon('cal',{c:DERM_ACCENT,size:18})}</div>
          <div style={{ flex:1 }}>
            <div style={{ fontSize:11, fontWeight:700, color:'#9ca3af', letterSpacing:'0.08em', textTransform:'uppercase' }}>Next Appointment</div>
            <div style={{ fontSize:14, fontWeight:700, color:'#111827', marginTop:2 }}>{client.next}</div>
          </div>
          <button style={{ border:`1px solid ${DERM_ACCENT}`, color:DERM_ACCENT, background:'none', borderRadius:8, padding:'6px 12px', fontSize:12, fontWeight:700, cursor:'pointer' }}>Reschedule</button>
        </div>

        {/* today's session */}
        {history.length > 0 && (
          <div style={{ marginTop:18 }}>
            <div style={{ fontSize:11, fontWeight:700, color:'#9ca3af', letterSpacing:'0.08em', textTransform:'uppercase', marginBottom:8, padding:'0 2px' }}>Today's Session</div>
            {history.map(t => (
              <div key={t.id} style={{ background:'#fff', borderRadius:12, padding:'12px 14px', boxShadow:'0 1px 4px rgba(0,0,0,0.04)', borderLeft:`3px solid ${dStatusColor(t.status)}` }}>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                  <div style={{ fontWeight:700, fontSize:13.5, color:'#111827' }}>{t.time} · {t.type}</div>
                  <span style={{ fontSize:10.5, fontWeight:700, background:dStatusBg(t.status), color:dStatusColor(t.status), borderRadius:20, padding:'3px 9px' }}>{t.status}</span>
                </div>
                <div style={{ fontSize:11.5, color:'#6b7280', marginTop:3 }}>{t.room} · {t.duration}</div>
              </div>
            ))}
          </div>
        )}

        {/* treatment history */}
        <div style={{ marginTop:18 }}>
          <div style={{ fontSize:11, fontWeight:700, color:'#9ca3af', letterSpacing:'0.08em', textTransform:'uppercase', marginBottom:8, padding:'0 2px' }}>Treatment History</div>
          {past.map((h,i) => (
            <div key={i} style={{ background:'#fff', borderRadius:12, padding:'12px 14px', marginBottom:8, boxShadow:'0 1px 4px rgba(0,0,0,0.04)' }}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                <div style={{ fontWeight:700, fontSize:13.5, color:'#111827' }}>{h.type}</div>
                <div style={{ fontSize:11.5, color:'#9ca3af' }}>{h.date}</div>
              </div>
              <div style={{ fontSize:11.5, color:'#6b7280', marginTop:3 }}>{h.therapist} — {h.notes}</div>
            </div>
          ))}
        </div>

        {/* actions */}
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10, marginTop:18 }}>
          <button style={{ border:`1px solid ${DERM_ACCENT}`, color:DERM_ACCENT, background:'#fff', borderRadius:10, padding:'11px', fontSize:13, fontWeight:700, cursor:'pointer' }}>Add Note</button>
          <button style={{ border:'none', background:DERM_ACCENT, color:'#fff', borderRadius:10, padding:'11px', fontSize:13, fontWeight:700, cursor:'pointer' }}>Book Session</button>
        </div>
      </div>
    </div>
  );
};

/* ── Clients list (taps into detail) ── */
const DermScreenClients = ({ role }) => {
  const [q, setQ] = React.useState('');
  const [sel, setSel] = React.useState(null);
  const list = DERM_DATA.clients.filter(c => !q || c.name.toLowerCase().includes(q.toLowerCase()) || c.concern.toLowerCase().includes(q.toLowerCase()));
  if (sel) return <DermClientDetail client={sel} onBack={() => setSel(null)} />;
  return (
    <div style={{ flex:1, display:'flex', flexDirection:'column', overflow:'hidden' }}>
      <div style={{ padding:'14px 16px 10px', background:'#fff', borderBottom:'1px solid #f3f4f6' }}>
        <div style={{ fontSize:18, fontWeight:800, color:'#111827', marginBottom:10 }}>Clients</div>
        <div style={{ position:'relative' }}>
          <span style={{ position:'absolute', left:12, top:'50%', transform:'translateY(-50%)', color:'#9ca3af', display:'flex' }}>{IndIcon('search',{size:14,c:'#9ca3af'})}</span>
          <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search clients..."
            style={{ width:'100%', background:'#f3f4f6', border:'none', borderRadius:10, padding:'9px 12px 9px 34px', fontSize:13.5, color:'#111827', outline:'none', boxSizing:'border-box' }} />
        </div>
      </div>
      <div style={{ flex:1, overflow:'auto', padding:'10px 14px 20px' }}>
        {list.map(c => (
          <div key={c.id} onClick={() => setSel(c)} style={{ background:'#fff', borderRadius:14, padding:'14px 16px', marginBottom:10, boxShadow:'0 1px 6px rgba(0,0,0,0.05)', cursor:'pointer', display:'flex', gap:12, alignItems:'flex-start' }}>
            <DermPhoto id={c.id} name={c.name} size={42} />
            <div style={{ flex:1, minWidth:0 }}>
              <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', gap:8 }}>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ fontWeight:700, fontSize:15, color:'#111827' }}>{c.name}</div>
                  <div style={{ fontSize:12, color:'#6b7280', marginTop:2 }}>{c.age} yrs · {c.concern}</div>
                  <div style={{ fontSize:11.5, color:'#9ca3af', marginTop:3 }}>{c.therapist} · {c.sessions} sessions</div>
                </div>
                <span style={{ fontSize:10.5, fontWeight:700, background:dStatusBg(c.status), color:dStatusColor(c.status), borderRadius:20, padding:'3px 9px', flexShrink:0 }}>{c.status}</span>
              </div>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginTop:10, paddingTop:10, borderTop:'1px solid #f3f4f6' }}>
                <div style={{ fontSize:11.5, color:'#6b7280' }}>Next: <strong>{c.next}</strong> · Skin: {c.skin}</div>
                <span style={{ color:DERM_ACCENT, display:'flex' }}>{IndIcon('arrowR',{c:DERM_ACCENT,size:14})}</span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

/* ── Session detail / check-in flow ── */
const DermSessionDetail = ({ session, onBack, onUpdate }) => {
  const [phase, setPhase] = React.useState(
    session.status === 'Confirmed' ? 'ready' :
    session.status === 'Waiting'   ? 'ready' :
    session.status === 'Scheduled' ? 'ready' : 'ready'
  );
  const [notes, setNotes] = React.useState('');
  const [beforeShot, setBeforeShot] = React.useState(false);
  const [afterShot, setAfterShot]   = React.useState(false);
  const [consent, setConsent]       = React.useState(false);
  const [patchTest, setPatchTest]   = React.useState(false);
  const client = (DERM_DATA.clients || []).find(c => c.name === session.client) || { name: session.client, age:'—', concern:'—', skin:'—' };

  const startSession  = () => setPhase('inProgress');
  const finishSession = () => setPhase('done');

  const Row = ({ icon, label, value }) => (
    <div style={{ display:'flex', alignItems:'center', gap:10, padding:'10px 0', borderBottom:'1px solid #f3f4f6' }}>
      <div style={{ width:30, height:30, borderRadius:8, background:DERM_ACCENT+'14', color:DERM_ACCENT, display:'flex', alignItems:'center', justifyContent:'center' }}>{IndIcon(icon,{c:DERM_ACCENT,size:15})}</div>
      <div style={{ flex:1 }}>
        <div style={{ fontSize:10.5, fontWeight:700, color:'#9ca3af', letterSpacing:'0.08em', textTransform:'uppercase' }}>{label}</div>
        <div style={{ fontSize:13.5, fontWeight:600, color:'#111827', marginTop:2 }}>{value}</div>
      </div>
    </div>
  );

  return (
    <div style={{ flex:1, display:'flex', flexDirection:'column', overflow:'hidden', background:'#f7f4f8' }}>
      <div style={{ padding:'12px 14px 10px', background:'#fff', borderBottom:'1px solid #f3f4f6', display:'flex', alignItems:'center', gap:10 }}>
        <button onClick={onBack} style={{ background:'none', border:'none', cursor:'pointer', padding:4, color:DERM_ACCENT, display:'flex' }}>
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M15 6l-6 6 6 6"/></svg>
        </button>
        <div style={{ flex:1 }}>
          <div style={{ fontSize:11, fontWeight:700, color:DERM_ACCENT, letterSpacing:'0.08em', textTransform:'uppercase' }}>{session.time} · {session.duration}</div>
          <div style={{ fontSize:15, fontWeight:800, color:'#111827', marginTop:1 }}>{session.type}</div>
        </div>
        <span style={{ fontSize:10.5, fontWeight:700, background:dStatusBg(session.status), color:dStatusColor(session.status), borderRadius:20, padding:'3px 9px' }}>{phase==='inProgress'?'In Session': phase==='done'?'Completed':session.status}</span>
      </div>

      <div style={{ flex:1, overflow:'auto', padding:'14px 14px 24px' }}>
        {/* client card */}
        <div style={{ background:'#fff', borderRadius:14, padding:'14px 14px', boxShadow:'0 1px 4px rgba(0,0,0,0.05)', display:'flex', gap:12, alignItems:'center' }}>
          <DermPhoto id={client.id||session.id} name={client.name} size={50} />
          <div style={{ flex:1, minWidth:0 }}>
            <div style={{ fontWeight:800, fontSize:15, color:'#111827' }}>{client.name}</div>
            <div style={{ fontSize:11.5, color:'#6b7280', marginTop:2 }}>{client.age} yrs · {client.skin} skin</div>
            <div style={{ fontSize:11, color:'#9ca3af', marginTop:1 }}>{client.concern}</div>
          </div>
        </div>

        {/* meta */}
        <div style={{ background:'#fff', borderRadius:14, padding:'4px 14px', marginTop:12, boxShadow:'0 1px 4px rgba(0,0,0,0.05)' }}>
          <Row icon="person" label="Therapist" value={session.therapist} />
          <Row icon="pin"    label="Room"      value={session.room} />
          <Row icon="clock"  label="Duration"  value={session.duration} />
        </div>

        {/* pre-session checklist */}
        {phase === 'ready' && (
          <div style={{ background:'#fff', borderRadius:14, padding:'14px 16px', marginTop:12, boxShadow:'0 1px 4px rgba(0,0,0,0.05)' }}>
            <div style={{ fontSize:11, fontWeight:700, color:'#9ca3af', letterSpacing:'0.08em', textTransform:'uppercase', marginBottom:10 }}>Pre-Session Checklist</div>
            {[{k:'consent',l:'Consent form signed',v:consent,set:setConsent},
              {k:'patch',  l:'Patch test cleared',  v:patchTest,set:setPatchTest},
              {k:'before', l:'Before photo captured',v:beforeShot,set:setBeforeShot}].map(x => (
              <div key={x.k} onClick={() => x.set(!x.v)} style={{ display:'flex', alignItems:'center', gap:10, padding:'9px 0', cursor:'pointer' }}>
                <div style={{ width:22, height:22, borderRadius:6, border:`2px solid ${x.v ? DERM_ACCENT : '#d1d5db'}`, background: x.v ? DERM_ACCENT : '#fff', display:'flex', alignItems:'center', justifyContent:'center' }}>
                  {x.v && <span style={{ color:'#fff', display:'flex' }}>{IndIcon('check',{c:'#fff',size:12,sw:3})}</span>}
                </div>
                <div style={{ fontSize:13, color:'#111827', flex:1 }}>{x.l}</div>
              </div>
            ))}
          </div>
        )}

        {/* in-progress: notes */}
        {phase === 'inProgress' && (
          <div style={{ background:'#fff', borderRadius:14, padding:'14px 16px', marginTop:12, boxShadow:'0 1px 4px rgba(0,0,0,0.05)' }}>
            <div style={{ fontSize:11, fontWeight:700, color:'#9ca3af', letterSpacing:'0.08em', textTransform:'uppercase', marginBottom:8 }}>Session Notes</div>
            <textarea value={notes} onChange={e => setNotes(e.target.value)} placeholder="Observations, reactions, parameters used…"
              style={{ width:'100%', minHeight:90, background:'#f9fafb', border:'1px solid #e5e7eb', borderRadius:10, padding:'10px 12px', fontSize:13, color:'#111827', outline:'none', resize:'vertical', fontFamily:'inherit', boxSizing:'border-box' }} />
            <div onClick={() => setAfterShot(!afterShot)} style={{ display:'flex', alignItems:'center', gap:10, padding:'12px 0 4px', cursor:'pointer' }}>
              <div style={{ width:22, height:22, borderRadius:6, border:`2px solid ${afterShot ? DERM_ACCENT : '#d1d5db'}`, background: afterShot ? DERM_ACCENT : '#fff', display:'flex', alignItems:'center', justifyContent:'center' }}>
                {afterShot && <span style={{ color:'#fff', display:'flex' }}>{IndIcon('check',{c:'#fff',size:12,sw:3})}</span>}
              </div>
              <div style={{ fontSize:13, color:'#111827' }}>After photo captured</div>
            </div>
          </div>
        )}

        {/* done summary */}
        {phase === 'done' && (
          <div style={{ background:'#fff', borderRadius:14, padding:'18px 16px', marginTop:12, boxShadow:'0 1px 4px rgba(0,0,0,0.05)', textAlign:'center' }}>
            <div style={{ width:54, height:54, borderRadius:99, background:'#dcfce7', display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto 10px' }}>
              <span style={{ color:'#16a34a', display:'flex' }}>{IndIcon('check',{c:'#16a34a',size:30,sw:2.4})}</span>
            </div>
            <div style={{ fontSize:16, fontWeight:800, color:'#111827' }}>Session Complete</div>
            <div style={{ fontSize:12.5, color:'#6b7280', marginTop:4 }}>Notes saved · Photos uploaded to client gallery</div>
          </div>
        )}
      </div>

      {/* footer action */}
      <div style={{ padding:'12px 14px 18px', background:'#fff', borderTop:'1px solid #f3f4f6', display:'flex', gap:8 }}>
        {phase === 'ready' && (
          <>
            <button style={{ flex:1, border:`1px solid ${DERM_ACCENT}`, color:DERM_ACCENT, background:'#fff', borderRadius:10, padding:'12px', fontSize:13, fontWeight:700, cursor:'pointer' }}>Reschedule</button>
            <button onClick={startSession} disabled={!(consent && patchTest && beforeShot)} style={{ flex:2, border:'none', background: (consent&&patchTest&&beforeShot)?DERM_ACCENT:'#d1d5db', color:'#fff', borderRadius:10, padding:'12px', fontSize:13, fontWeight:700, cursor:(consent&&patchTest&&beforeShot)?'pointer':'default' }}>Start Session →</button>
          </>
        )}
        {phase === 'inProgress' && (
          <button onClick={finishSession} style={{ flex:1, border:'none', background:DERM_ACCENT, color:'#fff', borderRadius:10, padding:'13px', fontSize:13, fontWeight:700, cursor:'pointer' }}>End Session</button>
        )}
        {phase === 'done' && (
          <button onClick={onBack} style={{ flex:1, border:'none', background:DERM_ACCENT, color:'#fff', borderRadius:10, padding:'13px', fontSize:13, fontWeight:700, cursor:'pointer' }}>Back to Sessions</button>
        )}
      </div>
    </div>
  );
};

/* ── Treatments (Today's sessions) ── */
const DermScreenTreatments = ({ role }) => {
  const [sel, setSel] = React.useState(null);
  const sessions = DERM_DATA.treatments;
  if (sel) return <DermSessionDetail session={sel} onBack={() => setSel(null)} />;
  const Meta = ({ icon, text }) => (
    <div style={{ display:'flex', alignItems:'center', gap:5, fontSize:11.5, color:'#6b7280', minWidth:0 }}>
      <span style={{ color:'#9ca3af', display:'flex' }}>{IndIcon(icon,{c:'#9ca3af',size:13})}</span>
      <span style={{ overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{text}</span>
    </div>
  );
  return (
    <div style={{ flex:1, display:'flex', flexDirection:'column', overflow:'hidden' }}>
      <div style={{ padding:'14px 16px 10px', background:'#fff', borderBottom:'1px solid #f3f4f6' }}>
        <div style={{ fontSize:18, fontWeight:800, color:'#111827' }}>Today's Sessions</div>
        <div style={{ fontSize:12, color:'#6b7280', marginTop:3 }}>{sessions.length} sessions · {sessions.filter(s => s.status === 'Confirmed').length} confirmed</div>
      </div>
      <div style={{ flex:1, overflow:'auto', padding:'12px 14px 24px' }}>
        {sessions.map(s => (
          <div key={s.id} onClick={() => setSel(s)} style={{ background:'#fff', borderRadius:14, padding:'14px 16px', marginBottom:10,
            boxShadow:'0 1px 6px rgba(0,0,0,0.05)', borderLeft:`4px solid ${dStatusColor(s.status)}`, cursor:'pointer' }}>
            <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', marginBottom:8, gap:8 }}>
              <div style={{ flex:1, minWidth:0 }}>
                <div style={{ fontWeight:800, fontSize:14, color:DERM_ACCENT }}>{s.time}</div>
                <div style={{ fontWeight:700, fontSize:14.5, color:'#111827', marginTop:3 }}>{s.client}</div>
              </div>
              <span style={{ fontSize:10.5, fontWeight:700, background:dStatusBg(s.status), color:dStatusColor(s.status), borderRadius:20, padding:'3px 9px', flexShrink:0 }}>{s.status}</span>
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8 }}>
              <Meta icon="face"   text={s.type} />
              <Meta icon="pin"    text={s.room} />
              <Meta icon="person" text={s.therapist} />
              <Meta icon="clock"  text={s.duration} />
            </div>
            <div style={{ display:'flex', justifyContent:'flex-end', alignItems:'center', gap:6, marginTop:10, paddingTop:10, borderTop:'1px solid #f3f4f6', fontSize:11.5, fontWeight:700, color:DERM_ACCENT }}>
              Open <span style={{ display:'flex' }}>{IndIcon('arrowR',{c:DERM_ACCENT,size:13})}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

/* ── Gallery ── */
const DermScreenGallery = () => (
  <div style={{ flex:1, display:'flex', flexDirection:'column', overflow:'hidden' }}>
    <div style={{ padding:'14px 16px 10px', background:'#fff', borderBottom:'1px solid #f3f4f6' }}>
      <div style={{ fontSize:18, fontWeight:800, color:'#111827' }}>Before &amp; After</div>
      <div style={{ fontSize:12, color:'#6b7280', marginTop:3 }}>{DERM_DATA.gallery.length} transformation stories</div>
    </div>
    <div style={{ flex:1, overflow:'auto', padding:'14px 14px 24px' }}>
      {DERM_DATA.gallery.map(g => (
        <div key={g.id} style={{ background:'#fff', borderRadius:16, padding:'16px', marginBottom:14, boxShadow:'0 1px 8px rgba(0,0,0,0.07)' }}>
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:12 }}>
            <div>
              <div style={{ fontWeight:700, fontSize:14.5, color:'#111827' }}>{g.client}</div>
              <div style={{ fontSize:12, color:'#6b7280', marginTop:2 }}>{g.treatment} · {g.sessions} sessions</div>
            </div>
            <div style={{ background:'#f0fdf4', borderRadius:10, padding:'4px 12px', textAlign:'center' }}>
              <div style={{ fontSize:16, fontWeight:800, color:'#16a34a' }}>{g.improvement}</div>
              <div style={{ fontSize:10, color:'#9ca3af' }}>improved</div>
            </div>
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
            {[{ label:'Before', tag:'b', bg:'#fef2f2', color:'#b04848', hue: dHue(g.id+'b') },
              { label:'After',  tag:'a', bg:'#f0fdf4', color:'#3a7a4a', hue: dHue(g.id+'a') }].map(side => (
              <div key={side.label} style={{ background:side.bg, borderRadius:12, padding:0, overflow:'hidden', position:'relative', aspectRatio:'4/3' }}>
                <div style={{ position:'absolute', inset:0, background:`linear-gradient(135deg, hsl(${side.hue}, 24%, 78%) 0%, hsl(${(side.hue+25)%360}, 18%, 60%) 100%)` }} />
                <div style={{ position:'absolute', inset:0, display:'flex', alignItems:'center', justifyContent:'center', color:'rgba(255,255,255,0.55)' }}>
                  {IndIcon('person',{c:'#ffffff',size:36,sw:1.4})}
                </div>
                <div style={{ position:'absolute', left:8, bottom:8, fontSize:11, fontWeight:700, color:'#fff', background:'rgba(0,0,0,0.35)', borderRadius:6, padding:'2px 8px', letterSpacing:'0.04em' }}>{side.label}</div>
              </div>
            ))}
          </div>
          <div style={{ fontSize:11, color:'#9ca3af', marginTop:10, textAlign:'center' }}>{g.date}</div>
        </div>
      ))}
    </div>
  </div>
);

/* ── Chat ── */
const DermScreenChat = ({ role }) => {
  const scrollRef = React.useRef();
  const [activeThread, setActiveThread] = React.useState('team');
  const [notifOpen, setNotifOpen]       = React.useState(false);
  React.useEffect(() => {
    const threads = window.getChatThreads('derm');
    if (!threads['team'].messages || threads['team'].messages.length === 0) {
      threads['team'].messages = DERM_DATA.chat.map(c => ({ ...c, tags:[], cards:[] }));
    }
  }, []);
  const [, forceUpdate] = React.useReducer(x => x+1, 0);
  React.useEffect(() => {
    const h = () => forceUpdate();
    document.addEventListener('sr:threads-updated', h);
    return () => document.removeEventListener('sr:threads-updated', h);
  }, []);
  const currentMsgs = (window.getChatThreads('derm')[activeThread] || {}).messages || [];
  // Live team from SRAPI.fetchTeam(), fallback to role list
  const [dermTeamList, setDermTeamList] = React.useState(window.SR_TEAM || null);
  React.useEffect(() => {
    const h = (e) => setDermTeamList(e.detail.members);
    document.addEventListener('sr:team-loaded', h);
    return () => document.removeEventListener('sr:team-loaded', h);
  }, []);
  const DERM_DEMO_TASKS = [
    { id:'dmt1', name:'Priya Sharma — post-peel follow-up', meta:'Therapist · Due Today', assignee:'Therapist' },
    { id:'dmt2', name:'Consent form — Anjali Bose', meta:'Front Desk · Pending', assignee:'Front Desk' },
    { id:'dmt3', name:'Laser session prep — Rm 3', meta:'Dermatologist · 2 PM', assignee:'Dermatologist' },
    { id:'dmt4', name:'Restock retinol serum', meta:'Unassigned · Low', assignee:'' },
    { id:'dmt5', name:'Monthly report — treatment outcomes', meta:'Manager · End of week', assignee:'Manager' },
  ];
  const DERM_TEAM = ['Therapist','Dermatologist','Front Desk','Manager'].map((r,i) => ({ id:'dr'+i, name:r, meta:'Dermatology' }));
  const liveDermTeam = dermTeamList && dermTeamList.length > 0 ? dermTeamList : DERM_TEAM;
  const allTasks = [...DERM_DEMO_TASKS, ...(window.listIndCreated ? window.listIndCreated('derm') : []).map(t => ({ id:t.id, name:t.title, meta:t.due||'', assignee:t.assignee||'' })), ...(window.PF_DATA && window.PF_DATA.tasks ? window.PF_DATA.tasks.map(t => ({ id:t.id, name:t.title, meta:(t.due||'TBD')+' · '+(t.priority||''), assignee:t.assignee||'' })) : [])];
  const isManager = role === 'Manager' || role === 'Admin';
  const dmPerson  = activeThread !== 'team' ? (window.getChatThreads('derm')[activeThread]||{}).name : null;
  const DERM_NAMES = ['Therapist','Dermatologist','Front Desk','Manager'];
  const liveClients = window.PF_DATA && window.PF_DATA.leads && window.PF_DATA.leads.length
    ? window.PF_DATA.leads.map(l => ({ id:l.id, name:l.name, meta:(l.project||'Client')+' · '+(l.lastTouch||'') }))
    : DERM_DATA.clients.map(c => ({ id:c.id, name:c.name, meta:c.skin+' · '+c.lastVisit }));
  const ds = {
    _ns:      'derm',
    client:   liveClients,
    task:     dmPerson && !isManager ? allTasks.filter(t => t.assignee === dmPerson) : allTasks,
    order:    DERM_DATA.sessions.map(s => ({ id:s.id, name:s.type+' — '+s.client, meta:s.date+' · '+s.status })),
    employee: liveDermTeam,
    nudge:    liveDermTeam,
  };
  const send = (text, tags = [], cards = []) => {
    if (!text && tags.length === 0 && cards.length === 0) return;
    window.addThreadMessage('derm', activeThread, { id:`u${Date.now()}`, from:'You', avatar:'ME', me:true, msg:text, tags, cards, time:'Now', unread:false });
    setTimeout(() => scrollRef.current && (scrollRef.current.scrollTop = scrollRef.current.scrollHeight), 60);
  };
  return (
    <div style={{ flex:1, display:'flex', flexDirection:'column', overflow:'hidden', position:'relative' }}>
      <div style={{ padding:'12px 16px 10px', background:'#fff', borderBottom:'1px solid #f3f4f6', display:'flex', alignItems:'center', gap:8 }}>
        <div style={{ flex:1 }}>
          <div style={{ fontSize:18, fontWeight:800, color:'#111827' }}>Team Chat</div>
          <div style={{ fontSize:12, color:'#6b7280', marginTop:1 }}>Glow Derma Studio · Staff Channel</div>
        </div>
        {window.NotifBell && <window.NotifBell ns="derm" user={role||'Therapist'} accent={DERM_ACCENT} onClick={() => setNotifOpen(true)} />}
      </div>
      {window.ChatThreadSwitcher && <window.ChatThreadSwitcher ns="derm" accent={DERM_ACCENT} activeThread={activeThread} onSwitch={setActiveThread} role={role} teamNames={DERM_NAMES} />}
      <div ref={scrollRef} style={{ flex:1, overflow:'auto', padding:'14px 14px 8px' }}>
        {currentMsgs.map(c => <window.ChatBubble key={c.id} msg={c} accent={DERM_ACCENT} />)}
        {currentMsgs.length === 0 && <div style={{ padding:'32px 0', textAlign:'center', color:'#9ca3af', fontSize:13 }}>Start your conversation</div>}
      </div>
      <window.ChatComposer accent={DERM_ACCENT} placeholder="Message team… (type / to tag)" onSend={send} dataSources={ds}
        commands={window.DERM_CMDS && window.DERM_CMDS[role] ? window.DERM_CMDS[role] : undefined} role={role} />
      {notifOpen && window.NotifPanel && <window.NotifPanel ns="derm" user={role||'Therapist'} accent={DERM_ACCENT} onClose={() => setNotifOpen(false)} />}
    </div>
  );
};

/* ── Tab bar ── */
/* ── Tasks (Dermatology) ── */
const DERM_ROLES = ['Therapist', 'Dermatologist', 'Front Desk', 'Manager', 'Admin'];

const DermScreenTasks = ({ role }) => {
  const [sel, setSel]         = React.useState(null);
  const [showAdd, setShowAdd] = React.useState(false);
  const [view, setView]       = React.useState('mine'); // 'mine' | 'team' (Manager only)
  const [, force] = React.useReducer(x => x + 1, 0);
  React.useEffect(() => {
    const fn = (e) => { if (e.detail.ns === 'derm') force(); };
    document.addEventListener('sr:ind-task-updated', fn);
    document.addEventListener('sr:nudge', fn);
    return () => {
      document.removeEventListener('sr:ind-task-updated', fn);
      document.removeEventListener('sr:nudge', fn);
    };
  }, []);

  const isManager = role === 'Manager' || role === 'Admin';

  // Live tasks = seeded DERM_DATA.tasks + user-added tasks, both overlaid with patches.
  const base = (DERM_DATA.tasks || []).concat(window.listIndCreated('derm'));
  const live = base.map(t => window.indTaskGet('derm', t));
  // 'mine' view filters by role; manager team view groups all.
  const visible = (isManager && view === 'team')
    ? live
    : live.filter(t => !t.role || t.role === role || (isManager && view === 'mine' && t.role === 'Manager'));

  const onToggle = (e, id) => { e.stopPropagation(); window.indTaskToggle('derm', id); };
  const onNudge  = (e, t) => {
    e.stopPropagation();
    const msg = window.prompt(`Nudge ${t.role || 'team'} about:\n"${t.title}"\n\nMessage:`, "Please prioritise this task today.");
    if (!msg) return;
    window.sendNudge('derm', t.role || 'Therapist', t.id, msg, 'Manager');
    alert(`Nudge sent to ${t.role || 'team'}.`);
  };

  if (sel) return <window.IndTaskDetail task={sel} accent={DERM_ACCENT} ns="derm" onBack={() => setSel(null)} />;

  // Team-grouping for manager view: group by role
  const byRole = {};
  if (isManager && view === 'team') {
    visible.forEach(t => {
      const k = t.role || 'Unassigned';
      (byRole[k] = byRole[k] || []).push(t);
    });
  }

  const counts = {
    open: visible.filter(t => !t.done).length,
    done: visible.filter(t => t.done).length,
    total: visible.length,
  };

  const renderRow = (t) => {
    const delayed = window.isTaskDelayed ? window.isTaskDelayed(t) : false;
    const nudgeKey = 'derm:' + (t.assignee || t.role || '');
    const nudged = !!(window.NUDGE_STORE && (window.NUDGE_STORE[nudgeKey]||[]).some(n => !n.dismissed && n.taskId === t.id));
    const flagged = (delayed || nudged) && !t.done;
    return (
    <div key={t.id} onClick={() => setSel(t)}
      style={{ background: flagged ? '#fff5f5' : '#fff', borderRadius:14, padding:'14px 16px', marginBottom:10, cursor:'pointer',
        boxShadow:'0 1px 6px rgba(0,0,0,0.05)', opacity: t.done ? 0.55 : 1,
        borderLeft: flagged ? '3px solid #ef4444' : '3px solid transparent',
        display:'flex', alignItems:'flex-start', gap:12 }}>
      <div onClick={(e) => onToggle(e, t.id)} style={{ width:22, height:22, borderRadius:6, border:`2px solid ${t.done ? DERM_ACCENT : '#d1d5db'}`,
        background: t.done ? DERM_ACCENT : '#fff', display:'flex', alignItems:'center', justifyContent:'center',
        flexShrink:0, marginTop:1, cursor:'pointer' }}>
        {t.done && <span style={{ display:'flex' }}>{IndIcon('check',{c:'#fff',size:12,sw:3})}</span>}
      </div>
      <div style={{ flex:1, minWidth:0 }}>
        <div style={{ fontWeight:600, fontSize:13.5, color:'#111827', textDecoration: t.done ? 'line-through' : 'none' }}>{t.title}</div>
        <div style={{ display:'flex', gap:8, marginTop:6, flexWrap:'wrap', alignItems:'center' }}>
          <span style={{ fontSize:10.5, fontWeight:700, background:dStatusBg(t.priority), color:dStatusColor(t.priority), borderRadius:20, padding:'2px 8px' }}>{t.priority}</span>
          <span style={{ fontSize:10.5, color:'#9ca3af' }}>Due: {t.due}</span>
          {t.role && <span style={{ fontSize:10.5, color:'#9ca3af' }}>→ {t.role}</span>}
          {t.assignee && <span style={{ fontSize:10.5, color:DERM_ACCENT, fontWeight:600 }}>· {t.assignee}</span>}
          {t._created && <span style={{ fontSize:10, color:'#16a34a', fontWeight:700, background:'#dcfce7', borderRadius:10, padding:'1px 7px' }}>New</span>}
        </div>
      </div>
      {isManager && !t.done && (
        <button onClick={(e) => onNudge(e, t)} style={{ flexShrink:0, border:`1px solid ${flagged?'#ef4444':DERM_ACCENT}`, background: flagged ? '#fff5f5' : '#fff', color:flagged?'#ef4444':DERM_ACCENT, borderRadius:8, padding:'5px 10px', fontSize:11, fontWeight:700, cursor:'pointer', fontFamily:'inherit', display:'flex', alignItems:'center', gap:4, alignSelf:'center' }}>
          {IndIcon('bell',{c:flagged?'#ef4444':DERM_ACCENT,size:11})} {flagged ? '🔴 Nudge' : 'Nudge'}
        </button>
      )}
      {flagged && !isManager && <span style={{ fontSize:10, color:'#ef4444', fontWeight:700, alignSelf:'center' }}>🔴</span>}
    </div>
    );
  };

  return (
    <div style={{ flex:1, display:'flex', flexDirection:'column', overflow:'hidden', position:'relative' }}>
      <div style={{ padding:'14px 16px 10px', background:'#fff', borderBottom:'1px solid #f3f4f6' }}>
        <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', gap:8 }}>
          <div>
            <div style={{ fontSize:18, fontWeight:800, color:'#111827' }}>Tasks</div>
            <div style={{ fontSize:12, color:'#6b7280', marginTop:3 }}>
              {counts.done}/{counts.total} completed{isManager && view==='team' ? ' · team view' : ''}
            </div>
          </div>
          <button onClick={() => setShowAdd(true)} style={{ width:36, height:36, borderRadius:'50%', border:'none', background:DERM_ACCENT, color:'#fff', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', boxShadow:'0 2px 8px '+DERM_ACCENT+'66', fontFamily:'inherit' }} title="New task">
            {IndIcon('plus',{c:'#fff',size:18,sw:2.4})}
          </button>
        </div>
        {isManager && (
          <div style={{ display:'flex', gap:6, marginTop:10 }}>
            <button onClick={() => setView('mine')} style={{ border:'none', borderRadius:99, padding:'5px 12px', fontSize:11.5, fontWeight:700, cursor:'pointer', fontFamily:'inherit', background: view==='mine' ? DERM_ACCENT : '#f3f4f6', color: view==='mine' ? '#fff' : '#6b7280' }}>My Tasks</button>
            <button onClick={() => setView('team')} style={{ border:'none', borderRadius:99, padding:'5px 12px', fontSize:11.5, fontWeight:700, cursor:'pointer', fontFamily:'inherit', background: view==='team' ? DERM_ACCENT : '#f3f4f6', color: view==='team' ? '#fff' : '#6b7280' }}>Team Pending</button>
          </div>
        )}
      </div>

      {/* Nudge banner for this role */}
      <window.IndNudgeBanner ns="derm" role={role} accent={DERM_ACCENT} />

      <div style={{ flex:1, overflow:'auto', padding:'12px 14px 24px' }}>
        {(isManager && view === 'team') ? (
          Object.entries(byRole).map(([r, items]) => (
            <div key={r} style={{ marginBottom:14 }}>
              <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'0 2px 6px' }}>
                <div style={{ fontSize:11, fontWeight:800, color:'#6b7280', letterSpacing:'0.1em', textTransform:'uppercase' }}>{r}</div>
                <div style={{ fontSize:11, color:'#9ca3af' }}>{items.filter(t=>!t.done).length} pending · {items.length} total</div>
              </div>
              {items.map(renderRow)}
            </div>
          ))
        ) : (
          visible.map(renderRow)
        )}
        {visible.length === 0 && (
          <div style={{ textAlign:'center', padding:'40px 20px', color:'#9ca3af' }}>
            <div style={{ display:'inline-flex', width:54, height:54, borderRadius:99, background:'#f3f4f6', alignItems:'center', justifyContent:'center', marginBottom:10 }}>
              {IndIcon('check',{c:'#9ca3af',size:24})}
            </div>
            <div style={{ fontSize:13, fontWeight:600 }}>No tasks yet</div>
            <div style={{ fontSize:11.5, marginTop:4 }}>Tap + to add one</div>
          </div>
        )}
      </div>

      {showAdd && (
        <window.IndTaskCreate
          ns="derm" accent={DERM_ACCENT}
          roles={DERM_ROLES} defaultRole={isManager ? 'Therapist' : role}
          onClose={() => setShowAdd(false)}
          onCreate={(t) => { if (t._notify && t.role && t.role !== role) { window.sendNudge('derm', t.role, t.id, `New task assigned: ${t.title}`, role || 'Manager'); } }}
        />
      )}
    </div>
  );
};

const DermTabBar = ({ active, onChange }) => (
  <div style={{ display:'flex', background:'#fff', borderTop:'1px solid #e5e7eb', paddingBottom:6, flexShrink:0 }}>
    {[
      { id:'home',       icon:IndIcon('home'), label:'Home'     },
      { id:'clients',    icon:IndIcon('team'), label:'Clients'  },
      { id:'treatments', icon:IndIcon('cal'), label:'Sessions' },
      { id:'tasks',      icon:IndIcon('check'), label:'Tasks'   },
      { id:'chat',       icon:IndIcon('msg'), label:'Chat'     },
    ].map(t => (
      <div key={t.id} onClick={() => onChange(t.id)}
        style={{ flex:1, display:'flex', flexDirection:'column', alignItems:'center', padding:'8px 0 2px', cursor:'pointer',
          color: active===t.id ? DERM_ACCENT : '#9ca3af' }}>
        <span style={{ fontSize:21 }}>{t.icon}</span>
        <span style={{ fontSize:10, fontWeight: active===t.id ? 700 : 400, marginTop:2 }}>{t.label}</span>
        {active===t.id && <div style={{ width:16, height:2.5, background:DERM_ACCENT, borderRadius:2, marginTop:3 }} />}
      </div>
    ))}
  </div>
);

// Maps a URL path (each native tab's fixed WebView path — see RootTabs.tsx)
// to the matching internal tab id. "Sessions" is the label but "treatments"
// is the internal tab id, same as the existing tab bar.
const DERM_PATH_TAB_MAP = { '/': 'home', '/clients': 'clients', '/sessions': 'treatments', '/tasks': 'tasks', '/chat': 'chat' };
function dermInitialTab() {
  const path = (typeof window !== 'undefined' && window.location && window.location.pathname) || '/';
  return DERM_PATH_TAB_MAP[path] || 'home';
}

/* ── App ── */
const DermApp = ({ role, platform, user }) => {
  const [tab, setTab] = React.useState(dermInitialTab);
  const topPad = platform === 'ios' ? 'env(safe-area-inset-top)' : 8; // was a hardcoded 54px guess; env() resolves correctly both inside the native wrapper and a plain browser tab
  let Screen;
  switch (tab) {
    case 'clients':    Screen = <DermScreenClients role={role} />; break;
    case 'treatments': Screen = <DermScreenTreatments role={role} />; break;
    case 'gallery':    Screen = <DermScreenGallery />; break;
    case 'tasks':      Screen = <DermScreenTasks role={role} />; break;
    case 'overview':   Screen = window.TaskOverview ? <window.TaskOverview ns="derm" accent={DERM_ACCENT} role={role} onBack={() => setTab('home')} teamNames={['Therapist','Dermatologist','Front Desk','Manager']} /> : null; break;
    case 'chat':       Screen = <DermScreenChat role={role} />; break;
    default:           Screen = <DermScreenHome role={role} go={setTab} user={user} />;
  }
  return (
    <div style={{ height:'100%', display:'flex', flexDirection:'column', background:'#faf5ff', paddingTop:topPad }}>
      <div style={{ flex:1, minHeight:0, display:'flex', flexDirection:'column', overflow:'hidden', position:'relative' }}>{Screen}</div>
      {/* Native wrapper now has matching tabs for all 5 screens (see
          RootTabs.tsx) with path-based routing above. */}
      {!IS_NATIVE_IOS && <DermTabBar active={tab} onChange={setTab} />}
    </div>
  );
};

Object.assign(window, { DermApp });


// ===== 20-pf-manufacturing.jsx =====
/* global React */

const MFG_DATA = {
  stats: { orders: 34, onTrack: 28, delayed: 3, efficiency: '92%', defects: '0.8%', shift: 'Morning' },
  orders: [
    { id:'o1', code:'WO-1041', product:'Gear Assembly A',   qty:500, done:480, due:'May 12', line:'L-1', supervisor:'Raj Kumar',  status:'On Track',  priority:'High'   },
    { id:'o2', code:'WO-1042', product:'Motor Casing B',    qty:200, done:190, due:'May 13', line:'L-2', supervisor:'Priya D.',   status:'On Track',  priority:'Medium' },
    { id:'o3', code:'WO-1043', product:'Drive Shaft C',     qty:100, done:60,  due:'May 12', line:'L-3', supervisor:'Raj Kumar',  status:'Delayed',   priority:'High'   },
    { id:'o4', code:'WO-1044', product:'Bearing Housing D', qty:300, done:300, due:'May 11', line:'L-1', supervisor:'Priya D.',   status:'Complete',  priority:'Medium' },
    { id:'o5', code:'WO-1045', product:'Coupling Set E',    qty:150, done:0,   due:'May 15', line:'L-2', supervisor:'Arjun S.',   status:'Pending',   priority:'Low'    },
    { id:'o6', code:'WO-1046', product:'Flange Assembly F', qty:250, done:120, due:'May 14', line:'L-3', supervisor:'Arjun S.',   status:'On Track',  priority:'Medium' },
  ],
  qcChecks: [
    { id:'q1', order:'WO-1041', product:'Gear Assembly A',  inspector:'Sonu QC', result:'Pass',   defects:2,  sample:50, date:'09:30 AM', notes:'Minor burr on shaft — acceptable' },
    { id:'q2', order:'WO-1043', product:'Drive Shaft C',    inspector:'Sonu QC', result:'Fail',   defects:8,  sample:30, date:'08:45 AM', notes:'Dimensional deviation on 8 units — rework required' },
    { id:'q3', order:'WO-1042', product:'Motor Casing B',   inspector:'Vinod QC',result:'Pass',   defects:1,  sample:40, date:'10:00 AM', notes:'Surface finish within spec' },
    { id:'q4', order:'WO-1044', product:'Bearing Housing D',inspector:'Sonu QC', result:'Pass',   defects:0,  sample:50, date:'Yesterday', notes:'All dimensions within tolerance' },
    { id:'q5', order:'WO-1046', product:'Flange Assembly F',inspector:'Vinod QC',result:'Pending',defects:0,  sample:0,  date:'--',        notes:'Awaiting batch completion' },
  ],
  maintenance: [
    { id:'m1', machine:'CNC Machine #3',      issue:'Coolant leak',             line:'L-3', priority:'Critical', status:'Open',       raised:'09:15 AM', assigned:'Mohan M.' },
    { id:'m2', machine:'Lathe #1',            issue:'Spindle vibration',        line:'L-1', priority:'High',     status:'In Progress',raised:'08:00 AM', assigned:'Ramesh M.' },
    { id:'m3', machine:'Press #2',            issue:'Hydraulic pressure drop',  line:'L-2', priority:'High',     status:'Open',       raised:'10:30 AM', assigned:'Mohan M.' },
    { id:'m4', machine:'Conveyor Belt B',     issue:'Scheduled PMS',            line:'L-2', priority:'Low',      status:'Scheduled',  raised:'May 15',   assigned:'Ramesh M.' },
    { id:'m5', machine:'Assembly Robot R-4',  issue:'Calibration drift',        line:'L-1', priority:'Medium',   status:'In Progress',raised:'Yesterday', assigned:'Auto.' },
  ],
  chat: [
    { id:'c1', from:'Supervisor Raj',  avatar:'RK', msg:'L-3 CNC down — coolant leak. Shifting Drive Shaft to L-1.', time:'9:20 AM', unread:true  },
    { id:'c2', from:'QC Sonu',         avatar:'SQ', msg:'WO-1043 failed QC — 8 units need rework before final.',      time:'9:35 AM', unread:true  },
    { id:'c3', from:'Maintenance',     avatar:'MM', msg:'CNC #3 parts ordered, ETA 4 hours.',                          time:'10:05 AM',unread:false },
    { id:'c4', from:'Manager',         avatar:'MG', msg:'Daily production review at 2 PM in conference room.',         time:'10:30 AM',unread:false },
    { id:'c5', from:'Safety Officer',  avatar:'SO', msg:'Reminder: PPE mandatory in L-3 zone.',                        time:'11:00 AM',unread:false },
  ],
};

const MFG_ACCENT = '#8a5e2c';
const MFG_BG = '#fffbeb';

const mStatusColor = s => ({
  'On Track':'#16a34a', Complete:'#2a5d7f', Delayed:'#dc2626', Pending:'#9ca3af',
  Pass:'#16a34a', Fail:'#dc2626', Open:'#dc2626', 'In Progress':'#ca8a04',
  Scheduled:'#2a5d7f', Critical:'#dc2626', High:'#ca8a04', Medium:'#2a5d7f', Low:'#9ca3af',
  Resolved:'#16a34a'
}[s] || '#6b7280');
const mStatusBg = s => mStatusColor(s) + '18';

/* ── Home ── */
const MFGScreenHome = ({ role, go, user }) => {
  const s = MFG_DATA.stats;
  const activeOrders = MFG_DATA.orders.filter(o => o.status !== 'Complete' && o.status !== 'Pending').slice(0,3);
  const openIssues = MFG_DATA.maintenance.filter(m => m.status === 'Open' || m.status === 'In Progress');
  const [profileOpen, setProfileOpen] = React.useState(false);
  return (
    <div style={{ flex:1, overflow:'auto', paddingBottom:20 }}>
      <div style={{ background:'linear-gradient(135deg, #8a5e2c, #d97706)', padding:'16px 20px 22px', color:'#fff', display:'flex', alignItems:'flex-start', justifyContent:'space-between' }}>
        <div>
          <div style={{ fontSize:12, opacity:.8, marginBottom:4 }}>Good morning · {role} · {s.shift} Shift</div>
          <div style={{ fontSize:22, fontWeight:800 }}>SkaleDesk MFG</div>
          <div style={{ fontSize:12, opacity:.75, marginTop:2 }}>Plant A · {new Date().toLocaleDateString('en-IN',{weekday:'short',day:'numeric',month:'short'})}</div>
        </div>
        {user && (
          <button
            onClick={() => {
              if (IS_NATIVE_IOS && window.ReactNativeWebView) {
                window.ReactNativeWebView.postMessage(JSON.stringify({ type: 'OPEN_MORE' }));
              } else {
                setProfileOpen(true);
              }
            }}
            style={{ background:'rgba(255,255,255,0.18)', border:'none', borderRadius:'50%', width:36, height:36,
              display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer', color:'#fff',
              fontSize:13, fontWeight:700, flexShrink:0 }}
          >{user.initials}</button>
        )}
      </div>
      {profileOpen && <ProfileSheet user={user} onClose={() => setProfileOpen(false)} />}
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:10, padding:'14px 14px 4px' }}>
        {[
          { label:'Orders', value:s.orders, icon:IndIcon('box') },
          { label:'Efficiency', value:s.efficiency, icon:IndIcon('bolt') },
          { label:'Defect Rate', value:s.defects, icon:IndIcon('search'), alert:parseFloat(s.defects) > 1 },
        ].map(stat => (
          <div key={stat.label} style={{ background:'#fff', borderRadius:14, padding:'12px 10px', textAlign:'center', boxShadow:'0 1px 8px rgba(0,0,0,0.06)' }}>
            <div style={{ fontSize:20 }}>{stat.icon}</div>
            <div style={{ fontSize:18, fontWeight:800, color: stat.alert ? '#dc2626' : '#111827', marginTop:2 }}>{stat.value}</div>
            <div style={{ fontSize:10, color:'#9ca3af', marginTop:1 }}>{stat.label}</div>
          </div>
        ))}
      </div>
      <div style={{ padding:'14px 14px 4px' }}>
        <div style={{ fontSize:13, fontWeight:700, color:'#374151', marginBottom:10 }}>Quick Actions</div>
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
          {[
            { label:'Work Orders', icon:IndIcon('box'), tab:'orders' },
            { label:'QC Checks', icon:IndIcon('check'), tab:'quality' },
            { label:'Maintenance', icon:IndIcon('wrench'), tab:'maintenance' },
            { label:'Overview', icon:IndIcon('grid'), tab:'overview' },
            { label:'Chat', icon:IndIcon('msg'), tab:'chat' },
          ].map(a => (
            <div key={a.label} onClick={() => go(a.tab)}
              style={{ background:'#fff', borderRadius:14, padding:'14px 14px', cursor:'pointer', boxShadow:'0 1px 6px rgba(0,0,0,0.06)', display:'flex', alignItems:'center', gap:10 }}>
              <span style={{ fontSize:22 }}>{a.icon}</span>
              <span style={{ fontWeight:600, fontSize:13, color:'#1f2937' }}>{a.label}</span>
            </div>
          ))}
        </div>
      </div>
      {openIssues.length > 0 && (
        <div style={{ padding:'14px 14px 4px' }}>
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:10 }}>
            <div style={{ fontSize:13, fontWeight:700, color:'#374151' }}>{IndIcon('warn')}️ Open Issues</div>
            <button onClick={() => go('maintenance')} style={{ border:'none', background:'none', color:MFG_ACCENT, fontSize:12, fontWeight:600, cursor:'pointer', padding:0 }}>See all</button>
          </div>
          {openIssues.map(i => (
            <div key={i.id} style={{ background:'#fff', borderRadius:12, padding:'11px 14px', marginBottom:8, boxShadow:'0 1px 4px rgba(0,0,0,0.05)', borderLeft:`3px solid ${mStatusColor(i.priority)}` }}>
              <div style={{ fontWeight:700, fontSize:13, color:'#111827' }}>{i.machine}</div>
              <div style={{ fontSize:11.5, color:'#6b7280', marginTop:2 }}>{i.issue} · {i.line}</div>
              <div style={{ display:'flex', gap:8, marginTop:6 }}>
                <span style={{ fontSize:10.5, fontWeight:700, background:mStatusBg(i.priority), color:mStatusColor(i.priority), borderRadius:20, padding:'2px 8px' }}>{i.priority}</span>
                <span style={{ fontSize:10.5, color:'#9ca3af' }}>{i.status} · {i.assigned}</span>
              </div>
            </div>
          ))}
        </div>
      )}
      <div style={{ padding:'14px 14px 4px' }}>
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:10 }}>
          <div style={{ fontSize:13, fontWeight:700, color:'#374151' }}>Active Orders</div>
          <button onClick={() => go('orders')} style={{ border:'none', background:'none', color:MFG_ACCENT, fontSize:12, fontWeight:600, cursor:'pointer', padding:0 }}>See all</button>
        </div>
        {activeOrders.map(o => (
          <div key={o.id} style={{ background:'#fff', borderRadius:12, padding:'12px 14px', marginBottom:8, boxShadow:'0 1px 4px rgba(0,0,0,0.05)' }}>
            <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', marginBottom:8 }}>
              <div>
                <div style={{ fontSize:11, color:MFG_ACCENT, fontWeight:700 }}>{o.code}</div>
                <div style={{ fontWeight:700, fontSize:13.5, color:'#111827', marginTop:2 }}>{o.product}</div>
              </div>
              <span style={{ fontSize:10.5, fontWeight:700, background:mStatusBg(o.status), color:mStatusColor(o.status), borderRadius:20, padding:'3px 8px' }}>{o.status}</span>
            </div>
            <div style={{ background:'#f3f4f6', borderRadius:8, height:6, overflow:'hidden' }}>
              <div style={{ height:'100%', background: o.status === 'Delayed' ? '#dc2626' : MFG_ACCENT, borderRadius:8, width:`${Math.round((o.done/o.qty)*100)}%`, transition:'width .4s' }} />
            </div>
            <div style={{ display:'flex', justifyContent:'space-between', marginTop:5 }}>
              <div style={{ fontSize:11, color:'#6b7280' }}>{o.done}/{o.qty} units</div>
              <div style={{ fontSize:11, color:'#6b7280' }}>Due: {o.due}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

/* ── Work Orders ── */
const MFGScreenOrders = ({ role }) => {
  const [filter, setFilter] = React.useState('All');
  const filters = ['All','On Track','Delayed','Complete','Pending'];
  const list = filter === 'All' ? MFG_DATA.orders : MFG_DATA.orders.filter(o => o.status === filter);
  return (
    <div style={{ flex:1, display:'flex', flexDirection:'column', overflow:'hidden' }}>
      <div style={{ padding:'14px 16px 10px', background:'#fff', borderBottom:'1px solid #f3f4f6' }}>
        <div style={{ fontSize:18, fontWeight:800, color:'#111827', marginBottom:10 }}>Work Orders</div>
        <div style={{ display:'flex', gap:6, overflowX:'auto', paddingBottom:2 }}>
          {filters.map(f => (
            <button key={f} onClick={() => setFilter(f)}
              style={{ border:'none', borderRadius:20, padding:'5px 12px', fontSize:12, fontWeight:600, cursor:'pointer', whiteSpace:'nowrap',
                background: filter===f ? MFG_ACCENT : '#f3f4f6', color: filter===f ? '#fff' : '#6b7280' }}>{f}</button>
          ))}
        </div>
      </div>
      <div style={{ flex:1, overflow:'auto', padding:'10px 14px 20px' }}>
        {list.map(o => (
          <div key={o.id} style={{ background:'#fff', borderRadius:14, padding:'14px 16px', marginBottom:10, boxShadow:'0 1px 6px rgba(0,0,0,0.05)' }}>
            <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', marginBottom:10 }}>
              <div>
                <div style={{ fontSize:11, color:MFG_ACCENT, fontWeight:700, marginBottom:3 }}>{o.code} · {o.line}</div>
                <div style={{ fontWeight:700, fontSize:15, color:'#111827' }}>{o.product}</div>
                <div style={{ fontSize:12, color:'#6b7280', marginTop:2 }}>Supervisor: {o.supervisor}</div>
              </div>
              <span style={{ fontSize:10.5, fontWeight:700, background:mStatusBg(o.status), color:mStatusColor(o.status), borderRadius:20, padding:'3px 9px' }}>{o.status}</span>
            </div>
            <div style={{ background:'#f3f4f6', borderRadius:8, height:8, overflow:'hidden', marginBottom:6 }}>
              <div style={{ height:'100%', background: o.status === 'Delayed' ? '#dc2626' : MFG_ACCENT, borderRadius:8, width:`${Math.round((o.done/o.qty)*100)}%` }} />
            </div>
            <div style={{ display:'flex', justifyContent:'space-between', marginBottom:10 }}>
              <div style={{ fontSize:12, color:'#374151', fontWeight:600 }}>{o.done}/{o.qty} units <span style={{ fontWeight:400, color:'#9ca3af' }}>({Math.round((o.done/o.qty)*100)}%)</span></div>
              <div style={{ fontSize:12, color:'#6b7280' }}>Due: {o.due}</div>
            </div>
            <div style={{ display:'flex', gap:8 }}>
              <button style={{ flex:1, border:`1px solid ${MFG_ACCENT}`, borderRadius:8, padding:'7px', fontSize:12, color:MFG_ACCENT, background:'none', cursor:'pointer', fontWeight:600 }}>Details</button>
              <button style={{ flex:1, border:'none', borderRadius:8, padding:'7px', fontSize:12, color:'#fff', background:MFG_ACCENT, cursor:'pointer', fontWeight:600 }}>Update</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

/* ── Quality ── */
const MFGScreenQuality = ({ role }) => (
  <div style={{ flex:1, display:'flex', flexDirection:'column', overflow:'hidden' }}>
    <div style={{ padding:'14px 16px 10px', background:'#fff', borderBottom:'1px solid #f3f4f6' }}>
      <div style={{ fontSize:18, fontWeight:800, color:'#111827' }}>QC Inspections</div>
      <div style={{ fontSize:12, color:'#6b7280', marginTop:3 }}>
        {MFG_DATA.qcChecks.filter(q => q.result === 'Pass').length} Pass · {MFG_DATA.qcChecks.filter(q => q.result === 'Fail').length} Fail · {MFG_DATA.qcChecks.filter(q => q.result === 'Pending').length} Pending
      </div>
    </div>
    <div style={{ flex:1, overflow:'auto', padding:'12px 14px 24px' }}>
      {MFG_DATA.qcChecks.map(q => (
        <div key={q.id} style={{ background:'#fff', borderRadius:14, padding:'14px 16px', marginBottom:10, boxShadow:'0 1px 6px rgba(0,0,0,0.05)',
          borderLeft:`4px solid ${mStatusColor(q.result)}` }}>
          <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', marginBottom:8 }}>
            <div>
              <div style={{ fontSize:11, color:MFG_ACCENT, fontWeight:700 }}>{q.order}</div>
              <div style={{ fontWeight:700, fontSize:14.5, color:'#111827', marginTop:3 }}>{q.product}</div>
            </div>
            <span style={{ fontSize:13, fontWeight:800, background:mStatusBg(q.result), color:mStatusColor(q.result),
              borderRadius:20, padding:'4px 12px' }}>{q.result}</span>
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8, marginBottom:8 }}>
            <div style={{ fontSize:12, color:'#6b7280' }}>Inspector: {q.inspector}</div>
            <div style={{ fontSize:12, color:'#6b7280' }}>Sample: {q.sample} units</div>
            <div style={{ fontSize:12, color: q.defects > 0 ? '#dc2626' : '#16a34a', fontWeight:600 }}>Defects: {q.defects}</div>
            <div style={{ fontSize:12, color:'#6b7280' }}>Time: {q.date}</div>
          </div>
          {q.notes && <div style={{ background:'#f9fafb', borderRadius:8, padding:'8px 10px', fontSize:12, color:'#374151', fontStyle:'italic' }}>"{q.notes}"</div>}
          {q.result === 'Fail' && (
            <button style={{ width:'100%', marginTop:10, border:'none', borderRadius:8, padding:'9px', fontSize:12.5, fontWeight:700, color:'#fff', background:'#dc2626', cursor:'pointer' }}>
              {IndIcon('retry')} Initiate Rework
            </button>
          )}
          {q.result === 'Pending' && (
            <button style={{ width:'100%', marginTop:10, border:`1.5px solid ${MFG_ACCENT}`, borderRadius:8, padding:'9px', fontSize:12.5, fontWeight:700, color:MFG_ACCENT, background:'none', cursor:'pointer' }}>
              Start Inspection
            </button>
          )}
        </div>
      ))}
    </div>
  </div>
);

/* ── Maintenance ── */
const MFG_ROLES = ['Technician', 'Supervisor', 'Manager'];
// Maintenance items map → task shape consumed by IndTaskDetail
const _mfgMaintAsTask = (m) => ({ id: m.id, title: m.machine + ' — ' + m.issue, due: m.raised, priority: m.priority, role: m.assigned, for: m.line, status: m.status, assignee: m.assigned });

const MFGScreenMaintenance = ({ role }) => {
  const isManager = role === 'Manager' || role === 'Supervisor';
  const [filter, setFilter] = React.useState('All');
  const [view, setView] = React.useState('mine');   // 'mine' | 'team'
  const [sel, setSel] = React.useState(null);
  const [showAdd, setShowAdd] = React.useState(false);
  const [, force] = React.useReducer(x => x + 1, 0);
  React.useEffect(() => {
    const fn = (e) => { if (e.detail && e.detail.ns === 'mfg') force(); };
    document.addEventListener('sr:ind-task-updated', fn);
    document.addEventListener('sr:nudge', fn);
    return () => { document.removeEventListener('sr:ind-task-updated', fn); document.removeEventListener('sr:nudge', fn); };
  }, []);

  // Merge overlay so done/assignee patches propagate
  const baseLive = MFG_DATA.maintenance.map(m => {
    const overlay = window.IND_TASK_STORE['mfg:' + m.id];
    const o = (typeof overlay === 'object' && overlay) ? overlay : {};
    return Object.assign({}, m, o, {
      status: o.done ? 'Resolved' : (o.status || m.status),
      assigned: o.assignee || m.assigned,
    });
  });
  // Add user-created tasks
  const created = (window.IND_CREATED_TASKS && window.IND_CREATED_TASKS['mfg']) ? window.IND_CREATED_TASKS['mfg'] : [];
  const live = [...baseLive, ...created.map(t => ({ id:t.id, machine: t.title, issue: t.notes || '', line: t.for || 'Plant', priority: t.priority || 'Medium', status: t.done ? 'Resolved' : 'Open', raised: t.due || 'Today', assigned: t.role || 'Technician' }))];

  const mine = live.filter(m => m.status !== 'Resolved');
  const filtered = filter === 'All' ? mine : mine.filter(m => m.status === filter || m.priority === filter);

  // Team pending grouped by assignee
  const pending = live.filter(m => m.status !== 'Resolved');
  const byRole = {};
  pending.forEach(m => { const k = m.assigned || 'Unassigned'; (byRole[k] = byRole[k] || []).push(m); });

  if (sel) return <window.IndTaskDetail task={_mfgMaintAsTask(sel)} accent={MFG_ACCENT} ns="mfg" onBack={() => setSel(null)} />;
  return (
    <div style={{ flex:1, display:'flex', flexDirection:'column', overflow:'hidden' }}>
      <window.IndNudgeBanner ns="mfg" role={role} accent={MFG_ACCENT} />
      <div style={{ padding:'14px 16px 10px', background:'#fff', borderBottom:'1px solid #f3f4f6' }}>
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:10 }}>
          <div style={{ fontSize:18, fontWeight:800, color:'#111827' }}>Maintenance</div>
          <button onClick={() => setShowAdd(true)}
            style={{ width:34, height:34, borderRadius:99, background:MFG_ACCENT, border:'none', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', color:'#fff', fontSize:22, fontWeight:300, lineHeight:1 }}>+</button>
        </div>
        {isManager && (
          <div style={{ display:'flex', background:'#f3f4f6', borderRadius:10, padding:3, marginBottom:10 }}>
            {[['mine','My Tasks'],['team','Team Pending']].map(([v,l]) => (
              <button key={v} onClick={() => setView(v)} style={{ flex:1, border:'none', borderRadius:8, padding:'6px', fontSize:12.5, fontWeight:700, cursor:'pointer',
                background: view===v ? '#fff' : 'transparent', color: view===v ? MFG_ACCENT : '#6b7280',
                boxShadow: view===v ? '0 1px 4px rgba(0,0,0,0.08)' : 'none' }}>{l}</button>
            ))}
          </div>
        )}
        {view === 'mine' && (
          <div style={{ display:'flex', gap:6, overflowX:'auto' }}>
            {['All','Open','In Progress','Scheduled','Resolved'].map(f => (
              <button key={f} onClick={() => setFilter(f)}
                style={{ border:'none', borderRadius:20, padding:'5px 12px', fontSize:12, fontWeight:600, cursor:'pointer', whiteSpace:'nowrap',
                  background: filter===f ? MFG_ACCENT : '#f3f4f6', color: filter===f ? '#fff' : '#6b7280' }}>{f}</button>
            ))}
          </div>
        )}
      </div>

      <div style={{ flex:1, overflow:'auto', padding:'12px 14px 24px' }}>
        {view === 'team' && isManager ? (
          Object.keys(byRole).length === 0 ? (
            <div style={{ textAlign:'center', color:'#9ca3af', fontSize:13, marginTop:40 }}>No pending tasks</div>
          ) : Object.entries(byRole).map(([assignee, tasks]) => (
            <div key={assignee} style={{ background:'#fff', borderRadius:14, marginBottom:12, boxShadow:'0 1px 6px rgba(0,0,0,0.05)', overflow:'hidden' }}>
              <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'12px 14px', borderBottom:'1px solid #f3f4f6' }}>
                <div>
                  <div style={{ fontWeight:700, fontSize:14, color:'#111827' }}>{assignee}</div>
                  <div style={{ fontSize:11.5, color:'#6b7280', marginTop:2 }}>{tasks.length} pending</div>
                </div>
                <button onClick={() => {
                  const tid = tasks[0].id;
                  window.sendNudge && window.sendNudge('mfg', assignee, tid, 'You have pending maintenance tasks — please attend to them.', role || 'Manager');
                  force();
                }} style={{ border:`1.5px solid ${MFG_ACCENT}`, borderRadius:20, padding:'5px 14px', fontSize:12, fontWeight:700, color:MFG_ACCENT, background:'none', cursor:'pointer' }}>
                  {IndIcon('bell',{c:MFG_ACCENT,size:13})} Nudge
                </button>
              </div>
              {tasks.slice(0,3).map(m => (
                <div key={m.id} onClick={() => setSel(m)} style={{ padding:'10px 14px', borderBottom:'1px solid #f9fafb', cursor:'pointer', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                  <div>
                    <div style={{ fontSize:13.5, fontWeight:600, color:'#111827' }}>{m.machine}</div>
                    <div style={{ fontSize:11.5, color:'#6b7280' }}>{m.issue || m.line}</div>
                  </div>
                  <span style={{ fontSize:10.5, fontWeight:700, background:mStatusBg(m.status), color:mStatusColor(m.status), borderRadius:20, padding:'3px 9px' }}>{m.status}</span>
                </div>
              ))}
              {tasks.length > 3 && <div style={{ padding:'8px 14px', fontSize:12, color:'#9ca3af' }}>+{tasks.length-3} more</div>}
            </div>
          ))
        ) : (
          filtered.length === 0 ? (
            <div style={{ textAlign:'center', color:'#9ca3af', fontSize:13, marginTop:40 }}>No items</div>
          ) : filtered.map(m => {
            const isResolved = m.status === 'Resolved';
            return (
            <div key={m.id} onClick={() => setSel(m)} style={{ background:'#fff', borderRadius:14, padding:'14px 16px', marginBottom:10, boxShadow:'0 1px 6px rgba(0,0,0,0.05)', borderLeft:`4px solid ${mStatusColor(m.priority)}`, cursor:'pointer', opacity: isResolved ? 0.6 : 1 }}>
              <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', marginBottom:8 }}>
                <div>
                  <div style={{ fontWeight:700, fontSize:15, color:'#111827', textDecoration: isResolved ? 'line-through' : 'none' }}>{m.machine}</div>
                  <div style={{ fontSize:12.5, color:'#6b7280', marginTop:3 }}>{m.issue}</div>
                </div>
                <span style={{ fontSize:10.5, fontWeight:700, background:mStatusBg(m.status), color:mStatusColor(m.status), borderRadius:20, padding:'3px 9px', flexShrink:0, marginLeft:8 }}>{m.status}</span>
              </div>
              <div style={{ display:'flex', flexWrap:'wrap', gap:12, marginBottom:10 }}>
                <div style={{ fontSize:11.5, color:'#6b7280', display:'flex', alignItems:'center', gap:4 }}>{IndIcon('pin',{c:'#9ca3af',size:12})} {m.line}</div>
                <div style={{ fontSize:11.5, color:'#6b7280', display:'flex', alignItems:'center', gap:4 }}>{IndIcon('person',{c:'#9ca3af',size:12})} {m.assigned}</div>
                <div style={{ fontSize:11.5, color:'#6b7280', display:'flex', alignItems:'center', gap:4 }}>{IndIcon('clock',{c:'#9ca3af',size:12})} {m.raised}</div>
              </div>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                <span style={{ fontSize:11, fontWeight:700, background:mStatusBg(m.priority), color:mStatusColor(m.priority), borderRadius:20, padding:'3px 10px' }}>{m.priority}</span>
                <span style={{ fontSize:11.5, fontWeight:700, color:MFG_ACCENT, display:'flex', alignItems:'center', gap:4 }}>View {IndIcon('arrowR',{c:MFG_ACCENT,size:12})}</span>
              </div>
            </div>
            );
          })
        )}
      </div>

      {showAdd && (
        <window.IndTaskCreate
          ns="mfg" accent={MFG_ACCENT}
          roles={MFG_ROLES} defaultRole={isManager ? 'Technician' : role}
          onClose={() => setShowAdd(false)}
          onCreate={(t) => {
            if (t._notify && t.role && t.role !== role) {
              window.sendNudge && window.sendNudge('mfg', t.role, t.id, `New maintenance task: ${t.title}`, role || 'Manager');
            }
            force();
          }}
        />
      )}
    </div>
  );
};

/* ── Chat ── */
const MFGScreenChat = ({ role }) => {
  const scrollRef = React.useRef();
  const [activeThread, setActiveThread] = React.useState('team');
  const [notifOpen, setNotifOpen]       = React.useState(false);
  React.useEffect(() => {
    const threads = window.getChatThreads('mfg');
    if (!threads['team'].messages || threads['team'].messages.length === 0) {
      threads['team'].messages = MFG_DATA.chat.map(c => ({ ...c, tags:[], cards:[] }));
    }
  }, []);
  const [, forceUpdate] = React.useReducer(x => x+1, 0);
  React.useEffect(() => {
    const h = () => forceUpdate();
    document.addEventListener('sr:threads-updated', h);
    return () => document.removeEventListener('sr:threads-updated', h);
  }, []);
  const currentMsgs = (window.getChatThreads('mfg')[activeThread] || {}).messages || [];
  // Live team from SRAPI.fetchTeam(), fallback to role list
  const [mfgTeamList, setMfgTeamList] = React.useState(window.SR_TEAM || null);
  React.useEffect(() => {
    const h = (e) => setMfgTeamList(e.detail.members);
    document.addEventListener('sr:team-loaded', h);
    return () => document.removeEventListener('sr:team-loaded', h);
  }, []);
  const MFG_DEMO_TASKS = [
    { id:'mgt1', name:'CNC-02 — bearing replacement', meta:'Technician A · Urgent', assignee:'Technician A' },
    { id:'mgt2', name:'Line 3 conveyor calibration', meta:'Unassigned · High', assignee:'' },
    { id:'mgt3', name:'QC audit — Batch #2891', meta:'QC Inspector · Today', assignee:'QC Inspector' },
    { id:'mgt4', name:'Safety drill sign-off', meta:'Supervisor · Pending', assignee:'Supervisor' },
    { id:'mgt5', name:'Shift handover report — Night shift', meta:'Shift Lead · Due Tonight', assignee:'Shift Lead' },
  ];
  const MFG_TEAM = MFG_ROLES.map((r,i) => ({ id:'mfg'+i, name:r, meta:'Manufacturing' }));
  const liveMFGTeam = mfgTeamList && mfgTeamList.length > 0 ? mfgTeamList : MFG_TEAM;
  const allTasks = [...MFG_DEMO_TASKS, ...MFG_DATA.maintenance.map(m => ({ id:m.id, name:m.machine+' — '+m.issue, meta:m.priority+' · '+m.status, assignee:'' })), ...(window.PF_DATA && window.PF_DATA.tasks ? window.PF_DATA.tasks.map(t => ({ id:t.id, name:t.title, meta:(t.due||'TBD')+' · '+(t.priority||''), assignee:t.assignee||'' })) : [])];
  const isManager = role === 'Manager' || role === 'Supervisor';
  const dmPerson  = activeThread !== 'team' ? (window.getChatThreads('mfg')[activeThread]||{}).name : null;
  const MFG_NAMES = MFG_ROLES;
  const ds = {
    _ns:      'mfg',
    order:    MFG_DATA.orders.map(o => ({ id:o.id, name:o.code+' — '+o.product, meta:o.line+' · '+o.status })),
    task:     dmPerson && !isManager ? allTasks.filter(t => t.assignee === dmPerson) : allTasks,
    employee: liveMFGTeam,
    nudge:    liveMFGTeam,
    update:  [
      { id:'progress', name:'Production Progress', meta:'' },
      { id:'blocker', name:'Line Blocker', meta:'' },
      { id:'qc', name:'QC Result', meta:'' },
      { id:'shift', name:'Shift Handover', meta:'' },
    ],
  };
  const send = (text, tags = [], cards = []) => {
    if (!text && tags.length === 0 && cards.length === 0) return;
    window.addThreadMessage('mfg', activeThread, { id:`u${Date.now()}`, from:'You', avatar:'ME', me:true, msg:text, tags, cards, time:'Now', unread:false });
    setTimeout(() => scrollRef.current && (scrollRef.current.scrollTop = scrollRef.current.scrollHeight), 60);
  };
  return (
    <div style={{ flex:1, display:'flex', flexDirection:'column', overflow:'hidden', position:'relative' }}>
      <div style={{ padding:'12px 16px 10px', background:'#fff', borderBottom:'1px solid #f3f4f6', display:'flex', alignItems:'center', gap:8 }}>
        <div style={{ flex:1 }}>
          <div style={{ fontSize:18, fontWeight:800, color:'#111827' }}>Plant Chat</div>
          <div style={{ fontSize:12, color:'#6b7280', marginTop:1 }}>Plant A · Shift Channel</div>
        </div>
        {window.NotifBell && <window.NotifBell ns="mfg" user={role||'Technician'} accent={MFG_ACCENT} onClick={() => setNotifOpen(true)} />}
      </div>
      {window.ChatThreadSwitcher && <window.ChatThreadSwitcher ns="mfg" accent={MFG_ACCENT} activeThread={activeThread} onSwitch={setActiveThread} role={role} teamNames={MFG_NAMES} />}
      <div ref={scrollRef} style={{ flex:1, overflow:'auto', padding:'14px 14px 8px' }}>
        {currentMsgs.map(c => <window.ChatBubble key={c.id} msg={c} accent={MFG_ACCENT} />)}
        {currentMsgs.length === 0 && <div style={{ padding:'32px 0', textAlign:'center', color:'#9ca3af', fontSize:13 }}>Start your conversation</div>}
      </div>
      <window.ChatComposer accent={MFG_ACCENT} placeholder="Message plant… (type / to tag)" onSend={send} dataSources={ds}
        commands={window.MFG_CMDS && window.MFG_CMDS[role] ? window.MFG_CMDS[role] : undefined} role={role} />
      {notifOpen && window.NotifPanel && <window.NotifPanel ns="mfg" user={role||'Technician'} accent={MFG_ACCENT} onClose={() => setNotifOpen(false)} />}
    </div>
  );
};

/* ── Tab bar ── */
const MFGTabBar = ({ active, onChange }) => (
  <div style={{ display:'flex', background:'#fff', borderTop:'1px solid #e5e7eb', paddingBottom:6, flexShrink:0 }}>
    {[
      { id:'home',        icon:IndIcon('home'), label:'Home'     },
      { id:'orders',      icon:IndIcon('box'), label:'Orders'   },
      { id:'quality',     icon:IndIcon('check'), label:'Quality'  },
      { id:'maintenance', icon:IndIcon('wrench'), label:'Maint.'   },
      { id:'chat',        icon:IndIcon('msg'), label:'Chat'     },
    ].map(t => (
      <div key={t.id} onClick={() => onChange(t.id)}
        style={{ flex:1, display:'flex', flexDirection:'column', alignItems:'center', padding:'8px 0 2px', cursor:'pointer',
          color: active===t.id ? MFG_ACCENT : '#9ca3af' }}>
        <span style={{ fontSize:21 }}>{t.icon}</span>
        <span style={{ fontSize:10, fontWeight: active===t.id ? 700 : 400, marginTop:2 }}>{t.label}</span>
        {active===t.id && <div style={{ width:16, height:2.5, background:MFG_ACCENT, borderRadius:2, marginTop:3 }} />}
      </div>
    ))}
  </div>
);

// Maps a URL path (each native tab's fixed WebView path — see RootTabs.tsx)
// to the matching internal tab id.
const MFG_PATH_TAB_MAP = { '/': 'home', '/orders': 'orders', '/quality': 'quality', '/maintenance': 'maintenance', '/chat': 'chat' };
function mfgInitialTab() {
  const path = (typeof window !== 'undefined' && window.location && window.location.pathname) || '/';
  return MFG_PATH_TAB_MAP[path] || 'home';
}

/* ── App ── */
const MFGApp = ({ role, platform, user }) => {
  const [tab, setTab] = React.useState(mfgInitialTab);
  const topPad = platform === 'ios' ? 'env(safe-area-inset-top)' : 8; // was a hardcoded 54px guess; env() resolves correctly both inside the native wrapper and a plain browser tab
  let Screen;
  switch (tab) {
    case 'orders':      Screen = <MFGScreenOrders role={role} />; break;
    case 'quality':     Screen = <MFGScreenQuality role={role} />; break;
    case 'maintenance': Screen = <MFGScreenMaintenance role={role} />; break;
    case 'overview':    Screen = window.TaskOverview ? <window.TaskOverview ns="mfg" accent={MFG_ACCENT} role={role} onBack={() => setTab('home')} teamNames={MFG_ROLES} /> : null; break;
    case 'chat':        Screen = <MFGScreenChat role={role} />; break;
    default:            Screen = <MFGScreenHome role={role} go={setTab} user={user} />;
  }
  return (
    <div style={{ height:'100%', display:'flex', flexDirection:'column', background:MFG_BG, paddingTop:topPad }}>
      <div style={{ flex:1, minHeight:0, display:'flex', flexDirection:'column', overflow:'hidden', position:'relative' }}>{Screen}</div>
      {/* Native wrapper now has matching tabs for all 5 screens (see
          RootTabs.tsx) with path-based routing above. */}
      {!IS_NATIVE_IOS && <MFGTabBar active={tab} onChange={setTab} />}
    </div>
  );
};

Object.assign(window, { MFGApp });


// ===== 21-skalecap-ilis.jsx =====
// SkaleCap mobile screens — v2 (Rams-simplified)
// Flat backgrounds · unified containers · single-purpose data · contextual contrast

const T2 = {
  navy: '#1E3A5F', navyDeep: '#13283F', navyLt: '#EEF1F6',
  // Soft sky → indigo header gradient (Calm-inspired). Keeps white text legible
  // and lets the gold accent stay vibrant.
  headerGrad: 'linear-gradient(180deg, #7FA8D5 0%, #7479C8 100%)',
  gold: '#C8960C', goldHi: '#FFD060', goldLt: '#FBF3DE',
  ink: '#13131F', inkMid: '#4A4A60', inkSoft: '#8A8AA0', inkFaint: '#B5B5C2',
  rule: '#E4E4EC', ruleSoft: '#EFEFF4',
  paper: '#FFFFFF', page: '#FAFAF7',
  sage: '#3D7A58', sageLt: '#E8F1EC',
  rose: '#BE3A3A', roseLt: '#FCEDED', roseFaint: '#FDF5F5',
  teal: '#0E7490', tealLt: '#E2F2F6',
  amber: '#B45309',
};

// Alias useState so Block 1 components can use the same useS shorthand as Blocks 2 & 3
const useS = React.useState;

// ─────────────────────────────────────────────────────────────
// Distinct subject-matter icons (line art)
// ─────────────────────────────────────────────────────────────
function I({ name, size = 20, c = 'currentColor', sw = 1.8 }) {
  const p = {
    // Navigation
    home:    <><path d="M3 11l9-7 9 7v9a1 1 0 01-1 1h-5v-7H10v7H5a1 1 0 01-1-1v-9z" strokeLinejoin="round"/></>,
    learn:   <><path d="M4 5a2 2 0 012-2h11v17H6a2 2 0 010-4h11"/><path d="M9 8h5"/></>,
    check:   <><circle cx="12" cy="12" r="9"/><path d="M8 12l3 3 5-6" strokeLinecap="round" strokeLinejoin="round"/></>,
    growth:  <><path d="M3 17l5-6 4 4 6-9"/><path d="M14 6h4v4" strokeLinecap="round"/></>,
    person:  <><circle cx="12" cy="8.5" r="3.5"/><path d="M5 20c.7-3.5 3.5-6 7-6s6.3 2.5 7 6"/></>,
    // L&D
    grid:    <><rect x="3" y="3" width="7" height="7" rx="1.4"/><rect x="14" y="3" width="7" height="7" rx="1.4"/><rect x="3" y="14" width="7" height="7" rx="1.4"/><rect x="14" y="14" width="7" height="7" rx="1.4"/></>,
    team:    <><circle cx="9" cy="9" r="3"/><circle cx="17" cy="10" r="2.4"/><path d="M3 19c.5-3 2.7-5 6-5s5.5 2 6 5M15 19c.4-2 1.7-3 4-3s3.2 1 3.5 3"/></>,
    cal:     <><rect x="3" y="5" width="18" height="16" rx="2"/><path d="M3 10h18M8 3v4M16 3v4"/></>,
    bar:     <><path d="M5 20V11M12 20V5M19 20v-7" strokeLinecap="round"/></>,
    // Module subject icons — distinct
    compass: <><circle cx="12" cy="12" r="9"/><path d="M16 8l-2.5 5.5L8 16l2.5-5.5L16 8z" strokeLinejoin="round"/></>,
    wave:    <><path d="M3 12c2-4 4-4 6 0s4 4 6 0 4-4 6 0" strokeLinecap="round"/></>,
    sprout:  <><path d="M12 21v-7M12 14c0-3 2-5 5-5 0 3-2 5-5 5zM12 14c0-3-2-5-5-5 0 3 2 5 5 5z" strokeLinejoin="round"/></>,
    flag:    <><path d="M5 21V4M5 4l10 1-2 4 2 4-10-1" strokeLinejoin="round"/></>,
    // UI
    bolt:    <><path d="M13 2L4 13h6l-1 9 9-12h-6l1-8z" strokeLinejoin="round"/></>,
    play:    <><path d="M8 5v14l11-7-11-7z" strokeLinejoin="round" fill={c}/></>,
    pause:   <><rect x="5" y="4" width="4" height="16" rx="1"/><rect x="15" y="4" width="4" height="16" rx="1"/></>,
    bell:    <><path d="M6 9a6 6 0 0112 0v4l1.5 2.5h-15L6 13V9z"/><path d="M9.5 19a2.5 2.5 0 005 0"/></>,
    chevR:   <><path d="M9 6l6 6-6 6" strokeLinecap="round" strokeLinejoin="round"/></>,
    chevL:   <><path d="M15 6l-6 6 6 6" strokeLinecap="round" strokeLinejoin="round"/></>,
    arrowUp: <><path d="M12 19V5M6 11l6-6 6 6" strokeLinecap="round" strokeLinejoin="round"/></>,
    arrowDn: <><path d="M12 5v14M6 13l6 6 6-6" strokeLinecap="round" strokeLinejoin="round"/></>,
    plus:    <><path d="M12 5v14M5 12h14" strokeLinecap="round"/></>,
    flame:   <><path d="M12 3c1 4-3 5-3 9a4 4 0 008 0c0-2-1-3-1-5 0 3-2 4-3 4 0-3 2-5-1-8z" strokeLinejoin="round" fill={c}/></>,
    search:  <><circle cx="11" cy="11" r="7"/><path d="M20.5 20.5L17 17" strokeLinecap="round"/></>,
    lock:    <><rect x="5" y="11" width="14" height="10" rx="2"/><path d="M8 11V8a4 4 0 018 0v3"/></>,
    target:  <><circle cx="12" cy="12" r="9"/><circle cx="12" cy="12" r="4.5"/></>,
    clock:   <><circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/></>,
    doc:     <><path d="M14 3H6a2 2 0 00-2 2v14a2 2 0 002 2h12a2 2 0 002-2V9l-6-6z"/><path d="M14 3v6h6"/></>,
    ping:    <><circle cx="12" cy="12" r="2.5" fill={c}/><path d="M6 12a6 6 0 0112 0M3 12a9 9 0 0118 0"/></>,
    msg:     <><path d="M4 5a2 2 0 012-2h12a2 2 0 012 2v9a2 2 0 01-2 2H9l-5 4V5z" strokeLinejoin="round"/></>,
    // Community + extras
    community:<><path d="M7 8a4 4 0 118 0c0 2-1 3-2 4M5 19c0-3 2-5 5-5h2c3 0 5 2 5 5" strokeLinejoin="round"/></>,
    aicoach: <><path d="M12 2a7 7 0 017 7c0 2.8-1.6 5.2-4 6.5V17a1 1 0 01-1 1h-4a1 1 0 01-1-1v-1.5C6.6 14.2 5 11.8 5 9a7 7 0 017-7z" strokeLinejoin="round"/><path d="M10 21h4M9 9h.01M15 9h.01M9 13c1 1.3 5 1.3 6 0" strokeLinecap="round"/></>,
    chevD:   <><path d="M6 9l6 6 6-6" strokeLinecap="round" strokeLinejoin="round"/></>,
    copy2:   <><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"/></>,
    thumbUp: <><path d="M14 9V5a3 3 0 00-3-3l-4 9v11h11.3a2 2 0 002-1.7l1.4-9a2 2 0 00-2-2.3H14z"/><path d="M7 22H4a2 2 0 01-2-2v-7a2 2 0 012-2h3" strokeLinecap="round"/></>,
    thumbDn: <><path d="M10 15v4a3 3 0 003 3l4-9V2H5.7a2 2 0 00-2 1.7L2.3 11a2 2 0 002 2.3H10z"/><path d="M17 2h2.7A2.3 2.3 0 0122 4.3v7a2.3 2.3 0 01-2.3 2.3H17" strokeLinecap="round"/></>,
    retry:   <><path d="M1 4v6h6M23 20v-6h-6"/><path d="M20.5 9A9 9 0 005.2 5.2L1 10M23 14l-4.2 4.8A9 9 0 013.5 15" strokeLinecap="round"/></>,
    mic:     <><rect x="9" y="2" width="6" height="11" rx="3"/><path d="M5 10a7 7 0 0014 0M12 19v3M8 22h8" strokeLinecap="round"/></>,
    heart:   <><path d="M12 20s-7-4.5-7-10a4 4 0 017-2.5A4 4 0 0119 10c0 5.5-7 10-7 10z" strokeLinejoin="round"/></>,
    heartFill:<><path d="M12 20s-7-4.5-7-10a4 4 0 017-2.5A4 4 0 0119 10c0 5.5-7 10-7 10z" strokeLinejoin="round" fill={c}/></>,
    comment: <><path d="M21 12a8 8 0 01-12.5 6.6L4 20l1.4-4.5A8 8 0 1121 12z" strokeLinejoin="round"/></>,
    share:   <><circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><path d="M9 11l6-4M9 13l6 4"/></>,
    pin:     <><path d="M12 2l3 5h4l-3 4 1 5-5-3-5 3 1-5-3-4h4l3-5z" strokeLinejoin="round" fill={c} fillOpacity=".15"/></>,
    hash:    <><path d="M5 9h14M5 15h14M10 3l-2 18M16 3l-2 18" strokeLinecap="round"/></>,
    trophy:  <><path d="M8 4h8v4a4 4 0 01-8 0V4zM16 6h3v2a3 3 0 01-3 3M8 6H5v2a3 3 0 003 3M12 12v4M9 20h6"/></>,
    sparkle: <><path d="M12 3l1.5 4.5L18 9l-4.5 1.5L12 15l-1.5-4.5L6 9l4.5-1.5L12 3z" strokeLinejoin="round" fill={c} fillOpacity=".18"/></>,
    edit:    <><path d="M14 4l6 6-11 11H3v-6L14 4z" strokeLinejoin="round"/><path d="M13 5l6 6"/></>,
    camera:  <><rect x="3" y="7" width="18" height="13" rx="2"/><circle cx="12" cy="13.5" r="3.5"/><path d="M9 7l2-3h2l2 3"/></>,
    smile:   <><circle cx="12" cy="12" r="9"/><circle cx="9" cy="10" r=".8" fill={c}/><circle cx="15" cy="10" r=".8" fill={c}/><path d="M8.5 14.5c.8 1.2 2 2 3.5 2s2.7-.8 3.5-2"/></>,
    eye:     <><path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12z"/><circle cx="12" cy="12" r="3"/></>,
    arrowR:  <><path d="M5 12h14M13 6l6 6-6 6" strokeLinecap="round" strokeLinejoin="round"/></>,
    star:    <><path d="M12 3l2.6 6 6.4.6-4.8 4.3 1.4 6.3L12 17l-5.6 3.2 1.4-6.3L3 9.6l6.4-.6L12 3z" strokeLinejoin="round" fill={c} fillOpacity=".18"/></>,
  };
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round" style={{flexShrink:0, display:'block'}}>
      {p[name] || null}
    </svg>
  );
}

// ─────────────────────────────────────────────────────────────
// Shared primitives — flat, restrained
// ─────────────────────────────────────────────────────────────
function Avatar({ initials, size = 36, bg = T2.navy, fg = '#fff' }) {
  return (
    <div style={{
      width: size, height: size, borderRadius: '50%',
      background: bg, color: fg, flexShrink: 0,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontWeight: 700, fontSize: size * 0.36, letterSpacing: '-0.01em',
      fontFamily: "'Lato', sans-serif",
    }}>{initials}</div>
  );
}

function IconButton({ icon, size = 36, c = T2.ink, border = T2.rule }) {
  return (
    <div style={{
      width: size, height: size, borderRadius: '50%',
      background: '#fff', border: `1px solid ${border}`,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      flexShrink: 0,
    }}>
      <I name={icon} size={16} c={c} sw={2} />
    </div>
  );
}

function Bar({ pct, c = T2.navy, bg = T2.ruleSoft, h = 4 }) {
  return (
    <div style={{height: h, borderRadius: h, background: bg, overflow: 'hidden'}}>
      <div style={{width: `${Math.min(100, pct)}%`, height: '100%', background: c}} />
    </div>
  );
}

function Label({ children, c = T2.inkSoft, mb = 0, mt = 0 }) {
  return (
    <div style={{
      fontSize: 11, fontWeight: 700, letterSpacing: '0.14em',
      color: c, textTransform: 'uppercase',
      marginTop: mt, marginBottom: mb,
      fontFamily: "'Lato', sans-serif",
    }}>{children}</div>
  );
}

function Section({ kicker, action, onAction, children, pt = 24 }) {
  return (
    <div style={{paddingTop: pt}}>
      {(kicker || action) && (
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
          padding: '0 16px 12px',
        }}>
          {kicker && <Label>{kicker}</Label>}
          {action && (
            <span onClick={onAction} style={{
              fontSize: 12, fontWeight: 700, color: T2.navy,
              cursor: onAction ? 'pointer' : 'default',
            }}>{action}</span>
          )}
        </div>
      )}
      {children}
    </div>
  );
}

// Flat solid header — no gradient, no orbs. Controls top row, title below = full width.
// FIX: kicker is forced single-line w/ ellipsis; right column is flex-shrink:0; title
// line-height 1.2 so wrapping never overlaps the subtitle.
function Header({ kicker, title, sub, right, onRight }) {
  return (
    <div style={{
      background: T2.headerGrad, padding: '12px 14px 22px', color: '#fff',
    }}>
      <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, marginBottom: 12}}>
        {kicker && (
          <div style={{
            flex: '1 1 auto', minWidth: 0,
            fontSize: 10.5, fontWeight: 700, letterSpacing: '0.16em',
            color: T2.goldHi, textTransform: 'uppercase',
            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
          }}>{kicker}</div>
        )}
        <div onClick={onRight} style={{flexShrink: 0, display: 'flex', alignItems: 'center', cursor:onRight?'pointer':'default'}}>{right}</div>
      </div>
      <div style={{
        fontFamily: "'Lato', sans-serif",
        fontSize: 24, fontWeight: 700, color: '#fff',
        letterSpacing: '-0.018em', lineHeight: 1.2,
        textWrap: 'balance',
      }}>{title}</div>
      {sub && (
        <div style={{
          fontSize: 12.5, color: 'rgba(255,255,255,0.88)',
          marginTop: 10, display: 'flex', alignItems: 'center', gap: 6,
          lineHeight: 1.35,
        }}>{sub}</div>
      )}
    </div>
  );
}

// Bottom nav — heavier, grounded
function BottomNav({ active = 'home', role = 'participant', onNav }) {
  // Native wrapper now has matching tabs (see RootTabs.tsx + the
  // role-aware path routing added to SkaleCapApp in 24-inline-03.jsx) —
  // gated once here instead of at each of this component's 10+ call
  // sites throughout this file.
  if (IS_NATIVE_IOS) return null;
  const items = role === 'participant' ? [
    {id:'home', icon:'home', label:'Home'},
    {id:'learn', icon:'learn', label:'Learn'},
    {id:'community', icon:'community', label:'Community'},
    {id:'growth', icon:'growth', label:'Growth'},
    {id:'ai', icon:'aicoach', label:'AI Coach'},
  ] : [
    {id:'home', icon:'grid', label:'Overview'},
    {id:'team', icon:'team', label:'Cohort'},
    {id:'sessions', icon:'cal', label:'Sessions'},
    {id:'analytics', icon:'bar', label:'Analytics'},
    {id:'ai', icon:'aicoach', label:'AI Coach'},
  ];
  return (
    <div style={{
      position: 'absolute', left: 0, right: 0, bottom: 0,
      background: '#fff', borderTop: `1px solid ${T2.rule}`,
      paddingBottom: 22, paddingTop: 10,
      display: 'flex', zIndex: 30,
    }}>
      {items.map(it => {
        const isActive = active === it.id;
        return (
          <div key={it.id} onClick={() => onNav && onNav(it.id)}
            style={{
              flex: 1, display: 'flex', flexDirection: 'column',
              alignItems: 'center', gap: 4, cursor: 'pointer', padding: '2px 0',
            }}>
            <I name={it.icon} size={22} c={isActive ? T2.navy : T2.inkFaint} sw={isActive ? 2.4 : 2} />
            <span style={{
              fontSize: 10, fontWeight: isActive ? 800 : 600,
              color: isActive ? T2.navy : T2.inkFaint,
              letterSpacing: '0.06em', textTransform: 'uppercase',
              fontFamily: "'Lato', sans-serif",
            }}>{it.label}</span>
          </div>
        );
      })}
    </div>
  );
}

const sh = {
  shell: {
    width: '100%', height: '100%', background: T2.paper,
    position: 'relative', overflow: 'hidden',
    fontFamily: "'Lato', -apple-system, system-ui, sans-serif",
    color: T2.ink,
  },
  scroll: {
    position: 'absolute', inset: 0, paddingBottom: 96,
    overflow: 'auto', WebkitOverflowScrolling: 'touch',
  },
  btnPrimary: {
    background: T2.navy, color: '#fff', border: 'none',
    borderRadius: 10, padding: '12px 16px',
    fontFamily: "'Lato', sans-serif", fontSize: 13, fontWeight: 700,
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    gap: 6, cursor: 'pointer', letterSpacing: '0.01em',
  },
  btnGhost: {
    background: 'transparent', color: T2.navy, border: `1.5px solid ${T2.rule}`,
    borderRadius: 10, padding: '11px 14px',
    fontFamily: "'Lato', sans-serif", fontSize: 12.5, fontWeight: 700,
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    gap: 6, cursor: 'pointer',
  },
  btnOutlineSoft: {
    background: 'transparent', color: T2.rose, border: `1.5px solid ${T2.rose}`,
    borderRadius: 8, padding: '7px 12px',
    fontFamily: "'Lato', sans-serif", fontSize: 11, fontWeight: 800,
    display: 'inline-flex', alignItems: 'center', gap: 5, cursor: 'pointer',
    letterSpacing: '0.06em', textTransform: 'uppercase',
  },
  btnDangerSolid: {
    background: T2.rose, color: '#fff', border: 'none',
    borderRadius: 8, padding: '8px 12px',
    fontFamily: "'Lato', sans-serif", fontSize: 11, fontWeight: 800,
    display: 'inline-flex', alignItems: 'center', gap: 5, cursor: 'pointer',
    letterSpacing: '0.06em', textTransform: 'uppercase',
  },
};

// ============================================================
// 1. PARTICIPANT — Home (Rams-simplified)
// ============================================================
function ScreenParticipantHome({ onNav, active = 'home' }) {
  // Wired against GET /api/participant/home (identity, cohort, capability
  // scores, next session, topic progress) and GET /api/participant/learn
  // (courses + per-topic progress, which drives "Today's focus" and the
  // module list). Both endpoints already existed and shipped with no caller —
  // this screen was 237 lines of hardcoded "Priya Mehta / Cohort 4" content.
  const [home, setHome]   = useS(null);
  const [mods, setMods]   = useS([]);
  const [state, setState] = useS('loading'); // loading | ready | notEnrolled | error

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!window.SRAPI || typeof window.SRAPI._get !== 'function') { setState('error'); return; }
      try {
        const h = await window.SRAPI._get('/api/participant/home');
        if (cancelled) return;
        setHome(h);
        // Learn is secondary — a failure there shouldn't blank the whole screen.
        try {
          const l = await window.SRAPI._get('/api/participant/learn');
          if (!cancelled) setMods(l?.modules || []);
        } catch (e) { /* modules section falls back to its empty state */ }
        if (!cancelled) setState('ready');
      } catch (err) {
        if (cancelled) return;
        setState(err && err.status === 404 ? 'notEnrolled' : 'error');
        if (err && err.status === 404) setHome({ _msg: err.body?.message });
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // ── Derived values (all from real data; nothing invented) ────────────────
  const name     = home?.user?.name || '';
  const first    = name.split(' ')[0] || '';
  const initials = name.split(' ').filter(Boolean).slice(0,2).map(w=>w[0]).join('').toUpperCase() || '·';
  const hr       = new Date().getHours();
  const greeting = hr < 12 ? 'Good morning' : hr < 17 ? 'Good afternoon' : 'Good evening';

  const caps    = home?.capabilities || [];
  const avgCap  = caps.length
    ? Math.round((caps.reduce((s,c)=>s+(Number(c.score)||0),0) / caps.length) * 100) / 100
    : null;
  // Lowest-scoring capability — a real, derivable "focus area". The old copy
  // claimed "2 peers ahead", which no endpoint here can support.
  const weakest = caps.length
    ? caps.slice().sort((a,b)=>(Number(a.score)||0)-(Number(b.score)||0))[0]
    : null;

  const prog    = home?.progress || { total:0, done:0 };
  const progPct = prog.total ? Math.round((prog.done / prog.total) * 100) : 0;

  // Module cards, with completion computed from each course's own topics.
  const moduleCards = mods.map(m => {
    const ts   = m.topics || [];
    const done = ts.filter(t=>t.progress==='completed').length;
    return {
      id: m.id,
      label: m.title,
      sub: ts.length ? `${done} of ${ts.length} ${ts.length===1?'lesson':'lessons'}` : 'No lessons yet',
      pct: ts.length ? Math.round((done/ts.length)*100) : 0,
    };
  });

  // "Today's focus" = the first lesson that isn't finished, in course order.
  const nextTopic = (() => {
    for (const m of mods) {
      for (const t of (m.topics || [])) {
        if (t.progress !== 'completed') return { ...t, course: m.title };
      }
    }
    return null;
  })();

  const nextSession = home?.nextSession || null;
  const sessDate    = nextSession?.starts_at ? new Date(nextSession.starts_at) : null;

  // ── Non-ready states ─────────────────────────────────────────────────────
  if (state !== 'ready') {
    const msg = state === 'loading'
      ? 'Loading your programme…'
      : state === 'notEnrolled'
        ? (home?._msg || 'Your account is not enrolled in any active SkaleCap batch. Contact your programme administrator.')
        : "Couldn't load your programme. Check your connection and try again.";
    return (
      <div style={sh.shell}>
        <div style={{ ...sh.scroll, display:'flex', alignItems:'center', justifyContent:'center', padding:'0 32px' }}>
          <div style={{ textAlign:'center', maxWidth:300 }}>
            {state === 'loading'
              ? <div style={{ fontSize:13, color:T2.inkSoft }}>{msg}</div>
              : <>
                  <div style={{
                    width:46, height:46, borderRadius:12, background:T2.ruleSoft, margin:'0 auto 14px',
                    display:'flex', alignItems:'center', justifyContent:'center',
                  }}>
                    <I name={state==='notEnrolled'?'flag':'bolt'} size={20} c={T2.inkSoft} sw={2}/>
                  </div>
                  <div style={{ fontSize:14.5, fontWeight:700, color:T2.ink, marginBottom:6 }}>
                    {state === 'notEnrolled' ? 'Not enrolled yet' : 'Something went wrong'}
                  </div>
                  <div style={{ fontSize:12.5, color:T2.inkSoft, lineHeight:1.55 }}>{msg}</div>
                </>}
          </div>
        </div>
        <BottomNav active={active} role="participant" onNav={onNav}/>
      </div>
    );
  }

  // ── Ready ────────────────────────────────────────────────────────────────
  return (
    <div style={sh.shell}>
      <div style={sh.scroll}>
        <Header
          kicker={home?.cohort?.name ? `SkaleCap · ${home.cohort.name}` : 'SkaleCap'}
          title={first ? `${greeting}, ${first}` : greeting}
          sub={home?.cohort?.name || ''}
          onRight={()=>onNav&&onNav('me')}
          right={<Avatar initials={initials} size={34} bg="rgba(255,213,96,0.2)" fg={T2.goldHi}/>}
        />

        {/* Capability score — real average across assessed capabilities.
            The old "Top 33% of your cohort / +0.42 this month" line was
            removed: neither a percentile nor a participant-facing delta is
            available from any endpoint this screen can call. */}
        {avgCap != null && (
          <div style={{padding:'18px 16px 0'}}>
            <div style={{
              display:'flex', alignItems:'center', gap:16,
              paddingBottom:18, borderBottom:`1px solid ${T2.rule}`,
            }}>
              <CapabilityRing score={avgCap} pct={Math.round((avgCap/5)*100)} />
              <div style={{flex:1, minWidth:0}}>
                <Label mb={4}>Capability score</Label>
                <div style={{
                  fontFamily:"'Lato', sans-serif",
                  fontSize:17, fontWeight:600, color:T2.ink,
                  letterSpacing:'-0.005em', lineHeight:1.25,
                }}>Across {caps.length} {caps.length===1?'capability':'capabilities'}</div>
                {prog.total > 0 && (
                  <div style={{
                    display:'flex', alignItems:'center', gap:4, marginTop:6,
                    fontSize:12, color:T2.inkSoft, fontWeight:600,
                  }}>
                    {prog.done} of {prog.total} lessons complete · {progPct}%
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Focus area — the lowest-scoring capability, genuinely derived. */}
        {weakest && (
          <div style={{padding:'14px 16px 0'}}>
            <div onClick={() => onNav && onNav('growth')} style={{
              display:'flex', alignItems:'center', gap:10, cursor:'pointer',
              padding:'10px 12px', background:T2.goldLt, borderRadius:10,
            }}>
              <I name="bolt" size={14} c={T2.gold} sw={2.2}/>
              <div style={{flex:1, fontSize:12.5, color:T2.navyDeep, fontWeight:600}}>
                Focus area: <strong>{weakest.label || weakest.key}</strong>
                {weakest.score != null && ` — ${Number(weakest.score).toFixed(2)}/5`}
              </div>
              <I name="chevR" size={13} c={T2.gold} sw={2.4}/>
            </div>
          </div>
        )}

        {/* Today's focus — first unfinished lesson across the batch's courses */}
        {nextTopic && (
          <Section kicker="Today's focus" pt={24}>
            <div style={{padding:'0 16px'}}>
              <div style={{
                border:`1px solid ${T2.rule}`, borderRadius:12,
                padding:'14px', background:T2.paper,
              }}>
                <div style={{display:'flex', gap:12, alignItems:'center'}}>
                  <div style={{
                    width:44, height:44, borderRadius:10, flexShrink:0,
                    background: T2.navy,
                    display:'flex', alignItems:'center', justifyContent:'center',
                  }}>
                    <I name="play" size={16} c="#fff" sw={2}/>
                  </div>
                  <div style={{flex:1, minWidth:0}}>
                    <div style={{fontSize:10, fontWeight:700, letterSpacing:'0.14em', color:T2.inkSoft, textTransform:'uppercase'}}>
                      {nextTopic.duration_min ? `${nextTopic.duration_min} min` : 'Lesson'}
                      {nextTopic.progress === 'in_progress' ? ' · In progress' : ''}
                    </div>
                    <div style={{
                      fontFamily:"'Lato', sans-serif",
                      fontSize:16, fontWeight:700, color:T2.ink,
                      lineHeight:1.25, marginTop:3,
                    }}>{nextTopic.title}</div>
                    <div style={{fontSize:11.5, color:T2.inkSoft, marginTop:3}}>{nextTopic.course}</div>
                  </div>
                </div>
                <div style={{display:'flex', gap:8, marginTop:14}}>
                  <button onClick={() => onNav && onNav('learn')} style={{...sh.btnPrimary, flex:1}}>
                    <I name="play" size={11} c="#fff" sw={2}/>
                    {nextTopic.progress === 'in_progress' ? ' Resume lesson' : ' Start lesson'}
                  </button>
                  <button onClick={() => onNav && onNav('learn')} style={sh.btnGhost} title="Course materials">
                    <I name="doc" size={14} c={T2.navy} sw={2}/>
                  </button>
                </div>
              </div>
            </div>
          </Section>
        )}

        {/* Modules — real courses assigned to this batch */}
        <Section kicker="Your modules" action={moduleCards.length ? 'See all' : undefined} onAction={() => onNav && onNav('learn')}>
          <div style={{padding:'0 16px', display:'flex', flexDirection:'column', gap:8}}>
            {moduleCards.length === 0 && (
              <div style={{
                border:`1px dashed ${T2.rule}`, borderRadius:12, padding:'18px 14px',
                textAlign:'center', fontSize:12.5, color:T2.inkSoft,
              }}>No modules assigned to your cohort yet.</div>
            )}
            {moduleCards.map((m) => (
              <div key={m.id} onClick={() => onNav && onNav('learn')} style={{
                background: T2.paper, border:`1px solid ${T2.rule}`, borderRadius:12,
                padding:'12px 14px', display:'flex', alignItems:'center', gap:12,
                cursor:'pointer',
              }}>
                <div style={{
                  width:38, height:38, borderRadius:10, background:T2.navyLt,
                  display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0,
                }}>
                  <I name="compass" size={17} c={T2.navy} sw={2}/>
                </div>
                <div style={{flex:1, minWidth:0}}>
                  <div style={{fontSize:13.5, fontWeight:700, color:T2.ink, letterSpacing:'-0.005em'}}>{m.label}</div>
                  <div style={{fontSize:11.5, color:T2.inkSoft, marginTop:2, marginBottom:7}}>{m.sub}</div>
                  <Bar pct={m.pct} c={T2.navy}/>
                </div>
                <span style={{
                  fontSize:13, fontWeight:800, color:T2.ink,
                  fontFamily:"'Lato', sans-serif", minWidth:36, textAlign:'right',
                  letterSpacing:'-0.01em',
                }}>{m.pct}%</span>
              </div>
            ))}
          </div>
        </Section>

        {/* Upcoming — the batch's real next scheduled session */}
        <Section kicker="Upcoming sessions" action="See all" onAction={() => onNav && onNav('sessions')}>
          <div style={{padding:'0 16px', display:'flex', flexDirection:'column', gap:8}}>
            {nextSession && sessDate ? (
              <div onClick={() => onNav && onNav('sessions')} style={{
                border:`1px solid ${T2.rule}`, borderRadius:12, padding:'12px 14px',
                display:'flex', alignItems:'center', gap:12, cursor:'pointer',
              }}>
                <DateBlock
                  dow={sessDate.toLocaleDateString('en-IN',{weekday:'short'}).toUpperCase()}
                  d={sessDate.getDate()}
                />
                <div style={{flex:1, minWidth:0}}>
                  <div style={{fontSize:13, fontWeight:700, color:T2.ink}}>{nextSession.title}</div>
                  <div style={{fontSize:11.5, color:T2.inkSoft, marginTop:3}}>
                    {sessDate.toLocaleTimeString('en-IN',{hour:'numeric',minute:'2-digit'})}
                  </div>
                </div>
                <I name="chevR" size={14} c={T2.inkSoft} sw={2}/>
              </div>
            ) : (
              <div style={{
                border:`1px dashed ${T2.rule}`, borderRadius:12, padding:'18px 14px',
                textAlign:'center', fontSize:12.5, color:T2.inkSoft,
              }}>No sessions scheduled yet.</div>
            )}
          </div>
        </Section>

        {/* The "Tasks · 3 due this week" shortcut that used to sit here was
            removed: there is no tasks table in the schema, so the count and
            the row beneath it were both invented. See FIX_TRACKER. */}

        <div style={{height: 24}}/>
      </div>
      <BottomNav active={active} role="participant" onNav={onNav}/>
    </div>
  );
}

function CapabilityRing({ score, pct }) {
  // Flat ring — no shadow, clean stroke
  const size = 64, r = 26, c = 2 * Math.PI * r;
  return (
    <div style={{position:'relative', width:size, height:size, flexShrink:0}}>
      <svg width={size} height={size}>
        <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={T2.ruleSoft} strokeWidth="4"/>
        <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={T2.gold} strokeWidth="4"
          strokeDasharray={c} strokeDashoffset={c * (1 - pct/100)}
          strokeLinecap="round" transform={`rotate(-90 ${size/2} ${size/2})`}/>
      </svg>
      <div style={{
        position:'absolute', inset:0, display:'flex', flexDirection:'column',
        alignItems:'center', justifyContent:'center',
      }}>
        <div style={{
          fontFamily:"'Lato', sans-serif", fontSize:17, fontWeight:700,
          color:T2.navy, letterSpacing:'-0.02em', lineHeight:1,
        }}>{score}</div>
        <div style={{fontSize:8.5, color:T2.inkSoft, marginTop:1, fontWeight:700, letterSpacing:'0.04em'}}>/5</div>
      </div>
    </div>
  );
}

function DateBlock({ dow, d }) {
  return (
    <div style={{
      width:44, height:48, borderRadius:10, border:`1px solid ${T2.rule}`,
      display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', flexShrink:0,
    }}>
      <div style={{fontSize:9, fontWeight:800, color:T2.gold, letterSpacing:'0.08em'}}>{dow}</div>
      <div style={{fontSize:15, fontWeight:800, color:T2.ink, lineHeight:1, marginTop:1, letterSpacing:'-0.01em'}}>{d}</div>
    </div>
  );
}

// ============================================================
// 2. PARTICIPANT — Learning module
// ============================================================
function ScreenParticipantLearn({ onNav, active = 'learn' }) {
  const lessons = [
    {n:1, title:'What makes a leader?', type:'Video', mins:12, done:true},
    {n:2, title:'Situational leadership model', type:'Article', mins:8, done:true},
    {n:3, title:'Power vs. authority', type:'Video', mins:10, done:true},
    {n:4, title:'Coaching conversations', type:'Exercise', mins:15, done:true},
    {n:5, title:'Stakeholder influence maps', type:'Video', mins:20, current:true},
    {n:6, title:'Decision frameworks', type:'Article', mins:10},
    {n:7, title:'Leading through change', type:'Video', mins:14},
    {n:8, title:'Module 1 — Assessment', type:'Assessment', mins:25, locked:true},
  ];
  return (
    <div style={sh.shell}>
      <div style={sh.scroll}>
        {/* Top bar — minimal */}
        <div style={{
          padding:'10px 16px', display:'flex', alignItems:'center', gap:10,
          borderBottom:`1px solid ${T2.rule}`,
        }}>
          <div onClick={() => onNav && onNav('home')} style={{cursor:'pointer'}}>
            <I name="chevL" size={20} c={T2.ink} sw={2.2}/>
          </div>
          <div style={{flex:1, fontSize:13, fontWeight:700, color:T2.ink, textAlign:'center'}}>Leadership Excellence</div>
          <IconButton icon="search" border={T2.rule}/>
        </div>

        {/* Module hero — soft sky-to-indigo gradient */}
        <div style={{background:T2.headerGrad, color:'#fff', padding:'16px'}}>
          <Label c={T2.goldHi} mb={8}>Module 1 of 4</Label>
          <div style={{
            fontFamily:"'Lato', sans-serif", fontSize:22, fontWeight:700,
            lineHeight:1.15, letterSpacing:'-0.01em',
          }}>Lead with quiet conviction</div>
          <div style={{fontSize:11.5, color:'rgba(255,255,255,0.85)', marginTop:6}}>
            8 lessons · 1 assessment · ~2 hrs total
          </div>
          <div style={{marginTop:14, display:'flex', alignItems:'center', gap:10}}>
            <div style={{flex:1}}>
              <Bar pct={50} c={T2.goldHi} bg="rgba(255,255,255,0.18)" h={4}/>
            </div>
            <span style={{fontSize:13, fontWeight:800, color:T2.goldHi, letterSpacing:'-0.01em'}}>4 of 8</span>
          </div>
        </div>

        {/* Lessons — unified container */}
        <div style={{padding:'16px'}}>
          <div style={{
            border:`1px solid ${T2.rule}`, borderRadius:12, overflow:'hidden',
            background: T2.paper,
          }}>
            {lessons.map((l, i) => (
              <div key={l.n} style={{
                display:'flex', alignItems:'center', gap:12,
                padding:'12px 14px',
                background: l.current ? T2.navyLt : 'transparent',
                borderBottom: i < lessons.length - 1 ? `1px solid ${T2.ruleSoft}` : 'none',
                opacity: l.locked ? 0.5 : 1, cursor: l.locked ? 'default' : 'pointer',
              }}>
                <div style={{
                  width:24, height:24, borderRadius:'50%', flexShrink:0,
                  background: l.done ? T2.navy : l.current ? '#fff' : 'transparent',
                  border: l.done ? `2px solid ${T2.navy}` : l.current ? `2px solid ${T2.navy}` : `2px solid ${T2.rule}`,
                  display:'flex', alignItems:'center', justifyContent:'center',
                  fontSize:10.5, fontWeight:800,
                  color: l.done ? '#fff' : l.current ? T2.navy : T2.inkFaint,
                }}>
                  {l.done ? <I name="check" size={12} c="#fff" sw={2.6}/> : l.n}
                </div>
                <div style={{flex:1, minWidth:0}}>
                  <div style={{
                    fontSize:13.5, fontWeight: l.current ? 800 : 600,
                    color: l.done ? T2.inkSoft : T2.ink, lineHeight:1.3,
                    textDecoration: l.done ? 'line-through' : 'none',
                    textDecorationColor: T2.inkFaint,
                  }}>{l.title}</div>
                  <div style={{fontSize:10.5, color:T2.inkSoft, marginTop:2, letterSpacing:'0.02em'}}>{l.type} · {l.mins} min</div>
                </div>
                {l.current && (
                  <span style={{
                    fontSize:9.5, fontWeight:800, color:T2.gold,
                    letterSpacing:'0.1em', textTransform:'uppercase',
                  }}>Up next</span>
                )}
                {l.locked && <I name="lock" size={13} c={T2.inkFaint} sw={2}/>}
                {!l.current && !l.locked && !l.done && (
                  <I name="chevR" size={13} c={T2.inkFaint} sw={2}/>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Resources */}
        <Section kicker="Resources & handouts" pt={6}>
          <div style={{padding:'0 16px', display:'flex', flexDirection:'column', gap:6}}>
            {[
              {t:'Stakeholder Map Template', sz:'1.1 MB · PPTX'},
              {t:'Influence Frameworks', sz:'2.4 MB · PDF'},
            ].map((r, i) => (
              <div key={i} style={{
                padding:'10px 12px', display:'flex', alignItems:'center', gap:12,
                border:`1px solid ${T2.rule}`, borderRadius:10,
              }}>
                <I name="doc" size={16} c={T2.inkSoft} sw={1.8}/>
                <div style={{flex:1}}>
                  <div style={{fontSize:12.5, fontWeight:700, color:T2.ink}}>{r.t}</div>
                  <div style={{fontSize:10.5, color:T2.inkSoft, marginTop:1}}>{r.sz}</div>
                </div>
                <I name="arrowDn" size={14} c={T2.navy} sw={2}/>
              </div>
            ))}
          </div>
        </Section>

        <div style={{height: 24}}/>
      </div>
      <BottomNav active={active} role="participant" onNav={onNav}/>
    </div>
  );
}

// ============================================================
// 3. PARTICIPANT — Assessment
// ============================================================
function ScreenParticipantAssess({ onNav, active = 'assess' }) {
  const [loading, setLoading]     = useS(true);
  const [assessment, setAssessment] = useS(null);
  const [qIdx, setQIdx]           = useS(0);
  const [answers, setAnswers]     = useS({});
  const [submitting, setSubmitting] = useS(false);
  const [result, setResult]       = useS(null);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!window.SRAPI || typeof window.SRAPI._get !== 'function') { setLoading(false); return; }
      try {
        const res = await window.SRAPI._get('/api/participant/assessments/active');
        if (!cancelled) setAssessment(res && res.assessment ? res.assessment : null);
      } catch (e) {
        if (!cancelled) setAssessment(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (loading) {
    return (
      <div style={sh.shell}>
        <div style={{...sh.scroll, display:'flex', alignItems:'center', justifyContent:'center'}}>
          <span style={{fontSize:12.5, color:T2.inkSoft, fontWeight:600}}>Loading assessment…</span>
        </div>
        <BottomNav active={active} role="participant" onNav={onNav}/>
      </div>
    );
  }

  if (!assessment) {
    return (
      <div style={sh.shell}>
        <div style={{...sh.scroll, display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', padding:24, textAlign:'center'}}>
          <I name="check" size={32} c={T2.sage} sw={1.6}/>
          <div style={{fontSize:15, fontWeight:700, color:T2.ink, marginTop:14}}>No assessment due</div>
          <div style={{fontSize:12.5, color:T2.inkSoft, marginTop:6}}>You're all caught up — check back when your trainer assigns a new one.</div>
        </div>
        <BottomNav active={active} role="participant" onNav={onNav}/>
      </div>
    );
  }

  const questions = assessment.questions || [];
  const total = questions.length;
  const q = questions[qIdx];
  const pct = total ? Math.round(((qIdx + 1) / total) * 100) : 0;
  const selected = q ? answers[q.id] : undefined;

  const submit = async () => {
    setSubmitting(true);
    try {
      const res = await window.SRAPI._post('/api/participant/assessments/' + assessment.id + '/submit', { answers });
      setResult(res);
    } catch (e) {
      setResult({ error: true });
    } finally {
      setSubmitting(false);
    }
  };

  if (result) {
    return (
      <div style={sh.shell}>
        <div style={{...sh.scroll, display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', padding:24, textAlign:'center'}}>
          {result.error ? (
            <>
              <div style={{fontSize:15, fontWeight:700, color:T2.rose}}>Couldn't submit</div>
              <div style={{fontSize:12.5, color:T2.inkSoft, marginTop:6}}>Check your connection and try again.</div>
              <button style={{...sh.btnPrimary, marginTop:16}} onClick={submit}>Retry</button>
            </>
          ) : (
            <>
              <div style={{fontSize:11, fontWeight:800, color:T2.gold, letterSpacing:'0.08em', textTransform:'uppercase'}}>Assessment complete</div>
              <div style={{fontSize:40, fontWeight:800, color:T2.navy, marginTop:10}}>{result.score}%</div>
              <div style={{fontSize:12.5, color:T2.inkSoft, marginTop:8}}>{assessment.title}</div>
              <button style={{...sh.btnPrimary, marginTop:20}} onClick={() => onNav && onNav('home')}>Back to home</button>
            </>
          )}
        </div>
        <BottomNav active={active} role="participant" onNav={onNav}/>
      </div>
    );
  }

  if (!q) return null;

  const options = Array.isArray(q.options) ? q.options : [];

  return (
    <div style={sh.shell}>
      <div style={sh.scroll}>
        {/* Header — soft sky-to-indigo gradient */}
        <div style={{background: T2.headerGrad, padding:'14px 14px 18px'}}>
          <div style={{display:'flex', alignItems:'center', gap:10, marginBottom:12}}>
            <div onClick={() => onNav && onNav('home')} style={{cursor:'pointer', flexShrink:0}}>
              <I name="chevL" size={18} c="#fff" sw={2.2}/>
            </div>
            <div style={{flex:1, minWidth:0, textAlign:'center', overflow:'hidden'}}>
              <Label c={T2.goldHi}>{assessment.capability || assessment.title}</Label>
              <div style={{fontSize:13, fontWeight:700, color:'#fff', marginTop:3, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis'}}>Question {qIdx + 1} of {total}</div>
            </div>
          </div>
          <Bar pct={pct} c={T2.goldHi} bg="rgba(255,255,255,0.18)" h={3}/>
        </div>

        {/* Question */}
        <div style={{padding:'24px 22px 16px'}}>
          <div style={{
            fontFamily:"'Lato', sans-serif",
            fontSize:20, fontWeight:600, color:T2.ink, lineHeight:1.35,
            letterSpacing:'-0.005em',
          }}>
            {q.text || q.question}
          </div>
        </div>

        {/* Options — flat, restrained */}
        <div style={{padding:'4px 16px 16px', display:'flex', flexDirection:'column', gap:8}}>
          {options.map((o, i) => {
            const optId = (o && typeof o === 'object') ? o.id : o;
            const optLabel = (o && typeof o === 'object') ? o.text : o;
            const sel = selected === optId;
            const k = String.fromCharCode(65 + i);
            return (
              <div key={optId != null ? optId : i}
                onClick={() => setAnswers(prev => ({ ...prev, [q.id]: optId }))}
                style={{
                  background: '#fff', cursor:'pointer',
                  border: sel ? `2px solid ${T2.navy}` : `1px solid ${T2.rule}`,
                  borderRadius: 10, padding: sel ? '11px 13px' : '12px 14px',
                  display:'flex', alignItems:'center', gap:12,
                }}>
                <div style={{
                  width:26, height:26, borderRadius:7, flexShrink:0,
                  background: sel ? T2.navy : 'transparent',
                  border: sel ? 'none' : `1.5px solid ${T2.rule}`,
                  color: sel ? '#fff' : T2.inkSoft,
                  display:'flex', alignItems:'center', justifyContent:'center',
                  fontWeight:800, fontSize:12,
                }}>{k}</div>
                <div style={{
                  flex:1, fontSize:13.5,
                  fontWeight: sel ? 700 : 500,
                  color: T2.ink, lineHeight:1.4,
                }}>{optLabel}</div>
                {sel && <I name="check" size={16} c={T2.navy} sw={2.6}/>}
              </div>
            );
          })}
        </div>

        <div style={{height: 110}}/>
      </div>

      {/* Bottom CTA */}
      <div style={{
        position:'absolute', bottom:0, left:0, right:0, zIndex:6,
        background:'#fff', borderTop:`1px solid ${T2.rule}`, padding:'14px 16px 28px',
        display:'flex', gap:10,
      }}>
        <button
          disabled={qIdx === 0}
          onClick={() => setQIdx(i => Math.max(0, i - 1))}
          style={{...sh.btnGhost, flex:1, opacity: qIdx === 0 ? 0.4 : 1}}>
          <I name="chevL" size={12} c={T2.navy} sw={2}/> Previous
        </button>
        {qIdx < total - 1 ? (
          <button
            disabled={selected === undefined}
            onClick={() => setQIdx(i => Math.min(total - 1, i + 1))}
            style={{...sh.btnPrimary, flex:1.4, opacity: selected === undefined ? 0.5 : 1}}>
            Next question <I name="chevR" size={12} c="#fff" sw={2}/>
          </button>
        ) : (
          <button
            disabled={selected === undefined || submitting}
            onClick={submit}
            style={{...sh.btnPrimary, flex:1.4, opacity: (selected === undefined || submitting) ? 0.5 : 1}}>
            {submitting ? 'Submitting…' : 'Submit'} <I name="chevR" size={12} c="#fff" sw={2}/>
          </button>
        )}
      </div>
    </div>
  );
}

// ============================================================
// 4. PARTICIPANT — Growth Map
// ============================================================
// Palette cycle for capability bars — stable by index, not tied to any
// particular capability name (the real list is workspace-defined).
const GROWTH_CAP_COLORS = [T2.navy, T2.teal, T2.sage, T2.rose, T2.amber];
// Wording for the server's completionStatus. The cutoffs behind it are set per
// batch (Programme settings), so the client never compares percentages itself.
const GROWTH_STATUS = {
  getting_started: { label: 'Getting started', c: T2.rose },
  in_progress:     { label: 'In progress',     c: T2.amber },
  on_track:        { label: 'On track',        c: T2.sage },
};
// Rank is by score on Growth and by progress on the leader's member view.
const RANK_BASIS_LABEL = { capability_score: 'by score', lesson_progress: 'by progress' };

function ScreenParticipantGrowth({ onNav, active = 'growth' }) {
  const [loading, setLoading] = useS(true);
  const [data, setData]       = useS(null);   // null = load failed / no SRAPI
  const [notEnrolled, setNotEnrolled] = useS(false);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!window.SRAPI || typeof window.SRAPI._get !== 'function') { setLoading(false); return; }
      try {
        const res = await window.SRAPI._get('/api/participant/growth');
        if (cancelled) return;
        setData(res || null);
      } catch (e) {
        if (cancelled) return;
        if (e && e.body && e.body.code === 'PARTICIPANT_NOT_FOUND') setNotEnrolled(true);
        else setData(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (loading) {
    return (
      <div style={sh.shell}>
        <div style={{...sh.scroll, display:'flex', alignItems:'center', justifyContent:'center'}}>
          <span style={{fontSize:12.5, color:T2.inkSoft, fontWeight:600}}>Loading your growth map…</span>
        </div>
        <BottomNav active={active} role="participant" onNav={onNav}/>
      </div>
    );
  }

  if (notEnrolled || !data) {
    return (
      <div style={sh.shell}>
        <div style={{...sh.scroll, display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', padding:24, textAlign:'center'}}>
          <I name="bolt" size={32} c={T2.inkFaint} sw={1.6}/>
          <div style={{fontSize:15, fontWeight:700, color:T2.ink, marginTop:14}}>
            {notEnrolled ? 'Not enrolled yet' : 'Could not load your growth map'}
          </div>
          <div style={{fontSize:12.5, color:T2.inkSoft, marginTop:6}}>
            {notEnrolled
              ? 'Contact your programme administrator to get set up.'
              : 'Check your connection and pull to refresh.'}
          </div>
        </div>
        <BottomNav active={active} role="participant" onNav={onNav}/>
      </div>
    );
  }

  const caps = (data.capabilities || []).map((c, i) => ({ ...c, c: GROWTH_CAP_COLORS[i % GROWTH_CAP_COLORS.length] }));
  const hasCaps = caps.length > 0;
  const hasPrev = caps.some(c => c.prevScore != null);
  const initials = String(data.participantName || '').split(' ').filter(Boolean).slice(0,2).map(w=>w[0]).join('').toUpperCase() || '·';
  const pm = data.programmeMonth;
  const subtitle = [data.batchName, pm ? `Month ${pm.current} of ${pm.total}` : null].filter(Boolean).join(' · ') || null;
  const status = GROWTH_STATUS[data.completionStatus];

  return (
    <div style={sh.shell}>
      <div style={sh.scroll}>
        <Header
          kicker="Growth Map"
          title="Your capability journey"
          sub={subtitle}
          onRight={()=>onNav&&onNav('me')}
          right={<Avatar initials={initials} size={34} bg="rgba(255,213,96,0.2)" fg={T2.goldHi}/>}
        />

        {/* Unified KPI strip — single row, dividers only. Any figure with no
            backing data shows '—', never a plausible-looking fake number. */}
        <div style={{padding:'18px 16px 0'}}>
          <div style={{
            display:'flex', borderBottom:`1px solid ${T2.rule}`, paddingBottom:18,
          }}>
            {[
              {v: data.overallScore != null ? data.overallScore.toFixed(2) : '—', l:'Overall',
               sub: data.overallDeltaPct != null ? `${data.overallDeltaPct > 0 ? '+' : ''}${data.overallDeltaPct}% growth`
                  : data.overallDelta != null ? `${data.overallDelta > 0 ? '+' : ''}${data.overallDelta.toFixed(2)}` : null,
               c: (data.overallDeltaPct ?? data.overallDelta) > 0 ? T2.sage : (data.overallDeltaPct ?? data.overallDelta) < 0 ? T2.rose : undefined},
              {v: data.rank != null ? `#${data.rank}` : '—', l:'Cohort rank',
               sub: data.rosterSize ? [`of ${data.rosterSize}`, RANK_BASIS_LABEL[data.rankBasis]].filter(Boolean).join(' · ') : null},
              {v: `${data.completionPct}%`, l:'Completion',
               sub: status ? status.label : null,
               c: status ? status.c : undefined},
            ].map((s, i, arr) => (
              <div key={i} style={{
                flex:1, padding:'0 4px',
                borderRight: i < arr.length - 1 ? `1px solid ${T2.ruleSoft}` : 'none',
                textAlign:'center',
              }}>
                <div style={{
                  fontFamily:"'Lato', sans-serif", fontSize:22, fontWeight:700,
                  color: T2.ink, letterSpacing:'-0.02em', lineHeight:1,
                }}>{s.v}</div>
                <Label mt={6}>{s.l}</Label>
                {s.sub && (
                  <div style={{fontSize:10.5, color: s.c || T2.inkSoft, fontWeight:700, marginTop:3}}>{s.sub}</div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Gap alert — real gap only, no fabricated recommendation text */}
        {data.gapCapability && (
          <div style={{padding:'14px 16px 0'}}>
            <div style={{
              padding:'12px 14px', background:T2.goldLt, borderRadius:10,
            }}>
              <div style={{display:'flex', alignItems:'center', gap:8, marginBottom:6}}>
                <I name="bolt" size={14} c={T2.gold} sw={2.2}/>
                <strong style={{fontSize:12.5, color:T2.navyDeep}}>Below cohort average: {data.gapCapability.label}</strong>
              </div>
              <div style={{fontSize:11.5, color:T2.inkMid, lineHeight:1.45}}>
                You're at {data.gapCapability.score.toFixed(1)}/5 vs cohort {data.gapCapability.batchAvg.toFixed(1)}. Ask your trainer for guidance on this area.
              </div>
            </div>
          </div>
        )}

        {/* Capability bars — flat, unified */}
        <Section kicker="Capabilities">
          {hasCaps ? (
            <>
              <div style={{padding:'0 16px', display:'flex', flexDirection:'column', gap:14}}>
                {caps.map(c => {
                  const pct = (c.score/5)*100;
                  const prevPct = c.prevScore != null ? (c.prevScore/5)*100 : null;
                  const avgPct = c.batchAvg != null ? (c.batchAvg/5)*100 : null;
                  return (
                    <div key={c.cap}>
                      <div style={{display:'flex', justifyContent:'space-between', alignItems:'baseline', marginBottom:7}}>
                        <span style={{fontSize:13.5, fontWeight:700, color:T2.ink}}>{c.label}</span>
                        <div style={{display:'flex', alignItems:'baseline', gap:8}}>
                          {c.delta != null && (
                            <span style={{
                              fontSize:10.5, fontWeight:700,
                              color: c.delta > 0 ? T2.sage : c.delta < 0 ? T2.rose : T2.inkSoft,
                            }}>
                              {c.delta > 0 ? '+' : ''}{c.delta.toFixed(1)}
                              {/* windows differ per capability, so say which one */}
                              {c.deltaWindow && <span style={{fontWeight:600, color:T2.inkSoft}}> · {c.deltaWindow}</span>}
                            </span>
                          )}
                          <span style={{
                            fontFamily:"'Lato', sans-serif", fontSize:17, fontWeight:700,
                            color: c.c, letterSpacing:'-0.02em',
                          }}>{c.score.toFixed(1)}</span>
                        </div>
                      </div>
                      <div style={{position:'relative', height:6, borderRadius:3, background:T2.ruleSoft, overflow:'visible'}}>
                        {/* Solid up to the lower of previous/current, then the change as its
                            own segment, so the previous score stays visible whichever way it moved. */}
                        <div style={{position:'absolute', left:0, top:0, height:'100%', borderRadius:3, width:`${prevPct != null ? Math.min(pct, prevPct) : pct}%`, background:c.c}}/>
                        {prevPct != null && pct !== prevPct && (
                          <div style={{position:'absolute', top:0, height:'100%', borderRadius:3,
                            left:`${Math.min(pct, prevPct)}%`, width:`${Math.abs(pct - prevPct)}%`,
                            background: pct > prevPct ? `${c.c}66` : `${T2.rose}55`}}/>
                        )}
                        {avgPct != null && (
                          <div style={{position:'absolute', left:`${avgPct}%`, top:-2, width:2, height:10, background:T2.inkMid, transform:'translateX(-1px)'}}/>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
              <div style={{fontSize:10.5, color:T2.inkSoft, padding:'12px 16px 0', display:'flex', gap:14}}>
                {hasPrev && (
                  <span><span style={{display:'inline-block', width:8, height:4, borderRadius:2, background:`${T2.navy}66`, verticalAlign:'middle', marginRight:5}}/>Change since previous</span>
                )}
                <span><span style={{display:'inline-block', width:2, height:8, background:T2.inkMid, verticalAlign:'middle', marginRight:5}}/>Cohort avg</span>
              </div>
            </>
          ) : (
            <div style={{padding:'0 16px', fontSize:12.5, color:T2.inkSoft}}>
              No capability scores recorded yet — these show up once your trainer records an assessment.
            </div>
          )}
        </Section>

        <div style={{height: 24}}/>
      </div>
      <BottomNav active={active} role="participant" onNav={onNav}/>
    </div>
  );
}

// ============================================================
// 5. PARTICIPANT — Me (programme & trainer)
// ============================================================
// ── Passkey Manager Modal ─────────────────────────────────────────────────────
function ModalPasskeyManager({ onClose }) {
  const base = (window.SRAPI && window.SRAPI._base) || 'http://localhost:3001/api';
  const authHdr = () => {
    const t = localStorage.getItem('sr_access_token') || '';
    return t ? { Authorization: `Bearer ${t}` } : {};
  };
  const [creds, setCreds]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [regName, setRegName] = useState('');
  const [regBusy, setRegBusy] = useState(false);
  const [toast, setToast]   = useState(null);
  const passkeySupported = !!(window.PublicKeyCredential);

  const showToast = (m) => { setToast(m); setTimeout(()=>setToast(null), 2500); };

  const load = async () => {
    try {
      const r = await fetch(`${base}/auth/passkey/credentials`, { headers: authHdr() });
      if (r.ok) setCreds(await r.json());
    } finally { setLoading(false); }
  };
  React.useEffect(() => { load(); }, []);

  const handleRegister = async () => {
    if (!passkeySupported) return;
    setRegBusy(true);
    try {
      const optR = await fetch(`${base}/auth/passkey/register-options`, {
        method:'POST', headers:{ 'Content-Type':'application/json', ...authHdr() },
      });
      if (!optR.ok) throw new Error('Could not start registration');
      const options = await optR.json();

      // Decode options for native API
      const toBytes = b64 => Uint8Array.from(atob(b64.replace(/-/g,'+').replace(/_/g,'/')), c=>c.charCodeAt(0));
      const pubKeyOptions = {
        ...options,
        challenge: toBytes(options.challenge),
        user: { ...options.user, id: toBytes(options.user.id) },
        excludeCredentials: (options.excludeCredentials||[]).map(c=>({...c,id:toBytes(c.id)})),
      };

      const cred = await navigator.credentials.create({ publicKey: pubKeyOptions });
      const toB64 = buf => btoa(String.fromCharCode(...new Uint8Array(buf))).replace(/\+/g,'-').replace(/\//g,'_').replace(/=/g,'');

      const credential = {
        id: cred.id,
        rawId: toB64(cred.rawId),
        type: cred.type,
        response: {
          attestationObject: toB64(cred.response.attestationObject),
          clientDataJSON: toB64(cred.response.clientDataJSON),
          transports: cred.response.getTransports ? cred.response.getTransports() : [],
        },
      };

      const verR = await fetch(`${base}/auth/passkey/register-verify`, {
        method:'POST',
        headers:{ 'Content-Type':'application/json', ...authHdr() },
        body: JSON.stringify({ credential, name: regName || 'My passkey' }),
      });
      if (!verR.ok) throw new Error((await verR.json().catch(()=>({}))).error || 'Registration failed');
      showToast('✓ Passkey registered');
      setRegName('');
      await load();
    } catch (err) {
      if (err && err.name !== 'NotAllowedError') showToast(err.message || 'Registration failed');
    } finally { setRegBusy(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Remove this passkey?')) return;
    const r = await fetch(`${base}/auth/passkey/credentials/${id}`, { method:'DELETE', headers: authHdr() });
    if (r.ok) { showToast('Passkey removed'); await load(); }
  };

  const fmt = d => d ? new Date(d).toLocaleDateString('en-IN', {day:'numeric',month:'short',year:'numeric'}) : '—';

  return (
    <div style={{ position:'fixed', inset:0, zIndex:200, background:'rgba(0,0,0,0.55)', display:'flex', flexDirection:'column', justifyContent:'flex-end' }}>
      <div style={{ background:'#fff', borderRadius:'20px 20px 0 0', padding:'20px 18px 36px', maxHeight:'80vh', overflowY:'auto' }}>
        {/* Handle */}
        <div style={{ width:36, height:4, background:'#E5E7EB', borderRadius:2, margin:'0 auto 16px' }} />
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:18 }}>
          <div>
            <div style={{ fontFamily:'var(--pf-serif)', fontSize:19, fontWeight:700, color:'#0C1A2E' }}>Passkeys &amp; Security</div>
            <div style={{ fontSize:12.5, color:'#6B7280', marginTop:3 }}>Sign in without a password using Face ID or fingerprint</div>
          </div>
          <button onClick={onClose} style={{ background:'none', border:'none', cursor:'pointer', fontSize:22, color:'#6B7280', lineHeight:1 }}>✕</button>
        </div>

        {toast && (
          <div style={{ marginBottom:14, padding:'10px 14px', borderRadius:10, background:'#f0fdf4', border:'1px solid #bbf7d0', fontSize:13, color:'#15803d' }}>
            {toast}
          </div>
        )}

        {!passkeySupported && (
          <div style={{ padding:'12px 14px', borderRadius:10, background:'#fefce8', border:'1px solid #fde68a', fontSize:13, color:'#92400e', marginBottom:14 }}>
            ⚠️ Passkeys not supported on this browser/device.
          </div>
        )}

        {passkeySupported && (
          <div style={{ marginBottom:20 }}>
            <div style={{ fontSize:11.5, fontWeight:700, color:'#374151', textTransform:'uppercase', letterSpacing:'.05em', marginBottom:8 }}>Add new passkey</div>
            <input
              value={regName} onChange={e=>setRegName(e.target.value)}
              placeholder="Name (e.g. iPhone 15, FaceID)"
              style={{ width:'100%', padding:'11px 12px', border:'1.5px solid #E5E7EB', borderRadius:10, fontSize:14, color:'#111827', boxSizing:'border-box', marginBottom:10, outline:'none' }}
            />
            <button onClick={handleRegister} disabled={regBusy} style={{
              width:'100%', padding:'13px', borderRadius:12, border:'none',
              background: regBusy ? '#9aa29d' : 'var(--pf-green)',
              color:'#fff', fontFamily:'var(--pf-sans)', fontWeight:700, fontSize:14,
              cursor: regBusy ? 'wait' : 'pointer',
              display:'flex', alignItems:'center', justifyContent:'center', gap:8,
            }}>
              {regBusy
                ? <><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"><animateTransform attributeName="transform" type="rotate" from="0 12 12" to="360 12 12" dur="0.75s" repeatCount="indefinite"/></path></svg>Registering…</>
                : <>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M12 10a2 2 0 1 0 0-4 2 2 0 0 0 0 4z"/><path d="M12 10v2"/><path d="M10.5 16l-1-3h5l-1 3"/>
                      <path d="M8 20.5A8.5 8.5 0 1 1 20.5 12"/><path d="M16 16l2 2 4-4"/>
                    </svg>
                    Register with Face ID / Fingerprint
                  </>
              }
            </button>
          </div>
        )}

        <div style={{ fontSize:11.5, fontWeight:700, color:'#374151', textTransform:'uppercase', letterSpacing:'.05em', marginBottom:10 }}>
          Your passkeys
        </div>
        {loading ? (
          <div style={{ fontSize:13, color:'#9CA3AF', padding:'12px 0' }}>Loading…</div>
        ) : creds.length === 0 ? (
          <div style={{ fontSize:13, color:'#9CA3AF', textAlign:'center', padding:'20px 0' }}>
            No passkeys yet. Add one above.
          </div>
        ) : (
          <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
            {creds.map(c => (
              <div key={c.id} style={{ display:'flex', alignItems:'center', gap:12, padding:'12px 14px', borderRadius:12, border:'1px solid #E5E7EB', background:'#F9FAFB' }}>
                <span style={{ fontSize:20 }}>{c.device_type==='multiDevice'?'☁️':'📱'}</span>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ fontWeight:700, fontSize:13.5, color:'#111827' }}>{c.name}</div>
                  <div style={{ fontSize:11, color:'#9CA3AF', marginTop:2 }}>
                    Added {fmt(c.created_at)}
                    {c.last_used_at ? ` · Used ${fmt(c.last_used_at)}` : ''}
                  </div>
                </div>
                <button onClick={()=>handleDelete(c.id)} style={{ padding:'6px 10px', borderRadius:8, border:'1px solid #fecaca', background:'#fff1f0', color:'#dc2626', fontSize:12, cursor:'pointer' }}>
                  Remove
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================================
// PARTICIPANT — Sessions (live + past, RSVP)
// ============================================================
// GET/PATCH /api/participant/sessions already existed and shipped with no
// mobile caller at all — the Home screen's "Upcoming sessions" card and its
// "See all" link both call onNav('sessions'), which resolved to
// window.ScreenParticipantSessions, a global nothing ever assigned. That
// was a live crash on every real participant device tapping either one,
// not just an unbuilt feature. Ports the design of the already-working web
// page at frontend/src/app/(main)/skalecap/sessions/page.tsx.
function ScreenParticipantSessions({ onNav, active = 'home' }) {
  const [sessions, setSessions] = useS([]);
  const [status, setStatus] = useS('loading'); // loading | ready | error

  const load = React.useCallback(() => {
    setStatus('loading');
    if (!window.SRAPI || typeof window.SRAPI._get !== 'function') { setStatus('error'); return; }
    window.SRAPI._get('/api/participant/sessions')
      .then(res => { setSessions((res && res.sessions) || []); setStatus('ready'); })
      .catch(() => setStatus('error'));
  }, []);
  React.useEffect(() => { load(); }, [load]);

  const isPast = (iso) => !!iso && new Date(iso).getTime() < Date.now();
  const fmtDateTime = (iso) => !iso ? 'Time not set' : new Date(iso).toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });

  const handleRsvp = async (id, nextStatus) => {
    setSessions(list => list.map(s => s.id === id ? { ...s, rsvp_status: nextStatus } : s));
    try {
      await window.SRAPI._patch('/api/participant/sessions/' + id + '/rsvp', { status: nextStatus });
    } catch (e) {
      setSessions(list => list.map(s => s.id === id ? { ...s, rsvp_status: nextStatus === 'going' ? 'not_going' : 'going' } : s));
    }
  };

  const upcoming = sessions.filter(s => !isPast(s.starts_at)).sort((a, b) => (a.starts_at || '').localeCompare(b.starts_at || ''));
  const past = sessions.filter(s => isPast(s.starts_at)).sort((a, b) => (b.starts_at || '').localeCompare(a.starts_at || ''));

  const SessionCard = ({ s }) => {
    const going = s.rsvp_status === 'going';
    const p = isPast(s.starts_at);
    const d = s.starts_at ? new Date(s.starts_at) : null;
    return (
      <div style={{ background: T2.paper, border: `1px solid ${T2.rule}`, borderRadius: 14, padding: 14, marginBottom: 10, display: 'flex', gap: 12, opacity: p ? 0.7 : 1 }}>
        {d ? <DateBlock dow={d.toLocaleDateString('en-IN', { weekday: 'short' }).toUpperCase()} d={d.getDate()} /> : <div style={{ width: 44, flexShrink: 0 }} />}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 800, color: T2.ink }}>{s.title}</div>
          <div style={{ fontSize: 11.5, color: T2.inkMid, marginTop: 3, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
            <I name="clock" size={11} c={T2.inkSoft} /> {fmtDateTime(s.starts_at)}
            {s.host && <span>· {s.host}</span>}
          </div>
          {(s.batch_name || s.roster_size > 0) && (
            <div style={{ fontSize: 11, color: T2.inkSoft, marginTop: 3, display: 'flex', alignItems: 'center', gap: 8 }}>
              {s.batch_name && <span>{s.batch_name}</span>}
              {s.roster_size > 0 && <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}><I name="team" size={11} c={T2.inkSoft} /> {s.roster_size}</span>}
            </div>
          )}
          <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
            {!p && s.join_url && (
              <a href={s.join_url} target="_blank" rel="noopener noreferrer" style={{ ...sh.btnPrimary, padding: '7px 14px', fontSize: 12, textDecoration: 'none' }}>
                <I name="play" size={12} c="#fff" /> Join
              </a>
            )}
            {p && s.recording_url && (
              <a href={s.recording_url} target="_blank" rel="noopener noreferrer" style={{ fontSize: 12, fontWeight: 700, color: T2.navy, alignSelf: 'center' }}>Watch recording</a>
            )}
            {!p && (
              <button onClick={() => handleRsvp(s.id, going ? 'not_going' : 'going')}
                style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '7px 12px', borderRadius: 20, border: going ? 'none' : `1.5px solid ${T2.navy}`, background: going ? T2.sageLt : 'transparent', color: going ? T2.sage : T2.navy, fontSize: 11, fontWeight: 800, letterSpacing: '0.04em', textTransform: 'uppercase', cursor: 'pointer' }}>
                {going && <I name="check" size={11} c={T2.sage} />} {going ? 'Going' : 'RSVP'}
              </button>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div style={sh.shell}>
      <Header kicker="SkaleCap" title="Live Sessions" sub="Join scheduled sessions, RSVP, and catch up on recordings" />
      <div style={sh.scroll}>
        {status === 'loading' && (
          <div style={{ padding: 60, textAlign: 'center', color: T2.inkSoft, fontSize: 12.5 }}>Loading…</div>
        )}
        {status === 'error' && (
          <div style={{ padding: '40px 24px', textAlign: 'center' }}>
            <div style={{ fontSize: 13, color: T2.inkSoft, marginBottom: 14 }}>Couldn't load your sessions. Check your connection and try again.</div>
            <button style={sh.btnPrimary} onClick={load}>Retry</button>
          </div>
        )}
        {status === 'ready' && (
          <React.Fragment>
            <Section kicker={`Upcoming (${upcoming.length})`}>
              {upcoming.length === 0 ? (
                <div style={{ margin: '0 16px', border: `1px dashed ${T2.rule}`, borderRadius: 12, padding: '18px 14px', textAlign: 'center', fontSize: 12.5, color: T2.inkSoft }}>No sessions scheduled yet.</div>
              ) : (
                <div style={{ padding: '0 16px' }}>{upcoming.map(s => <SessionCard key={s.id} s={s} />)}</div>
              )}
            </Section>
            {past.length > 0 && (
              <Section kicker="Past sessions">
                <div style={{ padding: '0 16px' }}>{past.map(s => <SessionCard key={s.id} s={s} />)}</div>
              </Section>
            )}
          </React.Fragment>
        )}
      </div>
      <BottomNav active={active} role="participant" onNav={onNav} />
    </div>
  );
}

const CAL_PROVIDER_KEY = 'sc_cal_provider';
function ScreenParticipantMe({ onNav, active = 'me' }) {
  // Wired against GET /api/participant/profile + /api/participant/home. Was
  // "Priya Mehta / Senior Manager / Mar 24 → Aug 18 / 14-day streak / Dr. Neha
  // Gupta ★4.8 / two invented focus-area gaps" — all fixture.
  const [pingOpen,  setPingOpen]  = useS(false);
  const [notifOpen, setNotifOpen] = useS(false);
  const [calOpen,   setCalOpen]   = useS(false);
  const [passkeyOpen, setPasskeyOpen] = useS(false);
  const [profile, setProfile] = useS(null);
  const [home, setHome] = useS(null);
  const [loading, setLoading] = useS(true);
  const [calProvider, setCalProvider] = useS(() => {
    try { return localStorage.getItem(CAL_PROVIDER_KEY) || ''; } catch { return ''; }
  });
  const calLabel = {google:'Google', outlook:'Outlook', apple:'Apple'}[calProvider] || '';
  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!window.SRAPI || typeof window.SRAPI._get !== 'function') { setLoading(false); return; }
      const [p, h] = await Promise.all([
        window.SRAPI._get('/api/participant/profile').catch(() => null),
        window.SRAPI._get('/api/participant/home').catch(() => null),
      ]);
      if (cancelled) return;
      setProfile(p); setHome(h); setLoading(false);
    })();
    return () => { cancelled = true; };
  }, []);
  const stored = (() => { try { return JSON.parse(localStorage.getItem('sr_user') || 'null') || {}; } catch { return {}; } })();
  const name = profile?.name || home?.user?.name || stored.name || stored.email || '';
  const subline = profile?.title || profile?.email || stored.email || '';
  const initials = String(name).split(' ').filter(Boolean).slice(0,2).map(w=>w[0]).join('').toUpperCase() || '·';
  const prog = home?.progress || null;
  const caps = Array.isArray(home?.capabilities) ? home.capabilities : [];
  const focus = caps.slice().filter(c => c && c.score != null).sort((a,b)=>(Number(a.score)||0)-(Number(b.score)||0)).slice(0,2);
  const stats = [
    {v: loading ? '…' : (home?.cohort?.name ? '✓' : '—'), l: home?.cohort?.name ? 'Enrolled' : 'No cohort'},
    {v: loading ? '…' : (prog ? prog.done : '—'), l:'Topics done'},
    {v: loading ? '…' : (prog ? prog.total : '—'), l:'Topics total', c:T2.gold},
  ];
  return (
    <div style={sh.shell}>
      {pingOpen    && <window.ModalPingTrainer         onClose={()=>setPingOpen(false)}/>}
      {notifOpen   && <window.ModalNotificationSettings onClose={()=>setNotifOpen(false)}/>}
      {calOpen     && <window.ModalCalendarSync calProvider={calProvider} onSync={(p)=>{ setCalProvider(p); try{localStorage.setItem(CAL_PROVIDER_KEY,p);}catch{} }} onClose={()=>setCalOpen(false)}/>}
      {passkeyOpen && <ModalPasskeyManager onClose={()=>setPasskeyOpen(false)}/>}
      <div style={sh.scroll}>
        <Header
          kicker="My Programme"
          title={loading && !name ? 'Loading…' : (name || 'Participant')}
          sub={<>{subline}</>}
          right={<Avatar initials={initials} size={36} bg="rgba(255,213,96,0.2)" fg={T2.goldHi}/>}
        />

        {/* Stats inline */}
        <div style={{padding:'18px 16px 0'}}>
          <div style={{
            display:'flex', borderBottom:`1px solid ${T2.rule}`, paddingBottom:18,
          }}>
            {stats.map((s, i, arr) => (
              <div key={i} style={{
                flex:1, textAlign:'center',
                borderRight: i < arr.length - 1 ? `1px solid ${T2.ruleSoft}` : 'none',
              }}>
                <div style={{
                  fontFamily:"'Lato', sans-serif", fontSize:17, fontWeight:700,
                  color: s.c || T2.ink, letterSpacing:'-0.01em', lineHeight:1,
                }}>{s.v}</div>
                <Label mt={6}>{s.l}</Label>
              </div>
            ))}
          </div>
        </div>

        {/* Cohort + trainer contact */}
        <Section kicker="My cohort">
          <div style={{padding:'0 16px'}}>
            <div style={{
              border:`1px solid ${T2.rule}`, borderRadius:12, padding:'12px 14px',
              display:'flex', alignItems:'center', gap:12,
            }}>
              <div style={{width:44,height:44,borderRadius:'50%',background:T2.navyLt,display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}><I name="team" size={18} c={T2.navy} sw={2}/></div>
              <div style={{flex:1,minWidth:0}}>
                <div style={{fontSize:14, fontWeight:700, color:T2.ink}}>{loading ? 'Loading…' : (home?.cohort?.name || 'Not enrolled yet')}</div>
                <div style={{fontSize:11.5, color:T2.inkSoft, marginTop:2}}>{home?.cohort?.name ? 'Questions? Ping your trainer.' : 'Ask your programme administrator for a join link.'}</div>
              </div>
              {home?.cohort?.name && (
                <button onClick={()=>setPingOpen(true)} style={{...sh.btnGhost, cursor:'pointer'}}>
                  <I name="msg" size={13} c={T2.navy} sw={2}/> Ping
                </button>
              )}
            </div>
          </div>
        </Section>

        {/* Focus areas — lowest two capability scores from the live assessment data */}
        {focus.length > 0 && (
        <Section kicker="My focus areas">
          <div style={{padding:'0 16px', display:'flex', flexDirection:'column', gap:6}}>
            {focus.map((g, i) => (
              <div key={i} onClick={()=>onNav&&onNav('personalised')} style={{
                border:`1px solid ${T2.rule}`, borderRadius:10, padding:'11px 13px',
                display:'flex', alignItems:'center', gap:11, cursor:'pointer',
              }}>
                <I name="bolt" size={14} c={T2.gold} sw={2.2}/>
                <div style={{flex:1}}>
                  <div style={{fontSize:13, fontWeight:700, color:T2.ink}}>{g.name || g.label || g.capability || 'Capability'}</div>
                  <div style={{fontSize:11, color:T2.inkSoft, marginTop:1}}>Score {Number(g.score).toFixed(1)}{g.cohort_avg != null ? ` vs cohort ${Number(g.cohort_avg).toFixed(1)}` : ''}</div>
                </div>
                <span style={{fontSize:11, fontWeight:800, color:T2.navy, letterSpacing:'0.06em', textTransform:'uppercase', display:'flex', alignItems:'center', gap:3}}>Practice <I name="chevR" size={11} c={T2.navy} sw={2}/></span>
              </div>
            ))}
          </div>
        </Section>
        )}

        {/* Account */}
        <Section kicker="Account">
          <div style={{padding:'0 16px'}}>
            <div style={{border:`1px solid ${T2.rule}`, borderRadius:12, overflow:'hidden'}}>
              {[
                {l:'Edit profile',    id:'edit'},
                {l:'Notifications',  v:'On',      action:()=>setNotifOpen(true)},
                {l:'Calendar sync',  v:calLabel,  action:()=>setCalOpen(true)},
                {l:'Passkeys & Security', v:'', action:()=>setPasskeyOpen(true)},
                {l:'Sign out',       id:'logout', danger:true},
              ].map((it, i, a) => (
                <div key={i} onClick={() => it.action ? it.action() : it.id && onNav && onNav(it.id)} style={{
                  padding:'13px 14px', display:'flex', alignItems:'center',
                  borderBottom: i < a.length - 1 ? `1px solid ${T2.ruleSoft}` : 'none',
                  cursor:'pointer',
                }}>
                  <span style={{flex:1, fontSize:13, color: it.danger ? T2.rose : T2.ink, fontWeight: it.danger ? 700 : 500}}>{it.l}</span>
                  {it.v && <span style={{fontSize:12, color:T2.inkSoft, marginRight:8}}>{it.v}</span>}
                  <I name="chevR" size={13} c={it.danger ? T2.rose : T2.inkFaint} sw={2}/>
                </div>
              ))}
            </div>
          </div>
        </Section>

        <div style={{height: 24}}/>
      </div>
      <BottomNav active={active} role="participant" onNav={onNav}/>
    </div>
  );
}

// ============================================================
// 6. LEADER — Overview
// ============================================================
function ModalNudge({ target, onClose }) {
  const first = target?.n?.split(' ')[0] || 'them';
  const [msg, setMsg] = useS('');
  const [status, setStatus] = useS('idle'); // idle | sending | sent | error
  const [resultMsg, setResultMsg] = useS('');
  const templates = [
    `Hey ${first}, just checking in — your module completion is below target. Need any support?`,
    `Quick reminder: this week's assessment is due soon. You've got this, ${first}! 💪`,
    `I noticed some gaps in ${target?.gap||'your recent modules'}. Let's schedule a quick 15-min check-in?`,
  ];

  const send = async () => {
    if (!msg.trim() || !target?.id) return;
    setStatus('sending');
    try {
      const res = await window.SRAPI._post('/api/leader/participants/' + target.id + '/nudge', { message: msg.trim() });
      if (res?.sent) {
        setStatus('sent');
        setResultMsg(`${first} has been notified.`);
        setTimeout(onClose, 2400);
      } else {
        setStatus('error');
        setResultMsg(res?.message || 'Could not deliver — no device registered.');
      }
    } catch (e) {
      setStatus('error');
      setResultMsg(e?.message || 'Could not send — please try again.');
    }
  };

  if (status === 'sent') return (
    <div onClick={onClose} style={{ position:'absolute', inset:0, zIndex:40, background:'rgba(0,0,0,0.5)', display:'flex', alignItems:'center', justifyContent:'center' }}>
      <div style={{ background:'#fff', borderRadius:20, padding:'32px 28px', textAlign:'center', margin:'0 28px' }}>
        <div style={{ width:60, height:60, borderRadius:'50%', background:T2.sageLt, margin:'0 auto 16px', display:'flex', alignItems:'center', justifyContent:'center' }}><I name="check" size={24} c={T2.sage} sw={2.8}/></div>
        <div style={{ fontSize:18, fontWeight:800, color:T2.ink, marginBottom:8 }}>Nudge sent!</div>
        <div style={{ fontSize:13, color:T2.inkSoft, lineHeight:1.5 }}>{resultMsg}</div>
      </div>
    </div>
  );
  return (
    <div onClick={onClose} style={{ position:'absolute', inset:0, zIndex:40, background:'rgba(0,0,0,0.45)', display:'flex', flexDirection:'column', justifyContent:'flex-end' }}>
      <div onClick={e=>e.stopPropagation()} style={{ background:'#fff', borderRadius:'20px 20px 0 0', padding:'0 0 24px' }}>
        <div style={{ padding:'10px 16px 12px', borderBottom:`1px solid ${T2.rule}`, display:'flex', alignItems:'center', position:'relative' }}>
          <div style={{ width:40, height:4, borderRadius:2, background:T2.rule, position:'absolute', left:'50%', top:8, transform:'translateX(-50%)' }}/>
          <div style={{ flex:1, marginTop:4 }}>
            <div style={{ fontSize:15, fontWeight:800, color:T2.ink }}>Nudge {target?.n}</div>
            <div style={{ fontSize:11, color:T2.rose, fontWeight:700, marginTop:2 }}>{target?.gap} · {target?.comp}% complete</div>
          </div>
          <div onClick={onClose} style={{ cursor:'pointer', width:28, height:28, borderRadius:'50%', background:T2.ruleSoft, display:'flex', alignItems:'center', justifyContent:'center' }}><div style={{ transform:'rotate(45deg)', display:'flex' }}><I name="plus" size={14} c={T2.inkSoft} sw={2.4}/></div></div>
        </div>
        <div style={{ padding:'16px 16px 0' }}>
          <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:14, padding:'10px 12px', background:T2.roseFaint, borderRadius:10 }}>
            <Avatar initials={target?.i} size={36} bg={T2.rose} fg="#fff"/>
            <div>
              <div style={{ fontSize:12, fontWeight:700, color:T2.ink }}>{target?.n}</div>
            </div>
          </div>
          <div style={{ fontSize:12, fontWeight:800, color:T2.inkSoft, letterSpacing:'0.08em', marginBottom:10, textTransform:'uppercase' }}>Quick templates</div>
          {templates.map((t,i)=>(
            <div key={i} onClick={()=>setMsg(t)} style={{ padding:'10px 13px', borderRadius:10, border:`1.5px solid ${msg===t?T2.navy:T2.rule}`, marginBottom:8, cursor:'pointer', background:msg===t?T2.navyLt:'#fff' }}>
              <div style={{ fontSize:12.5, fontWeight:600, color:msg===t?T2.navy:T2.ink, lineHeight:1.4 }}>{t}</div>
            </div>
          ))}
          <textarea value={msg} onChange={e=>setMsg(e.target.value)} placeholder="Or write a custom message…" rows={3}
            style={{ width:'100%', padding:'10px 13px', border:`1.5px solid ${msg?T2.navy:T2.rule}`, borderRadius:10, fontSize:13, color:T2.ink, outline:'none', fontFamily:'inherit', background:T2.page, boxSizing:'border-box', resize:'none', marginTop:2 }}/>
          {status === 'error' && (
            <div style={{ marginTop:10, fontSize:12, color:T2.rose }}>{resultMsg}</div>
          )}
        </div>
        <div style={{ padding:'12px 16px 0' }}>
          <button onClick={send} disabled={!msg.trim() || status==='sending'}
            style={{ width:'100%', padding:'14px', borderRadius:12, border:'none', background:msg.trim()?T2.rose:T2.ruleSoft, color:msg.trim()?'#fff':T2.inkFaint, fontSize:15, fontWeight:800, fontFamily:'inherit', cursor:msg.trim()?'pointer':'default', opacity: status==='sending' ? 0.7 : 1 }}>
            {status==='sending' ? 'Sending…' : 'Send nudge'}
          </button>
        </div>
      </div>
    </div>
  );
}

function ScreenLeaderOverview({ onNav, active = 'home' }) {
  const [profileOpen, setProfileOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [cohort, setCohort] = useState(null);   // from /leader/cohorts[0]
  const [report, setReport] = useState(null);   // from /skalecap/batches/:id/report
  const [roster, setRoster] = useState(null);   // from /leader/cohorts/:id/roster (status uses the batch's cutoffs)
  const [nextSession, setNextSession] = useState(null); // from /leader/sessions
  const navToFilter = (f) => { window._cohortFilter = f; onNav && onNav('team'); };
  const me = (() => { try { return JSON.parse(localStorage.getItem('sr_user') || 'null') || {}; } catch { return {}; } })();
  const myInitials = String(me.name || me.email || '').split(' ').filter(Boolean).slice(0,2).map(w=>w[0]).join('').toUpperCase() || '·';

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!window.SRAPI || typeof window.SRAPI._get !== 'function') { setLoading(false); return; }
      try {
        const cData = await window.SRAPI._get('/api/leader/cohorts');
        const first = cData?.cohorts?.[0] || null;
        if (cancelled) return;
        setCohort(first);
        window._leaderBatchId = first?.id || window._leaderBatchId;
        if (first) {
          const [reportData, rosterData, sessionsData] = await Promise.all([
            window.SRAPI._get('/api/skalecap/batches/' + first.id + '/report').catch(() => null),
            window.SRAPI._get('/api/leader/cohorts/' + first.id + '/roster').catch(() => null),
            window.SRAPI._get('/api/leader/sessions').catch(() => null),
          ]);
          if (cancelled) return;
          setReport(reportData);
          setRoster(rosterData);
          const upcoming = (sessionsData?.sessions || [])
            .filter(s => s.status !== 'completed' && s.status !== 'cancelled')
            .sort((a, b) => new Date(a.starts_at) - new Date(b.starts_at));
          setNextSession(upcoming[0] || null);
        }
      } catch (e) { /* leave defaults, screen shows honest empty states */ }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, []);

  // At risk = the roster's own status, so Overview, Cohort and the cohort
  // report agree and follow the batch's cutoffs (was a fixed < 40 here).
  const atRisk = (roster?.roster || []).filter(p => p.status === 'at_risk');
  const onTrackCount = roster ? Math.max(0, roster.total - atRisk.length) : 0;
  const dateRange = cohort?.start_date && cohort?.end_date
    ? new Date(cohort.start_date).toLocaleDateString('en-IN', { month:'short', year:'numeric' }) + '–' + new Date(cohort.end_date).toLocaleDateString('en-IN', { month:'short', year:'numeric' })
    : null;

  return (
    <div style={sh.shell}>
      {notifOpen && <window.ModalNotificationSettings onClose={()=>setNotifOpen(false)}/>}
      <div style={sh.scroll}>
        {/* Header — soft sky-to-indigo gradient */}
        <div style={{background: T2.headerGrad, padding:'12px 14px 18px', color:'#fff'}}>
          <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', gap:10, marginBottom:12}}>
            <div style={{flex:'1 1 auto', minWidth:0, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis'}}>
              <Label c={T2.goldHi}>L&amp;D Console · HR View</Label>
            </div>
            <div style={{display:'flex', gap:8, alignItems:'center', flexShrink:0}}>
              {/* Standardized: both 36 circles */}
              <button onClick={()=>setNotifOpen(true)} style={{
                width:36, height:36, borderRadius:'50%',
                background:'rgba(255,255,255,0.08)',
                border:'1px solid rgba(255,255,255,0.16)',
                display:'flex', alignItems:'center', justifyContent:'center',
                cursor:'pointer', padding:0,
              }}>
                <I name="bell" size={15} c="#fff" sw={2}/>
              </button>
              <div style={{position:'relative'}}>
                <button onClick={()=>setProfileOpen(o=>!o)} style={{background:'none',border:'none',padding:0,cursor:'pointer',display:'flex'}}>
                  <Avatar initials={myInitials} size={36} bg={T2.goldHi} fg={T2.navy}/>
                </button>
                {profileOpen && (
                  <div style={{
                    position:'absolute', top:42, right:0, zIndex:200,
                    background:'#fff', borderRadius:12, boxShadow:'0 8px 32px rgba(0,0,0,0.18)',
                    minWidth:160, padding:'8px 0', border:'1px solid rgba(0,0,0,0.08)',
                  }}>
                    {/* The only way into the leader Me screen (export, trainer roster, programme settings). */}
                    <button onClick={()=>{ setProfileOpen(false); onNav && onNav('me'); }}
                      style={{
                        display:'flex', alignItems:'center', gap:10, width:'100%',
                        background:'none', border:'none', padding:'11px 16px',
                        cursor:'pointer', color:T2.ink, fontSize:14, fontWeight:500, whiteSpace:'nowrap',
                      }}>
                      <I name="person" size={16} c={T2.ink} sw={2}/>
                      Profile &amp; settings
                    </button>
                    <button onClick={()=>{ setProfileOpen(false); document.dispatchEvent(new CustomEvent('sr:logout')); }}
                      style={{
                        display:'flex', alignItems:'center', gap:10, width:'100%',
                        background:'none', border:'none', padding:'11px 16px',
                        cursor:'pointer', color:'#dc2626', fontSize:14, fontWeight:500,
                      }}>
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
                      Sign out
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
          <div style={{
            fontFamily:"'Lato', sans-serif", fontSize:24, fontWeight:700,
            letterSpacing:'-0.018em', lineHeight:1.2, color:'#fff', textWrap:'balance',
          }}>{loading ? 'Loading…' : (cohort?.name || 'No cohort assigned')}</div>
          {cohort && (
            <div style={{
              fontSize:12, color:'rgba(255,255,255,0.88)', marginTop:10,
              display:'flex', alignItems:'center', gap:6,
            }}>
              <I name="team" size={12} c="rgba(255,255,255,0.88)" sw={2}/>
              {report?.participants ?? cohort.participant_count ?? 0} participants{dateRange ? ' · ' + dateRange : ''}
            </div>
          )}
        </div>

        {/* Unified KPI grid — one container, dividers, no icon boxes */}
        {cohort && (
        <div style={{padding:'18px 16px 0'}}>
          <div style={{
            border:`1px solid ${T2.rule}`, borderRadius:12, overflow:'hidden',
            display:'grid', gridTemplateColumns:'1fr 1fr',
          }}>
            {[
              {v: report ? `${report.avg_viewed_pct}%` : '—', l:'Avg completion', sub: report ? `${report.sessions_completed}/${report.sessions_total} sessions held` : '', c:T2.sage},
              {v: (report && report.avg_assessment_score != null && Number.isFinite(Number(report.avg_assessment_score))) ? String(Math.round(Number(report.avg_assessment_score))) : '—', l:'Assessment score', sub: report ? ((report.avg_assessment_score != null && Number.isFinite(Number(report.avg_assessment_score))) ? 'avg of scored assessments, /100' : 'no assessment yet') : ''},
              {v: roster ? String(onTrackCount) : '—', l:'On track', sub: roster ? `of ${roster.total} →` : '', nav:'on'},
              {v: roster ? String(atRisk.length) : '—', l:'At risk', sub: roster ? `Under ${roster.completionCutoffs.atRiskBelow}% or below avg →` : '', c:T2.rose, nav:'risk'},
            ].map((k, i) => (
              <div key={i} onClick={k.nav ? ()=>navToFilter(k.nav) : undefined} style={{
                padding:'14px 14px', position:'relative',
                cursor: k.nav ? 'pointer' : 'default',
                borderRight: i % 2 === 0 ? `1px solid ${T2.ruleSoft}` : 'none',
                borderBottom: i < 2 ? `1px solid ${T2.ruleSoft}` : 'none',
              }}>
                <div style={{
                  fontFamily:"'Lato', sans-serif", fontSize:24, fontWeight:700,
                  color: k.c || T2.ink, letterSpacing:'-0.02em', lineHeight:1,
                }}>{k.v}</div>
                <Label mt={6}>{k.l}</Label>
                <div style={{fontSize:10.5, color: k.c || T2.inkSoft, fontWeight:700, marginTop:3}}>{k.sub}</div>
              </div>
            ))}
          </div>
        </div>
        )}

        {/* At-risk participants from the roster: completion under the batch's at-risk cutoff, or capability well below the cohort */}
        {atRisk.length > 0 && (
        <Section kicker="Needs attention" action={`${atRisk.length} · View all`} onAction={() => onNav && onNav('team')}>
          <div style={{padding:'0 16px', display:'flex', flexDirection:'column', gap:8}}>
            {atRisk.slice(0, 3).map(p => (
              <div key={p.id} style={{
                background:T2.roseFaint, borderRadius:10, padding:'12px 14px',
                display:'flex', alignItems:'center', gap:12,
              }}>
                <Avatar initials={(p.name||'').split(' ').map(w=>w[0]).join('').slice(0,2)} size={36} bg={T2.rose} fg="#fff"/>
                <div style={{flex:1, minWidth:0}}>
                  <div style={{fontSize:13, fontWeight:700, color:T2.ink}}>{p.name}</div>
                  <div style={{fontSize:11, color:T2.rose, marginTop:2, fontWeight:600}}>{p.completionPct}% complete</div>
                  <div style={{display:'flex', alignItems:'center', gap:8, marginTop:6}}>
                    <div style={{flex:1, minWidth:0}}><Bar pct={p.completionPct||0} c={T2.rose} h={3}/></div>
                    <span style={{fontSize:10.5, fontWeight:800, color:T2.rose, letterSpacing:'-0.01em', whiteSpace:'nowrap'}}>{p.avgCapability != null ? `${p.avgCapability.toFixed(1)}/5` : '—'}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Section>
        )}

        {/* Next live session */}
        <Section kicker="Next live session">
          <div style={{padding:'0 16px'}}>
            {nextSession ? (
              <div onClick={()=>nextSession.join_url && window.open(nextSession.join_url, '_blank')} style={{
                border:`1px solid ${T2.rule}`, borderRadius:12, padding:'14px',
                display:'flex', alignItems:'center', gap:12, cursor: nextSession.join_url ? 'pointer' : 'default',
              }}>
                <DateBlock dow={new Date(nextSession.starts_at).toLocaleDateString('en-IN',{weekday:'short'}).toUpperCase()} d={new Date(nextSession.starts_at).getDate()}/>
                <div style={{flex:1, minWidth:0}}>
                  <div style={{fontSize:13.5, fontWeight:700, color:T2.ink}}>{nextSession.title}</div>
                  <div style={{fontSize:11.5, color:T2.inkSoft, marginTop:3}}>
                    {new Date(nextSession.starts_at).toLocaleTimeString('en-IN',{hour:'numeric',minute:'2-digit'})} · {nextSession.batch_name}
                  </div>
                </div>
                {nextSession.join_url && <I name="chevR" size={14} c={T2.inkFaint} sw={2}/>}
              </div>
            ) : (
              <div style={{padding:'20px 14px', textAlign:'center', border:`1px dashed ${T2.rule}`, borderRadius:12}}>
                <div style={{fontSize:12.5, color:T2.inkSoft}}>{loading ? 'Loading…' : 'No upcoming session scheduled'}</div>
              </div>
            )}
          </div>
        </Section>

        <div style={{height: 24}}/>
      </div>
      <BottomNav active={active} role="leader" onNav={onNav}/>
    </div>
  );
}

// ============================================================
// 7. LEADER — Cohort list
// ============================================================
function ScreenLeaderCohort({ onNav, active = 'team' }) {
  const stColor = {ahead:T2.sage, on:T2.teal, risk:T2.rose};
  const stLabel = {ahead:'Ahead', on:'On track', risk:'At risk'};

  const [filter, setFilter] = useS(window._cohortFilter || 'all');
  const [loading, setLoading] = useS(true);
  const [cohort, setCohort] = useS(null);
  const [members, setMembers] = useS([]); // from /leader/cohorts/:id/roster

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!window.SRAPI || typeof window.SRAPI._get !== 'function') { setLoading(false); return; }
      try {
        let batchId = window._leaderBatchId;
        let cohortObj = null;
        if (!batchId) {
          const cData = await window.SRAPI._get('/api/leader/cohorts');
          cohortObj = cData?.cohorts?.[0] || null;
          batchId = cohortObj?.id || null;
          if (batchId) window._leaderBatchId = batchId;
        }
        if (cancelled || !batchId) { setLoading(false); return; }
        // Roster status already applies the batch's cutoffs (was a fixed 40/80
        // here), and avgCapability is the 0-5 capability score the member
        // screen labels "Capability" (was the 0-100 quiz average).
        const roster = await window.SRAPI._get('/api/leader/cohorts/' + batchId + '/roster');
        if (cancelled) return;
        setCohort(cohortObj || { id: batchId, name: roster?.batchName });
        const ST = { at_risk: 'risk', ahead: 'ahead', on_track: 'on' };
        const rows = (roster?.roster || []).map(p => {
          const initials = (p.name||'').split(' ').map(w=>w[0]).join('').slice(0,2).toUpperCase() || '?';
          return {
            id: p.id, n: p.name, i: initials,
            comp: p.completionPct || 0, sc: p.avgCapability ?? 0, st: ST[p.status] || 'on',
            gaps: p.gaps || [],
          };
        });
        setMembers(rows);
      } catch (e) { /* leave empty, honest empty state below */ }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, []);

  const counts = {
    ahead: members.filter(m=>m.st==='ahead').length,
    on: members.filter(m=>m.st==='on').length,
    risk: members.filter(m=>m.st==='risk').length,
  };
  const avgSc = members.length ? members.reduce((s,m)=>s+m.sc,0)/members.length : 0;

  const CHIPS = [
    {key:'all',   label:`All ${members.length}`},
    {key:'risk',  label:`At risk · ${counts.risk}`},
    {key:'below', label:'Below avg'},
    {key:'top',   label:'Top'},
  ];

  const filtered = members.filter(m => {
    if (filter === 'all')   return true;
    if (filter === 'risk')  return m.st === 'risk';
    if (filter === 'below') return m.comp < 60 || m.sc < avgSc;
    if (filter === 'top')   return m.st === 'ahead';
    return true;
  });

  const navToMember = (m) => {
    window._memberState = { member: m };
    onNav && onNav('member');
  };

  return (
    <div style={sh.shell}>
      <div style={sh.scroll}>
        {/* Header */}
        <div style={{padding:'14px 16px 12px', borderBottom:`1px solid ${T2.rule}`, display:'flex', alignItems:'center', gap:10}}>
          <div onClick={() => onNav && onNav('home')} style={{cursor:'pointer'}}>
            <I name="chevL" size={20} c={T2.ink} sw={2.2}/>
          </div>
          <div style={{flex:1}}>
            <Label>{cohort?.name || 'Your cohort'}</Label>
            <div style={{
              fontFamily:"'Lato', sans-serif", fontSize:19, fontWeight:700,
              color:T2.ink, letterSpacing:'-0.015em',
            }}>Your batch</div>
          </div>
        </div>

        {loading ? (
          <div style={{padding:'40px 16px', textAlign:'center'}}>
            <span style={{fontSize:12.5, color:T2.inkSoft, fontWeight:600}}>Loading cohort…</span>
          </div>
        ) : members.length === 0 ? (
          <div style={{padding:'40px 16px', textAlign:'center'}}>
            <span style={{fontSize:12.5, color:T2.inkSoft, fontWeight:600}}>No participants found for this cohort.</span>
          </div>
        ) : (
        <>
        {/* Cohort health — single flat bar */}
        <div style={{padding:'14px 16px 0'}}>
          <div style={{display:'flex', height:6, borderRadius:3, overflow:'hidden', background:T2.ruleSoft}}>
            <div style={{flex: counts.ahead||0.001, background:T2.sage}}/>
            <div style={{flex: counts.on||0.001, background:T2.teal}}/>
            <div style={{flex: counts.risk||0.001, background:T2.rose}}/>
          </div>
          <div style={{display:'flex', justifyContent:'space-between', marginTop:8, padding:'0 2px'}}>
            {[
              {l:'Ahead', v:counts.ahead, c:T2.sage},
              {l:'On track', v:counts.on, c:T2.teal},
              {l:'At risk', v:counts.risk, c:T2.rose},
            ].map(s => (
              <div key={s.l} style={{display:'flex', alignItems:'center', gap:5}}>
                <div style={{width:8, height:8, borderRadius:'50%', background:s.c}}/>
                <span style={{fontSize:11.5, fontWeight:800, color:T2.ink}}>{s.v}</span>
                <span style={{fontSize:11, color:T2.inkSoft}}>{s.l}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Filter chips */}
        <div style={{padding:'14px 16px 4px', display:'flex', gap:6, overflowX:'auto'}}>
          {CHIPS.map(chip => {
            const isActive = filter === chip.key;
            return (
              <div key={chip.key} onClick={() => setFilter(chip.key)} style={{
                padding:'6px 12px', borderRadius:18, cursor:'pointer',
                background: isActive ? T2.navy : 'transparent',
                border: isActive ? 'none' : `1px solid ${T2.rule}`,
                color: isActive ? '#fff' : T2.inkMid,
                fontSize:11.5, fontWeight:700, whiteSpace:'nowrap',
                transition:'all 0.15s',
              }}>{chip.label}</div>
            );
          })}
        </div>

        {/* Member count for filtered result */}
        {filter !== 'all' && (
          <div style={{padding:'4px 16px 0'}}>
            <span style={{fontSize:11, color:T2.inkSoft, fontWeight:600}}>
              {filtered.length} participant{filtered.length !== 1 ? 's' : ''} · {CHIPS.find(c=>c.key===filter)?.label}
            </span>
          </div>
        )}

        {/* Members list */}
        <div style={{padding:'8px 16px', display:'flex', flexDirection:'column'}}>
          {filtered.length === 0 ? (
            <div style={{padding:'32px 0', textAlign:'center'}}>
              <div style={{fontSize:28, marginBottom:8}}>✓</div>
              <div style={{fontSize:13, fontWeight:700, color:T2.ink}}>No participants in this group</div>
              <div style={{fontSize:11.5, color:T2.inkSoft, marginTop:4}}>Great news — cohort is on track</div>
            </div>
          ) : filtered.map((m, i) => (
            <div key={m.id||m.i} onClick={() => navToMember(m)} style={{
              padding:'14px 0', display:'flex', alignItems:'center', gap:12, cursor:'pointer',
              borderBottom: i < filtered.length - 1 ? `1px solid ${T2.ruleSoft}` : 'none',
            }}>
              <Avatar initials={m.i} size={36} bg={stColor[m.st]} fg="#fff"/>
              <div style={{flex:1, minWidth:0}}>
                <div style={{fontSize:13.5, fontWeight:700, color:T2.ink}}>{m.n}</div>
                <div style={{display:'flex', alignItems:'center', gap:8, marginTop:4}}>
                  <span style={{
                    fontSize:10, fontWeight:800, letterSpacing:'0.06em',
                    color: stColor[m.st], textTransform:'uppercase',
                  }}>{stLabel[m.st]}</span>
                </div>
                {m.gaps.length > 0 && (
                  <div style={{display:'flex', gap:4, marginTop:5, flexWrap:'wrap'}}>
                    {m.gaps.map((g, gi) => (
                      <span key={gi} style={{
                        fontSize:10, color:T2.inkMid, fontWeight:600,
                        background:T2.ruleSoft, borderRadius:4, padding:'1px 6px',
                      }}>{g}</span>
                    ))}
                  </div>
                )}
              </div>
              <div style={{textAlign:'right', flexShrink:0}}>
                <div style={{
                  fontFamily:"'Lato', sans-serif", fontSize:17, fontWeight:700,
                  color:T2.ink, letterSpacing:'-0.02em', lineHeight:1,
                }}>{m.sc.toFixed(2)}</div>
                <div style={{fontSize:10, color:T2.inkSoft, marginTop:3, fontWeight:700, letterSpacing:'0.06em', textTransform:'uppercase'}}>{m.comp}%</div>
              </div>
              <I name="chevR" size={14} c={T2.inkFaint} sw={2}/>
            </div>
          ))}
        </div>
        </>
        )}

        <div style={{height: 96}}/>
      </div>
      <BottomNav active={active} role="leader" onNav={onNav}/>
    </div>
  );
}

// ============================================================
// 8. LEADER — Member detail
// ============================================================
function ScreenLeaderMember({ onNav, active = 'team' }) {
  const DEFAULT_M = {id:null, n:'Participant', i:'?', comp:0, sc:0, st:'on', gaps:[]};
  const m = (window._memberState && window._memberState.member) ? window._memberState.member : DEFAULT_M;

  const stColor = {ahead:T2.sage, on:T2.teal, risk:T2.rose};
  const stLabel = {ahead:'Ahead · Top performer', on:'On track', risk:'At risk · Needs support'};

  const [loading, setLoading] = useS(true);
  const [detail, setDetail] = useS(null); // from /leader/participants/:id/detail
  const [scheduleOpen, setScheduleOpen] = useS(false);
  const [nudgeOpen, setNudgeOpen] = useS(false);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!m.id || !window.SRAPI || typeof window.SRAPI._get !== 'function') { setLoading(false); return; }
      try {
        const res = await window.SRAPI._get('/api/leader/participants/' + m.id + '/detail');
        if (!cancelled) setDetail(res);
      } catch (e) { /* leave null, sections below degrade to honest empty states */ }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [m.id]);

  const isRisk = m.st === 'risk';
  const isAhead = m.st === 'ahead';

  // Real per-capability gaps, computed server-side from sc_capability_scores
  // vs the batch average — no fabricated gap text or taxonomy.
  const gaps = detail?.capabilities?.filter(c => c.batchAvg != null && c.score < c.batchAvg - 0.5) || [];

  return (
    <div style={sh.shell}>
      {scheduleOpen && (
        <ModalScheduleSession
          initialTopic={`1:1 with ${m.n}`}
          initialDuration={30}
          onClose={()=>setScheduleOpen(false)}
          onCreated={()=>{}}
        />
      )}
      {nudgeOpen && <ModalNudge target={{id:m.id, n:m.n, i:m.i, comp:m.comp}} onClose={()=>setNudgeOpen(false)}/>}
      <div style={sh.scroll}>
        {/* Header */}
        <div style={{background:T2.headerGrad, color:'#fff', padding:'10px 14px 18px'}}>
          <div style={{display:'flex', alignItems:'center', gap:10, marginBottom:14}}>
            <div onClick={() => onNav && onNav('team')} style={{cursor:'pointer', flexShrink:0}}>
              <I name="chevL" size={18} c="#fff" sw={2.2}/>
            </div>
            <div style={{flex:1, minWidth:0, textAlign:'center', fontSize:11, fontWeight:700, color:'rgba(255,255,255,0.7)', letterSpacing:'0.08em'}}>PARTICIPANT DETAIL</div>
          </div>
          <div style={{display:'flex', alignItems:'center', gap:14}}>
            <Avatar initials={m.i} size={56} bg={isRisk?'rgba(190,58,58,0.22)':isAhead?'rgba(61,122,88,0.22)':'rgba(14,116,144,0.22)'} fg={isRisk?T2.rose:isAhead?T2.sage:T2.tealLt}/>
            <div style={{flex:1, minWidth:0}}>
              <div style={{
                fontFamily:"'Lato', sans-serif", fontSize:21, fontWeight:700,
                letterSpacing:'-0.01em', lineHeight:1.2,
              }}>{m.n}</div>
              {(detail?.title || detail?.email) && (
                <div style={{fontSize:11.5, color:'rgba(255,255,255,0.85)', marginTop:3, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis'}}>{detail.title || detail.email}</div>
              )}
              <div style={{display:'flex', alignItems:'center', gap:8, marginTop:8, flexWrap:'wrap'}}>
                <span style={{fontSize:10, fontWeight:800, color: isRisk?'#FCA5A5':isAhead?'#9DDDB8':T2.tealLt, letterSpacing:'0.08em', textTransform:'uppercase'}}>● {stLabel[m.st]}</span>
                {detail?.streak > 0 && (
                  <span style={{fontSize:10.5, color:T2.goldHi, fontWeight:700, display:'flex', alignItems:'center', gap:3}}>
                    <I name="flame" size={10} c={T2.goldHi} sw={2}/> {detail.streak}-day streak
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Stats strip */}
        <div style={{padding:'18px 16px 0'}}>
          <div style={{display:'flex', borderBottom:`1px solid ${T2.rule}`, paddingBottom:18}}>
            {[
              {v:m.sc.toFixed(2), l:'Capability'},
              {v:m.comp+'%', l:'Completion'},
              {v: detail?.rank ? '#'+detail.rank : '—', l:'Rank', sub: detail?.rosterSize ? [`of ${detail.rosterSize}`, RANK_BASIS_LABEL[detail.rankBasis]].filter(Boolean).join(' · ') : ''},
              {v: detail?.monthDeltaPct != null ? (detail.monthDeltaPct >= 0 ? '+' : '') + detail.monthDeltaPct + '%' : detail?.monthDelta != null ? (detail.monthDelta >= 0 ? '+' : '') + detail.monthDelta.toFixed(2) : '—', l: detail?.monthDeltaLabel ? `Growth ${detail.monthDeltaLabel}` : 'Growth', sub: detail?.monthDelta == null && detail?.monthDeltaPct == null ? 'Not enough data yet' : '', c: (detail?.monthDeltaPct ?? detail?.monthDelta) > 0 ? T2.sage : (detail?.monthDeltaPct ?? detail?.monthDelta) < 0 ? T2.rose : undefined},
            ].map((s, i, arr) => (
              <div key={i} style={{
                flex:1, textAlign:'center',
                borderRight: i < arr.length - 1 ? `1px solid ${T2.ruleSoft}` : 'none',
              }}>
                <div style={{
                  fontFamily:"'Lato', sans-serif", fontSize:18, fontWeight:700,
                  color: s.c || T2.ink, letterSpacing:'-0.02em', lineHeight:1,
                }}>{s.v}</div>
                <Label mt={6}>{s.l}</Label>
                {s.sub && <div style={{fontSize:10, color:T2.inkSoft, marginTop:2}}>{s.sub}</div>}
              </div>
            ))}
          </div>
        </div>

        {/* Actions — "Schedule 1:1" reuses the existing real Zoom scheduling
            flow (POST /api/zoom/meetings + real sc_sessions row), pre-filled.
            "Send nudge" calls the real push endpoint (Firebase Admin SDK,
            see backend/src/services/pushNotifications.js) — honestly
            reports "no device"/"not configured" rather than faking success
            when a real push can't be delivered. */}
        <div style={{padding:'16px 16px 0', display:'flex', gap:8}}>
          <button onClick={()=>setNudgeOpen(true)} style={{...sh.btnDangerSolid, flex:1, justifyContent:'center'}}>
            <I name="ping" size={11} c="#fff" sw={2.2}/> Send nudge
          </button>
          <button onClick={()=>setScheduleOpen(true)} style={{...sh.btnGhost, flex:1, justifyContent:'center'}}>
            <I name="cal" size={13} c={T2.navy} sw={2}/> Schedule 1:1
          </button>
        </div>

        {/* Gap card — real capability scores vs batch average, only shown if any exist */}
        {gaps.length > 0 && (
          <div style={{padding:'16px 16px 0'}}>
            <div style={{padding:'14px', background:T2.roseFaint, borderRadius:12}}>
              <Label c={T2.rose} mb={6}>Capability gap{gaps.length > 1 ? 's' : ''}</Label>
              <div style={{fontSize:14, fontWeight:700, color:T2.ink}}>
                {gaps.map(g => g.cap).join(' · ')}
              </div>
              <div style={{fontSize:11.5, color:T2.inkMid, marginTop:4, lineHeight:1.5}}>
                {gaps.map(g => `${g.cap}: ${g.score.toFixed(1)} vs batch avg ${g.batchAvg.toFixed(1)}`).join(' · ')}
              </div>
            </div>
          </div>
        )}

        {/* Module progress */}
        <Section kicker="Module progress">
          <div style={{padding:'0 16px'}}>
            {loading ? (
              <div style={{padding:'20px 0', textAlign:'center'}}>
                <span style={{fontSize:12, color:T2.inkSoft, fontWeight:600}}>Loading…</span>
              </div>
            ) : !detail?.modules?.length ? (
              <div style={{padding:'20px 0', textAlign:'center'}}>
                <span style={{fontSize:12, color:T2.inkSoft, fontWeight:600}}>No module progress recorded yet.</span>
              </div>
            ) : (
              <div style={{border:`1px solid ${T2.rule}`, borderRadius:12, overflow:'hidden'}}>
                {detail.modules.map((mod, i, a) => (
                  <div key={i} style={{
                    padding:'12px 14px', display:'flex', alignItems:'center', gap:12,
                    borderBottom: i < a.length - 1 ? `1px solid ${T2.ruleSoft}` : 'none',
                  }}>
                    <div style={{fontSize:13, fontWeight:700, color:T2.ink, flex:1}}>{mod.label}</div>
                    <div style={{width:90}}><Bar pct={mod.pct} c={mod.pct === 0 ? T2.ruleSoft : T2.navy}/></div>
                    <span style={{fontSize:12, fontWeight:800, color: mod.pct === 0 ? T2.inkFaint : T2.ink, width:34, textAlign:'right', letterSpacing:'-0.01em'}}>{mod.pct}%</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </Section>

        {/* Recent activity */}
        <Section kicker="Recent activity">
          <div style={{padding:'0 16px', display:'flex', flexDirection:'column', gap:0}}>
            {!loading && (!detail?.activity || detail.activity.length === 0) ? (
              <div style={{padding:'20px 0', textAlign:'center'}}>
                <span style={{fontSize:12, color:T2.inkSoft, fontWeight:600}}>No recent activity.</span>
              </div>
            ) : (detail?.activity || []).map((a, i, arr) => (
              <div key={i} style={{
                padding:'11px 0', display:'flex', alignItems:'center', gap:12,
                borderBottom: i < arr.length - 1 ? `1px solid ${T2.ruleSoft}` : 'none',
              }}>
                <I name={a.kind === 'quiz' ? 'check' : 'play'} size={15} c={T2.inkSoft} sw={1.8}/>
                <div style={{flex:1}}>
                  <div style={{fontSize:12.5, fontWeight:600, color:T2.ink, lineHeight:1.35}}>
                    {a.kind === 'quiz' ? `Scored ${a.score}% · ${a.label}` : `Completed · ${a.label}`}
                  </div>
                  <div style={{fontSize:10.5, color:T2.inkSoft, marginTop:2}}>{a.at ? new Date(a.at).toLocaleString('en-IN',{dateStyle:'medium',timeStyle:'short'}) : ''}</div>
                </div>
              </div>
            ))}
          </div>
        </Section>

        <div style={{height: 96}}/>
      </div>
      <BottomNav active={active} role="leader" onNav={onNav}/>
    </div>
  );
}

// ============================================================
// 9. LEADER — Sessions
// ============================================================
// The 49-line LEADER_SESSION_DATA fixture that sat here — three invented
// sessions with invented enrolment lists, capacities and "not enrolled"
// risk flags — went when the leader session screens were wired to
// GET /leader/sessions. No enrolment concept exists in the schema at all;
// cohort size and Zoom attendance are what the screens show now.

// ─── Leader sessions / analytics data ─────────────────────────
// Wired against GET /leader/sessions (real sessions, roster size and Zoom
// attendance counts), GET /api/zoom/attendance/:id (the attendee list),
// PATCH /leader/sessions/:id/notes (trainer notes) and
// GET /skalecap/batches/:id/report (the analytics figures). All four already
// existed; none of them had a caller.
function fmtSessionDate(iso) {
  const d = iso ? new Date(iso) : null;
  if (!d || isNaN(d.getTime())) return { dow:'—', d:'', time:'', valid:false };
  return {
    dow: d.toLocaleDateString('en-IN', { weekday:'short' }).toUpperCase(),
    d:   d.getDate(),
    time: d.toLocaleTimeString('en-IN', { hour:'numeric', minute:'2-digit' }),
    valid: true,
    date: d,
  };
}

function sessionDuration(s) {
  if (!s.starts_at || !s.ends_at) return '';
  const mins = Math.round((new Date(s.ends_at) - new Date(s.starts_at)) / 60000);
  return mins > 0 ? `${mins} min` : '';
}

function LeaderEmpty({ icon, title, body }) {
  return (
    <div style={{ padding:'40px 32px', textAlign:'center' }}>
      <div style={{
        width:46, height:46, borderRadius:12, background:T2.ruleSoft, margin:'0 auto 14px',
        display:'flex', alignItems:'center', justifyContent:'center',
      }}><I name={icon||'calendar'} size={20} c={T2.inkSoft} sw={2}/></div>
      <div style={{ fontSize:14.5, fontWeight:700, color:T2.ink, marginBottom:6 }}>{title}</div>
      <div style={{ fontSize:12.5, color:T2.inkSoft, lineHeight:1.55 }}>{body}</div>
    </div>
  );
}

// ─── MODAL: Leader session detail ─────────────────────────────
function ModalLeaderSessionDetail({ s, onClose, onNotesSaved }) {
  const [tab, setTab]           = useS('notes'); // 'notes' | 'attendance'
  const [notes, setNotes]       = useS(s.notes || '');
  const [saving, setSaving]     = useS(false);
  const [savedAt, setSavedAt]   = useS(null);
  const [saveErr, setSaveErr]   = useS(false);
  const [copied, setCopied]     = useS(false);
  const [att, setAtt]           = useS(null); // null = not loaded
  const [attErr, setAttErr]     = useS(false);

  const when = fmtSessionDate(s.starts_at);
  const dur  = sessionDuration(s);
  const isPast = s.status === 'completed' || (when.valid && when.date < new Date());

  // Attendance list — loaded lazily, only when the tab is opened.
  React.useEffect(() => {
    if (tab !== 'attendance' || att !== null) return;
    let cancelled = false;
    (async () => {
      if (!window.SRAPI || typeof window.SRAPI._get !== 'function') { setAttErr(true); return; }
      try {
        const r = await window.SRAPI._get(`/api/zoom/attendance/${s.id}`);
        if (!cancelled) setAtt(r?.attendance || []);
      } catch (e) {
        if (!cancelled) { setAtt([]); setAttErr(true); }
      }
    })();
    return () => { cancelled = true; };
  }, [tab]);

  const saveNotes = async () => {
    if (saving) return;
    setSaving(true); setSaveErr(false);
    try {
      await window.SRAPI._patch(`/api/leader/sessions/${s.id}/notes`, { notes });
      setSavedAt(new Date());
      onNotesSaved && onNotesSaved(s.id, notes);
    } catch (e) {
      setSaveErr(true);
    }
    setSaving(false);
  };

  const copyLink = () => {
    if (!s.join_url) return;
    navigator.clipboard?.writeText(s.join_url).catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 2200);
  };

  return (
    <div onClick={onClose} style={{position:'absolute',inset:0,zIndex:42,background:'rgba(0,0,0,0.5)',display:'flex',flexDirection:'column',justifyContent:'flex-end'}}>
      <div onClick={e=>e.stopPropagation()} style={{
        background:'#fff', borderRadius:'20px 20px 0 0', maxHeight:'92%',
        display:'flex', flexDirection:'column', overflow:'hidden',
      }}>
        <div style={{padding:'14px 18px 6px', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0, position:'relative'}}>
          <div style={{width:36, height:4, borderRadius:2, background:T2.rule}}/>
          <div onClick={onClose} style={{position:'absolute', right:14, top:8, width:30, height:30, borderRadius:'50%', background:T2.page, border:`1px solid ${T2.rule}`, display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer'}}>
            <div style={{ transform:'rotate(45deg)', display:'flex' }}><I name="plus" size={14} c={T2.inkMid} sw={2.4}/></div>
          </div>
        </div>

        {/* Header */}
        <div style={{padding:'8px 18px 14px', borderBottom:`1px solid ${T2.rule}`, flexShrink:0}}>
          <div style={{display:'flex', alignItems:'flex-start', gap:12}}>
            {when.valid && <DateBlock dow={when.dow} d={when.d}/>}
            <div style={{flex:1, minWidth:0}}>
              <div style={{fontSize:16, fontWeight:800, color:T2.ink, lineHeight:1.3}}>{s.title}</div>
              <div style={{fontSize:12, color:T2.inkSoft, marginTop:3}}>
                {when.time}{dur?` · ${dur}`:''}{s.batch_name?` · ${s.batch_name}`:''}
              </div>
              <div style={{display:'flex', alignItems:'center', gap:8, marginTop:6, flexWrap:'wrap'}}>
                <span style={{
                  fontSize:9.5, fontWeight:800, letterSpacing:'0.08em', textTransform:'uppercase',
                  padding:'3px 8px', borderRadius:5,
                  background: s.status==='completed'?T2.sageLt:s.status==='cancelled'?T2.roseFaint:T2.navyLt,
                  color: s.status==='completed'?T2.sage:s.status==='cancelled'?T2.rose:T2.navy,
                }}>{s.status || 'scheduled'}</span>
                {s.roster_size != null && (
                  <span style={{fontSize:11, color:T2.inkSoft, fontWeight:600}}>
                    {s.roster_size} in cohort
                  </span>
                )}
              </div>
            </div>
          </div>
          {s.description && (
            <div style={{fontSize:12.5, color:T2.inkMid, marginTop:10, lineHeight:1.5}}>{s.description}</div>
          )}
          {/* Join / recording */}
          <div style={{display:'flex', gap:8, marginTop:12}}>
            {s.join_url && (
              <>
                <button onClick={()=>window.open(s.join_url,'_blank')} style={{...sh.btnPrimary, flex:1, justifyContent:'center'}}>
                  <I name="play" size={12} c="#fff" sw={2}/> {isPast ? 'Open link' : 'Start session'}
                </button>
                <button onClick={copyLink} style={{...sh.btnGhost, flexShrink:0}}>
                  {copied ? 'Copied' : 'Copy link'}
                </button>
              </>
            )}
            {!s.join_url && s.recording_url && (
              <button onClick={()=>window.open(s.recording_url,'_blank')} style={{...sh.btnPrimary, flex:1, justifyContent:'center'}}>
                <I name="play" size={12} c="#fff" sw={2}/> Watch recording
              </button>
            )}
            {!s.join_url && !s.recording_url && (
              <div style={{fontSize:11.5, color:T2.inkSoft, padding:'4px 0'}}>No join link published for this session.</div>
            )}
          </div>
        </div>

        {/* Tabs */}
        <div style={{display:'flex', borderBottom:`1px solid ${T2.rule}`, flexShrink:0}}>
          {[{id:'notes',label:'Session notes'},{id:'attendance',label:`Attendance${s.attended_count?` · ${s.attended_count}`:''}`}].map(t=>(
            <div key={t.id} onClick={()=>setTab(t.id)} style={{
              flex:1, padding:'12px 0', textAlign:'center', cursor:'pointer', fontSize:12,
              fontWeight: tab===t.id ? 800 : 600,
              color: tab===t.id ? T2.navy : T2.inkSoft,
              borderBottom: tab===t.id ? `2px solid ${T2.navy}` : '2px solid transparent',
            }}>{t.label}</div>
          ))}
        </div>

        <div style={{overflow:'auto', padding:'16px 18px 24px'}}>
          {tab==='notes' && (
            <>
              {/* This editor is new. PATCH /leader/sessions/:id/notes has
                  existed since the wiring migration but had no UI at all —
                  there was nowhere in the app to write a session note. */}
              <textarea
                value={notes}
                onChange={e=>{ setNotes(e.target.value); setSavedAt(null); }}
                placeholder="What happened in this session? Who needs follow-up?"
                style={{
                  width:'100%', minHeight:150, padding:'12px', borderRadius:10,
                  border:`1px solid ${T2.rule}`, fontSize:13, fontFamily:'inherit',
                  color:T2.ink, resize:'vertical', boxSizing:'border-box', lineHeight:1.55,
                }}/>
              {saveErr && (
                <div style={{fontSize:11.5, color:T2.rose, marginTop:8}}>
                  Couldn't save — check your connection and try again.
                </div>
              )}
              {savedAt && !saveErr && (
                <div style={{fontSize:11.5, color:T2.sage, marginTop:8, display:'flex', alignItems:'center', gap:5}}>
                  <I name="check" size={11} c={T2.sage} sw={2.6}/>
                  Saved at {savedAt.toLocaleTimeString('en-IN',{hour:'numeric',minute:'2-digit'})}
                </div>
              )}
              <button onClick={saveNotes} disabled={saving}
                style={{...sh.btnPrimary, width:'100%', marginTop:12, justifyContent:'center', opacity:saving?0.6:1}}>
                {saving ? 'Saving…' : 'Save notes'}
              </button>
            </>
          )}

          {tab==='attendance' && (
            <>
              {att === null && <div style={{fontSize:12.5, color:T2.inkSoft, textAlign:'center', padding:'20px 0'}}>Loading attendance…</div>}
              {att !== null && att.length === 0 && (
                <LeaderEmpty icon="team" title="No attendance recorded"
                  body={attErr
                    ? "Couldn't load attendance for this session."
                    : "Attendance appears here once the session has run through Zoom."}/>
              )}
              {att !== null && att.length > 0 && (
                <div style={{display:'flex', flexDirection:'column', gap:8}}>
                  {att.map((a,i)=>{
                    const nm = a.participant_name || a.participant_email || 'Unknown';
                    const initials = nm.split(' ').filter(Boolean).slice(0,2).map(w=>w[0]).join('').toUpperCase();
                    return (
                      <div key={a.id||i} style={{display:'flex', alignItems:'center', gap:11, padding:'9px 0', borderBottom:i<att.length-1?`1px solid ${T2.ruleSoft}`:'none'}}>
                        <Avatar initials={initials} size={32} bg={T2.navyLt} fg={T2.navy}/>
                        <div style={{flex:1, minWidth:0}}>
                          <div style={{fontSize:13, fontWeight:700, color:T2.ink}}>{nm}</div>
                          <div style={{fontSize:11, color:T2.inkSoft, marginTop:1}}>
                            {a.duration_minutes ? `${Math.round(a.duration_minutes)} min` : ''}
                            {a.joined_at ? `${a.duration_minutes?' · ':''}joined ${new Date(a.joined_at).toLocaleTimeString('en-IN',{hour:'numeric',minute:'2-digit'})}` : ''}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function ModalScheduleSession({ onClose, onCreated, initialTopic, initialDuration }) {
  const [topic, setTopic]       = useS(initialTopic || '');
  const [date, setDate]         = useS('');
  const [time, setTime]         = useS('10:00');
  const [duration, setDuration] = useS(initialDuration || 60);
  const [status, setStatus]     = useS('idle'); // idle | loading | done | error
  const [result, setResult]     = useS(null);
  const [errMsg, setErrMsg]     = useS('');

  const create = async () => {
    if (!topic.trim() || !date) return;
    setStatus('loading');
    try {
      const token = localStorage.getItem('sr_access_token') || '';
      if (!token) {
        throw new Error('Please log in to SkaleCap first before scheduling a session.');
      }
      const start_time = new Date(`${date}T${time}:00`).toISOString();
      // Resolve the leader's current cohort so the created meeting also
      // becomes a real sc_sessions row (not just a standalone Zoom meeting) —
      // cached on window since it won't change within a session.
      let batchId = window._leaderBatchId;
      if (!batchId) {
        try {
          const cRes = await fetch('https://api.skaledesk.com/api/leader/cohorts', {
            headers: { Authorization: 'Bearer ' + token },
          });
          const cData = await cRes.json().catch(() => ({}));
          batchId = cData?.cohorts?.[0]?.id || null;
          if (batchId) window._leaderBatchId = batchId;
        } catch { /* non-fatal — meeting still gets created without a session link */ }
      }
      // Use form-encoded body with token inside — avoids CORS preflight entirely
      const params = new URLSearchParams({
        _auth: token,
        topic: topic.trim(),
        start_time,
        duration: String(Number(duration)),
        ...(batchId ? { batch_id: batchId } : {}),
      });
      const res = await fetch('https://api.skaledesk.com/api/zoom/meetings', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });
      const resData = await res.json().catch(() => ({}));
      if (res.status === 401) throw new Error('Auth failed: ' + (resData.error || resData.code || '401'));
      if (!res.ok) throw new Error(resData.error || ('Server error ' + res.status));
      setResult(resData);
      setStatus('done');
      if (onCreated) onCreated(resData);
    } catch (e) {
      setErrMsg(e.message || 'Network error — check your connection and try again.');
      setStatus('error');
    }
  };

  const inputStyle = { width:'100%', padding:'10px 12px', borderRadius:9, border:`1.5px solid ${T2.rule}`, fontSize:13, fontFamily:'inherit', color:T2.ink, background:'#fff', boxSizing:'border-box' };

  return (
    <div onClick={onClose} style={{position:'absolute',inset:0,zIndex:35,background:'rgba(0,0,0,0.55)',display:'flex',flexDirection:'column',justifyContent:'flex-end'}}>
      <div onClick={e=>e.stopPropagation()} style={{background:'#fff',borderRadius:'20px 20px 0 0',display:'flex',flexDirection:'column',maxHeight:'85%'}}>
        {/* Handle + close */}
        <div style={{padding:'14px 18px 6px',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0,position:'relative'}}>
          <div style={{width:36,height:4,borderRadius:2,background:T2.rule}}/>
          <div onClick={onClose} style={{position:'absolute',right:14,top:8,width:30,height:30,borderRadius:'50%',background:T2.page,border:`1px solid ${T2.rule}`,display:'flex',alignItems:'center',justifyContent:'center',cursor:'pointer',fontSize:15,color:T2.inkMid}}>✕</div>
        </div>

        {/* Scrollable content */}
        <div style={{overflowY:'auto',flex:1,padding:'4px 18px 8px'}}>
          <div style={{display:'flex',alignItems:'center',gap:10,marginBottom:14}}>
            <div style={{width:36,height:36,borderRadius:10,background:'#2D8CFF',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>
              <span style={{fontSize:17,fontWeight:900,color:'#fff'}}>Z</span>
            </div>
            <div>
              <div style={{fontSize:15,fontWeight:800,color:T2.ink}}>Schedule Zoom Session</div>
              <div style={{fontSize:11,color:T2.inkSoft,marginTop:1}}>Creates a Zoom meeting automatically</div>
            </div>
          </div>

          {status === 'done' && result ? (
            <div>
              <div style={{padding:'14px',borderRadius:12,background:'#E8F5E9',border:'1px solid #A5D6A7',marginBottom:12}}>
                <div style={{fontSize:13,fontWeight:800,color:'#2E7D32',marginBottom:4}}>✅ Meeting created!</div>
                <div style={{fontSize:12,color:'#388E3C',marginBottom:8}}>{result.topic}</div>
                <div style={{fontSize:11,color:T2.ink,background:'#fff',borderRadius:8,padding:'8px 10px',border:`1px solid ${T2.rule}`,wordBreak:'break-all'}}>{result.join_url}</div>
              </div>
              <div style={{fontSize:10.5,fontWeight:700,color:T2.inkSoft,marginBottom:8,textTransform:'uppercase',letterSpacing:'0.06em'}}>Notify participants</div>
              <div style={{display:'flex',flexDirection:'column',gap:8}}>
                <button onClick={(e)=>{
                  navigator.clipboard?.writeText(result.join_url).then(()=>{
                    const btn = e.currentTarget;
                    const orig = btn.textContent;
                    btn.textContent = '✅ Copied!';
                    setTimeout(()=>{ btn.textContent = orig; }, 2000);
                  }).catch(()=>{});
                }} style={{...sh.btnPrimary,width:'100%',justifyContent:'center',background:'#2E7D32'}}>Copy join link</button>
                <button onClick={()=>{
                  const startFmt = result.start_time ? new Date(result.start_time).toLocaleString('en-IN',{dateStyle:'medium',timeStyle:'short',timeZone:'Asia/Kolkata'}) : '';
                  const msg = `You are invited to a Zoom meeting.\n\n*Topic:* ${result.topic}\n*Time:* ${startFmt} Mumbai, Kolkata, New Delhi\n\n*Join Zoom Meeting*\n${result.join_url}${result.password ? `\n\n*Passcode:* ${result.password}` : ''}`;
                  window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`,'_blank');
                }} style={{...sh.btnPrimary,width:'100%',justifyContent:'center',background:'#25D366'}}>WhatsApp participants</button>
                <button onClick={()=>{
                  const startFmt = result.start_time ? new Date(result.start_time).toLocaleString('en-IN',{dateStyle:'medium',timeStyle:'short',timeZone:'Asia/Kolkata'}) : '';
                  const s = encodeURIComponent(`Zoom Meeting: ${result.topic}`);
                  const b = encodeURIComponent(`You are invited to a Zoom meeting.\n\nTopic: ${result.topic}\nTime: ${startFmt} Mumbai, Kolkata, New Delhi\n\nJoin Zoom Meeting\n${result.join_url}${result.password ? `\n\nPasscode: ${result.password}` : ''}`);
                  window.location.href = `mailto:?subject=${s}&body=${b}`;
                }} style={{...sh.btnGhost,width:'100%',justifyContent:'center'}}>Email participants</button>
              </div>
            </div>
          ) : (
            <div style={{display:'flex',flexDirection:'column',gap:10}}>
              <div>
                <div style={{fontSize:10.5,fontWeight:700,color:T2.inkSoft,marginBottom:4,textTransform:'uppercase',letterSpacing:'0.06em'}}>Session title</div>
                <input style={inputStyle} placeholder="e.g. Coaching Conversations — Live" value={topic} onChange={e=>setTopic(e.target.value)}/>
              </div>
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8}}>
                <div>
                  <div style={{fontSize:10.5,fontWeight:700,color:T2.inkSoft,marginBottom:4,textTransform:'uppercase',letterSpacing:'0.06em'}}>Date</div>
                  <input type="date" style={inputStyle} value={date} onChange={e=>setDate(e.target.value)}/>
                </div>
                <div>
                  <div style={{fontSize:10.5,fontWeight:700,color:T2.inkSoft,marginBottom:4,textTransform:'uppercase',letterSpacing:'0.06em'}}>Time</div>
                  <input type="time" style={inputStyle} value={time} onChange={e=>setTime(e.target.value)}/>
                </div>
              </div>
              <div>
                <div style={{fontSize:10.5,fontWeight:700,color:T2.inkSoft,marginBottom:4,textTransform:'uppercase',letterSpacing:'0.06em'}}>Duration</div>
                <select style={inputStyle} value={duration} onChange={e=>setDuration(e.target.value)}>
                  {[30,45,60,75,90,120].map(d=><option key={d} value={d}>{d} minutes</option>)}
                </select>
              </div>
              {status === 'error' && <div style={{padding:'10px 12px',borderRadius:8,background:'#FFF3F3',border:`1px solid ${T2.rose}33`,fontSize:12,color:T2.rose}}>⚠ {errMsg}</div>}
            </div>
          )}
        </div>

        {/* Fixed footer — always visible */}
        <div style={{padding:'12px 18px 28px',borderTop:`1px solid ${T2.ruleSoft}`,flexShrink:0}}>
          {status === 'done'
            ? <button onClick={onClose} style={{...sh.btnGhost,width:'100%',justifyContent:'center'}}>Done</button>
            : <button onClick={create} disabled={!topic.trim()||!date||status==='loading'} style={{...sh.btnPrimary,width:'100%',justifyContent:'center',background:(!topic.trim()||!date)?T2.inkFaint:'#2D8CFF',opacity:status==='loading'?0.7:1}}>
                {status==='loading' ? 'Creating…' : 'Create Zoom Meeting'}
              </button>
          }
        </div>
      </div>
    </div>
  );
}

// ─── SCREEN: Leader sessions ──────────────────────────────────
function ScreenLeaderSessions({ onNav, active = 'sessions' }) {
  const [sessions, setSessions]           = useS([]);
  const [status, setStatus]               = useS('loading');
  const [activeSession, setActiveSession] = useS(null);
  const [showSchedule, setShowSchedule]   = useS(false);

  const load = React.useCallback(async () => {
    if (!window.SRAPI || typeof window.SRAPI._get !== 'function') { setStatus('error'); return; }
    try {
      const r = await window.SRAPI._get('/api/leader/sessions');
      setSessions(r?.sessions || []);
      setStatus('ready');
    } catch (e) { setStatus('error'); }
  }, []);

  React.useEffect(() => { load(); }, [load]);

  const now = Date.now();
  const upcoming = sessions.filter(s => s.starts_at && new Date(s.starts_at).getTime() >= now && s.status !== 'completed');
  const past     = sessions.filter(s => !(s.starts_at && new Date(s.starts_at).getTime() >= now && s.status !== 'completed'));

  // Keep the open modal in step with a notes save without a full refetch.
  const applyNotes = (id, notes) => {
    setSessions(list => list.map(x => x.id === id ? { ...x, notes } : x));
    setActiveSession(cur => cur && cur.id === id ? { ...cur, notes } : cur);
  };

  const Row = ({ s }) => {
    const when = fmtSessionDate(s.starts_at);
    const dur  = sessionDuration(s);
    return (
      <div onClick={()=>setActiveSession(s)} style={{
        border:`1px solid ${T2.rule}`, borderRadius:14, padding:'14px',
        display:'flex', alignItems:'center', gap:12, cursor:'pointer',
        background:'#fff', boxShadow:'0 1px 4px rgba(0,0,0,0.04)',
      }}>
        {when.valid && <DateBlock dow={when.dow} d={when.d}/>}
        <div style={{flex:1, minWidth:0}}>
          <div style={{fontSize:13.5, fontWeight:700, color:T2.ink}}>{s.title}</div>
          <div style={{fontSize:11.5, color:T2.inkSoft, marginTop:3}}>
            {when.time}{dur?` · ${dur}`:''}{s.batch_name?` · ${s.batch_name}`:''}
          </div>
          <div style={{display:'flex', alignItems:'center', gap:10, marginTop:6, flexWrap:'wrap'}}>
            {/* Real figures only: cohort size always, attendance once Zoom has
                reported it. The bar this replaces showed an invented
                "enrolled / capacity" split with a "3 not enrolled" pill —
                no enrolment concept exists in the schema at all. */}
            {s.roster_size != null && (
              <span style={{fontSize:10.5, color:T2.inkSoft, fontWeight:600}}>{s.roster_size} in cohort</span>
            )}
            {s.rsvp_going > 0 && (
              <span style={{fontSize:10.5, color:T2.teal, fontWeight:700}}>{s.rsvp_going} going</span>
            )}
            {s.attended_count > 0 && (
              <span style={{fontSize:10.5, color:T2.sage, fontWeight:700}}>{s.attended_count} attended</span>
            )}
            {s.notes ? (
              <span style={{fontSize:10.5, color:T2.navy, fontWeight:700, display:'flex', alignItems:'center', gap:3}}>
                <I name="doc" size={10} c={T2.navy} sw={2.2}/> Notes
              </span>
            ) : null}
          </div>
        </div>
        <div style={{display:'flex', flexDirection:'column', alignItems:'flex-end', gap:6, flexShrink:0}}>
          {s.recording_url && <I name="play" size={13} c={T2.inkFaint} sw={2}/>}
          <I name="chevR" size={13} c={T2.inkFaint} sw={2}/>
        </div>
      </div>
    );
  };

  return (
    <div style={sh.shell}>
      {activeSession && <ModalLeaderSessionDetail s={activeSession} onClose={()=>setActiveSession(null)} onNotesSaved={applyNotes}/>}
      {showSchedule && <ModalScheduleSession onClose={()=>setShowSchedule(false)} onCreated={()=>{ setShowSchedule(false); setStatus('loading'); load(); }}/>}
      <div style={sh.scroll}>
        <Header
          kicker="Live Sessions"
          title="Your sessions"
          sub={status==='ready'
            ? <>{upcoming.length} upcoming · {past.length} past</>
            : <>&nbsp;</>}
          right={
            <button onClick={()=>setShowSchedule(true)} style={{...sh.btnPrimary,padding:'8px 13px',fontSize:12,background:'#2D8CFF',gap:5}}>
              <I name="plus" size={12} c="#fff" sw={2.4}/> Schedule
            </button>
          }
        />

        {status==='loading' && <LeaderEmpty icon="calendar" title="Loading…" body="Fetching your sessions."/>}
        {status==='error'   && <LeaderEmpty icon="bolt" title="Couldn't load sessions" body="Check your connection and try again."/>}
        {status==='ready' && sessions.length===0 && (
          <LeaderEmpty icon="calendar" title="No sessions yet"
            body="Schedule one with the button above and it'll appear here."/>
        )}

        {status==='ready' && upcoming.length>0 && (
          <Section kicker="Upcoming" pt={20}>
            <div style={{padding:'0 16px', display:'flex', flexDirection:'column', gap:8}}>
              {upcoming.map(s => <Row key={s.id} s={s}/>)}
            </div>
          </Section>
        )}

        {status==='ready' && past.length>0 && (
          <Section kicker="Past" pt={20}>
            <div style={{padding:'0 16px', display:'flex', flexDirection:'column', gap:8}}>
              {past.map(s => <Row key={s.id} s={s}/>)}
            </div>
          </Section>
        )}

        <div style={{height: 96}}/>
      </div>
      <BottomNav active={active} role="leader" onNav={onNav}/>
    </div>
  );
}

// ─── SCREEN: Leader analytics ─────────────────────────────────
// Improvement by segment (task batch-improvement-segments): who improved, by how much, and what worked for whom.
// All figures come from GET /api/leader/cohorts/:id/analytics -> improvement; nothing is computed here.
const SEG_META = [
  { key: 'high',  label: 'High performers' },
  { key: 'good',  label: 'Good' },
  { key: 'below', label: 'Below expectations' },
];
// All figures are % growth on each learner's own starting score, computed by the server.
const fmtPct = (v) => (v == null ? '—' : `${v > 0 ? '+' : ''}${Number(v)}%`);
const capList = (arr) => (arr && arr.length ? arr.map(c => `${c.cap} ${fmtPct(c.avgPct)} (${c.learners})`).join(' · ') : '—');
const segNote = (key, m) => !m ? '' : key === 'high' ? `grew ${m.highCutoffPct}% or more`
  : key === 'good' ? `grew ${m.goodCutoffPct}% to ${m.highCutoffPct}%` : `below ${m.goodCutoffPct}%`;

function ImprovementSegments({ imp, failed, onRetry }) {
  // A failed analytics call must not look like "no improvement data" (Codex QA, P2).
  if (failed) {
    return (
      <Section kicker="Improvement by segment" pt={20}>
        <div style={{padding:'0 16px'}}>
          <div style={{border:`1px dashed ${T2.rose}`, borderRadius:12, padding:'14px', textAlign:'center'}}>
            <div style={{fontSize:13, fontWeight:700, color:T2.ink}}>Improvement data couldn't load</div>
            <div style={{fontSize:12, color:T2.inkSoft, marginTop:4}}>The rest of this report is up to date. Check your connection and retry.</div>
            <button type="button" onClick={onRetry} style={{marginTop:10, padding:'7px 16px', borderRadius:8, border:`1px solid ${T2.rule}`, background:'#fff', fontSize:12.5, fontWeight:700, color:T2.navy, cursor:'pointer'}}>Retry</button>
          </div>
        </div>
      </Section>
    );
  }
  if (!imp) return null;
  const segColor = { high: T2.sage, good: T2.navy, below: T2.rose };
  const excluded = imp.excluded || [];
  const reasonText = (r) => (r === 'below_expectations' ? 'below expectations' : r === 'outlier_low' ? 'unusually low'
    : r === 'outlier_high' ? 'unusually high' : r === 'excluded_by_admin' ? 'left out by the programme' : r);
  const m = imp.methodology;
  return (
    <Section kicker="Improvement by segment" pt={20}>
      <div style={{padding:'0 16px', display:'flex', flexDirection:'column', gap:8}}>
        {SEG_META.map(m => {
          const s = imp.segments?.[m.key];
          if (!s) return null;
          return (
            <div key={m.key} style={{border:`1px solid ${T2.rule}`, borderLeft:`4px solid ${segColor[m.key]}`, borderRadius:10, padding:'10px 12px'}}>
              <div style={{display:'flex', justifyContent:'space-between', alignItems:'baseline'}}>
                <span style={{fontSize:13, fontWeight:800, color:T2.ink}}>{m.label} <span style={{fontWeight:600, color:T2.inkSoft}}>· {s.count}</span></span>
                <span style={{fontFamily:"'Lato', sans-serif", fontSize:15, fontWeight:700, color:segColor[m.key]}}>{fmtPct(s.avgPct)}</span>
              </div>
              <div style={{fontSize:11, color:T2.inkFaint, marginTop:2}}>{segNote(m.key, imp.methodology)}</div>
              {s.count > 0 && (
                <div style={{fontSize:12, color:T2.inkSoft, marginTop:6, lineHeight:1.45}}>
                  <div><strong style={{color:T2.ink}}>Worked:</strong> {capList(s.whatWorked)}</div>
                  <div><strong style={{color:T2.ink}}>Did not:</strong> {capList(s.whatDidNot)}</div>
                </div>
              )}
            </div>
          );
        })}
        <div style={{fontSize:11.5, color:T2.inkSoft, lineHeight:1.5, padding:'2px 2px 0'}}>
          {imp.headline ? `${imp.headline.improvedCount} of ${imp.headline.withHistoryCount} learners improved. ` : ''}
          Average improvement {fmtPct(imp.headline?.pct)} leaves out {excluded.length} learner{excluded.length === 1 ? '' : 's'}
          {excluded.length ? `: ${excluded.map(e => `${e.name} (${e.reasons.map(reasonText).join(', ')})`).join('; ')}` : ''}.
          {' '}Everyone with history: {fmtPct(imp.allLearnersPct)}.
          {(imp.notEnoughData || []).length ? ` Not enough data yet: ${imp.notEnoughData.length}.` : ''}
          {' '}How this is measured: growth on each learner's own starting score, capped at {m?.growthCapPct ?? 100}%
          {m && m.outlierEnabled ? `, unusual results set aside with ${m.outlierMinLearners}+ learners` : ''}. Capability figures show (number of learners).
        </div>
      </div>
    </Section>
  );
}

function ScreenLeaderAnalytics({ onNav, active = 'analytics' }) {
  const [report, setReport] = useS(null);
  const [imp, setImp] = useS(null);
  const [impFailed, setImpFailed] = useS(false);
  const [cohortId, setCohortId] = useS(null);
  const loadImprovement = React.useCallback(async (id) => {
    if (!id) return;
    setImpFailed(false);
    try { const a = await window.SRAPI._get(`/api/leader/cohorts/${id}/analytics`); setImp(a?.improvement || null); }
    catch (e) { setImp(null); setImpFailed(true); }
  }, []);
  const [status, setStatus] = useS('loading');

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!window.SRAPI || typeof window.SRAPI._get !== 'function') { setStatus('error'); return; }
      try {
        const c = await window.SRAPI._get('/api/leader/cohorts');
        const first = (c?.cohorts || [])[0];
        if (!first) { if(!cancelled) setStatus('empty'); return; }
        const r = await window.SRAPI._get(`/api/skalecap/batches/${first.id}/report`);
        if (!cancelled) { setReport(r); setCohortId(first.id); setStatus('ready'); }
        // Improvement segments load separately: the report still renders if they fail, with a retry.
        if (!cancelled) await loadImprovement(first.id);
      } catch (e) {
        if (!cancelled) setStatus('error');
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (status !== 'ready' || !report) {
    return (
      <div style={sh.shell}>
        <div style={sh.scroll}>
          <Header kicker="Analytics" title="Cohort trajectory"/>
          {status==='loading' && <LeaderEmpty icon="bar" title="Loading…" body="Fetching your cohort report."/>}
          {status==='empty'   && <LeaderEmpty icon="bar" title="No cohort assigned" body="Once you're assigned a cohort, its analytics appear here."/>}
          {status==='error'   && <LeaderEmpty icon="bolt" title="Couldn't load analytics" body="Check your connection and try again."/>}
        </div>
        <BottomNav active={active} role="leader" onNav={onNav}/>
      </div>
    );
  }

  const caps = report.capability_scores || [];
  const CAP_COLORS = [T2.navy, T2.teal, T2.sage, T2.rose, T2.amber, T2.gold];

  // Improvement is the batch headline from the analytics endpoint: each learner's change since their snapshot
  // baseline, excluding below-expectations learners and outliers (see ImprovementSegments).
  const stats = [
    { v: impFailed ? 'n/a' : imp ? fmtPct(imp.headline?.pct) : '—', l:'Avg growth' },
    { v: report.avg_viewed_pct != null ? `${Math.round(report.avg_viewed_pct)}%` : '—', l:'Completion' },
    { v: (report.avg_assessment_score != null && Number.isFinite(Number(report.avg_assessment_score))) ? `${Math.round(Number(report.avg_assessment_score))}%` : '—', l:'Assessment' },
    { v: `${report.sessions_completed||0}/${report.sessions_total||0}`, l:'Sessions' },
  ];

  return (
    <div style={sh.shell}>
      <div style={sh.scroll}>
        <Header
          kicker="Analytics"
          title={report.name || 'Cohort trajectory'}
          sub={<>{report.participants || 0} participants{report.trainer?` · ${report.trainer}`:''}</>}
        />
        <div style={{padding:'18px 16px 0'}}>
          <div style={{display:'flex', borderBottom:`1px solid ${T2.rule}`, paddingBottom:18}}>
            {stats.map((s, i, arr) => (
              <div key={i} style={{
                flex:1, textAlign:'center',
                borderRight: i < arr.length - 1 ? `1px solid ${T2.ruleSoft}` : 'none',
              }}>
                <div style={{fontFamily:"'Lato', sans-serif", fontSize:22, fontWeight:700, color:T2.ink, letterSpacing:'-0.02em', lineHeight:1}}>{s.v}</div>
                <Label mt={6}>{s.l}</Label>
              </div>
            ))}
          </div>
        </div>

        <Section kicker="Capability averages (current, 0-5)">
          <div style={{padding:'0 16px'}}>
            {caps.length === 0 ? (
              <div style={{
                border:`1px dashed ${T2.rule}`, borderRadius:12, padding:'18px 14px',
                textAlign:'center', fontSize:12.5, color:T2.inkSoft,
              }}>No capability scores recorded for this cohort yet.</div>
            ) : (
              <div style={{border:`1px solid ${T2.rule}`, borderRadius:12, padding:'12px'}}>
                {caps.map((c, i, arr) => {
                  const label = c.label || c.cap || c.capability_key || '—';
                  // capability_scores rows carry avg_score: the CURRENT average for that capability (not a lift).
                  const val   = Number(c.avg_score ?? c.score ?? c.avg ?? 0);
                  const col   = CAP_COLORS[i % CAP_COLORS.length];
                  return (
                    <div key={label} style={{
                      padding:'10px 0',
                      borderBottom: i < arr.length - 1 ? `1px solid ${T2.ruleSoft}` : 'none',
                    }}>
                      <div style={{display:'flex', justifyContent:'space-between', marginBottom:6}}>
                        <span style={{fontSize:13, fontWeight:700, color:T2.ink}}>{label}</span>
                        <span style={{fontFamily:"'Lato', sans-serif", fontSize:15, fontWeight:700, color:col, letterSpacing:'-0.01em'}}>{val.toFixed(2)}</span>
                      </div>
                      <Bar pct={Math.max(0, Math.min(100, (val/5)*100))} c={col}/>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </Section>

        <ImprovementSegments imp={imp} failed={impFailed} onRetry={() => loadImprovement(cohortId)}/>

        {Array.isArray(report.leaderboard) && report.leaderboard.length > 0 && (
          <Section kicker="Leaderboard (ranked by completion)" pt={20}>
            {/* Rank is by completion. Learners on the same completion share a rank (1, 2, 2, 4). Assessment score is a separate figure, not shown here. */}
            <div style={{padding:'0 16px', display:'flex', flexDirection:'column', gap:8}}>
              {report.leaderboard.slice(0,10).map((p,i)=>(
                <div key={p.id||i} style={{
                  display:'flex', alignItems:'center', gap:11, padding:'10px 12px',
                  border:`1px solid ${T2.rule}`, borderRadius:10,
                }}>
                  <span style={{fontSize:12, fontWeight:800, color:T2.inkFaint, width:18}}>{p.rank ?? i+1}</span>
                  <div style={{flex:1, minWidth:0}}>
                    <div style={{fontSize:13, fontWeight:700, color:T2.ink}}>{p.name || '—'}</div>
                  </div>
                  {p.viewedPct != null && (
                    <span style={{fontSize:13, fontWeight:800, color:T2.navy}}>{Math.round(p.viewedPct)}% complete</span>
                  )}
                </div>
              ))}
              <div style={{fontSize:11, color:T2.inkFaint, lineHeight:1.4}}>Ranked by completion. Learners on the same completion share a rank.</div>
            </div>
          </Section>
        )}

        <div style={{height: 96}}/>
      </div>
      <BottomNav active={active} role="leader" onNav={onNav}/>
    </div>
  );
}

function ModalExportReport({ onClose }) {
  // POST /api/leader/cohorts/:id/export-report — the server renders the
  // PDF/XLSX and emails it to the signed-in leader. Was a setTimeout that
  // pretended to send to "riya@ashatech.com".
  const [sections, setSections] = useS({overview:true, individual:false, assessment:true, community:false});
  const [fmt, setFmt] = useS('pdf');
  const [state, setState] = useS('idle'); // idle | generating | done | error
  const [result, setResult] = useS(null);
  const toggle = (k) => setSections(p=>({...p,[k]:!p[k]}));
  const generate = async () => {
    const batchId = window._leaderBatchId;
    if (!batchId || !window.SRAPI || typeof window.SRAPI._post !== 'function') { setResult({ message: 'No cohort selected' }); setState('error'); return; }
    if (!Object.values(sections).some(Boolean)) { setResult({ message: 'Select at least one section' }); setState('error'); return; }
    setState('generating');
    try {
      const r = await window.SRAPI._post('/api/leader/cohorts/' + batchId + '/export-report', { format: fmt, sections });
      setResult(r || {});
      setState(r && r.sent === false ? 'error' : 'done');
    } catch (e) { setResult({ message: (e && e.message) || 'Export failed' }); setState('error'); }
  };
  const SECS = [{k:'overview',l:'Cohort overview',sub:'Progress, completion, XP totals'},{k:'individual',l:'Individual reports',sub:'Per-participant breakdown'},{k:'assessment',l:'Assessment results',sub:'Quiz scores & reflection data'},{k:'community',l:'Community activity',sub:'Posts, streaks, engagement'}];
  return (
    <div onClick={onClose} style={{position:'absolute',inset:0,zIndex:40,background:'rgba(0,0,0,0.5)',display:'flex',flexDirection:'column',justifyContent:'flex-end'}}>
      <div onClick={e=>e.stopPropagation()} style={{background:'#fff',borderRadius:'22px 22px 0 0',maxHeight:'90%',display:'flex',flexDirection:'column'}}>
        <div style={{width:40,height:4,borderRadius:2,background:T2.rule,margin:'10px auto 0',flexShrink:0}}/>
        <div style={{padding:'14px 16px 12px',borderBottom:`1px solid ${T2.rule}`,display:'flex',alignItems:'center',flexShrink:0}}>
          <div style={{flex:1,fontSize:15,fontWeight:800,color:T2.ink}}>Export Report</div>
          <div onClick={onClose} style={{cursor:'pointer',width:28,height:28,borderRadius:'50%',background:T2.ruleSoft,display:'flex',alignItems:'center',justifyContent:'center'}}><div style={{transform:'rotate(45deg)',display:'flex'}}><I name="plus" size={14} c={T2.inkSoft} sw={2.4}/></div></div>
        </div>
        {state==='done' ? (
          <div style={{padding:'32px 20px 28px',display:'flex',flexDirection:'column',alignItems:'center',gap:14}}>
            <I name="doc" size={40} c={T2.navy} sw={1.6}/>
            <div style={{fontSize:17,fontWeight:800,color:T2.ink}}>Report sent</div>
            <div style={{fontSize:13,color:T2.inkSoft,textAlign:'center'}}>Your {fmt.toUpperCase()} report{result?.filename ? ` (${result.filename})` : ''} has been emailed to {result?.to || 'your email'}.</div>
            <button onClick={onClose} style={{...sh.btnPrimary,width:'100%',justifyContent:'center',marginTop:8}}>Done</button>
          </div>
        ) : (
          <div style={{overflow:'auto',flex:1,padding:'16px'}}>
            {state==='error' && (
              <div style={{marginBottom:14,padding:'10px 13px',borderRadius:10,background:T2.roseLt,border:`1px solid ${T2.rose}44`,fontSize:12.5,color:T2.rose,fontWeight:600}}>
                {result?.message || result?.error || 'Could not generate the report.'}
              </div>
            )}
            <Label mb={10}>Include sections</Label>
            <div style={{display:'flex',flexDirection:'column',gap:8,marginBottom:18}}>
              {SECS.map(s=>(
                <div key={s.k} onClick={()=>toggle(s.k)} style={{display:'flex',alignItems:'center',gap:12,padding:'11px 13px',borderRadius:10,border:`1.5px solid ${sections[s.k]?T2.navy:T2.rule}`,background:sections[s.k]?T2.navyLt:'#fff',cursor:'pointer'}}>
                  <div style={{width:20,height:20,borderRadius:5,border:`1.5px solid ${sections[s.k]?T2.navy:T2.rule}`,background:sections[s.k]?T2.navy:'transparent',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>
                    {sections[s.k]&&<I name="check" size={11} c="#fff" sw={2.5}/>}
                  </div>
                  <div style={{flex:1}}><div style={{fontSize:13,fontWeight:700,color:T2.ink}}>{s.l}</div><div style={{fontSize:11,color:T2.inkSoft,marginTop:1}}>{s.sub}</div></div>
                </div>
              ))}
            </div>
            <Label mb={10}>Format</Label>
            <div style={{display:'flex',gap:8,marginBottom:20}}>
              {['pdf','xlsx'].map(f=>(
                <div key={f} onClick={()=>setFmt(f)} style={{flex:1,padding:'10px',textAlign:'center',borderRadius:9,border:`1.5px solid ${fmt===f?T2.navy:T2.rule}`,background:fmt===f?T2.navyLt:'#fff',cursor:'pointer',fontSize:12,fontWeight:fmt===f?800:600,color:fmt===f?T2.navy:T2.inkSoft,textTransform:'uppercase',letterSpacing:'0.06em'}}>{f}</div>
              ))}
            </div>
            <button onClick={generate} disabled={state==='generating'} style={{...sh.btnPrimary,width:'100%',justifyContent:'center',opacity:state==='generating'?0.65:1}}>
              {state==='generating'?'Generating…':<><I name="doc" size={13} c="#fff" sw={2}/> Generate & send to email</>}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}


// ── Modal: Trainer Roster ─────────────────────────────────────
function ModalTrainerRoster({ onClose }) {
  // GET/POST/DELETE /api/leader/cohorts/:id/trainers. Was a five-person
  // fixture ("Dr. Neha Gupta", …) with add/remove that only touched local state.
  const batchId = window._leaderBatchId;
  const [trainers, setTrainers] = useS([]);
  const [loading, setLoading] = useS(true);
  const [err, setErr] = useS('');
  const [showAdd, setShowAdd] = useS(false);
  const [newName, setNewName] = useS('');
  const [newEmail, setNewEmail] = useS('');
  const [newRole, setNewRole] = useS('Facilitator');
  const [confirmRemove, setConfirmRemove] = useS(null);
  const api = window.SRAPI;
  const load = async () => {
    if (!batchId || !api || typeof api._get !== 'function') { setLoading(false); return; }
    try { const r = await api._get('/api/leader/cohorts/' + batchId + '/trainers'); setTrainers(Array.isArray(r?.trainers) ? r.trainers : []); }
    catch (e) { setErr((e && e.message) || 'Could not load trainers'); }
    setLoading(false);
  };
  React.useEffect(() => { load(); }, []);
  const addTrainer = async () => {
    if (!newName.trim() || !newEmail.trim()) return;
    setErr('');
    try {
      await api._post('/api/leader/cohorts/' + batchId + '/trainers', { name: newName.trim(), email: newEmail.trim(), role: newRole });
      setNewName(''); setNewEmail(''); setShowAdd(false); await load();
    } catch (e) { setErr((e && e.message) || 'Could not add trainer'); }
  };
  const removeTrainer = async (id) => {
    setErr('');
    try { await api._delete('/api/leader/cohorts/' + batchId + '/trainers/' + id); setConfirmRemove(null); await load(); }
    catch (e) { setErr((e && e.message) || 'Could not remove trainer'); }
  };
  const statusStyle = (s) => {
    const k = String(s || '').toLowerCase();
    if (k === 'approved' || k === 'active') return { c: T2.sage, bg: T2.sageLt };
    if (k === 'rejected' || k === 'inactive') return { c: T2.inkSoft, bg: T2.ruleSoft };
    return { c: T2.gold, bg: T2.goldLt };
  };
  const statusLabel = (s) => String(s || 'pending').replace(/_/g, ' ');
  const initialsOf = (n) => String(n || '?').split(' ').filter(Boolean).map(w=>w[0]).join('').slice(0,2).toUpperCase();
  return (
    <div onClick={onClose} style={{position:'absolute',inset:0,zIndex:40,background:'rgba(0,0,0,0.5)',display:'flex',flexDirection:'column',justifyContent:'flex-end'}}>
      <div onClick={e=>e.stopPropagation()} style={{background:'#fff',borderRadius:'22px 22px 0 0',maxHeight:'92%',display:'flex',flexDirection:'column'}}>
        <div style={{width:40,height:4,borderRadius:2,background:T2.rule,margin:'10px auto 0',flexShrink:0}}/>
        <div style={{padding:'14px 16px 12px',borderBottom:`1px solid ${T2.rule}`,display:'flex',alignItems:'center',flexShrink:0}}>
          <div style={{flex:1,fontSize:15,fontWeight:800,color:T2.ink}}>Trainer Roster <span style={{fontSize:12,fontWeight:600,color:T2.inkSoft}}>· {trainers.length}</span></div>
          {batchId && <div onClick={()=>setShowAdd(!showAdd)} style={{cursor:'pointer',width:30,height:30,borderRadius:'50%',background:T2.navy,display:'flex',alignItems:'center',justifyContent:'center',marginRight:8}}><I name="plus" size={14} c="#fff" sw={2.4}/></div>}
          <div onClick={onClose} style={{cursor:'pointer',width:28,height:28,borderRadius:'50%',background:T2.ruleSoft,display:'flex',alignItems:'center',justifyContent:'center'}}><div style={{transform:'rotate(45deg)',display:'flex'}}><I name="plus" size={14} c={T2.inkSoft} sw={2.4}/></div></div>
        </div>
        {showAdd&&(
          <div style={{padding:'12px 16px',borderBottom:`1px solid ${T2.rule}`,background:T2.navyLt,flexShrink:0}}>
            <div style={{fontSize:12,fontWeight:700,color:T2.navy,marginBottom:8}}>Request a trainer for this cohort</div>
            <input value={newName} onChange={e=>setNewName(e.target.value)} placeholder="Full name" style={{width:'100%',padding:'9px 11px',borderRadius:9,border:`1.5px solid ${T2.rule}`,fontSize:13,fontFamily:'inherit',outline:'none',marginBottom:8,boxSizing:'border-box'}}/>
            <input value={newEmail} onChange={e=>setNewEmail(e.target.value)} placeholder="Email" type="email" style={{width:'100%',padding:'9px 11px',borderRadius:9,border:`1.5px solid ${T2.rule}`,fontSize:13,fontFamily:'inherit',outline:'none',marginBottom:8,boxSizing:'border-box'}}/>
            <div style={{display:'flex',gap:8}}>
              <select value={newRole} onChange={e=>setNewRole(e.target.value)} style={{flex:1,padding:'9px 11px',borderRadius:9,border:`1.5px solid ${T2.rule}`,fontSize:12.5,fontFamily:'inherit',background:'#fff',outline:'none'}}>
                {['Lead Facilitator','Facilitator','Guest Coach','Content Lead','Observer'].map(r=><option key={r}>{r}</option>)}
              </select>
              <button onClick={addTrainer} style={{...sh.btnPrimary,padding:'9px 16px'}}>Request</button>
            </div>
            <div style={{fontSize:11,color:T2.inkSoft,marginTop:8}}>Requests go to the SkaleCap team for approval.</div>
          </div>
        )}
        <div style={{overflow:'auto',flex:1}}>
          {err && <div style={{margin:'12px 16px',padding:'10px 13px',borderRadius:10,background:T2.roseLt,border:`1px solid ${T2.rose}44`,fontSize:12.5,color:T2.rose,fontWeight:600}}>{err}</div>}
          {confirmRemove&&(
            <div style={{margin:'12px 16px',padding:'13px',borderRadius:12,background:T2.roseLt,border:`1px solid ${T2.rose}44`}}>
              <div style={{fontSize:13,fontWeight:700,color:T2.rose,marginBottom:10}}>Remove {trainers.find(t=>t.id===confirmRemove)?.name}?</div>
              <div style={{display:'flex',gap:8}}>
                <button onClick={()=>setConfirmRemove(null)} style={{...sh.btnGhost,flex:1,justifyContent:'center',fontSize:12}}>Cancel</button>
                <button onClick={()=>removeTrainer(confirmRemove)} style={{flex:1,padding:'8px',borderRadius:8,border:'none',background:T2.rose,color:'#fff',fontSize:12,fontWeight:800,fontFamily:'inherit',cursor:'pointer'}}>Remove</button>
              </div>
            </div>
          )}
          {loading ? (
            <div style={{padding:'20px 16px',fontSize:13,color:T2.inkSoft}}>Loading trainers…</div>
          ) : !batchId ? (
            <LeaderEmpty icon="person" title="No cohort selected" body="Pick a cohort first to see its trainers."/>
          ) : trainers.length === 0 ? (
            <LeaderEmpty icon="person" title="No trainers yet" body="Request a trainer with the + button. The SkaleCap team reviews each request."/>
          ) : trainers.map((t,i,a)=>{
            const st = statusStyle(t.status);
            return (
            <div key={t.id} style={{display:'flex',alignItems:'center',gap:12,padding:'13px 16px',borderBottom:i<a.length-1?`1px solid ${T2.ruleSoft}`:'none'}}>
              <Avatar initials={initialsOf(t.name)} size={36} bg={T2.navyLt} fg={T2.navy}/>
              <div style={{flex:1,minWidth:0}}>
                <div style={{fontSize:13,fontWeight:700,color:T2.ink}}>{t.name}</div>
                <div style={{fontSize:11,color:T2.inkSoft,marginTop:2,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{t.role || 'Facilitator'} · {t.email}</div>
              </div>
              <span style={{fontSize:9.5,fontWeight:800,color:st.c,background:st.bg,padding:'2px 7px',borderRadius:8,letterSpacing:'0.06em',textTransform:'uppercase',marginRight:6}}>{statusLabel(t.status)}</span>
              <div onClick={()=>setConfirmRemove(t.id)} style={{cursor:'pointer',padding:4}}><I name="plus" size={13} c={T2.rose} sw={2.2} style={{transform:'rotate(45deg)'}}/></div>
            </div>
          );})}
        </div>
      </div>
    </div>
  );
}

// ── Modal: Programme Settings ─────────────────────────────────
// PATCH /api/leader/cohorts/:id. Every field is a real sc_batches column (the
// earlier modal here was a fixture with no backend and was removed). The
// completion cutoffs set the status learners see on Growth and the at-risk /
// ahead flags on the roster and cohort report.
function ModalProgrammeSettings({ cohort, onClose, onSaved }) {
  const d10 = (v) => (v ? String(v).slice(0, 10) : '');
  const [name, setName]     = useS(cohort?.name || '');
  const [start, setStart]   = useS(d10(cohort?.start_date));
  const [end, setEnd]       = useS(d10(cohort?.end_date));
  const [aiOn, setAiOn]     = useS(cohort?.ai_chatbot_enabled == null ? true : !!Number(cohort.ai_chatbot_enabled));
  const [aiCap, setAiCap]   = useS(String(cohort?.ai_monthly_cap ?? 200));
  const [atRisk, setAtRisk] = useS(String(cohort?.completion_at_risk_pct ?? 40));
  const [ahead, setAhead]   = useS(String(cohort?.completion_ahead_pct ?? 80));
  const [saving, setSaving] = useS(false);
  const [err, setErr]       = useS('');
  const [saved, setSaved]   = useS(false);
  // An empty number field would serialise to null and the server would keep
  // the stored value without saying so, so empties are caught here.
  const whole = (s) => /^\d+$/.test(String(s).trim()) ? Number(s) : null;
  const save = async () => {
    setErr(''); setSaved(false);
    if (!name.trim()) return setErr('Programme name is required.');
    if (start && end && end < start) return setErr('End date must be on or after the start date.');
    const cap = whole(aiCap), lo = whole(atRisk), hi = whole(ahead);
    if (cap == null) return setErr('AI Coach monthly cap must be a whole number.');
    if (lo == null || hi == null) return setErr('Both completion cutoffs must be whole numbers.');
    setSaving(true);
    try {
      const r = await window.SRAPI._patch('/api/leader/cohorts/' + cohort.id, {
        name: name.trim(), start_date: start || null, end_date: end || null,
        ai_chatbot_enabled: aiOn, ai_monthly_cap: cap,
        completion_at_risk_pct: lo, completion_ahead_pct: hi,
      });
      if (r && r.batch) { onSaved && onSaved(r.batch); setSaved(true); }
    } catch (e) { setErr((e && e.message) || 'Could not save settings'); }
    setSaving(false);
  };
  const field = {width:'100%',padding:'9px 11px',borderRadius:9,border:`1.5px solid ${T2.rule}`,fontSize:13,fontFamily:'inherit',outline:'none',boxSizing:'border-box',background:'#fff'};
  const row = (label, input, hint) => (
    <div style={{marginBottom:14}}>
      <Label mb={6}>{label}</Label>
      {input}
      {hint && <div style={{fontSize:11,color:T2.inkSoft,marginTop:5,lineHeight:1.4}}>{hint}</div>}
    </div>
  );
  return (
    <div onClick={onClose} style={{position:'absolute',inset:0,zIndex:40,background:'rgba(0,0,0,0.5)',display:'flex',flexDirection:'column',justifyContent:'flex-end'}}>
      <div onClick={e=>e.stopPropagation()} style={{background:'#fff',borderRadius:'22px 22px 0 0',maxHeight:'92%',display:'flex',flexDirection:'column'}}>
        <div style={{width:40,height:4,borderRadius:2,background:T2.rule,margin:'10px auto 0',flexShrink:0}}/>
        <div style={{padding:'14px 16px 12px',borderBottom:`1px solid ${T2.rule}`,display:'flex',alignItems:'center',flexShrink:0}}>
          <div style={{flex:1,fontSize:15,fontWeight:800,color:T2.ink}}>Programme settings</div>
          <div onClick={onClose} style={{cursor:'pointer',width:28,height:28,borderRadius:'50%',background:T2.ruleSoft,display:'flex',alignItems:'center',justifyContent:'center'}}><div style={{transform:'rotate(45deg)',display:'flex'}}><I name="plus" size={14} c={T2.inkSoft} sw={2.4}/></div></div>
        </div>
        <div style={{overflow:'auto',flex:1,padding:'16px'}}>
          {!cohort ? (
            <LeaderEmpty icon="team" title="No cohort selected" body="Pick a cohort first to edit its settings."/>
          ) : (<>
            {row('Programme name', <input value={name} onChange={e=>setName(e.target.value)} style={field}/>)}
            <div style={{display:'flex',gap:10}}>
              <div style={{flex:1}}>{row('Start date', <input type="date" value={start} onChange={e=>setStart(e.target.value)} style={field}/>)}</div>
              <div style={{flex:1}}>{row('End date', <input type="date" value={end} onChange={e=>setEnd(e.target.value)} style={field}/>)}</div>
            </div>
            <div style={{fontSize:11,color:T2.inkSoft,marginTop:-8,marginBottom:14}}>Dates drive the "Month X of Y" learners see on Growth.</div>

            <Label mb={8}>Completion status</Label>
            <div style={{display:'flex',gap:10}}>
              <div style={{flex:1}}>{row('At risk below (%)', <input inputMode="numeric" value={atRisk} onChange={e=>setAtRisk(e.target.value)} style={field}/>)}</div>
              <div style={{flex:1}}>{row('Ahead at (%)', <input inputMode="numeric" value={ahead} onChange={e=>setAhead(e.target.value)} style={field}/>)}</div>
            </div>
            <div style={{fontSize:11,color:T2.inkSoft,marginTop:-8,marginBottom:14,lineHeight:1.4}}>
              Learners below the first figure show "Getting started" and are flagged at risk; at or above the second they show "On track".
            </div>

            <Label mb={8}>AI Coach</Label>
            <div onClick={()=>setAiOn(!aiOn)} style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'10px 12px',border:`1.5px solid ${T2.rule}`,borderRadius:9,marginBottom:10,cursor:'pointer'}}>
              <span style={{fontSize:13,color:T2.ink,fontWeight:600}}>AI Coach enabled</span>
              <div style={{width:38,height:22,borderRadius:11,background:aiOn?T2.sage:T2.rule,position:'relative',transition:'background .15s'}}>
                <div style={{position:'absolute',top:2,left:aiOn?18:2,width:18,height:18,borderRadius:'50%',background:'#fff',transition:'left .15s'}}/>
              </div>
            </div>
            {row('Messages per learner per month', <input inputMode="numeric" value={aiCap} onChange={e=>setAiCap(e.target.value)} style={field}/>, 'Between 0 and 5000.')}

            {err && <div style={{marginBottom:12,padding:'10px 13px',borderRadius:10,background:T2.roseLt,border:`1px solid ${T2.rose}44`,fontSize:12.5,color:T2.rose,fontWeight:600}}>{err}</div>}
            {saved && !err && <div style={{marginBottom:12,padding:'10px 13px',borderRadius:10,background:T2.sageLt,fontSize:12.5,color:T2.sage,fontWeight:600}}>Saved.</div>}
            <button onClick={save} disabled={saving} style={{...sh.btnPrimary,width:'100%',justifyContent:'center',opacity:saving?0.6:1}}>{saving ? 'Saving…' : 'Save settings'}</button>
          </>)}
        </div>
      </div>
    </div>
  );
}

// ── Modal: Sign Out Confirm ───────────────────────────────────
function ModalSignOut({ onClose, onConfirm }) {
  return (
    <div onClick={onClose} style={{position:'absolute',inset:0,zIndex:40,background:'rgba(0,0,0,0.5)',display:'flex',flexDirection:'column',justifyContent:'flex-end'}}>
      <div onClick={e=>e.stopPropagation()} style={{background:'#fff',borderRadius:'22px 22px 0 0',padding:'28px 20px 32px',display:'flex',flexDirection:'column',alignItems:'center',gap:14}}>
        <div style={{width:56,height:56,borderRadius:'50%',background:T2.roseLt,display:'flex',alignItems:'center',justifyContent:'center',fontSize:26}}>👋</div>
        <div style={{textAlign:'center'}}>
          <div style={{fontSize:18,fontWeight:800,color:T2.ink}}>Sign out?</div>
          <div style={{fontSize:13,color:T2.inkSoft,marginTop:5,lineHeight:1.5}}>You'll need to sign back in to access your dashboard and cohort data.</div>
        </div>
        <div style={{width:'100%',display:'flex',gap:10,marginTop:4}}>
          <button onClick={onClose} style={{...sh.btnGhost,flex:1,justifyContent:'center'}}>Cancel</button>
          <button onClick={onConfirm} style={{flex:1,padding:'13px',borderRadius:10,border:'none',background:T2.rose,color:'#fff',fontSize:14,fontWeight:800,fontFamily:'inherit',cursor:'pointer'}}>Sign out</button>
        </div>
      </div>
    </div>
  );
}

function ScreenLeaderMe({ onNav, active = 'me' }) {
  // Wired against GET /api/leader/cohorts (+ /trainers for the selected one).
  // Was a hardcoded "Riya Desai / Asha Tech / 3 cohorts / 48 participants"
  // fixture with three invented cohorts — never acceptable on a paying
  // leader's production screen (LAUNCH_PLAN_10_DAYS.md day 3).
  const [modal, setModal] = useS(null); // 'export' | 'roster' | 'signout'
  const [cohorts, setCohorts] = useS([]);
  const [trainerCount, setTrainerCount] = useS(null);
  const [loading, setLoading] = useS(true);
  const [activeId, setActiveId] = useS(window._leaderBatchId || null);
  const me = (() => { try { return JSON.parse(localStorage.getItem('sr_user') || 'null') || {}; } catch { return {}; } })();
  const name = me.name || me.email || 'Programme lead';
  const initials = String(name).split(' ').filter(Boolean).slice(0,2).map(w=>w[0]).join('').toUpperCase() || '·';
  const loadTrainers = (id) => {
    if (!id || !window.SRAPI || typeof window.SRAPI._get !== 'function') return;
    window.SRAPI._get('/api/leader/cohorts/' + id + '/trainers')
      .then(t => setTrainerCount(Array.isArray(t?.trainers) ? t.trainers.length : null))
      .catch(() => setTrainerCount(null));
  };
  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!window.SRAPI || typeof window.SRAPI._get !== 'function') { setLoading(false); return; }
      try {
        const cData = await window.SRAPI._get('/api/leader/cohorts');
        if (cancelled) return;
        const list = Array.isArray(cData?.cohorts) ? cData.cohorts : [];
        setCohorts(list);
        const id = window._leaderBatchId || list[0]?.id || null;
        if (id) { window._leaderBatchId = id; setActiveId(id); loadTrainers(id); }
      } catch (e) { /* empty state below */ }
      if (!cancelled) setLoading(false);
    })();
    return () => { cancelled = true; };
  }, []);
  const switchCohort = (c) => {
    window._leaderBatchId = c.id; setActiveId(c.id); setTrainerCount(null); loadTrainers(c.id);
  };
  const participants = cohorts.reduce((n,c)=>n+(Number(c.participant_count)||0),0);
  const activeCohort = cohorts.find(c=>c.id===activeId) || null;
  const ADMIN_ROWS = [
    {l:'Export report',       icon:'doc',    key:'export'},
    {l:'Trainer roster',      icon:'person', key:'roster'},
    ...(activeCohort ? [{l:'Programme settings', icon:'learn', key:'settings'}] : []),
    {l:'Sign out',            icon:'arrowR', key:'signout', danger:true},
  ];
  const onSettingsSaved = (b) => setCohorts(list => list.map(c => (c.id === b.id ? { ...c, ...b } : c)));
  const stats = [
    {v: loading ? '…' : cohorts.length, l:'Cohorts'},
    {v: loading ? '…' : participants,   l:'Participants'},
    {v: loading ? '…' : (trainerCount == null ? '—' : trainerCount), l:'Trainers'},
  ];
  return (
    <div style={sh.shell}>
      {modal==='export'   && <ModalExportReport      onClose={()=>setModal(null)}/>}
      {modal==='roster'   && <ModalTrainerRoster       onClose={()=>setModal(null)}/>}
      {modal==='settings' && <ModalProgrammeSettings cohort={activeCohort} onClose={()=>setModal(null)} onSaved={onSettingsSaved}/>}
      {modal==='signout'  && <ModalSignOut onClose={()=>setModal(null)} onConfirm={()=>{setModal(null);onNav&&onNav('logout');}}/>}
      <div style={sh.scroll}>
        <Header kicker="Programme lead" title={name} sub={<>{me.email || ''}</>} right={<Avatar initials={initials} size={36} bg={T2.goldHi} fg={T2.navy}/>}/>
        <div style={{padding:'18px 16px 0'}}>
          <div style={{display:'flex', borderBottom:`1px solid ${T2.rule}`, paddingBottom:18}}>
            {stats.map((s,i,arr)=>(
              <div key={i} style={{flex:1,textAlign:'center',borderRight:i<arr.length-1?`1px solid ${T2.ruleSoft}`:'none'}}>
                <div style={{fontSize:20,fontWeight:700,color:T2.ink,letterSpacing:'-0.02em',lineHeight:1}}>{s.v}</div>
                <Label mt={6}>{s.l}</Label>
              </div>
            ))}
          </div>
        </div>

        <Section kicker="Switch cohort">
          {loading ? (
            <div style={{padding:'20px 16px',fontSize:13,color:T2.inkSoft}}>Loading cohorts…</div>
          ) : cohorts.length === 0 ? (
            <LeaderEmpty icon="team" title="No cohorts assigned" body="Cohorts you lead or train will appear here once your programme administrator assigns you."/>
          ) : (
          <div style={{padding:'0 16px'}}>
            <div style={{border:`1px solid ${T2.rule}`,borderRadius:12,overflow:'hidden'}}>
              {cohorts.map((c,i,a)=>(
                <div key={c.id} onClick={()=>switchCohort(c)} style={{padding:'13px 14px',display:'flex',alignItems:'center',borderBottom:i<a.length-1?`1px solid ${T2.ruleSoft}`:'none',background:activeId===c.id?T2.navyLt:'transparent',cursor:'pointer'}}>
                  <div style={{flex:1}}>
                    <div style={{fontSize:13.5,fontWeight:700,color:T2.ink}}>{c.name}</div>
                    <div style={{fontSize:11,color:T2.inkSoft,marginTop:2}}>{Number(c.participant_count)||0} participants · {c.status || 'active'}</div>
                  </div>
                  {activeId===c.id
                    ? <span style={{fontSize:10,fontWeight:800,color:T2.navy,letterSpacing:'0.08em',textTransform:'uppercase',display:'flex',alignItems:'center',gap:4}}><I name="check" size={10} c={T2.navy} sw={2.5}/>Viewing</span>
                    : <span style={{fontSize:11,color:T2.navy,fontWeight:700,cursor:'pointer'}}>Switch →</span>}
                </div>
              ))}
            </div>
            {activeCohort && cohorts.length > 1 && (
              <div style={{marginTop:10,padding:'10px 13px',borderRadius:10,background:T2.tealLt,border:`1px solid ${T2.teal}33`,display:'flex',alignItems:'center',gap:8}}>
                <I name="check" size={13} c={T2.teal} sw={2.2}/>
                <div style={{fontSize:12,color:T2.teal,fontWeight:600}}>Viewing <strong>{activeCohort.name}</strong>. Cohort, sessions and exports use this cohort.</div>
              </div>
            )}
          </div>
          )}
        </Section>

        <Section kicker="Admin">
          <div style={{padding:'0 16px'}}>
            <div style={{border:`1px solid ${T2.rule}`,borderRadius:12,overflow:'hidden'}}>
              {ADMIN_ROWS.map((r,i,a)=>(
                <div key={r.key} onClick={()=>setModal(r.key)} style={{padding:'14px',display:'flex',alignItems:'center',gap:12,borderBottom:i<a.length-1?`1px solid ${T2.ruleSoft}`:'none',cursor:'pointer',background:'#fff'}}>
                  <div style={{width:34,height:34,borderRadius:9,background:r.danger?T2.roseLt:T2.navyLt,display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>
                    <I name={r.icon} size={15} c={r.danger?T2.rose:T2.navy} sw={2}/>
                  </div>
                  <span style={{flex:1,fontSize:13.5,fontWeight:600,color:r.danger?T2.rose:T2.ink}}>{r.l}</span>
                  <I name="chevR" size={13} c={T2.inkFaint} sw={2}/>
                </div>
              ))}
            </div>
          </div>
        </Section>

        <div style={{height:96}}/>
      </div>
      <BottomNav active={active} role="leader" onNav={onNav}/>
    </div>
  );
}

// ============================================================
// AI COACH CHAT SCREEN  (shared by participant + leader)
// ============================================================
function ScreenAICoach({ onNav, active = 'ai', role = 'participant' }) {
  const useRef    = React.useRef;
  const useEffect = React.useEffect;


  // Real AI Coach call — POST /api/participant/ai/chat. This used to be a
  // client-side keyword-matched canned-response engine (INTENTS array) with
  // fake hardcoded score numbers baked into the reply text, regardless of
  // the participant's actual data. The backend endpoint was already fully
  // built (real GPT-4o-mini call when OPENAI_API_KEY is set, a sensible
  // fallback reply when it's not, per-message logging, per-batch monthly
  // quota) — it just wasn't being called from here.
  const askCoach = async (message, history) => {
    if (!window.SRAPI || typeof window.SRAPI._post !== 'function') {
      return { reply: "I'm not connected right now — check your connection and try again." };
    }
    try {
      return await window.SRAPI._post('/api/participant/ai/chat', {
        message,
        history: (history || []).slice(-6).map(m => ({
          role: m.from === 'user' ? 'user' : 'assistant',
          content: m.text,
        })),
      });
    } catch (e) {
      if (e && e.status === 429) {
        return { reply: "You've reached this month's AI Coach message limit. It resets next month — in the meantime your trainer can still help with any of this.", quotaExceeded: true };
      }
      return { reply: "Sorry, I couldn't get a response just now. Please try again in a moment." };
    }
  };

  // ── Opening message
  // This used to be a three-message hardcoded "conversation": the coach
  // greeting everyone as "Priya", claiming it had reviewed their progress,
  // asserting an invented score ("Strategic Thinking 2.9 vs cohort 3.7"),
  // and — worst of it — a fabricated *user* turn ("Yes, I struggle with
  // long-term planning") that the participant never typed. That fake history
  // was also passed to the model on the next real question, so the coach was
  // being told the learner had said something they hadn't. Replaced with a
  // single genuine opening that claims nothing about the person.
  const INIT_MSGS = [
    { from:'ai', time: (() => { const n = new Date(); return `${n.getHours()}:${String(n.getMinutes()).padStart(2,'0')}`; })(), text:"I'm your AI Coach. Ask me about anything in your programme — a concept you want unpacked, a conversation you're preparing for, or where to focus next." },
  ];

  const SUGGESTIONS = ['What are my learning gaps?', 'Help me prep my 1:1', 'Explain delegation', 'Better feedback', 'My progress stats'];

  // Check if coming from lesson notes ("Ask AI Coach" button)
  const noteContext = window._aiCoachContext;
  const noteInitMsg = noteContext
    ? { from:'user', text:`From my notes on "${noteContext.lessonTitle}":\n\n${noteContext.note}\n\nCan you help me think this through?`, time: (() => { const n = new Date(); return `${n.getHours()}:${String(n.getMinutes()).padStart(2,'0')}`; })() }
    : null;

  const [msgs, setMsgs]         = useS(noteInitMsg ? [...INIT_MSGS, noteInitMsg] : INIT_MSGS);
  const [input, setInput]       = useS('');
  const [thinking, setThinking] = useS(noteInitMsg ? true : false);
  const [booked, setBooked]     = useS(null);
  const [showDown, setShowDown] = useS(false);
  const [quota, setQuota]       = useS(null); // { used, cap, pct, unlimited } from GET /participant/ai/quota
  // Slash-command state
  const [slashMode,  setSlashMode]  = useS(null);   // null | 'cmds' | 'conv'
  const [cmdFilter,  setCmdFilter]  = useS('');
  const [convCmd,    setConvCmd]    = useS(null);
  const [convStep,   setConvStep]   = useS(0);
  const [convDataSC, setConvDataSC] = useS({});
  const [convAns,    setConvAns]    = useS('');
  const scrollRef = useRef(null);
  const endRef    = useRef(null);

  // If we came from lesson notes, auto-reply to that message
  useEffect(() => {
    if (noteInitMsg && thinking) {
      window._aiCoachContext = null;
      askCoach(noteContext.note, INIT_MSGS).then(res => {
        setMsgs(prev => [...prev, {
          from:'ai',
          text: `Great reflection on "${noteContext.lessonTitle}"! ${res.reply}`,
          time: (() => { const n = new Date(); return `${n.getHours()}:${String(n.getMinutes()).padStart(2,'0')}`; })(),
        }]);
        if (res.usage) setQuota(res.usage);
        setThinking(false);
      });
    }
  }, []);

  // Load the real quota on open so the input can be disabled before the
  // user even tries to send a message that would 429.
  useEffect(() => {
    if (window.SRAPI && typeof window.SRAPI._get === 'function') {
      window.SRAPI._get('/api/participant/ai/quota').then(setQuota).catch(() => {});
    }
  }, []);

  // Auto-scroll on new message / typing indicator
  useEffect(() => {
    endRef.current && endRef.current.scrollIntoView({ behavior: 'smooth' });
    setShowDown(false);
  }, [msgs, thinking]);

  // Show ↓ button when user has scrolled up
  const handleScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    setShowDown(el.scrollHeight - el.scrollTop - el.clientHeight > 80);
  };

  const scrollToBottom = () => endRef.current && endRef.current.scrollIntoView({ behavior: 'smooth' });

  const ts = () => { const n = new Date(); return `${n.getHours()}:${String(n.getMinutes()).padStart(2,'0')}`; };

  // Role → LD_CMDS key
  const ldCmds = (() => {
    const map = { participant:'Participant', leader:'Trainer', trainer:'Trainer',
                  'l&d manager':'L&D Manager', 'l&d head':'L&D Head' };
    const key = map[(role||'').toLowerCase()] || (role === 'leader' ? 'Trainer' : 'Participant');
    return (window.LD_CMDS && window.LD_CMDS[key]) || [];
  })();

  // SkaleCap data — live from SR_SC_DATA (populated by SRAPI.fetchSCData()) or mock fallback
  const [scLiveData, setScLiveData] = useS(window.SR_SC_DATA || null);
  React.useEffect(() => {
    const h = (e) => setScLiveData(e.detail);
    document.addEventListener('sr:scdata-loaded', h);
    // Kick off fetch if not yet done
    if (!window.SR_SC_DATA && window.SRAPI && typeof window.SRAPI.fetchSCData === 'function') {
      window.SRAPI.fetchSCData().catch(() => {});
    }
    return () => document.removeEventListener('sr:scdata-loaded', h);
  }, []);

  // Live only — an empty workspace shows empty pickers, never invented
  // batches/participants/modules.
  const SC_DATA_SRC = {
    batch:    (scLiveData && Array.isArray(scLiveData.batches)) ? scLiveData.batches : [],
    employee: (scLiveData && Array.isArray(scLiveData.participants)) ? scLiveData.participants : [],
    module:   (scLiveData && Array.isArray(scLiveData.modules)) ? scLiveData.modules : [],
  };
  const scGetItems = (key, q) => {
    const arr = SC_DATA_SRC[key] || [];
    const items = arr.map(i=>({id:i.id||i.name,label:i.name||i.label,meta:i.meta||''}));
    if (!q) return items;
    return items.filter(i=>i.label.toLowerCase().includes(q.toLowerCase()));
  };

  // Submit completed SkaleCap FlowCard to backend
  const submitSCFlowCard = (cmd, data, fid) => {
    if (window.SRAPI && typeof window.SRAPI.submitFlowCard === 'function') {
      window.SRAPI.submitFlowCard(cmd, data, 'skalecap').then(res => {
        if (res && res.success === false) {
          setMsgs(prev => prev.map(m => (m.flowCard && m.flowCard._fid === fid)
            ? { ...m, flowCard: { ...m.flowCard, result: { title: 'Could not save', message: res.error, error: true } } } : m));
        }
      }).catch(() => {});
    }
  };

  const filteredCmds = ldCmds.filter(c => !cmdFilter ||
    c.cmd.startsWith(cmdFilter) || c.label.toLowerCase().includes(cmdFilter.toLowerCase()));

  const openSlashCmds = (filter='') => { setSlashMode('cmds'); setCmdFilter(filter); };

  const selectSlashCmd = (def) => {
    if (!def) return;
    if (def.flow && def.flow.length > 0) {
      setConvCmd(def); setConvStep(0); setConvDataSC({}); setConvAns(''); setSlashMode('conv');
    } else {
      // Zero-step: post instant card as AI message
      setMsgs(prev => [...prev, {
        from:'ai', time:ts(),
        text:'', flowCard:{ cardType:'flow', cmd:def.cmd, icon:def.icon||'📋',
          label:def.label, cat:def.cat||'ops', data:{}, flow:[], ai:def.ai||[],
          result:def.result||{ title:def.label } },
      }]);
      setSlashMode(null); setCmdFilter(''); setInput('');
    }
  };

  const handleConvAnswerSC = (val) => {
    if (!val || !val.trim() || !convCmd) return;
    const step = convCmd.flow[convStep];
    const newData = { ...convDataSC, [step.key]: val.trim() };
    setConvDataSC(newData);
    setConvAns('');
    if (convStep + 1 < convCmd.flow.length) {
      setConvStep(s => s + 1);
    } else {
      const fid = 'f' + Date.now() + Math.random().toString(36).slice(2, 6);
      setMsgs(prev => [...prev, {
        from:'ai', time:ts(), text:'', flowCard:{
          _fid:fid, cardType:'flow', cmd:convCmd.cmd, icon:convCmd.icon||'📋', label:convCmd.label,
          cat:convCmd.cat||'ops', data:newData, flow:convCmd.flow,
          ai:convCmd.ai||[], result:convCmd.result||{ title:convCmd.label+' ✓' },
        },
      }]);
      submitSCFlowCard(convCmd.cmd, newData, fid);
      setSlashMode(null); setConvCmd(null); setConvStep(0); setConvDataSC({}); setInput('');
    }
  };

  const dismissSlash = () => { setSlashMode(null); setCmdFilter(''); setConvCmd(null); setConvStep(0); setConvDataSC({}); setConvAns(''); };

  const send = (txt) => {
    const t = (txt !== undefined ? txt : input).trim();
    if (!t) return;
    if (quota && !quota.unlimited && quota.pct >= 100) return; // input is disabled in this state too
    const time = ts();
    const historyForApi = msgs;
    setMsgs(prev => [...prev, { from:'user', text:t, time }]);
    setInput('');
    setThinking(true);
    askCoach(t, historyForApi).then(res => {
      setMsgs(prev => [...prev, { from:'ai', text: res.reply, time }]);
      if (res.usage) setQuota(res.usage);
      setThinking(false);
    });
  };

  const renderText = (t) => {
    return t.split(/(\*\*[^*]+\*\*|_[^_]+_)/g).map((p, i) => {
      if (p.startsWith('**') && p.endsWith('**')) return <strong key={i}>{p.slice(2,-2)}</strong>;
      if (p.startsWith('_')  && p.endsWith('_'))  return <em key={i}>{p.slice(1,-1)}</em>;
      return <span key={i}>{p}</span>;
    });
  };

  // The lesson-link cards that used to render under coach replies were
  // removed with the fake LESSONS catalogue they read from. Nothing set
  // `m.lesson` any more either — only a hardcoded seed message did — so
  // this path could never fire again.
  // ── Per-message action icons
  // Copy + Retry are real. The thumbs-up / thumbs-down pair that used to sit
  // between them was removed rather than left as decoration: there is no
  // feedback endpoint behind it (sc_ai_chat_log rows aren't keyed to rendered
  // messages), so clicking it could never have recorded anything. Same call
  // as the fake voice-input removal above — see FIX_TRACKER for the scope
  // note on adding real coach feedback.
  const MsgActions = ({ m, idx }) => {
    const [copied, setCopied] = useS(false);
    const doCopy = () => {
      const text = m && m.text ? m.text : '';
      if (!text) return;
      const done = () => { setCopied(true); setTimeout(()=>setCopied(false), 1600); };
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(text).then(done).catch(()=>{});
      } else {
        try {
          const ta = document.createElement('textarea');
          ta.value = text; ta.style.position = 'fixed'; ta.style.opacity = '0';
          document.body.appendChild(ta); ta.select(); document.execCommand('copy');
          document.body.removeChild(ta); done();
        } catch (e) { /* clipboard unavailable */ }
      }
    };
    // Re-ask the user turn that produced this reply.
    const lastUser = (() => {
      for (let i = (idx != null ? idx : msgs.length) - 1; i >= 0; i--) {
        if (msgs[i] && msgs[i].from === 'user' && msgs[i].text) return msgs[i].text;
      }
      return null;
    })();
    const doRetry = () => { if (lastUser && !thinking) send(lastUser); };
    return (
      <div style={{ display:'flex', gap:18, marginTop:10, paddingBottom:18, borderBottom:`1px solid ${T2.ruleSoft}` }}>
        <div onClick={doCopy} title={copied ? 'Copied' : 'Copy'}
             style={{ cursor:'pointer', opacity: m && m.text ? 0.75 : 0.25, display:'flex', alignItems:'center' }}>
          <I name={copied ? 'check' : 'copy2'} size={16} c={copied ? T2.sage : T2.inkSoft} sw={1.7}/>
        </div>
        <div onClick={doRetry} title={lastUser ? 'Retry' : 'Nothing to retry'}
             style={{ cursor: lastUser && !thinking ? 'pointer' : 'default', opacity: lastUser && !thinking ? 0.75 : 0.25, display:'flex', alignItems:'center' }}>
          <I name="retry" size={16} c={T2.inkSoft} sw={1.7}/>
        </div>
      </div>
    );
  };

  return (
    <div style={sh.shell}>

      {/* Voice input previously lived here — removed rather than left as a
          fake feature: it never accessed the microphone at all, just
          dropped a random canned sample sentence into the input box after
          two timed delays made to look like listening/transcribing. */}

      {/* ── Minimal header (Claude-style) */}
      <div style={{
        position:'absolute', top:0, left:0, right:0, zIndex:3,
        background:T2.paper, borderBottom:`1px solid ${T2.ruleSoft}`,
        padding:'12px 16px', display:'flex', alignItems:'center', gap:10,
      }}>
        {/* The hamburger and kebab that used to flank this title were removed:
            neither opened anything — there is no drawer or overflow menu in
            this screen for them to point at. "+" starts a fresh conversation,
            which is real. */}
        <div style={{ width:20, flexShrink:0 }}/>
        <div style={{ flex:1, textAlign:'center' }}>
          <div style={{ fontFamily:"'Lato',sans-serif", fontSize:15.5, fontWeight:800, color:T2.ink, letterSpacing:'-0.01em' }}>AI Coach</div>
        </div>
        <div style={{ display:'flex', gap:10, alignItems:'center' }}>
          <div
            onClick={()=>{ if (thinking) return; setMsgs([]); setInput(''); dismissSlash(); }}
            title="New conversation"
            style={{ cursor: thinking ? 'default' : 'pointer', opacity: thinking ? 0.35 : 1 }}>
            <I name="plus" size={20} c={T2.ink} sw={2}/>
          </div>
        </div>
      </div>

      {/* ── Messages scroll area */}
      <div ref={scrollRef} onScroll={handleScroll} style={{
        position:'absolute', top:58, bottom:148, left:0, right:0,
        overflowY:'auto', padding:'16px 18px 8px',
        display:'flex', flexDirection:'column',
        background:T2.paper,
      }}>
        {/* Context pill */}
        <div style={{
          alignSelf:'center', marginBottom:18,
          background:T2.navyLt, borderRadius:20, padding:'5px 12px',
          display:'flex', alignItems:'center', gap:6,
        }}>
          <div style={{ width:6, height:6, borderRadius:'50%', background:'#4ADE80', flexShrink:0 }}/>
          <span style={{ fontSize:11, color:T2.navy, fontWeight:700 }}>Live data · 3.96 score · 68% complete · 7-day streak</span>
        </div>

        {msgs.map((m, idx) => (
          m.from === 'user' ? (
            // User bubble — right aligned, grey pill
            <div key={idx} style={{ display:'flex', justifyContent:'flex-end', marginBottom:20 }}>
              <div style={{
                background:'#EBEBEB', borderRadius:22, padding:'10px 16px',
                fontSize:16, color:T2.ink, maxWidth:'72%', lineHeight:1.5,
                fontFamily:"'Lato',sans-serif",
              }}>{m.text}</div>
            </div>
          ) : (
            // AI message — full width, clean text, no bubble
            <div key={idx} style={{ marginBottom:4 }}>
              <div style={{
                fontSize:16, color:T2.ink, lineHeight:1.7,
                fontFamily:"'Lato',sans-serif", fontWeight:400,
              }}>
                {m.text.split('\n').map((line, li) => (
                  <div key={li} style={{ marginTop: li>0 ? 7 : 0 }}>{renderText(line)}</div>
                ))}
              </div>
              {m.flowCard && window.FlowCard && React.createElement(window.FlowCard, { card:m.flowCard })}
              <MsgActions m={m} idx={idx}/>
            </div>
          )
        ))}

        {/* Typing indicator */}
        {thinking && (
          <div style={{ marginBottom:16, display:'flex', gap:5, alignItems:'center', paddingLeft:2 }}>
            {[0,1,2].map(d => (
              <div key={d} style={{
                width:8, height:8, borderRadius:'50%', background:T2.inkFaint,
                animation:`bounce 1.1s ${d*0.22}s infinite`,
              }}/>
            ))}
          </div>
        )}

        {/* Disclaimer */}
        <div style={{
          display:'flex', alignItems:'center', justifyContent:'center', gap:6,
          padding:'10px 0 4px', marginTop:4,
        }}>
          <I name="aicoach" size={12} c={T2.inkFaint} sw={1.5}/>
          <span style={{ fontSize:11.5, color:T2.inkFaint, textAlign:'center', lineHeight:1.4 }}>
            AI Coach can make mistakes. Always verify with your trainer.
          </span>
        </div>

        <div ref={endRef} style={{ height:4 }}/>
      </div>

      {/* ── Scroll-to-bottom button */}
      {showDown && (
        <div onClick={scrollToBottom} style={{
          position:'absolute', zIndex:4,
          bottom:156, left:'50%', transform:'translateX(-50%)',
          width:36, height:36, borderRadius:'50%',
          background:'#fff', border:`1px solid ${T2.rule}`,
          boxShadow:'0 2px 10px rgba(0,0,0,0.14)',
          display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer',
        }}>
          <I name="chevD" size={18} c={T2.inkMid} sw={2.2}/>
        </div>
      )}

      {/* ── Input area */}
      <div style={{
        position:'absolute', bottom:72, left:0, right:0,
        background:T2.paper, borderTop:`1px solid ${T2.ruleSoft}`,
        padding:'8px 14px 10px',
      }}>
        {/* ── Slash command picker */}
        {slashMode === 'cmds' && ldCmds.length > 0 && (
          <div style={{ maxHeight:220, overflowY:'auto', borderBottom:`1px solid ${T2.rule}`, marginBottom:8 }}>
            {filteredCmds.length === 0 && <div style={{ padding:'10px 14px', fontSize:13, color:T2.inkFaint }}>No commands match "/{cmdFilter}"</div>}
            {filteredCmds.map(c => (
              <div key={c.cmd} onClick={() => selectSlashCmd(c)}
                style={{ display:'flex', alignItems:'center', gap:10, padding:'9px 14px', cursor:'pointer', borderBottom:`1px solid ${T2.ruleSoft}` }}
                onMouseEnter={e=>e.currentTarget.style.background=T2.page} onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
                <div style={{ width:34, height:34, borderRadius:9, background:T2.navyLt, display:'flex', alignItems:'center', justifyContent:'center', fontSize:15, flexShrink:0 }}>{c.icon||'📋'}</div>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ fontWeight:700, fontSize:13, color:T2.ink }}>
                    <span style={{ color:T2.navy, fontFamily:'monospace', fontSize:11.5 }}>/{c.cmd}</span>
                    <span style={{ marginLeft:6, color:T2.inkMid }}>{c.label}</span>
                  </div>
                  <div style={{ fontSize:11.5, color:T2.inkFaint }}>{c.desc}</div>
                </div>
                {c.flow && c.flow.length > 0 && <span style={{ fontSize:10, color:T2.navy, background:T2.navyLt, padding:'2px 7px', borderRadius:8, fontWeight:700 }}>guided</span>}
                {c.flow && c.flow.length === 0 && <span style={{ fontSize:10, color:T2.navy, background:T2.navyLt, padding:'2px 7px', borderRadius:8, fontWeight:700 }}>auto</span>}
              </div>
            ))}
          </div>
        )}

        {/* ── Conversational flow panel */}
        {slashMode === 'conv' && convCmd && convCmd.flow && (() => {
          const curStep = convCmd.flow[convStep] || {};
          return (
            <div style={{ borderBottom:`1px solid ${T2.rule}`, marginBottom:8, paddingBottom:8 }}>
              {/* Header */}
              <div style={{ display:'flex', alignItems:'center', gap:6, padding:'6px 0 8px' }}>
                <span style={{ fontSize:14 }}>{convCmd.icon}</span>
                <span style={{ fontSize:12.5, color:T2.navy, fontWeight:800 }}>/{convCmd.cmd}</span>
                <span style={{ fontSize:11.5, color:T2.inkFaint, marginLeft:2 }}>{convCmd.label}</span>
                <span style={{ fontSize:10, color:T2.inkFaint, marginLeft:'auto' }}>Step {convStep+1}/{convCmd.flow.length}</span>
                <div onClick={dismissSlash} style={{ cursor:'pointer', fontSize:17, color:T2.inkFaint, marginLeft:8, lineHeight:1 }}>✕</div>
              </div>
              {/* Previous answers */}
              {convStep > 0 && (
                <div style={{ marginBottom:8 }}>
                  {convCmd.flow.slice(0, convStep).map((s, i) => (
                    <div key={i} style={{ display:'flex', gap:6, marginBottom:4, alignItems:'flex-start' }}>
                      <span style={{ fontSize:11.5, color:T2.inkFaint, minWidth:70 }}>{s.ask.replace('?','')}</span>
                      <span style={{ fontSize:12, color:T2.ink, fontWeight:600, borderLeft:`2px solid ${T2.navy}40`, paddingLeft:6 }}>{convDataSC[s.key]||'—'}</span>
                    </div>
                  ))}
                </div>
              )}
              {/* Current question */}
              <div style={{ fontSize:13.5, fontWeight:700, color:T2.navy, marginBottom:8 }}>🤖 {curStep.ask}</div>
              {/* Option pills */}
              {curStep.opts && (
                <div style={{ display:'flex', flexWrap:'wrap', gap:6 }}>
                  {curStep.opts.map((opt, i) => (
                    <div key={i} onClick={() => handleConvAnswerSC(opt)}
                      style={{ padding:'6px 13px', borderRadius:20, border:`1px solid ${T2.navy}40`, background:T2.navyLt, color:T2.navy, fontSize:12.5, fontWeight:600, cursor:'pointer' }}>
                      {opt}
                    </div>
                  ))}
                </div>
              )}
              {/* Data-source picker (dsKey) */}
              {!curStep.opts && curStep.dsKey && (() => {
                const [scQ, setScQ] = useS('');
                const items = scGetItems(curStep.dsKey, scQ);
                return (
                  <div>
                    <input value={scQ} onChange={e=>setScQ(e.target.value)} autoFocus
                      placeholder={`Search ${curStep.dsKey}s…`}
                      style={{ width:'100%', boxSizing:'border-box', border:`1px solid ${T2.rule}`, borderRadius:20, padding:'8px 14px', fontSize:13, outline:'none', color:T2.ink, background:T2.page, fontFamily:"'Lato',sans-serif", marginBottom:6 }} />
                    <div style={{ maxHeight:140, overflowY:'auto', borderRadius:12, border:`1px solid ${T2.rule}`, background:'#fff' }}>
                      {items.length === 0 && <div style={{ padding:'10px 14px', color:T2.inkFaint, fontSize:13 }}>No results</div>}
                      {items.map(item => (
                        <div key={item.id} onClick={() => { handleConvAnswerSC(item.label); }}
                          style={{ display:'flex', alignItems:'center', gap:8, padding:'9px 14px', cursor:'pointer', borderBottom:`1px solid ${T2.ruleSoft}` }}>
                          <div style={{ flex:1, minWidth:0 }}>
                            <div style={{ fontSize:13, fontWeight:600, color:T2.ink, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{item.label}</div>
                            {item.meta && <div style={{ fontSize:11, color:T2.inkFaint }}>{item.meta}</div>}
                          </div>
                          <span style={{ fontSize:11, color:T2.navy, fontWeight:700 }}>Select →</span>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })()}
              {/* Free-text answer input */}
              {!curStep.opts && !curStep.dsKey && (
                <div style={{ display:'flex', gap:6 }}>
                  <input autoFocus value={convAns} onChange={e=>setConvAns(e.target.value)}
                    onKeyDown={e=>{ if(e.key==='Enter'&&convAns.trim()){ handleConvAnswerSC(convAns); } }}
                    placeholder="Type your answer…"
                    style={{ flex:1, border:`1px solid ${T2.rule}`, borderRadius:20, padding:'8px 14px', fontSize:13, outline:'none', color:T2.ink, background:T2.page, fontFamily:"'Lato',sans-serif" }} />
                  <div onClick={() => convAns.trim() && handleConvAnswerSC(convAns)}
                    style={{ width:36, height:36, borderRadius:'50%', background:T2.navy, display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer', flexShrink:0 }}>
                    <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><path d="M2 8h10M8 4l4 4-4 4" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                  </div>
                </div>
              )}
            </div>
          );
        })()}

        {/* Suggestion chips */}
        <div style={{ display:'flex', gap:6, overflowX:'auto', paddingBottom:9, marginBottom:2 }}>
          {SUGGESTIONS.map((s, i) => (
            <div key={i} onClick={()=>send(s)} style={{
              flexShrink:0, padding:'5px 12px', borderRadius:16,
              border:`1px solid ${T2.rule}`, background:T2.page,
              fontSize:11.5, color:T2.inkMid, fontWeight:600, cursor:'pointer', whiteSpace:'nowrap',
            }}>{s}</div>
          ))}
        </div>
        {/* Input row */}
        <div style={{
          display:'flex', gap:8, alignItems:'center',
          border:`1.5px solid ${T2.rule}`, borderRadius:26, padding:'6px 6px 6px 16px',
          background:T2.page,
        }}>
          <input
            value={input}
            disabled={quota && !quota.unlimited && quota.pct >= 100}
            onChange={e=>{ const v=e.target.value; setInput(v); const tok=v.split(' ').pop(); if(tok==='/'){openSlashCmds('');} else if(tok.startsWith('/')&&tok.length>1){openSlashCmds(tok.slice(1));} else if(slashMode==='cmds') setSlashMode(null); }}
            onKeyDown={e=>{ if(e.key==='Enter') send(); if(e.key==='Escape') dismissSlash(); }}
            placeholder={quota && !quota.unlimited && quota.pct >= 100 ? "Monthly limit reached — resets next month" : "Reply or type / for commands"}
            style={{
              flex:1, border:'none', outline:'none', background:'transparent',
              fontSize:14, color:T2.ink, fontFamily:"'Lato',sans-serif",
            }}
          />
          <div style={{ display:'flex', gap:6, alignItems:'center', flexShrink:0 }}>
            {quota && !quota.unlimited && (
              <div style={{
                padding:'5px 10px', borderRadius:14,
                background: quota.pct >= 100 ? '#fde8e8' : T2.navyLt,
                color: quota.pct >= 100 ? '#b91c1c' : T2.navy,
                fontSize:10.5, fontWeight:800,
              }}>{quota.used}/{quota.cap}</div>
            )}
            <div onClick={()=>send()} style={{
              width:36, height:36, borderRadius:'50%', flexShrink:0,
              background: input.trim() ? T2.ink : T2.ruleSoft,
              display:'flex', alignItems:'center', justifyContent:'center',
              cursor: input.trim() ? 'pointer' : 'default', transition:'background 0.15s',
            }}>
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M2 8h10M8 4l4 4-4 4" stroke={input.trim()?"#fff":T2.inkFaint} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
          </div>
        </div>
      </div>

      <BottomNav active={active} role={role} onNav={onNav}/>
    </div>
  );
}

Object.assign(window, {
  // Primitives (so external script files can use them)
  T2, I, Avatar, IconButton, Bar, Label, Section, Header, BottomNav,
  sh, DateBlock, CapabilityRing,
  // Existing screens
  ScreenParticipantHome, ScreenParticipantLearn, ScreenParticipantAssess,
  ScreenParticipantGrowth, ScreenParticipantMe, ScreenParticipantSessions,
  ScreenLeaderOverview, ScreenLeaderCohort, ScreenLeaderMember,
  ScreenLeaderSessions, ScreenLeaderAnalytics, ScreenLeaderMe,
  ScreenAICoach,
});



// ===== 22-inline-01.jsx =====
// SkaleCap — new flows added per May 2026 handoff
// Community (Feed/Goals/Discuss) · Sessions · Tasks · Edit Profile · Avatar Picker
// Uses primitives exported to window from the main script block.
// IIFE-wrapped so its local `const`s don't collide with the inline script's globals.

(function () {

const { useState: useS } = React;
const { T2, I, Avatar, IconButton, Bar, Label, Section, Header, BottomNav, sh, DateBlock } = window;

// ============================================================
// PARTICIPANT · Community (3 internal tabs: Feed · Goals · Discuss)
// ============================================================
// ─── Community data layer ─────────────────────────────────────
// Wired against /api/community/* (migration 133). Feed, Discuss and Goals
// were three tabs of entirely hardcoded content — invented members, invented
// XP totals, a leaderboard with fabricated numbers, and a comment thread that
// regenerated the same three names and replies regardless of which post you
// opened. All three now read and write real rows.
function timeAgo(iso) {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  const diffMs = Date.now() - d.getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return 'now';
  if (mins < 60) return `${mins}m`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d`;
  return d.toLocaleDateString('en-IN', { month:'short', day:'numeric' });
}

const POST_TAGS = {
  win:     { l:'🏆 Win',        c:T2.sage,  bg:T2.sageLt },
  insight: { l:'💡 Insight',    c:T2.teal,  bg:T2.tealLt },
  reflect: { l:'🔄 Reflection', c:T2.navy,  bg:T2.navyLt },
  ask:     { l:'❓ Question',   c:T2.amber, bg:T2.goldLt },
};
const EMOJI_REACTIONS = ['❤️','🔥','👏','🙌','💡','🚀'];

function CommunityEmpty({ icon, title, body }) {
  return (
    <div style={{ padding:'40px 32px', textAlign:'center' }}>
      <div style={{ width:46, height:46, borderRadius:12, background:T2.ruleSoft, margin:'0 auto 14px', display:'flex', alignItems:'center', justifyContent:'center' }}>
        <I name={icon||'comment'} size={20} c={T2.inkSoft} sw={2}/>
      </div>
      <div style={{ fontSize:14.5, fontWeight:700, color:T2.ink, marginBottom:6 }}>{title}</div>
      <div style={{ fontSize:12.5, color:T2.inkSoft, lineHeight:1.55 }}>{body}</div>
    </div>
  );
}

// ─── MODAL: Compose post ───────────────────────────────────────
function ModalComposePost({ onClose, onPost, type = 'post' }) {
  const [text, setText] = useS('');
  const [tag, setTag] = useS(null);
  const [saving, setSaving] = useS(false);
  const [posted, setPosted] = useS(false);
  const [err, setErr] = useS(false);
  const MAX = type === 'discuss' ? 2000 : 280;
  const left = MAX - text.length;

  const submit = async () => {
    if (!text.trim() || saving) return;
    setSaving(true); setErr(false);
    try {
      const r = await window.SRAPI._post('/api/community/posts', { type, tag, body: text.trim() });
      setPosted(true);
      onPost && onPost(r);
      setTimeout(onClose, 1400);
    } catch (e) { setErr(true); }
    setSaving(false);
  };

  if (posted) return (
    <div onClick={onClose} style={{ position:'absolute', inset:0, zIndex:45, background:'rgba(0,0,0,0.5)', display:'flex', alignItems:'center', justifyContent:'center' }}>
      <div style={{ background:'#fff', borderRadius:20, padding:'30px 28px', textAlign:'center', margin:'0 28px' }}>
        <div style={{ width:58, height:58, borderRadius:'50%', background:T2.sageLt, margin:'0 auto 14px', display:'flex', alignItems:'center', justifyContent:'center' }}>
          <I name="check" size={22} c={T2.sage} sw={2.8}/>
        </div>
        <div style={{ fontSize:18, fontWeight:800, color:T2.ink, marginBottom:6 }}>Posted</div>
        <div style={{ fontSize:13, color:T2.inkSoft }}>Your post is live in {type==='discuss'?'Discuss':'the Feed'}.</div>
      </div>
    </div>
  );

  return (
    <div style={{ position:'absolute', inset:0, zIndex:45, background:'rgba(0,0,0,0.45)', display:'flex', flexDirection:'column', justifyContent:'flex-end' }}>
      <div style={{ background:'#fff', borderRadius:'20px 20px 0 0', maxHeight:'88%', display:'flex', flexDirection:'column' }}>
        <div style={{ padding:'10px 16px 14px', borderBottom:`1px solid ${T2.rule}`, flexShrink:0 }}>
          <div style={{ width:40, height:4, borderRadius:2, background:T2.rule, margin:'0 auto 14px' }}/>
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between' }}>
            <div style={{ fontSize:16, fontWeight:800, color:T2.ink }}>{type==='discuss'?'Start a discussion':'New post'}</div>
            <div onClick={onClose} style={{ fontSize:13.5, fontWeight:700, color:T2.inkSoft, cursor:'pointer' }}>Cancel</div>
          </div>
        </div>
        <div style={{ overflow:'auto', flex:1, padding:'16px' }}>
          {type === 'post' && (
            <div style={{ marginBottom:16 }}>
              <Label mb={8}>Post type</Label>
              <div style={{ display:'flex', gap:7, flexWrap:'wrap' }}>
                {Object.entries(POST_TAGS).map(([id,t])=>(
                  <div key={id} onClick={()=>setTag(tag===id?null:id)} style={{ padding:'6px 12px', borderRadius:20, cursor:'pointer', border:`1.5px solid ${tag===id?t.c:T2.rule}`, background:tag===id?t.bg:'#fff' }}>
                    <span style={{ fontSize:12, fontWeight:800, color:tag===id?t.c:T2.inkSoft }}>{t.l}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          <div style={{ position:'relative' }}>
            <div style={{ display:'flex', gap:10, alignItems:'flex-start' }}>
              <div style={{ flex:1, background:T2.page, borderRadius:12, padding:'11px 13px', border:`1.5px solid ${text?T2.navy+'40':T2.rule}` }}>
                <textarea
                  value={text}
                  onChange={e=>{ if(e.target.value.length<=MAX) setText(e.target.value); }}
                  placeholder={type==='discuss' ? "What do you want to discuss with your cohort?" : "What's on your mind? Share a win, ask a question, or start a discussion…"}
                  rows={type==='discuss'?7:5}
                  style={{ width:'100%', border:'none', background:'transparent', fontSize:13.5, color:T2.ink, outline:'none', fontFamily:'inherit', resize:'none', lineHeight:1.55 }}
                />
              </div>
            </div>
            <div style={{ display:'flex', justifyContent:'flex-end', marginTop:6 }}>
              <span style={{ fontSize:11, fontWeight:700, color:left<30?T2.rose:T2.inkFaint }}>{left}</span>
            </div>
          </div>
          {err && <div style={{ fontSize:11.5, color:T2.rose, marginTop:10 }}>Couldn't post — check your connection and try again.</div>}
        </div>
        <div style={{ padding:'12px 16px 26px', flexShrink:0, borderTop:`1px solid ${T2.rule}`, display:'flex', gap:10, alignItems:'center' }}>
          <div style={{ flex:1, fontSize:12, color:T2.inkSoft }}>
            {tag && <span>Posting as <strong style={{color:T2.ink}}>{POST_TAGS[tag]?.l}</strong></span>}
          </div>
          <button onClick={submit} disabled={!text.trim()||saving}
            style={{ padding:'10px 22px', borderRadius:10, border:'none', background:text.trim()?T2.navy:T2.ruleSoft, color:text.trim()?'#fff':T2.inkFaint, fontSize:14, fontWeight:800, fontFamily:'inherit', cursor:text.trim()&&!saving?'pointer':'default', opacity:saving?0.6:1 }}>
            {saving ? 'Posting…' : 'Post'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── SCREEN: Community ─────────────────────────────────────────
function ScreenCommunity({ onNav, active = 'community' }) {
  const [tab, setTab] = useS('feed');
  const [showCompose, setShowCompose] = useS(false);
  const [commentPostId, setCommentPostId] = useS(null);
  const [sharePost, setSharePost] = useS(null);
  const [feedBump, setFeedBump] = useS(0); // forces the active tab to refetch after a new post

  return (
    <div style={sh.shell}>
      {showCompose && <ModalComposePost type={tab==='discuss'?'discuss':'post'} onClose={()=>setShowCompose(false)} onPost={()=>setFeedBump(n=>n+1)}/>}
      {commentPostId && <ModalCommentSheet postId={commentPostId} onClose={()=>setCommentPostId(null)}/>}
      {sharePost && <ModalShareSheet post={sharePost} onClose={()=>setSharePost(null)}/>}
      <div style={sh.scroll}>
        <Header
          kicker="Community"
          title="Stay close to your cohort"
          sub={<>{tab === 'feed' ? 'Feed' : tab === 'goals' ? 'XP, streaks, leaderboard' : 'Discussions'}</>}
          onRight={()=>onNav&&onNav('me')}
          right={<I name="chevR" size={18} c="#fff" sw={2}/>}
        />
        <div style={{
          display:'flex', borderBottom:`1px solid ${T2.rule}`, background:'#fff',
          position:'sticky', top:0, zIndex:4,
        }}>
          {[{id:'feed',label:'Feed'},{id:'goals',label:'Goals'},{id:'discuss',label:'Discuss'}].map(t => (
            <div key={t.id} onClick={() => setTab(t.id)} style={{
              flex:1, padding:'13px 0', textAlign:'center', cursor:'pointer',
              borderBottom: tab === t.id ? `2px solid ${T2.navy}` : '2px solid transparent',
              color: tab === t.id ? T2.navy : T2.inkSoft,
              fontSize: 12.5, fontWeight: tab === t.id ? 800 : 600,
              letterSpacing:'0.03em',
            }}>{t.label}</div>
          ))}
        </div>
        {tab === 'feed'    && <CommunityFeedTab key={'feed-'+feedBump} onComments={setCommentPostId} onShare={setSharePost}/>}
        {tab === 'goals'   && <CommunityGoalsTab/>}
        {tab === 'discuss' && <CommunityDiscussTab key={'discuss-'+feedBump} onComments={setCommentPostId}/>}
        <div style={{height: 24}}/>
      </div>
      {(tab === 'feed' || tab === 'discuss') && (
        <div onClick={()=>setShowCompose(true)} style={{
          position:'absolute', right:18, bottom: 90, zIndex:6,
          width:52, height:52, borderRadius:'50%', background:T2.gold, color:'#fff',
          display:'flex', alignItems:'center', justifyContent:'center',
          boxShadow:'0 8px 18px rgba(200,150,12,0.4)', cursor:'pointer',
        }}>
          <I name="plus" size={22} c="#fff" sw={2.4}/>
        </div>
      )}
      <BottomNav active={active} role="participant" onNav={onNav}/>
    </div>
  );
}

// ─── MODAL: Share ──────────────────────────────────────────────
function ModalShareSheet({ post, onClose }) {
  const [copied, setCopied] = useS(false);
  const copyText = () => {
    const text = post?.body || '';
    const done = () => { setCopied(true); setTimeout(()=>setCopied(false), 2000); };
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(text).then(done).catch(()=>{});
    } else {
      try {
        const ta = document.createElement('textarea');
        ta.value = text; ta.style.position='fixed'; ta.style.opacity='0';
        document.body.appendChild(ta); ta.select(); document.execCommand('copy');
        document.body.removeChild(ta); done();
      } catch (e) {}
    }
  };
  // "Send via Messages" and "More options…" were removed rather than kept as
  // dead rows: this is a WebView inside a native shell with no access to the
  // OS share sheet or Messages compose from here. Copy is the one channel
  // that's genuinely available.
  return (
    <div onClick={onClose} style={{ position:'absolute', inset:0, zIndex:40, background:'rgba(0,0,0,0.45)', display:'flex', flexDirection:'column', justifyContent:'flex-end' }}>
      <div onClick={e=>e.stopPropagation()} style={{ background:'#fff', borderRadius:'20px 20px 0 0', padding:'16px 0 28px' }}>
        <div style={{ width:40, height:4, borderRadius:2, background:T2.rule, margin:'0 auto 14px' }}/>
        <div style={{ padding:'0 16px 12px', fontSize:15, fontWeight:800, color:T2.ink }}>Share this post</div>
        <div onClick={copyText} style={{ display:'flex', alignItems:'center', gap:14, padding:'12px 20px', cursor:'pointer', background:copied?T2.sageLt:'transparent' }}>
          <div style={{ width:40, height:40, borderRadius:12, background:copied?T2.sage:T2.navyLt, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
            <I name="copy2" size={17} c={copied?'#fff':T2.navy} sw={2}/>
          </div>
          <div style={{ fontSize:14, fontWeight:700, color:copied?T2.sage:T2.ink }}>{copied?'Copied to clipboard':'Copy text'}</div>
        </div>
      </div>
    </div>
  );
}

// ─── MODAL: Comments ────────────────────────────────────────────
function ModalCommentSheet({ postId, onClose }) {
  const [data, setData]       = useS(null); // { post, comments }
  const [status, setStatus]   = useS('loading');
  const [text, setText]       = useS('');
  const [replyTo, setReplyTo] = useS(null); // { id, name }
  const [replyText, setReplyText] = useS('');
  const [emojiOpenFor, setEmojiOpenFor] = useS(null); // 'post' | comment id
  const [collapsed, setCollapsed] = useS({});
  const [sending, setSending] = useS(false);

  const load = React.useCallback(async () => {
    try {
      const r = await window.SRAPI._get(`/api/community/posts/${postId}`);
      setData(r); setStatus('ready');
    } catch (e) { setStatus('error'); }
  }, [postId]);
  React.useEffect(() => { load(); }, [load]);

  const toggleLike = async () => {
    if (!data) return;
    const optimistic = { ...data, post: { ...data.post, likedByMe: !data.post.likedByMe, likeCount: data.post.likeCount + (data.post.likedByMe?-1:1) } };
    setData(optimistic);
    try { await window.SRAPI._post(`/api/community/posts/${postId}/like`); }
    catch (e) { load(); }
  };

  const sendTop = async () => {
    if (!text.trim() || sending) return;
    setSending(true);
    try {
      const c = await window.SRAPI._post(`/api/community/posts/${postId}/comments`, { body: text.trim() });
      setData(d => ({ ...d, comments: [...d.comments, c] }));
      setText('');
    } catch (e) {}
    setSending(false);
  };

  const sendReply = async () => {
    if (!replyText.trim() || !replyTo || sending) return;
    setSending(true);
    try {
      const r = await window.SRAPI._post(`/api/community/posts/${postId}/comments`, { body: replyText.trim(), parentCommentId: replyTo.id });
      setData(d => ({ ...d, comments: d.comments.map(c => c.id === replyTo.id ? { ...c, replies: [...c.replies, r] } : c) }));
      setReplyText(''); setReplyTo(null);
    } catch (e) {}
    setSending(false);
  };

  const react = async (commentId, emoji) => {
    setEmojiOpenFor(null);
    try {
      const r = await window.SRAPI._post(`/api/community/comments/${commentId}/react`, { emoji });
      setData(d => ({
        ...d,
        comments: d.comments.map(c => {
          if (c.id === commentId) return { ...c, emojis: r.emojis };
          if (c.replies?.some(x=>x.id===commentId)) return { ...c, replies: c.replies.map(x => x.id===commentId ? { ...x, emojis: r.emojis } : x) };
          return c;
        }),
      }));
    } catch (e) {}
  };

  const toggleCollapse = (id) => setCollapsed(p => ({ ...p, [id]: !p[id] }));

  const EmojiBar = ({ onPick }) => (
    <div style={{ display:'flex', gap:6, background:'#fff', border:`1px solid ${T2.rule}`, borderRadius:24, padding:'5px 10px', boxShadow:'0 3px 14px rgba(0,0,0,0.12)', flexShrink:0 }}>
      {EMOJI_REACTIONS.map(e=>(
        <span key={e} onClick={()=>onPick(e)} style={{ fontSize:18, cursor:'pointer', lineHeight:1 }}>{e}</span>
      ))}
    </div>
  );

  const ReactionChips = ({ emojis, onPick }) => {
    const entries = Object.entries(emojis||{}).filter(([,n])=>n>0);
    if (!entries.length) return null;
    return (
      <div style={{ display:'flex', gap:5, flexWrap:'wrap', marginTop:6 }}>
        {entries.map(([e,n])=>(
          <span key={e} onClick={(ev)=>{ev.stopPropagation();onPick(e);}} style={{ background:T2.navyLt, borderRadius:12, padding:'2px 7px', fontSize:11, cursor:'pointer' }}>{e} {n}</span>
        ))}
      </div>
    );
  };

  const CommentRow = ({ c }) => {
    const isCollapsed = !!collapsed[c.id];
    const hasReplies = (c.replies||[]).length > 0;
    const heartCount = (c.emojis||{})['❤️'] || 0;
    return (
      <div style={{ display:'flex', gap:9 }}>
        <Avatar initials={c.author.initials} size={30} bg={T2.navyLt} fg={T2.navy}/>
        <div style={{ flex:1, minWidth:0 }}>
          <div onClick={hasReplies?()=>toggleCollapse(c.id):undefined}
               style={{ background:T2.page, borderRadius:'4px 12px 12px 12px', padding:'9px 12px', cursor:hasReplies?'pointer':'default' }}>
            <div style={{ display:'flex', alignItems:'center', gap:6, marginBottom:4, flexWrap:'wrap' }}>
              <span style={{ fontSize:12, fontWeight:800, color:T2.ink }}>{c.author.name}</span>
              <span style={{ fontSize:10, color:T2.inkFaint, marginLeft:'auto' }}>{timeAgo(c.created_at)}</span>
            </div>
            <div style={{ fontSize:12.5, color:T2.inkMid, lineHeight:1.45 }}>{c.body}</div>
            <ReactionChips emojis={c.emojis} onPick={(e)=>react(c.id, e)}/>
          </div>
          <div style={{ display:'flex', gap:14, marginTop:5, paddingLeft:4, alignItems:'center', position:'relative' }}>
            <span onClick={()=>react(c.id,'❤️')} style={{ fontSize:11, color:heartCount?T2.rose:T2.inkSoft, cursor:'pointer', fontWeight:700, display:'inline-flex', alignItems:'center', gap:3 }}>
              {heartCount?'❤️':'♡'} {heartCount>0?heartCount:'Like'}
            </span>
            <span onClick={()=>setEmojiOpenFor(emojiOpenFor===c.id?null:c.id)} style={{ fontSize:11, color:T2.inkSoft, cursor:'pointer', fontWeight:700 }}>😊</span>
            <span onClick={()=>{ setReplyTo(replyTo?.id===c.id?null:{id:c.id,name:c.author.name}); setReplyText(''); }} style={{ fontSize:11, color:replyTo?.id===c.id?T2.navy:T2.inkSoft, cursor:'pointer', fontWeight:700 }}>Reply</span>
            {hasReplies&&(
              <span onClick={()=>toggleCollapse(c.id)} style={{ marginLeft:'auto', fontSize:10.5, color:T2.navy, cursor:'pointer', fontWeight:700, display:'inline-flex', alignItems:'center', gap:3 }}>
                {isCollapsed
                  ? <><I name="chevR" size={10} c={T2.navy} sw={2}/> {c.replies.length} {c.replies.length===1?'reply':'replies'}</>
                  : <><I name="chevL" size={10} c={T2.navy} sw={2}/> Hide replies</>}
              </span>
            )}
            {emojiOpenFor===c.id&&(
              <div style={{ position:'absolute', bottom:22, left:0, zIndex:10 }}><EmojiBar onPick={(e)=>react(c.id,e)}/></div>
            )}
          </div>
          {hasReplies&&!isCollapsed&&(
            <div style={{ marginTop:8, display:'flex', flexDirection:'column', gap:8 }}>
              {c.replies.map((r)=>{
                const rHeart = (r.emojis||{})['❤️'] || 0;
                return (
                  <div key={r.id} style={{ display:'flex', gap:8 }}>
                    <div style={{ width:2, background:T2.rule, borderRadius:2, marginLeft:2, flexShrink:0 }}/>
                    <Avatar initials={r.author.initials} size={24} bg={T2.navyLt} fg={T2.navy}/>
                    <div style={{ flex:1, minWidth:0 }}>
                      <div style={{ background:'#fff', border:`1px solid ${T2.rule}`, borderRadius:'4px 10px 10px 10px', padding:'7px 10px' }}>
                        <div style={{ display:'flex', alignItems:'center', gap:6, marginBottom:3, flexWrap:'wrap' }}>
                          <span style={{ fontSize:11, fontWeight:800, color:T2.ink }}>{r.author.name}</span>
                          <span style={{ fontSize:10, color:T2.inkFaint, marginLeft:'auto' }}>{timeAgo(r.created_at)}</span>
                        </div>
                        <div style={{ fontSize:12, color:T2.inkMid, lineHeight:1.4 }}>{r.body}</div>
                        <ReactionChips emojis={r.emojis} onPick={(e)=>react(r.id,e)}/>
                      </div>
                      <div style={{ display:'flex', gap:12, marginTop:4, paddingLeft:2, alignItems:'center', position:'relative' }}>
                        <span onClick={()=>react(r.id,'❤️')} style={{ fontSize:10.5, color:rHeart?T2.rose:T2.inkSoft, cursor:'pointer', fontWeight:700, display:'inline-flex', alignItems:'center', gap:3 }}>
                          {rHeart?'❤️':'♡'} {rHeart>0?rHeart:'Like'}
                        </span>
                        <span onClick={()=>setEmojiOpenFor(emojiOpenFor===r.id?null:r.id)} style={{ fontSize:10.5, color:T2.inkSoft, cursor:'pointer', fontWeight:700 }}>😊</span>
                        {emojiOpenFor===r.id&&(
                          <div style={{ position:'absolute', bottom:20, left:0, zIndex:10 }}><EmojiBar onPick={(e)=>react(r.id,e)}/></div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          {replyTo?.id===c.id&&(
            <div style={{ display:'flex', gap:8, marginTop:8, alignItems:'center' }}>
              <div style={{ flex:1, background:T2.ruleSoft, borderRadius:18, padding:'7px 11px', display:'flex', alignItems:'center', gap:6 }}>
                <span style={{ fontSize:11, color:T2.inkSoft }}>@{replyTo.name.split(' ')[0]}</span>
                <input value={replyText} onChange={e=>setReplyText(e.target.value)} onKeyDown={e=>e.key==='Enter'&&sendReply()}
                  autoFocus placeholder="Write a reply…"
                  style={{ flex:1, border:'none', background:'transparent', fontSize:12, color:T2.ink, outline:'none', fontFamily:'inherit' }}/>
              </div>
              <div onClick={sendReply} style={{ width:30, height:30, borderRadius:'50%', background:replyText.trim()?T2.navy:T2.ruleSoft, display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer', flexShrink:0 }}>
                <I name="arrowR" size={13} c={replyText.trim()?'#fff':T2.inkFaint} sw={2.2}/>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div onClick={onClose} style={{ position:'absolute', inset:0, zIndex:40, background:'rgba(0,0,0,0.5)', display:'flex', flexDirection:'column', justifyContent:'flex-end' }}>
      <div onClick={e=>e.stopPropagation()} style={{ background:'#fff', borderRadius:'20px 20px 0 0', maxHeight:'92%', display:'flex', flexDirection:'column' }}>
        <div style={{ padding:'10px 16px 12px', borderBottom:`1px solid ${T2.rule}`, display:'flex', alignItems:'center', flexShrink:0, position:'relative' }}>
          <div style={{ width:40, height:4, borderRadius:2, background:T2.rule, position:'absolute', left:'50%', top:8, transform:'translateX(-50%)' }}/>
          <div style={{ fontSize:15, fontWeight:800, color:T2.ink, flex:1, marginTop:4 }}>Comments</div>
          <div onClick={onClose} style={{ cursor:'pointer', width:28, height:28, borderRadius:'50%', background:T2.ruleSoft, display:'flex', alignItems:'center', justifyContent:'center' }}>
            <div style={{ transform:'rotate(45deg)', display:'flex' }}><I name="plus" size={14} c={T2.inkSoft} sw={2.4}/></div>
          </div>
        </div>

        {status === 'loading' && <div style={{ padding:'30px', textAlign:'center', fontSize:12.5, color:T2.inkSoft }}>Loading…</div>}
        {status === 'error'   && <div style={{ padding:'30px', textAlign:'center', fontSize:12.5, color:T2.inkSoft }}>Couldn't load this post.</div>}

        {status === 'ready' && data && (
          <>
            <div style={{ padding:'14px 16px 12px', background:T2.page, borderBottom:`1px solid ${T2.rule}`, flexShrink:0 }}>
              <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:9 }}>
                <Avatar initials={data.post.author.initials} size={36} bg={T2.navyLt} fg={T2.navy}/>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ display:'flex', alignItems:'center', gap:6, flexWrap:'wrap' }}>
                    <span style={{ fontSize:13, fontWeight:800, color:T2.ink }}>{data.post.author.name}</span>
                    {data.post.tag && POST_TAGS[data.post.tag] && (
                      <span style={{ fontSize:9, fontWeight:800, color:POST_TAGS[data.post.tag].c, background:POST_TAGS[data.post.tag].bg, padding:'1px 5px', borderRadius:4 }}>{POST_TAGS[data.post.tag].l}</span>
                    )}
                  </div>
                  <div style={{ fontSize:10.5, color:T2.inkSoft, marginTop:1 }}>{timeAgo(data.post.created_at)}</div>
                </div>
              </div>
              <div style={{ fontSize:13.5, color:T2.ink, lineHeight:1.55, marginBottom:10 }}>{data.post.body}</div>
              <div style={{ display:'flex', alignItems:'center', gap:14 }}>
                <div onClick={toggleLike} style={{ display:'flex', alignItems:'center', gap:5, cursor:'pointer' }}>
                  <I name={data.post.likedByMe?'heartFill':'heart'} size={16} c={data.post.likedByMe?T2.rose:T2.inkSoft} sw={1.8}/>
                  <span style={{ fontSize:12.5, fontWeight:700, color:data.post.likedByMe?T2.rose:T2.inkSoft }}>{data.post.likeCount}</span>
                </div>
                <div style={{ display:'flex', alignItems:'center', gap:5 }}>
                  <I name="comment" size={16} c={T2.navy} sw={1.8}/>
                  <span style={{ fontSize:12.5, fontWeight:700, color:T2.navy }}>{data.comments.length} {data.comments.length===1?'comment':'comments'}</span>
                </div>
              </div>
            </div>
            <div style={{ overflow:'auto', flex:1, padding:'12px 14px 6px', display:'flex', flexDirection:'column', gap:14 }}>
              {data.comments.length === 0
                ? <CommunityEmpty icon="comment" title="No comments yet" body="Be the first to say something."/>
                : data.comments.map((c)=><CommentRow key={c.id} c={c}/>)}
            </div>
            <div style={{ padding:'10px 14px 22px', borderTop:`1px solid ${T2.rule}`, display:'flex', gap:10, alignItems:'center', flexShrink:0 }}>
              <div style={{ flex:1, background:T2.ruleSoft, borderRadius:22, padding:'9px 14px', display:'flex', alignItems:'center' }}>
                <input value={text} onChange={e=>setText(e.target.value)} onKeyDown={e=>e.key==='Enter'&&sendTop()}
                  placeholder="Add a comment…"
                  style={{ flex:1, border:'none', background:'transparent', fontSize:13, color:T2.ink, outline:'none', fontFamily:'inherit' }}/>
              </div>
              <div onClick={sendTop} style={{ width:36, height:36, borderRadius:'50%', background:text.trim()?T2.navy:T2.ruleSoft, display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer', flexShrink:0 }}>
                <I name="arrowR" size={15} c={text.trim()?'#fff':T2.inkFaint} sw={2.2}/>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ─── FEED post card ────────────────────────────────────────────
function FeedPostCard({ p, onComments, onShare }) {
  const [liked, setLiked] = useS(p.likedByMe);
  const [count, setCount] = useS(p.likeCount);
  const toggleLike = async (e) => {
    e.stopPropagation();
    setLiked(!liked); setCount(c => liked ? c-1 : c+1);
    try { await window.SRAPI._post(`/api/community/posts/${p.id}/like`); }
    catch (err) { setLiked(liked); setCount(p.likeCount); }
  };
  return (
    <div onClick={()=>onComments&&onComments(p.id)} style={{ padding:'14px', background:'#fff', border:`1px solid ${T2.rule}`, borderRadius:14, cursor:'pointer' }}>
      <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:9 }}>
        <Avatar initials={p.author.initials} size={32} bg={T2.navyLt} fg={T2.navy}/>
        <div style={{ flex:1, minWidth:0 }}>
          <div style={{ display:'flex', alignItems:'center', gap:6, flexWrap:'wrap' }}>
            <span style={{ fontSize:13, fontWeight:800, color:T2.ink }}>{p.author.name}</span>
            {p.tag && POST_TAGS[p.tag] && (
              <span style={{ fontSize:9, fontWeight:800, color:POST_TAGS[p.tag].c, background:POST_TAGS[p.tag].bg, padding:'1px 5px', borderRadius:4 }}>{POST_TAGS[p.tag].l}</span>
            )}
          </div>
          <div style={{ fontSize:10.5, color:T2.inkSoft, marginTop:1 }}>{timeAgo(p.created_at)}</div>
        </div>
      </div>
      <div style={{ fontSize:13.5, color:T2.ink, lineHeight:1.5 }}>{p.body}</div>
      <div onClick={e=>e.stopPropagation()} style={{ display:'flex', alignItems:'center', gap:14, marginTop:12, paddingTop:10, borderTop:`1px solid ${T2.ruleSoft}` }}>
        <div onClick={toggleLike} style={{ display:'flex', alignItems:'center', gap:5, cursor:'pointer' }}>
          <I name={liked?'heartFill':'heart'} size={15} c={liked?T2.rose:T2.inkSoft} sw={1.8}/>
          <span style={{ fontSize:12, fontWeight:700, color:liked?T2.rose:T2.inkSoft }}>{count}</span>
        </div>
        <div onClick={()=>onComments&&onComments(p.id)} style={{ display:'flex', alignItems:'center', gap:5, cursor:'pointer' }}>
          <I name="comment" size={15} c={T2.inkSoft} sw={1.8}/>
          <span style={{ fontSize:12, fontWeight:700, color:T2.inkSoft }}>{p.commentCount}</span>
        </div>
        <div style={{ flex:1 }}/>
        <div onClick={(e)=>{e.stopPropagation();onShare&&onShare(p);}} style={{ cursor:'pointer', padding:4 }}><I name="share" size={14} c={T2.inkSoft} sw={2}/></div>
      </div>
    </div>
  );
}

function CommunityFeedTab({ onComments, onShare }) {
  const [posts, setPosts]   = useS([]);
  const [status, setStatus] = useS('loading');

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await window.SRAPI._get('/api/community/feed?type=post');
        if (!cancelled) { setPosts(r?.posts || []); setStatus('ready'); }
      } catch (e) { if (!cancelled) setStatus('error'); }
    })();
    return () => { cancelled = true; };
  }, []);

  if (status === 'loading') return <div style={{ padding:'30px', textAlign:'center', fontSize:12.5, color:T2.inkSoft }}>Loading the feed…</div>;
  if (status === 'error')   return <CommunityEmpty icon="bolt" title="Couldn't load the feed" body="Check your connection and try again."/>;
  if (posts.length === 0)   return <CommunityEmpty icon="comment" title="Nothing posted yet" body="Be the first to share something with your cohort."/>;

  return (
    <Section kicker="Latest posts" pt={16}>
      <div style={{ padding:'0 16px', display:'flex', flexDirection:'column', gap:10 }}>
        {posts.map((p)=><FeedPostCard key={p.id} p={p} onComments={onComments} onShare={onShare}/>)}
      </div>
    </Section>
  );
}

// ─── DISCUSS tab ───────────────────────────────────────────────
function CommunityDiscussTab({ onComments }) {
  const [threads, setThreads] = useS([]);
  const [status, setStatus]   = useS('loading');

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await window.SRAPI._get('/api/community/feed?type=discuss');
        if (!cancelled) { setThreads(r?.posts || []); setStatus('ready'); }
      } catch (e) { if (!cancelled) setStatus('error'); }
    })();
    return () => { cancelled = true; };
  }, []);

  if (status === 'loading') return <div style={{ padding:'30px', textAlign:'center', fontSize:12.5, color:T2.inkSoft }}>Loading discussions…</div>;
  if (status === 'error')   return <CommunityEmpty icon="bolt" title="Couldn't load discussions" body="Check your connection and try again."/>;
  if (threads.length === 0) return <CommunityEmpty icon="comment" title="No discussions yet" body="Tap + to start one."/>;

  return (
    <div style={{ padding:'14px 16px 0', display:'flex', flexDirection:'column', gap:8 }}>
      {threads.map((t)=>(
        <div key={t.id} onClick={()=>onComments&&onComments(t.id)} style={{ padding:'13px 14px', borderRadius:12, border:`1px solid ${T2.rule}`, background:'#fff', cursor:'pointer' }}>
          <div style={{ fontSize:13.5, fontWeight:700, color:T2.ink, lineHeight:1.35 }}>{t.body.length > 90 ? t.body.slice(0,90)+'…' : t.body}</div>
          <div style={{ display:'flex', alignItems:'center', gap:8, marginTop:6 }}>
            <span style={{ fontSize:11, color:T2.inkSoft }}>Started by {t.author.name} · {timeAgo(t.created_at)}</span>
            <span style={{ marginLeft:'auto', fontSize:11, fontWeight:700, color:T2.navy }}>{t.commentCount} {t.commentCount===1?'reply':'replies'}</span>
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── GOALS tab ──────────────────────────────────────────────────
function CommunityGoalsTab() {
  const [data, setData]     = useS(null);
  const [status, setStatus] = useS('loading');

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await window.SRAPI._get('/api/community/goals');
        if (!cancelled) { setData(r); setStatus('ready'); }
      } catch (e) { if (!cancelled) setStatus('error'); }
    })();
    return () => { cancelled = true; };
  }, []);

  if (status === 'loading') return <div style={{ padding:'30px', textAlign:'center', fontSize:12.5, color:T2.inkSoft }}>Loading…</div>;
  if (status === 'error')   return <CommunityEmpty icon="bolt" title="Couldn't load Goals" body="Check your connection and try again."/>;

  const progressPct = data.progress.total ? Math.round((data.progress.done/data.progress.total)*100) : 0;

  return (
    <>
      {/* Replaces the old "Level 4 · Strategist · 1,240 XP" hero — no XP or
          level concept exists anywhere in the schema, so it was fabricated
          from nothing. Programme completion and current streak are both
          genuinely computable, so those are what's shown instead. */}
      <div style={{padding:'18px 16px 0'}}>
        <div style={{
          padding:'16px 16px',
          background:`linear-gradient(135deg, ${T2.navy} 0%, #2C4F7A 100%)`,
          borderRadius:16, color:'#fff',
        }}>
          <div style={{display:'flex', alignItems:'center', gap:14}}>
            <div style={{
              width:56, height:56, borderRadius:14, flexShrink:0,
              background:'rgba(255,213,96,0.18)', border:`2px solid ${T2.goldHi}`,
              display:'flex', alignItems:'center', justifyContent:'center',
              color:T2.goldHi, fontWeight:900, fontSize:20, letterSpacing:'-0.02em',
            }}><I name="flame" size={22} c={T2.goldHi} sw={2}/></div>
            <div style={{flex:1, minWidth:0}}>
              <div style={{fontSize:11, fontWeight:800, color:T2.goldHi, letterSpacing:'0.16em', textTransform:'uppercase'}}>Current streak</div>
              <div style={{fontSize:20, fontWeight:800, marginTop:2, letterSpacing:'-0.01em'}}>{data.streak} {data.streak===1?'day':'days'}</div>
              <div style={{display:'flex', alignItems:'center', gap:8, marginTop:8}}>
                <div style={{flex:1, height:5, borderRadius:3, background:'rgba(255,255,255,0.18)', overflow:'hidden'}}>
                  <div style={{width:`${progressPct}%`, height:'100%', background:T2.goldHi}}/>
                </div>
                <span style={{fontSize:10.5, color:'rgba(255,255,255,0.8)', fontWeight:700, whiteSpace:'nowrap'}}>{data.progress.done}/{data.progress.total} lessons</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Section kicker="Cohort leaderboard">
        <div style={{padding:'0 16px'}}>
          {data.leaderboard.length === 0 ? (
            <div style={{ border:`1px dashed ${T2.rule}`, borderRadius:12, padding:'18px 14px', textAlign:'center', fontSize:12.5, color:T2.inkSoft }}>No capability scores recorded for your cohort yet.</div>
          ) : (
            <div style={{border:`1px solid ${T2.rule}`, borderRadius:14, overflow:'hidden'}}>
              {data.leaderboard.map((p, i, arr) => (
                <div key={p.rank} style={{
                  padding:'12px 14px', display:'flex', alignItems:'center', gap:12,
                  background: p.isYou ? T2.navyLt : 'transparent',
                  borderBottom: i < arr.length - 1 ? `1px solid ${T2.ruleSoft}` : 'none',
                }}>
                  <div style={{ width:24, textAlign:'center', fontSize:14, fontWeight:800, color: p.isYou ? T2.navy : T2.inkSoft }}>
                    {p.rank===1?'🥇':p.rank===2?'🥈':p.rank===3?'🥉':`#${p.rank}`}
                  </div>
                  <Avatar initials={p.initials} size={32} bg={p.isYou ? T2.navy : T2.navyLt} fg={p.isYou ? '#fff' : T2.navy}/>
                  <div style={{flex:1, fontSize:13, fontWeight:p.isYou ? 800 : 600, color:T2.ink}}>{p.name}{p.isYou?' (You)':''}</div>
                  <div style={{textAlign:'right'}}>
                    <div style={{fontSize:13, fontWeight:800, color:T2.ink, letterSpacing:'-0.01em'}}>{p.avgScore != null ? p.avgScore.toFixed(2) : '—'}</div>
                    <div style={{fontSize:9.5, color:T2.inkSoft, fontWeight:700, letterSpacing:'0.06em', textTransform:'uppercase'}}>avg score</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </Section>

      <Section kicker="Milestone badges">
        <div style={{padding:'0 16px', display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:10}}>
          {data.badges.map((b) => (
            <div key={b.key} style={{
              padding:'14px 8px', borderRadius:12,
              border:`1px solid ${b.earned ? T2.gold+'40' : T2.rule}`,
              background: b.earned ? T2.gold+'10' : '#fff',
              textAlign:'center', opacity: b.earned ? 1 : 0.6,
            }}>
              <div style={{
                width:36, height:36, borderRadius:'50%', margin:'0 auto 8px',
                background: b.earned ? T2.gold : T2.ruleSoft,
                display:'flex', alignItems:'center', justifyContent:'center',
              }}>
                <I name={b.icon} size={16} c={b.earned ? '#fff' : T2.inkFaint} sw={2.2}/>
              </div>
              <div style={{fontSize:10.5, fontWeight:700, color: b.earned ? T2.ink : T2.inkSoft, lineHeight:1.3}}>{b.label}</div>
            </div>
          ))}
        </div>
      </Section>
    </>
  );
}

// ─── Tasks data layer ──────────────────────────────────────────
// Wired against /api/participant/tasks (migration 133 + the CRUD endpoints
// added alongside it). This screen previously mixed hardcoded personal
// to-dos with hardcoded programme items ("Module 2 Quiz", "Lesson 4
// completed") pulled from nowhere real. Programme items already have a real
// home — Learn and Sessions — so this screen is scoped to what its name
// says: the participant's own personal to-do list.
function fmtDue(dateStr) {
  if (!dateStr) return null;
  const d = new Date(dateStr + 'T00:00:00');
  if (isNaN(d.getTime())) return null;
  const today = new Date(); today.setHours(0,0,0,0);
  const diffDays = Math.round((d - today) / 86400000);
  if (diffDays === 0) return { label: 'Today', urgent: true };
  if (diffDays === 1) return { label: 'Tomorrow', urgent: true };
  if (diffDays < 0)   return { label: `Overdue · ${d.toLocaleDateString('en-IN',{month:'short',day:'numeric'})}`, urgent: true };
  if (diffDays <= 7)  return { label: d.toLocaleDateString('en-IN',{weekday:'short'}), urgent: diffDays <= 2 };
  return { label: d.toLocaleDateString('en-IN',{month:'short',day:'numeric'}), urgent: false };
}

function TaskRow({ task, onToggle, onDelete }) {
  const due = fmtDue(task.due_date);
  const done = task.status === 'completed';
  const PRIORITY_COLOR = { high: T2.rose, medium: T2.gold, low: T2.sage };
  return (
    <div style={{
      padding:'12px 14px', display:'flex', alignItems:'flex-start', gap:12,
      background:'#fff', border:`1px solid ${T2.rule}`, borderRadius:12,
      opacity: done ? 0.7 : 1,
    }}>
      <div onClick={()=>onToggle(task)} style={{
        width:22, height:22, borderRadius:6, flexShrink:0, marginTop:1,
        background: done ? T2.sage : 'transparent',
        border: done ? 'none' : `1.5px solid ${T2.inkFaint}`,
        display:'flex', alignItems:'center', justifyContent:'center',
        cursor:'pointer',
      }}>
        {done && <I name="check" size={12} c="#fff" sw={3}/>}
      </div>
      <div style={{flex:1, minWidth:0}}>
        <div style={{
          fontSize:13, fontWeight:700, color: done ? T2.inkSoft : T2.ink, lineHeight:1.35,
          textDecoration: done ? 'line-through' : 'none', textDecorationColor: T2.inkFaint,
        }}>{task.title}</div>
        <div style={{display:'flex', alignItems:'center', gap:8, marginTop:7, flexWrap:'wrap'}}>
          {due && !done && (
            <span style={{
              fontSize:10, fontWeight:800, letterSpacing:'0.06em', textTransform:'uppercase',
              color: due.urgent ? T2.rose : T2.inkMid, display:'inline-flex', alignItems:'center', gap:3,
            }}><I name="clock" size={10} c={due.urgent ? T2.rose : T2.inkMid} sw={2.2}/>{due.label}</span>
          )}
          {done && task.completed_at && (
            <span style={{fontSize:10, color:T2.inkSoft, fontWeight:700}}>
              Completed {new Date(task.completed_at).toLocaleDateString('en-IN',{month:'short',day:'numeric'})}
            </span>
          )}
          {!done && (
            <span style={{
              fontSize:9.5, fontWeight:800, letterSpacing:'0.08em', textTransform:'uppercase',
              color: PRIORITY_COLOR[task.priority] || T2.inkSoft,
            }}>{task.priority}</span>
          )}
        </div>
      </div>
      <div onClick={()=>onDelete(task)} style={{ cursor:'pointer', padding:4, flexShrink:0 }}>
        <div style={{ transform:'rotate(45deg)', display:'flex' }}><I name="plus" size={14} c={T2.inkFaint} sw={2}/></div>
      </div>
    </div>
  );
}

function ScreenParticipantTasks({ onNav, active }) {
  const [showAdd, setShowAdd] = useS(false);
  const [tasks, setTasks]     = useS([]);
  const [status, setStatus]   = useS('loading');

  const load = React.useCallback(async () => {
    if (!window.SRAPI || typeof window.SRAPI._get !== 'function') { setStatus('error'); return; }
    try {
      const r = await window.SRAPI._get('/api/participant/tasks');
      setTasks(r?.tasks || []);
      setStatus('ready');
    } catch (e) { setStatus('error'); }
  }, []);

  React.useEffect(() => { load(); }, [load]);

  const toggle = async (task) => {
    const next = task.status === 'completed' ? 'open' : 'completed';
    setTasks(list => list.map(t => t.id === task.id ? { ...t, status: next, completed_at: next === 'completed' ? new Date().toISOString() : null } : t));
    try {
      await window.SRAPI._patch(`/api/participant/tasks/${task.id}`, { status: next });
    } catch (e) { load(); } // resync on failure rather than leave an unsynced optimistic state
  };

  const remove = async (task) => {
    const prev = tasks;
    setTasks(list => list.filter(t => t.id !== task.id));
    try {
      await window.SRAPI._delete(`/api/participant/tasks/${task.id}`);
    } catch (e) { setTasks(prev); }
  };

  const now = new Date(); now.setHours(0,0,0,0);
  const weekOut = new Date(now); weekOut.setDate(weekOut.getDate() + 7);
  const open = tasks.filter(t => t.status !== 'completed');
  const dueThisWeek = open.filter(t => t.due_date && new Date(t.due_date + 'T00:00:00') <= weekOut);
  const upNext = open.filter(t => !dueThisWeek.includes(t));
  const completed = tasks.filter(t => t.status === 'completed');

  return (
    <div style={sh.shell}>
      {showAdd && <ModalAddTask onClose={()=>setShowAdd(false)} onCreated={(t)=>{ setTasks(list => [t, ...list]); }}/>}
      <div style={sh.scroll}>
        <div style={{ padding:'12px 14px 14px', borderBottom:`1px solid ${T2.rule}`, display:'flex', alignItems:'center', gap:10 }}>
          <div onClick={()=>onNav&&onNav('home')} style={{ cursor:'pointer' }}><I name="chevL" size={20} c={T2.ink} sw={2.2}/></div>
          <div style={{ flex:1 }}>
            <Label>To-do</Label>
            <div style={{ fontSize:18, fontWeight:800, color:T2.ink, letterSpacing:'-0.015em' }}>Your tasks</div>
          </div>
          <div onClick={()=>setShowAdd(true)} style={{ width:32, height:32, borderRadius:10, border:`1.5px solid ${T2.rule}`, display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer' }}>
            <I name="plus" size={16} c={T2.ink} sw={2.2}/>
          </div>
        </div>

        {status === 'loading' && (
          <div style={{ padding:'40px 32px', textAlign:'center', fontSize:12.5, color:T2.inkSoft }}>Loading your tasks…</div>
        )}
        {status === 'error' && (
          <div style={{ padding:'40px 32px', textAlign:'center', fontSize:12.5, color:T2.inkSoft }}>Couldn't load your tasks. Check your connection and try again.</div>
        )}

        {status === 'ready' && (
          <>
            <div style={{ padding:'18px 16px 0' }}>
              <div style={{ display:'flex', borderBottom:`1px solid ${T2.rule}`, paddingBottom:18 }}>
                {[
                  { v:dueThisWeek.length, l:'Due this week', c:T2.rose },
                  { v:upNext.length,      l:'Up next' },
                  { v:completed.length,   l:'Completed', c:T2.sage },
                ].map((s,i,arr)=>(
                  <div key={i} style={{ flex:1, textAlign:'center', borderRight:i<arr.length-1?`1px solid ${T2.ruleSoft}`:'none' }}>
                    <div style={{ fontSize:22, fontWeight:800, color:s.c||T2.ink, lineHeight:1, letterSpacing:'-0.02em' }}>{s.v}</div>
                    <Label mt={6}>{s.l}</Label>
                  </div>
                ))}
              </div>
            </div>

            {tasks.length === 0 && (
              <div style={{ padding:'40px 32px', textAlign:'center' }}>
                <div style={{ width:46, height:46, borderRadius:12, background:T2.ruleSoft, margin:'0 auto 14px', display:'flex', alignItems:'center', justifyContent:'center' }}>
                  <I name="check" size={20} c={T2.inkSoft} sw={2}/>
                </div>
                <div style={{ fontSize:14.5, fontWeight:700, color:T2.ink, marginBottom:6 }}>Nothing on your list yet</div>
                <div style={{ fontSize:12.5, color:T2.inkSoft }}>Tap + to add something.</div>
              </div>
            )}

            {dueThisWeek.length > 0 && (
              <Section kicker="Due this week" pt={20}>
                <div style={{ padding:'0 16px', display:'flex', flexDirection:'column', gap:8 }}>
                  {dueThisWeek.map(t => <TaskRow key={t.id} task={t} onToggle={toggle} onDelete={remove}/>)}
                </div>
              </Section>
            )}
            {upNext.length > 0 && (
              <Section kicker="Up next">
                <div style={{ padding:'0 16px', display:'flex', flexDirection:'column', gap:8 }}>
                  {upNext.map(t => <TaskRow key={t.id} task={t} onToggle={toggle} onDelete={remove}/>)}
                </div>
              </Section>
            )}
            {completed.length > 0 && (
              <Section kicker="Completed">
                <div style={{ padding:'0 16px', display:'flex', flexDirection:'column', gap:8 }}>
                  {completed.map(t => <TaskRow key={t.id} task={t} onToggle={toggle} onDelete={remove}/>)}
                </div>
              </Section>
            )}
          </>
        )}
        <div style={{ height:24 }}/>
      </div>
      <BottomNav active={active||'home'} role="participant" onNav={onNav}/>
    </div>
  );
}

function ModalAddTask({ onClose, onCreated }) {
  const [title, setTitle] = useS('');
  const [priority, setPriority] = useS('medium');
  const [due, setDue] = useS('tomorrow');
  const [reminder, setReminder] = useS(true);
  const [notify, setNotify] = useS(true);
  const [saving, setSaving] = useS(false);
  const [saved, setSaved] = useS(false);
  const [err, setErr] = useS(false);

  const Toggle = ({on, toggle}) => (
    <div onClick={toggle} style={{ width:44, height:25, borderRadius:13, background:on?T2.navy:T2.rule, position:'relative', cursor:'pointer', flexShrink:0 }}>
      <div style={{ position:'absolute', top:3, left:on?21:3, width:19, height:19, borderRadius:'50%', background:'#fff', boxShadow:'0 1px 4px rgba(0,0,0,0.2)', transition:'left 0.18s' }}/>
    </div>
  );

  const dueDateFor = (id) => {
    const d = new Date();
    if (id === 'tomorrow') d.setDate(d.getDate() + 1);
    else if (id === 'next') d.setDate(d.getDate() + 7);
    else if (id === 'today') { /* no-op */ }
    else return null; // 'pick' — no date picker built; left null on purpose
    return d.toISOString().slice(0,10);
  };

  const submit = async () => {
    if (!title.trim() || saving) return;
    setSaving(true); setErr(false);
    try {
      const r = await window.SRAPI._post('/api/participant/tasks', {
        title: title.trim(), priority, dueDate: dueDateFor(due),
        reminderEnabled: reminder, notifyEnabled: notify,
      });
      setSaved(true);
      onCreated && onCreated(r);
      setTimeout(onClose, 1600);
    } catch (e) {
      setErr(true);
    }
    setSaving(false);
  };

  if (saved) return (
    <div onClick={onClose} style={{ position:'absolute', inset:0, zIndex:40, background:'rgba(0,0,0,0.5)', display:'flex', alignItems:'center', justifyContent:'center' }}>
      <div style={{ background:'#fff', borderRadius:20, padding:'32px 28px', textAlign:'center', margin:'0 28px' }}>
        <div style={{ width:60, height:60, borderRadius:'50%', background:T2.sageLt, margin:'0 auto 16px', display:'flex', alignItems:'center', justifyContent:'center' }}>
          <I name="check" size={24} c={T2.sage} sw={2.8}/>
        </div>
        <div style={{ fontSize:18, fontWeight:800, color:T2.ink, marginBottom:8 }}>Task added</div>
        <div style={{ fontSize:13, color:T2.inkSoft, lineHeight:1.5 }}>Added to your list.{reminder?' Reminder set.':''}</div>
      </div>
    </div>
  );

  return (
    <div style={{ position:'absolute', inset:0, zIndex:40, background:'rgba(0,0,0,0.45)', display:'flex', flexDirection:'column', justifyContent:'flex-end' }}>
      <div style={{ background:'#fff', borderRadius:'20px 20px 0 0', maxHeight:'92%', display:'flex', flexDirection:'column' }}>
        <div style={{ padding:'10px 16px 14px', borderBottom:`1px solid ${T2.rule}`, flexShrink:0 }}>
          <div style={{ width:40, height:4, borderRadius:2, background:T2.rule, margin:'0 auto 14px' }}/>
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between' }}>
            <div style={{ fontSize:17, fontWeight:800, color:T2.ink }}>New task</div>
            <div onClick={onClose} style={{ fontSize:13.5, fontWeight:700, color:T2.inkSoft, cursor:'pointer' }}>Cancel</div>
          </div>
        </div>
        <div style={{ overflow:'auto', flex:1, padding:'18px 16px' }}>
          <div style={{ marginBottom:20 }}>
            <Label mb={8}>Task title</Label>
            <input value={title} onChange={e=>setTitle(e.target.value)} placeholder="What do you need to do?"
              style={{ width:'100%', padding:'12px 13px', border:`1.5px solid ${title?T2.navy:T2.rule}`, borderRadius:10, fontSize:14, color:T2.ink, outline:'none', fontFamily:'inherit', background:T2.page, boxSizing:'border-box' }}/>
          </div>
          <div style={{ marginBottom:20 }}>
            <Label mb={8}>Priority</Label>
            <div style={{ display:'flex', gap:8 }}>
              {[{id:'low',l:'Low',c:T2.sage},{id:'medium',l:'Medium',c:T2.amber},{id:'high',l:'High',c:T2.rose}].map(p=>(
                <div key={p.id} onClick={()=>setPriority(p.id)} style={{ flex:1, padding:'10px', textAlign:'center', borderRadius:10, cursor:'pointer', border:`2px solid ${priority===p.id?p.c:T2.rule}`, background:priority===p.id?`${p.c}15`:'#fff' }}>
                  <div style={{ fontSize:12.5, fontWeight:800, color:priority===p.id?p.c:T2.inkSoft }}>{p.l}</div>
                </div>
              ))}
            </div>
          </div>
          <div style={{ marginBottom:20 }}>
            <Label mb={8}>Due date</Label>
            <div style={{ display:'flex', gap:7 }}>
              {[{id:'today',l:'Today'},{id:'tomorrow',l:'Tomorrow'},{id:'next',l:'Next week'},{id:'pick',l:'No date'}].map(d=>(
                <div key={d.id} onClick={()=>setDue(d.id)} style={{ flex:1, padding:'9px 4px', textAlign:'center', borderRadius:10, cursor:'pointer', border:`2px solid ${due===d.id?T2.navy:T2.rule}`, background:due===d.id?T2.navyLt:'#fff' }}>
                  <div style={{ fontSize:10.5, fontWeight:800, color:due===d.id?T2.navy:T2.inkSoft, lineHeight:1.3 }}>{d.l}</div>
                </div>
              ))}
            </div>
          </div>
          {/* "Add to calendar" was dropped from this list: it's a one-time
              export action for a task with a due date, which doesn't fit a
              persistent toggle the way it does for a recurring session — and
              nothing in this modal has a real .ics target the way
              ModalSessionDetail's addCal() does. Reminder + push notification
              persist to the task row (migration 133); a calendar export can
              be added the same way ModalSessionDetail's does, if wanted. */}
          <div style={{ border:`1px solid ${T2.rule}`, borderRadius:14, overflow:'hidden' }}>
            {[
              {icon:'bell', label:'Reminder',         sub:'Get notified before due date', on:reminder, toggle:()=>setReminder(!reminder)},
              {icon:'ping', label:'Push notification', sub:'Alert on your phone',          on:notify,   toggle:()=>setNotify(!notify)},
            ].map((row,i,arr)=>(
              <div key={i} style={{ display:'flex', alignItems:'center', gap:12, padding:'13px 14px', borderBottom:i<arr.length-1?`1px solid ${T2.ruleSoft}`:'none' }}>
                <div style={{ width:36, height:36, borderRadius:10, background:row.on?T2.navyLt:T2.ruleSoft, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                  <I name={row.icon} size={16} c={row.on?T2.navy:T2.inkFaint} sw={2}/>
                </div>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ fontSize:13, fontWeight:700, color:T2.ink }}>{row.label}</div>
                  <div style={{ fontSize:11, color:T2.inkSoft, marginTop:1 }}>{row.sub}</div>
                </div>
                <Toggle on={row.on} toggle={row.toggle}/>
              </div>
            ))}
          </div>
          {err && <div style={{ fontSize:11.5, color:T2.rose, marginTop:12 }}>Couldn't save — check your connection and try again.</div>}
        </div>
        <div style={{ padding:'12px 16px 26px', flexShrink:0, borderTop:`1px solid ${T2.rule}` }}>
          <button onClick={submit} disabled={!title.trim()||saving}
            style={{ width:'100%', padding:'14px', borderRadius:12, border:'none', background:title.trim()?T2.navy:T2.ruleSoft, color:title.trim()?'#fff':T2.inkFaint, fontSize:15, fontWeight:800, fontFamily:'inherit', cursor:title.trim()&&!saving?'pointer':'default', opacity:saving?0.6:1 }}>
            {saving ? 'Adding…' : 'Add task'}
          </button>
        </div>
      </div>
    </div>
  );
}

function ScreenEditProfile({ onNav, active }) {
  const [name, setName] = useS('');
  const [role, setRole] = useS('');
  const [bio, setBio]   = useS('');
  const [email, setEmail] = useS('');
  const [loading, setLoading] = useS(true);
  const [saving, setSaving] = useS(false);
  const [saveErr, setSaveErr] = useS('');

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!window.SRAPI || typeof window.SRAPI._get !== 'function') { setLoading(false); return; }
      try {
        const res = await window.SRAPI._get('/api/participant/profile');
        if (cancelled) return;
        setName(res?.name || '');
        setRole(res?.title || '');
        setBio(res?.bio || '');
        setEmail(res?.email || '');
      } catch (e) { /* leave fields blank rather than fake data */ }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, []);

  const save = async () => {
    setSaving(true);
    setSaveErr('');
    try {
      await window.SRAPI._patch('/api/participant/profile', { name, title: role, bio });
      onNav && onNav('me');
    } catch (e) {
      setSaveErr(e?.message || 'Could not save — please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={sh.shell}>
      <div style={sh.scroll}>
        <div style={{
          padding:'12px 14px', borderBottom:`1px solid ${T2.rule}`,
          display:'flex', alignItems:'center', gap:10,
        }}>
          <div onClick={() => onNav && onNav('me')} style={{cursor:'pointer'}}>
            <I name="chevL" size={20} c={T2.ink} sw={2.2}/>
          </div>
          <div style={{flex:1, textAlign:'center', fontSize:14, fontWeight:800, color:T2.ink}}>Edit profile</div>
          <span onClick={saving ? undefined : save} style={{
            fontSize:13, fontWeight:800, color: saving ? T2.inkFaint : T2.navy, cursor: saving ? 'default' : 'pointer',
          }}>{saving ? 'Saving…' : 'Save'}</span>
        </div>
        {saveErr && (
          <div style={{padding:'8px 16px 0', fontSize:12, color:T2.rose}}>{saveErr}</div>
        )}

        {/* Avatar */}
        <div style={{padding:'30px 16px 18px', textAlign:'center'}}>
          <div onClick={() => onNav && onNav('avatar')} style={{
            display:'inline-block', position:'relative', cursor:'pointer',
          }}>
            <div style={{
              width:92, height:92, borderRadius:'50%',
              background:'#FFD7E3', display:'flex', alignItems:'center', justifyContent:'center',
              fontSize:42,
            }}>🦊</div>
            <div style={{
              position:'absolute', bottom:0, right:0,
              width:30, height:30, borderRadius:'50%', background:T2.navy,
              border:'3px solid #fff',
              display:'flex', alignItems:'center', justifyContent:'center',
            }}>
              <I name="camera" size={13} c="#fff" sw={2}/>
            </div>
          </div>
          <div style={{fontSize:11.5, color:T2.navy, fontWeight:700, marginTop:10, letterSpacing:'0.04em'}}>Tap to change avatar</div>
        </div>

        {/* Fields */}
        <div style={{padding:'0 16px', display:'flex', flexDirection:'column', gap:14}}>
          <FieldInput label="Name" value={name} onChange={setName} disabled={loading}/>
          <FieldInput label="Job title" value={role} onChange={setRole} disabled={loading}/>
          <FieldInput label="Bio" value={bio} onChange={setBio} multiline disabled={loading}/>
          <FieldStatic label="Work email" value={email || '—'}/>
        </div>

        <Section kicker="Visibility">
          <div style={{padding:'0 16px'}}>
            <div style={{border:`1px solid ${T2.rule}`, borderRadius:12, overflow:'hidden'}}>
              {[
                {l:'Show on cohort leaderboard', v:'On'},
                {l:'Allow direct messages',      v:'On'},
                {l:'Email digest',               v:'Weekly'},
              ].map((it, i, a) => (
                <div key={i} style={{
                  padding:'13px 14px', display:'flex', alignItems:'center',
                  borderBottom: i < a.length - 1 ? `1px solid ${T2.ruleSoft}` : 'none',
                }}>
                  <span style={{flex:1, fontSize:13, color:T2.ink}}>{it.l}</span>
                  <span style={{fontSize:12, color:T2.navy, fontWeight:700, marginRight:8}}>{it.v}</span>
                  <I name="chevR" size={13} c={T2.inkFaint} sw={2}/>
                </div>
              ))}
            </div>
          </div>
        </Section>

        <div style={{height: 24}}/>
      </div>
    </div>
  );
}

function FieldInput({ label, value, onChange, multiline }) {
  return (
    <div>
      <Label mb={8}>{label}</Label>
      {multiline ? (
        <textarea value={value} onChange={e => onChange(e.target.value)} rows={3} style={{
          width:'100%', padding:'12px 13px', resize:'none',
          background:'#fff', color:T2.ink, fontSize:13, lineHeight:1.5,
          border:`1px solid ${T2.rule}`, borderRadius:10, fontFamily:'inherit', outline:'none',
        }}/>
      ) : (
        <input value={value} onChange={e => onChange(e.target.value)} style={{
          width:'100%', padding:'12px 13px',
          background:'#fff', color:T2.ink, fontSize:13.5, fontWeight:600,
          border:`1px solid ${T2.rule}`, borderRadius:10, fontFamily:'inherit', outline:'none',
        }}/>
      )}
    </div>
  );
}

function FieldStatic({ label, value }) {
  return (
    <div>
      <Label mb={8}>{label}</Label>
      <div style={{
        padding:'12px 13px', borderRadius:10,
        background:T2.ruleSoft, color:T2.inkSoft, fontSize:13, fontWeight:600,
        display:'flex', alignItems:'center', gap:8,
      }}>
        <I name="lock" size={13} c={T2.inkFaint} sw={2}/>
        {value}
      </div>
    </div>
  );
}

// ============================================================
// PARTICIPANT · Avatar Picker (full screen — palette × emoji grid)
// ============================================================
function ScreenAvatarPicker({ onNav }) {
  const palettes = [
    '#FFD7E3', '#FFE2BF', '#FFF3B0', '#D6F2DC', '#CDEEF7', '#E5DBFF',
  ];
  const emojis = ['🦊','🐻','🦁','🐳','🌱','🚀','🌸','✨','🎯'];
  const [palette, setPalette] = useS('#FFE2BF');
  const [emoji, setEmoji] = useS('🦊');

  return (
    <div style={sh.shell}>
      <div style={sh.scroll}>
        <div style={{
          padding:'12px 14px', borderBottom:`1px solid ${T2.rule}`,
          display:'flex', alignItems:'center', gap:10,
        }}>
          <div onClick={() => onNav && onNav('edit')} style={{cursor:'pointer'}}>
            <I name="chevL" size={20} c={T2.ink} sw={2.2}/>
          </div>
          <div style={{flex:1, textAlign:'center', fontSize:14, fontWeight:800, color:T2.ink}}>Choose avatar</div>
          <span onClick={() => onNav && onNav('edit')} style={{
            fontSize:13, fontWeight:800, color:T2.navy, cursor:'pointer',
          }}>Done</span>
        </div>

        {/* Live preview */}
        <div style={{padding:'40px 16px 24px', textAlign:'center'}}>
          <div style={{
            width:118, height:118, borderRadius:'50%', margin:'0 auto',
            background:palette, display:'flex', alignItems:'center', justifyContent:'center',
            fontSize:56,
          }}>{emoji}</div>
          <div style={{
            fontSize:11.5, color:T2.inkSoft, fontWeight:700, marginTop:14,
            letterSpacing:'0.08em', textTransform:'uppercase',
          }}>Live preview</div>
        </div>

        {/* Palette */}
        <Section kicker="Background">
          <div style={{padding:'0 16px', display:'flex', gap:12, justifyContent:'center', flexWrap:'wrap'}}>
            {palettes.map(p => {
              const on = palette === p;
              return (
                <div key={p} onClick={() => setPalette(p)} style={{
                  width:48, height:48, borderRadius:'50%', background:p, cursor:'pointer',
                  border: on ? `3px solid ${T2.navy}` : '3px solid transparent',
                  display:'flex', alignItems:'center', justifyContent:'center',
                  position:'relative',
                }}>
                  {on && (
                    <div style={{
                      position:'absolute', top:-3, right:-3,
                      width:18, height:18, borderRadius:'50%',
                      background:T2.navy, border:'2px solid #fff',
                      display:'flex', alignItems:'center', justifyContent:'center',
                    }}>
                      <I name="check" size={10} c="#fff" sw={3}/>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </Section>

        <Section kicker="Symbol">
          <div style={{padding:'0 16px', display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:10}}>
            {emojis.map(e => {
              const on = emoji === e;
              return (
                <div key={e} onClick={() => setEmoji(e)} style={{
                  aspectRatio:'1', borderRadius:14, cursor:'pointer',
                  background: on ? T2.navyLt : '#fff',
                  border: on ? `2px solid ${T2.navy}` : `1px solid ${T2.rule}`,
                  display:'flex', alignItems:'center', justifyContent:'center',
                  fontSize:36,
                }}>{e}</div>
              );
            })}
          </div>
        </Section>

        <div style={{height: 24}}/>
      </div>
    </div>
  );
}

// ─── Me Screen Modals ─────────────────────────────────────────
function ModalPingTrainer({ onClose }) {
  const [msg, setMsg] = useS('');
  const [sent, setSent] = useS(false);
  const XBtn = () => <div onClick={onClose} style={{ cursor:'pointer', width:28, height:28, borderRadius:'50%', background:T2.ruleSoft, display:'flex', alignItems:'center', justifyContent:'center' }}><div style={{ transform:'rotate(45deg)', display:'flex' }}><I name="plus" size={14} c={T2.inkSoft} sw={2.4}/></div></div>;
  if (sent) return (
    <div onClick={onClose} style={{ position:'absolute', inset:0, zIndex:40, background:'rgba(0,0,0,0.5)', display:'flex', alignItems:'center', justifyContent:'center' }}>
      <div style={{ background:'#fff', borderRadius:20, padding:'32px 28px', textAlign:'center', margin:'0 28px' }}>
        <div style={{ width:60, height:60, borderRadius:'50%', background:T2.sageLt, margin:'0 auto 16px', display:'flex', alignItems:'center', justifyContent:'center' }}><I name="check" size={24} c={T2.sage} sw={2.8}/></div>
        <div style={{ fontSize:18, fontWeight:800, color:T2.ink, marginBottom:8 }}>Ping sent!</div>
        <div style={{ fontSize:13, color:T2.inkSoft, lineHeight:1.5 }}>Dr. Neha Gupta will respond within 24 hours.</div>
      </div>
    </div>
  );
  return (
    <div onClick={onClose} style={{ position:'absolute', inset:0, zIndex:40, background:'rgba(0,0,0,0.45)', display:'flex', flexDirection:'column', justifyContent:'flex-end' }}>
      <div onClick={e=>e.stopPropagation()} style={{ background:'#fff', borderRadius:'20px 20px 0 0', padding:'0 0 24px' }}>
        <div style={{ padding:'10px 16px 12px', borderBottom:`1px solid ${T2.rule}`, display:'flex', alignItems:'center', position:'relative' }}>
          <div style={{ width:40, height:4, borderRadius:2, background:T2.rule, position:'absolute', left:'50%', top:8, transform:'translateX(-50%)' }}/>
          <div style={{ fontSize:15, fontWeight:800, color:T2.ink, flex:1, marginTop:4 }}>Ping Dr. Neha Gupta</div>
          <XBtn/>
        </div>
        <div style={{ padding:'16px 16px 0' }}>
          <div style={{ display:'flex', gap:12, alignItems:'flex-start', marginBottom:14 }}>
            <Avatar initials="NG" size={40} bg={T2.navyLt} fg={T2.navy}/>
            <div style={{ flex:1, background:T2.ruleSoft, borderRadius:'4px 12px 12px 12px', padding:'10px 14px' }}>
              <div style={{ fontSize:11, fontWeight:800, color:T2.navy, marginBottom:4 }}>Dr. Neha Gupta</div>
              <div style={{ fontSize:12.5, color:T2.inkMid, lineHeight:1.45 }}>Hi Priya! Feel free to ask anything about your learning journey. 👋</div>
            </div>
          </div>
          {["Quick question on today's lesson", "I'm struggling with this module", "Can we schedule a check-in?"].map((t,i)=>(
            <div key={i} onClick={()=>setMsg(t)} style={{ padding:'10px 13px', borderRadius:10, border:`1.5px solid ${msg===t?T2.navy:T2.rule}`, marginBottom:8, cursor:'pointer', background:msg===t?T2.navyLt:'#fff' }}>
              <div style={{ fontSize:12.5, fontWeight:600, color:msg===t?T2.navy:T2.ink }}>{t}</div>
            </div>
          ))}
          <input value={msg} onChange={e=>setMsg(e.target.value)} placeholder="Or type a message…"
            style={{ width:'100%', padding:'10px 13px', border:`1.5px solid ${msg?T2.navy:T2.rule}`, borderRadius:10, fontSize:13, color:T2.ink, outline:'none', fontFamily:'inherit', background:T2.page, boxSizing:'border-box', marginTop:2 }}/>
        </div>
        <div style={{ padding:'14px 16px 0' }}>
          <button onClick={()=>{ if(msg.trim()){ setSent(true); setTimeout(onClose,2400); }}}
            style={{ width:'100%', padding:'14px', borderRadius:12, border:'none', background:msg.trim()?T2.navy:T2.ruleSoft, color:msg.trim()?'#fff':T2.inkFaint, fontSize:15, fontWeight:800, fontFamily:'inherit', cursor:msg.trim()?'pointer':'default' }}>
            Send ping
          </button>
        </div>
      </div>
    </div>
  );
}

function ModalNotificationSettings({ onClose }) {
  // Push is real (see registerWebPush in skalerealty-sync.js for the browser
  // path). Inside the native app shell, push is registered automatically at
  // launch via PushBridge (raw FCM/APNs token, no browser permission API
  // involved) — window.isNativeApp / window.nativePushToken are injected by
  // the native shell before this page loads, so we branch on that instead
  // of assuming a browser Notification API always exists.
  const isNative = typeof window !== 'undefined' && window.isNativeApp === true;
  const [push, setPush] = useS(isNative
    ? !!(typeof window !== 'undefined' && window.nativePushToken)
    : (typeof Notification !== 'undefined' && Notification.permission === 'granted'));
  const [pushStatus, setPushStatus] = useS(''); // '', 'requesting', 'error'
  const [email, setEmail] = useS(true);
  const [remind, setRemind] = useS(true);
  const [social, setSocial] = useS(false);
  const Tog = ({on,tog}) => <div onClick={tog} style={{ width:44, height:25, borderRadius:13, background:on?T2.navy:T2.rule, position:'relative', cursor:'pointer', flexShrink:0 }}><div style={{ position:'absolute', top:3, left:on?21:3, width:19, height:19, borderRadius:'50%', background:'#fff', boxShadow:'0 1px 4px rgba(0,0,0,0.2)', transition:'left 0.18s' }}/></div>;

  const togglePush = async () => {
    if (isNative) {
      if (push) return; // OS-level permission — nothing to flip from here
      // Ask the native shell to (re)request permission + fetch a device token.
      setPushStatus('requesting');
      window.ReactNativeWebView && window.ReactNativeWebView.postMessage(JSON.stringify({ type: 'PUSH_TOKEN_REQUEST' }));
      var waited = 0;
      var poll = setInterval(function () {
        waited += 400;
        if (window.nativePushToken) {
          clearInterval(poll); setPush(true); setPushStatus('');
        } else if (waited >= 6000) {
          clearInterval(poll); setPushStatus('permission_denied');
        }
      }, 400);
      return;
    }
    if (push) { setPush(false); return; } // can't programmatically revoke browser permission — just stop showing as on
    setPushStatus('requesting');
    const res = window.SRAPI && typeof window.SRAPI.registerWebPush === 'function'
      ? await window.SRAPI.registerWebPush()
      : { ok: false, reason: 'unsupported' };
    if (res.ok) { setPush(true); setPushStatus(''); }
    else { setPushStatus(res.reason || 'error'); }
  };
  const pushSub = pushStatus === 'requesting' ? 'Requesting permission…'
    : pushStatus === 'permission_denied' ? (isNative ? 'Blocked — enable in device Settings' : 'Blocked — enable in browser settings')
    : pushStatus === 'not_configured' ? 'Not available yet'
    : pushStatus === 'unsupported' ? 'Not supported on this browser'
    : pushStatus ? "Couldn't enable — try again"
    : 'Alerts on your phone';

  return (
    <div onClick={onClose} style={{ position:'absolute', inset:0, zIndex:40, background:'rgba(0,0,0,0.45)', display:'flex', flexDirection:'column', justifyContent:'flex-end' }}>
      <div onClick={e=>e.stopPropagation()} style={{ background:'#fff', borderRadius:'20px 20px 0 0', padding:'0 0 28px' }}>
        <div style={{ padding:'10px 16px 12px', borderBottom:`1px solid ${T2.rule}`, display:'flex', alignItems:'center', position:'relative' }}>
          <div style={{ width:40, height:4, borderRadius:2, background:T2.rule, position:'absolute', left:'50%', top:8, transform:'translateX(-50%)' }}/>
          <div style={{ fontSize:15, fontWeight:800, color:T2.ink, flex:1, marginTop:4 }}>Notifications</div>
          <div onClick={onClose} style={{ cursor:'pointer', width:28, height:28, borderRadius:'50%', background:T2.ruleSoft, display:'flex', alignItems:'center', justifyContent:'center' }}><div style={{ transform:'rotate(45deg)', display:'flex' }}><I name="plus" size={14} c={T2.inkSoft} sw={2.4}/></div></div>
        </div>
        <div style={{ border:`1px solid ${T2.rule}`, borderRadius:14, overflow:'hidden', margin:'16px 16px 0' }}>
          {[
            {icon:'bell', l:'Push notifications', sub:pushSub, on:push, tog:togglePush},
            {icon:'msg',  l:'Email digest',       sub:'Weekly summary to inbox', on:email, tog:()=>setEmail(!email)},
            {icon:'check',l:'Task reminders',     sub:'Before tasks are due', on:remind, tog:()=>setRemind(!remind)},
            {icon:'community',l:'Community activity',sub:'Replies, likes, mentions', on:social, tog:()=>setSocial(!social)},
          ].map((r,i,a)=>(
            <div key={i} style={{ display:'flex', alignItems:'center', gap:12, padding:'13px 14px', borderBottom:i<a.length-1?`1px solid ${T2.ruleSoft}`:'none' }}>
              <div style={{ width:36, height:36, borderRadius:10, background:r.on?T2.navyLt:T2.ruleSoft, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                <I name={r.icon} size={16} c={r.on?T2.navy:T2.inkFaint} sw={2}/>
              </div>
              <div style={{ flex:1 }}><div style={{ fontSize:13, fontWeight:700, color:T2.ink }}>{r.l}</div><div style={{ fontSize:11, color:T2.inkSoft, marginTop:1 }}>{r.sub}</div></div>
              <Tog on={r.on} tog={r.tog}/>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function ModalCalendarSync({ onClose, calProvider, onSync }) {
  const providers = [{id:'google',l:'Google Calendar'},{id:'outlook',l:'Outlook / MS 365'},{id:'apple',l:'Apple Calendar'}];
  const [sel, setSel] = useS(calProvider || 'google');
  const [synced, setSynced] = useS(false);
  const connected = calProvider && calProvider === sel;
  if (synced) return (
    <div onClick={onClose} style={{ position:'absolute', inset:0, zIndex:40, background:'rgba(0,0,0,0.5)', display:'flex', alignItems:'center', justifyContent:'center' }}>
      <div style={{ background:'#fff', borderRadius:20, padding:'32px 28px', textAlign:'center', margin:'0 28px' }}>
        <div style={{ width:60, height:60, borderRadius:'50%', background:T2.sageLt, margin:'0 auto 16px', display:'flex', alignItems:'center', justifyContent:'center' }}><I name="check" size={24} c={T2.sage} sw={2.8}/></div>
        <div style={{ fontSize:18, fontWeight:800, color:T2.ink, marginBottom:8 }}>Calendar synced!</div>
        <div style={{ fontSize:13, color:T2.inkSoft, lineHeight:1.5 }}>Sessions and deadlines will appear in {providers.find(p=>p.id===sel)?.l}.</div>
      </div>
    </div>
  );
  return (
    <div onClick={onClose} style={{ position:'absolute', inset:0, zIndex:40, background:'rgba(0,0,0,0.45)', display:'flex', flexDirection:'column', justifyContent:'flex-end' }}>
      <div onClick={e=>e.stopPropagation()} style={{ background:'#fff', borderRadius:'20px 20px 0 0', padding:'0 0 28px' }}>
        <div style={{ padding:'10px 16px 12px', borderBottom:`1px solid ${T2.rule}`, display:'flex', alignItems:'center', position:'relative' }}>
          <div style={{ width:40, height:4, borderRadius:2, background:T2.rule, position:'absolute', left:'50%', top:8, transform:'translateX(-50%)' }}/>
          <div style={{ fontSize:15, fontWeight:800, color:T2.ink, flex:1, marginTop:4 }}>Calendar sync</div>
          <div onClick={onClose} style={{ cursor:'pointer', width:28, height:28, borderRadius:'50%', background:T2.ruleSoft, display:'flex', alignItems:'center', justifyContent:'center' }}><div style={{ transform:'rotate(45deg)', display:'flex' }}><I name="plus" size={14} c={T2.inkSoft} sw={2.4}/></div></div>
        </div>
        <div style={{ padding:'18px 16px 0' }}>
          <div style={{ fontSize:13, color:T2.inkSoft, marginBottom:14, lineHeight:1.5 }}>Choose your calendar to sync sessions, deadlines, and tasks.</div>
          {providers.map((p,i)=>(
            <div key={i} onClick={()=>setSel(p.id)} style={{ display:'flex', alignItems:'center', gap:12, padding:'13px 14px', borderRadius:12, border:`2px solid ${sel===p.id?T2.navy:T2.rule}`, marginBottom:8, cursor:'pointer', background:sel===p.id?T2.navyLt:'#fff' }}>
              <div style={{ width:36, height:36, borderRadius:10, background:sel===p.id?T2.navy:T2.ruleSoft, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}><I name="cal" size={18} c={sel===p.id?'#fff':T2.inkSoft} sw={2}/></div>
              <div style={{ flex:1, fontSize:13.5, fontWeight:700, color:sel===p.id?T2.navy:T2.ink }}>{p.l}</div>
              {sel===p.id && calProvider===p.id && <span style={{ fontSize:9.5, fontWeight:800, color:T2.sage, background:T2.sageLt, borderRadius:6, padding:'2px 7px', letterSpacing:'0.06em' }}>CONNECTED</span>}
              {sel===p.id && calProvider!==p.id && <I name="check" size={16} c={T2.navy} sw={2.5}/>}
            </div>
          ))}
          <button
            onClick={()=>{ onSync && onSync(sel); setSynced(true); setTimeout(onClose, 2400); }}
            style={{ width:'100%', padding:'14px', borderRadius:12, border:'none', background: connected ? T2.sage : T2.navy, color:'#fff', fontSize:15, fontWeight:800, fontFamily:'inherit', cursor:'pointer', marginTop:4 }}>
            {connected ? `Reconnect ${providers.find(p=>p.id===sel)?.l}` : `Connect ${providers.find(p=>p.id===sel)?.l}`}
          </button>
        </div>
      </div>
    </div>
  );
}

Object.assign(window, {
  ScreenCommunity,
  ScreenParticipantTasks,
  ScreenEditProfile, ScreenAvatarPicker,
  ModalPingTrainer, ModalNotificationSettings, ModalCalendarSync,
});

})(); // end IIFE



// ===== 23-inline-02.jsx =====
(function () {
const { useState: useS } = React;
const { T2, I, Avatar, IconButton, Bar, Label, Section, Header, BottomNav, sh, DateBlock } = window;


// The fake `LESSONS` catalogue that used to sit here — eight invented
// lessons with invented prose, resources and XP — was removed when Learn
// was wired to /participant/learn. It also pre-seeded _lessonState with
// lesson 5, so opening Lesson detail directly showed fiction; the screen
// now renders a real empty state when nothing is selected.

const fmtTime = (s) => `${String(Math.floor(s/60)).padStart(2,'0')}:${String(s%60).padStart(2,'0')}`;

// ─── MODAL: Video Player ──────────────────────────────────────
// ── Screenshot shield ─────────────────────────────────────────────────────
// Shows a black overlay when the app loses visibility (tab switch, app
// switcher, or screenshot on most mobile browsers). Also blocks print.
// ─── Shared viewer chrome ─────────────────────────────────────
function useScreenshotShield() {
  const [blocked, setBlocked] = useS(false);
  React.useEffect(() => {
    const onVis = () => setBlocked(document.hidden);
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, []);
  return blocked;
}

function ViewerTopBar({ kicker, title, onClose, right }) {
  return (
    <div style={{ padding:'14px 16px 10px', display:'flex', alignItems:'center', gap:12, flexShrink:0 }}>
      <div onClick={onClose} style={{ cursor:'pointer' }}><I name="chevL" size={20} c="#fff" sw={2.2}/></div>
      <div style={{ flex:1, minWidth:0 }}>
        <div style={{ fontSize:10, fontWeight:700, color:'rgba(255,255,255,0.45)', letterSpacing:'0.12em', textTransform:'uppercase' }}>{kicker}</div>
        <div style={{ fontSize:13.5, fontWeight:700, color:'#fff', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{title}</div>
      </div>
      {right}
    </div>
  );
}

// Shown when an asset row exists but carries no usable location. Better than a
// viewer full of placeholder content, which is what these modals used to do.
function ViewerUnavailable({ what }) {
  return (
    <div style={{ flex:1, display:'flex', alignItems:'center', justifyContent:'center', padding:'0 40px' }}>
      <div style={{ textAlign:'center' }}>
        <div style={{
          width:46, height:46, borderRadius:12, background:'rgba(255,255,255,0.08)', margin:'0 auto 14px',
          display:'flex', alignItems:'center', justifyContent:'center',
        }}><I name="doc" size={20} c="rgba(255,255,255,0.5)" sw={2}/></div>
        <div style={{ fontSize:14, fontWeight:700, color:'#fff', marginBottom:6 }}>{what} isn't available</div>
        <div style={{ fontSize:12.5, color:'rgba(255,255,255,0.55)', lineHeight:1.55 }}>
          This item has no file attached yet. Ask your programme administrator to upload it.
        </div>
      </div>
    </div>
  );
}

function ScreenshotShield() {
  return (
    <div style={{
      position:'fixed', inset:0, zIndex:9999,
      background:'#000',
      display:'flex', alignItems:'center', justifyContent:'center',
      flexDirection:'column', gap:12,
    }} className="sc-shield no-screenshot">
      <div style={{ fontSize:32 }}>🔒</div>
      <div style={{ color:'#fff', fontSize:14, fontWeight:700, textAlign:'center', padding:'0 32px' }}>
        This content is protected
      </div>
      <div style={{ color:'rgba(255,255,255,0.5)', fontSize:12, textAlign:'center', padding:'0 40px' }}>
        Screenshots are not permitted for assessments and course materials
      </div>
    </div>
  );
}

// ─── MODAL: Video Player ──────────────────────────────────────
// Real playback. This was previously a simulation: a decorative waveform, a
// `progress` number in state with a click-to-scrub handler, a play button that
// only toggled an icon, and a four-line hardcoded "transcript" that was the
// same regardless of the lesson. Nothing was ever played and nothing recorded.
function ModalVideoPlayer({ lesson, onClose }) {
  const blocked  = useScreenshotShield();
  const videoRef = React.useRef(null);
  const media    = lesson.video || null;
  const [err, setErr] = useS(false);

  const isVimeo = media && (media.source === 'vimeo' || media.vimeo_id);
  // Prefer a progressive URL where one exists: HLS plays natively in Safari /
  // iOS WKWebView but not in Android Chrome without a JS player, and loading
  // one from a CDN is not an option under this app's CSP.
  const src     = media ? (media.url || media.hls_url || '') : '';
  const resumeAt = Number(lesson.last_watched_s || 0);

  // Throttled progress reporting — sc_progress.last_watched_s finally has a
  // writer, so a learner can resume where they stopped.
  const lastSent = React.useRef(0);
  const report = React.useCallback((sec, pct, done) => {
    if (!lesson.id) return;
    saveProgress(lesson.id, done ? 'completed' : 'in_progress', {
      completionPct: Math.round(pct),
      lastWatchedS: Math.round(sec),
    });
  }, [lesson.id]);

  const onTime = (e) => {
    const v = e.currentTarget;
    if (!v.duration || isNaN(v.duration)) return;
    const pct = (v.currentTime / v.duration) * 100;
    // At most one write every 15 s. Uses the absolute delta so that seeking
    // backwards still reports — a signed comparison would go quiet until
    // playback climbed back past the previous mark.
    if (Math.abs(v.currentTime - lastSent.current) >= 15) {
      lastSent.current = v.currentTime;
      report(v.currentTime, pct, false);
    }
  };
  const onEnded = (e) => {
    const v = e.currentTarget;
    report(v.duration || 0, 100, true);
  };
  const onLoaded = (e) => {
    if (resumeAt > 2 && resumeAt < (e.currentTarget.duration || Infinity) - 5) {
      e.currentTarget.currentTime = resumeAt;
      lastSent.current = resumeAt;
    }
  };

  return (
    <div style={{ position:'absolute', inset:0, zIndex:30, background:'#0A0F1C', display:'flex', flexDirection:'column', fontFamily:"'Lato',sans-serif" }}>
      {blocked && <ScreenshotShield/>}
      <ViewerTopBar kicker="Now playing" title={lesson.title} onClose={onClose}
        right={media && media.duration_min
          ? <div style={{ fontSize:10.5, fontWeight:800, color:T2.goldHi, border:'1.5px solid rgba(255,213,96,0.3)', borderRadius:4, padding:'2px 7px' }}>{Math.round(media.duration_min)} min</div>
          : null}/>

      {!media || (!src && !isVimeo) || err ? (
        <ViewerUnavailable what={err ? 'This video' : 'The video'}/>
      ) : isVimeo ? (
        <div style={{ margin:'0 16px', borderRadius:12, overflow:'hidden', background:'#000', position:'relative', paddingTop:'56.25%', flexShrink:0 }}>
          <iframe
            src={`https://player.vimeo.com/video/${media.vimeo_id}`}
            title={lesson.title}
            allow="autoplay; fullscreen; picture-in-picture"
            allowFullScreen
            style={{ position:'absolute', inset:0, width:'100%', height:'100%', border:'none' }}
          />
        </div>
      ) : (
        <div style={{ margin:'0 16px', borderRadius:12, overflow:'hidden', background:'#000', flexShrink:0 }}>
          {/* Native controls on purpose: real scrubbing, volume, fullscreen,
              captions and keyboard access for free, and correct behaviour in
              both WKWebView and Android WebView. */}
          <video
            ref={videoRef}
            src={src}
            poster={media.thumbnail_url || undefined}
            controls
            playsInline
            preload="metadata"
            onLoadedMetadata={onLoaded}
            onTimeUpdate={onTime}
            onEnded={onEnded}
            onError={()=>setErr(true)}
            style={{ width:'100%', maxHeight:280, display:'block', background:'#000' }}
          />
        </div>
      )}

      {/* Overview — the topic's own summary. The "transcript" tab that used to
          sit alongside this was removed: no transcript exists anywhere in the
          schema, and the four lines it showed were hardcoded. */}
      <div style={{ flex:1, overflow:'auto', background:'#fff', borderRadius:'14px 14px 0 0', marginTop:14 }}>
        <div style={{ padding:'14px 16px' }}>
          {resumeAt > 2 && (
            <div style={{ fontSize:11.5, color:T2.inkSoft, marginBottom:10 }}>
              Resumed from {fmtTime(Math.round(resumeAt))}.
            </div>
          )}
          {lesson.desc ? (
            <>
              <div style={{ fontSize:13, fontWeight:700, color:T2.ink, marginBottom:8 }}>About this lesson</div>
              <p style={{ fontSize:12.5, color:T2.inkMid, lineHeight:1.6, margin:0 }}>{lesson.desc}</p>
            </>
          ) : (
            <div style={{ fontSize:12.5, color:T2.inkSoft }}>No description for this lesson.</div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── MODAL: Document viewer (PDF / slides) ────────────────────
// One viewer for both. The two it replaces each rendered a hardcoded deck —
// the PDF always showed four "Situational Leadership" pages and the PPT a
// fixed slide count — regardless of which resource had been opened.
function ModalDocViewer({ resource, onClose }) {
  const blocked = useScreenshotShield();
  const [err, setErr] = useS(false);
  const url  = resource && resource.url;
  const kind = (resource && resource.type) || 'pdf';
  const label = kind === 'ppt' ? 'Slides' : 'Document';

  return (
    <div style={{ position:'absolute', inset:0, zIndex:30, background:'#0A0F1C', display:'flex', flexDirection:'column', fontFamily:"'Lato',sans-serif" }}>
      {blocked && <ScreenshotShield/>}
      <ViewerTopBar kicker={label} title={(resource && resource.t) || label} onClose={onClose}
        right={resource && resource.sz
          ? <div style={{ fontSize:10.5, fontWeight:700, color:'rgba(255,255,255,0.55)' }}>{resource.sz}</div>
          : null}/>

      {!url || err ? (
        <ViewerUnavailable what={err ? 'This file' : `The ${label.toLowerCase()}`}/>
      ) : (
        <>
          <div style={{ flex:1, margin:'0 16px 12px', borderRadius:12, overflow:'hidden', background:'#fff' }}>
            <iframe
              src={url}
              title={(resource && resource.t) || label}
              onError={()=>setErr(true)}
              style={{ width:'100%', height:'100%', border:'none', display:'block' }}
            />
          </div>
          {/* Inline rendering of PDFs and Office files varies by WebView, so
              there is always an explicit way out to the platform handler. */}
          <div style={{ padding:'0 16px 24px', flexShrink:0 }}>
            <button onClick={()=>window.open(url,'_blank')}
              style={{ ...sh.btnGhost, width:'100%', justifyContent:'center', background:'rgba(255,255,255,0.08)', color:'#fff', borderColor:'rgba(255,255,255,0.2)' }}>
              Open in a new tab
            </button>
          </div>
        </>
      )}
    </div>
  );
}

// Both names kept so existing call sites keep working.
const ModalPDFViewer = ModalDocViewer;
const ModalPPTViewer = ModalDocViewer;

// ─── MODAL: Reflection Quiz ────────────────────────────────────
// ─── Learn data layer ─────────────────────────────────────────
// All three Learn screens read from GET /api/participant/learn (courses,
// topics, per-topic progress, media) and GET /api/participant/home (capability
// scores, for the personalised path). Both endpoints shipped long ago with no
// caller at all — this module used to render a hardcoded LESSONS array.
//
// The result is cached on window so navigating Learn → Lesson → Path doesn't
// refetch on every hop; `_scLearnBump` lets a progress write invalidate it.
const MEDIA_KINDS = { video:'Video', ppt:'Slides', pdf:'Reading', audio:'Audio', note:'Note', link:'Link' };

function fmtSize(mb) {
  if (mb == null) return '';
  return mb >= 1 ? `${Number(mb).toFixed(1)} MB` : `${Math.round(Number(mb) * 1024)} KB`;
}

// Turns one API topic + the course's media into the shape these screens render.
function mapTopic(t, idx, media, courseTitle, courseId) {
  const mine  = (media || []).filter(m => m.topic_id === t.id);
  const video = mine.find(m => m.kind === 'video');
  const docs  = mine.filter(m => m.kind === 'pdf' || m.kind === 'ppt');
  return {
    id: t.id, n: idx + 1, courseId, course: courseTitle,
    title: t.title,
    desc:  t.summary || '',
    mins:  t.duration_min || 0,
    done:  t.progress === 'completed',
    inProgress: t.progress === 'in_progress',
    pct:   t.pct || 0,
    last_watched_s: t.last_watched_s || 0,
    type:  video ? MEDIA_KINDS.video : docs.length ? MEDIA_KINDS.pdf : 'Lesson',
    icon:  video ? 'play' : 'doc',
    video: video || null,
    resources: mine
      .filter(m => m.kind !== 'video')
      .map(m => ({
        id: m.id, t: m.title, type: m.kind, url: m.url,
        sz: fmtSize(m.size_mb), source: m.source,
      })),
  };
}

function mapCourse(c) {
  const lessons = (c.topics || []).map((t, i) => mapTopic(t, i, c.media, c.title, c.id));
  const done    = lessons.filter(l => l.done).length;
  return {
    id: c.id, title: c.title, capability: c.capability, description: c.description,
    lessons, done, total: lessons.length,
    pct: lessons.length ? Math.round((done / lessons.length) * 100) : 0,
    mins: lessons.reduce((s, l) => s + (l.mins || 0), 0),
    // Course-level assets — those not attached to any single topic.
    resources: (c.media || [])
      .filter(m => !m.topic_id && m.kind !== 'video')
      .map(m => ({ id:m.id, t:m.title, type:m.kind, url:m.url, sz:fmtSize(m.size_mb), source:m.source })),
  };
}

function useLearnData() {
  const [state, setState] = useS(window._scLearnCache || { status:'loading', courses:[], caps:[] });

  React.useEffect(() => {
    if (window._scLearnCache && !window._scLearnBump) { setState(window._scLearnCache); return; }
    let cancelled = false;
    (async () => {
      if (!window.SRAPI || typeof window.SRAPI._get !== 'function') {
        if (!cancelled) setState({ status:'error', courses:[], caps:[] });
        return;
      }
      try {
        const l = await window.SRAPI._get('/api/participant/learn');
        if (cancelled) return;
        const courses = (l?.modules || []).map(mapCourse);
        let caps = [];
        try {
          const h = await window.SRAPI._get('/api/participant/home');
          caps = h?.capabilities || [];
        } catch (e) { /* path personalisation degrades, list still works */ }
        const next = { status:'ready', courses, caps };
        window._scLearnCache = next;
        window._scLearnBump  = false;
        if (!cancelled) setState(next);
      } catch (err) {
        if (!cancelled) setState({ status:'error', courses:[], caps:[] });
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return state;
}

// Records progress and invalidates the cache so percentages refresh.
async function saveProgress(topicId, status, extra) {
  if (!window.SRAPI || typeof window.SRAPI._post !== 'function') return false;
  try {
    await window.SRAPI._post('/api/participant/progress',
      Object.assign({ topicId, status }, extra || {}));
    window._scLearnBump = true;
    window._scLearnCache = null;
    return true;
  } catch (e) {
    console.warn('[SkaleCap] progress save failed:', e && e.message);
    return false;
  }
}

// Opens a real media asset. Everything here comes from sc_media, so the only
// honest behaviour when there's no url is to say so rather than open a viewer
// full of placeholder content.
function openAsset(res) {
  if (res && res.url) { window.open(res.url, '_blank'); return true; }
  return false;
}

function LearnEmpty({ icon, title, body }) {
  return (
    <div style={{ padding:'40px 32px', textAlign:'center' }}>
      <div style={{
        width:46, height:46, borderRadius:12, background:T2.ruleSoft, margin:'0 auto 14px',
        display:'flex', alignItems:'center', justifyContent:'center',
      }}><I name={icon||'learn'} size={20} c={T2.inkSoft} sw={2}/></div>
      <div style={{ fontSize:14.5, fontWeight:700, color:T2.ink, marginBottom:6 }}>{title}</div>
      <div style={{ fontSize:12.5, color:T2.inkSoft, lineHeight:1.55 }}>{body}</div>
    </div>
  );
}

// ─── SCREEN: Lesson detail ────────────────────────────────────
function ScreenLessonDetail({ onNav, active }) {
  const lesson = window._lessonState?.lesson || null;
  const [modal, setModal]         = useS(null);
  const [done, setDone]           = useS(!!(lesson && lesson.done));
  const [saving, setSaving]       = useS(false);
  const [saveErr, setSaveErr]     = useS(false);
  const [notes, setNotes]         = useS('');
  const [noteSaved, setNoteSaved] = useS(false);

  const saveNote = () => { setNoteSaved(true); setTimeout(()=>setNoteSaved(false), 2000); };
  const sendToCoach = () => {
    if (!notes.trim() || !lesson) return;
    window._aiCoachContext = { note: notes.trim(), lessonTitle: lesson.title };
    onNav && onNav('ai');
  };

  const markDone = async () => {
    if (!lesson || done || saving) return;
    setSaving(true); setSaveErr(false);
    const ok = await saveProgress(lesson.id, 'completed', { completionPct: 100 });
    setSaving(false);
    if (ok) setDone(true); else setSaveErr(true);
  };

  if (!lesson) {
    return (
      <div style={sh.shell}>
        <div style={sh.scroll}>
          <LearnEmpty icon="learn" title="No lesson selected"
            body="Pick a lesson from your module list to open it."/>
        </div>
        <BottomNav active={active||'learn'} role="participant" onNav={onNav}/>
      </div>
    );
  }

  return (
    <div style={sh.shell}>
      {modal?.type==='video'&&<ModalVideoPlayer lesson={lesson} onClose={()=>setModal(null)}/>}
      {modal?.type==='pdf'&&<ModalPDFViewer resource={modal.resource} onClose={()=>setModal(null)}/>}
      {modal?.type==='ppt'&&<ModalPPTViewer resource={modal.resource} onClose={()=>setModal(null)}/>}
      <div style={sh.scroll}>
        <div style={{ background:T2.headerGrad, color:'#fff', padding:'10px 14px 18px' }}>
          <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:14 }}>
            <div onClick={()=>onNav&&onNav('learn')} style={{ cursor:'pointer', flexShrink:0 }}><I name="chevL" size={18} c="#fff" sw={2.2}/></div>
            <div style={{ flex:1, textAlign:'center', fontSize:10, fontWeight:700, color:T2.goldHi, letterSpacing:'0.14em', textTransform:'uppercase' }}>{lesson.course}</div>
            {done?<div style={{ fontSize:9.5, fontWeight:800, color:T2.goldHi, display:'flex', alignItems:'center', gap:3 }}><I name="check" size={11} c={T2.goldHi} sw={2.6}/>Done</div>:<div style={{ width:28 }}/>}
          </div>
          <div style={{ display:'flex', gap:7, marginBottom:10, flexWrap:'wrap' }}>
            <span style={{ fontSize:9.5, fontWeight:700, color:'rgba(255,255,255,0.65)', background:'rgba(255,255,255,0.08)', borderRadius:10, padding:'3px 9px' }}>
              {lesson.type}{lesson.mins ? ` · ${lesson.mins} min` : ''}
            </span>
          </div>
          <div style={{ fontSize:21, fontWeight:800, lineHeight:1.2, letterSpacing:'-0.01em' }}>{lesson.title}</div>
          {lesson.desc && <div style={{ fontSize:12.5, color:'rgba(255,255,255,0.75)', marginTop:8, lineHeight:1.45 }}>{lesson.desc}</div>}
        </div>

        {/* Primary content — shown only when this lesson genuinely has a video
            asset in sc_media. The old screen always rendered a video card. */}
        {lesson.video&&(<div style={{ padding:'16px 16px 0' }}>
          <div onClick={()=>setModal({type:'video'})} style={{ borderRadius:12, overflow:'hidden', background:`linear-gradient(135deg,${T2.navy} 0%,#0D1B2A 100%)`, height:136, position:'relative', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center' }}>
            {lesson.video.thumbnail_url && (
              <img src={lesson.video.thumbnail_url} alt="" style={{ position:'absolute', inset:0, width:'100%', height:'100%', objectFit:'cover', opacity:0.55 }}/>
            )}
            <div style={{ width:50, height:50, borderRadius:'50%', background:'rgba(255,255,255,0.92)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:2, boxShadow:'0 4px 20px rgba(0,0,0,0.35)' }}><I name="play" size={21} c={T2.navy} sw={2}/></div>
            {lesson.video.duration_min ? (
              <div style={{ position:'absolute', bottom:10, right:12, background:'rgba(0,0,0,0.6)', borderRadius:4, padding:'3px 7px', fontSize:11, fontWeight:700, color:'#fff', zIndex:2 }}>{Math.round(lesson.video.duration_min)} min</div>
            ) : null}
          </div>
        </div>)}

        {/* Overview — the topic's own summary. The previous screen rendered
            three paragraphs of invented prose here; sc_topics has a summary
            field and nothing longer, so that is what shows. */}
        {lesson.desc && (
          <Section kicker="Lesson overview" pt={20}>
            <div style={{ padding:'0 16px' }}>
              <p style={{ fontSize:13, color:T2.inkMid, lineHeight:1.65, margin:0 }}>{lesson.desc}</p>
            </div>
          </Section>
        )}

        {/* Resources — real sc_media rows attached to this topic */}
        {lesson.resources?.length>0&&(<Section kicker="Resources & downloads" pt={20}>
          <div style={{ padding:'0 16px', display:'flex', flexDirection:'column', gap:8 }}>
            {lesson.resources.map((res)=>(
              <div key={res.id} onClick={()=>{
                     if (res.type==='pdf'||res.type==='ppt') { setModal({type:res.type,resource:res}); }
                     else { openAsset(res); }
                   }}
                   style={{ padding:'12px 14px', display:'flex', alignItems:'center', gap:12, border:`1px solid ${T2.rule}`, borderRadius:12, cursor:'pointer' }}>
                <div style={{ width:40, height:40, borderRadius:10, flexShrink:0, background:res.type==='pdf'?'#FDECEA':res.type==='ppt'?T2.tealLt:T2.navyLt, display:'flex', alignItems:'center', justifyContent:'center' }}>
                  <I name={res.type==='ppt'?'bar':'doc'} size={17} c={res.type==='pdf'?'#C0392B':res.type==='ppt'?T2.teal:T2.navy} sw={2}/>
                </div>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ fontSize:13, fontWeight:700, color:T2.ink }}>{res.t}</div>
                  <div style={{ fontSize:11, color:T2.inkSoft, marginTop:2 }}>
                    {String(res.type||'').toUpperCase()}{res.sz?` · ${res.sz}`:''}
                  </div>
                </div>
                <I name="chevR" size={14} c={T2.inkFaint} sw={2}/>
              </div>
            ))}
          </div>
        </Section>)}

        {/* Notes — local to the device. There is no notes column on sc_progress
            for a participant, so this is deliberately labelled as private
            rather than implying it syncs anywhere. */}
        <Section kicker="Your notes" pt={20}>
          <div style={{ padding:'0 16px' }}>
            <textarea value={notes} onChange={e=>setNotes(e.target.value)}
              placeholder="Jot something down while it's fresh…"
              style={{ width:'100%', minHeight:84, padding:'11px 12px', borderRadius:10, border:`1px solid ${T2.rule}`, fontSize:13, fontFamily:'inherit', color:T2.ink, resize:'vertical', boxSizing:'border-box' }}/>
            <div style={{ display:'flex', gap:8, marginTop:8 }}>
              <button onClick={saveNote} disabled={!notes.trim()} style={{ ...sh.btnGhost, flex:1, justifyContent:'center', opacity:notes.trim()?1:0.45 }}>
                {noteSaved ? 'Saved on this device' : 'Save note'}
              </button>
              <button onClick={sendToCoach} disabled={!notes.trim()} style={{ ...sh.btnPrimary, flex:1, justifyContent:'center', opacity:notes.trim()?1:0.45 }}>
                Ask the coach
              </button>
            </div>
          </div>
        </Section>

        <div style={{ height:20 }}/>
      </div>

      {/* Mark complete — writes to POST /api/participant/progress */}
      <div style={{ padding:'12px 16px 28px', borderTop:`1px solid ${T2.rule}`, flexShrink:0 }}>
        {saveErr && (
          <div style={{ fontSize:11.5, color:T2.rose, marginBottom:8, textAlign:'center' }}>
            Couldn't save just now — check your connection and try again.
          </div>
        )}
        <button onClick={markDone} disabled={done||saving}
          style={{ ...(done ? sh.btnGhost : sh.btnPrimary), width:'100%', justifyContent:'center', opacity:saving?0.6:1 }}>
          {done
            ? <><I name="check" size={13} c={T2.navy} sw={2.4}/> Completed</>
            : saving ? 'Saving…' : 'Mark as complete'}
        </button>
      </div>
    </div>
  );
}

// ─── SCREEN: Personalised path ────────────────────────────────
function ScreenPersonalisedPath({ onNav, active }) {
  const { status, courses, caps } = useLearnData();

  const allLessons = courses.flatMap(c => c.lessons);
  const remaining  = allLessons.filter(l => !l.done);
  const completed  = allLessons.filter(l => l.done);

  // Real capability scores, weakest first. The old screen hardcoded
  // "Strategic Thinking 2.9 vs cohort 3.7" — a cohort average is not exposed
  // to participants by any endpoint, so only the learner's own score shows.
  const ranked = caps.slice().sort((a,b)=>(Number(a.score)||0)-(Number(b.score)||0)).slice(0,2);

  return (
    <div style={sh.shell}>
      <div style={sh.scroll}>
        <Header kicker="Your learning path" title="What to focus on next"
          sub={<>{completed.length} of {allLessons.length} lessons complete</>}
          onRight={()=>onNav&&onNav('me')}
          right={<I name="chevR" size={18} c="#fff" sw={2}/>}/>

        {status === 'loading' && <LearnEmpty icon="learn" title="Loading…" body="Fetching your learning path."/>}
        {status === 'error'   && <LearnEmpty icon="bolt" title="Couldn't load your path" body="Check your connection and try again."/>}

        {status === 'ready' && allLessons.length === 0 && (
          <LearnEmpty icon="learn" title="Nothing assigned yet"
            body="Once your cohort has courses assigned, your path will appear here."/>
        )}

        {status === 'ready' && ranked.length > 0 && (
          <Section kicker="Your lowest-scoring capabilities" pt={16}>
            <div style={{ padding:'0 16px', display:'flex', flexDirection:'column', gap:8 }}>
              {ranked.map((c,i)=>(
                <div key={c.key||i} style={{ padding:'10px 12px', borderRadius:8, background:i===0?T2.roseFaint:T2.goldLt, display:'flex', alignItems:'center', gap:9 }}>
                  <I name="bolt" size={13} c={i===0?T2.rose:T2.gold} sw={2.2}/>
                  <div style={{ flex:1, fontSize:12, fontWeight:700, color:T2.ink }}>
                    {c.label || c.key}
                    {c.score != null && <span style={{ color:i===0?T2.rose:T2.amber }}> · {Number(c.score).toFixed(2)}/5</span>}
                  </div>
                </div>
              ))}
            </div>
          </Section>
        )}

        {status === 'ready' && remaining.length > 0 && (
          <Section kicker={`Up next · ${remaining.length} remaining`} pt={20}>
            <div style={{ padding:'0 16px', display:'flex', flexDirection:'column', gap:8 }}>
              {remaining.slice(0,8).map((l)=>(
                <div key={l.id} onClick={()=>{ window._lessonState={lesson:l}; onNav&&onNav('lesson'); }}
                     style={{ padding:'12px 14px', borderRadius:12, border:`1px solid ${T2.rule}`, background:'#fff', cursor:'pointer', display:'flex', alignItems:'center', gap:12 }}>
                  <div style={{ width:38, height:38, borderRadius:10, flexShrink:0, background:T2.navyLt, display:'flex', alignItems:'center', justifyContent:'center' }}>
                    <I name={l.icon} size={16} c={T2.navy} sw={2}/>
                  </div>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ fontSize:13, fontWeight:700, color:T2.ink }}>{l.title}</div>
                    <div style={{ fontSize:11, color:T2.inkSoft, marginTop:2 }}>
                      {l.course} · {l.type}{l.mins?` · ${l.mins} min`:''}
                      {l.inProgress && <span style={{ marginLeft:6, color:T2.gold, fontWeight:800 }}>In progress</span>}
                    </div>
                  </div>
                  <I name="chevR" size={13} c={T2.inkFaint} sw={2}/>
                </div>
              ))}
            </div>
          </Section>
        )}

        {status === 'ready' && completed.length > 0 && (
          <Section kicker="Completed" pt={20}>
            <div style={{ padding:'0 16px', display:'flex', flexDirection:'column', gap:8 }}>
              {completed.map((l)=>(
                <div key={l.id} style={{ padding:'11px 14px', borderRadius:10, border:`1px solid ${T2.sage}22`, background:T2.sageLt, display:'flex', alignItems:'center', gap:12, opacity:0.85 }}>
                  <I name="check" size={16} c={T2.sage} sw={2.2}/>
                  <div style={{ flex:1 }}>
                    <div style={{ fontSize:12.5, fontWeight:700, color:T2.ink }}>{l.title}</div>
                    <div style={{ fontSize:10.5, color:T2.inkSoft, marginTop:1 }}>{l.course}</div>
                  </div>
                </div>
              ))}
            </div>
          </Section>
        )}

        <div style={{ height:24 }}/>
      </div>
      <BottomNav active="learn" role="participant" onNav={onNav}/>
    </div>
  );
}

// ─── SCREEN: Learn ────────────────────────────────────────────
function ScreenLearnV2({ onNav, active='learn' }) {
  const { status, courses } = useLearnData();
  const [view, setView]     = useS('modules');
  const [courseIdx, setCourseIdx] = useS(0);

  // Default to the first course that still has unfinished lessons.
  React.useEffect(() => {
    if (status !== 'ready' || !courses.length) return;
    const i = courses.findIndex(c => c.done < c.total);
    setCourseIdx(i >= 0 ? i : 0);
  }, [status, courses.length]);

  const course = courses[courseIdx] || null;

  if (status === 'loading' || status === 'error' || !course) {
    return (
      <div style={sh.shell}>
        <div style={sh.scroll}>
          {status === 'loading'
            ? <LearnEmpty icon="learn" title="Loading…" body="Fetching your modules."/>
            : status === 'error'
              ? <LearnEmpty icon="bolt" title="Couldn't load your modules" body="Check your connection and try again."/>
              : <LearnEmpty icon="learn" title="No modules assigned yet"
                  body="Once your cohort has courses assigned, they'll appear here."/>}
        </div>
        <BottomNav active={active} role="participant" onNav={onNav}/>
      </div>
    );
  }

  return (
    <div style={sh.shell}>
      <div style={sh.scroll}>
        <div style={{ padding:'10px 16px', display:'flex', alignItems:'center', gap:10, borderBottom:`1px solid ${T2.rule}` }}>
          <div onClick={()=>onNav&&onNav('home')} style={{ cursor:'pointer' }}><I name="chevL" size={20} c={T2.ink} sw={2.2}/></div>
          <div style={{ flex:1, fontSize:13, fontWeight:700, color:T2.ink, textAlign:'center' }}>{course.title}</div>
          <div style={{ width:20, flexShrink:0 }}/>
        </div>

        <div style={{ background:T2.headerGrad, color:'#fff', padding:'16px' }}>
          <Label c={T2.goldHi} mb={8}>
            {courses.length > 1 ? `Module ${courseIdx+1} of ${courses.length}` : 'Your module'}
          </Label>
          <div style={{ fontSize:22, fontWeight:700, lineHeight:1.15, letterSpacing:'-0.01em' }}>{course.title}</div>
          <div style={{ fontSize:11.5, color:'rgba(255,255,255,0.85)', marginTop:6 }}>
            {course.total} {course.total===1?'lesson':'lessons'}
            {course.mins ? ` · ~${course.mins >= 60 ? (course.mins/60).toFixed(1)+' hrs' : course.mins+' min'} total` : ''}
          </div>
          <div style={{ marginTop:14, display:'flex', alignItems:'center', gap:10 }}>
            <div style={{ flex:1 }}><Bar pct={course.pct} c={T2.goldHi} bg="rgba(255,255,255,0.18)" h={4}/></div>
            <span style={{ fontSize:13, fontWeight:800, color:T2.goldHi }}>{course.done} of {course.total}</span>
          </div>
        </div>

        {/* Course switcher — only when the batch actually has more than one */}
        {courses.length > 1 && (
          <div style={{ display:'flex', gap:6, padding:'10px 16px 0', overflowX:'auto' }}>
            {courses.map((c,i)=>(
              <div key={c.id} onClick={()=>setCourseIdx(i)} style={{
                flexShrink:0, padding:'6px 12px', borderRadius:16, cursor:'pointer',
                fontSize:11.5, fontWeight:i===courseIdx?800:600,
                background:i===courseIdx?T2.navy:T2.ruleSoft,
                color:i===courseIdx?'#fff':T2.inkSoft,
              }}>{c.title}</div>
            ))}
          </div>
        )}

        {/* Two tabs. The third — a self-contained 20-question "Assess" quiz —
            was removed: its questions, answers and countdown timer were all
            hardcoded (the clock literally used Math.random() for its seconds),
            and it duplicated ScreenParticipantAssess, which is wired to the
            real /participant/assessments endpoints. The tab now routes there. */}
        <div style={{ display:'flex', borderBottom:`1px solid ${T2.rule}`, background:'#fff', position:'sticky', top:0, zIndex:4 }}>
          {[{id:'modules',label:'Lessons'},{id:'path',label:'My Path'},{id:'assess',label:'Assess'}].map(t=>(
            <div key={t.id}
              onClick={()=>{ if(t.id==='assess'){ onNav&&onNav('assess'); } else { setView(t.id); } }}
              style={{ flex:1, padding:'12px 0', textAlign:'center', cursor:'pointer', borderBottom:view===t.id?`2px solid ${T2.navy}`:'2px solid transparent', color:view===t.id?T2.navy:T2.inkSoft, fontSize:12, fontWeight:view===t.id?800:600 }}>{t.label}</div>
          ))}
        </div>

        {view==='modules'&&(<>
          <div style={{ padding:'16px' }}>
            {course.lessons.length === 0 ? (
              <LearnEmpty icon="learn" title="No lessons yet"
                body="This module doesn't have any lessons published yet."/>
            ) : (
              <div style={{ border:`1px solid ${T2.rule}`, borderRadius:12, overflow:'hidden', background:T2.paper }}>
                {course.lessons.map((l,i)=>{
                  const isNext = !l.done && course.lessons.slice(0,i).every(p=>p.done);
                  return (
                    <div key={l.id} onClick={()=>{ window._lessonState={lesson:l}; onNav&&onNav('lesson'); }}
                      style={{ display:'flex', alignItems:'center', gap:12, padding:'12px 14px', background:isNext?T2.navyLt:'transparent', borderBottom:i<course.lessons.length-1?`1px solid ${T2.ruleSoft}`:'none', cursor:'pointer' }}>
                      <div style={{ width:24, height:24, borderRadius:'50%', flexShrink:0, background:l.done?T2.navy:'transparent', border:`2px solid ${l.done||isNext?T2.navy:T2.rule}`, display:'flex', alignItems:'center', justifyContent:'center', fontSize:10.5, fontWeight:800, color:l.done?'#fff':isNext?T2.navy:T2.inkFaint }}>
                        {l.done?<I name="check" size={12} c="#fff" sw={2.6}/>:l.n}
                      </div>
                      <div style={{ flex:1, minWidth:0 }}>
                        <div style={{ fontSize:13.5, fontWeight:isNext?800:600, lineHeight:1.3, color:l.done?T2.inkSoft:T2.ink, textDecoration:l.done?'line-through':'none', textDecorationColor:T2.inkFaint }}>{l.title}</div>
                        <div style={{ fontSize:10.5, color:T2.inkSoft, marginTop:2 }}>
                          {l.type}{l.mins?` · ${l.mins} min`:''}
                          {l.inProgress&&<span style={{ marginLeft:6, color:T2.gold, fontWeight:800 }}>{l.pct}% done</span>}
                        </div>
                      </div>
                      {isNext&&<span style={{ fontSize:9.5, fontWeight:800, color:T2.gold, letterSpacing:'0.1em', textTransform:'uppercase' }}>Up next</span>}
                      {!isNext&&!l.done&&<I name="chevR" size={13} c={T2.inkFaint} sw={2}/>}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {course.resources.length > 0 && (
            <Section kicker="Module resources" pt={6}>
              <div style={{ padding:'0 16px', display:'flex', flexDirection:'column', gap:6 }}>
                {course.resources.map((r)=>(
                  <div key={r.id} onClick={()=>openAsset(r)} style={{ padding:'10px 12px', display:'flex', alignItems:'center', gap:12, border:`1px solid ${T2.rule}`, borderRadius:10, cursor:r.url?'pointer':'default', opacity:r.url?1:0.5 }}>
                    <I name="doc" size={16} c={T2.inkSoft} sw={1.8}/>
                    <div style={{ flex:1 }}>
                      <div style={{ fontSize:12.5, fontWeight:700, color:T2.ink }}>{r.t}</div>
                      <div style={{ fontSize:10.5, color:T2.inkSoft, marginTop:1 }}>
                        {r.sz}{r.sz&&r.type?' · ':''}{String(r.type||'').toUpperCase()}
                      </div>
                    </div>
                    {r.url && <I name="chevR" size={14} c={T2.navy} sw={2}/>}
                  </div>
                ))}
              </div>
            </Section>
          )}
        </>)}

        {view==='path'&&(<div style={{ padding:'16px' }}>
          <div style={{ padding:'14px', borderRadius:12, background:T2.goldLt, border:`1px solid ${T2.gold}33`, marginBottom:14 }}>
            <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:6 }}>
              <I name="sparkle" size={14} c={T2.gold} sw={2}/>
              <div style={{ fontSize:13, fontWeight:800, color:T2.ink }}>What's left in this module</div>
            </div>
            <div style={{ fontSize:12, color:T2.inkMid, lineHeight:1.5 }}>
              {course.total - course.done} of {course.total} lessons still to go.
            </div>
          </div>
          {course.lessons.filter(l=>!l.done).map((l)=>(
            <div key={l.id} onClick={()=>{ window._lessonState={lesson:l}; onNav&&onNav('lesson'); }} style={{ marginBottom:10, padding:'13px 14px', borderRadius:12, cursor:'pointer', border:`1px solid ${T2.rule}`, background:'#fff', display:'flex', alignItems:'center', gap:12 }}>
              <div style={{ width:42, height:42, borderRadius:10, flexShrink:0, background:T2.navyLt, display:'flex', alignItems:'center', justifyContent:'center' }}><I name={l.icon} size={18} c={T2.navy} sw={2}/></div>
              <div style={{ flex:1, minWidth:0 }}>
                <div style={{ fontSize:13, fontWeight:700, color:T2.ink }}>{l.title}</div>
                <div style={{ fontSize:11, color:T2.inkSoft, marginTop:2 }}>{l.type}{l.mins?` · ${l.mins} min`:''}</div>
              </div>
              <I name="chevR" size={13} c={T2.inkFaint} sw={2}/>
            </div>
          ))}
          <button onClick={()=>onNav&&onNav('personalised')} style={{ ...sh.btnGhost, width:'100%', marginTop:4, justifyContent:'center' }}>
            View full learning path <I name="chevR" size={13} c={T2.navy} sw={2}/>
          </button>
        </div>)}

        <div style={{ height:24 }}/>
      </div>
      <BottomNav active={active} role="participant" onNav={onNav}/>
    </div>
  );
}

Object.assign(window, { ScreenLessonDetail, ScreenPersonalisedPath, ScreenLearnV2 });
})();



// ===== 24-inline-03.jsx =====
// SkaleCap v2 — side-by-side iOS + Android with role toggle
// Tap bottom-nav inside the phones to navigate between screens.

const { useState, useEffect } = React;

// ─────────────────────────────────────────────────────────────
// Minimal iOS bezel — only chrome, content fills inside
// ─────────────────────────────────────────────────────────────

// ─── SkaleCapApp wrapper (no bezel; SkaleRealty shell provides device chrome) ───

const PARTICIPANT_SCREENS = {
  home:      {label:'Home',         Comp: ScreenParticipantHome,     tab:'home'},
  learn:     {label:'Learn',        Comp: window.ScreenLearnV2 || ScreenParticipantLearn, tab:'learn'},
  community: {label:'Community',    Comp: window.ScreenCommunity,           tab:'community'},
  growth:    {label:'Growth',       Comp: ScreenParticipantGrowth,   tab:'growth'},
  me:        {label:'Me',           Comp: ScreenParticipantMe,       tab:'me',        sub:true},
  ai:        {label:'AI Coach',     Comp: (p) => React.createElement(window.ScreenAICoach, {...p, role:'participant'}), tab:'ai'},
  // Sub-screens (reachable from inside tabs; not in bottom nav)
  assess:    {label:'· Assess',     Comp: ScreenParticipantAssess,   tab:'learn',     sub:true},
  sessions:  {label:'· Sessions',   Comp: window.ScreenParticipantSessions, tab:'home',      sub:true},
  tasks:     {label:'· Tasks',      Comp: window.ScreenParticipantTasks,    tab:'home',      sub:true},
  edit:      {label:'· Edit',       Comp: window.ScreenEditProfile,         tab:'me',        sub:true},
  avatar:    {label:'· Avatar',     Comp: window.ScreenAvatarPicker,        tab:'me',        sub:true},
  lesson:    {label:'· Lesson',     Comp: window.ScreenLessonDetail,        tab:'learn',     sub:true},
  personalised:{label:'· My Path', Comp: window.ScreenPersonalisedPath,    tab:'learn',     sub:true},
};

const LEADER_SCREENS = {
  home:      {label:'Overview',    Comp: ScreenLeaderOverview},
  team:      {label:'Cohort',      Comp: ScreenLeaderCohort},
  member:    {label:'Member',      Comp: ScreenLeaderMember},
  sessions:  {label:'Sessions',    Comp: ScreenLeaderSessions},
  analytics: {label:'Analytics',   Comp: ScreenLeaderAnalytics},
  me:        {label:'Me',          Comp: ScreenLeaderMe,            sub:true},
  ai:        {label:'AI Coach',    Comp: (p) => React.createElement(window.ScreenAICoach, {...p, role:'leader'})},
};


function SkaleCapApp({ role: outerRole, platform }) {
  // Map outer 'role' to internal participant/leader. Anything explicitly
  // 'l&d', 'ld', 'hr', 'leader', 'admin' → leader. Everyone else → participant.
  const normalized = (() => {
    const r = (outerRole || '').toString().toLowerCase();
    if (/^(l&d|ld|hr|leader|admin|trainer|manager)$/.test(r) || r.includes('l&d') || r.includes('manager') || r.includes('trainer') || /(^|[^a-z])hr([^a-z]|$)/.test(r)) return 'leader';
    return 'participant';
  })();
  const [role, setRole] = useState(normalized);
  React.useEffect(() => { setRole(normalized); }, [normalized]);

  // Skip login for embedded use — user is already authenticated in SkaleRealty.
  const [authed, setAuthed] = useState(true);
  // Native wrapper's tabs each load a fixed URL path (see RootTabs.tsx) —
  // map it to the matching initial screen, role-aware since participant
  // and leader have completely different tab sets sharing the same paths
  // conceptually (e.g. leader's "Sessions" vs a path a participant tab
  // would never load).
  const initialScreenFromPath = () => {
    const path = (typeof window !== 'undefined' && window.location && window.location.pathname) || '/';
    const participantMap = { '/': 'home', '/learn': 'learn', '/community': 'community', '/growth': 'growth', '/ai': 'ai' };
    const leaderMap = { '/': 'home', '/cohort': 'team', '/sessions': 'sessions', '/analytics': 'analytics', '/ai': 'ai' };
    const map = normalized === 'leader' ? leaderMap : participantMap;
    return map[path] || 'home';
  };
  const [pScreen, setPScreen] = useState(normalized === 'participant' ? initialScreenFromPath : 'home');
  const [lScreen, setLScreen] = useState(normalized === 'leader' ? initialScreenFromPath : 'home');
  const [lessonKey, setLessonKey] = useState(0);

  const screen = role === 'participant' ? pScreen : lScreen;
  const setScreen = (id) => {
    // SkaleCap has no session of its own here — it's embedded inside the
    // SkaleRealty shell and reuses that session (see comment above). Sign-out
    // dispatches the same event the outer app's own sign-out buttons use.
    if (id === 'logout') { document.dispatchEvent(new CustomEvent('sr:logout')); return; }
    if (role === 'leader' && id === 'team' && lScreen === 'team') {
      setLScreen('member'); return;
    }
    if (id === 'lesson') setLessonKey(k => k + 1);
    if (role === 'participant') setPScreen(id);
    else setLScreen(id);
  };

  const map = role === 'participant' ? PARTICIPANT_SCREENS : LEADER_SCREENS;
  const entry = map[screen] || Object.values(map)[0];
  const Comp = entry.Comp;
  const navActive = entry.tab || screen;

  // Top safe-area padding for platform chrome
  const topPad = platform === 'ios' ? 0 : 0; // screens handle their own padding

  return (
    <div style={{
      height: '100%', width: '100%',
      background: '#FAFAF7',
      fontFamily: "'Lato', -apple-system, system-ui, sans-serif",
      color: '#13131F',
      paddingTop: topPad,
      overflow: 'hidden',
      display: 'flex', flexDirection: 'column',
    }}>
      <div style={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
        <Comp
          key={screen === 'lesson' ? `lesson-${lessonKey}` : screen}
          active={navActive}
          onNav={setScreen}
        />
      </div>
    </div>
  );
}

Object.assign(window, { SkaleCapApp });



// ===== 24a-pf-credits.jsx =====
// ─────────────────────────────────────────────────────────────
// VOICE CREDITS — read-only wallet view for admins/owners.
//
// Shows the prepaid AI-calling balance, auto-pay status, recent credit
// activity and payments (GET /api/billing/credits). Buying credits and
// changing auto-pay stay on work.skaledesk.com/billing on purpose: the GST
// split, billing details and Razorpay mandate flow already live there, the
// iOS app must not sell digital credits through its own checkout (App Store
// review guideline 3.1.1), and UPI intent flows are unreliable inside a
// WebView. "Add credits" therefore hands off to the web app.
//
// Invoices / receipts / credit notes open the same way: the app asks the API
// for a 5-minute link to ONE document (POST /api/billing/files/link) and hands
// it to the phone's browser, because an authenticated blob can't be saved
// reliably from a WebView.
//
// This file is identical in src/ios and src/android (the two trees share these
// UI primitives); keep them in sync. Its name sorts it just before the app
// shell (25-/26-pf-app.jsx) so the screen exists when the router renders it.
// ─────────────────────────────────────────────────────────────

const PF_BILLING_URL = 'https://work.skaledesk.com/billing';

// Same roles the backend lets buy credits (routes/billing-credits.js
// adminOnly = admin, business_owner, superadmin). The server is the real
// boundary — this only hides the entry point from people who couldn't use it.
const canManageCredits = () => {
  const role = (window.SRAPI && window.SRAPI.role) || (typeof localStorage !== 'undefined' && localStorage.getItem('sr_role')) || '';
  return ['admin', 'business_owner', 'superadmin'].includes(String(role).toLowerCase().replace(/\s+/g, '_'));
};

const pfInr = (paise, decimals) => {
  const v = (Number(paise) || 0) / 100;
  const d = decimals === false ? 0 : 2;
  return (v < 0 ? '−' : '') + '₹' + Math.abs(v).toLocaleString('en-IN', { minimumFractionDigits: d, maximumFractionDigits: d });
};
const pfCredits = (paise) => {
  const v = (Number(paise) || 0) / 100;
  return (v < 0 ? '−' : '') + Math.abs(v).toLocaleString('en-IN', { maximumFractionDigits: 2 });
};
const pfMinutesLeft = (w) => (w && w.ratePaisePerMin > 0 && w.totalPaise > 0 ? Math.floor(w.totalPaise / w.ratePaisePerMin) : 0);
const pfShortDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
};
// Same threshold the backend alerts at (services/credits.js LOW_BALANCE_MINUTES)
const PF_LOW_BALANCE_MINUTES = 10;

/**
 * Open a web page outside the app.
 * - Browser/PWA: a new tab.
 * - Native app: only when the installed build advertises it can hand the link
 *   to the OS (window.nativeExternalLinks, injected by skaledesk-native). An
 *   older build would navigate the WebView itself to the page, with no way
 *   back — so there we copy the link instead and say so.
 * @returns {'opened'|'copied'|'failed'}
 */
const PF_openWeb = (url) => {
  if (window.isNativeApp === true && window.nativeExternalLinks !== true) {
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) { navigator.clipboard.writeText(url); return 'copied'; }
    } catch (e) { /* fall through */ }
    return 'failed';
  }
  window.open(url, '_blank');
  return 'opened';
};

const PFScreenCredits = ({ go }) => {
  const [data, setData] = React.useState(null);   // null = loading
  const [error, setError] = React.useState('');
  const [toast, setToast] = React.useState(null);
  const [docBusy, setDocBusy] = React.useState(null); // "kind:id" while a link is being issued
  const showToast = (m) => { setToast(m); setTimeout(() => setToast(null), 2600); };

  const load = React.useCallback(() => {
    setError('');
    if (!window.SRAPI || typeof window.SRAPI.getBillingCredits !== 'function') { setData(false); return; }
    window.SRAPI.getBillingCredits()
      .then((r) => { if (r && r.wallet) setData(r); else setData(false); })
      .catch((e) => { setError((e && e.message) || 'Could not load voice credits'); setData(false); });
  }, []);
  React.useEffect(() => { load(); }, [load]);

  const addCredits = () => {
    const res = PF_openWeb(PF_BILLING_URL);
    if (res === 'copied') showToast('Link copied — open it in your browser to add credits');
    else if (res === 'failed') showToast('Open work.skaledesk.com/billing in your browser to add credits');
  };

  const openDoc = async (kind, id) => {
    if (docBusy) return;
    setDocBusy(kind + ':' + id);
    try {
      const r = await window.SRAPI.createBillingFileLink(kind, id);
      const res = PF_openWeb(r.url);
      if (res === 'copied') showToast('Link copied — paste it in your browser within 5 minutes');
      else if (res === 'failed') showToast('Could not open the document — try from work.skaledesk.com/billing');
    } catch (e) {
      showToast((e && e.message) || 'Could not open the document');
    } finally {
      setDocBusy(null);
    }
  };

  const w = data && data.wallet;
  const minutes = pfMinutesLeft(w);
  const low = !!w && w.totalPaise < w.ratePaisePerMin * PF_LOW_BALANCE_MINUTES;
  const empty = !!w && w.totalPaise <= 0;
  const freePct = w && w.monthlyGrantPaise > 0 ? Math.max(0, Math.min(100, Math.round((w.freePaise / w.monthlyGrantPaise) * 100))) : 0;
  const autopay = data && data.autopay;
  const ledger = (data && data.ledger) || [];
  const charges = ((data && data.charges) || []).filter((c) => c.status !== 'abandoned').slice(0, 6);
  const refunds = ((data && data.creditNotes) || []).filter((n) => n.status === 'issued').slice(0, 3);
  const docBtn = (kind, id, label) => (
    <button key={kind} className="pf-btn pf-btn-light pf-btn-sm" disabled={!!docBusy}
      style={{ padding: '3px 9px', fontSize: 11 }} onClick={() => openDoc(kind, id)}>
      {docBusy === kind + ':' + id ? 'Opening…' : label}
    </button>
  );
  const KIND = { topup: 'Credit top-up', auto_topup: 'Auto top-up', plan_renewal: 'Plan renewal', plan_change: 'Plan change' };
  const STATUS_COLOR = { paid: '#15803d', pending: '#b45309', created: 'var(--pf-ink-3)', failed: '#b91c1c' };

  return (
    <div className="pf-screen pf-anim-in">
      <PFHeader left={<PFBackBtn onClick={() => go('home')} />} title="Voice credits" subtitle="AI calling balance" />
      <div className="pf-scroll" style={{ padding: '0 16px 24px' }}>
        {data === null ? (
          <div style={{ fontSize: 12.5, color: 'var(--pf-ink-3)', padding: '20px 0' }}>Loading…</div>
        ) : data === false ? (
          <div style={{ padding: '20px 0', textAlign: 'center' }}>
            <div style={{ fontSize: 12.5, color: error ? '#b91c1c' : 'var(--pf-ink-3)', marginBottom: 10 }}>{error || 'Voice credits are not available for this account.'}</div>
            <button className="pf-btn pf-btn-light pf-btn-sm" onClick={load}>Try again</button>
          </div>
        ) : (
          <>
            {low && (
              <div style={{
                marginBottom: 12, padding: '10px 14px', borderRadius: 12, fontSize: 12.5, lineHeight: 1.45,
                background: empty ? '#fef2f2' : '#fffbeb', border: `1px solid ${empty ? '#fecaca' : '#fde68a'}`, color: empty ? '#991b1b' : '#92400e',
              }}>
                <strong>{empty ? 'Out of voice credits.' : 'Running low.'}</strong>{' '}
                {empty
                  ? (w.enforcement === 'block' ? 'AI calls are paused until credits are added.' : 'Calls still go through and are settled from your next top-up.')
                  : `About ${minutes} minute${minutes === 1 ? '' : 's'} of AI calling left.`}
              </div>
            )}

            <div className="pf-card" style={{ padding: 16, marginBottom: 12 }}>
              <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8, color: 'var(--pf-ink-3)' }}>Balance</div>
              <div style={{ fontSize: 32, fontWeight: 700, marginTop: 4, color: w.totalPaise < 0 ? '#b91c1c' : 'var(--pf-ink)', fontVariantNumeric: 'tabular-nums' }}>{pfInr(w.totalPaise)}</div>
              <div style={{ fontSize: 12.5, color: 'var(--pf-ink-3)', marginTop: 2 }}>
                About {minutes.toLocaleString('en-IN')} min of AI calling at {pfInr(w.ratePaisePerMin)}/min
              </div>
              {w.monthlyGrantPaise > 0 && (
                <div style={{ marginTop: 14 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11.5, color: 'var(--pf-ink-3)' }}>
                    <span>Free this month</span>
                    <span style={{ fontVariantNumeric: 'tabular-nums' }}>{pfCredits(w.freePaise)} of {pfCredits(w.monthlyGrantPaise)} left</span>
                  </div>
                  <div style={{ height: 6, borderRadius: 3, background: 'var(--pf-line-2)', marginTop: 6, overflow: 'hidden' }}>
                    <div style={{ width: `${freePct}%`, height: '100%', background: 'var(--pf-green)' }} />
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--pf-ink-4)', marginTop: 6 }}>Free credits are used first and reset on the 1st. Paid credits never expire.</div>
                </div>
              )}
              <button className="pf-btn pf-btn-block" style={{ marginTop: 14 }} onClick={addCredits}>Add credits on the web</button>
            </div>

            {autopay && (
              <div className="pf-card" style={{ padding: '12px 14px', marginBottom: 12, display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13.5, fontWeight: 600 }}>Auto-pay {autopay.enabled ? 'is on' : 'is off'}</div>
                  <div style={{ fontSize: 12, color: 'var(--pf-ink-3)', marginTop: 2, lineHeight: 1.4 }}>
                    {autopay.enabled && autopay.mandate
                      ? `${autopay.mandate.label} · next: ${autopay.nextRenewal.planLabel} plan ${pfInr(autopay.nextRenewal.amountPaise, false)} on ${pfShortDate(autopay.nextRenewal.at)}`
                      : autopay.mandate && autopay.mandate.status === 'pending'
                        ? 'Your bank is confirming the e-mandate (1–2 working days).'
                        : 'Turn it on from the web app so renewals and top-ups never need chasing.'}
                  </div>
                  {w.autoTopup && w.autoTopup.enabled && (
                    <div style={{ fontSize: 12, color: 'var(--pf-ink-3)', marginTop: 4 }}>
                      Auto top-up: adds {pfInr(w.autoTopup.amountPaise, false)} below {pfInr(w.autoTopup.thresholdPaise, false)}
                    </div>
                  )}
                </div>
              </div>
            )}

            <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8, color: 'var(--pf-ink-3)', margin: '16px 4px 8px' }}>Recent activity</div>
            {ledger.length === 0 ? (
              <div style={{ fontSize: 12.5, color: 'var(--pf-ink-3)', padding: '8px 4px' }}>No credit activity yet.</div>
            ) : (
              <div className="pf-card" style={{ padding: 0, overflow: 'hidden' }}>
                {ledger.slice(0, 12).map((e, i) => (
                  <div key={e.id} className="pf-row" style={{ padding: '10px 14px', borderTop: i ? '1px solid var(--pf-line-2)' : 0, alignItems: 'center' }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{e.description || e.type}</div>
                      <div style={{ fontSize: 11, color: 'var(--pf-ink-4)', marginTop: 1 }}>{pfShortDate(e.createdAt)}</div>
                    </div>
                    <div style={{ textAlign: 'right', flexShrink: 0, fontVariantNumeric: 'tabular-nums' }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: e.amountPaise >= 0 ? '#15803d' : 'var(--pf-ink)' }}>{e.amountPaise >= 0 ? '+' : ''}{pfCredits(e.amountPaise)}</div>
                      {e.balanceAfterPaise != null && <div style={{ fontSize: 10.5, color: 'var(--pf-ink-4)' }}>bal {pfCredits(e.balanceAfterPaise)}</div>}
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8, color: 'var(--pf-ink-3)', margin: '16px 4px 8px' }}>Payments</div>
            {charges.length === 0 && refunds.length === 0 ? (
              <div style={{ fontSize: 12.5, color: 'var(--pf-ink-3)', padding: '8px 4px' }}>No payments yet.</div>
            ) : (
              <div className="pf-card" style={{ padding: 0, overflow: 'hidden' }}>
                {refunds.map((n, i) => (
                  <div key={n.id} className="pf-row" style={{ padding: '10px 14px', borderTop: i ? '1px solid var(--pf-line-2)' : 0, alignItems: 'center' }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13 }}>Refund</div>
                      <div style={{ fontSize: 11, color: '#15803d', marginTop: 1 }}>refunded · {pfShortDate(n.issuedAt || n.createdAt)}</div>
                      <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>{docBtn('credit_note', n.id, 'Credit note')}</div>
                    </div>
                    <div style={{ fontSize: 13, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>−{pfInr(n.totalPaise)}</div>
                  </div>
                ))}
                {charges.map((c, i) => (
                  <div key={c.id} className="pf-row" style={{ padding: '10px 14px', borderTop: (i || refunds.length) ? '1px solid var(--pf-line-2)' : 0, alignItems: 'center' }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13 }}>{KIND[c.kind] || 'Payment'}</div>
                      <div style={{ fontSize: 11, color: STATUS_COLOR[c.status] || 'var(--pf-ink-4)', marginTop: 1 }}>
                        {(c.status === 'created' ? 'open' : c.status)} · {pfShortDate(c.createdAt)}
                      </div>
                      {c.invoiceId && (
                        <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
                          {docBtn('invoice', c.invoiceId, 'Invoice')}
                          {c.status === 'paid' && docBtn('receipt', c.invoiceId, 'Receipt')}
                        </div>
                      )}
                    </div>
                    <div style={{ fontSize: 13, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{pfInr(c.totalPaise)}</div>
                  </div>
                ))}
              </div>
            )}
            <div style={{ fontSize: 11, color: 'var(--pf-ink-4)', margin: '12px 4px 0', lineHeight: 1.5 }}>
              Billing details and plan changes are on work.skaledesk.com/billing.
            </div>
          </>
        )}

        {toast && (
          <div style={{
            position: 'fixed', bottom: 90, left: '50%', transform: 'translateX(-50%)', maxWidth: '86%', textAlign: 'center',
            background: 'var(--pf-ink)', color: '#fff', padding: '10px 18px', borderRadius: 18,
            fontSize: 13, fontWeight: 500, zIndex: 100, boxShadow: '0 8px 24px rgba(0,0,0,0.25)',
          }}>{toast}</div>
        )}
      </div>
    </div>
  );
};

/**
 * Home-screen nudge for admins/owners: only renders when the balance is under
 * ~10 minutes (or empty), so a healthy wallet adds no clutter. One request on
 * mount; failures are silent (a banner is never worth an error).
 */
const PFCreditsBanner = ({ go }) => {
  const [w, setW] = React.useState(null);
  React.useEffect(() => {
    if (!canManageCredits() || !window.SRAPI || typeof window.SRAPI.getBillingCredits !== 'function') return undefined;
    let cancelled = false;
    window.SRAPI.getBillingCredits().then((r) => { if (!cancelled && r && r.wallet) setW(r.wallet); }).catch(() => {});
    return () => { cancelled = true; };
  }, []);
  if (!w || w.enforcement === 'off' || w.totalPaise >= w.ratePaisePerMin * PF_LOW_BALANCE_MINUTES) return null;
  const empty = w.totalPaise <= 0;
  const minutes = pfMinutesLeft(w);
  return (
    <button onClick={() => go('credits')} style={{
      display: 'block', width: '100%', margin: '0 0 12px', textAlign: 'left', cursor: 'pointer', fontFamily: 'inherit',
      padding: '10px 14px', borderRadius: 12, fontSize: 12.5, lineHeight: 1.45,
      background: empty ? '#fef2f2' : '#fffbeb', border: `1px solid ${empty ? '#fecaca' : '#fde68a'}`, color: empty ? '#991b1b' : '#92400e',
    }}>
      <strong>{empty ? 'Out of voice credits' : 'Voice credits running low'}</strong>
      {' — '}{empty ? 'tap to see how to add more' : `about ${minutes} min of AI calling left · tap to view`}
    </button>
  );
};

Object.assign(window, { PFScreenCredits, PFCreditsBanner, canManageCredits, PF_openWeb });

// ===== 25-pf-app.jsx =====
/* global React, ReactDOM, IOSDevice, AndroidDevice, PFTabBar, TweaksPanel, useTweaks, TweakText */
/* global PFScreenHome, PFScreenLeads, PFScreenLead, PFScreenCallActive, PFScreenDisposition, PFScreenCalls */
/* global PFScreenTasks, PFScreenTeamTasks, PFScreenTaskDetail, PFScreenEvidence, PFScreenProfiling, PFScreenLearn, PFScreenChat, PFScreenAssets, PF_DATA */

// True when this page is loaded inside SkaleDesk-Native's WebView (the
// native wrapper injects this before the page loads). The real device
// already draws its own status bar / dynamic island / home indicator, so
// any simulated device chrome in this preview UI should be suppressed.
const IS_NATIVE_APP = typeof window !== 'undefined' && window.isNativeApp === true;
// Both native platforms load this exact same deployed bundle (it's mirrored
// to the site root as well as /ios/ — there's no separate /android/-only
// build a device actually fetches), so which OS's native chrome exists
// around this page has to be told apart at runtime via nativePlatform
// (also injected by the wrapper), not by which build folder this file
// lives in. Only the iOS wrapper has its own native UITabBar (RootTabs.tsx)
// — Android is a single full-screen WebView with no native nav of its own,
// so it still needs this in-page tab bar.
const IS_NATIVE_IOS = IS_NATIVE_APP && typeof window !== 'undefined' && window.nativePlatform === 'ios';

// ADDITION 2: FCM in-app toast — floats at top, auto-dismisses after 4 s
const PFFCMToast = () => {
  const [toasts, setToasts] = React.useState([]);
  React.useEffect(() => {
    let idSeq = 0;
    function show(title, body) {
      const id = ++idSeq;
      setToasts(prev => [...prev, { id, title, body }]);
      setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), 4000);
    }
    window.__SR_SHOW_TOAST = show;
    (window.__SR_FCM_TOAST_QUEUE || []).forEach(q => show(q.title, q.body));
    window.__SR_FCM_TOAST_QUEUE = [];
    return () => { window.__SR_SHOW_TOAST = null; };
  }, []);
  if (!toasts.length) return null;
  return (
    <div style={{
      position: 'fixed', top: 16, left: 16, right: 16, zIndex: 9999,
      display: 'flex', flexDirection: 'column', gap: 8, pointerEvents: 'none',
    }}>
      {toasts.map(t => (
        <div key={t.id} style={{
          background: '#0f5132', color: '#fff',
          borderRadius: 14, padding: '10px 14px',
          boxShadow: '0 8px 24px rgba(0,0,0,0.22)',
          animation: 'pfIn 0.2s ease-out', pointerEvents: 'auto',
        }}>
          <div style={{ fontWeight: 700, fontSize: 13 }}>{t.title}</div>
          {t.body && <div style={{ fontSize: 12, opacity: 0.85, marginTop: 3 }}>{t.body}</div>}
        </div>
      ))}
    </div>
  );
};

// ADDITION 3: Offline banner — shown whenever navigator.onLine === false
const SROfflineBanner = () => {
  const [offline, setOffline] = React.useState(!navigator.onLine);
  React.useEffect(() => {
    const goOffline = () => setOffline(true);
    const goOnline  = () => setOffline(false);
    window.addEventListener('offline', goOffline);
    window.addEventListener('online',  goOnline);
    return () => {
      window.removeEventListener('offline', goOffline);
      window.removeEventListener('online',  goOnline);
    };
  }, []);
  if (!offline) return null;
  return (
    <div style={{
      background: '#b45309', color: '#fff', textAlign: 'center',
      padding: '6px 16px', fontSize: 12.5, fontWeight: 600,
      letterSpacing: '0.02em', zIndex: 9998, flexShrink: 0,
    }}>
      Offline — showing cached data
    </div>
  );
};

// A stalled request (e.g. a QUIC/HTTP3 handshake that never completed) used
// to leave whatever screen triggered it blank forever with zero feedback —
// skalerealty-sync.js now aborts any fetch after 15s and dispatches this
// event, but most screens still just swallow that error silently. This is
// the one place a user can actually see it happened and recover, instead of
// wondering whether the app is frozen.
const SRSlowConnectionBanner = () => {
  const [show, setShow] = React.useState(false);
  React.useEffect(() => {
    const onTimeout = () => setShow(true);
    document.addEventListener('sr:fetch-timeout', onTimeout);
    return () => document.removeEventListener('sr:fetch-timeout', onTimeout);
  }, []);
  if (!show) return null;
  return (
    <div style={{
      background: '#b91c1c', color: '#fff', textAlign: 'center',
      padding: '8px 16px', fontSize: 12.5, fontWeight: 600,
      letterSpacing: '0.02em', zIndex: 9998, flexShrink: 0,
      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
    }}>
      <span>Slow connection — some data may be missing</span>
      <button
        onClick={() => window.location.reload()}
        style={{
          background: 'rgba(255,255,255,0.2)', border: '1px solid rgba(255,255,255,0.4)',
          borderRadius: 6, color: '#fff', fontSize: 11.5, fontWeight: 700,
          padding: '3px 10px', cursor: 'pointer',
        }}
      >Reload</button>
      <button
        onClick={() => setShow(false)}
        style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.8)', fontSize: 15, cursor: 'pointer', lineHeight: 1, padding: 0 }}
      >×</button>
    </div>
  );
};

const DEFAULTS = /*EDITMODE-BEGIN*/{
  "salespersonName": "Vinay Joshi",
  "salespersonRole": "Sales Head",
  "salespersonGroup": "Bhawani Group",
  "salespersonInitials": "VJ"
}/*EDITMODE-END*/;

// Maps a URL path (as set by the native wrapper's per-tab WebViews — each
// native tab loads a fixed path like /calling) to the matching in-app
// screen/tab. Previously nothing read the path at all, so every native tab
// silently rendered the same default Home screen regardless of which one
// was tapped, and the native shell had no way to deep-link into a specific
// screen either.
// '/calls' is an alias for '/calling': the native deep-link layer
// (skaledesk-native/src/DeepLink.ts) documents and emits skaledesk://calls,
// which lands here as '/calls'. Without the alias it fell through to 'home',
// so the deep link silently opened the wrong screen.
const PATH_TAB_MAP = { '/': 'home', '/leads': 'leads', '/calling': 'calls', '/calls': 'calls', '/assets': 'assets', '/chat': 'chat' };

// Entity deep links — skaledesk://lead/123 → '/lead/123'. These carry an id,
// so they can't live in the flat map above. Nothing handled them before,
// which meant a push notification about a specific lead opened the home
// screen instead of that lead.
const PATH_ENTITY_MAP = { lead: 'lead', task: 'taskDetail', sitevisit: 'siteVisit' };

function initialRouteFromPath() {
  const path = (typeof window !== 'undefined' && window.location && window.location.pathname) || '/';
  if (PATH_TAB_MAP[path]) return { screen: PATH_TAB_MAP[path], arg: null };
  const parts = path.split('/').filter(Boolean);
  if (parts.length === 2) {
    const screen = PATH_ENTITY_MAP[parts[0].toLowerCase()];
    if (screen) return { screen, arg: decodeURIComponent(parts[1]) };
  }
  return { screen: 'home', arg: null };
}

// ─── Router for one device ───
const PFApp = ({ user, platform }) => {
  const [route, setRoute] = React.useState(initialRouteFromPath);
  // An entity deep link resolves to a detail screen ('lead', 'taskDetail'),
  // which is not itself a tab id — map it onto its owning tab so the bottom
  // bar still shows the right item highlighted, the same way go() does.
  const SCREEN_TAB_MAP = { lead: 'leads', taskDetail: 'home', siteVisit: 'leads' };
  const [tab, setTab] = React.useState(() => {
    const s = initialRouteFromPath().screen;
    return SCREEN_TAB_MAP[s] || s;
  });
  const topPad = platform === 'ios' ? 'env(safe-area-inset-top)' : 8;

  const go = (screen, arg) => {
    setRoute({ screen, arg });
    const tabMap = { home: 'home', leads: 'leads', lead: 'leads', calls: 'calls',
      callActive: 'calls', tasks: 'home', taskDetail: 'home', evidence: 'leads', profiling: 'leads',
      siteVisit: 'leads',
      assets: 'assets',
      learn: 'learn', chat: 'chat' };
    if (tabMap[screen]) setTab(tabMap[screen]);
  };

  const onTabChange = (id) => {
    setTab(id);
    const routes = { home: 'home', leads: 'leads', calls: 'calls', assets: 'assets', chat: 'chat' };
    setRoute({ screen: routes[id], arg: null });
  };

  // No bottom-nav on call-active or disposition (immersive)
  const immersive = ['callActive'].includes(route.screen);

  let ScreenEl;
  switch (route.screen) {
    case 'home': ScreenEl = <PFScreenHome go={go} user={user} />; break;
    case 'leads': ScreenEl = <PFScreenLeads go={go} />; break;
    case 'addLead': ScreenEl = <PFScreenAddLead go={go} user={user} />; break;
    case 'deletedLeads': ScreenEl = <PFScreenDeletedLeads go={go} />; break;
    case 'credits': ScreenEl = <PFScreenCredits go={go} />; break;
    case 'lead': ScreenEl = <PFScreenLead go={go} leadId={route.arg} />; break;
    case 'callActive': ScreenEl = <PFScreenCallActive go={go} leadId={route.arg} />; break;
    case 'calls': ScreenEl = <PFScreenCalls go={go} />; break;
    case 'tasks': ScreenEl = <PFScreenTasks go={go} role={user && user.role ? user.role : 'Agent'} />; break;
    case 'teamTasks': ScreenEl = <PFScreenTeamTasks go={go} />; break;
    case 'taskDetail': ScreenEl = <PFScreenTaskDetail go={go} taskId={route.arg} />; break;
    case 'evidence': ScreenEl = <PFScreenEvidence go={go} leadId={route.arg} />; break;
    case 'profiling': ScreenEl = <PFScreenProfiling go={go} leadId={route.arg} />; break;
    case 'siteVisit': ScreenEl = <PFScreenSiteVisit go={go} leadId={route.arg} />; break;
    case 'learn':  ScreenEl = <PFScreenLearn go={go} />; break;
    case 'assets': ScreenEl = <PFScreenAssets go={go} />; break;
    case 'chat':     ScreenEl = <PFScreenChat go={go} role={user && user.role ? user.role : 'Agent'} fromLeadId={route.arg} />; break;
    case 'overview': ScreenEl = <window.TaskOverview ns="realty" accent="var(--pf-green)" role={user && user.role ? user.role : 'Manager'} onBack={() => go('home')} teamNames={['Priya Menon','Rahul Singh','Kavya Nair','Deepak Iyer','Ankit Sharma']} />; break;
    default: ScreenEl = <PFScreenHome go={go} user={user} />;
  }

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', background: 'var(--pf-bg)', paddingTop: topPad }}>
      <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', position: 'relative' }}>
        {ScreenEl}
      </div>
      {/* iOS's native wrapper already provides its own UITabBar chrome (see
          skaledesk-native/src/RootTabs.tsx) mapped 1:1 to these same five
          screens via URL path — showing this in-page bar too would stack
          two competing tab bars on screen at once. Android has no native
          tab bar (single full-screen WebView, see App.tsx), so it still
          needs this one. */}
      {!immersive && !IS_NATIVE_IOS && <PFTabBar active={tab} onChange={onTabChange} />}
    </div>
  );
};

// ─── Main: two frames side by side + tweaks ───
/* global PFIndustrySelector, HCApp, DermApp, MFGApp, SkaleCapApp */
const App = () => {
  const [loggedIn,  setLoggedIn]  = React.useState(false);
  const [industry,  setIndustry]  = React.useState(null); // null → show selector
  const [role,      setRole]      = React.useState(null);
  // True when login couldn't reach the real backend (bad credentials, no
  // network, etc.) and the app fell back to local prototype/demo data —
  // previously this happened silently, with zero indication that nothing
  // typed or tapped from here on is actually being saved.
  const [demoMode,  setDemoMode]  = React.useState(false);

  // Was always DEFAULTS regardless of who actually logged in — every real
  // account saw "Vinay Joshi / Sales Head / Bhawani Group" (the prototype's
  // seed identity) on Home, ProfileSheet, and everywhere else `user` is
  // used, since nothing here ever read the real logged-in session. Falls
  // back to DEFAULTS only when there's genuinely no authenticated user
  // (prototype/demo mode with no token at all).
  const realSessionUser = (window.SRAPI && typeof window.SRAPI.getUser === 'function') ? window.SRAPI.getUser() : null;
  const realJobRole = (window.SRAPI && typeof window.SRAPI.getIndustryContext === 'function')
    ? (window.SRAPI.getIndustryContext() || {}).role : null;
  const realWorkspaceName = (() => { try { return localStorage.getItem('sr_workspace_name'); } catch (e) { return null; } })();
  const getInitials = (name) => (name || '').split(' ').filter(Boolean).slice(0, 2).map(w => w[0]).join('').toUpperCase() || '?';
  const user = (realSessionUser && realSessionUser.name)
    ? {
        name: realSessionUser.name,
        role: realJobRole || DEFAULTS.salespersonRole,
        group: realWorkspaceName || DEFAULTS.salespersonGroup,
        initials: getInitials(realSessionUser.name),
      }
    : { name: DEFAULTS.salespersonName, role: DEFAULTS.salespersonRole, group: DEFAULTS.salespersonGroup, initials: DEFAULTS.salespersonInitials };

  const homeBg    = loggedIn  ? 'var(--pf-bg)' : '#fff';
  const pillColor = loggedIn  ? 'var(--pf-ink)' : '#0C1A2E';

  // Consume a completed Google/Microsoft OAuth redirect. The backend finishes
  // the OAuth exchange server-side and redirects back here with tokens in the
  // URL hash (never sent to servers, so this is safe) as
  // #oauth=t=<access>&r=<refresh>&role=<role>&industry=<industry>.
  React.useEffect(() => {
    const hash = window.location.hash || '';
    if (!hash.startsWith('#oauth=')) return;
    try {
      const params = new URLSearchParams(hash.slice('#oauth='.length));
      const accessToken  = params.get('t');
      const refreshToken = params.get('r');
      const oauthRole     = params.get('role');
      const oauthIndustry = params.get('industry');
      if (accessToken && window.SRAPI) {
        window.SRAPI._token = accessToken;
        localStorage.setItem('sr_access_token', accessToken);
        if (refreshToken) localStorage.setItem('sr_refresh_token', refreshToken);
        if (oauthRole)     localStorage.setItem('sr_role', oauthRole);
        if (oauthIndustry) localStorage.setItem('sr_industry', oauthIndustry);
        window.history.replaceState(null, '', window.location.pathname + window.location.search);
        handleLogin({ industry: oauthIndustry, role: oauthRole || 'agent' });
      }
    } catch (_) { /* malformed hash — ignore, user lands on login screen normally */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Restore an existing session on a fresh load — e.g. the native app's
  // WebView, which always does a brand-new navigation on every launch
  // (unlike a browser tab that stays warm). Without this, a still-valid
  // token already sitting in localStorage (seeded by the native shell, or
  // just left over from an earlier visit) was silently ignored and the
  // login screen showed anyway, even though the user was already signed in.
  React.useEffect(() => {
    if ((window.location.hash || '').startsWith('#oauth=')) return; // handled above
    try {
      const existingToken = localStorage.getItem('sr_access_token');
      if (existingToken && window.SRAPI) {
        window.SRAPI._token = existingToken;
        // Use the industry/role already sealed into this session (seeded by
        // the native shell alongside the token) instead of calling
        // handleLogin() bare, which re-derives industry from a live
        // /api/mobile/client-config fetch on every single call. That path
        // is for a token with no already-known industry (e.g. email/password
        // login); it is NOT guaranteed to return the same industry on every
        // call for every account. Since each native tab is a separate fresh
        // WebView load, calling it bare here meant every tab could
        // independently land on a different industry's app entirely.
        const existingIndustry = localStorage.getItem('sr_industry');
        const existingRole = localStorage.getItem('sr_role');
        handleLogin(existingIndustry ? { industry: existingIndustry, role: existingRole || 'agent' } : undefined);
      }
    } catch (_) { /* no-op — falls through to the normal login screen */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Request FCM permission + auto-detect industry via /api/mobile/client-config
  const handleLogin = React.useCallback((liveCtx) => {
    // liveCtx is { industry, role } when login resolved from the JWT payload;
    // undefined/null in prototype mode — fall through to client-config fetch.
    const hasRealToken = !!(window.SRAPI && (window.SRAPI._token || localStorage.getItem('sr_access_token')));
    setDemoMode(!hasRealToken);
    if (liveCtx && liveCtx.industry) {
      setIndustry(liveCtx.industry);
      setRole(liveCtx.role || 'agent');
      setLoggedIn(true);
    } else {
      // Fetch workspace config to determine industry — skips PFIndustrySelector
      setLoggedIn(true);
      if (window.SRAPI && window.SRAPI._token) {
        window.SRAPI._get('/api/mobile/client-config').then(cfg => {
          if (cfg && cfg.industry) {
            setIndustry(cfg.industry);
            // Cache for offline use
            try { localStorage.setItem('sr:industry', cfg.industry); } catch {}
          } else {
            // Fall back to cached industry, then show selector
            const cached = (() => { try { return localStorage.getItem('sr:industry'); } catch {} })();
            if (cached) setIndustry(cached);
          }
        }).catch(() => {
          const cached = (() => { try { return localStorage.getItem('sr:industry'); } catch {} })();
          if (cached) setIndustry(cached);
        });
      }
    }
    setTimeout(() => {
      window.SR_NOTIFY?.requestPermission().catch(() => {});
    }, 2000);
  }, []);

  const handleIndustrySelect = React.useCallback((ind, r) => {
    // Canonicalize role labels from the picker into the keys downstream
    // lookups expect (LD_CMDS, HEAD_ROLES, SkaleCapApp normalizer).
    // 'HR / L&D' is shown to the user, but commands are keyed by 'L&D Manager'.
    const canonical = r === 'HR / L&D' ? 'L&D Manager' : r;
    setIndustry(ind);
    setRole(canonical);
  }, []);

  // Allow any child screen to trigger an industry switch or logout via custom events
  React.useEffect(() => {
    const onSwitch = (e) => {
      const { industry: ind, role: r } = e.detail || {};
      if (ind) handleIndustrySelect(ind, r || 'agent');
    };
    const onLogout = () => {
      // Sign out must end the real session, not just hide it: without this the tokens stayed in localStorage (a reload
      // logged straight back in) and the refresh token was never revoked. The Android shell already does this.
      // Re-dispatch loop is prevented by _clearingSession guard in skalerealty-sync.js _clearLocalSession.
      if (window.SRAPI && typeof window.SRAPI.logout === 'function') window.SRAPI.logout().catch(() => {});
      setLoggedIn(false);
      setIndustry(null);
      setRole(null);
    };
    document.addEventListener('sr:switch-industry', onSwitch);
    document.addEventListener('sr:logout', onLogout);
    return () => {
      document.removeEventListener('sr:switch-industry', onSwitch);
      document.removeEventListener('sr:logout', onLogout);
    };
  }, [handleIndustrySelect]);

  // Pick the right app shell once industry + role are chosen
  const renderApp = () => {
    switch (industry) {
      case 'skalecap':      return <SkaleCapApp role={role} platform="ios" user={user} />;
      case 'healthcare':    return <HCApp   role={role} platform="ios" user={user} />;
      case 'dermatology':   return <DermApp role={role} platform="ios" user={user} />;
      case 'manufacturing': return <MFGApp  role={role} platform="ios" user={user} />;
      default:              return <PFApp   user={user} platform="ios" />;
    }
  };

  // Determine body bg for the pill bar at the bottom
  const bodyBg = !loggedIn
    ? '#fff'
    : !industry
      ? '#f9fafb'
      : industry === 'skalecap'
        ? '#f0f4f9'
        : industry === 'healthcare'
          ? '#eff6ff'
          : industry === 'dermatology'
            ? '#faf5ff'
            : industry === 'manufacturing'
              ? '#fffbeb'
              : 'var(--pf-bg)';

  return (
    <div style={{ height: '100dvh', display: 'flex', flexDirection: 'column', overflow: 'hidden', background: bodyBg }}>
      {/* ADDITION 2: FCM in-app toast overlay */}
      <PFFCMToast />
      {/* ADDITION 3: offline banner */}
      <SROfflineBanner />
      <SRSlowConnectionBanner />
      {loggedIn && demoMode && (
        <div style={{
          flexShrink: 0, background: '#7c2d12', color: '#fff', fontSize: 11.5, fontWeight: 600,
          textAlign: 'center', padding: '6px 10px', letterSpacing: '0.02em',
        }}>
          ⚠ DEMO MODE — not connected to the server. Nothing you do here is being saved.
        </div>
      )}
      <div style={{ flex: 1, minHeight: 0, position: 'relative', overflow: 'hidden' }}>
        {!loggedIn
          ? <PFScreenLogin onLogin={handleLogin} platform="ios" />
          : !industry
            ? <PFIndustrySelector onSelect={handleIndustrySelect} />
            : renderApp()
        }
      </div>
      {!IS_NATIVE_APP && (
        <div style={{ flexShrink: 0, height: 34, display: 'flex', justifyContent: 'center', alignItems: 'flex-end', paddingBottom: 8 }}>
          <div style={{ width: 134, height: 5, background: pillColor, borderRadius: 3, opacity: 0.18 }} />
        </div>
      )}
    </div>
  );
};

function reportSrError(payload) {
  try {
    const base = window.__SR_API_BASE || '';
    fetch(base + '/api/errors/frontend', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(Object.assign({
        source: 'mobile-ios',
        level: 'error',
        page: (typeof window !== 'undefined' && window.location) ? window.location.pathname : 'unknown',
        ts: new Date().toISOString(),
      }, payload || {})),
    }).catch(() => {});
  } catch {}
}

// ── Global toast queue (used by fetch failures, event-handler errors) ────────
window.SR_TOAST = window.SR_TOAST || (function () {
  const listeners = new Set();
  const queue = [];
  function push(kind, message, opts) {
    const t = { id: Math.random().toString(36).slice(2), kind, message: String(message || '').slice(0, 240), ttl: (opts && opts.ttl) || 4500 };
    queue.push(t);
    listeners.forEach(fn => { try { fn(queue.slice()); } catch {} });
    setTimeout(() => {
      const i = queue.findIndex(x => x.id === t.id);
      if (i >= 0) queue.splice(i, 1);
      listeners.forEach(fn => { try { fn(queue.slice()); } catch {} });
    }, t.ttl);
  }
  return {
    error: (m, o) => push('error', m, o),
    success: (m, o) => push('success', m, o),
    warn: (m, o) => push('warn', m, o),
    info: (m, o) => push('info', m, o),
    subscribe: (fn) => { listeners.add(fn); fn(queue.slice()); return () => listeners.delete(fn); },
  };
})();

function ToastHost() {
  const [toasts, setToasts] = React.useState([]);
  React.useEffect(() => window.SR_TOAST.subscribe(setToasts), []);
  if (!toasts.length) return null;
  const colors = {
    error:   { bg: '#fef2f2', bd: '#fecaca', fg: '#991b1b' },
    warn:    { bg: '#fffbeb', bd: '#fde68a', fg: '#92400e' },
    success: { bg: '#f0fdf4', bd: '#bbf7d0', fg: '#166534' },
    info:    { bg: '#eff6ff', bd: '#bfdbfe', fg: '#1e40af' },
  };
  return (
    <div style={{ position: 'fixed', left: 12, right: 12, bottom: 90, zIndex: 99999, display: 'flex', flexDirection: 'column', gap: 8, pointerEvents: 'none' }}>
      {toasts.map(t => {
        const c = colors[t.kind] || colors.info;
        return (
          <div key={t.id} style={{ background: c.bg, border: '1px solid ' + c.bd, color: c.fg, padding: '10px 14px', borderRadius: 10, fontSize: 13, fontWeight: 500, boxShadow: '0 4px 12px rgba(0,0,0,0.08)', pointerEvents: 'auto' }}>
            {t.message}
          </div>
        );
      })}
    </div>
  );
}

// ── Global async / event-handler error safety net ────────────────────────────
if (!window.__SR_GLOBAL_HANDLERS_INSTALLED__) {
  window.__SR_GLOBAL_HANDLERS_INSTALLED__ = true;
  window.addEventListener('error', (e) => {
    const msg = (e && e.message) || 'An unexpected error occurred.';
    if (window.SR_TOAST) window.SR_TOAST.error(msg);
    reportSrError({ message: msg, stack: e?.error?.stack, kind: 'window.onerror' });
  });
  window.addEventListener('unhandledrejection', (e) => {
    const r = e && e.reason;
    const msg = (r && (r.message || r.toString())) || 'Something went wrong. Please try again.';
    if (window.SR_TOAST) window.SR_TOAST.error(msg);
    reportSrError({ message: msg, stack: r?.stack, kind: 'unhandledrejection' });
  });
  // Wrap SRAPI/fetch failures with an automatic toast for anything unhandled.
  const _origFetch = window.fetch ? window.fetch.bind(window) : null;
  if (_origFetch) {
    window.fetch = function (input, init) {
      return _origFetch(input, init).then((res) => {
        if (!res.ok && res.status >= 500) {
          try {
            const url = typeof input === 'string' ? input : (input && input.url) || '';
            // Only toast our own API failures — skip 3p (analytics, etc)
            if (url.indexOf('/api/') >= 0) {
              window.SR_TOAST && window.SR_TOAST.error('Server error (' + res.status + '). Please try again.');
            }
          } catch {}
        }
        return res;
      }).catch((err) => {
        try {
          const url = typeof input === 'string' ? input : (input && input.url) || '';
          if (url.indexOf('/api/') >= 0) {
            window.SR_TOAST && window.SR_TOAST.error('Network error. Check your connection and try again.');
          }
        } catch {}
        throw err;
      });
    };
  }
}

function srErrorCode(input) {
  let hash = 0x811c9dc5;
  const s = String(input || 'unknown');
  for (let i = 0; i < s.length; i++) {
    hash ^= s.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  const hex = (hash >>> 0).toString(16).toUpperCase().padStart(8, '0');
  return 'ERR-' + hex.slice(0, 6);
}

const SR_SUPPORT_WA = '917709159978';

class ErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { crashed: false, error: null, code: null, copied: false }; }
  static getDerivedStateFromError(error) { return { crashed: true, error }; }
  componentDidCatch(error, info) {
    const message = error?.message || String(error);
    const code = srErrorCode(message + '|' + (error?.stack || '') + '|' + (window.location ? window.location.pathname : ''));
    this.setState({ code });
    reportSrError({
      message,
      stack: error?.stack,
      componentStack: info?.componentStack,
      kind: 'react-boundary',
      payload: { code },
    });
  }
  copyCode() {
    try { navigator.clipboard && navigator.clipboard.writeText(this.state.code); } catch {}
    this.setState({ copied: true });
    setTimeout(() => this.setState({ copied: false }), 1800);
  }
  reportViaWhatsApp() {
    const text = 'SkaleDesk error report\nCode: ' + this.state.code + '\nMessage: ' + (this.state.error?.message || 'Unknown');
    window.open('https://wa.me/' + SR_SUPPORT_WA + '?text=' + encodeURIComponent(text), '_blank');
  }
  render() {
    if (!this.state.crashed) return this.props.children;
    return (
      <div style={{ height: '100dvh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 32, fontFamily: 'sans-serif', background: '#f9fafb' }}>
        <div style={{ fontSize: 40, marginBottom: 16 }}>⚠️</div>
        <div style={{ fontWeight: 700, fontSize: 18, marginBottom: 8 }}>Something went wrong</div>
        <div style={{ fontSize: 13, color: '#6b7280', marginBottom: 12, textAlign: 'center' }}>The error has been reported. Please restart the app.</div>
        {this.state.code && (
          <button onClick={() => this.copyCode()} style={{ display: 'flex', alignItems: 'center', gap: 6, margin: '0 0 20px', padding: '4px 10px', borderRadius: 999, border: '1px solid #e5e7eb', background: '#fff', color: '#6b7280', fontSize: 12, fontFamily: 'monospace', fontWeight: 700 }}>
            {this.state.copied ? '✓' : '⧉'} {this.state.code}
          </button>
        )}
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={() => window.location.reload()} style={{ padding: '10px 24px', borderRadius: 10, border: 0, background: '#0f5132', color: '#fff', fontWeight: 600, cursor: 'pointer' }}>Restart</button>
          <button onClick={() => this.reportViaWhatsApp()} style={{ padding: '10px 24px', borderRadius: 10, border: '1px solid #d1d5db', background: '#fff', color: '#374151', fontWeight: 600, cursor: 'pointer' }}>Report</button>
        </div>
      </div>
    );
  }
}

ReactDOM.createRoot(document.getElementById('root')).render(<ErrorBoundary><><App /><ToastHost /></></ErrorBoundary>);


